/* pamela-monument-editor-v2.js
   Shopify (Dawn) Monument Editor Ã¢â‚¬â€ Field-button workflow (Companion + Single ready)

   Ã¢Å“â€¦ Fixes in this rewrite:
   - Removes duplicate Boot blocks + removes all Ã¢â‚¬Å“append hacksÃ¢â‚¬Â (single clean file)
   - Fixes Ã¢â‚¬Å“sticky mouseÃ¢â‚¬Â (drag not releasing) with:
     1) pointer-based viewport wiring
     2) global forced Konva drag release (pointerup/mouseup/touchend/blur/ESC/visibilitychange)
     3) editor-level pan-release on global pointerup

   Ã¢Å“â€¦ Added now (Jan 2026):
   - Border PNG Overlay layer (TOP layer, 100% click-through)
   - Safe placement margins (0.5" top/left/right, 1.5" bottom)
     -> clamps Text + Clipart so users cannot place into border areas

   Keeps:
   - Variant dropdown (background) + price
   - Auto text color by variant title
   - Grid toggle + center guide ONLY while dragging text
   - Text buttons + A-/A+ sizing rules
   - Deterministic spacing for NAME/DATE/VERSE
   - Snap rules (lastname/name/verse to centerline; date to nearest name only if close)
   - Inline edit Ã¢â‚¬Å“typing on stoneÃ¢â‚¬Â
   - Clipart library hook (window.PAMELA_ADD_CLIPART_TO_STAGE)
   - Clipart tools (Flip / Rotate / - + scale clamped to original)
   - Pan Mode toggle + Zoom buttons
   - Mutual exclusion hooks for Clipart vs Panels/Frames

   IMPORTANT:
   - Panels/Frames button is included and placed in the DESIGN row.
   - This file exposes:
     window.PAMELA_CLOSE_CLIPART_UI()
     window.PAMELA_CLOSE_PANELS_UI()
     window.PAMELA_TOGGLE_PANELS_UI()  (dispatch-only default)
*/

(() => {
  "use strict";

// =============================
// Fonts (from Registry)
// =============================

if (!Array.isArray(window.PAMELA_FONT_REGISTRY)) {
  console.error("PAMELA_FONT_REGISTRY not loaded.");
}

const PAMELA_ALL_FONTS = Array.isArray(window.PAMELA_FONT_REGISTRY)
  ? window.PAMELA_FONT_REGISTRY
      .filter(f => f.group.includes("name_date") || f.group.includes("lastname"))
      .map(f => f.key)
  : [];

const PAMELA_LIMITED_FONTS = Array.isArray(window.PAMELA_FONT_REGISTRY)
  ? window.PAMELA_FONT_REGISTRY
      .filter(f => f.group.includes("verse"))
      .map(f => f.key)
  : [];


// =============================
// Canvas / Units
// =============================
const DPI = 72;
const PAMELA_NUDGE_STEP_IN = 1 / 16;
const PAMELA_SINGLE_BACK_LASTNAME_MAX_IN = 2.5;
const PAMELA_SINGLE_BACK_LASTNAME_MIN_IN = 1.25;
const PAMELA_SINGLE_BACK_LASTNAME_STEP_IN = 0.25;

const CANVAS_IN = { w: 80, h: 60 };

const DESIGN_PX = {
  w: CANVAS_IN.w * DPI,
  h: CANVAS_IN.h * DPI,
}; // 5616 x 2592

const PADDING_IN = 0.25;
const PADDING_PX = PADDING_IN * DPI;

const ZOOM_STEP = 1.08;

const VIEW_MIN_SCALE = 0.15;
const VIEW_MAX_SCALE = 6.0;

// Spacing for NAME/DATE/VERSE only
const MIN_TEXT_GAP_IN = 0.5;
const MIN_TEXT_GAP_PX = MIN_TEXT_GAP_IN * DPI;

// Panel minimum size rules
const PANEL_MIN_H_IN = 1.5;
const PANEL_MIN_W_IN = 8.0;
const PANEL_CENTER_IF_WIDER_THAN_IN = 16.0;
const PANEL_SIDE_LANDING_INSET_IN = 2.0;
const LASTNAME_PANEL_STRETCH_CENTER_IN = 3.0;
const LASTNAME_PANEL_CLEARANCE_PER_SIDE_IN = 1.5;
const NAME_DATE_PANEL_STRETCH_CENTER_IN = 3.0;
const NAME_DATE_PANEL_FIXED_SIDE_IN = 2.7;
const NAME_DATE_PANEL_FIXED_CAP_IN = 0.75;
const NAME_DATE_PANEL_SOURCE_CAP_IN = 1.25;

const PANEL_MIN_H_PX = PANEL_MIN_H_IN * DPI; // 108
const PANEL_MIN_W_PX = PANEL_MIN_W_IN * DPI; // 576
const PANEL_CENTER_IF_WIDER_THAN_PX = PANEL_CENTER_IF_WIDER_THAN_IN * DPI;
const PANEL_SIDE_LANDING_INSET_PX = PANEL_SIDE_LANDING_INSET_IN * DPI;
const LASTNAME_PANEL_STRETCH_CENTER_PX = LASTNAME_PANEL_STRETCH_CENTER_IN * DPI;
const LASTNAME_PANEL_CLEARANCE_TOTAL_PX = LASTNAME_PANEL_CLEARANCE_PER_SIDE_IN * 2 * DPI;
const NAME_DATE_PANEL_STRETCH_CENTER_PX = NAME_DATE_PANEL_STRETCH_CENTER_IN * DPI;
const NAME_DATE_PANEL_FIXED_SIDE_PX = NAME_DATE_PANEL_FIXED_SIDE_IN * DPI;
const NAME_DATE_PANEL_FIXED_CAP_PX = NAME_DATE_PANEL_FIXED_CAP_IN * DPI;
const NAME_DATE_PANEL_SOURCE_CAP_PX = NAME_DATE_PANEL_SOURCE_CAP_IN * DPI;

function pamelaIsLastNamePanelNode(node) {
  try {
    return !!(
      node &&
      node.className === "Image" &&
      node.getAttr?.("pamelaPanel") === true &&
      String(node.getAttr?.("pamelaPanelGroup") || "").toLowerCase() === "lastname"
    );
  } catch (_) {
    return false;
  }
}

function pamelaFindLastNameTextNode(editor) {
  try {
    const stage = editor?.stage;
    if (!stage || typeof stage.find !== "function") return null;

    const candidates = stage.find((n) => {
      try {
        if (!n || n.className !== "Text") return false;
        const role = String(n.getAttr?.("pamelaTextRole") || n.getAttr?.("pamelaRole") || "").toLowerCase();
        const kind = String(n.getAttr?.("pamelaTextKind") || "").toLowerCase();
        if (role === "lastname" || kind === "lastname" || n.getAttr?.("pamelaIsLastName")) return true;
        const t = String(n.text?.() || "").trim().toLowerCase();
        return t === "last name" || t.includes("last name");
      } catch (_) {
        return false;
      }
    });

    return candidates && candidates.length ? candidates[0] : null;
  } catch (_) {
    return null;
  }
}

function pamelaGetLastNamePanelSliceMetrics(node) {
  const baseW = Math.max(1, Number(node?.getAttr?.("pamelaPanelBaseWidth") || node?.width?.() || 0) || 1);
  const centerW = Math.max(1, Math.min(LASTNAME_PANEL_STRETCH_CENTER_PX, baseW - 2));
  const endW = Math.max(1, (baseW - centerW) / 2);
  return { baseW, centerW, endW, fixedEndsW: endW * 2 };
}

function pamelaGetLastNamePanelMinWidth(editor, node) {
  if (!pamelaIsLastNamePanelNode(node)) return PANEL_MIN_W_PX;
  return PANEL_MIN_W_PX;
}

function pamelaRenderLastNamePanelSlice(sourceImg, baseW, baseH, targetW, targetH) {
  if (!sourceImg) return null;

  const outW = Math.max(1, Math.round(Number(targetW || 0) || 1));
  const outH = Math.max(1, Math.round(Number(targetH || 0) || 1));
  const bW = Math.max(1, Number(baseW || outW) || outW);
  const bH = Math.max(1, Number(baseH || outH) || outH);
  const centerW = Math.max(1, Math.min(LASTNAME_PANEL_STRETCH_CENTER_PX, bW - 2));
  const endW = Math.max(1, Math.min((bW - centerW) / 2, (outW - 1) / 2));
  const midW = Math.max(1, outW - (endW * 2));

  const srcW = Math.max(1, Number(sourceImg.naturalWidth || sourceImg.width || bW) || bW);
  const srcH = Math.max(1, Number(sourceImg.naturalHeight || sourceImg.height || bH) || bH);
  const sx = srcW / bW;
  const sy = srcH / bH;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, outW, outH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Left and right end caps are the same mirrored artwork and must sample
  // equal-width source slices. The right cap previously took "whatever's
  // left" in the source image (srcW - rightSrcX) instead of the same
  // endW-derived width as the left -- harmless when endW matched its
  // natural unclamped size, but once the panel got small enough that endW
  // had to shrink to fit, the left cap sampled proportionally less of its
  // source (visibly squished) while the right cap's leftover region kept
  // growing (visibly oversized).
  const capSrcW = Math.max(1, Math.round(endW * sx));
  const leftSrcW = capSrcW;
  const rightSrcW = capSrcW;
  const rightSrcX = Math.max(0, srcW - rightSrcW);
  const centerSrcX = Math.min(leftSrcW, rightSrcX);
  const centerSrcW = Math.max(1, Math.min(Math.round(centerW * sx), rightSrcX - centerSrcX));
  const srcFullH = Math.max(1, Math.round(srcH));

  ctx.drawImage(sourceImg, 0, 0, leftSrcW, srcFullH, 0, 0, endW, outH);
  ctx.drawImage(sourceImg, centerSrcX, 0, centerSrcW, srcFullH, endW, 0, midW, outH);
  ctx.drawImage(sourceImg, rightSrcX, 0, rightSrcW, srcFullH, endW + midW, 0, endW, outH);

  return canvas;
}

function pamelaUpdateLastNamePanelSliceImage(node) {
  if (!pamelaIsLastNamePanelNode(node)) return false;

  const sourceImg = node.__pamelaLastNameSliceSourceImage || node.__pamelaPanelOriginalImage || node.image?.();
  if (!sourceImg) return false;

  const baseW = Math.max(1, Number(node.getAttr?.("pamelaPanelBaseWidth") || node.width?.() || 0) || 1);
  const baseH = Math.max(1, Number(node.getAttr?.("pamelaPanelBaseHeight") || node.height?.() || 0) || 1);
  const targetW = Math.max(1, Number(node.width?.() || baseW) || baseW);
  const targetH = Math.max(1, Number(node.height?.() || baseH) || baseH);
  const canvas = pamelaRenderLastNamePanelSlice(sourceImg, baseW, baseH, targetW, targetH);
  if (!canvas) return false;

  node.image(canvas);
  node.__pamelaPanelKnockoutSourceImage = canvas;
  node.__pamelaPanelKnockoutImage = null;

  const maskSource = node.__pamelaLastNameSliceMaskSourceImage || node.__pamelaPanelKnockoutMaskImage || sourceImg;
  const maskCanvas = pamelaRenderLastNamePanelSlice(maskSource, baseW, baseH, targetW, targetH);
  if (maskCanvas) node.__pamelaPanelKnockoutMaskImage = maskCanvas;

  return true;
}

function pamelaIsNameDatePanelNode(node) {
  try {
    return !!(
      node &&
      node.className === "Image" &&
      node.getAttr?.("pamelaPanel") === true &&
      String(node.getAttr?.("pamelaPanelGroup") || "").toLowerCase() === "name_date"
    );
  } catch (_) {
    return false;
  }
}

function pamelaRenderNameDatePanelSlice(sourceImg, baseW, baseH, targetW, targetH) {
  if (!sourceImg) return null;

  const outW = Math.max(1, Math.round(Number(targetW || 0) || 1));
  const outH = Math.max(1, Math.round(Number(targetH || 0) || 1));
  const bW = Math.max(1, Number(baseW || outW) || outW);
  const bH = Math.max(1, Number(baseH || outH) || outH);

  const sideW = Math.max(1, Math.min(NAME_DATE_PANEL_FIXED_SIDE_PX, bW / 3));
  const capH = Math.max(1, Math.min(NAME_DATE_PANEL_FIXED_CAP_PX, bH / 3));
  const sourceCapH = Math.max(capH, Math.min(NAME_DATE_PANEL_SOURCE_CAP_PX, bH / 3));
  const destCapH = Math.max(1, Math.min(sourceCapH, (outH - 1) / 2));
  const centerW = Math.max(1, bW - (sideW * 2));
  const centerH = Math.max(1, bH - (sourceCapH * 2));
  const midW = Math.max(1, outW - (sideW * 2));
  const midH = Math.max(1, outH - (destCapH * 2));

  const srcW = Math.max(1, Number(sourceImg.naturalWidth || sourceImg.width || bW) || bW);
  const srcH = Math.max(1, Number(sourceImg.naturalHeight || sourceImg.height || bH) || bH);
  const sx = srcW / bW;
  const sy = srcH / bH;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, outW, outH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const srcX = [
    0,
    Math.max(0, Math.round(sideW * sx)),
    Math.max(0, Math.round((sideW + centerW) * sx)),
  ];
  const srcY = [
    0,
    Math.max(0, Math.round(sourceCapH * sy)),
    Math.max(0, Math.round((sourceCapH + centerH) * sy)),
  ];
  const srcWidths = [
    Math.max(1, Math.round(sideW * sx)),
    Math.max(1, Math.round(centerW * sx)),
    Math.max(1, srcW - srcX[2]),
  ];
  const srcHeights = [
    Math.max(1, Math.round(sourceCapH * sy)),
    Math.max(1, Math.round(centerH * sy)),
    Math.max(1, srcH - srcY[2]),
  ];
  const dstX = [0, sideW, sideW + midW];
  const dstY = [0, destCapH, destCapH + midH];
  const dstWidths = [sideW, midW, sideW];
  const dstHeights = [destCapH, midH, destCapH];

  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      ctx.drawImage(
        sourceImg,
        srcX[col],
        srcY[row],
        srcWidths[col],
        srcHeights[row],
        dstX[col],
        dstY[row],
        dstWidths[col],
        dstHeights[row]
      );
    }
  }

  return canvas;
}

function pamelaGetNameDatePanelSliceMetrics(node) {
  const baseW = Math.max(1, Number(node?.getAttr?.("pamelaPanelBaseWidth") || node?.width?.() || 0) || 1);
  const baseH = Math.max(1, Number(node?.getAttr?.("pamelaPanelBaseHeight") || node?.height?.() || 0) || 1);
  const sideW = Math.max(1, Math.min(NAME_DATE_PANEL_FIXED_SIDE_PX, baseW / 3));
  const capH = Math.max(1, Math.min(NAME_DATE_PANEL_FIXED_CAP_PX, baseH / 3));
  return { baseW, baseH, sideW, capH, fixedW: sideW * 2, fixedH: capH * 2 };
}

function pamelaUpdateNameDatePanelSliceImage(node) {
  if (!pamelaIsNameDatePanelNode(node)) return false;

  const sourceImg = node.__pamelaNameDateSliceSourceImage || node.__pamelaPanelOriginalImage || node.image?.();
  if (!sourceImg) return false;

  const baseW = Math.max(1, Number(node.getAttr?.("pamelaPanelBaseWidth") || node.width?.() || 0) || 1);
  const baseH = Math.max(1, Number(node.getAttr?.("pamelaPanelBaseHeight") || node.height?.() || 0) || 1);
  const targetW = Math.max(1, Number(node.width?.() || baseW) || baseW);
  const targetH = Math.max(1, Number(node.height?.() || baseH) || baseH);
  const canvas = pamelaRenderNameDatePanelSlice(sourceImg, baseW, baseH, targetW, targetH);
  if (!canvas) return false;

  node.image(canvas);
  node.__pamelaPanelKnockoutSourceImage = canvas;
  node.__pamelaPanelKnockoutImage = null;

  const maskSource = node.__pamelaNameDateSliceMaskSourceImage || node.__pamelaPanelKnockoutMaskImage || sourceImg;
  const maskCanvas = pamelaRenderNameDatePanelSlice(maskSource, baseW, baseH, targetW, targetH);
  if (maskCanvas) node.__pamelaPanelKnockoutMaskImage = maskCanvas;

  return true;
}

function pamelaUpdateTraditionalPanelSliceImage(node) {
  return pamelaUpdateLastNamePanelSliceImage(node) || pamelaUpdateNameDatePanelSliceImage(node);
}

// Auto unlink/relink threshold (Y distance)
const PAMELA_PAIR_BREAK_IN = 1.0;
const PAMELA_PAIR_BREAK_PX = PAMELA_PAIR_BREAK_IN * DPI; // 72

// Horizontal mirror-link behavior for companion name/date columns
const PAMELA_MIRROR_BREAK_PX = 48;
const PAMELA_MIRROR_RELINK_PX = 20;
const PAMELA_MIRROR_BREAK_MS = 180;
const PAMELA_MIRROR_BREAK_RATIO = 1.35;
const PAMELA_TEXT_STACK_X_OVERLAP_PX = 18;
const PAMELA_MIRROR_PAIR_MIN_X_GAP_PX = 24;
const PAMELA_DEFAULT_HINT_TEXT = "Click to select | Drag to move | Double-click text to edit | Ctrl+Wheel to zoom";
const PAMELA_INDEPENDENT_HINT_DESKTOP = "Hold Shift to move this mirrored text independently";
const PAMELA_INDEPENDENT_HINT_MOBILE = "Unlink Mirrored Text lets you adjust one side only";

// Rotation snap for text
const ROTATE_SNAP_DEG = 1;

const snapDeg = (deg) =>
  Math.round(deg / ROTATE_SNAP_DEG) * ROTATE_SNAP_DEG;

// Clipart rotate step
const CLIP_ROTATE_STEP_DEG = 5;

// Snap threshold (px)
const PAMELA_SNAP_THRESHOLD_PX = 30;
const PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX = 24;

function pamelaGetEffectiveSnapThreshold(editor, baseThreshold) {
  const base = Math.max(1, Number(baseThreshold) || 1);
  try {
    if (!pamelaIsMobileEditorViewport()) return base;
    const viewScale = Number(editor?.state?.view?.scale || 1) || 1;
    if (viewScale >= 1) return base;
    return Math.min((base * 0.8) / Math.max(0.1, viewScale), base * 3);
  } catch (_) {
    return base;
  }
}

// Variant / Color rules
const WHITE_TEXT_MATCH = [
  "jet black",
  "bahama blue",
  "bahama",
  "superior black",
  "black",
];

const FORCE_BLACK_MATCH = [
  "georgia gray",
  "georgia grey",
  "gray",
  "grey",
];

const getAutoTextFillForVariant = (variantLike) => {
  const variantId = String(
    variantLike && typeof variantLike === "object"
      ? (variantLike.id || "")
      : ""
  ).trim();

  try {
    const toneMap = window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ || {};
    const tone = String(toneMap?.[variantId] || "").trim().toLowerCase();

    if (tone === "light") return "#111";
    if (tone === "dark") return "#fff";
  } catch (_) {}

  const variantTitle =
    variantLike && typeof variantLike === "object"
      ? (variantLike.title || variantLike.public_title || "")
      : variantLike || "";

  const t = String(variantTitle || "").toLowerCase();

  if (FORCE_BLACK_MATCH.some((k) => t.includes(k))) return "#111";
  if (WHITE_TEXT_MATCH.some((k) => t.includes(k))) return "#fff";

  return "#111";
};

function pamelaIsDarkGraniteVariant(variantLike) {
  const variantId = String(
    variantLike && typeof variantLike === "object" ? (variantLike.id || "") : ""
  ).trim();

  try {
    const toneMap = window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ || {};
    const tone = String(toneMap?.[variantId] || "").trim().toLowerCase();
    if (tone === "dark") return true;
    if (tone === "light") return false;
  } catch (_) {}

  const title = String(
    variantLike && typeof variantLike === "object"
      ? (variantLike.title || variantLike.public_title || "")
      : (variantLike || "")
  ).toLowerCase();

  return WHITE_TEXT_MATCH.some((name) => title.includes(name));
}

function pamelaSyncDarkGraniteFullscreenBackground(editor, variantLike) {
  const wrap = editor?.wrapEl || document.getElementById("pamela-stageWrap");
  if (!wrap) return;
  wrap.classList.toggle("pamela-dark-granite", pamelaIsDarkGraniteVariant(variantLike));
}
  // ============================================================
// Auto text color override:
// If the variant default text is WHITE, force BLACK for any
// Text node that overlaps a Panel or Clipart image.
// Panels: Konva.Image with attrs.pamelaPanel === true
// Clipart: Konva.Image with attrs.pamelaClipart === true
// ============================================================

function pamelaIsWhiteFill(fill) {
  const f = String(fill || "").trim().toLowerCase();
  return f === "#fff" || f === "#ffffff" || f === "white";
}

function pamelaGetSideLayers(editor, key) {
  if (!editor?.layers) return [];

  const out = [];
  const push = (layer) => {
    if (!layer) return;
    if (out.includes(layer)) return;
    out.push(layer);
  };

  push(editor.layers?.[key]);
  push(editor.layers?.sides?.front?.[key]);
  push(editor.layers?.sides?.back?.[key]);

  return out;
}

function pamelaGetBlockerImages(editor) {
  if (!editor?.layers) return [];

  const blockers = [];

  // Clipart lives on CONTENT
  const contentLayers = pamelaGetSideLayers(editor, "content");
  contentLayers.forEach((content) => {
    const clip = content.getChildren((n) => {
      if (!n) return false;
      if (n.className !== "Image") return false;
      const a = n.attrs || {};
      return a.pamelaClipart === true;
    });
    blockers.push(...clip);
  });

  // Panels live on PANELS
  const panelLayers = pamelaGetSideLayers(editor, "panels");
  panelLayers.forEach((panels) => {
    const pan = panels.getChildren((n) => {
      if (!n) return false;
      if (n.className !== "Image") return false;
      const a = n.attrs || {};
      return a.pamelaPanel === true;
    });
    blockers.push(...pan);
  });

  return blockers;
}

function pamelaGetLaserImages(editor) {
  if (!editor?.layers) return [];

  const lasers = [];
  const contentLayers = pamelaGetSideLayers(editor, "content");
  contentLayers.forEach((content) => {
    const nodes = content.getChildren((n) => {
      return pamelaIsLaserArtworkNode(n);
    });
    lasers.push(...nodes);
  });
  return lasers;
}

function pamelaIsLaserArtworkNode(node) {
  try {
    if (!node) return false;
    const cls = node.getClassName?.() || node.className || "";
    if (cls !== "Image") return false;

    const attrs = node.attrs || {};
    const name = String(node.name?.() || attrs.name || "").toLowerCase();
    const kind = String(node.getAttr?.("__pamelaKind") || attrs.__pamelaKind || "").toLowerCase();
    const laserKind = String(node.getAttr?.("pamelaLaserKind") || attrs.pamelaLaserKind || "").toLowerCase();
    const libraryType = String(node.getAttr?.("pamelaLaserLibraryType") || attrs.pamelaLaserLibraryType || "").toLowerCase();

    return (
      node.getAttr?.("pamelaLaserEtching") === true ||
      attrs.pamelaLaserEtching === true ||
      kind === "laser" ||
      name.includes("pamela-laser") ||
      laserKind === "spot" ||
      laserKind === "portrait" ||
      laserKind === "scene" ||
      libraryType === "spot" ||
      libraryType === "portrait" ||
      libraryType === "scene"
    );
  } catch (_) {
    return false;
  }
}

function pamelaMarkLaserArtworkNode(node) {
  try {
    if (!pamelaIsLaserArtworkNode(node)) return;
    node.setAttr?.("pamelaLaserEtching", true);
    node.setAttr?.("__pamelaKind", "laser");
    node.setAttr?.("__pamelaSelectable", true);
    node.setAttr?.("pamelaDrag", true);
    node.setAttr?.("pamelaDraggable", true);
  } catch (_) {}
}

function pamelaNodeRect(editor, node) {
  // Use absolute client rect so rotation/scale is respected reliably.
  // (No relativeTo needed because we compare in the same coordinate space.)
  return node.getClientRect({ skipTransform: false });
}

function pamelaTextOverlapsLaser(editor, textNode) {
  const lasers = pamelaGetLaserImages(editor);
  if (!lasers.length) return false;

  const tr = pamelaNodeRect(editor, textNode);
  const textLayer = textNode.getLayer?.() || null;
  for (let i = 0; i < lasers.length; i++) {
    const laser = lasers[i];
    if (!laser || !laser.isVisible()) continue;
    // Front and back use the same coordinate system. Only compare artwork
    // from the selected text's own side/layer so the hidden side cannot
    // accidentally enable or style this text.
    if (textLayer && laser.getLayer?.() !== textLayer) continue;
    if (Konva.Util.haveIntersection(tr, pamelaNodeRect(editor, laser))) return true;
  }
  return false;
}

function pamelaCanReverseSelectedLaserText(editor) {
  const textNode = editor?.selected;
  return !!(
    textNode &&
    pamelaIsTextLikeNode(textNode) &&
    pamelaTextOverlapsLaser(editor, textNode)
  );
}

function pamelaToggleSelectedLaserText(editor) {
  const textNode = editor?.selected;
  if (!editor || !textNode || !pamelaCanReverseSelectedLaserText(editor)) return false;

  const isReversed = textNode.getAttr?.("pamelaLaserTextReversed") === true;
  try { textNode.setAttr("pamelaLaserTextReversed", !isReversed); } catch (_) {}
  try { pamelaUpdateOneTextColorForOverlap(editor, textNode); } catch (_) {}
  try { textNode.getLayer?.()?.batchDraw?.(); } catch (_) {}
  try { editor._historyCommit?.("laser-text-style"); } catch (_) {}
  try { editor._refreshClipButtons?.(); } catch (_) {}
  try { pamelaSyncMobileToolbars(editor); } catch (_) {}
  return true;
}

function pamelaApplyLaserTextOutline(editor, textNode) {
  if (!editor || !textNode || !pamelaIsTextLikeNode(textNode)) return;

  const shouldOutline = pamelaTextOverlapsLaser(editor, textNode);
  if (!shouldOutline) {
    if (textNode.getAttr?.("pamelaLaserAutoOutline") === true) {
      try { textNode.stroke(String(textNode.getAttr("pamelaLaserPrevStroke") || "")); } catch (_) {}
      try { textNode.strokeWidth(Number(textNode.getAttr("pamelaLaserPrevStrokeWidth") || 0) || 0); } catch (_) {}
      try { textNode.strokeEnabled(Boolean(textNode.getAttr("pamelaLaserPrevStrokeEnabled") || false)); } catch (_) {}
      try { textNode.lineJoin(String(textNode.getAttr("pamelaLaserPrevLineJoin") || "miter")); } catch (_) {}
      try { textNode.fillAfterStrokeEnabled(Boolean(textNode.getAttr("pamelaLaserPrevFillAfterStrokeEnabled") || false)); } catch (_) {}
      try { textNode.shadowEnabled(Boolean(textNode.getAttr("pamelaLaserPrevShadowEnabled") || false)); } catch (_) {}
      try { textNode.shadowColor(String(textNode.getAttr("pamelaLaserPrevShadowColor") || "")); } catch (_) {}
      try { textNode.shadowBlur(Number(textNode.getAttr("pamelaLaserPrevShadowBlur") || 0) || 0); } catch (_) {}
      try { textNode.shadowOpacity(Number(textNode.getAttr("pamelaLaserPrevShadowOpacity") || 0) || 0); } catch (_) {}
      try { textNode.shadowOffsetX(Number(textNode.getAttr("pamelaLaserPrevShadowOffsetX") || 0) || 0); } catch (_) {}
      try { textNode.shadowOffsetY(Number(textNode.getAttr("pamelaLaserPrevShadowOffsetY") || 0) || 0); } catch (_) {}
      try { textNode.setAttr("pamelaLaserAutoOutline", false); } catch (_) {}
    }
    return;
  }

  if (textNode.getAttr?.("pamelaLaserAutoOutline") !== true) {
    try { textNode.setAttr("pamelaLaserPrevStroke", String(textNode.stroke?.() || "")); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevStrokeWidth", Number(textNode.strokeWidth?.() || 0) || 0); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevStrokeEnabled", Boolean(textNode.strokeEnabled?.() || false)); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevLineJoin", String(textNode.lineJoin?.() || "miter")); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevFillAfterStrokeEnabled", Boolean(textNode.fillAfterStrokeEnabled?.() || false)); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowEnabled", Boolean(textNode.shadowEnabled?.() || false)); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowColor", String(textNode.shadowColor?.() || "")); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowBlur", Number(textNode.shadowBlur?.() || 0) || 0); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowOpacity", Number(textNode.shadowOpacity?.() || 0) || 0); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowOffsetX", Number(textNode.shadowOffsetX?.() || 0) || 0); } catch (_) {}
    try { textNode.setAttr("pamelaLaserPrevShadowOffsetY", Number(textNode.shadowOffsetY?.() || 0) || 0); } catch (_) {}
  }

  const fill = String(textNode.fill?.() || editor.currentTextFill || "#111").trim();
  const outline = pamelaIsWhiteFill(fill) ? "#111" : "#fff";
  const isSoftWhiteOutline = pamelaIsWhiteFill(outline);
  const fontSize = Math.max(12, Number(textNode.fontSize?.() || 0) || 12);
  const width = isSoftWhiteOutline
    ? Math.max(3, Math.min(9, fontSize * 0.12))
    : Math.max(5, Math.min(14, fontSize * 0.18));

  try { textNode.stroke(outline); } catch (_) {}
  try { textNode.strokeWidth(width); } catch (_) {}
  try { textNode.strokeEnabled(true); } catch (_) {}
  try { textNode.lineJoin("round"); } catch (_) {}
  try { textNode.fillAfterStrokeEnabled(true); } catch (_) {}
  try { textNode.shadowColor(outline); } catch (_) {}
  // Reversed black lettering gets a soft white falloff beyond its crisp
  // inner keyline. The normal black outline stays dense for readability.
  try {
    textNode.shadowBlur(
      isSoftWhiteOutline
        ? Math.max(4, Math.min(8, fontSize * 0.08))
        : Math.max(2, Math.min(5, fontSize * 0.055))
    );
  } catch (_) {}
  try { textNode.shadowOpacity(isSoftWhiteOutline ? 0.5 : 1); } catch (_) {}
  try { textNode.shadowOffsetX(0); } catch (_) {}
  try { textNode.shadowOffsetY(0); } catch (_) {}
  try { textNode.shadowEnabled(true); } catch (_) {}
  try { textNode.setAttr("pamelaLaserAutoOutline", true); } catch (_) {}
}

function pamelaGetOverlapEffectiveSvgMode(editor, textNode) {
  const blockers = pamelaGetBlockerImages(editor);
  if (!blockers.length) return "";

  const tr = pamelaNodeRect(editor, textNode);
  let foundWhiteLike = false;

  for (let i = 0; i < blockers.length; i++) {
    const b = blockers[i];
    if (!b || !b.isVisible()) continue;

    const br = pamelaNodeRect(editor, b);
    if (!Konva.Util.haveIntersection(tr, br)) continue;

    const mode = String(pamelaGetEffectiveSvgModeForNode(editor, b) || "").trim().toLowerCase();

    if (mode === "standard") return "standard";
    if (mode === "white-outline" || mode === "reverse-process") {
      foundWhiteLike = true;
    }
  }

  return foundWhiteLike ? "white-outline" : "";
}

function pamelaUpdateOneTextColorForOverlap(editor, textNode) {
  if (!editor || !textNode || !pamelaIsTextLikeNode(textNode)) return;

  pamelaApplyLaserTextOutline(editor, textNode);

  // Only run this system if the variant default fill is WHITE
  if (!pamelaIsWhiteFill(editor.currentTextFill)) return;

  const defaultFill =
    textNode.getAttr("pamelaVariantDefaultFill") ||
    editor.currentTextFill ||
    "#fff";

  const overlapMode = pamelaGetOverlapEffectiveSvgMode(editor, textNode);
  const reverseLaserText =
    textNode.getAttr?.("pamelaLaserTextReversed") === true &&
    pamelaTextOverlapsLaser(editor, textNode);

  let wanted = defaultFill;

  if (reverseLaserText) {
    wanted = "#111";
  } else if (overlapMode === "standard") {
    wanted = "#111";
  } else if (
    overlapMode === "white-outline" ||
    overlapMode === "reverse-process" ||
    overlapMode === ""
  ) {
    wanted = defaultFill;
  }

  if (textNode.fill() !== wanted) textNode.fill(wanted);
  pamelaApplyLaserTextOutline(editor, textNode);
}

function pamelaUpdateAllTextColorsForOverlap(editor) {
  if (!editor) return;

  // Only the visible side has matching visible artwork. Recoloring the hidden
  // side treats all of its panels/clipart as absent and overwrites its saved
  // black overlap text with white.
  const layer = editor.layers?.content || null;
  if (!layer) return;

  const texts = layer.getChildren((n) => n && pamelaIsTextLikeNode(n));
  texts.forEach((t) => pamelaUpdateOneTextColorForOverlap(editor, t));
  layer.batchDraw();
}
function pamelaMakeRafScheduler(fn) {
  let raf = null;
  return function () {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = null;
      fn();
    });
  };
}


  // =============================
  // SAFE BORDER / NO-PLACE MARGINS (inches)
  // =============================
  // Customer should NOT be able to place text/clipart into the border PNG areas.
  // Your spec:
  // - 0.5" from top and ends
  // - 1.5" from bottom (upright area)
  // We enforce against the design space for now (this matches your PNG layout intent).
  const SAFE_MARGIN_TOP_IN = 0.5;
  const SAFE_MARGIN_SIDE_IN = 0.5;
  const SAFE_MARGIN_BOTTOM_IN = 1.5;

// =============================
// Border Overlay (manifest-based, global)
// =============================
async function pamelaLoadBorderManifestOnce() {
  if (window.__PAMELA_BORDER_MANIFEST_CACHE) return window.__PAMELA_BORDER_MANIFEST_CACHE;
  if (window.__PAMELA_BORDER_MANIFEST_PROMISE) return window.__PAMELA_BORDER_MANIFEST_PROMISE;

  const url = String(window.PAMELA_BORDER_MANIFEST_URL || "").trim();
  if (!url) {
    window.__PAMELA_BORDER_MANIFEST_CACHE = null;
    return null;
  }

  window.__PAMELA_BORDER_MANIFEST_PROMISE = fetch(url, { cache: "no-store" })
    .then(async (r) => {
      if (!r.ok) throw new Error("Border manifest fetch failed: " + r.status);
      return r.json();
    })
    .then((data) => {
      window.__PAMELA_BORDER_MANIFEST_CACHE = data;
      return data;
    })
    .catch((e) => {
      console.error("[Border Manifest] Load error:", e);
      window.__PAMELA_BORDER_MANIFEST_CACHE = null;
      return null;
    });

  return window.__PAMELA_BORDER_MANIFEST_PROMISE;
}

// Flexible picker: supports either an OBJECT map or an ARRAY of entries
async function pickBorderOverlayUrl(handle, variant) {
  const data = await pamelaLoadBorderManifestOnce();
  if (!data) return "";

  const h = String(handle || "").trim().toLowerCase();
  const vid = String(variant?.id || "").trim();
  const vt = String(variant?.title || "").trim().toLowerCase();

  // Case 1: object map like { "bahama blue": "url", "jet black": "url" } or {bahama:"url"...}
  if (data && typeof data === "object" && !Array.isArray(data)) {
    // try direct title key
    if (data[vt]) return String(data[vt] || "");
    // try normalized color keys
    if (vt.includes("bahama") && data.bahama) return String(data.bahama || "");
    if ((vt.includes("georgia") || vt.includes("gray") || vt.includes("grey")) && data.georgia) return String(data.georgia || "");
    if ((vt.includes("jet") || vt.includes("black")) && data.jet) return String(data.jet || "");
    return "";
  }

  // Case 2: array of entries
  if (Array.isArray(data)) {
    const norm = (s) => String(s || "").trim().toLowerCase();

    // Prefer: match by handle + variantId
    let hit =
      data.find((it) => norm(it.handle || it.product || it.product_handle) === h && String(it.variantId || it.variant_id || "") === vid) ||
      null;

    // Next: match by handle + variantTitle (exact or contains)
    if (!hit) {
      hit =
        data.find((it) => {
          const ih = norm(it.handle || it.product || it.product_handle);
          if (ih && ih !== h) return false;

          const t = norm(it.variant || it.variantTitle || it.variant_title || it.title);
          if (!t) return false;

          return t === vt || vt.includes(t) || t.includes(vt);
        }) || null;
    }

    // Last: fall back to color keywords (global defaults)
    if (!hit) {
      hit =
        data.find((it) => vt.includes("bahama") && norm(it.key) === "bahama") ||
        data.find((it) => (vt.includes("georgia") || vt.includes("gray") || vt.includes("grey")) && norm(it.key) === "georgia") ||
        data.find((it) => (vt.includes("jet") || vt.includes("black")) && norm(it.key) === "jet") ||
        null;
    }

    const url = hit ? (hit.url || hit.src || hit.image || "") : "";
    return String(url || "");
  }

  return "";
}


  // =============================
  // Field constraints (inches)
  // =============================
  const FIELD_SPECS = {
    lastname: { label: "Last Name", sizeIn: 3.0, resizable: false, max: { companion: 1, single: 0 } },

    name: {
      label: "Name",
      sizeIn: 1.25,
      resizable: true,
      minIn: 1.25,
      maxIn: 1.75,
      stepIn: 0.25,
      max: { companion: 4, single: 2 },
    },

    date: {
      label: "Date",
      sizeIn: 1.0,
      resizable: true,
      minIn: 1.0,
      maxIn: 1.25,
      stepIn: 0.25,
      max: { companion: 4, single: 2 },
    },

    wedding: {
      label: "Wedding Date",
      sizeIn: 0.75,
      resizable: false,
      max: { companion: 1, single: 0 },
      lineLimit: 2,
      lineHeight: 1.45,
      wordLimit: 6,
    },

    verse: {
      label: "Verse",
      sizeIn: 0.75,
      resizable: false,
      max: { companion: 6, single: 5 },
      charLimit: 100,
      singleLine: true,
    },
  };

  // =============================
  // Helpers
  // =============================
  const rafThrottle = (fn) => {
    let ticking = false;
    return (...args) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn(...args);
      });
    };
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const pxFromIn = (inches) => inches * DPI;
  const inFromPx = (px) => px / DPI;
  const pamelaCapRatioCache = new Map();
  const pamelaIsTextLikeNode = (node) => {
    const cls = node && (node.getClassName ? node.getClassName() : node.className);
    return cls === "Text" || cls === "TextPath";
  };

  function pamelaFontForCanvas(fontFamily, fontSizePx) {
    const fam = String(fontFamily || "MDRoman").replace(/"/g, '\\"');
    return `${fontSizePx}px "${fam}", serif`;
  }

  function pamelaCapHeightRatio(fontFamily) {
    const key = String(fontFamily || "MDRoman");
    if (pamelaCapRatioCache.has(key)) return pamelaCapRatioCache.get(key);

    let ratio = 1;
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const testSize = 200;
      ctx.font = pamelaFontForCanvas(key, testSize);
      const metrics = ctx.measureText("TLI");
      const ascent = Number(metrics.actualBoundingBoxAscent || 0);
      if (Number.isFinite(ascent) && ascent > 0) ratio = ascent / testSize;
    } catch (_) {}

    pamelaCapRatioCache.set(key, ratio);
    return ratio;
  }

  function pamelaFontSizePxForCapHeight(fontFamily, capHeightIn) {
    const ratio = pamelaCapHeightRatio(fontFamily);
    return pxFromIn(capHeightIn) / (ratio > 0 ? ratio : 1);
  }

  function pamelaCapHeightInForTextNode(node) {
    const fontFamily = typeof node?.fontFamily === "function" ? node.fontFamily() : node?.attrs?.fontFamily;
    const fontSize = Number(typeof node?.fontSize === "function" ? node.fontSize() : node?.attrs?.fontSize || 0);
    return inFromPx(fontSize * pamelaCapHeightRatio(fontFamily));
  }

  function pamelaApplyCapHeightInToTextNode(node, capHeightIn) {
    const fontFamily = typeof node?.fontFamily === "function" ? node.fontFamily() : node?.attrs?.fontFamily;
    node.fontSize(pamelaFontSizePxForCapHeight(fontFamily, capHeightIn));
  }

  function pamelaInferLayoutFromText(value) {
    const text = String(value || "").toLowerCase();
    if (!text) return "";

    const hasCompanionCue =
      /\bcompanion\b/.test(text) ||
      /\bdouble\b/.test(text) ||
      /\btwo[-\s]?person\b/.test(text) ||
      /\bfor[-\s]?two\b/.test(text) ||
      /\b2[-\s]?person\b/.test(text);
    if (hasCompanionCue) return "companion";

    const hasSingleCue =
      /\bsingle\b/.test(text) ||
      /\bone[-\s]?person\b/.test(text) ||
      /\bfor[-\s]?one\b/.test(text);
    if (hasSingleCue) return "single";

    const hasSingleSlantCue =
      /\bslants?\b/.test(text) ||
      /\bslanted\b/.test(text) ||
      /\bslant[-\s]?marker\b/.test(text);
    if (hasSingleSlantCue) return "single";

    const hasMarkerCue =
      text.includes("grass-level") ||
      text.includes("grass level") ||
      text.includes("flat-marker") ||
      text.includes("flat marker") ||
      /\bmarker\b/.test(text);
    const sizeMatch =
      text.match(/(\d+(?:\.\d+)?)\s*(?:long|l)?\s*[-\s]*x[-\s]*(\d+(?:\.\d+)?)\s*(?:wide|w)?/) ||
      text.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
    const longIn = sizeMatch ? Number(sizeMatch[1]) : 0;
    if (hasMarkerCue && longIn > 0 && longIn <= 36) return "single";

    return "";
  }

  function pamelaGetEditorLayoutKind(editor) {
    try {
      const v = String(
        editor?.layout ||
        editor?.kind ||
        editor?.productLayout ||
        window.__PAMELA_CURRENT_LAYOUT__ ||
        window.PAMELA_CURRENT_LAYOUT ||
        ""
      ).toLowerCase();
      if (v.includes("single")) return "single";
      if (v.includes("comp")) return "companion";
    } catch (_) {}

    try {
      const p = new URLSearchParams(location.search);
      const layout = String(p.get("layout") || "").toLowerCase();
      if (layout.includes("single")) return "single";
      if (layout.includes("comp")) return "companion";

      const productContext = [
        p.get("product") || "",
        p.get("style") || "",
        document.querySelector(".product__title, .product-title, h1")?.textContent || ""
      ].join(" ");
      const inferred = pamelaInferLayoutFromText(productContext);
      if (inferred) return inferred;
    } catch (_) {}

    return "companion";
  }

  function pamelaIsSingleBackLastNameNode(node, editor) {
    const nm = (typeof node?.name === "function") ? String(node.name() || "") : String(node?.attrs?.name || "");
    const id = (typeof node?.id === "function") ? String(node.id() || "") : String(node?.attrs?.id || "");
    if (nm !== "__cm_back_lastname" && id !== "__cm_back_lastname") return false;
    const layout = pamelaGetEditorLayoutKind(editor);
    if (layout === "single") return true;
    if (layout === "companion") return false;
    const marker = String(node.getAttr?.("__cmBackLastNameLayout") || "").toLowerCase();
    return marker === "single";
  }

  function pamelaNormalizeSingleBackLastNameDefault(node, editor) {
    if (!pamelaIsSingleBackLastNameNode(node, editor)) return false;
    if (node.getAttr?.("__cmBackLastNameSizeTouched") === true) return false;

    const curIn = pamelaCapHeightInForTextNode(node);
    if (curIn >= PAMELA_SINGLE_BACK_LASTNAME_MAX_IN - 0.001) return false;

    pamelaApplyCapHeightInToTextNode(node, PAMELA_SINGLE_BACK_LASTNAME_MAX_IN);
    try { node.setAttr("__cmBackLastNameLayout", "single"); } catch (_) {}
    try { node.setAttr("__cmBackLastNameInitialized", true); } catch (_) {}
    return true;
  }

  window.pamelaNormalizeSingleBackLastNameDefault = pamelaNormalizeSingleBackLastNameDefault;
  window.pamelaApplyCapHeightInToTextNode = pamelaApplyCapHeightInToTextNode;

  const pamelaLimitWords = (value, maxWords, opts = {}) => {
    const limit = Number(maxWords);
    if (!Number.isFinite(limit) || limit <= 0) return String(value ?? "");

    const tokens = String(value ?? "").match(/\S+|\s+/g) || [];
    let words = 0;
    let out = "";

    for (const token of tokens) {
      if (/\S/.test(token)) {
        if (words >= limit) break;
        out += token;
        words += 1;
      } else if (words < limit) {
        out += token;
      }
    }

    return opts?.trimTrailing === false ? out : out.replace(/[ \t]+$/g, "");
  };

  const PAMELA_DATE_HELPER_SEEN_KEY = "pamela_date_format_helper_seen_v1";
  const PAMELA_DATE_MONTHS_FULL = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];
  const PAMELA_DATE_MONTHS_ABBR = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUNE",
    "JUL",
    "JULY",
    "AUG",
    "SEP",
    "SEPT",
    "OCT",
    "NOV",
    "DEC",
  ];

  function pamelaEscapeRegex(value) {
    return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  const PAMELA_DATE_MONTH_FULL_RE = PAMELA_DATE_MONTHS_FULL.map(pamelaEscapeRegex).join("|");
  const PAMELA_DATE_MONTH_ABBR_RE = PAMELA_DATE_MONTHS_ABBR.map(pamelaEscapeRegex).join("|");

  function pamelaIsDateTextNode(node) {
    return !!(
      node &&
      node.className === "Text" &&
      String(node.attrs?.pamelaFieldType || "") === "date"
    );
  }

  function pamelaIsSingleLayout(editor) {
    return String(editor?.layout || "").toLowerCase() === "single";
  }

  function pamelaNormalizeDateText(raw, opts = {}) {
    const allowStacked = !!opts.allowStacked;
    let v = String(raw ?? "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/[^A-Za-z0-9.,\n ]+/g, "")
      .replace(/[ \t]+/g, " ")
      .replace(/[ \t]*\n[ \t]*/g, "\n");

    if (!allowStacked) {
      return v.replace(/\n+/g, " ").slice(0, 22).trimStart();
    }

    const lines = v.split("\n").slice(0, 2);
    if (lines.length > 1) {
      lines[0] = lines[0].slice(0, 12).trimStart();
      lines[1] = lines[1].replace(/\D+/g, "").slice(0, 4);
      return lines.join("\n");
    }

    return lines[0].slice(0, 22).trimStart();
  }

  function pamelaClassifyDateText(value, opts = {}) {
    const allowStacked = !!opts.allowStacked;
    const raw = String(value ?? "").trim();
    if (
      !raw ||
      /^(date|date of birth|date of death|birth date|death date)$/i.test(raw)
    ) {
      return "placeholder";
    }

    const dateLine = "([1-9]|[12][0-9]|3[01])";
    const year = "\\d{4}";
    const fullSingle = new RegExp(`^(?:${PAMELA_DATE_MONTH_FULL_RE})\\s+${dateLine},\\s*${year}$`, "i");
    const abbrSingle = new RegExp(`^(?:${PAMELA_DATE_MONTH_ABBR_RE})\\.?\\s+${dateLine},\\s*${year}$`, "i");
    const stacked = new RegExp(`^(?:${PAMELA_DATE_MONTH_ABBR_RE})\\.?\\s+${dateLine},\\n${year}$`, "i");

    if (fullSingle.test(raw) || abbrSingle.test(raw)) return "single";
    if (allowStacked && stacked.test(raw)) return "stacked";
    return "";
  }

  function pamelaDateIsStacked(node) {
    if (!pamelaIsDateTextNode(node)) return false;
    const saved = String(node.getAttr?.("pamelaDateFormat") || "");
    return saved === "stacked" || String(node.text?.() || "").includes("\n");
  }

  function pamelaApplyDateLineSpacing(node) {
    if (!pamelaIsDateTextNode(node) || typeof node.lineHeight !== "function") return;

    if (!pamelaDateIsStacked(node)) {
      node.lineHeight(1);
      return;
    }

    const fontSize = Math.max(1, Number(node.fontSize?.() || pxFromIn(1)));
    const gapPx = pxFromIn(0.375);
    node.lineHeight(1 + gapPx / fontSize);
  }

  function pamelaShouldShowDateHelper(editor) {
    if (!pamelaIsSingleLayout(editor)) return false;
    try {
      return window.localStorage?.getItem(PAMELA_DATE_HELPER_SEEN_KEY) !== "1";
    } catch (_) {
      return !window.__pamelaDateHelperSeen;
    }
  }

  function pamelaMarkDateHelperSeen() {
    window.__pamelaDateHelperSeen = true;
    try { window.localStorage?.setItem(PAMELA_DATE_HELPER_SEEN_KEY, "1"); } catch (_) {}
  }

  function pamelaShowDateFormatHelper() {
    return new Promise((resolve) => {
      const style = document.createElement("style");
      style.textContent = `
        #pamela-date-format-helper-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          background: rgba(17, 17, 17, 0.28);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(8px, 2vw, 18px);
        }
        #pamela-date-format-helper-modal {
          width: min(900px, calc(100vw - 24px));
          max-height: calc(100vh - 24px);
          overflow: hidden;
          box-sizing: border-box;
          background: #f5f0eb;
          color: #111;
          border: clamp(3px, 0.45vw, 4px) solid #765a79;
          border-radius: clamp(18px, 4vw, 34px);
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
          padding: clamp(14px, 3vw, 28px) clamp(16px, 4vw, 42px) clamp(16px, 3vw, 30px);
          text-align: center;
        }
        #pamela-date-format-helper-title {
          font: 700 clamp(22px, 3.6vw, 36px)/1.12 Georgia, 'Times New Roman', serif;
          margin: 0 0 clamp(6px, 1vw, 10px);
        }
        #pamela-date-format-helper-message {
          font: italic clamp(14px, 2.15vw, 21px)/1.22 Georgia, 'Times New Roman', serif;
          max-width: 720px;
          margin: 0 auto clamp(16px, 2.4vw, 26px);
        }
        #pamela-date-format-helper-message .pamela-date-helper-message-line {
          display: block;
        }
        #pamela-date-format-helper-examples {
          display: grid;
          justify-items: center;
          gap: clamp(5px, 1.2vw, 11px);
          margin: 0 auto clamp(12px, 2vw, 22px);
          font-family: MDRoman, Georgia, serif;
          letter-spacing: 0;
        }
        #pamela-date-format-helper-examples .pamela-date-helper-caption {
          font: italic clamp(12px, 1.75vw, 19px)/1.18 Georgia, 'Times New Roman', serif;
          margin: clamp(3px, 0.7vw, 7px) 0 clamp(4px, 0.8vw, 8px);
          max-width: 100%;
        }
        #pamela-date-format-helper-examples .pamela-date-helper-sample {
          max-width: 100%;
          text-align: center;
          white-space: nowrap;
          font-size: clamp(30px, 5.3vw, 62px);
          line-height: 0.98;
        }
        #pamela-date-format-helper-examples .pamela-date-helper-sample-stacked {
          line-height: 1.22;
        }
        #pamela-date-format-helper-examples .pamela-date-helper-sample-long {
          font-size: clamp(24px, 4.7vw, 54px);
        }
        #pamela-date-format-helper-button {
          border: 0;
          border-radius: 8px;
          background: #111;
          color: #fff;
          font: 400 clamp(14px, 1.9vw, 18px)/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;
          padding: clamp(10px, 1.8vw, 14px) clamp(20px, 3vw, 28px);
          cursor: pointer;
          min-width: clamp(120px, 18vw, 150px);
        }
        @media (max-width: 520px) {
          #pamela-date-format-helper-modal {
            width: min(430px, calc(100vw - 16px));
            max-height: calc(100vh - 16px);
            padding: 12px 14px 14px;
          }
          #pamela-date-format-helper-message {
            font-size: clamp(12.5px, 3.65vw, 14px);
            line-height: 1.2;
            max-width: none;
            margin-bottom: 13px;
          }
          #pamela-date-format-helper-message .pamela-date-helper-message-line {
            white-space: nowrap;
          }
          #pamela-date-format-helper-examples {
            width: 100%;
            gap: 5px;
            margin-bottom: 10px;
            justify-items: center;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample {
            width: auto;
            box-sizing: border-box;
            display: block;
            justify-self: center;
            margin-left: auto;
            margin-right: auto;
            text-align: center;
            font-size: clamp(13px, 5.1vw, 18px);
            line-height: 1;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-caption {
            width: auto;
            box-sizing: border-box;
            display: block;
            justify-self: center;
            margin-left: auto;
            margin-right: auto;
            text-align: center;
            margin-bottom: 7px;
            padding: 0;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample-stacked {
            line-height: 1.32;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample-long {
            font-size: clamp(13px, 5.1vw, 18px);
          }
          #pamela-date-format-helper-examples .pamela-date-helper-caption .pamela-date-helper-caption-line {
            display: block;
            width: auto;
            text-align: center;
          }
        }
        @media (max-height: 680px) {
          #pamela-date-format-helper-modal {
            padding-top: 10px;
            padding-bottom: 10px;
          }
          #pamela-date-format-helper-title {
            font-size: clamp(20px, 3vw, 30px);
            margin-bottom: 5px;
          }
          #pamela-date-format-helper-message {
            font-size: clamp(13px, 1.8vw, 18px);
            margin-bottom: 8px;
          }
          #pamela-date-format-helper-examples {
            gap: 3px;
            margin-bottom: 9px;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-caption {
            font-size: clamp(11px, 1.5vw, 16px);
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample {
            font-size: clamp(24px, 4.8vw, 48px);
            line-height: 0.94;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample-stacked {
            line-height: 1.16;
          }
          #pamela-date-format-helper-examples .pamela-date-helper-sample-long {
            font-size: clamp(20px, 4vw, 40px);
          }
          #pamela-date-format-helper-button {
            padding-top: 9px;
            padding-bottom: 9px;
          }
        }
      `;

      const overlay = document.createElement("div");
      overlay.id = "pamela-date-format-helper-overlay";

      const modal = document.createElement("div");
      modal.id = "pamela-date-format-helper-modal";
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      modal.setAttribute("aria-label", "Date Formats");

      const title = document.createElement("div");
      title.id = "pamela-date-format-helper-title";
      title.textContent = "Date Formats";

      const message = document.createElement("div");
      message.id = "pamela-date-format-helper-message";
      message.innerHTML =
        '<span class="pamela-date-helper-message-line">Dates can be formatted in one of 3 ways.</span>' +
        '<span class="pamela-date-helper-message-line">Other text is not allowed in the date text box.</span>';

      const examples = document.createElement("div");
      examples.id = "pamela-date-format-helper-examples";

      const makeCaption = (text) => {
        const caption = document.createElement("div");
        caption.className = "pamela-date-helper-caption";
        const lines = Array.isArray(text) ? text : [text];
        lines.forEach((line) => {
          const row = document.createElement("span");
          row.className = "pamela-date-helper-caption-line";
          row.textContent = line;
          caption.appendChild(row);
        });
        return caption;
      };

      const makeSample = (lines, opts = {}) => {
        const sample = document.createElement("div");
        sample.className =
          "pamela-date-helper-sample" +
          (opts.long ? " pamela-date-helper-sample-long" : "") +
          (opts.stacked ? " pamela-date-helper-sample-stacked" : "");

        lines.forEach((line) => {
          const row = document.createElement("div");
          row.textContent = line;
          sample.appendChild(row);
        });

        return sample;
      };

      examples.appendChild(makeCaption("Most common"));
      examples.appendChild(makeSample(["SEPT. 24, 2022"]));
      examples.appendChild(makeCaption("Used only when space allows"));
      examples.appendChild(makeSample(["SEPTEMBER 24, 2022"], { long: true }));
      examples.appendChild(makeCaption([
        "Used when portraits or art is between dates",
        "(Birth left - Death right)",
      ]));
      examples.appendChild(makeSample(["SEPT. 24,", "2022"], { stacked: true }));

      const button = document.createElement("button");
      button.type = "button";
      button.id = "pamela-date-format-helper-button";
      button.textContent = "Continue";

      let keyHandler = null;
      const close = () => {
        pamelaMarkDateHelperSeen();
        if (keyHandler) document.removeEventListener("keydown", keyHandler);
        try { document.head.removeChild(style); } catch (_) {}
        try { document.body.removeChild(overlay); } catch (_) {}
        resolve();
      };

      button.addEventListener("click", close);
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });
      keyHandler = function onKey(e) {
        if (e.key !== "Escape") return;
        close();
      };
      document.addEventListener("keydown", keyHandler);

      modal.appendChild(title);
      modal.appendChild(message);
      modal.appendChild(examples);
      modal.appendChild(button);
      overlay.appendChild(modal);
      document.head.appendChild(style);
      document.body.appendChild(overlay);
      button.focus({ preventScroll: true });
    });
  }

  const toAbsShopifyUrl = (url) => {
    if (!url) return "";
    const s = String(url).trim();
    if (!s) return "";
    if (s.startsWith("//")) return window.location.protocol + s;
    return s;
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const url = toAbsShopifyUrl(src);
      if (!url) return resolve(null);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image: " + url));
      img.src = url;
    });

  // Exposed globally so code defined outside this closure (e.g. the laser
  // Fit to Stone modal) can load the foreground overlay without duplicating
  // this loader or risking a cross-closure ReferenceError.
  window.__PAMELA_LOAD_IMAGE__ = loadImage;

  function pamelaDetectLetteringRectFromImage(img) {
    try {
      if (!img) return null;

      const naturalW = Number(img.naturalWidth || img.width || 0);
      const naturalH = Number(img.naturalHeight || img.height || 0);
      if (!naturalW || !naturalH) return null;

      const maxSample = 220;
      const scale = Math.min(1, maxSample / Math.max(naturalW, naturalH));
      const sw = Math.max(1, Math.round(naturalW * scale));
      const sh = Math.max(1, Math.round(naturalH * scale));
      const scanBottom = Math.max(1, Math.round(sh * 0.74));

      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return null;

      ctx.clearRect(0, 0, sw, sh);
      ctx.drawImage(img, 0, 0, sw, sh);

      const data = ctx.getImageData(0, 0, sw, scanBottom).data;
      const total = sw * scanBottom;
      const solid = new Uint8Array(total);

      for (let i = 0, p = 0; i < total; i++, p += 4) {
        if (data[p + 3] > 24) solid[i] = 1;
      }

      const seen = new Uint8Array(total);
      const queue = new Int32Array(total);
      let best = null;

      for (let i = 0; i < total; i++) {
        if (!solid[i] || seen[i]) continue;

        let head = 0;
        let tail = 0;
        let count = 0;
        let minX = sw;
        let maxX = 0;
        let minY = scanBottom;
        let maxY = 0;

        seen[i] = 1;
        queue[tail++] = i;

        while (head < tail) {
          const idx = queue[head++];
          const x = idx % sw;
          const y = (idx - x) / sw;

          count++;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;

          const neighbors = [
            x > 0 ? idx - 1 : -1,
            x < sw - 1 ? idx + 1 : -1,
            y > 0 ? idx - sw : -1,
            y < scanBottom - 1 ? idx + sw : -1
          ];

          for (const next of neighbors) {
            if (next < 0 || seen[next] || !solid[next]) continue;
            seen[next] = 1;
            queue[tail++] = next;
          }
        }

        const componentW = maxX - minX + 1;
        const componentH = maxY - minY + 1;
        if (componentW < sw * 0.08 || componentH < scanBottom * 0.18) continue;
        if (!best || count > best.count) {
          best = { count, minX, maxX, minY, maxY };
        }
      }

      if (!best) return null;

      const inv = 1 / scale;
      return {
        left: Math.max(0, Math.round(best.minX * inv)),
        top: Math.max(0, Math.round(best.minY * inv)),
        width: Math.min(naturalW, Math.round((best.maxX - best.minX + 1) * inv)),
        height: Math.min(naturalH, Math.round((best.maxY - best.minY + 1) * inv))
      };
    } catch (e) {
      console.warn("[LetteringRect] Could not detect upright area:", e);
      return null;
    }
  }

  function pamelaGetImageNodeDrawRect(node) {
    try {
      if (!node) return null;
      const x = Number(node.x?.() || 0);
      const y = Number(node.y?.() || 0);
      const w = Number(node.width?.() || 0);
      const h = Number(node.height?.() || 0);
      const sx = Number(node.scaleX?.() || 1) || 1;
      const sy = Number(node.scaleY?.() || 1) || 1;
      const x2 = x + w * sx;
      const y2 = y + h * sy;
      const left = Math.min(x, x2);
      const top = Math.min(y, y2);
      const right = Math.max(x, x2);
      const bottom = Math.max(y, y2);
      return { left, top, width: right - left, height: bottom - top, right, bottom };
    } catch (_) {
      return null;
    }
  }

  function pamelaGetLetteringRectForBackground(node) {
    try {
      const local = node?.getAttr?.("pamelaLetteringRect") || null;
      if (!local) return null;

      const x = Number(node.x?.() || 0);
      const y = Number(node.y?.() || 0);
      const sx = Number(node.scaleX?.() || 1) || 1;
      const sy = Number(node.scaleY?.() || 1) || 1;

      const x1 = x + Number(local.left || 0) * sx;
      const x2 = x + (Number(local.left || 0) + Number(local.width || 0)) * sx;
      const y1 = y + Number(local.top || 0) * sy;
      const y2 = y + (Number(local.top || 0) + Number(local.height || 0)) * sy;

      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const right = Math.max(x1, x2);
      const bottom = Math.max(y1, y2);
      if (right <= left || bottom <= top) return null;

      return { left, top, width: right - left, height: bottom - top, right, bottom };
    } catch (_) {
      return null;
    }
  }

  const waitForKonva = (timeoutMs = 8000) =>
    new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        if (window.Konva && window.Konva.Stage) return resolve(true);
        if (Date.now() - start > timeoutMs) return reject(new Error("Konva not found on window"));
        setTimeout(tick, 50);
      };
      tick();
    });

  const formatMoneyFromCents = (cents) => {
    const n = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
    
  };
// =========================================
// FORCE-REGISTER PAMELA WEBFONTS FOR CANVAS
// =========================================
async function pamelaRegisterFontFace(fontKey) {
  const reg = (window.PAMELA_FONT_REGISTRY || []);
  const f = reg.find(x => x && x.key === fontKey);
  if (!f) return;
  window.__pamelaRegisteredFontFaces = window.__pamelaRegisteredFontFaces || new Set();
  if (window.__pamelaRegisteredFontFaces.has(fontKey)) return;

  const sources = [];
  const woff2Url = toAbsShopifyUrl(f.woff2_url || f.woff2Url || "");
  const woffUrl = toAbsShopifyUrl(f.woff_url || f.woffUrl || "");

  if (woff2Url) sources.push(`url("${woff2Url}") format("woff2")`);
  if (woffUrl)  sources.push(`url("${woffUrl}") format("woff")`);
  if (!sources.length) return;

  try {
    const face = new FontFace(
      fontKey,
      sources.join(", "),
      {
        style: String(f.style || "normal"),
        weight: String(f.weight || 400)
      }
    );
    document.fonts.add(face);
    await face.load();
    await document.fonts.ready;
    window.__pamelaRegisteredFontFaces.add(fontKey);
  } catch (e) {
    console.warn("CM font load failed:", fontKey, e);
  }
}

async function pamelaRegisterAllFontFaces() {
  const reg = (window.PAMELA_FONT_REGISTRY || []);
  for (const f of reg) {
    if (!f?.key) continue;
    await pamelaRegisterFontFace(f.key);
  }
}

function pamelaGetAllContentTextNodes(editor) {
  if (!editor?.layers) return [];

  const seen = new Set();
  const out = [];
  const pushLayerTexts = (layer) => {
    if (!layer || seen.has(layer)) return;
    seen.add(layer);
    const nodes = layer.getChildren?.((n) => n && n.className === "Text") || [];
    out.push(...nodes);
  };

  pushLayerTexts(editor.layers.content);
  pushLayerTexts(editor.layers?.sides?.front?.content);
  pushLayerTexts(editor.layers?.sides?.back?.content);

  return out;
}

function pamelaRefreshAllTextMetrics(editor) {
  const texts = pamelaGetAllContentTextNodes(editor);
  if (!texts.length) return;

  texts.forEach((n) => {
    try { pamelaFixTextMetrics(n); } catch (_) {}
  });

  try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
  try { editor.layers?.sides?.front?.content?.batchDraw?.(); } catch (_) {}
  try { editor.layers?.sides?.back?.content?.batchDraw?.(); } catch (_) {}
  try { if (editor.selected) editor.selectNode(editor.selected); } catch (_) {}
}

function pamelaEnsureNodeFontReady(editor, node, delayMs = 0) {
  const run = async () => {
    try {
      if (!node || node.destroyed?.()) return;
      const fontKey = String(
        typeof node.fontFamily === "function" ? (node.fontFamily() || "") : (node.attrs?.fontFamily || "")
      ).trim();
      if (!fontKey) return;

      await pamelaRegisterFontFace(fontKey);
      if (document.fonts && document.fonts.load) {
        try {
          await document.fonts.load(`16px "${fontKey}"`);
          await document.fonts.ready;
        } catch (_) {}
      }

      if (!node || node.destroyed?.()) return;
      pamelaFixTextMetrics(node);
      pamelaRefreshAllTextMetrics(editor);
    } catch (_) {}
  };

  if (delayMs > 0) {
    setTimeout(run, delayMs);
  } else {
    Promise.resolve().then(run);
  }
}

// =============================
// Library drawer helpers (Panels vs Clipart)
// =============================
function pamelaIsOpen(el) {
  if (!el) return false;

  // Works for the most common patterns:
  // - display none/block
  // - hidden attribute
  // - aria-hidden true/false
  if (el.hasAttribute("hidden")) return false;

  const aria = el.getAttribute("aria-hidden");
  if (aria === "true") return false;
  if (aria === "false") return true;

  // fallback to display
  return el.style.display !== "none";
}

function pamelaClose(el) {
  if (!el) return;
  el.setAttribute("aria-hidden", "true");
  el.setAttribute("hidden", "");
  el.style.display = "none";
}

function pamelaOpen(el) {
  if (!el) return;
  el.setAttribute("aria-hidden", "false");
  el.removeAttribute("hidden");
  el.style.display = "block";
}

  // =============================
  // SAFE AREA math (world coords)
  // =============================
  function pamelaGetSafeRect(designW, designH) {
    const left = SAFE_MARGIN_SIDE_IN * DPI;
    const top = SAFE_MARGIN_TOP_IN * DPI;
    const right = designW - SAFE_MARGIN_SIDE_IN * DPI;
    const bottom = designH - SAFE_MARGIN_BOTTOM_IN * DPI;

    return { left, top, right, bottom };
  }
// =============================
// Initial landing offsets / slot centers
// =============================

// Ã¢Å“â€¦ Adjust this one number any time to move Last Name landing up/down.
const LASTNAME_Y_OFFSET_PX = 60; // <-- increase to land LOWER, decrease to land HIGHER

function pamelaGetCenters(editor) {
  const w = editor.state?.design?.width || DESIGN_PX.w;
  const h = editor.state?.design?.height || DESIGN_PX.h;

  const safe = pamelaGetSafeRect(w, h);
  const letteringRect =
    editor?.layout === "single"
      ? pamelaGetLetteringRectForBackground(editor?.nodes?.bg)
      : null;
  const fullCenter = letteringRect
    ? letteringRect.left + letteringRect.width / 2
    : w / 2;

  // "Tablet edges" = safe rect edges (respects your border/safe margins)
  const leftCenter = (safe.left + fullCenter) / 2;
  const rightCenter = (safe.right + fullCenter) / 2;

  return { w, h, safe, fullCenter, leftCenter, rightCenter };
}

// Slot patterns (by index)
const NAME_DATE_SIDES_COMPANION = ["left", "right", "left", "right"];
const VERSE_SIDES_ANY = ["center", "left", "right"];

// Choose the first missing slot index (fills gaps after deletes)
function pamelaNextSlotIndex(editor, fieldType, maxSlots) {
  const layer = editor.layers?.content;
  if (!layer) return 0;

  const used = new Set(
    layer
      .getChildren((n) => n && n.className === "Text" && n.attrs?.pamelaFieldType === fieldType)
      .map((n) => Number(n.getAttr("pamelaSlotIndex")))
      .filter((v) => Number.isFinite(v))
  );

  for (let i = 0; i < maxSlots; i++) {
    if (!used.has(i)) return i;
  }
  return maxSlots - 1;
}


function pamelaClampNodeIntoSafeRect(editor, node) {
  try {
    if (!editor || !node) return;

    const layer = node.getLayer ? node.getLayer() : null;
    const fallback = editor.layers?.content;
    const useLayer = layer || fallback;
    if (!useLayer) return;

    const w = editor.state?.design?.width || DESIGN_PX.w;
    const h = editor.state?.design?.height || DESIGN_PX.h;
    const safe = pamelaGetSafeRect(w, h);

    // Use client rect so rotation/scale is respected
    const r = node.getClientRect({
      relativeTo: useLayer, // Ã¢Å“â€¦ FIXED (was uselayer)
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

    let dx = 0;
    let dy = 0;

    if (r.x < safe.left) dx = safe.left - r.x;
    if (r.x + r.width > safe.right) dx = (safe.right - (r.x + r.width));

    if (r.y < safe.top) dy = safe.top - r.y;
    if (r.y + r.height > safe.bottom) dy = (safe.bottom - (r.y + r.height));

    if (dx || dy) {
      node.x(node.x() + dx);
      node.y(node.y() + dy);
    }
  } catch (e) {
    // Keep silent so a clamp error can never break dragging again
  }
}

function pamelaGetTabletLandingRect(editor) {
  try {
    const w = editor?.state?.design?.width || DESIGN_PX.w;
    const h = editor?.state?.design?.height || DESIGN_PX.h;
    const canvasSafe = pamelaGetSafeRect(w, h);
    const bg = editor?.nodes?.bg;
    const rect =
      pamelaGetLetteringRectForBackground(bg) ||
      pamelaGetImageNodeDrawRect(bg) ||
      {
        left: canvasSafe.left,
        top: canvasSafe.top,
        right: canvasSafe.right,
        bottom: canvasSafe.bottom,
        width: canvasSafe.right - canvasSafe.left,
        height: canvasSafe.bottom - canvasSafe.top,
      };

    if (!Number.isFinite(rect.left) || !Number.isFinite(rect.right) || rect.right <= rect.left) return null;
    if (!Number.isFinite(rect.top) || !Number.isFinite(rect.bottom) || rect.bottom <= rect.top) return null;
    return rect;
  } catch (_) {
    return null;
  }
}

function pamelaGetLaserPlacementRect(editor) {
  try {
    const w = editor?.state?.design?.width || DESIGN_PX.w;
    const h = editor?.state?.design?.height || DESIGN_PX.h;
    const canvasSafe = pamelaGetSafeRect(w, h);
    const bg = editor?.nodes?.bg;
    const bgRect = pamelaGetImageNodeDrawRect(bg);
    const detectedFace = pamelaGetLetteringRectForBackground(bg);
    const candidate = detectedFace || bgRect || null;

    const fallback = {
      left: canvasSafe.left,
      top: canvasSafe.top,
      right: canvasSafe.right,
      bottom: canvasSafe.bottom,
      width: canvasSafe.right - canvasSafe.left,
      height: canvasSafe.bottom - canvasSafe.top
    };

    const rect = candidate || fallback;
    const left = Math.max(canvasSafe.left, Number(rect.left || 0));
    const top = Math.max(canvasSafe.top, Number(rect.top || 0));
    const right = Math.min(canvasSafe.right, Number(rect.right ?? (Number(rect.left || 0) + Number(rect.width || 0))));
    const bottom = Math.min(canvasSafe.bottom, Number(rect.bottom ?? (Number(rect.top || 0) + Number(rect.height || 0))));

    if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) return fallback;
    if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= top) return fallback;

    return { left, top, right, bottom, width: right - left, height: bottom - top };
  } catch (_) {
    const w = editor?.state?.design?.width || DESIGN_PX.w;
    const h = editor?.state?.design?.height || DESIGN_PX.h;
    const safe = pamelaGetSafeRect(w, h);
    return {
      left: safe.left,
      top: safe.top,
      right: safe.right,
      bottom: safe.bottom,
      width: safe.right - safe.left,
      height: safe.bottom - safe.top
    };
  }
}

function pamelaDrawForegroundInRect(ctx, foregroundNode, foregroundImage, rect, outW, outH) {
  if (!ctx || !foregroundNode || !foregroundImage || !rect) return false;
  try {
    const worldToCanvasX = Number(outW || 1) / Math.max(1, Number(rect.width || 1));
    const worldToCanvasY = Number(outH || 1) / Math.max(1, Number(rect.height || 1));
    const nodeScaleX = Number(foregroundNode.scaleX?.() || 1) || 1;
    const nodeScaleY = Number(foregroundNode.scaleY?.() || 1) || 1;
    const offsetX = Number(foregroundNode.offsetX?.() || 0);
    const offsetY = Number(foregroundNode.offsetY?.() || 0);
    const translateX = (
      Number(foregroundNode.x?.() || 0) - Number(rect.left || 0) - offsetX * nodeScaleX
    ) * worldToCanvasX;
    const translateY = (
      Number(foregroundNode.y?.() || 0) - Number(rect.top || 0) - offsetY * nodeScaleY
    ) * worldToCanvasY;
    ctx.save();
    ctx.setTransform(
      nodeScaleX * worldToCanvasX,
      0,
      0,
      nodeScaleY * worldToCanvasY,
      translateX,
      translateY
    );
    ctx.drawImage(
      foregroundImage,
      0,
      0,
      Number(foregroundNode.width?.() || foregroundImage.naturalWidth || foregroundImage.width || 0),
      Number(foregroundNode.height?.() || foregroundImage.naturalHeight || foregroundImage.height || 0)
    );
    ctx.restore();
    return true;
  } catch (_) {
    try { ctx.restore(); } catch (_) {}
    return false;
  }
}

function pamelaDetectLaserOpeningFromForeground(editor, full) {
  try {
    const foregroundNode = editor?.nodes?.borderOverlay || null;
    const foregroundImage = foregroundNode?.image?.() || null;
    if (!foregroundNode || !foregroundImage || !full?.width || !full?.height) return null;

    const imageKey = String(foregroundImage.currentSrc || foregroundImage.src || "");
    const cacheKey = [
      imageKey,
      full.left, full.top, full.width, full.height,
      foregroundNode.x?.(), foregroundNode.y?.(), foregroundNode.width?.(), foregroundNode.height?.(),
      foregroundNode.scaleX?.(), foregroundNode.scaleY?.()
    ].join("|");
    if (foregroundNode.__pamelaLaserOpeningCache?.key === cacheKey) {
      return { ...foregroundNode.__pamelaLaserOpeningCache.rect };
    }

    const maxSample = 360;
    const sampleScale = Math.min(1, maxSample / Math.max(Number(full.width), Number(full.height)));
    const sw = Math.max(80, Math.round(Number(full.width) * sampleScale));
    const sh = Math.max(50, Math.round(Number(full.height) * sampleScale));
    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.clearRect(0, 0, sw, sh);
    pamelaDrawForegroundInRect(ctx, foregroundNode, foregroundImage, full, sw, sh);
    const pixels = ctx.getImageData(0, 0, sw, sh).data;
    const total = sw * sh;
    const open = new Uint8Array(total);
    for (let i = 0, p = 3; i < total; i += 1, p += 4) {
      if (pixels[p] <= 40) open[i] = 1;
    }

    const seen = new Uint8Array(total);
    const queue = new Int32Array(total);
    const centerX = Math.floor(sw / 2);
    const centerY = Math.floor(sh / 2);
    let best = null;
    for (let start = 0; start < total; start += 1) {
      if (!open[start] || seen[start]) continue;
      let head = 0;
      let tail = 0;
      let count = 0;
      let minX = sw;
      let minY = sh;
      let maxX = 0;
      let maxY = 0;
      let touchesBoundary = false;
      let containsCenter = false;
      seen[start] = 1;
      queue[tail++] = start;
      while (head < tail) {
        const idx = queue[head++];
        const x = idx % sw;
        const y = (idx - x) / sw;
        count += 1;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        if (x === 0 || y === 0 || x === sw - 1 || y === sh - 1) touchesBoundary = true;
        if (x === centerX && y === centerY) containsCenter = true;
        const neighbors = [
          x > 0 ? idx - 1 : -1,
          x < sw - 1 ? idx + 1 : -1,
          y > 0 ? idx - sw : -1,
          y < sh - 1 ? idx + sw : -1
        ];
        for (const next of neighbors) {
          if (next < 0 || seen[next] || !open[next]) continue;
          seen[next] = 1;
          queue[tail++] = next;
        }
      }
      if (touchesBoundary || count < total * 0.08) continue;
      const score = count + (containsCenter ? total * 2 : 0);
      if (!best || score > best.score) best = { score, count, minX, minY, maxX, maxY };
    }
    if (!best) return null;

    const detectedWidthRatio = (best.maxX - best.minX + 1) / sw;
    const detectedHeightRatio = (best.maxY - best.minY + 1) / sh;
    if (detectedWidthRatio < 0.45 || detectedHeightRatio < 0.35) return null;

    // Move half a sample pixel inward to keep antialiased foreground pixels out.
    const pad = 0.5;
    const left = Number(full.left) + ((best.minX + pad) / sw) * Number(full.width);
    const right = Number(full.left) + ((best.maxX + 1 - pad) / sw) * Number(full.width);
    const top = Number(full.top) + ((best.minY + pad) / sh) * Number(full.height);
    const bottom = Number(full.top) + ((best.maxY + 1 - pad) / sh) * Number(full.height);
    if (right <= left || bottom <= top) return null;
    const ppi = Math.max(1, Number(typeof DPI !== "undefined" ? DPI : 72) || 72);
    const rect = {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
      source: "foreground-detected",
      insetsIn: {
        left: (left - Number(full.left)) / ppi,
        right: (Number(full.right) - right) / ppi,
        top: (top - Number(full.top)) / ppi,
        bottom: (Number(full.bottom) - bottom) / ppi
      }
    };
    foregroundNode.__pamelaLaserOpeningCache = { key: cacheKey, rect: { ...rect } };
    return rect;
  } catch (e) {
    console.warn("[Laser Fit] foreground opening detection failed:", e);
    return null;
  }
}

// The transparent opening in the foreground is authoritative. Physical inset
// defaults are retained only for monuments without a usable foreground mask.
function pamelaGetLaserEngravingRect(editor) {
  const full = pamelaGetLaserPlacementRect(editor);
  if (!full) return null;

  const measured = pamelaDetectLaserOpeningFromForeground(editor, full);
  if (measured) return measured;

  const variantId = String(document.getElementById("pamela-variant-select")?.value || "").trim();
  const variantMap = window.__PAMELA_LASER_FIT_INSETS_BY_VARIANT_ID__ || {};
  const configured = variantMap?.[variantId] || window.PAMELA_LASER_FIT_INSETS_IN || {};
  const sideDefault = Number(configured.side ?? 0.5);
  const leftIn = Math.max(0, Number(configured.left ?? sideDefault) || 0);
  const rightIn = Math.max(0, Number(configured.right ?? sideDefault) || 0);
  const topIn = Math.max(0, Number(configured.top ?? 0.5) || 0);
  const bottomIn = Math.max(0, Number(configured.bottom ?? 1.75) || 0);
  const ppi = Math.max(1, Number(typeof DPI !== "undefined" ? DPI : 72) || 72);

  const left = Number(full.left) + leftIn * ppi;
  const right = Number(full.right) - rightIn * ppi;
  const top = Number(full.top) + topIn * ppi;
  const bottom = Number(full.bottom) - bottomIn * ppi;

  // Bad product metadata must never collapse the fit area.
  if (right - left < ppi * 2 || bottom - top < ppi * 2) return full;
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    source: "configured-insets",
    insetsIn: { left: leftIn, right: rightIn, top: topIn, bottom: bottomIn }
  };
}

function pamelaClampImageCenterIntoRect(node, rect) {
  try {
    if (!node || !rect) return;
    const nodeW = Math.abs(Number(node.width?.() || 0) * (Number(node.scaleX?.() || 1) || 1));
    const nodeH = Math.abs(Number(node.height?.() || 0) * (Number(node.scaleY?.() || 1) || 1));
    if (!Number.isFinite(nodeW) || !Number.isFinite(nodeH) || nodeW <= 0 || nodeH <= 0) return;

    const minX = rect.left + nodeW / 2;
    const maxX = rect.right - nodeW / 2;
    const minY = rect.top + nodeH / 2;
    const maxY = rect.bottom - nodeH / 2;

    const clamp = (value, min, max, fallback) => {
      if (!Number.isFinite(value)) return fallback;
      if (min > max) return fallback;
      return Math.max(min, Math.min(max, value));
    };

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    node.x(clamp(Number(node.x?.() || 0), minX, maxX, centerX));
    node.y(clamp(Number(node.y?.() || 0), minY, maxY, centerY));
  } catch (_) {}
}

function pamelaGetPanelSideLandingX(editor, side, panelWidthPx) {
  try {
    if (side !== "left" && side !== "right") return null;
    const rect = pamelaGetTabletLandingRect(editor);
    const panelW = Math.max(1, Number(panelWidthPx || 0));
    if (!rect || !Number.isFinite(panelW)) return null;

    const inset = Math.min(PANEL_SIDE_LANDING_INSET_PX, Math.max(0, rect.width / 2));
    const centerX = side === "left"
      ? rect.left + inset + panelW / 2
      : rect.right - inset - panelW / 2;

    if (!Number.isFinite(centerX)) return null;
    return centerX;
  } catch (_) {
    return null;
  }
}

  // =============================
  // Sticky-mouse FIX: global forced drag release
  // =============================
  function pamelaForceReleaseKonvaDrag() {
    try {
      if (window.Konva && Array.isArray(window.Konva.stages) && window.Konva.stages.length) {
        try {
          window.Konva.stages[0].stopDrag();
        } catch (_) {}
      }
    } catch (_) {}

    try {
      if (window.Konva && window.Konva.DD && window.Konva.DD._dragElements) {
        Object.keys(window.Konva.DD._dragElements).forEach((k) => {
          const el = window.Konva.DD._dragElements[k];
          if (el && el.node && typeof el.node.stopDrag === "function") el.node.stopDrag();
        });
      }
    } catch (_) {}
  }

function wireGlobalAntiStickyDrag() {
  // Ã¢Å“â€¦ only wire once per page load (prevents stacking listeners)
  if (window.__pamelaAntiStickyDragWired) return;
  window.__pamelaAntiStickyDragWired = true;

  const stage =
    (window.pamelaMonumentEditor && window.pamelaMonumentEditor.stage) ||
    (window.pamelaEditor && window.pamelaEditor.stage);

  if (!stage) return;

  const container = stage.container();
  if (!container) return;

  const shouldCaptureForTarget = (t) => {
    if (!t) return false;

    const cls =
      typeof t.getClassName === "function"
        ? t.getClassName()
        : t.className;

    if (cls === "Text") return true;

    if (cls === "Image") {
      const a = t.attrs || {};
      if (a.pamelaClipart === true || a.pamelaPanel === true) return true;
    }

    return false;
  };

  stage.off("pointerdown.pamelaCap");

  stage.on("pointerdown.pamelaCap", (e) => {
    const t = e?.target;
    if (!shouldCaptureForTarget(t)) return;

    const pid = e?.evt?.pointerId;
    if (pid == null) return;

    try {
      container.setPointerCapture(pid);
    } catch (_) {}
  });

  const release = (ev) => {
    const pid = ev?.pointerId;
    if (pid == null) return;

    try {
      container.releasePointerCapture(pid);
    } catch (_) {}
  };

  container.addEventListener("pointerup", release, true);
  container.addEventListener("pointercancel", release, true);
}

function pamelaMakeRightMatchLeft(editor) {
  try {
    if (!editor || editor.layout !== "companion") return;

    const layer = editor.layers?.content;
    if (!layer) return;

    const texts = layer.getChildren((n) => n && pamelaIsTextLikeNode(n));
    if (!texts || !texts.length) return;

    const getSlot = (n) => {
      const v = Number(n.getAttr("pamelaSlotIndex"));
      return Number.isFinite(v) ? v : null;
    };

    const sideOf = (n) => pamelaGetSide(editor, n);

    const findNode = (type, side, slot) =>
      texts.find(
        (n) =>
          n &&
          n.className === "Text" &&
          n.attrs?.pamelaFieldType === type &&
          sideOf(n) === side &&
          getSlot(n) === slot
      );

    // Pair slots:
    // Pair 0: left 0 <-> right 1
    // Pair 1: left 2 <-> right 3
    const PAIRS = [
      { pair: 0, left: 0, right: 1 },
      { pair: 1, left: 2, right: 3 },
    ];

    // Helper: default = linked, unless explicitly turned off
    const isLinked = (type, pairIndex) => {
      const key = `${type}:${pairIndex}`;
      return editor.pairLinkOverrides?.[key] !== false;
    };

    // NAME + DATE: right copies left by pair IF linked
    for (const p of PAIRS) {
      if (isLinked("name", p.pair)) {
        const ln = findNode("name", "left", p.left);
        const rn = findNode("name", "right", p.right);
        if (ln && rn) {
          rn.y(ln.y());
          pamelaClampNodeIntoSafeRect(editor, rn);
        }
      }

      if (isLinked("date", p.pair)) {
        const ld = findNode("date", "left", p.left);
        const rd = findNode("date", "right", p.right);
        if (ld && rd) {
          rd.y(ld.y());
          pamelaClampNodeIntoSafeRect(editor, rd);
        }
      }
    }

    // NOTE: Verse pairing is NOT handled here (keeps your verse independence work intact)

    layer.batchDraw();
  } catch (_) {}
}

function pamelaIsHorizontalMirrorText(editor, node) {
  if (!editor || !node || !pamelaIsTextLikeNode(node)) return false;
  if (editor.layout !== "companion") return false;

  const type = String(node.attrs?.pamelaFieldType || "");
  return type === "name" || type === "date";
}

function pamelaIsCoarsePointerDevice() {
  try {
    return (
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "")
    );
  } catch (_) {
    return false;
  }
}

function pamelaCanMoveSideIndependently(editor, node) {
  return pamelaIsHorizontalMirrorText(editor, node || editor?.selected);
}

function pamelaShouldMoveSideIndependently(editor, useShift) {
  if (!editor) return false;
  if (editor.horizontalMirrorIndependentMode) return true;
  if (editor.horizontalMirrorShiftKeyDown) return true;
  return !!useShift;
}

function pamelaRefreshIndependentMoveUI(editor) {
  try {
    const hint = document.getElementById("pamela-toolbar-hint");
    const toggle = document.getElementById("pamela-independent-sides-toggle");
    const canUse = pamelaCanMoveSideIndependently(editor);
    const isMobile = pamelaIsCoarsePointerDevice();

    if (hint) {
      hint.textContent = canUse
        ? (isMobile ? PAMELA_INDEPENDENT_HINT_MOBILE : PAMELA_INDEPENDENT_HINT_DESKTOP)
        : PAMELA_DEFAULT_HINT_TEXT;
    }

    if (!toggle) return;

    if (canUse && isMobile) {
      toggle.style.display = "inline-flex";
      toggle.disabled = false;
      const active = !!editor.horizontalMirrorIndependentMode;
      toggle.setAttribute("aria-pressed", active ? "true" : "false");
      toggle.textContent = active ? "Relink" : "Unlink";
      // Label toggles with state, so keep its tip in sync here too -- same
      // reason pamelaRefreshTextGroupUI does this for Group/Ungroup Text.
      toggle.removeAttribute("title");
      toggle.setAttribute(
        "data-pamela-tip",
        active
          ? "Relink this text so it moves together with its mirrored match again"
          : "Move this mirrored text independently of its matching pair on the other side"
      );
      toggle.setAttribute("aria-label", active ? "Relink mirrored text" : "Unlink mirrored text");
      toggle.style.background = active ? "#111" : "#fff";
      toggle.style.color = active ? "#fff" : "#111";
      toggle.style.borderColor = "#111";
    } else {
      toggle.style.display = "none";
      toggle.disabled = true;
      toggle.setAttribute("aria-pressed", "false");
      toggle.style.background = "#fff";
      toggle.style.color = "#111";
    }
  } catch (_) {}
}

function pamelaGetHorizontalMirrorTexts(editor) {
  if (!editor || editor.layout !== "companion") return [];

  const layer = editor.layers?.content;
  if (!layer) return [];

  return layer.getChildren((n) => pamelaIsHorizontalMirrorText(editor, n)) || [];
}

function pamelaIsHorizontalMirrorLinked(editor) {
  return !!editor && editor.horizontalMirrorLinked !== false;
}

function pamelaGetHorizontalMirrorPairs(editor) {
  const texts = pamelaGetHorizontalMirrorTexts(editor);
  if (!texts.length) return [];

  const byKey = new Map();
  texts.forEach((n) => {
    const type = String(n.attrs?.pamelaFieldType || "");
    const slot = Number(n.getAttr("pamelaSlotIndex"));
    if (!Number.isFinite(slot)) return;
    byKey.set(`${type}:${slot}`, n);
  });

  const pairs = [];
  for (const type of ["name", "date"]) {
    for (const leftSlot of [0, 2]) {
      const rightSlot = pamelaMateSlotFor(leftSlot);
      const left = byKey.get(`${type}:${leftSlot}`) || null;
      const right = byKey.get(`${type}:${rightSlot}`) || null;
      if (left && right) pairs.push({ type, left, right });
    }
  }

  return pairs;
}

function pamelaGetHorizontalMirrorMaxMismatch(editor) {
  const pairs = pamelaGetHorizontalMirrorPairs(editor);
  if (!pairs.length) return Infinity;

  const { fullCenter } = pamelaGetCenters(editor);
  let maxMismatch = 0;

  for (const pair of pairs) {
    const expectedRightX = (2 * fullCenter) - pair.left.x();
    maxMismatch = Math.max(maxMismatch, Math.abs(pair.right.x() - expectedRightX));
  }

  return maxMismatch;
}

function pamelaSyncHorizontalMirror(editor) {
  try {
    if (!editor || editor.layout !== "companion") return;
    if (!pamelaIsHorizontalMirrorLinked(editor)) return;

    const { fullCenter } = pamelaGetCenters(editor);
    const pairs = pamelaGetHorizontalMirrorPairs(editor);
    if (!pairs.length) return;

    for (const pair of pairs) {
      pair.right.x((2 * fullCenter) - pair.left.x());
      pamelaClampNodeIntoSafeRect(editor, pair.right);
    }

    editor.layers?.content?.batchDraw?.();
  } catch (_) {}
}

function pamelaBuildHorizontalMirrorDragState(editor, node) {
  if (!pamelaIsHorizontalMirrorText(editor, node)) return null;

  const side = pamelaGetSide(editor, node);
  if (side !== "left" && side !== "right") return null;

  const nodes = pamelaGetHorizontalMirrorTexts(editor);
  if (!nodes.length) return null;

  return {
    startedAt: Date.now(),
    side,
    items: nodes.map((n) => ({
      node: n,
      startX: Number(n.x()) || 0,
      side: pamelaGetSide(editor, n),
    })),
  };
}

function pamelaShouldBreakHorizontalMirror(meta, dx, dy) {
  if (!meta) return false;
  if (Date.now() - meta.startedAt > PAMELA_MIRROR_BREAK_MS) return false;
  if (Math.abs(dx) < PAMELA_MIRROR_BREAK_PX) return false;
  if (Math.abs(dx) < Math.abs(dy) * PAMELA_MIRROR_BREAK_RATIO) return false;
  return true;
}

function pamelaApplyHorizontalMirrorDrag(editor, node, meta, rawDx, rawDy, useIndependentMode) {
  try {
    if (!editor || !node || !meta || !meta.items?.length) return;
    if (!pamelaIsHorizontalMirrorText(editor, node)) return;

    const independent = !!useIndependentMode;
    if (!independent) editor.horizontalMirrorLinked = true;

    const dx = (Number(node.x()) || 0) - (Number(node._pamelaPickupPos?.x) || 0);
    const linked = !independent;

    for (const item of meta.items) {
      if (!item?.node) continue;

      if (item.side === meta.side) {
        item.node.x(item.startX + dx);
      } else if (linked) {
        item.node.x(item.startX - dx);
      } else {
        item.node.x(item.startX);
      }

      pamelaClampNodeIntoSafeRect(editor, item.node);
    }
  } catch (_) {}
}

function pamelaTryRelinkHorizontalMirror(editor) {
  try {
    if (!editor || editor.layout !== "companion") return;
    if (editor.horizontalMirrorIndependentMode) return;
    if (pamelaIsHorizontalMirrorLinked(editor)) return;

    editor.horizontalMirrorLinked = true;
    pamelaSyncHorizontalMirror(editor);
  } catch (_) {}
}

function pamelaEnforceMirrorPairSeparation(editor) {
  try {
    if (!editor || editor.layout !== "companion") return;

    const layer = editor.layers?.content;
    if (!layer) return;

    const pairs = pamelaGetHorizontalMirrorPairs(editor);
    if (!pairs.length) return;

    const rectOf = (n) =>
      n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

    for (const pair of pairs) {
      const leftNode = pair.left;
      const rightNode = pair.right;
      if (!leftNode || !rightNode) continue;

      let leftRect = rectOf(leftNode);
      let rightRect = rectOf(rightNode);

      const overlapY =
        Math.min(leftRect.y + leftRect.height, rightRect.y + rightRect.height) -
        Math.max(leftRect.y, rightRect.y);

      if (overlapY <= 0) continue;

      const currentGap = rightRect.x - (leftRect.x + leftRect.width);
      if (currentGap >= PAMELA_MIRROR_PAIR_MIN_X_GAP_PX) continue;

      const push = (PAMELA_MIRROR_PAIR_MIN_X_GAP_PX - currentGap) / 2;

      leftNode.x(leftNode.x() - push);
      rightNode.x(rightNode.x() + push);

      pamelaClampNodeIntoSafeRect(editor, leftNode);
      pamelaClampNodeIntoSafeRect(editor, rightNode);

      leftRect = rectOf(leftNode);
      rightRect = rectOf(rightNode);

      const remainingGap = rightRect.x - (leftRect.x + leftRect.width);
      if (remainingGap < PAMELA_MIRROR_PAIR_MIN_X_GAP_PX) {
        const fallbackPush = PAMELA_MIRROR_PAIR_MIN_X_GAP_PX - remainingGap;
        leftNode.x(leftNode.x() - fallbackPush);
        rightNode.x(rightNode.x() + fallbackPush);
        pamelaClampNodeIntoSafeRect(editor, leftNode);
        pamelaClampNodeIntoSafeRect(editor, rightNode);
      }
    }
  } catch (_) {}
}

function pamelaFindTextNodeByTypeSlot(editor, type, slotIndex) {
  try {
    if (!editor || editor.layout !== "companion") return null;
    const layer = editor.layers?.content;
    if (!layer) return null;

    const wantType = String(type || "");
    const wantSlot = Number(slotIndex);
    if (!wantType || !Number.isFinite(wantSlot)) return null;

    const texts = layer.getChildren(
      (n) =>
        n &&
        n.className === "Text" &&
        String(n.attrs?.pamelaFieldType || "") === wantType &&
        Number(n.getAttr?.("pamelaSlotIndex")) === wantSlot
    );

    return texts && texts.length ? texts[0] : null;
  } catch (_) {
    return null;
  }
}

function pamelaGetNamedDateGroupCenterForSide(editor, side) {
  try {
    if (!editor || editor.layout !== "companion") return null;
    if (side !== "left" && side !== "right") return null;

    const layer = editor.layers?.content;
    if (!layer) return null;

    const nodes = layer.getChildren((n) => {
      if (!n || n.className !== "Text") return false;
      const type = String(n.attrs?.pamelaFieldType || "");
      if (!(type === "name" || type === "date")) return false;
      return pamelaGetSide(editor, n) === side;
    });

    if (!nodes || !nodes.length) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const n of nodes) {
      pamelaFixTextMetrics(n);
      const r = n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
      return null;
    }

    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    };
  } catch (_) {
    return null;
  }
}

function pamelaIsPanelLikeImageNode(node) {
  try {
    const cls = node?.getClassName?.() || node?.className || "";
    return cls === "Image" && (
      node?.getAttr?.("pamelaPanel") === true ||
      node?.getAttr?.("pamelaClipartLibraryPanel") === true
    );
  } catch (_) {
    return false;
  }
}

function pamelaGetPanelCenterXMatch(editor, node) {
  try {
    if (!editor || !pamelaIsPanelLikeImageNode(node)) return null;
    const myX = Number(node.x?.() ?? node.attrs?.x);
    const myY = Number(node.y?.() ?? node.attrs?.y);
    if (!Number.isFinite(myX) || !Number.isFinite(myY)) return null;

    const isCompanion = editor.layout === "companion";
    const mySide = isCompanion ? pamelaGetSide(editor, node) : "any";

    let best = null;
    const consider = (centerX, centerY, nodeForMatch = null, slot = "") => {
      if (!Number.isFinite(centerX)) return;
      const dx = centerX - myX;
      const dy = Number.isFinite(centerY) ? centerY - myY : 0;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (!best || distance < best.distance) {
        best = { node: nodeForMatch, slot, centerX, deltaX: dx, absDeltaX: Math.abs(dx), distance };
      }
    };

    const texts = editor.layers?.content?.getChildren?.((n) => {
      if (!n || !pamelaIsTextLikeNode(n)) return false;
      const type = String(n.attrs?.pamelaFieldType || n.getAttr?.("pamelaFieldType") || "").toLowerCase();
      if (!(type === "lastname" || type === "name" || type === "date")) return false;
      return !isCompanion || type === "lastname" || pamelaGetSide(editor, n) === mySide;
    }) || [];

    texts.forEach((t) => consider(pamelaGetNodeCenterX(t), pamelaGetNodeAlignY(editor, t), t));

    const centers = pamelaGetCenters(editor);
    const targets = editor.layout === "companion"
      ? [
          { slot: "left", centerX: centers.leftCenter },
          { slot: "center", centerX: centers.fullCenter },
          { slot: "right", centerX: centers.rightCenter },
        ]
      : [
          { slot: "center", centerX: centers.fullCenter },
        ];
    targets.forEach((target) => consider(target.centerX, null, null, target.slot));
    return best;
  } catch (_) {
    return null;
  }
}

function pamelaGetPanelAnchorForSide(editor, side) {
  try {
    if (!editor || (side !== "left" && side !== "right")) return null;

    const panels = [];
    const pushPanels = (layer) => {
      const kids = layer?.getChildren?.() || [];
      kids.forEach((n) => {
        if (!n || !pamelaIsPanelLikeImageNode(n)) return;
        const slot = String(n.getAttr?.("pamelaPanelSlot") || n.getAttr?.("pamelaClipartPanelSlot") || "").toLowerCase();
        if (slot && slot !== side) return;
        if (!slot && pamelaGetSide(editor, n) !== side) return;
        panels.push(n);
      });
    };

    pushPanels(editor.layers?.panels);
    pushPanels(editor.layers?.content);
    if (!panels.length) return null;

    const { leftCenter, rightCenter } = pamelaGetCenters(editor);
    const targetX = side === "left" ? leftCenter : rightCenter;
    let best = null;
    let bestDx = Infinity;

    for (const panel of panels) {
      const x = Number(panel.x?.() ?? panel.attrs?.x);
      const y = Number(panel.y?.() ?? panel.attrs?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const dx = Math.abs(x - targetX);
      if (!best || dx < bestDx) {
        best = { node: panel, x, y };
        bestDx = dx;
      }
    }

    return best;
  } catch (_) {
    return null;
  }
}

function pamelaSnapPanelToNearestTextCenter(editor, node) {
  try {
    const match = pamelaGetPanelCenterXMatch(editor, node);
    if (!match || match.absDeltaX > PAMELA_SNAP_THRESHOLD_PX) return false;
    node.x(node.x() + match.deltaX);
    return true;
  } catch (_) {
    return false;
  }
}

function pamelaGetNearestPanelCenterX(editor, node) {
  try {
    if (!editor || !node || node.className !== "Text") return null;

    const isCompanion = editor.layout === "companion";
    const mySide = isCompanion ? pamelaGetSide(editor, node) : "any";
    const myX = Number(node.x?.() || 0);
    const myY = Number(node.y?.() || 0);

    const panels = [];
    const pushPanels = (layer) => {
      const kids = layer?.getChildren?.() || [];
      kids.forEach((n) => {
        if (n && pamelaIsPanelLikeImageNode(n)) panels.push(n);
      });
    };
    pushPanels(editor.layers?.panels);
    pushPanels(editor.layers?.content);
    if (!panels || !panels.length) return null;

    let bestNode = null;
    let bestCenterX = null;
    let bestDistance = Infinity;

    for (const panel of panels) {
      if (isCompanion) {
        const side = pamelaGetSide(editor, panel);
        if (side !== mySide) continue;
      }

      const px = Number(panel.x?.() || 0);
      const py = Number(panel.y?.() || 0);
      const dx = px - myX;
      const dy = py - myY;
      const d = Math.sqrt(dx * dx + dy * dy);

      if (d < bestDistance) {
        bestDistance = d;
        bestNode = panel;
        bestCenterX = px;
      }
    }

    if (!bestNode || !Number.isFinite(bestCenterX)) return null;

    return {
      node: bestNode,
      centerX: bestCenterX,
      deltaX: bestCenterX - myX,
      absDeltaX: Math.abs(bestCenterX - myX),
    };
  } catch (_) {
    return null;
  }
}

function pamelaGetSideTextGroupNodes(editor, side) {
  try {
    if (!editor || editor.layout !== "companion") return [];
    if (side !== "left" && side !== "right") return [];
    const layer = editor.layers?.content;
    if (!layer) return [];

    const nodes = layer.getChildren((n) => {
      if (!n || n.className !== "Text") return false;
      const type = String(n.attrs?.pamelaFieldType || "");
      if (!(type === "name" || type === "date")) return false;
      return pamelaGetSide(editor, n) === side;
    }) || [];

    return nodes.sort((a, b) => {
      const dy = (Number(a.y?.() || 0) - Number(b.y?.() || 0));
      if (Math.abs(dy) > 0.01) return dy;
      const sa = Number(a.getAttr?.("pamelaSlotIndex"));
      const sb = Number(b.getAttr?.("pamelaSlotIndex"));
      return (Number.isFinite(sa) ? sa : 9999) - (Number.isFinite(sb) ? sb : 9999);
    });
  } catch (_) {
    return [];
  }
}

function pamelaCanGroupSideText(editor, node) {
  if (!editor || !node || node.className !== "Text") return false;
  if (editor.layout !== "companion") return false;
  const type = String(node.attrs?.pamelaFieldType || "");
  if (!(type === "name" || type === "date")) return false;
  const side = pamelaGetSide(editor, node);
  return pamelaGetSideTextGroupNodes(editor, side).length >= 2;
}

function pamelaIsGroupedSideTextActive(editor, node) {
  if (!editor || !editor.groupedTextSide) return false;
  if (!pamelaCanGroupSideText(editor, node)) return false;
  return pamelaGetSide(editor, node) === editor.groupedTextSide;
}

function pamelaBuildTextGroupDragState(editor, node) {
  const side = editor?.groupedTextSide || pamelaGetSide(editor, node);
  if (!editor || side !== "left" && side !== "right") return null;
  if (!pamelaIsGroupedSideTextActive(editor, node)) return null;
  const items = pamelaGetSideTextGroupNodes(editor, side).map((n) => ({
    node: n,
    startX: Number(n.x?.() || 0),
    startY: Number(n.y?.() || 0),
  }));
  if (!items.length) return null;
  return { side, items };
}

function pamelaApplyTextGroupDrag(editor, meta, dx, dy) {
  try {
    if (!editor || !meta?.items?.length) return;
    for (const item of meta.items) {
      if (!item?.node) continue;
      item.node.x(item.startX + dx);
      item.node.y(item.startY + dy);
      pamelaClampNodeIntoSafeRect(editor, item.node);
    }
  } catch (_) {}
}

function pamelaGetTextGroupCenter(editor, nodes) {
  try {
    const layer = editor.layers?.content;
    if (!layer || !nodes?.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of nodes) {
      pamelaFixTextMetrics(n);
      const r = n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    }
    if (!Number.isFinite(minX)) return null;
    return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  } catch (_) {
    return null;
  }
}

function pamelaRotateGroupedSideText(editor, side, deltaDeg) {
  try {
    const nodes = pamelaGetSideTextGroupNodes(editor, side);
    if (!nodes.length) return false;
    const center = pamelaGetTextGroupCenter(editor, nodes);
    if (!center) return false;
    const rad = (Number(deltaDeg || 0) * Math.PI) / 180;
    const sin = Math.sin(rad);
    const cos = Math.cos(rad);

    for (const n of nodes) {
      const x = Number(n.x?.() || 0);
      const y = Number(n.y?.() || 0);
      const dx = x - center.x;
      const dy = y - center.y;
      n.x(center.x + (dx * cos - dy * sin));
      n.y(center.y + (dx * sin + dy * cos));
      n.rotation(snapDeg((Number(n.rotation?.() || 0) + Number(deltaDeg || 0))));
      pamelaFixTextMetrics(n);
      pamelaClampNodeIntoSafeRect(editor, n);
    }
    try { pamelaUpdateAllTextColorsForOverlap(editor); } catch (_) {}
    try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

function pamelaHasGroupedSideText(editor) {
  if (!editor || !editor.groupedTextSide) return false;
  return pamelaGetSideTextGroupNodes(editor, editor.groupedTextSide).length >= 2;
}

function pamelaRefreshTextGroupUI(editor) {
  try {
    const btn = document.getElementById("pamela-group-side-text");
    if (!btn) return;
    const canGroup = pamelaCanGroupSideText(editor, editor?.selected);
    const active = pamelaIsGroupedSideTextActive(editor, editor?.selected);
    const visible = canGroup || active;

    btn.style.display = visible ? "" : "none";
    btn.setAttribute("aria-hidden", visible ? "false" : "true");
    btn.disabled = !visible;
    btn.textContent = active ? "Ungroup Text" : "Group Text";
    // Use the styled tooltip system, not a native title -- this label/state
    // toggles on every selection change, so it has to be kept in sync here
    // rather than relying on the one-time tipsById pass.
    btn.removeAttribute("title");
    btn.setAttribute(
      "data-pamela-tip",
      active
        ? "Ungroup side text so each piece can move independently again"
        : "Group all text on this side so dragging one piece moves them together"
    );
    btn.setAttribute("aria-label", active ? "Ungroup side text" : "Group side text");
    btn.style.background = active ? "#111" : "#fff";
    btn.style.color = active ? "#fff" : "#111";
    btn.style.borderColor = "#111";
    btn.style.opacity = btn.disabled ? "0.35" : "1";
  } catch (_) {}
}

function pamelaRefreshTextGroupOverlay(editor) {
  try {
    if (!editor?.layers?.ui) return;

    let box = editor.nodes?.groupTextBox || null;
    if (!box) {
      box = new Konva.Rect({
        listening: false,
        visible: false,
        stroke: "#3b82f6",
        strokeWidth: 1.5,
        dash: [8, 6],
        cornerRadius: 10,
      });
      editor.nodes = editor.nodes || {};
      editor.nodes.groupTextBox = box;
      editor.layers.ui.add(box);
    }

    if (!pamelaHasGroupedSideText(editor)) {
      box.visible(false);
      editor.layers.ui.batchDraw?.();
      return;
    }

    const layer = editor.layers?.content;
    const nodes = pamelaGetSideTextGroupNodes(editor, editor.groupedTextSide);
    if (!layer || !nodes.length) {
      box.visible(false);
      editor.layers.ui.batchDraw?.();
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const n of nodes) {
      const r = n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    }

    if (!Number.isFinite(minX)) {
      box.visible(false);
      editor.layers.ui.batchDraw?.();
      return;
    }

    const pad = 14;
    box.x(minX - pad);
    box.y(minY - pad);
    box.width((maxX - minX) + pad * 2);
    box.height((maxY - minY) + pad * 2);
    box.visible(true);
    box.moveToBottom?.();
    editor.nodes?.tr?.moveToTop?.();
    editor.nodes?.trPanel?.moveToTop?.();
    editor.layers.ui.batchDraw?.();
  } catch (_) {}
}

function pamelaPairIndexFromSlot(slot) {
  // Pair 0 = slots 0/1, Pair 1 = slots 2/3
  return slot <= 1 ? 0 : 1;
}

function pamelaMateSlotFor(slot) {
  // 0<->1, 2<->3
  if (slot === 0) return 1;
  if (slot === 1) return 0;
  if (slot === 2) return 3;
  if (slot === 3) return 2;
  return null;
}

function pamelaFindMate(editor, node) {
  if (!editor || !node || !pamelaIsTextLikeNode(node)) return null;
  if (editor.layout !== "companion") return null;

  const type = String(node.attrs?.pamelaFieldType || "");
  if (!(type === "name" || type === "date")) return null;

  const slot = Number(node.getAttr("pamelaSlotIndex"));
  if (!Number.isFinite(slot)) return null;

  const mateSlot = pamelaMateSlotFor(slot);
  if (mateSlot == null) return null;

  const layer = editor.layers?.content;
  if (!layer) return null;

  const texts = layer.getChildren((n) => n && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === type);
  return texts.find((n) => Number(n.getAttr("pamelaSlotIndex")) === mateSlot) || null;
}

// Ã¢Å“â€¦ Auto behavior:
// - Default is LINKED
// - If dragged > 1" out of Y alignment => auto UNLINK (for that pair + type)
// - If later dragged back within 1" => auto RELINK and snap mate Y
function pamelaAutoPairLinkFromDrag(editor, node) {
  try {
    if (!editor || !node || !pamelaIsTextLikeNode(node)) return;
    if (editor.layout !== "companion") return;

    const type = String(node.attrs?.pamelaFieldType || "");
    if (!(type === "name" || type === "date")) return;

    const slot = Number(node.getAttr("pamelaSlotIndex"));
    if (!Number.isFinite(slot)) return;

    const pairIndex = pamelaPairIndexFromSlot(slot);
    const key = `${type}:${pairIndex}`;

    const mate = pamelaFindMate(editor, node);
    if (!mate) return;

    const dy = Math.abs(node.y() - mate.y());

    // linked unless explicitly set false
    const linkedNow = editor.pairLinkOverrides?.[key] !== false;

    if (linkedNow) {
      // If they pull it too far away, break the link
      if (dy > PAMELA_PAIR_BREAK_PX) {
        editor.pairLinkOverrides[key] = false;
        return;
      }
      // Otherwise keep mate Y synced
      mate.y(node.y());
      pamelaClampNodeIntoSafeRect(editor, mate);
      return;
    }

    // Currently unlinked: if they bring it back close, relink + snap
    if (dy <= PAMELA_PAIR_BREAK_PX) {
      editor.pairLinkOverrides[key] = true;
      mate.y(node.y());
      pamelaClampNodeIntoSafeRect(editor, mate);
    }
  } catch (_) {}
}


  // =============================
  // Spacing enforcement (Name/Date/Wedding/Verse)
  // =============================
  function pamelaShouldEnforceSpacing(node) {
    // Curved TextPath fields participate in the exact same layout rules as
    // straight Text fields.  Excluding them lets the next verse occupy their
    // slot and then makes the whole stack appear to jump upward.
    if (!node || !pamelaIsTextLikeNode(node)) return false;
    const t = String(node.attrs?.pamelaFieldType || "");
    return t === "name" || t === "date" || t === "wedding" || t === "verse";
  }

  function pamelaEnforceSpacingForText(editor) {
    if (!editor) return;
    const layer = editor.layers?.content;
    if (!layer) return;

    const nodes = layer.getChildren((n) => pamelaShouldEnforceSpacing(n));
    if (!nodes || nodes.length < 2) return;

    const isCompanion = editor.layout === "companion";
    const designW = editor.state?.design?.width || DESIGN_PX.w;
    const designH = editor.state?.design?.height || DESIGN_PX.h;
    const centerX = designW / 2;

    const rectOf = (n) =>
      n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

    const minY = PADDING_PX;
    const maxY = designH - PADDING_PX;

    const sideOfItem = (it) => {
      const forced = pamelaGetSide(editor, it?.n);
      if (forced === "left" || forced === "right") return forced;
      const mid = it.r.x + it.r.width / 2;
      return mid < centerX ? "left" : "right";
    };

    const overlapsEnoughOnX = (a, b) => {
      const left = Math.max(a.x, b.x);
      const right = Math.min(a.x + a.width, b.x + b.width);
      return (right - left) > PAMELA_TEXT_STACK_X_OVERLAP_PX;
    };

    const enforceStack = (items) => {
      if (!items || items.length < 2) return;

      items.sort((a, b) => {
  const dy = a.r.y - b.r.y;
  if (Math.abs(dy) > 0.01) return dy;

  const sa = Number(a.n?.getAttr?.("pamelaSlotIndex"));
  const sb = Number(b.n?.getAttr?.("pamelaSlotIndex"));

  const ha = Number.isFinite(sa) ? sa : 9999;
  const hb = Number.isFinite(sb) ? sb : 9999;

  return ha - hb;
});


      // clamp top inside padding
      {
        const first = items[0];
        const dy = minY - first.r.y;
        if (dy > 0) {
          first.n.y(first.n.y() + dy);
          first.r = rectOf(first.n);
        }
      }

      // push down
      for (let i = 1; i < items.length; i++) {
        const prev = items[i - 1];
        const cur = items[i];

        if (!overlapsEnoughOnX(prev.r, cur.r)) continue;

        const prevBottom = prev.r.y + prev.r.height;
        const neededTop = prevBottom + MIN_TEXT_GAP_PX;

        if (cur.r.y < neededTop) {
          const dy = neededTop - cur.r.y;
          cur.n.y(cur.n.y() + dy);
          cur.r = rectOf(cur.n);
        }
      }

      // shift up if overflow
      const last = items[items.length - 1];
      const lastBottom = last.r.y + last.r.height;
      const overflow = lastBottom - maxY;

      if (overflow > 0) {
        let shiftUp = overflow;
        const topNow = rectOf(items[0].n).y;
        const maxShiftUp = topNow - minY;
        shiftUp = Math.min(shiftUp, maxShiftUp);

        if (shiftUp > 0) items.forEach((it) => it.n.y(it.n.y() - shiftUp));
      }
    };

    const items = nodes.map((n) => ({ n, r: rectOf(n) }));

    if (!isCompanion) {
      enforceStack(items);
      layer.batchDraw();
      return;
    }

    const left = [];
    const right = [];
    for (const it of items) (sideOfItem(it) === "left" ? left : right).push(it);

    enforceStack(left);
    enforceStack(right);
    pamelaEnforceMirrorPairSeparation(editor);
    pamelaMakeRightMatchLeft(editor);

    layer.batchDraw();
  }

  // =============================
  // Font preload + metrics fix
  // =============================
  async function pamelaPreloadFontsOnce() {
    if (window.__pamelaFontsPreloaded) return;
    window.__pamelaFontsPreloaded = true;

    if (!document.fonts || !document.fonts.load) return;

    const all = Array.from(new Set([...(PAMELA_ALL_FONTS || []), ...(PAMELA_LIMITED_FONTS || [])]));
    try {
      await Promise.allSettled(all.map((f) => document.fonts.load(`16px "${f}"`)));
      await document.fonts.ready;
    } catch (_) {}
  }

  function pamelaFixTextMetrics(t) {
    if (!t || t.className !== "Text") return;

    // force re-measure
    const txt = t.text();
    t.text(txt);

    const r = t.getSelfRect ? t.getSelfRect() : { x: 0, y: 0, width: t.width(), height: t.height() };
    t.offsetX(r.x + r.width / 2);
    t.offsetY(r.y + r.height / 2);

    // easier hit
    t.hitStrokeWidth(30);
  }
  // =============================
  // Snap helpers
  // =============================
  function pamelaGetSide(editor, node) {
    try {
      if (!editor || !node) return "any";
      if (editor.layout !== "companion") return "any";

      const type = String(node.attrs?.pamelaFieldType || "");
      const slot = Number(node.getAttr?.("pamelaSlotIndex"));
      if ((type === "name" || type === "date") && Number.isFinite(slot)) {
        return slot % 2 === 0 ? "left" : "right";
      }

      const layer = editor.layers?.content;
      if (!layer) return "any";

      const w = editor.state?.design?.width || DESIGN_PX.w;
      const seamX = w / 2;

      const r = node.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      const mid = r.x + r.width / 2;
      return mid < seamX ? "left" : "right";
    } catch (_) {
      return "any";
    }
  }

  function pamelaSnapDateToNearestNameCenterX(editor, dateNode) {
    try {
      if (!editor || !dateNode || !pamelaIsTextLikeNode(dateNode)) return;
      if (String(dateNode.attrs?.pamelaFieldType || "") !== "date") return;

      const layer = editor.layers?.content;
      if (!layer) return;

      const isCompanion = editor.layout === "companion";
      const dateSide = pamelaGetSide(editor, dateNode);

      const nameNodes = layer.getChildren((n) => n && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === "name");
      if (!nameNodes || !nameNodes.length) return;

      let bestX = null;
      let bestD = Infinity;

      const x0 = pamelaGetNodeCenterX(dateNode);
      const y0 = dateNode.y();

      for (const n of nameNodes) {
        if (isCompanion) {
          const side = pamelaGetSide(editor, n);
          if (side !== dateSide) continue;
        }
        const nx = pamelaGetNodeCenterX(n);
        const dx = nx - x0;
        const dy = n.y() - y0;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < bestD) {
          bestD = d;
          bestX = nx;
        }
      }

      if (bestX == null) return;
      if (Math.abs(pamelaGetNodeCenterX(dateNode) - bestX) <= PAMELA_SNAP_THRESHOLD_PX) {
        dateNode.x(dateNode.x() + (bestX - pamelaGetNodeCenterX(dateNode)));
      }
    } catch (_) {}
  }

  function pamelaMaybeSnapTextToCenterX(editor, t, opts = {}) {
    try {
      if (!editor || !t || !pamelaIsTextLikeNode(t)) return;
      const type = String(t.attrs?.pamelaFieldType || "");
      if (opts?.skipDateSnap && type === "date") return;
      pamelaSnapTextToCenterX(editor, t);
    } catch (_) {}
  }

  function pamelaGetNearestTextCenterXMatch(editor, t) {
    try {
      if (!editor || !t || !pamelaIsTextLikeNode(t)) return null;
      const type = String(t.attrs?.pamelaFieldType || "");
      const layer = editor.layers?.content;
      if (!layer) return null;

      const isCompanion = editor.layout === "companion";
      const mySide = isCompanion ? pamelaGetSide(editor, t) : "any";
      let candidates = [];

      if (type === "date") {
        candidates = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === "name");
      } else if (type === "name") {
        candidates = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === "name");
      } else if (type === "wedding" || type === "verse") {
        candidates = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n));
      }

      let bestNode = null;
      let bestX = null;
      let bestAbsDx = Infinity;

      for (const n of candidates) {
        if (isCompanion) {
          const side = pamelaGetSide(editor, n);
          if (side !== mySide) continue;
        }
        const x = pamelaGetNodeCenterX(n);
        if (!Number.isFinite(x)) continue;
        const tx = pamelaGetNodeCenterX(t);
        const absDx = Math.abs(x - tx);
        if (absDx < bestAbsDx) {
          bestAbsDx = absDx;
          bestX = x;
          bestNode = n;
        }
      }

      if (!bestNode || !Number.isFinite(bestX)) return null;
      return { node: bestNode, centerX: bestX, absDeltaX: bestAbsDx };
    } catch (_) {
      return null;
    }
  }

  function pamelaGetTextBaselineY(editor, t) {
    try {
      if (!editor || !t || !pamelaIsTextLikeNode(t)) return null;
      const layer = editor.layers?.content;
      if (!layer) return null;
      const r = t.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      const fontSize = Number(t.fontSize?.() || t.attrs?.fontSize || 0);
      if (!Number.isFinite(r?.y) || !Number.isFinite(r?.height)) return null;
      const baselineOffset = Number.isFinite(fontSize) && fontSize > 0 ? (fontSize * 0.18) : 0;
      return r.y + r.height - baselineOffset;
    } catch (_) {
      return null;
    }
  }

  function pamelaGetNearestVerseBaseline(editor, t) {
    try {
      if (!editor || !t || !pamelaIsTextLikeNode(t)) return null;
      if (String(t.attrs?.pamelaFieldType || "") !== "verse") return null;
      const layer = editor.layers?.content;
      if (!layer) return null;

      const myBaseline = pamelaGetTextBaselineY(editor, t);
      if (!Number.isFinite(myBaseline)) return null;

      const isCompanion = editor.layout === "companion";
      const mySide = isCompanion ? pamelaGetSide(editor, t) : "any";
      const verses = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === "verse");

      let bestNode = null;
      let bestBaseline = null;
      let bestDelta = Infinity;

      for (const n of verses) {
        if (isCompanion) {
          const side = pamelaGetSide(editor, n);
          if (side !== mySide) continue;
        }
        const baseline = pamelaGetTextBaselineY(editor, n);
        if (!Number.isFinite(baseline)) continue;
        const delta = Math.abs(baseline - myBaseline);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestBaseline = baseline;
          bestNode = n;
        }
      }

      if (!bestNode || !Number.isFinite(bestBaseline)) return null;
      return { node: bestNode, baselineY: bestBaseline, deltaY: bestBaseline - myBaseline, absDeltaY: bestDelta };
    } catch (_) {
      return null;
    }
  }

  function pamelaSnapVerseBaseline(editor, t) {
    try {
      const match = pamelaGetNearestVerseBaseline(editor, t);
      if (!match) return false;
      if (match.absDeltaY > pamelaGetEffectiveSnapThreshold(editor, PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX)) return false;
      t.y(t.y() + match.deltaY);
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaIsGuideAlignNode(node) {
    try {
      if (!node) return false;
      const cls = node.getClassName?.() || node.className || "";
      if (pamelaIsTextLikeNode(node)) return true;
      if (cls !== "Image") return false;
      return node.getAttr?.("pamelaPanel") === true || node.getAttr?.("pamelaClipart") === true || pamelaIsLaserArtworkNode(node);
    } catch (_) {
      return false;
    }
  }

  function pamelaIsArtAlignImage(node) {
    try {
      if (!node) return false;
      const cls = node.getClassName?.() || node.className || "";
      if (cls !== "Image") return false;
      return node.getAttr?.("pamelaPanel") === true || node.getAttr?.("pamelaClipart") === true || pamelaIsLaserArtworkNode(node);
    } catch (_) {
      return false;
    }
  }

  function pamelaGetNodeCenterX(node) {
    try {
      if (!node) return null;
      if (pamelaIsTextLikeNode(node)) {
        const layer = node.getLayer?.();
        const c = pamelaGetVisualCenter(node, layer);
        if (Number.isFinite(c?.x)) return c.x;
      }
      const x = Number(node.x?.() ?? node.attrs?.x);
      return Number.isFinite(x) ? x : null;
    } catch (_) {
      return null;
    }
  }

  function pamelaGetNodeAlignY(editor, node) {
    try {
      if (!pamelaIsGuideAlignNode(node)) return null;
      const cls = node.getClassName?.() || node.className || "";
      if (pamelaIsTextLikeNode(node)) return pamelaGetTextBaselineY(editor, node);
      const y = Number(node.y?.() ?? node.attrs?.y);
      return Number.isFinite(y) ? y : null;
    } catch (_) {
      return null;
    }
  }

  function pamelaGetGuideAlignCandidates(editor, node) {
    try {
      if (!editor || !node) return [];
      const out = [];
      const pushKids = (layer) => {
        const kids = layer?.getChildren?.() || [];
        kids.forEach((n) => {
          if (!n || n === node) return;
          if (!pamelaIsGuideAlignNode(n)) return;
          out.push(n);
        });
      };
      pushKids(editor.layers?.content);
      pushKids(editor.layers?.panels);
      return out;
    } catch (_) {
      return [];
    }
  }

  function pamelaGetNearestHorizontalAlignment(editor, node) {
    try {
      if (!editor || !node || !pamelaIsGuideAlignNode(node)) return null;
      const myY = pamelaGetNodeAlignY(editor, node);
      if (!Number.isFinite(myY)) return null;

      const isCompanion = editor.layout === "companion";
      const mySide = isCompanion ? pamelaGetSide(editor, node) : "any";
      const cls = node.getClassName?.() || node.className || "";
      const isArtImage = pamelaIsArtAlignImage(node);
      const isText = pamelaIsTextLikeNode(node);
      const others = pamelaGetGuideAlignCandidates(editor, node).filter((other) => {
        if (!isArtImage) return true;
        return pamelaIsArtAlignImage(other);
      });

      let bestNode = null;
      let bestY = null;
      let bestDelta = Infinity;

      for (const other of others) {
        if (isCompanion && !isArtImage && !isText) {
          const side = pamelaGetSide(editor, other);
          if (side !== mySide) continue;
        }
        const otherY = pamelaGetNodeAlignY(editor, other);
        if (!Number.isFinite(otherY)) continue;
        const delta = Math.abs(otherY - myY);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestY = otherY;
          bestNode = other;
        }
      }

      if (!bestNode || !Number.isFinite(bestY)) return null;
      return { node: bestNode, alignY: bestY, deltaY: bestY - myY, absDeltaY: bestDelta };
    } catch (_) {
      return null;
    }
  }

  function pamelaSnapNodeHorizontalAlignment(editor, node) {
    try {
      const match = pamelaGetNearestHorizontalAlignment(editor, node);
      if (!match) return false;
      if (match.absDeltaY > pamelaGetEffectiveSnapThreshold(editor, PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX)) return false;
      node.y(node.y() + match.deltaY);
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaGetNearestClipartCenterAlignment(editor, node) {
    try {
      if (!editor || !node) return null;
      if (!pamelaIsArtAlignImage(node)) return null;

      const myX = pamelaGetNodeCenterX(node);
      if (!Number.isFinite(myX)) return null;

      const isCompanion = editor.layout === "companion";
      const mySide = isCompanion ? pamelaGetSide(editor, node) : "any";
      const others = pamelaGetGuideAlignCandidates(editor, node);

      let bestNode = null;
      let bestX = null;
      let bestDelta = Infinity;

      for (const other of others) {
        if (isCompanion) {
          const side = pamelaGetSide(editor, other);
          if (side !== mySide) continue;
        }
        const otherX = pamelaGetNodeCenterX(other);
        if (!Number.isFinite(otherX)) continue;
        const delta = Math.abs(otherX - myX);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestX = otherX;
          bestNode = other;
        }
      }

      if (!bestNode || !Number.isFinite(bestX)) return null;
      return { node: bestNode, alignX: bestX, deltaX: bestX - myX, absDeltaX: bestDelta };
    } catch (_) {
      return null;
    }
  }

  function pamelaSnapClipartCenterXAlignment(editor, node) {
    try {
      const match = pamelaGetNearestClipartCenterAlignment(editor, node);
      if (!match) return false;
      if (match.absDeltaX > pamelaGetEffectiveSnapThreshold(editor, PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX)) return false;
      node.x(node.x() + match.deltaX);
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaSnapLaserArtworkCenterX(editor, node) {
    try {
      if (!editor || !node || !pamelaIsLaserArtworkNode(node)) return false;
      const { fullCenter } = pamelaGetCenters(editor);
      const x = Number(node.x?.() ?? node.attrs?.x);
      if (!Number.isFinite(x) || !Number.isFinite(fullCenter)) return false;
      const deltaX = fullCenter - x;
      if (Math.abs(deltaX) > pamelaGetEffectiveSnapThreshold(editor, PAMELA_SNAP_THRESHOLD_PX)) return false;
      node.x(fullCenter);
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaIsSingleCenterSnapImage(editor, node) {
    try {
      if (!editor || editor.layout !== "single" || !node) return false;
      const cls = node.getClassName?.() || node.className || "";
      if (cls !== "Image") return false;
      return node.getAttr?.("pamelaClipart") === true || node.getAttr?.("pamelaPanel") === true || pamelaIsLaserArtworkNode(node);
    } catch (_) {
      return false;
    }
  }

  function pamelaGetSingleBackgroundCenterSnap(editor, node) {
    try {
      if (!pamelaIsSingleCenterSnapImage(editor, node)) return null;
      const w = editor.state?.design?.width || DESIGN_PX.w;
      const h = editor.state?.design?.height || DESIGN_PX.h;
      const { fullCenter } = pamelaGetCenters(editor);
      const letteringRect = pamelaGetLetteringRectForBackground(editor?.nodes?.bg);
      const centerX = fullCenter;
      const centerY = letteringRect
        ? letteringRect.top + letteringRect.height / 2
        : h / 2;
      const x = Number(node.x?.() ?? node.attrs?.x);
      const y = Number(node.y?.() ?? node.attrs?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

      const deltaX = centerX - x;
      const deltaY = centerY - y;
      const snapThreshold = pamelaGetEffectiveSnapThreshold(editor, PAMELA_SNAP_THRESHOLD_PX);
      return {
        centerX,
        centerY,
        deltaX,
        deltaY,
        snapX: Math.abs(deltaX) <= snapThreshold,
        snapY: Math.abs(deltaY) <= snapThreshold,
      };
    } catch (_) {
      return null;
    }
  }

  function pamelaSnapImageToSingleBackgroundCenter(editor, node) {
    try {
      const match = pamelaGetSingleBackgroundCenterSnap(editor, node);
      if (!match) return false;
      let snapped = false;
      if (match.snapX) {
        node.x(match.centerX);
        snapped = true;
      }
      if (match.snapY) {
        node.y(match.centerY);
        snapped = true;
      }
      return snapped;
    } catch (_) {
      return false;
    }
  }

  function pamelaIsPortraitWrapperNode(node) {
    try {
      if (!node) return false;
      const nm = String(node.name?.() || node.attrs?.name || "").toLowerCase();
      return (
        node.getAttr?.("__isPortraitGroup") === true ||
        node.getAttr?.("pamelaType") === "portrait" ||
        node.getAttr?.("__pamelaKind") === "portrait" ||
        nm.includes("pamela-portrait-group")
      );
    } catch (_) {
      return false;
    }
  }

  function pamelaGetPortraitWrapperSize(node) {
    try {
      const ppi = Number(node.getAttr?.("__portraitPPI")) || 72;
      const wIn = Number(node.getAttr?.("__portraitWIn")) || 4;
      const hIn = Number(node.getAttr?.("__portraitHIn")) || 4;
      return {
        width: Math.max(1, wIn * ppi),
        height: Math.max(1, hIn * ppi)
      };
    } catch (_) {
      return { width: 288, height: 288 };
    }
  }

  function pamelaGetPortraitCenterSnap(editor, node) {
    try {
      if (!editor || !pamelaIsPortraitWrapperNode(node)) return null;
      const { fullCenter, leftCenter, rightCenter } = pamelaGetCenters(editor);
      const h = editor.state?.design?.height || DESIGN_PX.h;
      const letteringRect = pamelaGetLetteringRectForBackground(editor?.nodes?.bg);
      const size = pamelaGetPortraitWrapperSize(node);
      const nodeCenterX = Number(node.x?.() || 0) + size.width / 2;
      const nodeCenterY = Number(node.y?.() || 0) + size.height / 2;
      const xTargets = editor.layout === "companion"
        ? [leftCenter, fullCenter, rightCenter]
        : [fullCenter];
      const yTargets = [
        letteringRect ? letteringRect.top + letteringRect.height / 2 : h / 2
      ];

      let bestX = xTargets[0];
      let bestAbsDx = Infinity;
      for (const targetX of xTargets) {
        const absDx = Math.abs(nodeCenterX - targetX);
        if (absDx < bestAbsDx) {
          bestAbsDx = absDx;
          bestX = targetX;
        }
      }

      let bestY = yTargets[0];
      let bestAbsDy = Infinity;
      for (const targetY of yTargets) {
        const absDy = Math.abs(nodeCenterY - targetY);
        if (absDy < bestAbsDy) {
          bestAbsDy = absDy;
          bestY = targetY;
        }
      }

      return {
        centerX: bestX,
        centerY: bestY,
        deltaX: bestX - nodeCenterX,
        deltaY: bestY - nodeCenterY,
        snapX: bestAbsDx <= PAMELA_SNAP_THRESHOLD_PX,
        snapY: bestAbsDy <= PAMELA_SNAP_THRESHOLD_PX
      };
    } catch (_) {
      return null;
    }
  }

  function pamelaSnapPortraitWrapperCenter(editor, node) {
    try {
      const match = pamelaGetPortraitCenterSnap(editor, node);
      if (!match) return false;
      let snapped = false;
      if (match.snapX) {
        node.x(node.x() + match.deltaX);
        snapped = true;
      }
      if (match.snapY) {
        node.y(node.y() + match.deltaY);
        snapped = true;
      }
      return snapped;
    } catch (_) {
      return false;
    }
  }

  function pamelaSnapTextToCenterX(editor, t) {
    try {
      if (!editor || !t || !pamelaIsTextLikeNode(t)) return;

      const moveVisualCenterTo = (targetX) => {
        const curX = pamelaGetNodeCenterX(t);
        if (Number.isFinite(curX) && Number.isFinite(targetX)) t.x(t.x() + (targetX - curX));
      };
      const currentCenterX = () => pamelaGetNodeCenterX(t);
      const type = String(t.attrs?.pamelaFieldType || "");
      const isCompanion = editor.layout === "companion";
     const { fullCenter, leftCenter, rightCenter } = pamelaGetCenters(editor);


      const panelMatch = pamelaGetNearestPanelCenterX(editor, t);

      if (type === "date") {
        pamelaSnapDateToNearestNameCenterX(editor, t);
        if (panelMatch && panelMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
          moveVisualCenterTo(panelMatch.centerX);
        } else if (!isCompanion && Math.abs(currentCenterX() - fullCenter) <= PAMELA_SNAP_THRESHOLD_PX) {
          moveVisualCenterTo(fullCenter);
        }
        return;
      }

      if (!(type === "lastname" || type === "name" || type === "verse" || type === "wedding")) return;

      if (type === "lastname") {
        if (Math.abs(currentCenterX() - fullCenter) <= PAMELA_SNAP_THRESHOLD_PX) moveVisualCenterTo(fullCenter);
        return;
      }

      const layer = editor.layers?.content;
      const mySide = isCompanion ? pamelaGetSide(editor, t) : "any";
      const sideCenter = !isCompanion ? fullCenter : mySide === "left" ? leftCenter : rightCenter;

      // NAME: snap to other name X (same side) if close
      if (type === "name" && layer) {
        const others = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n) && n.attrs?.pamelaFieldType === "name");

        let bestX = null;
        let bestAbsDx = Infinity;

        for (const n of others) {
          if (isCompanion) {
            const side = pamelaGetSide(editor, n);
            if (side !== mySide) continue;
          }
          const nx = pamelaGetNodeCenterX(n);
          if (!Number.isFinite(nx)) continue;
          const tx = pamelaGetNodeCenterX(t);
          if (!Number.isFinite(tx)) continue;
          const adx = Math.abs(nx - tx);
          if (adx < bestAbsDx) {
            bestAbsDx = adx;
            bestX = nx;
          }
        }

        if (bestX != null && Math.abs(currentCenterX() - bestX) <= PAMELA_SNAP_THRESHOLD_PX) {
          moveVisualCenterTo(bestX);
          return;
        }
      }

      if (panelMatch && panelMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
        moveVisualCenterTo(panelMatch.centerX);
        return;
      }

      // WEDDING / VERSE: gently snap to the nearest other text center first
      if ((type === "wedding" || type === "verse") && layer) {
        const others = layer.getChildren((n) => n && n !== t && pamelaIsTextLikeNode(n));

        let bestX = null;
        let bestAbsDx = Infinity;

        for (const n of others) {
          const nx = pamelaGetNodeCenterX(n);
          if (!Number.isFinite(nx)) continue;
          const tx = pamelaGetNodeCenterX(t);
          if (!Number.isFinite(tx)) continue;
          const adx = Math.abs(nx - tx);
          if (adx < bestAbsDx) {
            bestAbsDx = adx;
            bestX = nx;
          }
        }

        if (bestX != null && Math.abs(currentCenterX() - bestX) <= PAMELA_SNAP_THRESHOLD_PX) {
          moveVisualCenterTo(bestX);
          return;
        }
      }

      if (type === "wedding" || type === "verse") {
        if (Math.abs(currentCenterX() - fullCenter) <= PAMELA_SNAP_THRESHOLD_PX) moveVisualCenterTo(fullCenter);
        return;
      }

      if (Math.abs(currentCenterX() - sideCenter) <= PAMELA_SNAP_THRESHOLD_PX) moveVisualCenterTo(sideCenter);
    } catch (_) {}
  }
  
  // =============================
  // Shopify Product JSON fetch
  // =============================
  async function fetchProductJsonByHandle(handle) {
    const h = String(handle || "").trim();
    if (!h) throw new Error("Missing product handle in URL (?product=...)");
    const url = `/products/${encodeURIComponent(h)}.js`;
    const res = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`Failed to fetch product JSON (${res.status}) from ${url}`);
    return res.json();
  }

  async function fetchEditorAssetMapByHandle(handle) {
    const h = String(handle || "").trim();
    if (!h) return {};

    const apiBase =
      window.CM_SVG_API_BASE ||
      "https://cm-editor-server.onrender.com";

    const url =
      apiBase.replace(/\/+$/, "") +
      "/api/editor-asset-map?handle=" +
      encodeURIComponent(h);

    const res = await fetch(url, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch editor asset map (${res.status}) from ${url}`);
    }

    const data = await res.json();
    return (data && typeof data === "object") ? data : {};
  }

  function fetchStoredEditorAssetMapByHandle(handle) {
    const h = String(handle || "").trim();
    if (!h) return {};

    try {
      const raw = window.sessionStorage.getItem(`pamela_editor_asset_map:${h}`) || "";
      if (!raw) return {};

      const data = JSON.parse(raw);
      return (data && typeof data === "object") ? data : {};
    } catch (_) {
      return {};
    }
  }

  //Find light/dark setting from variant metafiled to apply clipart/panel color rules

    async function fetchGraniteToneMapByHandle(handle) {
    const h = String(handle || "").trim();
    if (!h) return {};

    const apiBase =
      window.CM_SVG_API_BASE ||
      "https://cm-editor-server.onrender.com";

    const url =
      apiBase.replace(/\/+$/, "") +
      "/api/granite-tone-map?handle=" +
      encodeURIComponent(h);

    const res = await fetch(url, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch granite tone map (${res.status}) from ${url}`);
    }

    const data = await res.json();
    return (data && typeof data === "object") ? data : {};
  }

  async function fetchVariantImageMapByHandle(handle) {
  const h = String(handle || "").trim();
  if (!h) return {};

  const apiBase =
    window.CM_SVG_API_BASE ||
    "https://cm-editor-server.onrender.com";

  const url =
    apiBase.replace(/\/+$/, "") +
    "/api/variant-image-map?handle=" +
    encodeURIComponent(h);

  const res = await fetch(url, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch variant image map (${res.status}) from ${url}`);
  }

  const data = await res.json();
  return (data && typeof data === "object") ? data : {};
}

  function pamelaSelectedGraniteTone() {
    const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;
    const toneMap = window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ || {};
    const variantId = String(selectedVariant?.id || "").trim();

    if (variantId && toneMap && typeof toneMap === "object") {
      const tone = String(toneMap[variantId] || "").trim().toLowerCase();
      if (tone === "light") return "light";
    }

    return "dark";
  }

  function pamelaSelectedGraniteIsLight() {
    return pamelaSelectedGraniteTone() === "light";
  }

function pamelaUpdateLightGraniteToggleVisibility() {
  const isLight = pamelaSelectedGraniteIsLight();

  const panelBtn = document.getElementById("pamela-panel-mode-toggle");
  const clipartBtn = document.getElementById("pamela-clipart-mode-toggle");

  if (panelBtn) {
    panelBtn.style.display = isLight ? "none" : "";
  }

  if (clipartBtn) {
    clipartBtn.style.display = isLight ? "none" : "";
  }
}

  function pamelaDefaultSvgMode() {
    return pamelaSelectedGraniteIsLight() ? "standard" : "white-outline";
  }

  function pamelaGetActiveSvgMode() {
    const forced = String(window.__PAMELA_FORCED_SVG_MODE__ || "").trim().toLowerCase();

    if (
      forced === "standard" ||
      forced === "white-outline" ||
      forced === "reverse-process"
    ) {
      return forced;
    }

    return pamelaDefaultSvgMode();
  }

  function pamelaTransformSvgTextForGranite(svgText, mode) {
    let svg = String(svgText || "");
    const m = String(mode || "").trim().toLowerCase();

    if (!svg) return svg;

    // Normalize common color forms first
    svg = svg.replace(/#E5E5E5/gi, "#e5e5e5");
    svg = svg.replace(/#000000/gi, "#000000");
    svg = svg.replace(/#000\b/gi, "#000000");
    svg = svg.replace(/\bblack\b/gi, "#000000");
    svg = svg.replace(/rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)/gi, "#000000");
    svg = svg.replace(/#FFFFFF/gi, "#ffffff");
    svg = svg.replace(/#FFF\b/gi, "#ffffff");
    svg = svg.replace(/\bwhite\b/gi, "#ffffff");

    // Never allow visible strokes
    svg = svg.replace(/\sstroke="[^"]*"/gi, ' stroke="none"');
    svg = svg.replace(/\sstroke-width="[^"]*"/gi, "");
    svg = svg.replace(/\sstroke-opacity="[^"]*"/gi, "");

    // Also remove stroke declarations inside inline style=""
    svg = svg.replace(/style="([^"]*)"/gi, (full, styleText) => {
      let s = String(styleText || "");

      s = s.replace(/(?:^|;)\s*stroke\s*:\s*[^;"]*/gi, "");
      s = s.replace(/(?:^|;)\s*stroke-width\s*:\s*[^;"]*/gi, "");
      s = s.replace(/(?:^|;)\s*stroke-opacity\s*:\s*[^;"]*/gi, "");

      s = s.replace(/^;+|;+$/g, "").trim();
      return s ? `style="${s}"` : "";
    });

    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const root = doc.documentElement;

      if (root && !root.querySelector("parsererror")) {
        const normalizeColor = (value) => {
          const raw = String(value || "").trim().toLowerCase();
          if (!raw) return "";
          if (raw === "black" || raw === "#000" || raw === "#000000" || raw === "rgb(0,0,0)" || raw === "rgb(0, 0, 0)") return "black";
          if (raw === "white" || raw === "#fff" || raw === "#ffffff" || raw === "rgb(255,255,255)" || raw === "rgb(255, 255, 255)") return "white";
          if (raw === "#e5e5e5" || raw === "rgb(229,229,229)" || raw === "rgb(229, 229, 229)") return "frost";
          return raw;
        };

        const getStyleValue = (styleText, prop) => {
          const parts = String(styleText || "").split(";");
          for (let i = 0; i < parts.length; i++) {
            const pair = parts[i].split(":");
            if (pair.length < 2) continue;
            if (String(pair[0] || "").trim().toLowerCase() === prop) {
              return pair.slice(1).join(":").trim();
            }
          }
          return "";
        };

        const setStyleValue = (el, prop, value) => {
          const styleText = String(el.getAttribute("style") || "");
          const next = [];
          let found = false;

          styleText.split(";").forEach((part) => {
            const rawPart = String(part || "").trim();
            if (!rawPart) return;
            const pair = rawPart.split(":");
            const key = String(pair[0] || "").trim();
            if (!key) return;
            if (key.toLowerCase() === prop) {
              found = true;
              if (value !== null) next.push(`${key}:${value}`);
            } else {
              next.push(rawPart);
            }
          });

          if (!found && value !== null) next.push(`${prop}:${value}`);
          if (next.length) el.setAttribute("style", next.join(";"));
          else el.removeAttribute("style");
        };

        const applyFill = (el, fill, opacity) => {
          if (el.hasAttribute("fill")) el.setAttribute("fill", fill);
          if (getStyleValue(el.getAttribute("style"), "fill")) setStyleValue(el, "fill", fill);
          if (!el.hasAttribute("fill") && !getStyleValue(el.getAttribute("style"), "fill")) el.setAttribute("fill", fill);

          if (opacity === null) {
            el.removeAttribute("fill-opacity");
            setStyleValue(el, "fill-opacity", null);
          } else {
            el.setAttribute("fill-opacity", opacity);
            if (getStyleValue(el.getAttribute("style"), "fill-opacity")) setStyleValue(el, "fill-opacity", opacity);
          }
        };

        root.querySelectorAll("*").forEach((el) => {
          if (!el || String(el.tagName || "").toLowerCase() === "metadata") return;
          if (String(el.getAttribute("id") || "").trim().toLowerCase() === "text-path") return;

          el.setAttribute("stroke", "none");
          el.removeAttribute("stroke-width");
          el.removeAttribute("stroke-opacity");
          setStyleValue(el, "stroke", null);
          setStyleValue(el, "stroke-width", null);
          setStyleValue(el, "stroke-opacity", null);

          const styleText = String(el.getAttribute("style") || "");
          const fill = normalizeColor(el.getAttribute("fill") || getStyleValue(styleText, "fill"));
          const metaText = String(el.innerHTML || "").toLowerCase();
          const isFrost = fill === "frost" || (metaText.includes("styleid") && metaText.includes("frost"));
          const isLitho = fill === "black" || (metaText.includes("styleid") && metaText.includes("litho"));

          if (!isFrost && !isLitho) return;

          if (m === "standard") {
            if (isFrost) applyFill(el, "#e5e5e5", "0.4");
            else applyFill(el, "#000000", null);
          } else if (m === "white-outline") {
            if (isFrost) applyFill(el, "none", "0");
            else applyFill(el, "#ffffff", null);
          } else if (m === "reverse-process") {
            if (isFrost) applyFill(el, "#ffffff", null);
            else applyFill(el, "none", "0");
          }
        });

        return new XMLSerializer().serializeToString(doc);
      }
    } catch (_) {}

    if (m === "standard") {
      // gray becomes 40%
      svg = svg.replace(/fill="#e5e5e5"/gi, 'fill="#e5e5e5" fill-opacity="0.4"');
      svg = svg.replace(/fill:#e5e5e5/gi, "fill:#e5e5e5;fill-opacity:0.4");

      // black stays black
      svg = svg.replace(/fill="#000000"\s+fill-opacity="[^"]*"/gi, 'fill="#000000"');
      svg = svg.replace(/fill:#000000;?\s*fill-opacity:[^;"]*/gi, "fill:#000000");
    }

    if (m === "white-outline") {
      // gray disappears
      svg = svg.replace(/fill="#e5e5e5"/gi, 'fill="none" fill-opacity="0"');
      svg = svg.replace(/fill:#e5e5e5/gi, "fill:none;fill-opacity:0");

      // black becomes white
      svg = svg.replace(/fill="#000000"/gi, 'fill="#ffffff"');
      svg = svg.replace(/fill:#000000/gi, "fill:#ffffff");
    }

    if (m === "reverse-process") {
      // gray becomes white
      svg = svg.replace(/fill="#e5e5e5"/gi, 'fill="#ffffff"');
      svg = svg.replace(/fill:#e5e5e5/gi, "fill:#ffffff");

      // black disappears
      svg = svg.replace(/fill="#000000"/gi, 'fill="none" fill-opacity="0"');
      svg = svg.replace(/fill:#000000/gi, "fill:none;fill-opacity:0");
    }

    return svg;
  }

  // =====================================================
// SVG RAW TEXT CACHE (speed boost)
// =====================================================
  const PAMELA_SVG_TEXT_CACHE = {};

  async function pamelaGetRawSvgText(url) {
    const src = String(url || "").trim();
    if (!src) return null;

    const isSvg = /\.svg(\?|$)/i.test(src);

let rawSvg = PAMELA_SVG_TEXT_CACHE[src];

if (!rawSvg) {
  const res = await fetch(src, {
    method: "GET",
    credentials: "omit",
    headers: { Accept: "image/svg+xml,text/plain,*/*" },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch SVG (${res.status}) from ${src}`);
  }

  rawSvg = await res.text();
  if (!isSvg && !/<svg[\s>]/i.test(rawSvg)) return null;
  PAMELA_SVG_TEXT_CACHE[src] = rawSvg;
}

    return rawSvg;
  }

  function pamelaExtractSvgTextPathMeta(svgText) {
    const svg = String(svgText || "");
    if (!svg || !/id=["']text-path["']/i.test(svg)) return null;

    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const root = doc.documentElement;
      const path = doc.getElementById("text-path");
      const d = String(path?.getAttribute?.("d") || "").trim();
      if (!d) return null;

      const vbText = String(root?.getAttribute?.("viewBox") || "").trim();
      const nums = vbText
        .split(/[\s,]+/)
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));

      const width = Number(root?.getAttribute?.("width")?.replace(/[^\d. -]/g, ""));
      const height = Number(root?.getAttribute?.("height")?.replace(/[^\d. -]/g, ""));

      return {
        data: d,
        viewBox: nums.length >= 4
          ? { x: nums[0], y: nums[1], width: nums[2], height: nums[3] }
          : { x: 0, y: 0, width: Number.isFinite(width) && width > 0 ? width : 1, height: Number.isFinite(height) && height > 0 ? height : 1 },
      };
    } catch (_) {}

    const m = svg.match(/<path\b[^>]*\bid=["']text-path["'][^>]*\bd=["']([^"']+)["'][^>]*>/i);
    if (!m) return null;
    const vb = svg.match(/\bviewBox=["']([^"']+)["']/i);
    const nums = String(vb?.[1] || "")
      .split(/[\s,]+/)
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    return {
      data: String(m[1] || "").trim(),
      viewBox: nums.length >= 4 ? { x: nums[0], y: nums[1], width: nums[2], height: nums[3] } : { x: 0, y: 0, width: 1, height: 1 },
    };
  }

  async function pamelaLoadSvgTextPathMeta(url) {
    try {
      const rawSvg = await pamelaGetRawSvgText(url);
      return pamelaExtractSvgTextPathMeta(rawSvg);
    } catch (_) {
      return null;
    }
  }

  function pamelaSvgLengthToInches(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return NaN;

    const m = raw.match(/^(-?\d+(?:\.\d+)?)\s*(in|px|pt|mm|cm)?$/i);
    if (!m) return NaN;

    const n = Number(m[1]);
    if (!Number.isFinite(n) || n <= 0) return NaN;

    const unit = String(m[2] || "px").toLowerCase();
    if (unit === "in") return n;
    if (unit === "pt") return n / 72;
    if (unit === "mm") return n / 25.4;
    if (unit === "cm") return n / 2.54;
    return n / 96;
  }

  function pamelaExtractSvgWidthIn(svgText) {
    const svg = String(svgText || "");
    if (!svg) return NaN;

    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const root = doc.documentElement;
      const w = pamelaSvgLengthToInches(root?.getAttribute?.("width"));
      if (Number.isFinite(w) && w > 0) return w;
    } catch (_) {}

    const m = svg.match(/<svg\b[^>]*\bwidth=["']([^"']+)["']/i);
    const w = pamelaSvgLengthToInches(m?.[1]);
    return Number.isFinite(w) && w > 0 ? w : NaN;
  }

  async function pamelaLoadSvgWidthIn(url) {
    try {
      const rawSvg = await pamelaGetRawSvgText(url);
      return pamelaExtractSvgWidthIn(rawSvg);
    } catch (_) {
      return NaN;
    }
  }

  function pamelaGetPanelMetaWidthIn(meta) {
    const candidates = [
      { value: meta?.wIn, inches: true },
      { value: meta?.widthIn, inches: true },
      { value: meta?.width_in, inches: true },
      { value: meta?.width_inches, inches: true },
      { value: meta?.widthInches, inches: true },
      { value: meta?.physicalWidthIn, inches: true },
      { value: meta?.physical_width_in, inches: true },
      { value: meta?.dimensions?.wIn, inches: true },
      { value: meta?.dimensions?.widthIn, inches: true },
      { value: meta?.dimensions?.width_in, inches: true },
    ];

    for (const candidate of candidates) {
      const value = candidate.value;
      const n = candidate.inches ? Number(value) : pamelaSvgLengthToInches(value);
      if (Number.isFinite(n) && n > 0) return n;
    }

    return NaN;
  }

  async function pamelaLoadSvgImageForGranite(url, mode) {
    const src = String(url || "").trim();
    if (!src) return null;

    const isSvg = /\.svg(\?|$)/i.test(src);
    if (!isSvg) {
      return loadImage(src);
    }

    const rawSvg = await pamelaGetRawSvgText(src);
    const transformedSvg = pamelaTransformSvgTextForGranite(rawSvg, mode);

    const blob = new Blob([transformedSvg], { type: "image/svg+xml;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);

    try {
      const img = await loadImage(blobUrl);
      return img;
    } finally {
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (_) {}
      }, 1000);
    }
  }

  function pamelaGetAllPlacedSvgNodes(editor) {
    if (!editor?.layers) return [];

    const out = [];

    const contentLayers = pamelaGetSideLayers(editor, "content");
    contentLayers.forEach((content) => {
      const clipart = content.getChildren((n) => {
        if (!n) return false;
        if (n.className !== "Image") return false;
        const a = n.attrs || {};
        return a.pamelaClipart === true;
      });
      out.push(...clipart);
    });

    const panelLayers = pamelaGetSideLayers(editor, "panels");
    panelLayers.forEach((panels) => {
      const pan = panels.getChildren((n) => {
        if (!n) return false;
        if (n.className !== "Image") return false;
        const a = n.attrs || {};
        return a.pamelaPanel === true;
      });
      out.push(...pan);
    });

    return out;
  }

  function pamelaAttachTextPathMetaToImage(node, meta) {
    if (!node || !meta?.data || !meta?.viewBox) return;
    try { node.setAttr("pamelaTextPathData", String(meta.data || "")); } catch (_) {}
    try { node.setAttr("pamelaTextPathViewBox", meta.viewBox); } catch (_) {}
    try { node.setAttr("pamelaHasTextPath", true); } catch (_) {}
  }

  function pamelaGetTextPathCandidateImages(editor) {
    if (!editor?.layers) return [];
    return pamelaGetAllPlacedSvgNodes(editor).filter((n) => {
      try {
        return !!String(n.getAttr?.("pamelaTextPathData") || "").trim();
      } catch (_) {
        return false;
      }
    });
  }

  function pamelaFindNearestTextPathImage(editor, textNode) {
    const candidates = pamelaGetTextPathCandidateImages(editor);
    if (!candidates.length) return null;

    let tr = null;
    try {
      tr = textNode.getClientRect({
        relativeTo: textNode.getLayer?.() || editor.layers?.content,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
    } catch (_) {}

    const tx = Number.isFinite(tr?.x) ? tr.x + tr.width / 2 : Number(textNode.x?.() || 0);
    const ty = Number.isFinite(tr?.y) ? tr.y + tr.height / 2 : Number(textNode.y?.() || 0);

    let best = null;
    let bestScore = Infinity;
    candidates.forEach((img) => {
      try {
        const layer = img.getLayer?.() || editor.layers?.content;
        const r = img.getClientRect({
          relativeTo: layer,
          skipStroke: true,
          skipShadow: true,
          skipTransform: false,
        });
        const cx = r.x + r.width / 2;
        const cy = r.y + r.height / 2;
        const score = Math.hypot(cx - tx, cy - ty);
        if (score < bestScore) {
          bestScore = score;
          best = img;
        }
      } catch (_) {}
    });

    return best;
  }

  function pamelaCanSelectedTextFollowShape(editor) {
    const sel = editor?.selected;
    if (!sel || !pamelaIsTextLikeNode(sel)) return false;
    if (sel.className === "TextPath") return true;
    return !!pamelaFindNearestTextPathImage(editor, sel);
  }

  function pamelaMakeStraightTextFromTextPath(editor, textPath) {
    if (!editor || !textPath || textPath.className !== "TextPath") return null;

    editor._historyCommit?.("remove-shape");
    const layer = editor.layers?.content || textPath.getLayer?.();
    const before = pamelaGetVisualCenter(textPath, layer);

    const t = new Konva.Text({
      x: before.x,
      y: before.y,
      text: String(textPath.text?.() || ""),
      fontFamily: String(textPath.fontFamily?.() || "MDRoman"),
      fontSize: Number(textPath.fontSize?.() || pxFromIn(0.75)),
      fontStyle: String(textPath.fontStyle?.() || "normal"),
      fill: String(textPath.fill?.() || editor.currentTextFill || "#111"),
      align: "center",
      letterSpacing: Number(textPath.letterSpacing?.() || 0),
      draggable: false,
      listening: true,
      name: "pamela-text",
    });

    [
      "pamelaUid",
      "pamelaFieldType",
      "pamelaSlotIndex",
      "pamelaIndependent",
      "pamelaIndependentArmed",
      "pamelaVariantDefaultFill",
      "__cmBackLastNameLayout",
      "__cmBackLastNameInitialized",
      "__cmBackLastNameSizeTouched",
    ].forEach((k) => {
      const v = textPath.getAttr?.(k);
      if (v !== undefined) {
        try { t.setAttr(k, v); } catch (_) {}
      }
    });

    t.setAttr("__pamelaSelectable", true);
    t.setAttr("__pamelaKind", "text");
    t.setAttr("pamelaDrag", true);
    t.setAttr("pamelaDraggable", true);

    try { textPath.destroy(); } catch (_) {}
    editor.layers?.content?.add(t);
    try { pamelaFixTextMetrics(t); } catch (_) {}
    try {
      const after = pamelaGetVisualCenter(t, layer);
      t.x(Number(t.x?.() || 0) + (before.x - after.x));
      t.y(Number(t.y?.() || 0) + (before.y - after.y));
    } catch (_) {}
    try { pamelaBindTextEditHandlers(editor, t); } catch (_) {}
    try { editor._enableDirectPointerDrag?.(t, "content"); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, t); } catch (_) {}
    try { editor._pamelaRestackContentLayer?.(); } catch (_) {}
    try { pamelaEnforceSpacingForText(editor); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(editor, t); } catch (_) {}

    editor.selectNode?.(t);
    editor.layers?.content?.batchDraw?.();
    editor.layers?.ui?.batchDraw?.();
    editor.refreshButtons?.();
    editor.refreshSizeButtons?.();
    editor._refreshClipButtons?.();
    editor._refreshFontUI?.();
    editor._historyCommit?.("remove-shape");
    return t;
  }

  function pamelaTransformSvgPathDataToCanvas(data, viewBox, scaleX, scaleY) {
    const d = String(data || "");
    const vbX = Number(viewBox?.x || 0) || 0;
    const vbY = Number(viewBox?.y || 0) || 0;
    const sx = Number(scaleX || 1) || 1;
    const sy = Number(scaleY || 1) || 1;
    const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) || [];
    const pairCounts = { M: 1, L: 1, T: 1, C: 3, S: 2, Q: 2 };
    const out = [];
    let i = 0;
    let cmd = "";

    const isCommand = (v) => /^[a-zA-Z]$/.test(String(v || ""));
    const fmt = (n) => {
      const v = Number(n);
      return Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : "0";
    };

    while (i < tokens.length) {
      if (isCommand(tokens[i])) {
        cmd = tokens[i++];
        out.push(cmd);
      }
      if (!cmd) break;

      const upper = cmd.toUpperCase();
      const rel = cmd === cmd.toLowerCase();

      if (upper === "Z") continue;

      if (upper === "H") {
        while (i < tokens.length && !isCommand(tokens[i])) {
          const x = Number(tokens[i++]);
          out.push(fmt((rel ? x : x - vbX) * sx));
        }
        continue;
      }

      if (upper === "V") {
        while (i < tokens.length && !isCommand(tokens[i])) {
          const y = Number(tokens[i++]);
          out.push(fmt((rel ? y : y - vbY) * sy));
        }
        continue;
      }

      if (upper === "A") {
        while (i + 6 < tokens.length && !isCommand(tokens[i])) {
          const rx = Number(tokens[i++]);
          const ry = Number(tokens[i++]);
          const rot = Number(tokens[i++]);
          const large = Number(tokens[i++]);
          const sweep = Number(tokens[i++]);
          const x = Number(tokens[i++]);
          const y = Number(tokens[i++]);
          out.push(
            fmt(rx * sx),
            fmt(ry * sy),
            fmt(rot),
            fmt(large),
            fmt(sweep),
            fmt((rel ? x : x - vbX) * sx),
            fmt((rel ? y : y - vbY) * sy)
          );
        }
        continue;
      }

      const pairs = pairCounts[upper] || 0;
      if (!pairs) break;

      while (i + pairs * 2 - 1 < tokens.length && !isCommand(tokens[i])) {
        for (let p = 0; p < pairs; p++) {
          const x = Number(tokens[i++]);
          const y = Number(tokens[i++]);
          out.push(
            fmt((rel ? x : x - vbX) * sx),
            fmt((rel ? y : y - vbY) * sy)
          );
        }
      }
    }

    return out.join(" ");
  }

  function pamelaBuildArcTextPathData(width, strength, slide) {
    const w = Math.max(60, Number(width || 0) || 240);
    const s = clamp(Number(strength || 0) || 0, -w * 0.75, w * 0.75);
    const pad = Math.max(36, w * 0.22);
    const startX = -pad;
    const endX = w + pad;
    const controlX = w / 2;
    return `M ${startX} 0 Q ${controlX} ${-s} ${endX} 0`;
  }

  function pamelaArcStepPx() {
    return pxFromIn(1);
  }

  function pamelaArcSlideStepPx() {
    return pxFromIn(0.25);
  }

  function pamelaDesignCenter(editor) {
    return {
      x: Number(editor?.state?.design?.width || DESIGN_PX.w || 0) / 2,
      y: Number(editor?.state?.design?.height || DESIGN_PX.h || 0) / 2,
    };
  }

  function pamelaRotateNodeAroundPoint(node, pivot, deltaDeg) {
    if (!node || !pivot || !Number.isFinite(deltaDeg) || Math.abs(deltaDeg) < 0.0001) return;

    const x0 = Number(node.x?.() || 0);
    const y0 = Number(node.y?.() || 0);
    const dx = x0 - pivot.x;
    const dy = y0 - pivot.y;
    const rad = deltaDeg * Math.PI / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    node.x(pivot.x + dx * cos - dy * sin);
    node.y(pivot.y + dx * sin + dy * cos);
    node.rotation(snapDeg((Number(node.rotation?.() || 0) || 0) + deltaDeg));
  }

  function pamelaSlideArcTextAroundCenter(editor, node, dir) {
    if (!editor || !node) return;

    const pivot = pamelaDesignCenter(editor);
    const center = pamelaGetVisualCenter(node, editor.layers?.content);
    const dx = center.x - pivot.x;
    const dy = center.y - pivot.y;
    const radius = Math.max(1, Math.hypot(dx, dy));
    const stepPx = pamelaArcSlideStepPx();
    const deltaDeg = (dir >= 0 ? 1 : -1) * (stepPx / radius) * (180 / Math.PI);

    pamelaRotateNodeAroundPoint(node, pivot, deltaDeg);
  }

  function pamelaGetVisualCenter(node, layer) {
    try {
      const r = node.getClientRect({
        relativeTo: layer || node.getLayer?.(),
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
      if (Number.isFinite(r?.x) && Number.isFinite(r?.y) && Number.isFinite(r?.width) && Number.isFinite(r?.height)) {
        return { x: r.x + r.width / 2, y: r.y + r.height / 2, rect: r };
      }
    } catch (_) {}
    return { x: Number(node?.x?.() || 0), y: Number(node?.y?.() || 0), rect: null };
  }

  function pamelaCopyTextAttrs(fromNode, toNode) {
    [
      "pamelaUid",
      "pamelaFieldType",
      "pamelaSlotIndex",
      "pamelaIndependent",
      "pamelaIndependentArmed",
      "pamelaVariantDefaultFill",
      "__cmBackLastNameLayout",
      "__cmBackLastNameInitialized",
      "__cmBackLastNameSizeTouched",
    ].forEach((k) => {
      const v = fromNode?.getAttr?.(k);
      if (v !== undefined) {
        try { toNode.setAttr(k, v); } catch (_) {}
      }
    });
  }

  function pamelaBindTextEditHandlers(editor, textNode) {
    if (!editor || !textNode || !pamelaIsTextLikeNode(textNode)) return;

    try { textNode.hitStrokeWidth(30); } catch (_) {}

    textNode.off("dblclick.pamelaTextEdit dbltap.pamelaTextEdit");
    textNode.on("dblclick.pamelaTextEdit dbltap.pamelaTextEdit", (e) => {
      try { e.cancelBubble = true; } catch (_) {}
      try { e?.evt?.preventDefault?.(); } catch (_) {}
      if (editor._inlineEditActive) return;
      pamelaStartInlineEdit(editor, textNode);
    });
  }

  function pamelaFinalizeNewTextPath(editor, oldNode, textPath, reason) {
    if (!editor || !textPath) return null;

    textPath.setAttr("pamelaTextPathApplied", true);
    textPath.setAttr("__pamelaSelectable", true);
    textPath.setAttr("__pamelaKind", "text");
    textPath.setAttr("pamelaDrag", true);
    textPath.setAttr("pamelaDraggable", true);

    pamelaBindTextEditHandlers(editor, textPath);
    try { oldNode?.destroy?.(); } catch (_) {}
    editor.layers?.content?.add(textPath);
    try { textPath.moveToTop(); } catch (_) {}
    try { editor._enableDirectPointerDrag?.(textPath, "content"); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, textPath); } catch (_) {}
    try { editor._pamelaRestackContentLayer?.(); } catch (_) {}
    try { pamelaEnforceSpacingForText(editor); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(editor, textPath); } catch (_) {}

    editor.selectNode?.(textPath);
    editor.layers?.content?.batchDraw?.();
    editor.layers?.ui?.batchDraw?.();
    editor.refreshButtons?.();
    editor.refreshSizeButtons?.();
    editor._refreshClipButtons?.();
    editor._refreshFontUI?.();
    editor._historyCommit?.(reason || "curve-text");
    return textPath;
  }

  function pamelaApplyBuiltInArcToSelectedText(editor, dir) {
    const selected = editor?.selected;
    if (!editor || !selected || !pamelaIsTextLikeNode(selected)) return null;
    if (!window.Konva || typeof Konva.TextPath !== "function") {
      alert("Curved text is not available in this editor build.");
      return null;
    }

    editor._historyCommit?.("curve-text");

    if (selected.className === "TextPath" && selected.getAttr?.("pamelaTextPathKind") === "arc") {
      const width = Number(selected.getAttr("pamelaArcWidth") || 240);
      const current = Number(selected.getAttr("pamelaArcStrength") || 0);
      const next = current + (dir > 0 ? pamelaArcStepPx() : -pamelaArcStepPx());
      selected.setAttr("pamelaArcStrength", next);
      selected.setAttr("pamelaTextPathData", pamelaBuildArcTextPathData(width, next, 0));
      selected.data(selected.getAttr("pamelaTextPathData"));
      try { pamelaClampNodeIntoSafeRect(editor, selected); } catch (_) {}
      try { pamelaEnforceSpacingForText(editor); } catch (_) {}
      try { pamelaUpdateOneTextColorForOverlap(editor, selected); } catch (_) {}
      try { selected.getLayer?.()?.batchDraw?.(); } catch (_) {}
      editor._historyCommit?.("curve-text");
      return selected;
    }

    let rect = null;
    try {
      rect = selected.getClientRect({
        relativeTo: editor.layers?.content,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });
    } catch (_) {}

    const cx = Number.isFinite(rect?.x) ? rect.x + rect.width / 2 : Number(selected.x?.() || 0);
    const cy = Number.isFinite(rect?.y) ? rect.y + rect.height / 2 : Number(selected.y?.() || 0);
    const width = Math.max(Number(rect?.width || 0) * 1.35, Number(selected.fontSize?.() || pxFromIn(0.75)) * Math.max(6, String(selected.text?.() || "").length) * 0.55, 160);
    const strength = (dir < 0 ? -1 : 1) * pamelaArcStepPx();
    const data = pamelaBuildArcTextPathData(width, strength, 0);

    const tp = new Konva.TextPath({
      data,
      x: cx - width / 2,
      y: cy,
      rotation: Number(selected.rotation?.() || 0),
      text: String(selected.text?.() || ""),
      fontFamily: String(selected.fontFamily?.() || "MDRoman"),
      fontSize: Number(selected.fontSize?.() || pxFromIn(0.75)),
      fontStyle: String(selected.fontStyle?.() || "normal"),
      fill: String(selected.fill?.() || editor.currentTextFill || "#111"),
      align: "center",
      letterSpacing: Number(selected.letterSpacing?.() || 0),
      draggable: false,
      listening: true,
      name: "pamela-text pamela-text-path",
    });

    pamelaCopyTextAttrs(selected, tp);
    tp.setAttr("pamelaTextPathKind", "arc");
    tp.setAttr("pamelaTextPathData", data);
    tp.setAttr("pamelaTextPathViewBox", { x: 0, y: 0, width, height: Math.abs(strength) * 2 + 1 });
    tp.setAttr("pamelaArcWidth", width);
    tp.setAttr("pamelaArcStrength", strength);
    tp.setAttr("pamelaArcSlide", 0);

    return pamelaFinalizeNewTextPath(editor, selected, tp, "curve-text");
  }

  function pamelaAdjustSelectedArcText(editor, action) {
    const node = editor?.selected;
    if (!editor || !node || node.className !== "TextPath") return;

    if (node.getAttr?.("pamelaTextPathKind") !== "arc") {
      if (action === "straight") pamelaMakeStraightTextFromTextPath(editor, node);
      return;
    }

    editor._historyCommit?.("curve-adjust");

    const width = Math.max(60, Number(node.getAttr("pamelaArcWidth") || 240));
    let strength = Number(node.getAttr("pamelaArcStrength") || width * 0.18);
    const arcStep = pamelaArcStepPx();

    if (action === "arc-up") strength += arcStep;
    if (action === "arc-down") strength -= arcStep;
    if (action === "slide-left" || action === "slide-right") {
      const dir = action === "slide-right" ? 1 : -1;
      pamelaSlideArcTextAroundCenter(editor, node, dir);
    }

    const data = pamelaBuildArcTextPathData(width, strength, 0);
    node.setAttr("pamelaArcStrength", strength);
    node.setAttr("pamelaArcSlide", 0);
    node.setAttr("pamelaTextPathData", data);
    try { node.data(data); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, node); } catch (_) {}
    try { pamelaEnforceSpacingForText(editor); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(editor, node); } catch (_) {}
    try { node.getLayer?.()?.batchDraw?.(); } catch (_) {}
    editor.selectNode?.(node);
    editor._historyCommit?.("curve-adjust");
  }

  function pamelaToggleFollowShapeForSelectedText(editor) {
    try {
      const selected = editor?.selected;
      if (!editor || !selected || !pamelaIsTextLikeNode(selected)) return;
      if (selected.className === "TextPath") {
        pamelaMakeStraightTextFromTextPath(editor, selected);
        return;
      }

      if (!window.Konva || typeof Konva.TextPath !== "function") {
        alert("Curved text is not available in this editor build.");
        return;
      }

      const carrier = pamelaFindNearestTextPathImage(editor, selected);
      if (!carrier) {
        alert("No ribbon text path was found on the placed clipart.");
        return;
      }

      const data = String(carrier.getAttr?.("pamelaTextPathData") || "").trim();
      const vb = carrier.getAttr?.("pamelaTextPathViewBox") || {};
      const vbW = Number(vb.width || 0) || Number(carrier.width?.() || 1) || 1;
      const vbH = Number(vb.height || 0) || Number(carrier.height?.() || 1) || 1;
      const vbX = Number(vb.x || 0) || 0;
      const vbY = Number(vb.y || 0) || 0;
      if (!data) return;

      editor._historyCommit?.("follow-shape");

      const carrierW = Number(carrier.width?.() || 1) || 1;
      const carrierH = Number(carrier.height?.() || 1) || 1;
      const carrierScaleX = Math.abs(Number(carrier.scaleX?.() || 1) || 1);
      const carrierScaleY = Math.abs(Number(carrier.scaleY?.() || 1) || 1);
      const drawnW = carrierW * carrierScaleX;
      const drawnH = carrierH * carrierScaleY;
      const pathScaleX = drawnW / vbW;
      const pathScaleY = drawnH / vbH;
      const canvasPathData = pamelaTransformSvgPathDataToCanvas(data, { x: vbX, y: vbY }, pathScaleX, pathScaleY);

      const tp = new Konva.TextPath({
        data: canvasPathData,
        x: Number(carrier.x?.() || 0) - drawnW / 2,
        y: Number(carrier.y?.() || 0) - drawnH / 2,
        rotation: Number(carrier.rotation?.() || 0),
        text: String(selected.text?.() || ""),
        fontFamily: String(selected.fontFamily?.() || "MDRoman"),
        fontSize: Number(selected.fontSize?.() || pxFromIn(0.75)),
        fontStyle: String(selected.fontStyle?.() || "normal"),
        fill: String(selected.fill?.() || editor.currentTextFill || "#111"),
        align: "center",
        letterSpacing: Number(selected.letterSpacing?.() || 0),
        draggable: false,
        listening: true,
        name: "pamela-text pamela-text-path",
      });

      pamelaCopyTextAttrs(selected, tp);
      tp.setAttr("pamelaTextPathSourceUid", carrier.getAttr?.("pamelaUid") || "");
      tp.setAttr("pamelaTextPathKind", "shape");
      tp.setAttr("pamelaTextPathData", canvasPathData);
      tp.setAttr("pamelaTextPathViewBox", { x: vbX, y: vbY, width: vbW, height: vbH });
      pamelaFinalizeNewTextPath(editor, selected, tp, "follow-shape");
    } catch (e) {
      console.warn("Follow shape failed:", e);
      alert("Could not apply curved shape text yet.");
    }
  }

  function pamelaCloseCurveTextMenu() {
    const menu = document.getElementById("pamela-curve-text-menu");
    if (menu) menu.style.display = "none";
  }

  function pamelaEnsureCurveTextMenu(editor) {
    let menu = document.getElementById("pamela-curve-text-menu");
    if (menu) return menu;

    menu = document.createElement("div");
    menu.id = "pamela-curve-text-menu";
    menu.style.cssText =
      "position:fixed;z-index:2147483646;display:none;background:#fff;color:#111;" +
      "border:1px solid #111;border-radius:8px;box-shadow:0 10px 26px rgba(0,0,0,.18);" +
      "padding:6px;gap:6px;grid-template-columns:1fr;width:196px;max-width:calc(100vw - 24px);" +
      "font:800 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;";

    const addBtn = (id, label, fn) => {
      const b = document.createElement("button");
      b.type = "button";
      b.id = id;
      b.innerHTML = label;
      b.style.cssText =
        "min-height:38px;padding:7px 9px;border-radius:7px;border:1px solid #111;background:#fff;color:#111;" +
        "font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;" +
        "display:flex;align-items:center;justify-content:space-between;gap:6px;overflow:hidden;";
      b.addEventListener("click", (e) => {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
        fn();
        try { editor?._refreshClipButtons?.(); } catch (_) {}
        try { pamelaRefreshCurveTextMenuState(editor); } catch (_) {}
      });
      menu.appendChild(b);
      return b;
    };

    const curveIcon = (kind) => {
      const isDown = kind === "down";
      const isSlide = kind === "slide-left" || kind === "slide-right";
      const path = isDown ? "M 8 18 Q 38 28 68 18" : "M 8 24 Q 38 8 68 24";
      if (isSlide) {
        const dir = kind === "slide-left" ? -1 : 1;
        const angle = dir * 16;
        const arcSweep = dir < 0 ? "M 38 31 A 21 21 0 0 0 22 14" : "M 38 31 A 21 21 0 0 1 54 14";
        const arrow = dir < 0 ? "M 22 14 L 27 14 L 24 18" : "M 54 14 L 49 14 L 52 18";
        return `
          <svg width="54" height="26" viewBox="0 0 76 36" aria-hidden="true" focusable="false" style="flex:0 0 auto;">
            <path d="${arcSweep}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
            <path d="${arrow}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M 38 31 L 38 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 3" opacity=".7"/>
            <circle cx="38" cy="31" r="2.5" fill="currentColor"/>
            <text x="38" y="13" text-anchor="middle" dominant-baseline="middle"
              font-family="Georgia, serif" font-size="16" font-weight="900" fill="currentColor"
              transform="rotate(${angle} 38 31)">ABC</text>
          </svg>`;
      }
      const textY = isDown ? 17 : 25;
      return `
        <svg width="54" height="26" viewBox="0 0 76 36" aria-hidden="true" focusable="false" style="flex:0 0 auto;">
          <path d="${path}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
          <text x="38" y="${textY}" text-anchor="middle" dominant-baseline="middle"
            font-family="Georgia, serif" font-size="16" font-weight="900" fill="currentColor"
            transform="rotate(${isDown ? 5 : -5} 38 ${textY})">ABC</text>
        </svg>`;
    };

    addBtn("pamela-curve-follow-shape", "Fit to Shape", () => pamelaToggleFollowShapeForSelectedText(editor));
    addBtn("pamela-curve-straight", "Straighten", () => {
      const sel = editor?.selected;
      if (sel?.className === "TextPath") pamelaMakeStraightTextFromTextPath(editor, sel);
    });
    addBtn("pamela-curve-arc-up", `Arc Up ${curveIcon("up")}`, () => {
      const sel = editor?.selected;
      if (sel?.className === "TextPath" && sel.getAttr?.("pamelaTextPathKind") === "arc") pamelaAdjustSelectedArcText(editor, "arc-up");
      else pamelaApplyBuiltInArcToSelectedText(editor, +1);
    });
    addBtn("pamela-curve-arc-down", `Arc Down ${curveIcon("down")}`, () => {
      const sel = editor?.selected;
      if (sel?.className === "TextPath" && sel.getAttr?.("pamelaTextPathKind") === "arc") pamelaAdjustSelectedArcText(editor, "arc-down");
      else pamelaApplyBuiltInArcToSelectedText(editor, -1);
    });
    addBtn("pamela-curve-slide-left", `Slide Left ${curveIcon("slide-left")}`, () => pamelaAdjustSelectedArcText(editor, "slide-left"));
    addBtn("pamela-curve-slide-right", `Slide Right ${curveIcon("slide-right")}`, () => pamelaAdjustSelectedArcText(editor, "slide-right"));

    const stop = (e) => {
      try { e.stopPropagation(); } catch (_) {}
    };
    menu.addEventListener("pointerdown", stop, true);
    menu.addEventListener("mousedown", stop, true);
    menu.addEventListener("touchstart", stop, true);
    document.body.appendChild(menu);

    if (!window.__pamelaCurveTextMenuOutsideClick) {
      window.__pamelaCurveTextMenuOutsideClick = true;
      document.addEventListener("pointerdown", (e) => {
        const m = document.getElementById("pamela-curve-text-menu");
        const btn = document.getElementById("pamela-follow-shape");
        if (!m || m.style.display === "none") return;
        const t = e.target;
        if (m.contains(t) || btn?.contains?.(t)) return;
        m.style.display = "none";
      }, true);
    }

    return menu;
  }

  function pamelaRefreshCurveTextMenuState(editor) {
    const menu = document.getElementById("pamela-curve-text-menu");
    if (!menu) return;

    const sel = editor?.selected;
    const isText = !!(sel && pamelaIsTextLikeNode(sel));
    const isArc = !!(sel && sel.className === "TextPath" && sel.getAttr?.("pamelaTextPathKind") === "arc");
    const canShape = !!(sel && sel.className !== "TextPath" && pamelaFindNearestTextPathImage(editor, sel));
    const isCurved = !!(sel && sel.className === "TextPath");

    const setEnabled = (id, enabled) => {
      const b = document.getElementById(id);
      if (!b) return;
      b.disabled = !enabled;
      b.style.opacity = enabled ? "1" : "0.35";
      b.style.cursor = enabled ? "pointer" : "not-allowed";
    };

    setEnabled("pamela-curve-follow-shape", isText && canShape);
    setEnabled("pamela-curve-arc-up", isText);
    setEnabled("pamela-curve-arc-down", isText);
    setEnabled("pamela-curve-straight", isCurved);
    setEnabled("pamela-curve-slide-left", isArc);
    setEnabled("pamela-curve-slide-right", isArc);
  }

  function pamelaOpenCurveTextMenu(editor, anchor) {
    const menu = pamelaEnsureCurveTextMenu(editor);
    const btn = anchor || document.getElementById("pamela-follow-shape");
    if (!menu || !btn) return;

    if (menu.style.display === "grid") {
      menu.style.display = "none";
      return;
    }

    pamelaRefreshCurveTextMenuState(editor);

    const r = btn.getBoundingClientRect();
    menu.style.display = "grid";
    const width = Math.min(196, Math.max(168, menu.offsetWidth || 196));
    const height = Math.max(220, menu.offsetHeight || 250);
    const gap = 10;
    const mobile = pamelaIsMobileEditorViewport();
    const stageRect = editor?.stage?.container?.()?.getBoundingClientRect?.() ||
      editor?.wrapEl?.getBoundingClientRect?.() ||
      null;
    const viewportW = window.innerWidth || document.documentElement.clientWidth || width + 24;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || height + 24;
    const sideLeft = stageRect ? stageRect.right + gap : r.right + gap;
    const sideRoom = viewportW - sideLeft - gap;
    let left;
    let top;

    if (!mobile && sideRoom >= width) {
      left = sideLeft;
      top = Math.max(gap, Math.min(viewportH - height - gap, r.top));
    } else if (!mobile && stageRect && stageRect.left - gap >= width) {
      left = Math.max(gap, stageRect.left - width - gap);
      top = Math.max(gap, Math.min(viewportH - height - gap, r.top));
    } else {
      left = Math.max(gap, viewportW - width - gap);
      if (mobile && stageRect && stageRect.top - height - gap >= gap) {
        top = stageRect.top - height - gap;
      } else {
        top = mobile
          ? Math.max(gap, Math.min(viewportH - height - gap, r.bottom + gap))
          : Math.max(gap, Math.min(viewportH - height - gap, r.top));
      }
    }

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }

  function pamelaGetAllPlacedPanels(editor) {
    return pamelaGetAllPlacedSvgNodes(editor).filter((n) => {
      try { return n.getAttr("pamelaPanel") === true; } catch (_) { return false; }
    });
  }

  function pamelaGetAllPlacedClipart(editor) {
    return pamelaGetAllPlacedSvgNodes(editor).filter((n) => {
      try { return n.getAttr("pamelaClipart") === true; } catch (_) { return false; }
    });
  }

  function pamelaIsPanelKnockoutTarget(node) {
    try {
      return !!(
        node &&
        node.className === "Image" &&
        node.getAttr?.("pamelaPanel") === true &&
        typeof node.image === "function"
      );
    } catch (_) {
      return false;
    }
  }

  function pamelaIsPanelKnockoutBlocker(node) {
    try {
      if (!node || node.className !== "Image") return false;
      if (node.isVisible && !node.isVisible()) return false;

      const a = node.attrs || {};
      const nm = String(typeof node.name === "function" ? node.name() || "" : "");
      const kind = String(a.__pamelaKind || a.pamelaType || "").trim().toLowerCase();

      if (
        kind === "portrait" ||
        kind === "vase" ||
        a.__isPortraitGroup === true ||
        a.pamelaPortrait === true ||
        nm.includes("pamela-portrait") ||
        nm.includes("pamela-vase")
      ) {
        return false;
      }

      return a.pamelaClipart === true || a.pamelaPanel === true;
    } catch (_) {
      return false;
    }
  }

  function pamelaGetPlacedNodeSide(editor, node) {
    try {
      const layer = node?.getLayer?.();
      const sides = editor?.layers?.sides;
      if (!layer || !sides) return "";

      for (const side of ["front", "back"]) {
        const bucket = sides?.[side];
        if (!bucket) continue;
        if (
          layer === bucket.panels ||
          layer === bucket.content ||
          layer === bucket.portraits ||
          layer === bucket.vase
        ) {
          return side;
        }
      }
    } catch (_) {}
    return "";
  }

  function pamelaRememberPanelKnockoutSource(panel) {
    if (!pamelaIsPanelKnockoutTarget(panel)) return null;

    const img = panel.image();
    if (!img) return null;

    if (!panel.__pamelaPanelKnockoutSourceImage) {
      panel.__pamelaPanelKnockoutSourceImage = img;
    }

    return panel.__pamelaPanelKnockoutSourceImage || img;
  }

  function pamelaRestorePanelKnockouts(editor) {
    const panels = pamelaGetAllPlacedSvgNodes(editor).filter(pamelaIsPanelKnockoutTarget);

    panels.forEach((panel) => {
      try {
        if (!pamelaIsPanelKnockoutTarget(panel)) return;
        const src = panel.__pamelaPanelKnockoutSourceImage;
        if (!src) {
          pamelaRememberPanelKnockoutSource(panel);
          return;
        }
        if (panel.image() !== src) panel.image(src);
        panel.__pamelaPanelKnockoutImage = null;
      } catch (_) {}
    });

    try { editor?.layers?.panels?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.content?.batchDraw?.(); } catch (_) {}
  }

  function pamelaRectsOverlap(a, b) {
    return !!(
      a &&
      b &&
      a.width > 0 &&
      a.height > 0 &&
      b.width > 0 &&
      b.height > 0 &&
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  function pamelaApplyBlockerAlphaKnockout(ctx, panel, blocker) {
    const img = blocker?.__pamelaPanelKnockoutMaskImage || blocker?.image?.();
    if (!img) return false;

    try {
      const panelRect = panel.getClientRect({ skipShadow: true, skipStroke: true });
      const blockerRect = blocker.getClientRect({ skipShadow: true, skipStroke: true });
      if (!pamelaRectsOverlap(panelRect, blockerRect)) return false;
    } catch (_) {}

    try {
      const rel = panel
        .getAbsoluteTransform()
        .copy()
        .invert()
        .multiply(blocker.getAbsoluteTransform().copy());
      const m = rel.getMatrix();

      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);

      const w = blocker.width();
      const h = blocker.height();
      const mask = document.createElement("canvas");
      mask.width = Math.max(1, Math.round(w));
      mask.height = Math.max(1, Math.round(h));

      const maskCtx = mask.getContext("2d");
      if (!maskCtx) {
        ctx.drawImage(img, 0, 0, w, h);
        ctx.restore();
        return true;
      }

      maskCtx.drawImage(img, 0, 0, mask.width, mask.height);

      try {
        const data = maskCtx.getImageData(0, 0, mask.width, mask.height);
        const px = data.data;

        for (let i = 3; i < px.length; i += 4) {
          px[i] = px[i] > 0 ? 255 : 0;
        }

        maskCtx.putImageData(data, 0, 0);
      } catch (_) {}

      ctx.drawImage(mask, 0, 0, w, h);
      ctx.restore();
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaRebuildPanelKnockouts(editor) {
    try {
      return pamelaRebuildPanelKnockoutsInner(editor);
    } catch (e) {
      console.error("[Knockout] pamelaRebuildPanelKnockouts THREW:", e);
    }
  }

  function pamelaRebuildPanelKnockoutsInner(editor) {
    if (!editor?.layers) return;

    pamelaRestorePanelKnockouts(editor);

    const panels = pamelaGetAllPlacedSvgNodes(editor).filter(pamelaIsPanelKnockoutTarget);
    const blockers = pamelaGetAllPlacedSvgNodes(editor).filter(pamelaIsPanelKnockoutBlocker);
    if (!panels.length || !blockers.length) return;

    panels.forEach((panel) => {
      try {
        const src = pamelaRememberPanelKnockoutSource(panel);
        if (!src) return;

        const w = Math.max(1, Math.round(panel.width() || src.width || 0));
        const h = Math.max(1, Math.round(panel.height() || src.height || 0));
        if (!w || !h) return;

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(src, 0, 0, w, h);

        let changed = false;
        blockers.forEach((blocker) => {
          if (!blocker || blocker === panel) return;
          const panelSide = pamelaGetPlacedNodeSide(editor, panel);
          const blockerSide = pamelaGetPlacedNodeSide(editor, blocker);
          if (panelSide && blockerSide && panelSide !== blockerSide) return;
          try {
            const panelZ = typeof panel.getAbsoluteZIndex === "function" ? panel.getAbsoluteZIndex() : 0;
            const blockerZ = typeof blocker.getAbsoluteZIndex === "function" ? blocker.getAbsoluteZIndex() : 0;
            if (blockerZ <= panelZ) return;
          } catch (_) {}
          if (pamelaApplyBlockerAlphaKnockout(ctx, panel, blocker)) changed = true;
        });

        if (!changed) return;

        panel.__pamelaPanelKnockoutImage = canvas;
        panel.image(canvas);
      } catch (_) {}
    });

    try { editor.layers.panels?.batchDraw?.(); } catch (_) {}
    try { editor.layers.content?.batchDraw?.(); } catch (_) {}
  }

  function pamelaSchedulePanelKnockoutRebuild(editor) {
    if (!editor) return;
    if (editor.__pamelaPanelKnockoutRaf) return;

    editor.__pamelaPanelKnockoutRaf = requestAnimationFrame(() => {
      editor.__pamelaPanelKnockoutRaf = null;
      pamelaRebuildPanelKnockouts(editor);
    });
  }

  // Exposed globally because setSide()/switchToSide() (defined later in this file,
  // outside this closure) need to trigger a knockout rebuild after switching sides.
  // Without this, those call sites throw "pamelaRebuildPanelKnockouts is not defined",
  // silently caught, so the knockout never recomputes after a side switch.
  window.pamelaRebuildPanelKnockouts = pamelaRebuildPanelKnockouts;
  window.pamelaSchedulePanelKnockoutRebuild = pamelaSchedulePanelKnockoutRebuild;

  function pamelaGetStoredPanelMode(editor) {
    if (pamelaSelectedGraniteIsLight()) return "standard";

    const panels = pamelaGetAllPlacedPanels(editor);

    for (let i = 0; i < panels.length; i++) {
      const n = panels[i];
      const explicit = n.getAttr("pamelaSvgModeExplicit") === true;
      if (!explicit) continue;
      const mode = String(n.getAttr("pamelaPanelMode") || "").trim().toLowerCase();

      if (mode === "standard") return "standard";
      if (mode === "white-outline") return "white-outline";
    }

    return "auto";
  }

  function pamelaAnySvgForcesStandard(editor) {
    if (pamelaSelectedGraniteIsLight()) return true;

    const nodes = pamelaGetAllPlacedSvgNodes(editor);

    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      const explicit = n.getAttr("pamelaSvgModeExplicit") === true;
      if (!explicit) continue;

      const panelMode = String(n.getAttr("pamelaPanelMode") || "").trim().toLowerCase();
      const clipartMode = String(n.getAttr("pamelaClipartMode") || "").trim().toLowerCase();

      if (panelMode === "standard") return true;
      if (clipartMode === "standard") return true;
    }

    return false;
  }

  function pamelaGetEffectivePanelMode(editor) {
    if (pamelaSelectedGraniteIsLight()) return "standard";
    if (pamelaAnySvgForcesStandard(editor)) return "standard";

    const panelMode = pamelaGetStoredPanelMode(editor);

    if (panelMode === "standard") return "standard";
    if (panelMode === "white-outline") return "white-outline";

    return pamelaDefaultSvgMode() === "standard" ? "standard" : "white-outline";
  }

  function pamelaGetModeForNewPanel(editor) {
    if (!editor) return { panelMode: "auto", explicit: false };
    if (pamelaSelectedGraniteIsLight()) {
      return { panelMode: "standard", explicit: false };
    }

    const panels = pamelaGetAllPlacedPanels(editor);

    for (let i = 0; i < panels.length; i++) {
      const node = panels[i];
      if (!node) continue;

      const explicit = node.getAttr("pamelaSvgModeExplicit") === true;
      const storedMode = String(node.getAttr("pamelaPanelMode") || "").trim().toLowerCase();

      if (
        explicit &&
        (storedMode === "standard" || storedMode === "white-outline")
      ) {
        return { panelMode: storedMode, explicit: true };
      }

      const effectiveMode = String(
        node.getAttr("pamelaSvgMode") || pamelaGetEffectivePanelMode(editor)
      ).trim().toLowerCase();

      if (effectiveMode === "standard" || effectiveMode === "white-outline") {
        return { panelMode: effectiveMode, explicit: true };
      }
    }

    return { panelMode: "auto", explicit: false };
  }

  function pamelaGetEffectiveClipartMode(editor, node) {
    if (pamelaSelectedGraniteIsLight()) return "standard";
    if (pamelaAnySvgForcesStandard(editor)) return "standard";

    const explicit = node?.getAttr?.("pamelaSvgModeExplicit") === true;
    const stored = String(node?.getAttr?.("pamelaClipartMode") || "").trim().toLowerCase();
    if (
      explicit &&
      (
        stored === "standard" ||
        stored === "white-outline" ||
        stored === "reverse-process"
      )
    ) {
      return stored;
    }

    return pamelaDefaultSvgMode();
  }

  function pamelaGetEffectiveSvgModeForNode(editor, node) {
    if (!node) return pamelaDefaultSvgMode();

    if (node.getAttr && node.getAttr("pamelaPanel") === true) {
      return pamelaGetEffectivePanelMode(editor);
    }

    if (node.getAttr && node.getAttr("pamelaClipart") === true) {
      return pamelaGetEffectiveClipartMode(editor, node);
    }

    return pamelaDefaultSvgMode();
  }

    async function pamelaRefreshPlacedSvgNodesForGranite(editor) {
    if (!editor?.layers) return;

    const refreshSeq = Number(editor.__pamelaSvgRefreshSeq || 0) + 1;
    editor.__pamelaSvgRefreshSeq = refreshSeq;

    const nodes = pamelaGetAllPlacedSvgNodes(editor);
    if (!nodes.length) {
      if (editor.__pamelaSvgRefreshSeq === refreshSeq) {
        try { pamelaUpdateAllTextColorsForOverlap(editor); } catch (_) {}
      }
      return;
    }

    for (let i = 0; i < nodes.length; i++) {
      if (editor.__pamelaSvgRefreshSeq !== refreshSeq) return;

      const node = nodes[i];
      // Granite changes must update both sides. A hidden side still owns its
      // SVG nodes and would otherwise keep the previous granite's bitmap.
      if (!node) continue;

      const src =
        String(
          node.getAttr?.("pamelaPanelUrl") ||
          node.getAttr?.("pamelaClipartUrl") ||
          ""
        ).trim();

      if (!src) continue;

      const effectiveMode = pamelaGetEffectiveSvgModeForNode(editor, node);

      try {
        const img = await pamelaLoadSvgImageForGranite(src, effectiveMode);
        if (!img) continue;
        if (editor.__pamelaSvgRefreshSeq !== refreshSeq) return;

        if (node.getAttr?.("pamelaPanel") === true) {
          node.__pamelaPanelOriginalImage = img;
          if (pamelaIsLastNamePanelNode(node)) node.__pamelaLastNameSliceSourceImage = img;
          if (pamelaIsNameDatePanelNode(node)) node.__pamelaNameDateSliceSourceImage = img;
          node.__pamelaPanelKnockoutSourceImage = img;
          node.__pamelaPanelKnockoutImage = null;
        } else if (node.getAttr?.("pamelaClipart") === true) {
          node.__pamelaPanelKnockoutSourceImage = img;
          node.__pamelaPanelKnockoutImage = null;
        }
        try {
          node.__pamelaPanelKnockoutMaskImage = await pamelaLoadSvgImageForGranite(src, "standard");
        } catch (_) {
          node.__pamelaPanelKnockoutMaskImage = img;
        }
        if (pamelaIsLastNamePanelNode(node)) {
          node.__pamelaLastNameSliceMaskSourceImage = node.__pamelaPanelKnockoutMaskImage;
          try { pamelaUpdateTraditionalPanelSliceImage(node); } catch (_) { node.image(img); }
        } else if (pamelaIsNameDatePanelNode(node)) {
          node.__pamelaNameDateSliceMaskSourceImage = node.__pamelaPanelKnockoutMaskImage;
          try { pamelaUpdateTraditionalPanelSliceImage(node); } catch (_) { node.image(img); }
        } else {
          node.image(img);
        }
        node.setAttr("pamelaSvgMode", effectiveMode);
      } catch (e) {
        console.warn("[Granite Refresh] Failed to refresh SVG node:", src, e);
      }
    }

    if (editor.__pamelaSvgRefreshSeq !== refreshSeq) return;

    try { editor.layers.panels?.batchDraw?.(); } catch (_) {}
    try { editor.layers.content?.batchDraw?.(); } catch (_) {}

    try { pamelaSchedulePanelKnockoutRebuild(editor); } catch (_) {}
    try { pamelaUpdateAllTextColorsForOverlap(editor); } catch (_) {}
  }

  // =====================================================
// SVG MODE SETTERS (Step 1)
// =====================================================

async function pamelaSetAllPanelsMode(editor, mode) {
  if (!editor) return;

  if (pamelaSelectedGraniteIsLight()) {
    const panels = pamelaGetAllPlacedPanels(editor);
    for (let i = 0; i < panels.length; i++) {
      try {
        panels[i].setAttr("pamelaPanelMode", "standard");
        panels[i].setAttr("pamelaSvgModeExplicit", false);
      } catch (_) {}
    }
    await pamelaRefreshPlacedSvgNodesForGranite(editor);
    return;
  }

  const m = String(mode || "").trim().toLowerCase();
  if (m !== "standard" && m !== "white-outline") return;

  const panels = pamelaGetAllPlacedPanels(editor);
  if (!panels.length) return;

  for (let i = 0; i < panels.length; i++) {
    try {
      panels[i].setAttr("pamelaPanelMode", m);
      panels[i].setAttr("pamelaSvgModeExplicit", true);
    } catch (_) {}
  }

  await pamelaRefreshPlacedSvgNodesForGranite(editor);
}

async function pamelaSetSelectedClipartMode(editor, mode) {
  if (!editor || !editor.selected) return;

  const node = editor.selected;

  const isClipart =
    node &&
    node.getAttr &&
    node.getAttr("pamelaClipart") === true;

  if (!isClipart) return;

  if (pamelaSelectedGraniteIsLight()) {
    try {
      node.setAttr("pamelaClipartMode", "standard");
      node.setAttr("pamelaSvgModeExplicit", false);
    } catch (_) {}
    await pamelaRefreshPlacedSvgNodesForGranite(editor);
    return;
  }

  const m = String(mode || "").trim().toLowerCase();
  if (
    m !== "standard" &&
    m !== "white-outline" &&
    m !== "reverse-process"
  ) return;

  try {
    node.setAttr("pamelaClipartMode", m);
    node.setAttr("pamelaSvgModeExplicit", true);
  } catch (_) {}

  await pamelaRefreshPlacedSvgNodesForGranite(editor);
}

  function pickVariant(productJson, variantId) {
    const variants = Array.isArray(productJson?.variants) ? productJson.variants : [];
    if (!variants.length) return null;
    if (variantId) {
      const v = variants.find((x) => String(x.id) === String(variantId));
      if (v) return v;
    }
    const avail = variants.find((x) => x.available);
    return avail || variants[0];
  }

function pickAssetUrl(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value !== "object") return "";

  const direct =
    value.src ||
    value.url ||
    value.href ||
    value.image ||
    value.image_url ||
    value.imageUrl ||
    value.preview_image?.src ||
    value.preview_image?.url ||
    value.value?.src ||
    value.value?.url ||
    value.value ||
    "";

  return typeof direct === "string" ? direct : "";
}

function pickFirstAssetUrl(...values) {
  for (const value of values) {
    const url = pickAssetUrl(value);
    if (url) return url;
  }
  return "";
}

function pickMappedVariantAsset(map, variantId, keys, opts = {}) {
  const id = String(variantId || "").trim();
  if (!id || !map || typeof map !== "object") return "";

  const entry = map[id];
  if (!entry) return "";
  if (typeof entry === "string") return opts.allowString === false ? "" : entry;

  for (const key of keys) {
    const url = pickAssetUrl(entry?.[key]);
    if (url) return url;
  }

  return "";
}

function pickProductMetafieldAsset(productJson, keys) {
  const p = productJson || {};
  const mf = p?.metafields?.custom || {};

  for (const key of keys) {
    const url = pickFirstAssetUrl(p?.[key], mf?.[key]);
    if (url) return url;
  }

  return "";
}

function pickVariantImageUrl(productJson, variant) {
  const v = variant || {};
  const p = productJson || {};

  const variantId = String(v?.id || "").trim();
  const variantImageMap = window.__PAMELA_VARIANT_IMAGE_MAP__ || {};

  const mfImg = pickMappedVariantAsset(variantImageMap, variantId, [
    "front_background",
    "frontBackground",
    "background",
    "editor_base_image",
    "editorBaseImage"
  ]);

  if (mfImg) return toAbsShopifyUrl(mfImg);

  const vImg = v?.featured_image?.src || v?.featured_image?.url || v?.image || "";
  if (vImg) return toAbsShopifyUrl(vImg);

  const pMetaImg = pickProductMetafieldAsset(p, [
    "front_background",
    "frontBackground",
    "editor_base_image",
    "editorBaseImage"
  ]);

  if (pMetaImg) return toAbsShopifyUrl(pMetaImg);

  const pImg = p?.featured_image || (Array.isArray(p?.images) ? p.images[0] : "");
  return toAbsShopifyUrl(pImg || "");
}

function pickVariantForegroundUrl(productJson, variant) {
  const variantId = String(variant?.id || "").trim();
  const borderMap = window.__PAMELA_VARIANT_BORDER_MAP__ || {};

  const mapped = pickMappedVariantAsset(borderMap, variantId, [
    "front_foreground",
    "frontForeground",
    "foreground",
    "border_mask",
    "borderMask"
  ]);
  if (mapped) return toAbsShopifyUrl(mapped);

  const productMapped = pickProductMetafieldAsset(productJson, [
    "front_foreground",
    "frontForeground",
    "border_mask",
    "borderMask"
  ]);
  return productMapped ? toAbsShopifyUrl(productMapped) : "";
}

function pickSideVariantAssetUrls(productJson, variant, side = "front") {
  const isBack = side === "back";
  const variantId = String(variant?.id || "").trim();
  const assetMap = window.__PAMELA_VARIANT_ASSET_MAP__ || {};
  const imageMap = window.__PAMELA_VARIANT_IMAGE_MAP__ || {};
  const borderMap = window.__PAMELA_VARIANT_BORDER_MAP__ || {};

  const backgroundKeys = isBack
    ? ["back_background", "backBackground", "background_back", "back"]
    : ["front_background", "frontBackground", "background", "editor_base_image", "editorBaseImage"];

  const foregroundKeys = isBack
    ? ["back_foreground", "backForeground", "foreground_back", "back"]
    : ["front_foreground", "frontForeground", "foreground", "border_mask", "borderMask"];

  const mappedAssetBackground = pickMappedVariantAsset(assetMap, variantId, backgroundKeys);
  const mappedAssetForeground = pickMappedVariantAsset(assetMap, variantId, foregroundKeys);

  const mappedBackground = pickMappedVariantAsset(imageMap, variantId, backgroundKeys, {
    allowString: !isBack
  });
  const productBackground = pickProductMetafieldAsset(productJson, backgroundKeys);
  const frontBackground = pickVariantImageUrl(productJson, variant);

  const mappedForeground = pickMappedVariantAsset(borderMap, variantId, foregroundKeys, {
    allowString: !isBack
  });
  const productForeground = pickProductMetafieldAsset(productJson, foregroundKeys);
  const frontForeground = pickVariantForegroundUrl(productJson, variant);

  const background = mappedAssetBackground || mappedBackground || productBackground;
  const foreground = mappedAssetForeground || mappedForeground || productForeground;

  return {
    background: toAbsShopifyUrl(background || frontBackground || ""),
    foreground: toAbsShopifyUrl(foreground || frontForeground || ""),
    mirrorBackground: isBack && !background && !!frontBackground,
    mirrorForeground: isBack && !foreground && !!frontForeground
  };
}
  // =============================
  // UI (Top Bar)
  // =============================
  function ensureTopBar(wrapEl) {
    let bar = document.getElementById("pamela-editor-topbar");
    if (bar) return bar;

    bar = document.createElement("div");
    bar.id = "pamela-editor-topbar";
    bar.style.cssText =
      "max-width:1200px;margin:0 auto 10px auto;padding:10px 12px;border:1px solid #e6e6e6;border-radius:12px;" +
      "display:flex;flex-wrap:nowrap;gap:12px;align-items:center;justify-content:space-between;background:#fff;overflow:hidden;";

    const left = document.createElement("div");
    left.style.cssText =
      "display:flex;gap:10px;align-items:center;min-width:0;flex:1 1 0;overflow:hidden;";
      
    const title = document.createElement("div");
    title.id = "pamela-product-title";
    title.style.cssText =
      "font:900 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;" +
      "min-width:0;max-width:clamp(180px, 28vw, 420px);flex:0 1 clamp(180px, 28vw, 420px);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;visibility:hidden;";

    title.textContent = "";

    const variantWrap = document.createElement("div");
    variantWrap.id = "pamela-topbar-variant-wrap";
    variantWrap.style.cssText = "display:flex;gap:8px;align-items:center;min-width:0;flex:0 1 auto;";

    const variantLabel = document.createElement("div");

    variantLabel.style.cssText =
      "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#667;letter-spacing:.5px;text-transform:uppercase;";

    const variantSelect = document.createElement("select");
    variantSelect.id = "pamela-variant-select";
    variantSelect.style.cssText =
      "padding:8px 10px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
      "font:800 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;min-width:220px;max-width:280px;width:clamp(220px, 20vw, 280px);";

    variantWrap.appendChild(variantLabel);
    variantWrap.appendChild(variantSelect);

    left.appendChild(title);
    left.appendChild(variantWrap);

    const right = document.createElement("div");
    right.id = "pamela-topbar-right";
    right.style.cssText =
      "display:flex;gap:16px;align-items:center;justify-content:flex-end;flex:0 0 auto;flex-wrap:nowrap;white-space:nowrap;";
    const startPriceWrap = document.createElement("div");
    startPriceWrap.id = "pamela-topbar-price-wrap";
    startPriceWrap.style.cssText = "display:flex;flex-direction:column;gap:6px;align-items:flex-start;";

    const price = document.createElement("div");
    price.id = "pamela-variant-price";
    price.style.cssText =
      "font:950 18px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;padding:8px 10px;" +
      "border:1px solid #111;border-radius:10px;background:#fff;";
    price.textContent = "$0.00";

    startPriceWrap.appendChild(price);

    right.appendChild(startPriceWrap);

    // --- Finalize + Add to Cart button ---
    const addBtn = document.createElement("button");
    addBtn.id = "pamela-finalize-addtocart";
    addBtn.type = "button";
    addBtn.textContent = "Finalize & Add to Cart";
    addBtn.style.cssText =
      "padding:12px 14px;border-radius:10px;border:1px solid #111;background:#111;color:#fff;" +
      "font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

    const status = document.createElement("div");
    status.id = "pamela-finalize-status";
    status.style.cssText =
      "font:800 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#444;max-width:260px;";

    right.appendChild(addBtn);
    right.appendChild(status);

    bar.appendChild(left);
    bar.appendChild(right);

    (wrapEl.parentNode || document.body).insertBefore(bar, wrapEl);
    return bar;
    
  }

  function pamelaIsMobileEditorViewport() {
    return !!(
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    );
  }

  // pamelaIsMobileEditorViewport() misses touchscreen LAPTOPS: when a trackpad
  // is also present, browsers commonly report the primary pointer as "fine"
  // (not "coarse") even though touch input works, and the viewport/userAgent
  // checks obviously don't match a laptop either. That gap is what let a
  // mirrored clone's clone-can't-be-selected-by-touch bug through -- the
  // "mobile retap" fix that rebinds select-on-touch for clones never ran.
  // Check actual touch capability directly instead of inferring it from
  // viewport size.
  function pamelaIsTouchCapableDevice() {
    return !!(
      ("ontouchstart" in window) ||
      (typeof navigator !== "undefined" && Number(navigator.maxTouchPoints || 0) > 0)
    );
  }

  function pamelaEnsureMobileToolbarStyle() {
    if (document.getElementById("pamela-mobile-toolbar-style")) return;

    const style = document.createElement("style");
    style.id = "pamela-mobile-toolbar-style";
    style.textContent = `
      #pamela-mobile-top-groups {
        display: none;
      }
      #pamela-mobile-context-toolbar {
        display: none;
      }
      @media (max-width: 768px) {
        #pamela-editor-topbar {
          padding: 8px !important;
          gap: 8px !important;
          border-radius: 10px !important;
          flex-wrap: wrap !important;
          align-items: stretch !important;
          overflow: visible !important;
        }
        #pamela-editor-topbar #pamela-product-title {
          font-size: 11px !important;
          line-height: 1.2 !important;
          max-width: none !important;
          flex: 1 1 100% !important;
          white-space: nowrap !important;
        }
        #pamela-editor-topbar #pamela-topbar-variant-wrap {
          gap: 6px !important;
          flex: 1 1 100% !important;
        }
        #pamela-editor-topbar #pamela-variant-select {
          min-width: 0 !important;
          width: 100% !important;
          max-width: none !important;
          padding: 7px 9px !important;
          font-size: 12px !important;
        }
        #pamela-editor-topbar #pamela-topbar-right {
          gap: 8px !important;
          width: 100% !important;
          flex: 1 1 100% !important;
          justify-content: space-between !important;
          align-items: center !important;
        }
        #pamela-editor-topbar #pamela-topbar-price-wrap {
          flex: 0 0 auto !important;
        }
        #pamela-editor-topbar #pamela-variant-price {
          padding: 6px 8px !important;
          font-size: 14px !important;
        }
        #pamela-editor-topbar #pamela-finalize-addtocart {
          padding: 8px 10px !important;
          font-size: 11px !important;
          line-height: 1.1 !important;
          white-space: normal !important;
        }
        #pamela-editor-topbar #pamela-finalize-status {
          display: none !important;
        }
        #pamela-editor-toolbar {
          gap: 8px !important;
          padding: 8px !important;
          border-radius: 14px !important;
        }
        #pamela-mobile-top-groups {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        #pamela-mobile-top-groups button {
          flex: 1 1 0;
          min-width: 0;
          padding: 10px 12px;
          border-radius: 999px;
          border: 1px solid #111;
          background: #fff;
          color: #111;
          font: 900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;
        }
        #pamela-mobile-top-groups button[data-active="true"] {
          background: #111;
          color: #fff;
        }
        #pamela-editor-toolbar .pamela-toolbar-hint {
          display: none !important;
        }
        #pamela-editor-toolbar .pamela-toolbar-row {
          gap: 8px !important;
        }
        #pamela-editor-toolbar .pamela-toolbar-group {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 2px;
        }
        #pamela-editor-toolbar .pamela-toolbar-group::-webkit-scrollbar {
          display: none;
        }
        #pamela-editor-toolbar .pamela-toolbar-group button,
        #pamela-editor-toolbar .pamela-toolbar-group select {
          flex: 0 0 auto;
        }
        #pamela-editor-toolbar .pamela-mobile-hide {
          display: none !important;
        }
        #pamela-toolbar-row-images {
          display: none !important;
        }
        #pamela-mobile-context-toolbar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: calc(env(safe-area-inset-bottom, 0px) + 8px);
          z-index: 999999;
          padding: 0 10px;
          pointer-events: none;
          transform: translateY(calc(100% + 24px));
          opacity: 0;
          transition: transform 180ms ease, opacity 180ms ease;
        }
        #pamela-mobile-context-toolbar[data-open="true"] {
          display: block;
          transform: translateY(0);
          opacity: 1;
        }
        #pamela-mobile-context-sheet,
        #pamela-mobile-context-bar {
          pointer-events: auto;
          max-width: 720px;
          margin: 0 auto;
          background: rgba(255,255,255,0.97);
          border: 1px solid rgba(17,17,17,0.12);
          box-shadow: 0 10px 28px rgba(0,0,0,0.14);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        #pamela-mobile-context-sheet {
          display: none;
          margin-bottom: 8px;
          border-radius: 16px;
          padding: 10px;
        }
        #pamela-mobile-context-sheet[data-open="true"] {
          display: block;
        }
        #pamela-mobile-context-bar {
          border-radius: 18px;
          padding: 8px;
          display: flex;
          gap: 8px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        #pamela-mobile-context-bar::-webkit-scrollbar {
          display: none;
        }
        .pamela-mobile-context-btn,
        .pamela-mobile-context-sheet-btn,
        .pamela-mobile-context-align-btn {
          border: 1px solid #111;
          background: #fff;
          color: #111;
          border-radius: 12px;
          font: 800 13px/1.1 system-ui,-apple-system,Segoe UI,Roboto,Arial;
          padding: 10px 12px;
          min-height: 42px;
        }
        .pamela-mobile-context-btn {
          flex: 0 0 auto;
          white-space: nowrap;
        }
        .pamela-mobile-context-btn[disabled],
        .pamela-mobile-context-sheet-btn[disabled] {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .pamela-mobile-context-sheet-btn[aria-pressed="true"] {
          background: #111;
          color: #fff;
        }
        .pamela-mobile-context-sheet-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .pamela-mobile-context-sheet-label {
          font: 900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;
          color: #4b5563;
          text-transform: uppercase;
          letter-spacing: .04em;
          margin: 0 0 8px 0;
        }
        .pamela-mobile-context-font {
          width: 100%;
          border: 1px solid #111;
          background: #fff;
          color: #111;
          border-radius: 12px;
          padding: 10px 12px;
          font: 800 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function pamelaToolbarProxyClick(id) {
    const el = document.getElementById(id);
    if (!el || el.disabled) return false;
    try {
      el.click();
      return true;
    } catch (_) {
      return false;
    }
  }

  function pamelaIsLaserToolbarNode(node) {
    try {
      return !!(
        node &&
        (node.getClassName?.() || node.className) === "Image" &&
        (node.getAttr?.("pamelaLaserEtching") === true || node.attrs?.pamelaLaserEtching === true)
      );
    } catch (_) {
      return false;
    }
  }

  function pamelaGetActiveMobileLaserNode(editor) {
    try {
      const trNode = editor?.nodes?.tr?.nodes?.()?.[0] || null;
      if (pamelaIsLaserToolbarNode(trNode)) return trNode;

      const selected = editor?.selected || null;
      if (pamelaIsLaserToolbarNode(selected)) return selected;

      const forced = window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ || null;
      if (pamelaIsLaserToolbarNode(forced) && forced.destroyed?.() !== true) {
        const selectedIsText = !!(selected && pamelaIsTextLikeNode(selected));
        if (!selected || selected === forced || selectedIsText) return forced;
      }

      const last = window.__PAMELA_SELECTED_LASER_NODE__ || null;
      if (pamelaIsLaserToolbarNode(last) && last.destroyed?.() !== true) {
        if (!selected || selected === last) return last;
      }
    } catch (_) {}
    return null;
  }

  function pamelaSelectMobileLaserForAction(editor) {
    try {
      const ed =
        editor ||
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
        null;
      const node = pamelaGetActiveMobileLaserNode(ed) || getSelectedLaserArtworkNode?.(ed) || null;
      if (!node || !pamelaIsLaserToolbarNode(node)) return null;
      window.__PAMELA_SELECTED_LASER_NODE__ = node;
      window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ = node;
      try { ed?.selectNode?.(node); } catch (_) {}
      return node;
    } catch (_) {
      return null;
    }
  }

  function pamelaLaserToolbarProxyClick(editor, id) {
    pamelaSelectMobileLaserForAction(editor);
    return pamelaToolbarProxyClick(id);
  }

  function pamelaGetLaserNudgeMenu(editor) {
    return {
      type: "buttons",
      label: "Nudge 1/16 in",
      buttons: [
        { label: "Left", onClick: () => { pamelaSelectMobileLaserForAction(editor); pamelaNudgeSelected(editor, -PAMELA_NUDGE_STEP_IN, 0); } },
        { label: "Up", onClick: () => { pamelaSelectMobileLaserForAction(editor); pamelaNudgeSelected(editor, 0, -PAMELA_NUDGE_STEP_IN); } },
        { label: "Down", onClick: () => { pamelaSelectMobileLaserForAction(editor); pamelaNudgeSelected(editor, 0, PAMELA_NUDGE_STEP_IN); } },
        { label: "Right", onClick: () => { pamelaSelectMobileLaserForAction(editor); pamelaNudgeSelected(editor, PAMELA_NUDGE_STEP_IN, 0); } },
      ],
    };
  }

  function pamelaGetSelectionKind(editor) {
    const sel = editor?.selected || editor?.__cmSelectedPortraitWrapper || null;
    if (!sel) return "none";

    try {
      if (pamelaIsTextLikeNode(sel)) return "text";
      if (sel.className === "Image" && sel.attrs?.pamelaClipart === true) return "clipart";
      if (sel.className === "Image" && sel.attrs?.pamelaPanel === true) return "panel";
      if (pamelaIsLaserToolbarNode(sel)) return "laser";

      const attrs = sel.attrs || {};
      const nm =
        (typeof sel.name === "function" ? String(sel.name() || "") : "") ||
        String(attrs.name || "");

      if (
        attrs.__isPortraitGroup === true ||
        attrs.__pamelaKind === "portrait" ||
        attrs.pamelaType === "portrait" ||
        attrs.pamelaPortrait === true ||
        nm.includes("pamela-portrait-group")
      ) {
        return "portrait";
      }
    } catch (_) {}

    if (pamelaGetActiveMobileLaserNode(editor)) return "laser";

    return "none";
  }

  function pamelaMobileGroupForSelectionKind(kind) {
    if (kind === "text") return "text";
    if (kind === "clipart" || kind === "panel" || kind === "laser" || kind === "portrait") return "design";
    return "";
  }

  function pamelaSetTextAlign(editor, align) {
    const sel = editor?.selected;
    if (!sel || !pamelaIsTextLikeNode(sel)) return;

    try { editor._historyCommit?.("align"); } catch (_) {}

    try { sel.align(align); } catch (_) {}
    try { pamelaFixTextMetrics(sel); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, sel); } catch (_) {}
    try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
    try { editor.selectNode?.(sel); } catch (_) {}
    try { pamelaEnforceSpacingForText(editor); } catch (_) {}
  }

  function pamelaNudgePortraitScale(editor, dir) {
    const wrapper = editor?.__cmSelectedPortraitWrapper || editor?.selected || null;
    if (!wrapper) return;
    const factor = dir > 0 ? 1.08 : (1 / 1.08);

    try {
      wrapper.scaleX((Number(wrapper.scaleX?.() ?? 1) || 1) * factor);
      wrapper.scaleY((Number(wrapper.scaleY?.() ?? 1) || 1) * factor);
      wrapper.getLayer?.()?.batchDraw?.();
      editor.layers?.ui?.batchDraw?.();
      editor.selectNode?.(wrapper);
    } catch (_) {}
  }

  function pamelaOpenPortraitAdjust(editor) {
    const wrapper = pamelaFindToolbarPortraitWrapper(editor) || null;
    if (!wrapper) return;

    try {
      if (window.CM_resumePortraitEditing?.(wrapper, { select: true })) return;
    } catch (_) {}

    const legacyBtn = document.getElementById("cm_porcelain_edit_selected");
    if (legacyBtn && legacyBtn.id !== "pamela-porcelain-size-reposition-btn" && !legacyBtn.disabled) {
      try { legacyBtn.click(); } catch (_) {}
    }
  }

  function pamelaGetNudgeMenu(editor) {
    return {
      type: "buttons",
      label: "Nudge 1/16 in",
      buttons: [
        { label: "Left", onClick: () => pamelaNudgeSelected(editor, -PAMELA_NUDGE_STEP_IN, 0) },
        { label: "Up", onClick: () => pamelaNudgeSelected(editor, 0, -PAMELA_NUDGE_STEP_IN) },
        { label: "Down", onClick: () => pamelaNudgeSelected(editor, 0, PAMELA_NUDGE_STEP_IN) },
        { label: "Right", onClick: () => pamelaNudgeSelected(editor, PAMELA_NUDGE_STEP_IN, 0) },
      ],
    };
  }

  function pamelaNudgeSelected(editor, dxIn, dyIn) {
    try {
      const sel = editor?.selected || null;
      if (!editor || !sel) return;

      const isText = pamelaIsTextLikeNode(sel);
      const isClipOrPanel =
        sel.className === "Image" &&
        (
          sel.getAttr?.("pamelaClipart") === true ||
          sel.getAttr?.("pamelaPanel") === true ||
          pamelaIsLaserArtworkNode(sel)
        );

      if (!isText && !isClipOrPanel) return;

      const ppi =
        typeof getDesignPixelsPerInch === "function"
          ? Number(getDesignPixelsPerInch(editor) || DPI)
          : DPI;

      const dx = Number(dxIn || 0) * ppi;
      const dy = Number(dyIn || 0) * ppi;
      if (!dx && !dy) return;

      try { editor._historyCommit?.("nudge"); } catch (_) {}

      sel.x(Number(sel.x?.() || 0) + dx);
      sel.y(Number(sel.y?.() || 0) + dy);

      try { pamelaClampNodeIntoSafeRect(editor, sel); } catch (_) {}
      try { sel.getLayer?.()?.batchDraw?.(); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
      try { editor.selectNode?.(sel); } catch (_) {}
      if (isText) {
        try { pamelaEnforceSpacingForText(editor); } catch (_) {}
      } else if (sel.getAttr?.("pamelaLaserEtching") === true) {
        try { syncLaserSizeFieldsFromArtwork(sel); } catch (_) {}
      }
    } catch (_) {}
  }

  function pamelaEnsureMobileContextToolbar() {
    let root = document.getElementById("pamela-mobile-context-toolbar");
    if (root) return root;

    root = document.createElement("div");
    root.id = "pamela-mobile-context-toolbar";
    root.setAttribute("data-open", "false");

    const sheet = document.createElement("div");
    sheet.id = "pamela-mobile-context-sheet";
    sheet.setAttribute("data-open", "false");

    const bar = document.createElement("div");
    bar.id = "pamela-mobile-context-bar";

    const stop = (e) => {
      try { e.stopPropagation(); } catch (_) {}
    };
    [root, sheet, bar].forEach((el) => {
      el.addEventListener("pointerdown", stop, true);
      el.addEventListener("mousedown", stop, true);
      el.addEventListener("touchstart", stop, true);
    });

    root.appendChild(sheet);
    root.appendChild(bar);
    document.body.appendChild(root);
    return root;
  }

  function pamelaRenderMobileContextSheet(editor, spec) {
    const root = pamelaEnsureMobileContextToolbar();
    const sheet = root.querySelector("#pamela-mobile-context-sheet");
    if (!sheet) return;

    if (!spec) {
      sheet.innerHTML = "";
      sheet.setAttribute("data-open", "false");
      return;
    }

    sheet.innerHTML = "";

    const label = document.createElement("div");
    label.className = "pamela-mobile-context-sheet-label";
    label.textContent = spec.label || "";
    sheet.appendChild(label);

    if (spec.type === "font") {
      const topSelect = document.getElementById("pamela-font-select");
      if (!topSelect) return;

      const sel = document.createElement("select");
      sel.className = "pamela-mobile-context-font";
      sel.innerHTML = topSelect.innerHTML;
      sel.value = topSelect.value;
      sel.disabled = !!topSelect.disabled;
      sel.addEventListener("change", () => {
        try { topSelect.value = sel.value; } catch (_) {}
        try { topSelect.onchange && topSelect.onchange(); } catch (_) {}
      });
      sheet.appendChild(sel);
    } else if (spec.type === "align") {
      const row = document.createElement("div");
      row.className = "pamela-mobile-context-sheet-row";
      [
        ["Left", "left"],
        ["Center", "center"],
        ["Right", "right"],
      ].forEach(([txt, value]) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "pamela-mobile-context-align-btn";
        btn.textContent = txt;
        btn.addEventListener("click", () => pamelaSetTextAlign(editor, value));
        row.appendChild(btn);
      });
      sheet.appendChild(row);
    } else if (spec.type === "buttons") {
      const row = document.createElement("div");
      row.className = "pamela-mobile-context-sheet-row";
      (spec.buttons || []).forEach((cfg) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "pamela-mobile-context-sheet-btn";
        btn.textContent = cfg.label;
        btn.disabled = !!cfg.disabled;
        if (cfg.selected) btn.setAttribute("aria-pressed", "true");
        let fired = false;
        const run = (e) => {
          try { e?.preventDefault?.(); } catch (_) {}
          try { e?.stopPropagation?.(); } catch (_) {}
          if (fired) return;
          fired = true;
          try { cfg.onClick && cfg.onClick(); } catch (_) {}
          try {
            const actionLabel = String(spec?.__actionLabel || "");
            const freshMenu =
              pamelaGetMobileContextActions(editor)
                .find((action) => String(action?.label || "") === actionLabel)
                ?.menu || spec;
            pamelaRenderMobileContextSheet(
              editor,
              actionLabel ? { ...freshMenu, __actionLabel: actionLabel } : freshMenu
            );
          } catch (_) {}
          setTimeout(() => { fired = false; }, 0);
        };
        btn.addEventListener("click", run);
        btn.addEventListener("pointerup", run);
        btn.addEventListener("touchend", run, { passive: false });
        row.appendChild(btn);
      });
      sheet.appendChild(row);
    }

    sheet.setAttribute("data-open", "true");
  }

  function pamelaGetMobileContextActions(editor) {
    const kind = pamelaGetSelectionKind(editor);
    if (kind === "none") return [];

    if (kind === "text") {
      const sel = editor?.selected || null;
      const nm = (typeof sel?.name === "function") ? String(sel.name() || "") : String(sel?.attrs?.name || "");
      const id = (typeof sel?.id === "function") ? String(sel.id() || "") : String(sel?.attrs?.id || "");
      const fieldType = String(sel?.getAttr?.("pamelaFieldType") || sel?.attrs?.pamelaFieldType || "").trim().toLowerCase();
      const isBackVerse = (nm === "__cm_back_verse" || id === "__cm_back_verse");
      const isLastName = fieldType === "lastname";
      return [
        { label: "Edit Text", onClick: () => pamelaStartInlineEdit(editor, editor.selected) },
        { label: "Font", menu: { type: "font", label: "Font" } },
        { label: "Curve Text", onClick: (anchor) => pamelaOpenCurveTextMenu(editor, anchor) },
        ...(pamelaCanReverseSelectedLaserText(editor) ? [{
          label: sel?.getAttr?.("pamelaLaserTextReversed") === true ? "Use White Text" : "Use Black Text",
          onClick: () => pamelaToggleSelectedLaserText(editor),
        }] : []),
        ...(!isLastName ? [{
          label: "Size",
          menu: {
            type: "buttons",
            label: "Text Size",
            buttons: [
              { label: "A-", disabled: !!document.getElementById("pamela-size-down")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-size-down") },
              { label: "A+", disabled: !!document.getElementById("pamela-size-up")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-size-up") },
            ],
          },
        }] : []),
        { label: "Nudge", menu: pamelaGetNudgeMenu(editor) },
        ...(isBackVerse ? [{ label: "Align", menu: { type: "align", label: "Text Align" } }] : []),
        { label: "Delete", onClick: () => pamelaToolbarProxyClick("pamela-delete") },
        {
          label: "Rotate",
          menu: {
            type: "buttons",
            label: "Rotate",
            buttons: [
              { label: "Left", disabled: !!document.getElementById("pamela-rot-left")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-left") },
              { label: "Right", disabled: !!document.getElementById("pamela-rot-right")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-right") },
            ],
          },
        },
        // Only shown for companion-layout mirrored text (one stone with two
        // people's name/date sets, mirrored left/right). Reuses the existing
        // toggle button's own click handler via pamelaToolbarProxyClick.
        ...(pamelaCanMoveSideIndependently(editor, sel) ? [{
          label: editor?.horizontalMirrorIndependentMode ? "Relink Mirrored Text" : "Unlink Mirrored Text",
          onClick: () => pamelaToolbarProxyClick("pamela-independent-sides-toggle"),
        }] : []),
        // Reuses the desktop Group/Ungroup Text button's own click handler via
        // pamelaToolbarProxyClick, so the toggle logic itself lives in exactly
        // one place. Placed last so it lands at the right end of this row.
        ...(pamelaCanGroupSideText(editor, sel) || pamelaIsGroupedSideTextActive(editor, sel) ? [{
          label: pamelaIsGroupedSideTextActive(editor, sel) ? "Ungroup Text" : "Group Text",
          onClick: () => pamelaToolbarProxyClick("pamela-group-side-text"),
        }] : []),
      ];
    }

    if (kind === "clipart") {
      return [
        ...(!pamelaSelectedGraniteIsLight() && typeof window.PAMELA_getSvgStyleMenuSpec === "function" ? [{
          label: "Clipart Style",
          menu: window.PAMELA_getSvgStyleMenuSpec(editor, "clipart"),
        }] : []),
        {
          label: "Size",
          menu: {
            type: "buttons",
            label: "Clipart Size",
            buttons: [
              { label: "-", disabled: !!document.getElementById("pamela-clip-size-down")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-clip-size-down") },
              { label: "+", disabled: !!document.getElementById("pamela-clip-size-up")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-clip-size-up") },
            ],
          },
        },
        {
          label: "Rotate",
          menu: {
            type: "buttons",
            label: "Rotate",
            buttons: [
              { label: "Left", disabled: !!document.getElementById("pamela-rot-left")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-left") },
              { label: "Right", disabled: !!document.getElementById("pamela-rot-right")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-right") },
            ],
          },
        },
        { label: "Nudge", menu: pamelaGetNudgeMenu(editor) },
        { label: "Flip", onClick: () => pamelaToolbarProxyClick("pamela-flip-x") },
        { label: "Delete", onClick: () => pamelaToolbarProxyClick("pamela-delete") },
        { label: "Mirror", onClick: () => pamelaToolbarProxyClick("pamela-mirror-clone-btn") },
      ];
    }

    if (kind === "panel") {
      return [
        ...(!pamelaSelectedGraniteIsLight() && typeof window.PAMELA_getSvgStyleMenuSpec === "function" ? [{
          label: "Panel Style",
          menu: window.PAMELA_getSvgStyleMenuSpec(editor, "panel"),
        }] : []),
        {
          label: "Size",
          menu: {
            type: "buttons",
            label: "Panel Size",
            buttons: [
              { label: "-", disabled: !!document.getElementById("pamela-clip-size-down")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-clip-size-down") },
              { label: "+", disabled: !!document.getElementById("pamela-clip-size-up")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-clip-size-up") },
            ],
          },
        },
        {
          label: "Rotate",
          menu: {
            type: "buttons",
            label: "Rotate",
            buttons: [
              { label: "Left", disabled: !!document.getElementById("pamela-rot-left")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-left") },
              { label: "Right", disabled: !!document.getElementById("pamela-rot-right")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-right") },
            ],
          },
        },
        { label: "Nudge", menu: pamelaGetNudgeMenu(editor) },
        { label: "Delete", onClick: () => pamelaToolbarProxyClick("pamela-delete") },
        { label: "Mirror", onClick: () => pamelaToolbarProxyClick("pamela-mirror-clone-btn") },
      ];
    }

    if (kind === "laser") {
      const selectedLaser = pamelaGetActiveMobileLaserNode(editor) || getSelectedLaserArtworkNode(editor);
      const selectedFeatherPx = Math.max(0, Math.round(Number(selectedLaser?.getAttr?.("pamelaLaserFeatherPx") || 0)));
      const selectedLaserKind = String(selectedLaser?.getAttr?.("pamelaLaserKind") || "spot").toLowerCase();
      return [
        { label: "Crop", onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela_laser_crop_btn") },
        { label: "Fit to Stone", onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela_laser_fit_stone_btn") },
        { label: "Fade -", disabled: selectedFeatherPx <= 0 || !!document.getElementById("pamela_laser_fade_down_btn")?.disabled, onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela_laser_fade_down_btn") },
        { label: "Fade +", disabled: !!document.getElementById("pamela_laser_fade_up_btn")?.disabled, onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela_laser_fade_up_btn") },
        { label: "Flip", onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela-flip-x") },
        { label: "Mirror", disabled: selectedLaserKind !== "spot" || !!document.getElementById("pamela-mirror-clone-btn")?.disabled, onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela-mirror-clone-btn") },
        { label: "Nudge", menu: pamelaGetLaserNudgeMenu(editor) },
        { label: "Delete", onClick: () => pamelaLaserToolbarProxyClick(editor, "pamela-delete") },
      ];
    }

    if (kind === "portrait") {
      return [
        { label: "Replace", onClick: () => pamelaReplaceSelectedImageFromToolbar(editor) },
        { label: "Fit Photo", onClick: () => pamelaOpenPortraitAdjust(editor) },
        {
          label: "Rotate",
          menu: {
            type: "buttons",
            label: "Rotate",
            buttons: [
              { label: "Left", disabled: !!document.getElementById("pamela-rot-left")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-left") },
              { label: "Right", disabled: !!document.getElementById("pamela-rot-right")?.disabled, onClick: () => pamelaToolbarProxyClick("pamela-rot-right") },
            ],
          },
        },
        { label: "Delete", onClick: () => pamelaToolbarProxyClick("pamela-delete") },
      ];
    }

    return [];
  }

  function pamelaSyncMobileToolbars(editor) {
    pamelaEnsureMobileToolbarStyle();
    const root = pamelaEnsureMobileContextToolbar();
    const bar = root.querySelector("#pamela-mobile-context-bar");
    const sheet = root.querySelector("#pamela-mobile-context-sheet");
    if (!bar || !sheet) return;
    const topToolbar = document.getElementById("pamela-editor-toolbar");
    let activeTopGroup = topToolbar?.dataset?.mobileGroup || "text";
    const selectionKind = pamelaGetSelectionKind(editor);
    const previousKind = String(sheet.getAttribute("data-selection-kind") || "");
    const manualGroup = topToolbar?.dataset?.mobileGroupManual === "true";
    const selectionGroup = pamelaMobileGroupForSelectionKind(selectionKind);

    if (
      !manualGroup &&
      selectionGroup &&
      activeTopGroup !== selectionGroup &&
      topToolbar
    ) {
      topToolbar.dataset.mobileGroup = selectionGroup;
      pamelaSyncMobileTopToolbar(topToolbar);
      activeTopGroup = selectionGroup;
    }

    if (!pamelaIsMobileEditorViewport()) {
      root.setAttribute("data-open", "false");
      sheet.setAttribute("data-open", "false");
      bar.innerHTML = "";
      return;
    }

    const actions = pamelaGetMobileContextActions(editor);
    if (previousKind !== selectionKind) {
      sheet.innerHTML = "";
      sheet.setAttribute("data-open", "false");
      sheet.removeAttribute("data-kind");
    }
    sheet.setAttribute("data-selection-kind", selectionKind);
    const selectionMatchesActiveGroup =
      (activeTopGroup === "text" && selectionKind === "text") ||
      (activeTopGroup === "design" && (selectionKind === "clipart" || selectionKind === "panel" || selectionKind === "laser" || selectionKind === "portrait"));
    if (!actions.length || activeTopGroup === "view" || !selectionMatchesActiveGroup) {
      root.setAttribute("data-open", "false");
      sheet.setAttribute("data-open", "false");
      bar.innerHTML = "";
      return;
    }

    root.setAttribute("data-open", "true");
    bar.innerHTML = "";

    actions.forEach((cfg) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pamela-mobile-context-btn";
      btn.textContent = cfg.label;
      btn.disabled = !!cfg.disabled;
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        if (cfg.menu) {
          const isOpen =
            sheet.getAttribute("data-open") === "true" &&
            sheet.getAttribute("data-kind") === cfg.label;
          if (isOpen) {
            sheet.innerHTML = "";
            sheet.setAttribute("data-open", "false");
            sheet.removeAttribute("data-kind");
          } else {
            pamelaRenderMobileContextSheet(editor, { ...cfg.menu, __actionLabel: cfg.label });
            sheet.setAttribute("data-kind", cfg.label);
          }
          return;
        }

        sheet.innerHTML = "";
        sheet.setAttribute("data-open", "false");
        sheet.removeAttribute("data-kind");
        cfg.onClick && cfg.onClick(btn);
      });
      bar.appendChild(btn);
    });
  }

  function pamelaSyncMobileTopToolbar(bar) {
    if (!bar) return;

    const isMobile = pamelaIsMobileEditorViewport();
    const active = bar.dataset.mobileGroup || "text";
    const nav = document.getElementById("pamela-mobile-top-groups");
    const groups = [
      document.getElementById("pamela-toolbar-group-text"),
      document.getElementById("pamela-toolbar-group-design-main"),
      document.getElementById("pamela-toolbar-group-design-extra"),
      document.getElementById("pamela-toolbar-group-view"),
    ];

    if (nav) {
      Array.from(nav.querySelectorAll("button[data-group]")).forEach((btn) => {
        btn.setAttribute("data-active", btn.dataset.group === active ? "true" : "false");
      });
    }

    if (!isMobile) {
      groups.forEach((el) => {
        if (!el) return;
        el.style.display = el.dataset.baseDisplay || "flex";
      });
      return;
    }

    groups.forEach((el) => {
      if (!el) return;
      const groupName = el.dataset.mobileGroup || "";
      el.style.display = groupName === active ? (el.dataset.baseDisplay || "flex") : "none";
    });
  }

  function pamelaFindToolbarPortraitWrapper(editor) {
    try {
      const isLiveWrapper = (wrapper) => !!(
        wrapper &&
        wrapper.destroyed?.() !== true &&
        wrapper.getStage?.()
      );

      const isWrapper = (node) => {
        const attrs = node?.attrs || {};
        const nm = typeof node?.name === "function" ? String(node.name() || "") : String(attrs.name || "");
        return !!(
          attrs.__isPortraitGroup === true ||
          attrs.__pamelaKind === "portrait" ||
          attrs.pamelaType === "portrait" ||
          attrs.pamelaPortrait === true ||
          nm.includes("pamela-portrait-group")
        );
      };

      const isPortraitPart = (node) => {
        const attrs = node?.attrs || {};
        const nm = typeof node?.name === "function" ? String(node.name() || "") : String(attrs.name || "");
        return !!(
          isWrapper(node) ||
          attrs.__pamelaKind === "portrait" ||
          attrs.pamelaType === "portrait" ||
          attrs.pamelaPortrait === true ||
          nm.includes("pamela-portrait-inner") ||
          nm.includes("pamela-portrait-photo") ||
          nm.includes("pamela-hit")
        );
      };

      const findWrapperUp = (start) => {
        let cur = start;
        let hops = 0;
        while (cur && hops < 20) {
          if (isWrapper(cur)) return cur;
          try { cur = cur.getParent?.() || null; } catch (_) { cur = null; }
          hops += 1;
        }
        return null;
      };

      const getTransformerNode = (tr) => {
        try {
          const nodes = tr?.nodes?.() || [];
          return nodes[0] || null;
        } catch (_) {
          return null;
        }
      };

      const editorCandidates = [];
      [
        editor || null,
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null),
        window.pamelaMonumentEditor || null,
        window.pamelaEditor || null,
        window.editor || null,
        window.PAMELA_EDITOR || null,
        window.__PAMELA_EDITOR__ || null
      ].forEach((ed) => {
        if (ed && !editorCandidates.includes(ed)) editorCandidates.push(ed);
      });

      for (const ed of editorCandidates) {
        const selected = ed?.selected || null;
        const selectedWrapper = findWrapperUp(selected);
        if (isLiveWrapper(selectedWrapper)) return selectedWrapper;

        const candidates = [
          getTransformerNode(ed?.nodes?.tr),
          getTransformerNode(ed?.nodes?.trPanel),
          getTransformerNode(ed?.stage?.findOne?.("#cmPortraitTransformer"))
        ];

        for (const start of candidates) {
          const wrapper = findWrapperUp(start);
          if (isLiveWrapper(wrapper)) return wrapper;
        }
      }
    } catch (_) {}
    return null;
  }

  function pamelaReplaceSelectedImageFromToolbar(editor) {
    try {
      const ed =
        editor ||
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
        null;
      const portraitButtonVisible =
        document.getElementById("pamela-porcelain-size-reposition-btn")?.style?.display !== "none";
      const activePortraitWrapper =
        pamelaFindToolbarPortraitWrapper(ed) ||
        null;

      if (
        portraitButtonVisible &&
        activePortraitWrapper &&
        activePortraitWrapper.destroyed?.() !== true &&
        window.CM_PortraitUpload?.openPickerForWrapper
      ) {
        try {
          if (ed) ed.__cmSelectedPortraitWrapper = activePortraitWrapper;
          window.__CM_SELECTED_PORTRAIT_WRAPPER__ = activePortraitWrapper;
          window.__CM_LAST_PORTRAIT_WRAPPER__ = activePortraitWrapper;
          window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = activePortraitWrapper;
          ed?.selectNode?.(activePortraitWrapper);
        } catch (_) {}
        window.CM_PortraitUpload.openPickerForWrapper(activePortraitWrapper);
        return;
      }

      const selectedLaser = pamelaGetActiveMobileLaserNode(ed) || getSelectedLaserArtworkNode?.(ed) || null;
      const isLaserSelected = !!(
        selectedLaser &&
        selectedLaser.destroyed?.() !== true &&
        (
          ed?.selected === selectedLaser ||
          ed?.nodes?.tr?.nodes?.()?.[0] === selectedLaser
        )
      );
      if (isLaserSelected) {
        try { ed?.selectNode?.(selectedLaser); } catch (_) {}
        pamelaToolbarProxyClick("pamela_laser_clone_btn");
        return;
      }

      const portraitWrapper =
        pamelaFindToolbarPortraitWrapper(ed) ||
        null;
      if (portraitWrapper && window.CM_PortraitUpload?.openPickerForWrapper) {
        try {
          if (ed) ed.__cmSelectedPortraitWrapper = portraitWrapper;
          window.__CM_SELECTED_PORTRAIT_WRAPPER__ = portraitWrapper;
          window.__CM_LAST_PORTRAIT_WRAPPER__ = portraitWrapper;
          window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = portraitWrapper;
          ed?.selectNode?.(portraitWrapper);
        } catch (_) {}
        window.CM_PortraitUpload.openPickerForWrapper(portraitWrapper);
        return;
      }

    } catch (e) {
      console.warn("[Images Toolbar] Replace image failed:", e);
    }
  }

  function pamelaEnsureImagesToolbarElements() {
    const bar = document.getElementById("pamela-editor-toolbar");
    if (!bar) return null;

    let row = document.getElementById("pamela-toolbar-row-images");
    if (!row) {
      row = document.createElement("div");
      row.id = "pamela-toolbar-row-images";
      row.className = "pamela-toolbar-row";
      row.style.cssText = "display:none;align-items:center;justify-content:flex-start;gap:12px;flex-wrap:wrap;";

      const hint = document.getElementById("pamela-toolbar-hint");
      if (hint && hint.parentNode === bar) bar.insertBefore(row, hint);
      else bar.appendChild(row);
    }

    let group = document.getElementById("pamela-toolbar-group-images");
    if (!group) {
      group = document.createElement("div");
      group.id = "pamela-toolbar-group-images";
      group.className = "pamela-toolbar-group";
      group.dataset.baseDisplay = "flex";
      group.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";
      row.appendChild(group);
    } else if (group.parentNode !== row) {
      row.appendChild(group);
    }

    const labels = Array.from(group.querySelectorAll(".pamela-toolbar-group-label"))
      .filter((el) => String(el.textContent || "").trim().toUpperCase() === "IMAGES");
    labels.forEach((label, index) => {
      if (index === 0) {
        label.dataset.pamelaImagesLabel = "true";
      } else {
        try { label.remove(); } catch (_) {}
      }
    });

    if (!group.querySelector('[data-pamela-images-label="true"]')) {
      const label = document.createElement("div");
      label.textContent = "IMAGES";
      label.className = "pamela-toolbar-group-label";
      label.dataset.pamelaImagesLabel = "true";
      label.style.cssText = "font:950 16px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-right:4px;";
      group.insertBefore(label, group.firstChild || null);
    }

    if (!document.getElementById("pamela-image-replace-btn")) {
      const replaceBtn = document.createElement("button");
      replaceBtn.type = "button";
      replaceBtn.id = "pamela-image-replace-btn";
      replaceBtn.textContent = "Replace Image";
      replaceBtn.style.cssText =
        "display:none;padding:7px 10px;border-radius:10px;border:1px solid #111;" +
        "font:800 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;" +
        "background:#fff;color:#111;";
      replaceBtn.addEventListener("click", () => {
        pamelaReplaceSelectedImageFromToolbar(
          window.pamelaMonumentEditor ||
          window.pamelaEditor ||
          window.editor ||
          window.PAMELA_EDITOR ||
          window.__PAMELA_EDITOR__
        );
      });
      group.appendChild(replaceBtn);
    }

    if (!window.__pamelaImagesToolbarRefreshTimer) {
      window.__pamelaImagesToolbarRefreshTimer = setInterval(() => {
        try {
          const ed =
            (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
            window.pamelaMonumentEditor ||
            window.pamelaEditor ||
            window.editor ||
            window.PAMELA_EDITOR ||
            window.__PAMELA_EDITOR__ ||
            null;
          (window.pamelaRefreshImagesToolbar || pamelaRefreshImagesToolbar)?.(ed);
        } catch (_) {}
      }, 150);
    }

    return { row, group };
  }

  function pamelaEnsurePortraitAdjustToolbarButton(editor) {
    const ensured = pamelaEnsureImagesToolbarElements();
    const group = ensured?.group || null;
    if (!group) return null;

    let btn =
      document.getElementById("pamela-porcelain-size-reposition-btn") ||
      document.getElementById("cm_porcelain_edit_selected");

    if (!btn) {
      btn = document.createElement("button");
      btn.type = "button";
      btn.style.cssText =
        "padding:7px 10px;border-radius:10px;border:1px solid #111;" +
        "font:800 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;" +
        "background:#fff;color:#111;";
    }

    btn.id = "pamela-porcelain-size-reposition-btn";
    btn.textContent = "Fit Photo";

    if (!btn.__pamelaPortraitAdjustToolbarBound) {
      btn.__pamelaPortraitAdjustToolbarBound = true;
      btn.addEventListener("click", (e) => {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
        pamelaOpenPortraitAdjust(
          window.pamelaMonumentEditor ||
          window.pamelaEditor ||
          window.editor ||
          window.PAMELA_EDITOR ||
          window.__PAMELA_EDITOR__ ||
          editor
        );
      });
    }

    if (btn.parentNode !== group) group.appendChild(btn);
    return btn;
  }

  function pamelaGetImagesToolbarPortraitWrapper(editor) {
    try {
      const isLive = (node) => !!(node && node.destroyed?.() !== true && node.getStage?.());
      const isWrapper = (node) => {
        try {
          const a = node?.attrs || {};
          const nm = typeof node?.name === "function" ? String(node.name() || "") : String(a.name || "");
          return !!(
            a.__isPortraitGroup === true ||
            a.__pamelaKind === "portrait" ||
            a.pamelaType === "portrait" ||
            a.pamelaPortrait === true ||
            nm.includes("pamela-portrait-group")
          );
        } catch (_) {
          return false;
        }
      };
      const findWrapperUp = (node) => {
        let cur = node;
        let hops = 0;
        while (cur && hops < 24) {
          if (isWrapper(cur)) return cur;
          try { cur = cur.getParent?.() || null; } catch (_) { cur = null; }
          hops += 1;
        }
        return null;
      };
      const firstTransformerNode = (tr) => {
        try {
          const nodes = tr?.nodes?.() || [];
          return nodes[0] || null;
        } catch (_) {
          return null;
        }
      };

      const editors = [];
      [
        editor || null,
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null),
        window.pamelaMonumentEditor || null,
        window.pamelaEditor || null,
        window.editor || null,
        window.PAMELA_EDITOR || null,
        window.__PAMELA_EDITOR__ || null
      ].forEach((ed) => {
        if (ed && !editors.includes(ed)) editors.push(ed);
      });

      for (const ed of editors) {
        const selectedWrapper = findWrapperUp(ed?.selected || null);
        if (isLive(selectedWrapper)) return selectedWrapper;

        const normalWrapper =
          findWrapperUp(firstTransformerNode(ed?.nodes?.tr)) ||
          findWrapperUp(firstTransformerNode(ed?.nodes?.trPanel));
        if (isLive(normalWrapper)) return normalWrapper;

        const portraitTr = ed?.stage?.findOne?.("#cmPortraitTransformer") || null;
        const portraitTrNode = firstTransformerNode(portraitTr);
        if (portraitTrNode) {
          const trWrapper =
            findWrapperUp(portraitTrNode) ||
            findWrapperUp(window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ || null) ||
            findWrapperUp(window.__CM_SELECTED_PORTRAIT_WRAPPER__ || null) ||
            findWrapperUp(window.__CM_LAST_PORTRAIT_WRAPPER__ || null);
          if (isLive(trWrapper)) return trWrapper;
        }
      }
    } catch (_) {}
    return null;
  }

  function pamelaShowPortraitImagesToolbar(editor, wrapper) {
    try {
      if (wrapper && (wrapper.destroyed?.() === true || !wrapper.getStage?.())) return;
      const ensured = pamelaEnsureImagesToolbarElements();
      const row = ensured?.row || null;
      const group = ensured?.group || null;
      if (!row || !group) return;

      const ed =
        editor ||
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
        null;

      const liveWrapper =
        (wrapper && wrapper.destroyed?.() !== true && wrapper.getStage?.() ? wrapper : null) ||
        pamelaGetImagesToolbarPortraitWrapper(ed) ||
        (typeof getActivePortraitWrapper === "function" ? getActivePortraitWrapper(ed) : null) ||
        pamelaFindToolbarPortraitWrapper(ed) ||
        null;
      if (!liveWrapper) {
        pamelaRefreshImagesToolbar(ed);
        return;
      }

      try { if (ed) ed.__cmSelectedPortraitWrapper = liveWrapper; } catch (_) {}
      try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = liveWrapper; } catch (_) {}
      try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = liveWrapper; } catch (_) {}
      try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}

      const editPhotoBtn = pamelaEnsurePortraitAdjustToolbarButton(ed);
      const replaceImageBtn = document.getElementById("pamela-image-replace-btn");
      const laserCropBtn = document.getElementById("pamela_laser_crop_btn");
      const laserFadeDownBtn = document.getElementById("pamela_laser_fade_down_btn");
      const laserFadeUpBtn = document.getElementById("pamela_laser_fade_up_btn");

      if (replaceImageBtn && replaceImageBtn.parentNode !== group) group.appendChild(replaceImageBtn);
      group.style.display = "flex";
      if (editPhotoBtn) editPhotoBtn.style.display = "inline-flex";
      if (replaceImageBtn) replaceImageBtn.style.display = "";
      [laserCropBtn, laserFadeDownBtn, laserFadeUpBtn].forEach((btn) => {
        if (btn) btn.style.display = "none";
      });

      row.style.display = "flex";
    } catch (_) {}
  }

  function pamelaRefreshImagesToolbar(editor) {
    try {
      const ensured = pamelaEnsureImagesToolbarElements();
      const row = ensured?.row || null;
      const group = ensured?.group || null;
      if (!row || !group) return;

      const ed =
        editor ||
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
        null;
      const selectedLaser = pamelaGetActiveMobileLaserNode(ed) || getSelectedLaserArtworkNode?.(ed) || null;
      const isLaserSelected = !!(
        selectedLaser &&
        selectedLaser.destroyed?.() !== true &&
        (
          ed?.selected === selectedLaser ||
          ed?.nodes?.tr?.nodes?.()?.[0] === selectedLaser
        )
      );
      const activePortraitWrapper =
        (typeof getActivePortraitWrapper === "function" ? getActivePortraitWrapper(ed) : null);
      const isPortraitSelected = !!activePortraitWrapper && window.__PAMELA_IMAGES_FORCE_HIDDEN__ !== true;

      const editPhotoBtn = pamelaEnsurePortraitAdjustToolbarButton(ed);
      const laserCropBtn = document.getElementById("pamela_laser_crop_btn");
      const laserFadeDownBtn = document.getElementById("pamela_laser_fade_down_btn");
      const laserFadeUpBtn = document.getElementById("pamela_laser_fade_up_btn");
      const replaceImageBtn = document.getElementById("pamela-image-replace-btn");

      [editPhotoBtn, laserCropBtn, laserFadeDownBtn, laserFadeUpBtn].forEach((btn) => {
        if (btn && btn.parentNode !== group) group.appendChild(btn);
      });

      const hasImageSelection = isPortraitSelected || isLaserSelected;

      if (editPhotoBtn) editPhotoBtn.style.display = isPortraitSelected ? "inline-flex" : "none";
      [laserCropBtn, laserFadeDownBtn, laserFadeUpBtn].forEach((btn) => {
        if (btn) btn.style.display = isLaserSelected ? "" : "none";
      });
      if (replaceImageBtn) replaceImageBtn.style.display = hasImageSelection ? "" : "none";

      group.style.display = hasImageSelection ? "flex" : "none";
      row.style.display = hasImageSelection ? "flex" : "none";
    } catch (_) {}
  }

  try { window.pamelaRefreshImagesToolbar = pamelaRefreshImagesToolbar; } catch (_) {}
  try { window.pamelaShowPortraitImagesToolbar = pamelaShowPortraitImagesToolbar; } catch (_) {}

  function pamelaForceHidePortraitImagesToolbar(editor) {
    try {
      const ed =
        editor ||
        (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
        window.pamelaMonumentEditor ||
        window.pamelaEditor ||
        window.editor ||
        window.PAMELA_EDITOR ||
        window.__PAMELA_EDITOR__ ||
        null;

      try { if (ed) ed.__cmSelectedPortraitWrapper = null; } catch (_) {}
      try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
      try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
      try { window.__CM_LAST_PORTRAIT_WRAPPER__ = null; } catch (_) {}
      try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = true; } catch (_) {}

      try {
        const ptr = ed?.stage?.findOne?.("#cmPortraitTransformer") || null;
        if (ptr?.nodes) {
          ptr.nodes([]);
          try { ptr.forceUpdate?.(); } catch (_) {}
          try { ptr.getLayer?.()?.batchDraw?.(); } catch (_) {}
        }
      } catch (_) {}

      const row = document.getElementById("pamela-toolbar-row-images");
      const group = document.getElementById("pamela-toolbar-group-images");
      const editPhotoBtn = document.getElementById("pamela-porcelain-size-reposition-btn");
      const replaceImageBtn = document.getElementById("pamela-image-replace-btn");
      const laserCropBtn = document.getElementById("pamela_laser_crop_btn");
      const laserFadeDownBtn = document.getElementById("pamela_laser_fade_down_btn");
      const laserFadeUpBtn = document.getElementById("pamela_laser_fade_up_btn");

      [editPhotoBtn, replaceImageBtn, laserCropBtn, laserFadeDownBtn, laserFadeUpBtn].forEach((btn) => {
        if (btn) btn.style.display = "none";
      });
      if (editPhotoBtn) {
        editPhotoBtn.disabled = true;
        editPhotoBtn.style.opacity = "0.35";
        editPhotoBtn.style.cursor = "not-allowed";
      }
      if (group) group.style.display = "none";
      if (row) row.style.display = "none";
    } catch (_) {}
  }

  try { window.pamelaForceHidePortraitImagesToolbar = pamelaForceHidePortraitImagesToolbar; } catch (_) {}

  // Portraits use their own transformer, separate from the editor's normal
  // selection. Clear it before opening a different tool or monument side.
  function pamelaIsPortraitToolbarAction(button) {
    if (!button) return false;

    // The Images group contains photo-only actions such as Fit Photo and Replace.
    if (button.closest?.("#pamela-toolbar-group-images, #pamela-toolbar-row-images")) return true;

    // These shared transform controls also operate on the selected photo.
    return [
      "pamela-rot-left",
      "pamela-rot-right",
      "pamela-size-up",
      "pamela-size-down",
      "pamela-nudge-left",
      "pamela-nudge-up",
      "pamela-nudge-down",
      "pamela-nudge-right",
      "pamela-delete"
    ].includes(String(button.id || ""));
  }

  function pamelaClearPortraitForNonPhotoTool() {
    const ed =
      (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
      window.pamelaMonumentEditor || window.pamelaEditor || window.editor ||
      window.PAMELA_EDITOR || window.__PAMELA_EDITOR__ || null;
    if (!ed) return;

    const portraitTransformer = ed.stage?.findOne?.("#cmPortraitTransformer");
    const portraitIsSelected = !!(
      portraitTransformer?.nodes?.()?.length ||
      ed.__cmSelectedPortraitWrapper ||
      window.__CM_SELECTED_PORTRAIT_WRAPPER__
    );
    if (!portraitIsSelected) return;

    try { window.pamelaForceHidePortraitImagesToolbar?.(ed); } catch (_) {}
    try { ed.selectNode?.(null); } catch (_) { try { ed.selected = null; } catch (_) {} }
    try { ed.layers?.ui?.batchDraw?.(); } catch (_) {}
  }

  // Capture phase ensures the cleanup happens before a tool can swap sides.
  if (!window.__pamelaPortraitToolbarDeselectBound) {
    window.__pamelaPortraitToolbarDeselectBound = true;
    document.addEventListener("click", (event) => {
      const button = event.target?.closest?.("button, [role='button']");
      if (!button || pamelaIsPortraitToolbarAction(button)) return;

      const isEditorTool = !!button.closest?.(
        "#pamela-editor-toolbar, #pamela-mobile-context-toolbar, #pamela-addon-flyout, #pamela-panels-dock, #pamela-clipart-panel"
      );
      if (isEditorTool) pamelaClearPortraitForNonPhotoTool();
    }, true);
  }

  function pamelaMoveButtonToImagesToolbar(btn) {
    try {
      const group = document.getElementById("pamela-toolbar-group-images");
      if (!btn) return false;
      if (!group) {
        window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__ = window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__ || [];
        if (!window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__.includes(btn)) {
          window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__.push(btn);
        }
        return false;
      }
      group.appendChild(btn);
      return true;
    } catch (_) {
      return false;
    }
  }

  // =============================
  // UI (Toolbar) Ã¢â‚¬â€ 3 rows, smaller buttons, correct order
  // =============================
  function ensureToolbar(wrapEl) {
    let bar = document.getElementById("pamela-editor-toolbar");
    if (bar) {
      try { pamelaEnsureImagesToolbarElements(); } catch (_) {}
      return bar;
    }

    const smallBtnCss =
      "padding:7px 10px;border-radius:10px;border:1px solid #111;" +
      "font:800 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;";

    const mkBtn = (id, label, dark = true) => {
      const b = document.createElement("button");
      b.type = "button";
      b.id = id;
      b.textContent = label;
      b.style.cssText = smallBtnCss + (dark ? "background:#111;color:#fff;" : "background:#fff;color:#111;");
      return b;
    };

    const mkLabel = (txt) => {
      const d = document.createElement("div");
      d.textContent = txt;
      d.style.cssText = "font:950 16px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-right:4px;";
      return d;
    };

    bar = document.createElement("div");
    bar.id = "pamela-editor-toolbar";
    bar.style.cssText =
      "max-width:1200px;margin:0 auto 10px auto;padding:10px;border-radius:16px;background:rgba(0, 0, 0, 0.1);" +
      "display:flex;flex-direction:column;gap:10px;position:relative;z-index:999999;pointer-events:auto;";
    bar.dataset.mobileGroup = "text";

    pamelaEnsureMobileToolbarStyle();

    // Stop clicks on toolbar from reaching Konva container
    const stopDownOnly = (e) => {
      try { e.stopPropagation(); } catch (_) {}
    };
    bar.addEventListener("pointerdown", stopDownOnly, true);
    bar.addEventListener("mousedown", stopDownOnly, true);
    bar.addEventListener("touchstart", stopDownOnly, true);

    const row1 = document.createElement("div");
    row1.className = "pamela-toolbar-row";
    row1.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;";

    const row2 = document.createElement("div");
    row2.className = "pamela-toolbar-row";
    row2.style.cssText = "display:flex;align-items:center;justify-content:flex-start;gap:12px;flex-wrap:wrap;";

    const rowImages = document.createElement("div");
    rowImages.id = "pamela-toolbar-row-images";
    rowImages.className = "pamela-toolbar-row";
    rowImages.style.cssText = "display:none;align-items:center;justify-content:flex-start;gap:12px;flex-wrap:wrap;";

    const r1left = document.createElement("div");
    r1left.id = "pamela-toolbar-group-text";
    r1left.className = "pamela-toolbar-group";
    r1left.dataset.mobileGroup = "text";
    r1left.dataset.baseDisplay = "flex";
    r1left.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";
    const r1right = document.createElement("div");
    r1right.id = "pamela-toolbar-group-design-main";
    r1right.className = "pamela-toolbar-group";
    r1right.dataset.mobileGroup = "design";
    r1right.dataset.baseDisplay = "flex";
    r1right.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";
    const r2left = document.createElement("div");
    r2left.id = "pamela-toolbar-group-view";
    r2left.className = "pamela-toolbar-group";
    r2left.dataset.mobileGroup = "view";
    r2left.dataset.baseDisplay = "flex";
    r2left.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";

    const r2right = document.createElement("div");
    r2right.id = "pamela-toolbar-group-design-extra";
    r2right.className = "pamela-toolbar-group";
    r2right.dataset.mobileGroup = "design";
    r2right.dataset.baseDisplay = "flex";
    r2right.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";

    const rImages = document.createElement("div");
    rImages.id = "pamela-toolbar-group-images";
    rImages.className = "pamela-toolbar-group";
    rImages.dataset.baseDisplay = "flex";
    rImages.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;";

    const mobileTopGroups = document.createElement("div");
    mobileTopGroups.id = "pamela-mobile-top-groups";
    [
      ["text", "Text"],
      ["design", "Design"],
      ["view", "View"],
    ].forEach(([group, label]) => {
      const b = document.createElement("button");
      b.type = "button";
      b.dataset.group = group;
      b.setAttribute("data-active", group === "text" ? "true" : "false");
      b.textContent = label;
      b.addEventListener("click", () => {
        bar.dataset.mobileGroup = group;
        bar.dataset.mobileGroupManual = "true";
        pamelaSyncMobileTopToolbar(bar);
        try { pamelaSyncMobileToolbars(window.pamelaMonumentEditor || window.pamelaEditor); } catch (_) {}
      });
      mobileTopGroups.appendChild(b);
    });

    const independentSidesBtn = mkBtn("pamela-independent-sides-toggle", "Unlink", false);
    independentSidesBtn.style.display = "none";
    // Short label + small footprint -- this only ever shows on mobile (see
    // canUse && isMobile gate below), where it needs to fit on the same wrap
    // line as the text-add buttons instead of forcing its own row.
    independentSidesBtn.style.minWidth = "0";
    independentSidesBtn.style.padding = "6px 10px";
    independentSidesBtn.style.font = "800 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial";

    const panelModeBtn = mkBtn("pamela-panel-mode-toggle", "Panel Style", false);
    panelModeBtn.style.minWidth = "170px";
    panelModeBtn.classList.add("pamela-mobile-hide");
    panelModeBtn.title = "Choose how panels appear on dark granite";

    const clipartModeBtn = mkBtn("pamela-clipart-mode-toggle", "Select Clipart to Style", false);
    clipartModeBtn.style.minWidth = "200px";
    clipartModeBtn.classList.add("pamela-mobile-hide");
    clipartModeBtn.title = "Choose how selected clipart appears on dark granite";
    r2right.appendChild(panelModeBtn);
    r2right.appendChild(clipartModeBtn);
    // Unlink Mirrored Text only ever acts on selected text (a companion-layout
    // mirrored name/date pair), so it belongs in the Text tab's row, not here
    // under Design -- moved below, next to Group Text.

    // Row 1: TEXT
    const textLabel = mkLabel("ADD TEXT");
    textLabel.className = "pamela-toolbar-group-label pamela-mobile-hide";
    r1left.appendChild(textLabel);
    const textAddButtonsWrap = document.createElement("div");
    textAddButtonsWrap.id = "pamela-toolbar-text-add-buttons";
    textAddButtonsWrap.style.cssText = "position:relative;display:inline-flex;align-items:center;gap:10px;flex-wrap:wrap;";
    textAddButtonsWrap.appendChild(mkBtn("pamela-add-lastname", "Last Name", true));
    textAddButtonsWrap.appendChild(mkBtn("pamela-add-name", "Name", true));
    textAddButtonsWrap.appendChild(mkBtn("pamela-add-date", "Date", true));
    textAddButtonsWrap.appendChild(mkBtn("pamela-add-wedding", "Wedding Date", true));
    textAddButtonsWrap.appendChild(mkBtn("pamela-add-verse", "Verse", true));
    r1left.appendChild(textAddButtonsWrap);
    // Unlink/Group Text only ever apply to companion front name/date text
    // (never to the back-side fields this wrap's overlay pill covers), so
    // nesting them here is safe -- and keeps them wrapping onto the same
    // line as Verse instead of forcing a whole extra row when they appear.
    const fontSelect = document.createElement("select");
    fontSelect.id = "pamela-font-select";
    fontSelect.disabled = true;
    fontSelect.style.cssText =
      "padding:7px 10px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
      "font:800 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;min-width:180px;pointer-events:auto;";
    fontSelect.addEventListener("pointerdown", stopDownOnly, true);
    fontSelect.addEventListener("mousedown", stopDownOnly, true);
    fontSelect.addEventListener("touchstart", stopDownOnly, true);

    const opt = document.createElement("option");
    opt.value = "";
    opt.textContent = "Select text to change font";
    fontSelect.appendChild(opt);
    fontSelect.className = "pamela-mobile-hide";
    r1left.appendChild(mkBtn("pamela-size-down", "A-", true)).classList.add("pamela-mobile-hide");
    r1left.appendChild(mkBtn("pamela-size-up", "A+", true)).classList.add("pamela-mobile-hide");
    const curveTextBtn = mkBtn("pamela-follow-shape", "Curve Text", false);
    curveTextBtn.classList.add("pamela-mobile-hide");
    curveTextBtn.style.minWidth = "108px";
    curveTextBtn.style.paddingLeft = "8px";
    curveTextBtn.style.paddingRight = "8px";
    curveTextBtn.style.background = "#111";
    curveTextBtn.style.color = "#fff";
    curveTextBtn.style.borderColor = "#111";
    r1left.appendChild(curveTextBtn);
    const laserTextStyleBtn = mkBtn("pamela-laser-text-style", "Use Black Text", false);
    laserTextStyleBtn.style.display = "none";
    laserTextStyleBtn.classList.add("pamela-mobile-hide");
    laserTextStyleBtn.style.minWidth = "124px";
    laserTextStyleBtn.style.background = "#111";
    laserTextStyleBtn.style.color = "#fff";
    laserTextStyleBtn.style.borderColor = "#111";
    r1left.appendChild(laserTextStyleBtn);
    const groupSideTextBtn = mkBtn("pamela-group-side-text", "Group Text", false);
    groupSideTextBtn.style.display = "none";
    groupSideTextBtn.setAttribute("aria-hidden", "true");
    groupSideTextBtn.setAttribute("aria-label", "Group side text");
    groupSideTextBtn.setAttribute("data-pamela-tip", "Group all text on this side so dragging one piece moves them together");
    textAddButtonsWrap.appendChild(independentSidesBtn);
    textAddButtonsWrap.appendChild(groupSideTextBtn);
    r1left.appendChild(fontSelect);

    // Row 2: DESIGN
    const designLabel = mkLabel("DESIGN");
    designLabel.className = "pamela-toolbar-group-label pamela-mobile-hide";
    r1right.appendChild(designLabel);
    r1right.appendChild(mkBtn("pamela-view-clipart", "View Library", true));
    r1right.appendChild(mkBtn("pamela-panels-frames", "Panels/Frames", true));

    // Ã¢Å“â€¦ NEW: Undo / Redo (white icon buttons)
    const undoBtn = mkBtn("pamela-undo-btn", "\u21A9", false);
    undoBtn.title = "Undo (Ctrl+Z)";
    undoBtn.disabled = true;
    undoBtn.style.minWidth = "42px";
    // Mobile now has its own pinned undo/redo in the top tab row
    // (pamela-mobile-undo-btn/redo), so this desktop pair next to Optional
    // Add-ons under Design would just be a redundant second copy on mobile.
    undoBtn.classList.add("pamela-mobile-hide");

    const redoBtn = mkBtn("pamela-redo-btn", "\u21AA", false);
    redoBtn.title = "Redo (Ctrl+Shift+Z)";
    redoBtn.disabled = true;
    redoBtn.style.minWidth = "42px";
    redoBtn.classList.add("pamela-mobile-hide");
    const resetViewBtn = mkBtn("pamela-reset-view", "Reset View", false);
    const mobileResetViewBtn = mkBtn("pamela-mobile-reset-view", "Reset", false);
    const mobileUndoBtn = mkBtn("pamela-mobile-undo-btn", "\u21A9", false);
    const mobileRedoBtn = mkBtn("pamela-mobile-redo-btn", "\u21AA", false);
    mobileUndoBtn.title = "Undo";
    mobileRedoBtn.title = "Redo";
    mobileUndoBtn.disabled = true;
    mobileRedoBtn.disabled = true;

    // Undo/redo need to work no matter which selection-dependent context bar
    // (text/clipart/panel/etc.) is showing, so they live in the top tab row
    // itself -- not inside any one of those bars -- pinned to its right end.
    // This row stays on screen regardless of tab or selection.
    const mobileTopUndoRedoWrap = document.createElement("div");
    mobileTopUndoRedoWrap.id = "pamela-mobile-top-undo-redo";
    mobileTopUndoRedoWrap.style.cssText = "display:flex;align-items:center;gap:6px;flex:0 0 auto;margin-left:auto;";
    mobileTopUndoRedoWrap.appendChild(mobileUndoBtn);
    mobileTopUndoRedoWrap.appendChild(mobileRedoBtn);
    mobileTopGroups.appendChild(mobileTopUndoRedoWrap);

    // Ã¢Å“â€¦ Toolbar button: Optional Add-ons (opens right drawer if it exists)
const addonsBtn = mkBtn("pamela-open-addons", "Optional Add-ons", true);
addonsBtn.style.cssText =
  smallBtnCss + "background:#111;color:#fff;"; // same style as your black buttons

addonsBtn.addEventListener("click", async () => {
  const fly = ensureAddOnFlyout();
  const opening = fly.style.display !== "block";
  fly.style.display = opening ? "block" : "none";
  if (!opening) return;

  try {
  if (typeof window.pamelaPopulatePorcelainDropdownNow === "function") {
    await window.pamelaPopulatePorcelainDropdownNow();
  }
  } catch (e) {
    console.warn("Porcelain fill failed:", e);
  }
});

r1right.appendChild(addonsBtn);
r1right.appendChild(undoBtn);
r1right.appendChild(redoBtn);
r1right.appendChild(mkBtn("pamela-rot-left", "\u27F2", false)).classList.add("pamela-mobile-hide");
r1right.appendChild(mkBtn("pamela-rot-right", "\u27F3", false)).classList.add("pamela-mobile-hide");
    const nudgeWrap = document.createElement("div");
    nudgeWrap.className = "pamela-mobile-hide";
    nudgeWrap.style.cssText = "position:relative;display:inline-flex;align-items:center;";
    const nudgeToggleBtn = mkBtn("pamela-nudge-toggle", "Nudge", false);
    nudgeToggleBtn.setAttribute("aria-expanded", "false");
    const nudgeMenu = document.createElement("div");
    nudgeMenu.id = "pamela-nudge-menu";
    nudgeMenu.style.cssText =
      "display:none;position:absolute;top:calc(100% + 8px);right:0;z-index:1000;" +
      "background:#fff;border:1px solid rgba(17,17,17,.18);border-radius:12px;padding:8px;" +
      "box-shadow:0 10px 28px rgba(0,0,0,.14);grid-template-columns:repeat(3,minmax(36px,auto));gap:6px;";
    const nudgeSpacer1 = document.createElement("div");
    const nudgeSpacer2 = document.createElement("div");
    nudgeMenu.appendChild(nudgeSpacer1);
    nudgeMenu.appendChild(mkBtn("pamela-nudge-up", "\u2191", false));
    nudgeMenu.appendChild(nudgeSpacer2);
    nudgeMenu.appendChild(mkBtn("pamela-nudge-left", "\u2190", false));
    nudgeMenu.appendChild(mkBtn("pamela-nudge-down", "\u2193", false));
    nudgeMenu.appendChild(mkBtn("pamela-nudge-right", "\u2192", false));
    nudgeWrap.appendChild(nudgeToggleBtn);
    nudgeWrap.appendChild(nudgeMenu);
    nudgeToggleBtn.addEventListener("click", (e) => {
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
      const open = nudgeMenu.style.display === "grid";
      nudgeMenu.style.display = open ? "none" : "grid";
      nudgeToggleBtn.setAttribute("aria-expanded", open ? "false" : "true");
    });
    nudgeMenu.addEventListener("click", (e) => {
      try { e.stopPropagation(); } catch (_) {}
    });
    if (!window.__pamelaNudgeMenuOutsideCloseBound) {
      window.__pamelaNudgeMenuOutsideCloseBound = true;
      document.addEventListener("click", () => {
        const menu = document.getElementById("pamela-nudge-menu");
        const toggle = document.getElementById("pamela-nudge-toggle");
        if (menu) menu.style.display = "none";
        if (toggle) toggle.setAttribute("aria-expanded", "false");
      });
    }
    r1right.appendChild(nudgeWrap);
    r1right.appendChild(mkBtn("pamela-flip-x", "Flip", false)).classList.add("pamela-mobile-hide");
    const mirrorCloneBtn = mkBtn("pamela-mirror-clone-btn", "Mirror (Clone)", false);
    mirrorCloneBtn.disabled = true;
    r1right.appendChild(mirrorCloneBtn).classList.add("pamela-mobile-hide");
r1right.appendChild(mkBtn("pamela-clip-size-down", "\u2212", false)).classList.add("pamela-mobile-hide");
r1right.appendChild(mkBtn("pamela-clip-size-up", "+", false)).classList.add("pamela-mobile-hide");
r1right.appendChild(mkBtn("pamela-delete", "Delete", false)).classList.add("pamela-mobile-hide");

    // Row 3: VIEW
    const viewLabel = mkLabel("VIEW");
    viewLabel.className = "pamela-toolbar-group-label pamela-mobile-hide";
    r2left.appendChild(viewLabel);
    r2left.appendChild(mkBtn("pamela-zoom-out", "Zoom \u2212", false));
    r2left.appendChild(mkBtn("pamela-zoom-in", "Zoom +", false));
    r2left.appendChild(resetViewBtn);
    r2left.appendChild(mkBtn("pamela-toggle-grid", "Grid: Off", false));
    r2left.appendChild(mkBtn("pamela-fullscreen", "Full Screen", false));

    row1.appendChild(r1left);
    row1.appendChild(r1right);
    row2.appendChild(r2left);
    row2.appendChild(r2right);
    const imagesLabel = mkLabel("IMAGES");
    imagesLabel.className = "pamela-toolbar-group-label";
    imagesLabel.dataset.pamelaImagesLabel = "true";
    rImages.appendChild(imagesLabel);
    const replaceImageBtn = mkBtn("pamela-image-replace-btn", "Replace Image", false);
    replaceImageBtn.style.display = "none";
    replaceImageBtn.addEventListener("click", () => {
      pamelaReplaceSelectedImageFromToolbar(window.pamelaMonumentEditor || window.pamelaEditor || window.editor || window.PAMELA_EDITOR || window.__PAMELA_EDITOR__);
    });
    rImages.appendChild(replaceImageBtn);
    const portraitSizePositionBtn = mkBtn("pamela-porcelain-size-reposition-btn", "Fit Photo", false);
    portraitSizePositionBtn.style.display = "none";
    portraitSizePositionBtn.addEventListener("click", () => {
      pamelaOpenPortraitAdjust(window.pamelaMonumentEditor || window.pamelaEditor || window.editor || window.PAMELA_EDITOR || window.__PAMELA_EDITOR__);
    });
    rImages.appendChild(portraitSizePositionBtn);
    try {
      const pendingImageButtons = window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__ || [];
      pendingImageButtons.forEach((btn) => {
        try { if (btn) rImages.appendChild(btn); } catch (_) {}
      });
      window.__PAMELA_PENDING_IMAGE_TOOLBAR_BTNS__ = [];
    } catch (_) {}
    rowImages.appendChild(rImages);

    const hint = document.createElement("div");
    hint.id = "pamela-toolbar-hint";
    hint.className = "pamela-toolbar-hint";
    hint.textContent = PAMELA_DEFAULT_HINT_TEXT;
    hint.style.cssText = "font:700 15px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#000;text-align:center;padding-top:2px;";

    bar.appendChild(mobileTopGroups);
    bar.appendChild(row1);
    bar.appendChild(row2);
    bar.appendChild(rowImages);
    bar.appendChild(hint);
    requestAnimationFrame(() => {
      pamelaRefreshImagesToolbar(window.pamelaMonumentEditor || window.pamelaEditor || window.editor || window.PAMELA_EDITOR || window.__PAMELA_EDITOR__);
    });
    if (!window.__pamelaImagesToolbarRefreshTimer) {
      window.__pamelaImagesToolbarRefreshTimer = setInterval(() => {
        pamelaRefreshImagesToolbar(window.pamelaMonumentEditor || window.pamelaEditor || window.editor || window.PAMELA_EDITOR || window.__PAMELA_EDITOR__);
      }, 250);
    }

    wrapEl.style.position = wrapEl.style.position || "relative";
    const floatingBox = document.createElement("div");
    floatingBox.id = "pamela-floating-view-box";
    floatingBox.style.cssText =
      "position:absolute;top:12px;right:12px;z-index:1000001;pointer-events:auto;" +
      "background:rgba(255,255,255,0.96);border:1px solid rgba(17,17,17,0.18);" +
      "border-radius:12px;box-shadow:0 10px 24px rgba(0,0,0,0.14);width:132px;display:none;";

    const floatingHeader = document.createElement("div");
    floatingHeader.style.cssText =
      "display:flex;align-items:center;justify-content:space-between;gap:8px;" +
      "padding:6px 8px;border-bottom:1px solid rgba(17,17,17,0.1);cursor:grab;touch-action:none;" +
      "font:900 11px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
    const floatingTitle = document.createElement("div");
    floatingTitle.textContent = "View";
    const floatingToggle = document.createElement("button");
    floatingToggle.type = "button";
    floatingToggle.id = "pamela-floating-view-toggle";
    floatingToggle.textContent = "\u2212";
    floatingToggle.style.cssText =
      "width:28px;height:28px;padding:0;border-radius:9px;border:1px solid #111;background:#fff;color:#111;" +
      "font:900 16px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";
    floatingHeader.appendChild(floatingTitle);
    floatingHeader.appendChild(floatingToggle);

    const floatingBody = document.createElement("div");
    floatingBody.id = "pamela-floating-view-body";
    floatingBody.style.cssText = "display:flex;flex-direction:column;gap:6px;padding:8px;";
    mobileResetViewBtn.style.cssText = smallBtnCss + "background:#fff;color:#111;padding:6px 8px;font:800 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
    // Undo/redo used to live here too, but that floating box is easy to miss
    // (collapsible, draggable, View-tab-flavored) -- they now live pinned to
    // the top tab row instead. See pamela-mobile-top-undo-redo.
    mobileUndoBtn.style.cssText = smallBtnCss + "background:#fff;color:#111;padding:5px 0;font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;min-width:0;";
    mobileRedoBtn.style.cssText = smallBtnCss + "background:#fff;color:#111;padding:5px 0;font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;min-width:0;";
    floatingBody.appendChild(mobileResetViewBtn);

    floatingToggle.addEventListener("click", (e) => {
      try { e.stopPropagation(); } catch (_) {}
      const open = floatingBody.style.display !== "none";
      floatingBody.style.display = open ? "none" : "flex";
      floatingToggle.textContent = open ? "+" : "\u2212";
    });

    let dragState = null;
    const endDrag = () => {
      dragState = null;
      try { floatingHeader.style.cursor = "grab"; } catch (_) {}
    };
    floatingHeader.addEventListener("pointerdown", (e) => {
      if (e.target === floatingToggle) return;
      dragState = {
        startX: e.clientX,
        startY: e.clientY,
        left: floatingBox.offsetLeft,
        top: floatingBox.offsetTop,
      };
      floatingBox.style.left = `${floatingBox.offsetLeft}px`;
      floatingBox.style.top = `${floatingBox.offsetTop}px`;
      floatingBox.style.right = "auto";
      floatingHeader.style.cursor = "grabbing";
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
      try { floatingHeader.setPointerCapture?.(e.pointerId); } catch (_) {}
    });
    window.addEventListener("pointermove", (e) => {
      if (!dragState) return;
      floatingBox.style.left = `${Math.max(0, dragState.left + (e.clientX - dragState.startX))}px`;
      floatingBox.style.top = `${Math.max(0, dragState.top + (e.clientY - dragState.startY))}px`;
    }, true);
    window.addEventListener("pointerup", endDrag, true);
    window.addEventListener("pointercancel", endDrag, true);

    [floatingBox, floatingHeader, floatingBody].forEach((el) => {
      el.addEventListener("pointerdown", stopDownOnly, true);
      el.addEventListener("mousedown", stopDownOnly, true);
      el.addEventListener("touchstart", stopDownOnly, true);
    });

    floatingBox.appendChild(floatingHeader);
    floatingBox.appendChild(floatingBody);
    wrapEl.appendChild(floatingBox);

    const syncFloatingBoxVisibility = () => {
      const isMobileFloating = !!pamelaIsMobileEditorViewport?.();
      floatingBox.style.display = isMobileFloating ? "" : "none";
    };
    syncFloatingBoxVisibility();

    (wrapEl.parentNode || document.body).insertBefore(bar, wrapEl);
    pamelaEnsureMobileContextToolbar();
    pamelaSyncMobileTopToolbar(bar);
    window.addEventListener("resize", () => pamelaSyncMobileTopToolbar(bar));
    window.addEventListener("resize", syncFloatingBoxVisibility);

    // -----------------------------
    // Wire +/- (Clipart + Panels)
    // -----------------------------

const getEditor = () => window.pamelaMonumentEditor || window.pamelaEditor;

independentSidesBtn.addEventListener("click", () => {
  const ed = getEditor();
  if (!ed || !pamelaCanMoveSideIndependently(ed)) return;
  ed.horizontalMirrorIndependentMode = !ed.horizontalMirrorIndependentMode;
  pamelaRefreshIndependentMoveUI(ed);
});

groupSideTextBtn.addEventListener("click", () => {
  const ed = getEditor();
  if (!ed) return;
  if (pamelaIsGroupedSideTextActive(ed, ed.selected)) {
    ed.groupedTextSide = null;
  } else if (pamelaCanGroupSideText(ed, ed.selected)) {
    ed.groupedTextSide = pamelaGetSide(ed, ed.selected);
  }
  pamelaRefreshTextGroupUI(ed);
  pamelaRefreshTextGroupOverlay(ed);
  try { ed.refreshButtons?.(); } catch (_) {}
  try { if (ed.selected) ed.selectNode(ed.selected); } catch (_) {}
});

const bumpImageSize = (dir) => {
  const ed = getEditor();
  if (!ed || !ed.selected) return;

  const n = ed.selected;
  const cls = n.getClassName?.() || n.className || "";

  const isPanel = cls === "Image" && n.attrs?.pamelaPanel === true;
  const isClip  = cls === "Image" && n.attrs?.pamelaClipart === true;
  if (!isPanel && !isClip) return;

  const layer = isPanel ? ed.layers?.panels : ed.layers?.content;
  if (!layer) return;

  // capture visual center BEFORE
  const r1 = n.getClientRect({ relativeTo: layer, skipTransform: false });
  const c1x = r1.x + r1.width / 2;
  const c1y = r1.y + r1.height / 2;

  // commit BEFORE
  ed._historyCommit?.("resize-btn");

  if (isPanel) {
    // Panels: bake width/height, keep mirror sign
    const sx = Number(n.scaleX?.() ?? 1) || 1;
    const sy = Number(n.scaleY?.() ?? 1) || 1;
    const signX = sx < 0 ? -1 : 1;
    const signY = sy < 0 ? -1 : 1;

    const w0 = Number(n.width?.() ?? 0) || 0;
    const h0 = Number(n.height?.() ?? 0) || 0;
    const baseW = Number(n.getAttr?.("pamelaPanelBaseWidth") || w0) || w0;
    const baseH = Number(n.getAttr?.("pamelaPanelBaseHeight") || h0) || h0;
    const isLastNamePanel = pamelaIsLastNamePanelNode(n);
    const step = (dir >= 0 ? 18 : -18); // 0.25" @ 72dpi
    const aspect = (h0 > 0) ? (w0 / h0) : 1;

    let newW = w0 + step;
    let newH = isLastNamePanel ? h0 : (aspect ? (newW / aspect) : (h0 + step));

    try {
      const minW = isLastNamePanel
        ? pamelaGetLastNamePanelMinWidth(ed, n)
        : (dir >= 0 ? PANEL_MIN_W_PX : Math.max(PANEL_MIN_W_PX, baseW));
      const minH = dir >= 0 ? PANEL_MIN_H_PX : Math.max(PANEL_MIN_H_PX, baseH);
      if (typeof minW === "number") newW = Math.max(minW, newW);
      if (typeof minH === "number") newH = Math.max(minH, newH);
    } catch (_) {}

    n.size({ width: newW, height: newH });
    try { pamelaUpdateTraditionalPanelSliceImage(n); } catch (_) {}
    n.scale({ x: signX, y: signY });

    n.offsetX(newW / 2);
    n.offsetY(newH / 2);
  } else {
    // Clipart: scale only
    const f = (dir >= 0) ? 1.08 : (1 / 1.08);
    n.scaleX((Number(n.scaleX?.() ?? 1) || 1) * f);
    n.scaleY((Number(n.scaleY?.() ?? 1) || 1) * f);
  }

  try { pamelaClampNodeIntoSafeRect(ed, n); } catch (_) {}

  const r2 = n.getClientRect({ relativeTo: layer, skipTransform: false });
  const c2x = r2.x + r2.width / 2;
  const c2y = r2.y + r2.height / 2;

  try {
    n.x(n.x() + (c1x - c2x));
    n.y(n.y() + (c1y - c2y));
  } catch (_) {}

  try { pamelaClampNodeIntoSafeRect(ed, n); } catch (_) {}

  ed._scheduleOverlapColors && ed._scheduleOverlapColors();

  try { ed.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { ed.layers?.content?.batchDraw?.(); } catch (_) {}
  try { ed.layers?.ui?.batchDraw?.(); } catch (_) {}

  try { ed.selectNode?.(n); } catch (_) {}

  // commit AFTER
  ed._historyCommit?.("resize-btn");
};

// IMPORTANT: remove any old handlers (prevents double-firing)
const up = document.getElementById("pamela-clip-size-up");
const dn = document.getElementById("pamela-clip-size-down");

if (up) {
  up.onclick = null;
  up.addEventListener("click", () => bumpImageSize(+1));
}
if (dn) {
  dn.onclick = null;
  dn.addEventListener("click", () => bumpImageSize(-1));
}

// -----------------------------
// SVG style menus (desktop + mobile)
// -----------------------------
const panelModeToggleBtn = document.getElementById("pamela-panel-mode-toggle");
const clipartModeToggleBtn = document.getElementById("pamela-clipart-mode-toggle");

function pamelaGetSvgStyleChoices(kind) {
  if (kind === "panel") {
    return [
      { label: "White Outline", mode: "white-outline" },
      { label: "Frosted", mode: "standard" },
    ];
  }
  return [
    { label: "White Outline", mode: "white-outline" },
    { label: "Frosted", mode: "standard" },
    { label: "Solid White", mode: "reverse-process" },
  ];
}

function pamelaGetCurrentSvgStyleMode(editor, kind) {
  try {
    if (kind === "panel") return pamelaGetEffectivePanelMode(editor);
    const node = editor?.selected || null;
    if (node?.getAttr?.("pamelaClipart") !== true) return "";
    return pamelaGetEffectiveClipartMode(editor, node);
  } catch (_) {
    return "";
  }
}

function pamelaShowSvgStyleNotice(message) {
  let notice = document.getElementById("pamela-svg-style-notice");
  if (!notice) {
    notice = document.createElement("div");
    notice.id = "pamela-svg-style-notice";
    notice.style.cssText =
      "position:fixed;left:50%;bottom:86px;z-index:1000002;max-width:min(420px,calc(100vw - 24px));" +
      "transform:translate(-50%,8px);padding:10px 14px;border-radius:12px;background:#111;color:#fff;" +
      "box-shadow:0 10px 28px rgba(0,0,0,.25);font:800 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "text-align:center;opacity:0;pointer-events:none;transition:opacity 160ms ease,transform 160ms ease;";
    document.body.appendChild(notice);
  }

  notice.textContent = String(message || "");
  notice.style.opacity = "1";
  notice.style.transform = "translate(-50%,0)";
  clearTimeout(window.__pamelaSvgStyleNoticeTimer);
  window.__pamelaSvgStyleNoticeTimer = setTimeout(() => {
    try {
      notice.style.opacity = "0";
      notice.style.transform = "translate(-50%,8px)";
    } catch (_) {}
  }, 2800);
}

async function pamelaApplySvgStyleChoice(editor, kind, mode) {
  if (!editor || pamelaSelectedGraniteIsLight()) return;
  let changedPanelsForClipart = false;

  if (kind === "panel") {
    await pamelaSetAllPanelsMode(editor, mode);
  } else {
    if (mode !== "standard" && pamelaGetStoredPanelMode(editor) === "standard") {
      await pamelaSetAllPanelsMode(editor, "white-outline");
      changedPanelsForClipart = true;
    }
    await pamelaSetSelectedClipartMode(editor, mode);
  }

  try { pamelaUpdateSvgModeButtonLabels(); } catch (_) {}
  try { pamelaSyncMobileToolbars(editor); } catch (_) {}
  if (changedPanelsForClipart) {
    pamelaShowSvgStyleNotice("Panels changed to White Outline to support this clipart style.");
  }

  try {
    const actionLabel = kind === "panel" ? "Panel Style" : "Clipart Style";
    const sheet = document.getElementById("pamela-mobile-context-sheet");
    if (
      sheet?.getAttribute("data-open") === "true" &&
      sheet?.getAttribute("data-kind") === actionLabel
    ) {
      pamelaRenderMobileContextSheet(editor, {
        ...pamelaGetSvgStyleMenuSpec(editor, kind),
        __actionLabel: actionLabel,
      });
      sheet.setAttribute("data-kind", actionLabel);
    }
  } catch (_) {}
}

function pamelaGetSvgStyleMenuSpec(editor, kind) {
  const current = pamelaGetCurrentSvgStyleMode(editor, kind);
  return {
    type: "buttons",
    label: kind === "panel" ? "Panel Style" : "Clipart Style",
    buttons: pamelaGetSvgStyleChoices(kind).map((choice) => ({
      label: choice.label,
      selected: current === choice.mode,
      onClick: () => pamelaApplySvgStyleChoice(editor, kind, choice.mode),
    })),
  };
}

// The mobile context toolbar is defined outside this editor-init scope.
// Publish the menu builder explicitly so black-granite clipart/panel
// selection cannot fail with an out-of-scope ReferenceError.
window.PAMELA_getSvgStyleMenuSpec = pamelaGetSvgStyleMenuSpec;

function pamelaCloseDesktopSvgStyleMenu() {
  const menu = document.getElementById("pamela-svg-style-menu");
  if (menu) menu.style.display = "none";
}

function pamelaEnsureDesktopSvgStyleMenu() {
  let menu = document.getElementById("pamela-svg-style-menu");
  if (menu) return menu;

  menu = document.createElement("div");
  menu.id = "pamela-svg-style-menu";
  menu.style.cssText =
    "display:none;position:fixed;z-index:1000001;min-width:190px;padding:10px;" +
    "border:1px solid rgba(17,17,17,.2);border-radius:12px;background:#fff;" +
    "box-shadow:0 12px 30px rgba(0,0,0,.2);";
  menu.addEventListener("click", (e) => e.stopPropagation());
  document.body.appendChild(menu);

  if (!window.__pamelaSvgStyleMenuOutsideCloseBound) {
    window.__pamelaSvgStyleMenuOutsideCloseBound = true;
    document.addEventListener("click", (e) => {
      const liveMenu = document.getElementById("pamela-svg-style-menu");
      if (!liveMenu || liveMenu.style.display === "none") return;
      if (liveMenu.contains(e.target)) return;
      if (e.target === panelModeToggleBtn || e.target === clipartModeToggleBtn) return;
      pamelaCloseDesktopSvgStyleMenu();
    });
    window.addEventListener("resize", pamelaCloseDesktopSvgStyleMenu);
    window.addEventListener("scroll", pamelaCloseDesktopSvgStyleMenu, true);
  }

  return menu;
}

function pamelaOpenDesktopSvgStyleMenu(anchor, editor, kind) {
  if (!anchor || !editor || pamelaSelectedGraniteIsLight()) return;
  const menu = pamelaEnsureDesktopSvgStyleMenu();
  const current = pamelaGetCurrentSvgStyleMode(editor, kind);
  menu.innerHTML = "";

  const title = document.createElement("div");
  title.textContent = kind === "panel" ? "Panel Style" : "Clipart Style";
  title.style.cssText =
    "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
    "text-transform:uppercase;letter-spacing:.04em;color:#4b5563;margin:0 0 8px;";
  menu.appendChild(title);

  pamelaGetSvgStyleChoices(kind).forEach((choice) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = choice.label;
    btn.setAttribute("aria-pressed", current === choice.mode ? "true" : "false");
    btn.style.cssText =
      "display:block;width:100%;margin:6px 0 0;padding:9px 10px;border:1px solid #111;" +
      "border-radius:9px;text-align:left;font:800 13px/1.1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      (current === choice.mode ? "background:#111;color:#fff;" : "background:#fff;color:#111;");
    btn.addEventListener("click", async (e) => {
      try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
      await pamelaApplySvgStyleChoice(editor, kind, choice.mode);
      pamelaCloseDesktopSvgStyleMenu();
    });
    menu.appendChild(btn);
  });

  menu.style.display = "block";
  const rect = anchor.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuRect.width - 8));
  const top = Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - menuRect.height - 8));
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

const pamelaUpdateSvgModeButtonLabels = () => {
  const ed = getEditor();
  const isLightGranite = pamelaSelectedGraniteIsLight();

  if (panelModeToggleBtn) {
    let panelEnabled = false;
    try {
      const panels = ed?.layers?.panels?.getChildren?.((node) =>
        node?.getAttr?.("pamelaPanel") === true && node.isVisible?.() !== false
      ) || [];
      panelEnabled = panels.length > 0;
    } catch (_) {}

    panelModeToggleBtn.textContent = "Panel Style";
    panelModeToggleBtn.disabled = isLightGranite || !panelEnabled;
    panelModeToggleBtn.style.display = isLightGranite ? "none" : "";
    panelModeToggleBtn.style.opacity = (isLightGranite || !panelEnabled) ? "0.45" : "1";
    panelModeToggleBtn.style.cursor = (isLightGranite || !panelEnabled) ? "not-allowed" : "pointer";
  }

  if (clipartModeToggleBtn) {
    let clipEnabled = false;

    try {
      if (
        ed &&
        ed.selected &&
        ed.selected.getAttr &&
        ed.selected.getAttr("pamelaClipart") === true
      ) {
        clipEnabled = true;

      }
    } catch (_) {}

    clipartModeToggleBtn.textContent = clipEnabled ? "Clipart Style" : "Select Clipart to Style";
    clipartModeToggleBtn.disabled = !clipEnabled || isLightGranite;
    clipartModeToggleBtn.style.display = isLightGranite ? "none" : "";
    clipartModeToggleBtn.style.opacity = (!clipEnabled || isLightGranite) ? "0.45" : "1";
    clipartModeToggleBtn.style.cursor = (!clipEnabled || isLightGranite) ? "not-allowed" : "pointer";
  }
};

if (panelModeToggleBtn) {
  panelModeToggleBtn.onclick = null;
  panelModeToggleBtn.addEventListener("click", (e) => {
    try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
    const ed = getEditor();
    if (!ed) return;
    if (pamelaSelectedGraniteIsLight()) return;
    pamelaOpenDesktopSvgStyleMenu(panelModeToggleBtn, ed, "panel");
  });
}

if (clipartModeToggleBtn) {
  clipartModeToggleBtn.onclick = null;
  clipartModeToggleBtn.addEventListener("click", (e) => {
    try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
    const ed = getEditor();
    if (!ed || !ed.selected) return;
    if (pamelaSelectedGraniteIsLight()) return;
    if (ed.selected.getAttr?.("pamelaClipart") !== true) return;
    pamelaOpenDesktopSvgStyleMenu(clipartModeToggleBtn, ed, "clipart");
  });
}

// Keep button state in sync when selection changes
if (!window.__pamelaSvgModeButtonWatcher__) {
  let lastSelectedKey = "";

  window.__pamelaSvgModeButtonWatcher__ = setInterval(() => {
    try {
      const ed = getEditor();
      const sel = ed?.selected || null;

      let key = "none";

      if (sel) {
        const isClipart = sel.getAttr && sel.getAttr("pamelaClipart") === true;
        const isPanel = sel.getAttr && sel.getAttr("pamelaPanel") === true;
        const id =
          sel._id ||
          sel.id?.() ||
          sel._id ||
          "";

        if (isClipart) key = `clipart:${id}:${String(sel.getAttr("pamelaClipartMode") || "")}`;
        else if (isPanel) key = `panel:${id}:${String(sel.getAttr("pamelaPanelMode") || "")}`;
        else key = `other:${id}`;
      }

      if (key !== lastSelectedKey) {
        lastSelectedKey = key;
        pamelaUpdateSvgModeButtonLabels();
      }
    } catch (_) {}
  }, 150);
}

pamelaUpdateSvgModeButtonLabels();
pamelaUpdateLightGraniteToggleVisibility();

return bar;
}

// =============================
// Add-ons Flyout (Right Drawer) Ã¢â‚¬â€ Clipart/Panels style (keeps ALL existing Add-ons UI)
// =============================
function ensureAddOnFlyout() {
  let fly = document.getElementById("pamela-addon-flyout");
  if (fly) return fly;

  fly = document.createElement("div");
  fly.id = "pamela-addon-flyout";
  
  fly.style.cssText =
    "position:fixed;top:0;right:0;height:100vh;width:420px;max-width:92vw;" +
    "background:#fff;border-left:1px solid rgba(0,0,0,0.12);" +
    "box-shadow:-12px 0 28px rgba(0,0,0,0.18);" +
    "z-index:9999999;display:none;overflow:hidden;";

  // Header (Clipart-style) + X
  const head = document.createElement("div");
  head.style.cssText =
    "display:flex;align-items:flex-start;justify-content:space-between;gap:12px;" +
    "padding:12px 14px;border-bottom:1px solid rgba(0,0,0,0.10);" +
    "font:900 18px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";

  const headTitle = document.createElement("div");
  headTitle.innerHTML =
    `<div style="font:900 18px/1.15 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;">Optional Add-ons</div>` +
    `<div style="margin-top:3px;font:800 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;">Enhance your monument with extras.</div>` +
    `<div style="margin-top:2px;font:800 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;">Prices update in the price box as you add items.</div>`;

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close");
    closeBtn.textContent = "x";
  closeBtn.style.cssText =
    "width:44px;height:38px;border-radius:12px;border:1px solid rgba(0,0,0,0.18);" +
    "background:#fff;color:#111;font:900 18px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
    "cursor:pointer;";

  closeBtn.addEventListener("click", () => {
    fly.style.display = "none";
  });

  head.appendChild(headTitle);
  head.appendChild(closeBtn);
  fly.appendChild(head);

  // Body (scroll)
  const body = document.createElement("div");
  body.id = "pamela-addon-body";
  body.style.cssText =
    "height:calc(100vh - 86px);overflow:auto;padding:14px 14px 24px 14px;" +
    "font:700 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  fly.appendChild(body);

  // Stop clicks from reaching canvas (Konva)
  const stop = (e) => { try { e.stopPropagation(); } catch (_) {} };
  fly.addEventListener("pointerdown", stop, true);
  fly.addEventListener("mousedown", stop, true);
  fly.addEventListener("touchstart", stop, true);

  // -----------------------------
  // EXISTING CARD CONTENT (UNCHANGED) Ã¢â‚¬â€ mounted inside drawer body
  // -----------------------------

  // Inner scroller (kept, but drawer body already scrolls)
  const scroller = document.createElement("div");
  scroller.id = "pamela-addon-scroller";
  scroller.style.cssText = "padding:2px 2px 6px 2px;";

  // Title (kept from your card)
  const title = document.createElement("div");
  title.textContent = "Optional Add-ons";
  title.style.cssText =
    "font:900 28px/1.05 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
    "text-align:center;margin:6px 0 14px 0;color:#111;display:none;";
  scroller.appendChild(title);

  // Small helper to create section headers
  const mkH = (txt) => {
    const h = document.createElement("div");
    h.textContent = txt;
    h.style.cssText =
      "font:900 22px/1.05 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "margin:18px 0 6px 0;color:#111;";
    return h;
  };

  // Small helper to create paragraph text
  const mkP = (html) => {
    const p = document.createElement("div");
    p.innerHTML = html;
    p.style.cssText =
      "font:700 13px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#222;" +
      "margin:0 0 10px 0;";
    return p;
  };

// Small input style (matches toolbar sizing)
const bigFieldCss =
  "width:100%;padding:8px 10px;border:2px solid #111;border-radius:12px;" +
  "font:800 13px/1.1 system-ui,-apple-system,Segoe UI,Roboto,Arial;background:#fff;color:#111;";

// Small black button style (matches toolbar sizing)
const bigBtnCss =
  "display:inline-flex;align-items:center;justify-content:center;" +
  "padding:8px 12px;border-radius:12px;border:2px solid #111;" +
  "background:#111;color:#fff;font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
  "cursor:pointer;";

  const rowBtnWrapCss = "display:flex;gap:12px;flex-wrap:wrap;";

  // -----------------------------
  // PORCELAIN PORTRAITS (working dropdown mount)
  // -----------------------------
  scroller.appendChild(mkH("Color Porcelain Portraits"));
  scroller.appendChild(
    mkP("Porcelain portraits can be added to any granite color. Prices vary by shape and size. They are recessed into the granite for long-lasting durability and include a forever guarantee. See terms and conditions for details and photo tips.")
  );

  // Mount / dropdown (we reuse your existing dropdown id)
  const porSel = document.createElement("select");
  porSel.id = "cm_porcelain_dropdown";
  porSel.style.cssText = bigFieldCss;
  scroller.appendChild(porSel);

  const addPorBtn = document.createElement("button");
  addPorBtn.type = "button";
  addPorBtn.id = "cm_porcelain_add_to_order";
  addPorBtn.textContent = "Add to order";
  addPorBtn.style.cssText = bigBtnCss + "flex:1;";
  // Ã¢Å“â€¦ Put Add + Edit buttons on the same row
const porBtnRow = document.createElement("div");
  porBtnRow.style.cssText = "display:flex;gap:12px;flex-wrap:wrap;margin-top:10px;width:100%;";

porBtnRow.appendChild(addPorBtn);

// Fit Photo controls the selected porcelain portrait photo inside its mask.
const editPorBtn =
  document.getElementById("pamela-porcelain-size-reposition-btn") ||
  document.getElementById("cm_porcelain_edit_selected") ||
  document.createElement("button");
editPorBtn.type = "button";
editPorBtn.id = "pamela-porcelain-size-reposition-btn";
editPorBtn.textContent = "Fit Photo";
editPorBtn.style.cssText = bigBtnCss + "flex:1;";

// --- helpers ---
const getEditor = () =>
  window.pamelaMonumentEditor ||
  window.pamelaEditor ||
  window.editor ||
  window.PAMELA_EDITOR ||
  window.__PAMELA_EDITOR__ ||
  null;

function findPortraitWrapperFromNode(n) {
  let cur = n;
  let hops = 0;

  while (cur && hops < 20) {
    const a = cur.attrs || {};
    const nm =
      (typeof cur.name === "function" ? String(cur.name() || "") : "") ||
      String(a.name || "");

    // Only return the actual portrait wrapper group, not inner/photo/hit children.
    const isPortraitWrapper =
      a.__isPortraitGroup === true ||
      a.__pamelaKind === "portrait" ||
      a.pamelaType === "portrait" ||
      a.pamelaPortrait === true ||
      nm.includes("pamela-portrait-group");

    if (isPortraitWrapper) return cur;

    try { cur = cur.getParent ? cur.getParent() : null; } catch (_) { cur = null; }
    hops++;
  }
  return null;
}

function findPortraitPhoto(wrapper) {
  if (!wrapper) return null;

  // Best path: inner group -> photo
  try {
    const inner =
      (typeof wrapper.findOne === "function" && wrapper.findOne(".pamela-portrait-inner")) || null;

    if (inner && typeof inner.findOne === "function") {
      const p = inner.findOne(".pamela-portrait-photo") || inner.findOne("pamela-portrait-photo");
      if (p) return p;
    }
  } catch (_) {}

  // Fallback: anywhere under wrapper
  try {
    if (typeof wrapper.findOne === "function") {
      return wrapper.findOne(".pamela-portrait-photo") || wrapper.findOne("pamela-portrait-photo") || null;
    }
  } catch (_) {}

  return null;
}

function setEnabled(on) {
  editPorBtn.disabled = !on;
  editPorBtn.style.opacity = on ? "1" : "0.35";
  editPorBtn.style.cursor = on ? "pointer" : "not-allowed";
}

function getActivePortraitWrapper(ed) {
  const isLiveWrapper = (wrapper) => !!(
    wrapper &&
    wrapper.destroyed?.() !== true &&
    wrapper.getStage?.()
  );

  const selectedWrap = findPortraitWrapperFromNode(ed?.selected || null);
  if (isLiveWrapper(selectedWrap)) return selectedWrap;

  const transformerNodes = [];
  try { transformerNodes.push(ed?.nodes?.tr?.nodes?.()?.[0] || null); } catch (_) {}
  try { transformerNodes.push(ed?.nodes?.trPanel?.nodes?.()?.[0] || null); } catch (_) {}
  try { transformerNodes.push(ed?.stage?.findOne?.("#cmPortraitTransformer")?.nodes?.()?.[0] || null); } catch (_) {}
  for (const node of transformerNodes) {
    const wrapper = findPortraitWrapperFromNode(node);
    if (isLiveWrapper(wrapper)) return wrapper;
  }

  return null;
}

// Enable/disable automatically based on current selection (no need to edit selectNode)
(function autoEnableEditButton() {
  if (window.__cmAutoEnableEditPortraitBtn) return;
  window.__cmAutoEnableEditPortraitBtn = true;

  const tick = () => {
    try {
      const ed = getEditor();
      const wrap = getActivePortraitWrapper(ed);
      setEnabled(!!wrap);
      if (wrap) {
        try { pamelaShowPortraitImagesToolbar?.(ed, wrap); } catch (_) {}
      } else {
        try { window.pamelaRefreshImagesToolbar?.(ed); } catch (_) {}
      }
    } catch (_) {
      setEnabled(false);
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
})();

// The desktop Images toolbar can be rebuilt after its original listener is
// attached. Capture this one action at the document level so Fit to Stone is
// never left as a visible no-op.
document.addEventListener("click", (event) => {
  const button = event.target?.closest?.("#pamela_laser_fit_stone_btn");
  if (!button) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    window.fitSelectedLaserArtworkToStone();
  } catch (error) {
    console.error("Fit to Stone failed:", error);
    alert("Fit to Stone could not be applied. Please select the laser image and try again.");
  }
}, true);

// Click = select the PHOTO and show portrait transform handles (NO UPLOAD)
if (!editPorBtn.__pamelaPortraitAdjustToolbarBound) editPorBtn.addEventListener("click", (e) => {
  try { e.preventDefault(); e.stopPropagation(); } catch (_) {}

  const ed = getEditor();
  if (!ed) return;

  const wrapper = getActivePortraitWrapper(ed);
  if (!wrapper) {
    alert("Nothing selected. Click the portrait on the canvas first.");
    return;
  }

  try { ed.__cmSelectedPortraitWrapper = wrapper; } catch (_) {}
  try { ed.selectNode?.(wrapper); } catch (_) {}

  const photo = findPortraitPhoto(wrapper);
  if (!photo) {
    alert("No photo is inside this portrait yet. Add/upload the photo first.");
    return;
  }

  try { window.__CM_LAST_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}

  try {
    if (window.CM_resumePortraitEditing?.(wrapper, { select: true })) {
      return;
    }
  } catch (_) {}

  // Force the portrait transformer (from your center-handle module) onto the photo
  try {
    const stage = photo.getStage && photo.getStage();
    let tr = stage && stage.findOne && stage.findOne("#cmPortraitTransformer");
    if (!tr && stage && typeof Konva !== "undefined") {
      const trLayer =
        ed?.layers?.ui ||
        (photo.getLayer && photo.getLayer()) ||
        null;

      if (trLayer && typeof trLayer.add === "function") {
        tr = new Konva.Transformer({
          id: "cmPortraitTransformer",
          rotateEnabled: true,
          keepRatio: true,
          enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
          ignoreStroke: true
        });
        trLayer.add(tr);
      }
    }
    if (tr && typeof tr.nodes === "function") {
      tr.nodes([photo]);
      try { tr.visible(true); } catch (_) {}
      try { tr.forceUpdate && tr.forceUpdate(); } catch (_) {}
      try { tr.moveToTop && tr.moveToTop(); } catch (_) {}
      const trLayer = tr.getLayer && tr.getLayer();
      if (trLayer?.batchDraw) trLayer.batchDraw();
      else if (trLayer?.draw) trLayer.draw();
    }
  } catch (_) {}

  // Show the blue center handle if it exists
  try {
    const h = wrapper.findOne && wrapper.findOne(".cm-portrait-move-handle");
    if (h) {
      h.visible(true);
      try { h.moveToTop(); } catch (_) {}
      const hl = h.getLayer && h.getLayer();
      if (hl?.batchDraw) hl.batchDraw();
      else if (hl?.draw) hl.draw();
    }
  } catch (_) {}

  // Optional: make sure your editor thinks something is selected
  try { ed.selectNode && ed.selectNode(photo); } catch (_) {}
});
editPorBtn.__pamelaPortraitAdjustToolbarBound = true;

pamelaMoveButtonToImagesToolbar(editPorBtn);
pamelaRefreshImagesToolbar(getEditor());
scroller.appendChild(porBtnRow);

  // Divider
  const hr1 = document.createElement("div");
  hr1.style.cssText = "height:1px;background:#e6e6e6;margin:18px 0 0 0;";
  scroller.appendChild(hr1);

  const __pamelaMonStyle =
    String(
      window.pamelaEditor?.monumentStyle ||
      window.pamelaMonumentEditor?.monumentStyle ||
      window.PAMELA_EDITOR?.monumentStyle ||
      ""
    ).toLowerCase();

  const __pamelaProductTitle =
    String(
      document.querySelector(".product__title, .product-title, h1")?.textContent || ""
    ).toLowerCase();

  const __isCompanionMonument =
    (window.pamelaEditor && window.pamelaEditor.layout === "companion") ||
    (window.pamelaMonumentEditor && window.pamelaMonumentEditor.layout === "companion") ||
    (window.PAMELA_EDITOR && window.PAMELA_EDITOR.layout === "companion");

  const __backLetteringIntro = __isCompanionMonument
    ? "Back engraving begins at $200, which includes the last name and a set amount of text."
    : "Back engraving begins at $150, which includes the last name and a set amount of text.";

  const __backLetteringBody =
    "You can add additional wording, designs and more as you wish. Your total will update automatically as you design, so you'll always know exactly where you stand, and you can remove anything at any time.";

  const __isMarkerMonument =
    __pamelaMonStyle.includes("marker") ||
    __pamelaProductTitle.includes("marker");

  const __showBackLettering = !__isMarkerMonument;

  if (__showBackLettering) {
  // -----------------------------
  // BACK LETTERING BUTTONS
  // -----------------------------
  scroller.appendChild(mkH("Lettering or Design on Back"));
  scroller.appendChild(mkP(__backLetteringIntro));
  scroller.appendChild(mkP(__backLetteringBody));

    const backBtns = document.createElement("div");
    backBtns.style.cssText = rowBtnWrapCss;

    const backLastBtn = document.createElement("button");
    backLastBtn.type = "button";
    backLastBtn.textContent = "Add Last Name on Back";
    backLastBtn.style.cssText = bigBtnCss;

    const backVerseBtn = document.createElement("button");
    backVerseBtn.type = "button";
    backVerseBtn.textContent = "Add Verse on Back";
    backVerseBtn.style.cssText = bigBtnCss;

    backLastBtn.id = "pamela-add-back-lastname";
    backVerseBtn.id = "pamela-add-back-verse";

    backBtns.appendChild(backLastBtn);
    backBtns.appendChild(backVerseBtn);
    scroller.appendChild(backBtns);
  }
  // -----------------------------
  // VASE / LASER INFO NOTE
  // -----------------------------
  const addonInfoHdr = mkH("");
  addonInfoHdr.id = "pamela-addon-info-header";
  scroller.appendChild(addonInfoHdr);

  const vaseInfoNote = mkP(
    "<b style='font-size:13px;'>Vase Engraving</b><br>" +
    "Designs or lettering can be added to select vases in any granite color. The cost is $50 per vase, per side (front and/or back). If you would like engraving on your vases, please contact us. Upon placing your order, a full mock-up will be provided for your approval before production begins."
  );
  vaseInfoNote.style.marginTop = "14px";
  vaseInfoNote.style.paddingTop = "8px";
  vaseInfoNote.style.borderTop = "1px solid #e5e5e5";
  vaseInfoNote.id = "pamela-vase-info-note";
  scroller.appendChild(vaseInfoNote);

  const laserInfoNote = mkP(
    "<b style='font-size:13px;'>Laser Etching</b><br>" +
    "Laser etching is available only on Jet Black granite monuments. This allows for detailed black-and-white portraits or realistic scenes created from our design library or from your personal photo. Because each etching is unique and requires additional preparation, pricing is provided individually. Please contact us for details."
  );
  laserInfoNote.style.marginTop = "14px";
  laserInfoNote.style.paddingTop = "8px";
  laserInfoNote.style.borderTop = "1px solid #e5e5e5";
  laserInfoNote.id = "pamela-laser-info-note";
  scroller.appendChild(laserInfoNote);
  // Mount content into drawer body (instead of card)
  body.appendChild(scroller);

  const makeAddonAccordionSection = (titleText, key, nodes) => {
    const section = document.createElement("div");
    section.dataset.pamelaAddonSection = key;
    section.style.cssText = "border:1px solid #ddd;border-radius:10px;background:#fff;overflow:hidden;margin:0 0 10px;";

    const header = document.createElement("button");
    header.type = "button";
    header.style.cssText =
      "width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;" +
      "padding:12px 13px;border:0;background:#f7f7f7;color:#111;cursor:pointer;" +
      "font:900 15px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;text-align:left;";
    header.setAttribute("aria-expanded", "false");

    const label = document.createElement("span");
    label.textContent = titleText;
    const icon = document.createElement("span");
    icon.textContent = "+";
    icon.setAttribute("aria-hidden", "true");
    header.appendChild(label);
    header.appendChild(icon);

    const content = document.createElement("div");
    content.style.cssText = "display:none;padding:12px;";
    (nodes || []).forEach((node) => {
      if (node) content.appendChild(node);
    });

    header.addEventListener("click", () => {
      const isOpen = content.style.display !== "none";
      scroller.querySelectorAll("[data-pamela-addon-section]").forEach((other) => {
        const otherContent = other.querySelector("[data-pamela-addon-content]");
        const otherIcon = other.querySelector("[data-pamela-addon-icon]");
        const otherHeader = other.querySelector("button");
        if (otherContent) otherContent.style.display = "none";
        if (otherIcon) otherIcon.textContent = "+";
        if (otherHeader) otherHeader.setAttribute("aria-expanded", "false");
      });
      if (!isOpen) {
        content.style.display = "block";
        icon.textContent = "-";
        header.setAttribute("aria-expanded", "true");
      }
    });

    icon.setAttribute("data-pamela-addon-icon", "true");
    content.setAttribute("data-pamela-addon-content", "true");
    section.appendChild(header);
    section.appendChild(content);
    return section;
  };

  window.PAMELA_makeAddonAccordionSection = makeAddonAccordionSection;
  window.PAMELA_openAddonFlyoutSection = (key) => {
    const section = scroller.querySelector(`[data-pamela-addon-section="${String(key || "")}"]`);
    if (!section) return false;
    scroller.querySelectorAll("[data-pamela-addon-section]").forEach((other) => {
      const otherContent = other.querySelector("[data-pamela-addon-content]");
      const otherIcon = other.querySelector("[data-pamela-addon-icon]");
      const otherHeader = other.querySelector("button");
      if (otherContent) otherContent.style.display = "none";
      if (otherIcon) otherIcon.textContent = "+";
      if (otherHeader) otherHeader.setAttribute("aria-expanded", "false");
    });
    const content = section.querySelector("[data-pamela-addon-content]");
    const icon = section.querySelector("[data-pamela-addon-icon]");
    const header = section.querySelector("button");
    if (content) content.style.display = "block";
    if (icon) icon.textContent = "-";
    if (header) header.setAttribute("aria-expanded", "true");
    try { section.scrollIntoView({ block: "nearest" }); } catch (_) {}
    return true;
  };

  const moveSectionNodes = (headerText, stopAtNode) => {
    const children = Array.from(scroller.children);
    const header = children.find((node) => String(node.textContent || "").trim() === headerText);
    if (!header) return [];
    const nodes = [];
    let cur = header;
    while (cur) {
      const next = cur.nextElementSibling;
      if (cur !== header && (cur === stopAtNode || cur.dataset?.pamelaAddonSection)) break;
      if (cur === stopAtNode) break;
      nodes.push(cur);
      cur = next;
      if (cur && cur === stopAtNode) break;
    }
    return nodes;
  };

  const porcelainStop = hr1;
  const porcelainNodes = moveSectionNodes("Color Porcelain Portraits", porcelainStop);
  const backStop = addonInfoHdr;
  const backNodes = moveSectionNodes("Lettering or Design on Back", backStop);
  if (porcelainNodes.length) {
    const porcelainSection = makeAddonAccordionSection("Porcelain Portraits", "porcelain", porcelainNodes);
    scroller.insertBefore(porcelainSection, scroller.firstChild);
  }
  if (backNodes.length) {
    const backSection = makeAddonAccordionSection("Lettering on Back", "back-lettering", backNodes);
    scroller.insertBefore(backSection, scroller.firstChild);
  }

  function refreshAddonInfoVisibility() {
    const vaseNote = document.getElementById("pamela-vase-info-note");
    const laserNote = document.getElementById("pamela-laser-info-note");
    const infoHdr = document.getElementById("pamela-addon-info-header");

    const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;
    const variantText = String(
      selectedVariant?.title ||
      selectedVariant?.public_title ||
      ""
    ).toLowerCase();

    const urlParams = new URL(window.location.href).searchParams;
    const productHandle = String(urlParams.get("product") || "").toLowerCase();

    const isJetBlack = variantText.includes("jet black");
    const hasVaseOption =
      productHandle.includes("with-vase") ||
      productHandle.includes("with-vases");

    const hasModernAddonPanel = !!document.getElementById("pamela-addon-panel");

    if (vaseNote) {
      vaseNote.style.display = hasModernAddonPanel ? "none" : (hasVaseOption ? "" : "none");
    }

    if (laserNote) {
      laserNote.style.display = hasModernAddonPanel ? "none" : (isJetBlack ? "" : "none");
    }

    if (infoHdr) {
      infoHdr.style.display = hasModernAddonPanel ? "none" : ((hasVaseOption || isJetBlack) ? "" : "none");
    }

    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
  }

  window.refreshAddonInfoVisibility = refreshAddonInfoVisibility;

  // Now append flyout to DOM (once)
  document.body.appendChild(fly);
  refreshAddonInfoVisibility();

  // Populate once the first time itÃ¢â‚¬â„¢s created
  setTimeout(() => {
    try { pamelaPopulatePorcelainDropdownNow(); } catch (_) {}
  }, 0);

  // -----------------------------
  // Populate porcelain dropdown from catalog (same data you already have)

  // Now append flyout to DOM (once)
  document.body.appendChild(fly);
  refreshAddonInfoVisibility();

// Populate once the first time itÃ¢â‚¬â„¢s created
setTimeout(() => { 
  try { pamelaPopulatePorcelainDropdownNow(); } catch (_) {} 
}, 0);
  // -----------------------------
  // Populate porcelain dropdown from catalog (same data you already have)
  // - Safe: runs even if flyout already existed
  // - Safe: shows a helpful option if catalog loader is missing/fails
  // -----------------------------
  async function pamelaPopulatePorcelainDropdownNow() {
    const porSelLive = document.getElementById("cm_porcelain_dropdown");
    if (!porSelLive) return;

   
    // Default/placeholder while loading (prevents "empty dropdown" confusion)
    porSelLive.innerHTML = "";
    const loadingOpt = document.createElement("option");
    loadingOpt.value = "";
    loadingOpt.textContent = "Loading portraits...";
    porSelLive.appendChild(loadingOpt);

    try {
      if (typeof loadAddonCatalogOnce !== "function") {
        throw new Error("loadAddonCatalogOnce() is not defined.");
      }

      const CFG = window.PAMELA_ADDON_CONFIG;
      const cat = await loadAddonCatalogOnce();
      const sym = (CFG && CFG.currencySymbol) ? CFG.currencySymbol : "$";

      const list = (cat && Array.isArray(cat.porcelainVariants)) ? cat.porcelainVariants : [];

      porSelLive.innerHTML = "";
      const o0 = document.createElement("option");
      o0.value = "";
      o0.textContent = "Select a portrait...";
      porSelLive.appendChild(o0);

      if (!list.length) {
        const oEmpty = document.createElement("option");
        oEmpty.value = "";
        oEmpty.textContent = "No portrait options found (catalog empty).";
        porSelLive.appendChild(oEmpty);
        return;
      }

      list.forEach(v => {
        const opt = document.createElement("option");
        opt.value = String(v.id);

        // moneyFromCents is yours; if missing, fall back to dollars
        let priceTxt = "";
        try {
          if (typeof moneyFromCents === "function") {
            priceTxt = moneyFromCents(v.price_cents, sym);
          } else {
            priceTxt = `${sym}${((Number(v.price_cents) || 0) / 100).toFixed(2)}`;
          }
        } catch (_) {
          priceTxt = `${sym}${((Number(v.price_cents) || 0) / 100).toFixed(2)}`;
        }

        opt.textContent = `${v.title} - ${priceTxt}`;
        porSelLive.appendChild(opt);
      });
    } catch (e) {
      console.warn("Porcelain dropdown fill failed:", e);

      porSelLive.innerHTML = "";
      const oErr = document.createElement("option");
      oErr.value = "";
      oErr.textContent = "Portrait options failed to load (see console).";
      porSelLive.appendChild(oErr);
    }
  }
      window.pamelaPopulatePorcelainDropdownNow = pamelaPopulatePorcelainDropdownNow;

  // -----------------------------
  // Porcelain "Add to order" click (wire later Ã¢â‚¬â€ for now just confirms selection)
  // -----------------------------
  addPorBtn.addEventListener("click", () => {
    const v = String(porSel.value || "");
    if (!v) {
      alert("Please select a portrait size first.");
      return;
    }
    
  });

  return fly;
}

function pamelaCloseAddonFlyoutOnMobile() {
  try {
    if (!pamelaIsMobileEditorViewport()) return;
    const fly = document.getElementById("pamela-addon-flyout");
    if (fly) fly.style.display = "none";
  } catch (_) {}
}
window.PAMELA_closeAddonFlyoutOnMobile = pamelaCloseAddonFlyoutOnMobile;
// =============================
// Status line helpers
// =============================
function ensureStatusLine(wrapEl) {
  let el = document.getElementById("pamela-editor-status");
  if (el) return el;

  el = document.createElement("div");
  el.id = "pamela-editor-status";
  el.style.cssText =
    "max-width:1200px;margin:0 auto 10px auto;" +
    "font:800 14px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#555;text-align:center;";
  (wrapEl.parentNode || document.body).insertBefore(el, wrapEl);
  return el;
}

function setStatus(wrapEl, msg) {
  ensureStatusLine(wrapEl).textContent = msg || "";
}

function setBtnEnabled(id, enabled) {
  const b = document.getElementById(id);
  if (!b) return;
  b.disabled = !enabled;
  b.style.opacity = enabled ? "1" : "0.35";
  b.style.cursor = enabled ? "pointer" : "not-allowed";
}

const PAMELA_START_GUIDANCE_TEXT = "For the easiest experience, start here and work left to right.";

function pamelaEnsureStartGuidanceStyles() {
  if (document.getElementById("pamela-start-guidance-styles")) return;
  const style = document.createElement("style");
  style.id = "pamela-start-guidance-styles";
  style.textContent = `
    @keyframes pamelaStartPulse {
      0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.34), 0 0 0 0 rgba(37, 99, 235, 0.18); }
      70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0), 0 0 0 18px rgba(37, 99, 235, 0); }
      100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0), 0 0 0 0 rgba(37, 99, 235, 0); }
    }
    #pamela-editor-toolbar .pamela-start-guidance-target {
      position: relative !important;
      z-index: 2 !important;
      border-color: #2563eb !important;
      background: #111827 !important;
      color: #fff !important;
      box-shadow: 0 0 0 3px rgba(191, 219, 254, 0.95), 0 12px 24px rgba(37, 99, 235, 0.24) !important;
      animation: pamelaStartPulse 1.65s ease-out infinite !important;
    }
    #pamela-start-guidance-tip {
      position: fixed;
      z-index: 2147483647;
      visibility: hidden;
      max-width: 310px;
      padding: 10px 12px;
      border-radius: 12px;
      background: #eef4ff;
      color: #1f2933;
      border: 1px solid rgba(37, 99, 235, 0.18);
      box-shadow: 0 14px 32px rgba(37, 99, 235, 0.16);
      font: 700 13px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;
    }
    #pamela-start-guidance-tip::after {
      content: "";
      position: absolute;
      left: var(--pamela-start-guidance-arrow-left, 26px);
      bottom: -8px;
      width: 14px;
      height: 14px;
      background: #eef4ff;
      border-right: 1px solid rgba(37, 99, 235, 0.18);
      border-bottom: 1px solid rgba(37, 99, 235, 0.18);
      transform: rotate(45deg);
    }
    #pamela-start-guidance-tip.pamela-start-guidance-below::after {
      top: -8px;
      bottom: auto;
      border-right: 0;
      border-bottom: 0;
      border-left: 1px solid rgba(37, 99, 235, 0.18);
      border-top: 1px solid rgba(37, 99, 235, 0.18);
    }
  `;
  document.head.appendChild(style);
}

function pamelaGetStartGuidanceButton(editor) {
  if (!editor) return null;
  const buttonId = editor.layout === "single" ? "pamela-add-name" : "pamela-add-lastname";
  return document.getElementById(buttonId);
}

function pamelaClearStartGuidance() {
  document
    .querySelectorAll("#pamela-editor-toolbar .pamela-start-guidance-target")
    .forEach((el) => el.classList.remove("pamela-start-guidance-target"));
}

function pamelaUpdateStartGuidance(editor) {
  pamelaClearStartGuidance();

  if (!editor) return;
  const isBack = !!(window.PAMELA_SIDES && window.PAMELA_SIDES.current === "back");
  if (isBack) return;

  const target = pamelaGetStartGuidanceButton(editor);
  if (!target) return;
  if (target.disabled) return;
  if (target.style.display === "none") return;

  target.classList.add("pamela-start-guidance-target");
}

function pamelaShowStartGuidance(editor) {
  if (!editor || window.__pamelaStartGuidanceShown) return;

  pamelaEnsureStartGuidanceStyles();
  pamelaUpdateStartGuidance(editor);

  const target = pamelaGetStartGuidanceButton(editor);
  if (!target || target.disabled || target.style.display === "none") return;

  window.__pamelaStartGuidanceShown = true;

  let tip = document.getElementById("pamela-start-guidance-tip");
  if (!tip) {
    tip = document.createElement("div");
    tip.id = "pamela-start-guidance-tip";
    tip.textContent = PAMELA_START_GUIDANCE_TEXT;
    document.body.appendChild(tip);
  } else {
    tip.textContent = PAMELA_START_GUIDANCE_TEXT;
  }

  const positionTip = () => {
    if (!tip || !target || !document.body.contains(tip) || !document.body.contains(target)) return;
    const rect = target.getBoundingClientRect();
    const margin = 12;
    const tipRect = tip.getBoundingClientRect();
    let left = rect.left + rect.width / 2 - tipRect.width / 2;
    left = Math.max(12, Math.min(window.innerWidth - tipRect.width - 12, left));
    const fitsAbove = rect.top - tipRect.height - margin >= 12;
    let top = fitsAbove ? rect.top - tipRect.height - margin : rect.bottom + margin;
    const targetCenterX = rect.left + rect.width / 2;
    const arrowLeft = Math.max(12, Math.min(tipRect.width - 26, targetCenterX - left - 7));

    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
    tip.style.setProperty("--pamela-start-guidance-arrow-left", `${Math.round(arrowLeft)}px`);
    tip.classList.toggle("pamela-start-guidance-below", !fitsAbove);
    tip.style.visibility = "visible";
  };

  const dismiss = () => {
    if (tip && tip.parentNode) tip.parentNode.removeChild(tip);
    window.removeEventListener("resize", positionTip, true);
    window.removeEventListener("scroll", positionTip, true);
    document.removeEventListener("click", dismissOnToolbarClick, true);
    pamelaClearStartGuidance();
  };

  window.addEventListener("resize", positionTip, true);
  window.addEventListener("scroll", positionTip, true);
  positionTip();
  window.requestAnimationFrame(positionTip);
  window.setTimeout(positionTip, 250);
  // Keep this first-use cue available until the customer starts working in the
  // toolbar.  A timer made it disappear before many customers had noticed it.
  const dismissOnToolbarClick = (event) => {
    if (event.target?.closest?.("#pamela-editor-toolbar button")) dismiss();
  };
  document.addEventListener("click", dismissOnToolbarClick, true);
}

// =============================
// Fullscreen helpers
// =============================

  function ensureFullscreenExitOverlay() {
    if (!document.getElementById("pamela-dark-granite-fullscreen-style")) {
      const style = document.createElement("style");
      style.id = "pamela-dark-granite-fullscreen-style";
      style.textContent = `
        .pamela-dark-granite:fullscreen,
        .pamela-dark-granite:-webkit-full-screen {
          background: #f3f4f6 !important;
        }
      `;
      document.head.appendChild(style);
    }

    let btn = document.getElementById("pamela-fs-exit");
    if (btn) return btn;

    btn = document.createElement("button");
    btn.id = "pamela-fs-exit";
    btn.type = "button";
    btn.setAttribute("aria-label", "Exit full screen");
  btn.textContent = "x";
    btn.style.cssText =
      "position:fixed;top:10px;right:10px;z-index:2147483647;" +
      "width:44px;height:44px;border-radius:999px;border:1px solid rgba(255,255,255,.35);" +
      "background:rgba(0,0,0,.55);color:#fff;font:900 20px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "display:none;align-items:center;justify-content:center;cursor:pointer;" +
      "backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);";
    document.body.appendChild(btn);

    btn.addEventListener("click", () => {
      try {
        if (document.fullscreenElement) document.exitFullscreen();
      } catch (_) {}
    });

    const sync = () => {
      const on = !!document.fullscreenElement;
      btn.style.display = on && window.innerWidth <= 768 ? "flex" : "none";
    };
    document.addEventListener("fullscreenchange", sync);
    window.addEventListener("resize", sync);
    sync();

    return btn;
  }

  async function toggleFullscreenFor(el) {
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
    } catch (_) {}
  }
// =============================
// Mutual Exclusivity: Clipart vs Panels/Frames
// =============================
const CLIPART_UI_IDS = [
  "pamela-clipart-dock",
  "pamela-clipart-modal",
  "pamela-library-modal",
  "pamela-clipart-drawer",

  // Ã¢Å“â€¦ REAL current Clipart UI (container ONLY)
  "pamela-clipart-panel",
];

const PANELS_UI_IDS = [
  // Ã¢Å“â€¦ REAL current Panels UI (confirmed by your console)
  "pamela-panels-dock",
];

function hideElById(id) {
  const el = document.getElementById(id);
  if (el) el.style.display = "none";
}

function closeClipartUI() {
  CLIPART_UI_IDS.forEach(hideElById);
  if (window.PAMELA_CLIPART_UI && typeof window.PAMELA_CLIPART_UI.close === "function") {
    try { window.PAMELA_CLIPART_UI.close(); } catch (_) {}
  }
}

function closePanelsUI() {
  PANELS_UI_IDS.forEach(hideElById);
  if (window.PAMELA_PANELS_UI && typeof window.PAMELA_PANELS_UI.close === "function") {
    try { window.PAMELA_PANELS_UI.close(); } catch (_) {}
  }
}

// Expose for your other scripts
window.PAMELA_CLOSE_CLIPART_UI = closeClipartUI;
window.PAMELA_CLOSE_PANELS_UI = closePanelsUI;

// Default behavior: dispatch event so another script can toggle itself
window.PAMELA_TOGGLE_PANELS_UI = () => {
  try { window.dispatchEvent(new CustomEvent("pamela:togglePanels")); } catch (_) {}
};

// Ã¢Å“â€¦ Close Panels whenever the Clipart button (or anything inside it) is clicked
(function wireClipartClosesPanelsDelegated() {
  if (window.__pamelaClipartClosePanelsDelegatedWired) return;
  window.__pamelaClipartClosePanelsDelegatedWired = true;

  function closestId(el, id) {
    while (el) {
      if (el.id === id) return el;
      el = el.parentNode;
    }
    return null;
  }

  document.addEventListener(
    "click",
    function (e) {
      var t = e && e.target ? e.target : null;
      if (!t) return;

      // If click is on the Clipart button or any child inside it
      if (closestId(t, "pamela-view-clipart")) {
        try { closePanelsUI(); } catch (_) {}
      }
    },
    true // capture phase
  );
})();


  // =============================
  // Inline edit: type on stone (INPUT-based)
  // =============================
  function pamelaStartInlineEdit(editor, textNode) {
    if (!editor || !textNode || !pamelaIsTextLikeNode(textNode)) return;
    if (editor._inlineEditActive) return;

    editor._inlineEditActive = true;
    editor.selectNode(textNode);

    const spec = FIELD_SPECS[textNode.attrs?.pamelaFieldType] || {};
    if (typeof spec.lineHeight === "number" && typeof textNode.lineHeight === "function") {
      textNode.lineHeight(spec.lineHeight);
    }
    const stage = editor.stage;
    const container = stage.container();
    const parent = container;
    parent.style.position = parent.style.position || "relative";

    if (textNode.className === "Text") pamelaFixTextMetrics(textNode);

    const prevDraggable = textNode.draggable();
    textNode.draggable(false);

    // Ã¢Å“â€¦ Multiline only where the field rules explicitly allow it.
    const ft = String(textNode.attrs?.pamelaFieldType || "");
    const nm = (typeof textNode.name === "function") ? String(textNode.name() || "") : String(textNode.attrs?.name || "");
    const id = (typeof textNode.id === "function") ? String(textNode.id() || "") : String(textNode.attrs?.id || "");
    const isBackVerse = (nm === "__cm_back_verse" || id === "__cm_back_verse");
    const isSingleDate = ft === "date" && pamelaIsSingleLayout(editor);
    const allowMultiline = isSingleDate || isBackVerse || Number(spec.lineLimit) > 1;

    // Ã¢Å“â€¦ Use INPUT for normal text (keeps your "white box" placement perfect)
    // Ã¢Å“â€¦ Use TEXTAREA for back verse (supports Enter)
    const input = allowMultiline ? document.createElement("textarea") : document.createElement("input");
    if (!allowMultiline) input.type = "text";

    input.value = String(textNode.text?.() || "");

    input.setAttribute("spellcheck", "false");
    input.setAttribute("autocorrect", "off");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("autocomplete", "off");

    // Ã¢Å“â€¦ Keep the SAME visual behavior you had:
    // invisible text, visible caret, no big white box styling changes
    input.style.position = "absolute";
    input.style.zIndex = "2147483647";
    input.style.background = "transparent";
    input.style.border = "none";
    input.style.outline = "none";
    input.style.padding = "0";
    input.style.margin = "0";
    input.style.color = "transparent";
    input.style.caretColor = "rgba(0, 120, 255, 0.95)";

    // Ã¢Å“â€¦ MATCH the Konva Text node (this fixes caret/line-break mismatch)
    input.style.fontFamily = textNode.fontFamily() || "MDRoman";
    input.style.textAlign = (typeof textNode.align === "function" ? (textNode.align() || "center") : "center");
    input.style.letterSpacing = `${(typeof textNode.letterSpacing === "function" ? (Number(textNode.letterSpacing()) || 0) : 0)}px`;

    const _fs = (typeof textNode.fontStyle === "function" ? (textNode.fontStyle() || "normal") : "normal");
    input.style.fontStyle = _fs.includes("italic") ? "italic" : "normal";
    input.style.fontWeight = _fs.includes("bold") ? "700" : "400";

    // IMPORTANT: lineHeight must match Konva (your "1" was the cursor-offset culprit)
    input.style.lineHeight = String(typeof textNode.lineHeight === "function" ? (textNode.lineHeight() || 1) : 1);

    if (allowMultiline) {
      input.style.resize = "none";
      input.style.overflow = "hidden";
      input.wrap = "off"; // Ã¢Å“â€¦ do NOT hard-wrap; customer controls lines with Enter
    }

    // stop stage interactions under the editor
    const stop = (e) => {
      try { e.stopPropagation(); } catch (_) {}
    };
    input.addEventListener("pointerdown", stop, true);
    input.addEventListener("mousedown", stop, true);
    input.addEventListener("touchstart", stop, true);

    const rectInWorld = () =>
      textNode.getClientRect({
        relativeTo: editor.layers.content,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

    const placeInput = () => {
      const r = rectInWorld();
      const scale = editor.state.view.scale;
      const viewX = editor.state.view.x;
      const viewY = editor.state.view.y;

      const left = viewX + r.x * scale;
      const top  = viewY + r.y * scale;

      // Base DOM box size from Konva rect
      let w = Math.max(12, r.width * scale);
      let h = Math.max(12, r.height * scale);

      // Ã¢Å“â€¦ MATCH Konva font metrics EXACTLY (this fixes caret/line-break mismatch)
      // Konva fontStyle can be: "normal", "italic", "bold", "bold italic"
      const kStyle = (typeof textNode.fontStyle === "function" ? String(textNode.fontStyle() || "normal") : "normal").toLowerCase();
      const isItalic = kStyle.includes("italic");
      const isBold = kStyle.includes("bold");

      try { input.style.fontFamily = textNode.fontFamily() || "MDRoman"; } catch (_) {}
      input.style.fontStyle = isItalic ? "italic" : "normal";
      input.style.fontWeight = isBold ? "700" : "400";
      input.style.fontVariant = "normal";
      input.style.fontStretch = "normal";
      input.style.textAlign = (typeof textNode.align === "function" ? textNode.align() : "center");

      try {
        const lh = (typeof textNode.lineHeight === "function" ? Number(textNode.lineHeight()) : 1) || 1;
        input.style.lineHeight = String(lh);
      } catch (_) {
        input.style.lineHeight = "1";
      }

      // Ã¢Å“â€¦ Very important for multiline caret alignment (preserve spaces/newlines exactly)
      input.style.whiteSpace = "pre";
      input.style.boxSizing = "content-box";

      // Ã¢Å“â€¦ Add a small right-side "caret pad" so the last letters/caret never get clipped.
      // (Especially noticeable with italic fonts.)
      const caretPad = Math.max(10, Math.round(14 * scale));
      w = w + caretPad;

      // Ã¢Å“â€¦ Placement rules:
      // - Single-line: keep your old optical shift (nice centered box)
      // - Multi-line: small shift only (keeps verse centered but caret accurate)
      const shiftUp = allowMultiline ? Math.max(0, h * 0.10) : Math.max(6, h * 0.40);
      const extraH  = allowMultiline ? Math.max(6, h * 0.16) : Math.max(2, h * 0.30);

      input.style.left = `${left}px`;
      input.style.top = `${top - shiftUp}px`;
      input.style.width = `${w}px`;
      input.style.height = `${h + extraH}px`;
      input.style.fontSize = `${Math.max(10, textNode.fontSize() * scale)}px`;
    };

    // Ã¢Å“â€¦ No char-limit for back verse (charged per word)
    const normalizeValue = (raw) => {
      let v = String(raw ?? "");
      if (isSingleDate) {
        v = pamelaNormalizeDateText(v, { allowStacked: true });
      } else if (allowMultiline) {
        v = v.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

        if (Number(spec.lineLimit) > 0) {
          v = v.split("\n").slice(0, Number(spec.lineLimit)).join("\n");
        }

        if (typeof spec.wordLimit === "number") {
          v = pamelaLimitWords(v, spec.wordLimit, { trimTrailing: false });
        }
      } else {
        v = v.replace(/[\r\n]+/g, " ");
        if (typeof spec.charLimit === "number") v = v.slice(0, spec.charLimit);
        if (typeof spec.wordLimit === "number") v = pamelaLimitWords(v, spec.wordLimit, { trimTrailing: false });
      }
      return v;
    };

    const applyLive = () => {
      if (!editor._inlineEditActive) return;

      const raw = input.value ?? "";
      const val = normalizeValue(raw);

      if (val !== raw) {
        const caret = input.selectionStart ?? val.length;
        input.value = val;
        try {
          input.selectionStart = input.selectionEnd = Math.min(caret, val.length);
        } catch (_) {}
      }

      textNode.text(val);
      if (ft === "date") {
        textNode.setAttr("pamelaDateFormat", val.includes("\n") ? "stacked" : "single");
        pamelaApplyDateLineSpacing(textNode);
      }
      if (textNode.className === "Text") {
        pamelaFixTextMetrics(textNode);
        pamelaSnapTextToCenterX(editor, textNode);
      }

      // Ã¢Å“â€¦ Keep typed text inside safe area too
      pamelaClampNodeIntoSafeRect(editor, textNode);

      editor.layers.content.batchDraw();

      // Ã¢Å“â€¦ Keep transformer synced while typing so the blue box grows
      try { editor.nodes?.tr?.forceUpdate?.(); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}

      placeInput();
    };

    // lock zoom/pan while typing
    const stopWheel = (e) => {
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
    };
    const stopTouch = (e) => {
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
    };
    window.addEventListener("wheel", stopWheel, { passive: false, capture: true });
    window.addEventListener("touchmove", stopTouch, { passive: false, capture: true });

    const unlockViewport = () => {
      window.removeEventListener("wheel", stopWheel, { capture: true });
      window.removeEventListener("touchmove", stopTouch, { capture: true });
    };

    const finish = (reason = "commit") => {
      if (!editor._inlineEditActive) return;

      applyLive();
      if (isSingleDate) {
        const dateFormat = pamelaClassifyDateText(input.value, { allowStacked: true });
        if (!dateFormat) {
          if (reason === "blur") {
            try { input.focus({ preventScroll: true }); } catch (_) {}
            return;
          }

          window.alert("Please enter the date in one of the shown formats.");
          setTimeout(() => {
            try { input.focus({ preventScroll: true }); } catch (_) {}
          }, 0);
          return;
        }
        textNode.setAttr("pamelaDateFormat", dateFormat === "stacked" ? "stacked" : "single");
        pamelaApplyDateLineSpacing(textNode);
      }

      pamelaEnforceSpacingForText(editor);

      try { parent.removeChild(input); } catch (_) {}
      textNode.draggable(prevDraggable);

      editor._inlineEditActive = false;
      unlockViewport();

      editor.selectNode(textNode);
      editor.refreshSizeButtons();
      try { pamelaRefreshCurveTextMenuState(editor); } catch (_) {}
    };

    parent.appendChild(input);
    placeInput();

    input.focus({ preventScroll: true });
    if (input.select) input.select();

    input.addEventListener("input", applyLive);

    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish("commit");
        return;
      }

      if (e.key === "Enter" && allowMultiline && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        finish("commit");
        return;
      }

      if (e.key === "Enter" && allowMultiline && Number(spec.lineLimit) > 0) {
        const currentLines = String(input.value || "").split(/\r\n|\r|\n/).length;
        const selected = (input.selectionEnd ?? 0) > (input.selectionStart ?? 0);

        if (currentLines >= Number(spec.lineLimit) && !selected) {
          e.preventDefault();
          return;
        }
      }

      // Ã¢Å“â€¦ Enter behavior:
      // - Multiline fields: Enter makes a NEW LINE (Ctrl/Cmd+Enter confirms)
      // - Everything else: Enter finishes
      if (e.key === "Enter" && !allowMultiline) {
        e.preventDefault();
        finish("commit");
        return;
      }
    });

    input.addEventListener("blur", () => finish("blur"));
  }

  class PamelaMonumentEditorV2 {

  // -----------------------------
  // Z-ORDER (Panels behind Clipart behind Text)
  // -----------------------------
  _normalizeZOrder() {
    const layer = this.layers?.content;
    if (!layer) return;

    const panels = layer.getChildren(
      (n) => n && n.className === "Image" && n.attrs?.pamelaPanel === true
    );

    const clips = layer.getChildren(
      (n) => n && n.className === "Image" && n.attrs?.pamelaClipart === true
    );

    const texts = layer.getChildren((n) => n && pamelaIsTextLikeNode(n));

    // Bottom -> top: Panels, Clipart, Text
    panels.forEach((n) => n.moveToBottom());
    clips.forEach((n) => n.moveToTop());
    texts.forEach((n) => n.moveToTop());

    layer.batchDraw();
    this.layers.ui?.batchDraw();
  }

    constructor({ wrapEl, layout }) {
      this.fontState = {
        lastname: "MDRoman",
        namedate: "MDRoman",
        wedding: "MDAntique",
        verse: "MDAntique",
      };

      this.wrapEl = wrapEl;
      this.layout = layout;

      this.stage = null;

      // Ã¢Å“â€¦ Added overlay layer
      this.layers = { background: null, panels: null, grid: null, content: null, portraits: null, border: null, ui: null };


      // Ã¢Å“â€¦ Added overlay node reference
      this.nodes = { bg: null, borderOverlay: null, tr: null, gridGroup: null, centerGuideLine: null, verseBaselineGuideLine: null, clipartCenterGuideLine: null };


      this.state = {
        design: { width: DESIGN_PX.w, height: DESIGN_PX.h, aspect: DESIGN_PX.w / DESIGN_PX.h },
        stage: { width: 0, height: 0 },
        view: { scale: 1, x: 0, y: 0 },
        fit: { scale: 1, x: 0, y: 0 },
      };

      this.selected = null;

      this._isPanning = false;
      this._panStart = { x: 0, y: 0 };
      this._viewStart = { x: 0, y: 0 };
      this._userMovedView = false;

      this._inlineEditActive = false;

      this._onResize = rafThrottle(() => {
        if (document.fullscreenElement) return;
        this._resize(false);
      });

      this.currentTextFill = "#111";
      this.gridOn = false;

      this.panMode = false;
     
      // NEW: per-pair overrides (name/date pairs only)
      this.pairLinkOverrides = Object.create(null);
      this.horizontalMirrorLinked = true;
      this.horizontalMirrorIndependentMode = false;
      this.horizontalMirrorShiftKeyDown = false;
      this.groupedTextSide = null;

      // Make sure we can force-release panning even if pointerup happens off-canvas
      this._wireHorizontalMirrorShiftKeyOnce();
      this._wireGlobalPanReleaseOnce();
    }

// =============================
// Undo / Redo (move/resize/rotate/font only)
// =============================

// Stable UID generator for nodes (no searching, no collisions)
_makePamelaUid(prefix = "n") {
  const r = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}_${r}`;
}

_ensurePamelaUid(node, prefix = "n") {
  if (!node) return "";
  let uid = node.getAttr && node.getAttr("pamelaUid");
  if (!uid) {
    uid = this._makePamelaUid(prefix);
    try { node.setAttr("pamelaUid", uid); } catch (_) {}
  }
  return uid;
}

_historyReset() {
  this._undo = [];
  this._redo = [];
  this._historyLock = false;
  this._lastHistoryHash = "";
}

// Collect ONLY what we agreed to track:
// Ã¢Å“â€ move Ã¢Å“â€ resize Ã¢Å“â€ rotate Ã¢Å“â€ font change
// (NOT add/delete/variant/layout/text content)
_historySnapshot() {
  const snap = { v: 1, items: [] };

  const pushItem = (node, kind) => {
    if (!node) return;
    const uid = this._ensurePamelaUid(
      node,
      kind === "panel" ? "p" : kind === "clipart" ? "c" : "t"
    );
    if (!uid) return;

    const base = {
      uid,
      kind, // "text" | "clipart" | "panel"
      x: Number(node.x?.() ?? 0),
      y: Number(node.y?.() ?? 0),
      rot: Number(node.rotation?.() ?? 0),
      sx: Number(node.scaleX?.() ?? 1),
      sy: Number(node.scaleY?.() ?? 1),
      ox: Number(node.offsetX?.() ?? 0),
      oy: Number(node.offsetY?.() ?? 0),
    };

    if (kind === "text") {
      // Track font changes + font size, but NOT the actual text string.
      base.ff = String(node.fontFamily?.() ?? "");
      base.fs = Number(node.fontSize?.() ?? 0);
      base.lr = node.getAttr?.("pamelaLaserTextReversed") === true;

      // helpful metadata (not required, but safe)
      base.ft = String(node.getAttr?.("pamelaFieldType") ?? "");
      const si = Number(node.getAttr?.("pamelaSlotIndex"));
      base.si = Number.isFinite(si) ? si : null;
    } else {
      // Images
      base.w = Number(node.width?.() ?? 0);
      base.h = Number(node.height?.() ?? 0);

      if (kind === "panel") {
        base.pu = String(node.getAttr?.("pamelaPanelUrl") ?? "");
      } else if (kind === "clipart") {
        base.cu = String(node.getAttr?.("pamelaClipartUrl") ?? "");
      } else if (kind === "laser") {
        base.lu = String(node.getAttr?.("pamelaLaserUrl") ?? "");
        // Laser artwork is a baked raster (crop/feather/fit-to-stone all bake
        // into node.image()), unlike panel/clipart which re-render from an
        // SVG URL at any size. Position/size alone can't restore it, so the
        // canvas + related fit/crop state are cloned into the snapshot too.
        //
        // _historyCommit() runs on every editor action anywhere, not just
        // ones touching this node, so re-cloning up to 3 full canvases per
        // laser node on every single commit (typing, dragging a panel,
        // anything) was a real, recurring cost as soon as any laser artwork
        // existed. node.image()/_pamelaLaserPreFitImage/_pamelaLaserCropRootImage
        // only get reassigned to a NEW canvas when the artwork is actually
        // re-baked (crop/feather/fit/replace), so cache clones keyed by
        // reference identity and only re-clone when the source actually
        // changed.
        try {
          const img = node.image?.() || null;
          const preFit = node._pamelaLaserPreFitImage || null;
          const cropRoot = node._pamelaLaserCropRootImage || null;

          if (!node.__pamelaLaserSnapshotCache) node.__pamelaLaserSnapshotCache = {};
          const snapCache = node.__pamelaLaserSnapshotCache;

          if (snapCache.imgSrc !== img) {
            snapCache.imgSrc = img;
            snapCache.imgClone = cloneLaserCanvasSource(img) || null;
          }
          if (snapCache.preFitSrc !== preFit) {
            snapCache.preFitSrc = preFit;
            snapCache.preFitClone = cloneLaserCanvasSource(preFit) || null;
          }
          if (snapCache.cropRootSrc !== cropRoot) {
            snapCache.cropRootSrc = cropRoot;
            snapCache.cropRootClone = cloneLaserCanvasSource(cropRoot) || null;
          }

          base.img = snapCache.imgClone;
          base.preFitImg = snapCache.preFitClone;
          base.cropRootImg = snapCache.cropRootClone;
          base.cropRootW = node._pamelaLaserCropRootWidth;
          base.cropRootH = node._pamelaLaserCropRootHeight;
          base.fitToStone = node.getAttr?.("pamelaLaserFitToStone") === true;
          base.fitCrop = node.getAttr?.("pamelaLaserFitCrop") || null;
          base.fitBottomRatio = node.getAttr?.("pamelaLaserFitBottomRatio");
          base.crop = node.getAttr?.("pamelaLaserCrop") || null;
          base.featherPx = node.getAttr?.("pamelaLaserFeatherPx");
        } catch (_) {}
      }
    }

    snap.items.push(base);
  };

  // Panels layer
  const panLayer = this.layers?.panels;
  if (panLayer) {
    panLayer.getChildren((n) => n && n.className === "Image" && n.attrs?.pamelaPanel === true)
      .forEach((n) => pushItem(n, "panel"));
  }

  // Content layer (clipart + text)
  const content = this.layers?.content;
  if (content) {
    content.getChildren((n) => n && n.className === "Image" && n.attrs?.pamelaClipart === true)
      .forEach((n) => pushItem(n, "clipart"));

    content.getChildren((n) => n && n.className === "Image" && n.attrs?.pamelaLaserEtching === true)
      .forEach((n) => pushItem(n, "laser"));

    content.getChildren((n) => n && pamelaIsTextLikeNode(n))
      .forEach((n) => pushItem(n, "text"));
  }

  // Sort for stable hashing
  snap.items.sort((a, b) => (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
  return snap;
}

_historyHash(snap) {
  // Small stacks (<=3), so stringify is safe + simple.
  try { return JSON.stringify(snap); } catch (_) { return ""; }
}

// Commit a snapshot AFTER a tracked action finishes.
// Standard behavior: redo clears on new action.
_historyCommit(reason = "") {
  if (this._historyLock) return;

  // Ã¢Å“â€¦ Prevent any Ã¢â‚¬Å“side-effect commitsÃ¢â‚¬Â right after undo/redo
  const now = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
  if (this._suppressHistoryCommitsUntil && now < this._suppressHistoryCommitsUntil) return;


  if (!this._undo) this._undo = [];
  if (!this._redo) this._redo = [];
  if (typeof this._lastHistoryHash !== "string") this._lastHistoryHash = "";

  const snap = this._historySnapshot();
  const h = this._historyHash(snap);

  // Avoid duplicate commits (common if multiple events fire)
  if (h && h === this._lastHistoryHash) return;

  this._undo.push(snap);

  // Limit = 3 states (keep last 3)
  while (this._undo.length > 30) this._undo.shift();

  // Redo clears on new action
  this._redo.length = 0;

  this._lastHistoryHash = h;

  // Optional: update buttons if you add them
  this._refreshUndoRedoUI?.();
}

_applyHistorySnapshot(snap) {
  if (!snap || !Array.isArray(snap.items)) return;

  const findByUid = (layer, uid) => {
    if (!layer || !uid) return null;
    const kids = layer.getChildren?.() || [];
    for (let i = 0; i < kids.length; i++) {
      const n = kids[i];
      if (!n || !n.getAttr) continue;
      if (String(n.getAttr("pamelaUid") || "") === uid) return n;
    }
    return null;
  };

  this._historyLock = true;
  try {
    // Remove any panel/clipart/laser node that isn't part of the snapshot
    // being restored (covers undo-of-add and redo-of-delete). Text and
    // portraits are untouched here -- portraits aren't tracked by this
    // snapshot at all, and text nodes are never pruned by this helper.
    try { this._pruneExtraPamelaNodesFromSnapshot(snap); } catch (_) {}

    const panLayer = this.layers?.panels;
    const content = this.layers?.content;

    snap.items.forEach((it) => {
      const layer = it.kind === "panel" ? panLayer : content;
      const node = findByUid(layer, it.uid);
      if (!node) return;

      // position + rotation + scale + offset
      try { node.x(it.x); node.y(it.y); } catch (_) {}
      try { node.rotation(it.rot); } catch (_) {}
      try { node.scaleX(it.sx); node.scaleY(it.sy); } catch (_) {}
      try { node.offsetX(it.ox); node.offsetY(it.oy); } catch (_) {}

      if (it.kind === "text") {
        // Only font family/size are tracked here
        try { if (it.ff) node.fontFamily(it.ff); } catch (_) {}
        try { if (Number.isFinite(it.fs) && it.fs > 0) node.fontSize(it.fs); } catch (_) {}
        try { node.setAttr?.("pamelaLaserTextReversed", it.lr === true); } catch (_) {}
        if (node.className === "Text") {
          try { pamelaFixTextMetrics(node); } catch (_) {}
        }
        try { pamelaClampNodeIntoSafeRect(this, node); } catch (_) {}
      } else {
        // Images: panels use width/height (because you bake), clipart uses scale
        try {
          if (Number.isFinite(it.w) && it.w > 0) node.width(it.w);
          if (Number.isFinite(it.h) && it.h > 0) node.height(it.h);
        } catch (_) {}
        if (it.kind === "laser" && it.img) {
          // Restore the actual baked canvas, not just the node's box -- a
          // laser node's pixels (crop/feather/fit-to-stone) live entirely in
          // node.image(), so resizing alone would just stretch the wrong
          // picture into the right-sized box.
          try { node.image(it.img); } catch (_) {}
          try { node._pamelaLaserPreFitImage = it.preFitImg || null; } catch (_) {}
          try { node._pamelaLaserBaseImage = it.img; } catch (_) {}
          try { node._pamelaLaserCropRootImage = it.cropRootImg || it.img; } catch (_) {}
          try { node._pamelaLaserCropRootWidth = it.cropRootW; } catch (_) {}
          try { node._pamelaLaserCropRootHeight = it.cropRootH; } catch (_) {}
          try { node.setAttr("pamelaLaserFitToStone", it.fitToStone === true); } catch (_) {}
          try { node.setAttr("pamelaLaserFitCrop", it.fitCrop || null); } catch (_) {}
          try { node.setAttr("pamelaLaserFitBottomRatio", it.fitBottomRatio); } catch (_) {}
          try { node.setAttr("pamelaLaserCrop", it.crop || null); } catch (_) {}
          try { node.setAttr("pamelaLaserFeatherPx", it.featherPx); } catch (_) {}
          try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
        }
        try { pamelaClampNodeIntoSafeRect(this, node); } catch (_) {}
      }
    });

    // Re-run your normal visual rules after any history jump
    try { this._pamelaRestackContentLayer?.(); } catch (_) {}
    try { pamelaEnforceSpacingForText?.(this); } catch (_) {}
    try { this._scheduleOverlapColors?.(); } catch (_) {}
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}

    // Keep transformer consistent if something is selected
    try {
      if (this.selected) {
        this.selectNode(this.selected);
      } else {
        this.nodes?.tr?.nodes?.([]);
        this.nodes?.trPanel?.nodes?.([]);
      }
    } catch (_) {}

    // Draw
    try { this.layers?.panels?.batchDraw?.(); } catch (_) {}
    try { this.layers?.content?.batchDraw?.(); } catch (_) {}
    try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
  } finally {
    this._historyLock = false;
  }
}
_pruneExtraPamelaNodesFromSnapshot(state) {
  if (!state) return;

  const keep = new Set();

  const addUid = (o) => {
    // Ã¢Å“â€¦ Support ALL shapes:
    // - our history snapshot items: { uid: "t_xxx" }
    // - node-like: { attrs: { pamelaUid } }
    // - other: { pamelaUid } or { id }
    const uid =
      o?.uid ??
      o?.attrs?.pamelaUid ??
      o?.pamelaUid ??
      o?.id;

    if (uid) keep.add(String(uid));
  };

  // Ã¢Å“â€¦ Our snapshot shape
  if (Array.isArray(state.items)) state.items.forEach(addUid);

  // Older/alternate shapes (keep support)
  if (Array.isArray(state.nodes)) state.nodes.forEach(addUid);
  if (Array.isArray(state.content)) state.content.forEach(addUid);
  if (Array.isArray(state.panels)) state.panels.forEach(addUid);
  if (Array.isArray(state.laser)) state.laser.forEach(addUid);

  // Stage JSON variant
  if (typeof state.stageJson === "string" && state.stageJson.trim()) {
    try {
      const j = JSON.parse(state.stageJson);
      const walk = (x) => {
        if (!x) return;
        addUid(x);
        if (Array.isArray(x.children)) x.children.forEach(walk);
      };
      walk(j);
    } catch (_) {}
  }

  const pruneLayer = (layer) => {
    if (!layer?.getChildren) return;

    layer.getChildren().forEach((node) => {
      const cls = node?.getClassName?.() || node?.className || "";

      const isPamelaImage =
        cls === "Image" && (
          node.attrs?.pamelaClipart === true ||
          node.attrs?.pamelaPanel === true ||
          node.attrs?.pamelaLaserEtching === true
        );

      const isPamelaText =
        cls === "Text" && (node.attrs?.pamelaFieldType || node.attrs?.pamelaIsCustomText);

      if (!isPamelaImage && !isPamelaText) return;

      const uid = node.attrs?.pamelaUid;
      if (!uid) return;

      // Ã¢Å“â€¦ If snapshot doesn't include it, remove it (undo add / redo delete support)
      if (!keep.has(String(uid))) {
        try { node.destroy(); } catch (_) {}
      }
    });
  };

  pruneLayer(this.layers?.content);
  pruneLayer(this.layers?.panels);

  try { this.layers?.content?.batchDraw?.(); } catch (_) {}
  try { this.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
}


      // Undo: apply previous snapshot
      undo() {
        if (this._historyLock) return;
        if (!this._undo || this._undo.length < 2) return; // need at least: current + previous

        const current = this._undo.pop();      // remove current
        this._redo = this._redo || [];
        this._redo.push(current);

        const prev = this._undo[this._undo.length - 1];
        this._lastHistoryHash = this._historyHash(prev);
        // Ã¢Å“â€¦ Suppress any accidental commits triggered by selection/transform handlers
        this._suppressHistoryCommitsUntil =
         ((typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now()) + 250;

        this._applyHistorySnapshot(prev);

        this._refreshUndoRedoUI?.();
      }

      // Redo: re-apply the snapshot we just undid
      redo() {
        if (this._historyLock) return;
        if (!this._redo || !this._redo.length) return;

        const next = this._redo.pop();
        this._undo = this._undo || [];
        this._undo.push(next);

        while (this._undo.length > 30) this._undo.shift();


        this._lastHistoryHash = this._historyHash(next);
        // Ã¢Å“â€¦ Suppress any accidental commits triggered by selection/transform handlers
        this._suppressHistoryCommitsUntil =
          ((typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now()) + 250;

        this._applyHistorySnapshot(next);

        this._refreshUndoRedoUI?.();
      }

      // Optional helper: call this once after the editor is ready,
      // so undo has a "starting point".
      _historySeedInitialState() {
        if (!this._undo) this._undo = [];
        const snap = this._historySnapshot();
        this._undo.length = 0;
        this._undo.push(snap);
        this._redo = [];
        this._lastHistoryHash = this._historyHash(snap);
        this._refreshUndoRedoUI?.();
      }


    _wireGlobalPanReleaseOnce() {
      if (this.__pamelaPanReleaseWired) return;
      this.__pamelaPanReleaseWired = true;

      const endPan = () => {
        this._isPanning = false;
      };

      window.addEventListener("pointerup", endPan, true);
      window.addEventListener("pointercancel", endPan, true);
      window.addEventListener("mouseup", endPan, true);
      window.addEventListener("touchend", endPan, true);
      window.addEventListener("touchcancel", endPan, true);
      window.addEventListener("blur", endPan, true);
    }

      async init() {
      await waitForKonva();
      await pamelaRegisterAllFontFaces();
      await pamelaPreloadFontsOnce();

      ensureTopBar(this.wrapEl);
      ensureToolbar(this.wrapEl);
      ensureStatusLine(this.wrapEl);
      ensureFullscreenExitOverlay();

      pamelaRefreshAllTextMetrics(this);
      pamelaEnsureNodeFontReady(this, this.selected, 250);
      setTimeout(() => pamelaRefreshAllTextMetrics(this), 300);
      setTimeout(() => pamelaRefreshAllTextMetrics(this), 1200);

      if (!window.__pamelaFontReflowWired) {
        window.__pamelaFontReflowWired = true;
        window.addEventListener("pageshow", () => {
          try { pamelaRefreshAllTextMetrics(window.pamelaMonumentEditor || window.pamelaEditor); } catch (_) {}
        });
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState !== "visible") return;
          try { pamelaRefreshAllTextMetrics(window.pamelaMonumentEditor || window.pamelaEditor); } catch (_) {}
        });
        window.addEventListener("orientationchange", () => {
          setTimeout(() => {
            try { pamelaRefreshAllTextMetrics(window.pamelaMonumentEditor || window.pamelaEditor); } catch (_) {}
          }, 250);
        });
      }
      
// =============================
// Delayed tooltips (hover helpers)
// - Delay before showing
// - Works even on disabled buttons
// =============================
window.pamelaWireDelayedTooltips =
  window.pamelaWireDelayedTooltips ||
  function pamelaWireDelayedTooltips() {
    if (window.__pamelaDelayedTooltipsWired) return;
    window.__pamelaDelayedTooltipsWired = true;

    const SHOW_DELAY_MS = 260; // a little faster without feeling jumpy
    const OFFSET_PX = 10;

    const tip = document.createElement("div");
    tip.id = "pamela-delayed-tooltip";
    tip.style.cssText =
      "position:fixed;left:0;top:0;transform:translate(-9999px,-9999px);" +
      "z-index:2147483647;pointer-events:none;" +
      "max-width:340px;padding:8px 10px;border-radius:10px;" +
      "background:#eef4ff;color:#1f2933;border:1px solid rgba(37,99,235,0.18);" +
      "font:700 13px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "box-shadow:0 12px 28px rgba(37,99,235,0.14);";
    document.body.appendChild(tip);

    let hoverEl = null;
    let showTimer = 0;
    let visible = false;

    const hide = () => {
      visible = false;
      tip.style.transform = "translate(-9999px,-9999px)";
    };

    const setText = (t) => {
      tip.textContent = t || "";
    };

    const position = () => {
      if (!hoverEl || !visible) return;

      const rect = hoverEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Default: above, centered
      let x = rect.left + rect.width / 2;
      let y = rect.top - OFFSET_PX;

      // Measure tooltip
      tip.style.transform = "translate(-9999px,-9999px)";
      tip.style.left = "0px";
      tip.style.top = "0px";
      tip.style.display = "block";
      const tRect = tip.getBoundingClientRect();

      // Clamp horizontally
      let left = x - tRect.width / 2;
      left = Math.max(8, Math.min(vw - tRect.width - 8, left));

      // Prefer above; if not enough room, put below
      let top = y - tRect.height;
      if (top < 8) top = rect.bottom + OFFSET_PX;

      // Clamp vertically
      top = Math.max(8, Math.min(vh - tRect.height - 8, top));

      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
      tip.style.transform = "translate(0,0)";
    };

    const getTipTargetFromPoint = (x, y) => {
      const el = document.elementFromPoint(x, y);
      if (!el) return null;
      return el.closest && el.closest("[data-pamela-tip]") ? el.closest("[data-pamela-tip]") : null;
    };

    const scheduleShow = (el) => {
      clearTimeout(showTimer);
      showTimer = window.setTimeout(() => {
        const txt = (el && el.getAttribute && el.getAttribute("data-pamela-tip")) || "";
        if (!txt) return;

        hoverEl = el;
        setText(txt);
        visible = true;
        position();
      }, SHOW_DELAY_MS);
    };

    // Track mouse position and find hovered tooltip target (works even if button disabled)
    window.addEventListener(
      "mousemove",
      (e) => {
        const newEl = getTipTargetFromPoint(e.clientX, e.clientY);

        if (newEl === hoverEl) {
          if (visible) position();
          return;
        }

        clearTimeout(showTimer);
        hide();

        hoverEl = newEl;
        if (hoverEl) scheduleShow(hoverEl);
      },
      true
    );

    // Hide on scroll/resize or when leaving window
    window.addEventListener("scroll", () => { clearTimeout(showTimer); hide(); }, true);
    window.addEventListener("resize", () => { clearTimeout(showTimer); hide(); }, true);
    window.addEventListener("blur", () => { clearTimeout(showTimer); hide(); }, true);
  };

// Apply tooltip text to your toolbar buttons (call after toolbar is created)
window.pamelaApplyToolbarTips =
  window.pamelaApplyToolbarTips ||
  function pamelaApplyToolbarTips(rootEl) {
    if (!rootEl) return;

    const currentLayout =
      window.pamelaEditor && window.pamelaEditor.layout === "companion"
        ? "companion"
        : "single";
    const addNameTip =
      currentLayout === "companion"
        ? "Add up to 2 lines for the name on each side"
        : "Add up to 2 lines for the name";

    const tipsById = {
      // TEXT row
      "pamela-add-lastname": "Add family last name text",
      "pamela-add-name": addNameTip,
      "pamela-add-date": "Add birth and death dates",
      "pamela-add-wedding": "Add wedding or anniversary text",
      "pamela-add-verse": currentLayout === "companion"
        ? "Add verse or custom inscription (up to 6)"
        : "Add verse or custom inscription (up to 5)",
      "pamela-size-down": "Decrease text size",
      "pamela-size-up": "Increase text size",
      "pamela-laser-text-style": "Switch selected overlapping laser text between white and black lettering",
      "pamela-font-select": "Change font for selected text",
      "pamela-follow-shape": "Curve selected text to follow the stone's shape",
      "pamela-group-side-text": pamelaIsGroupedSideTextActive(window.pamelaMonumentEditor || window.pamelaEditor, (window.pamelaMonumentEditor || window.pamelaEditor)?.selected)
        ? "Ungroup side text so each piece can move independently again"
        : "Group all text on this side so dragging one piece moves them together",

      // DESIGN row
      "pamela-view-clipart": "Open clipart library",
      "pamela-panels-frames": "Open panels and frames",
      "pamela-flip-x": "Flip selected item horizontally",
      "pamela-mirror-clone-btn": "Create a mirrored copy of the selected item",
      "pamela-rot-left": "Rotate left",
      "pamela-rot-right": "Rotate right",
      "pamela-clip-size-down": "Make selected image smaller",
      "pamela-clip-size-up": "Make selected image larger",
      "pamela-undo-btn": "Undo last change (Ctrl+Z). Does not undo image placement",
      "pamela-redo-btn": "Redo last undone change (Ctrl+Shift+Z). Does not redo image placement",
      "pamela-delete": "Delete selected item",
      "pamela-open-addons": "Open optional add-ons like portraits and more",
      "pamela-panel-mode-toggle": "Choose how panels appear on dark granite",
      "pamela-clipart-mode-toggle": "Select a placed clipart item to change its color or style",
      "pamela-independent-sides-toggle": "Move this mirrored text independently of its matching pair on the other side",
      "pamela-image-replace-btn": "Replace the selected image with a new upload",
      "pamela-porcelain-size-reposition-btn": "Adjust the size and position of the portrait photo",
      "pamela-nudge-toggle": "Move the selected item by small steps using arrow buttons",
      "pamela-nudge-up": "Nudge up",
      "pamela-nudge-down": "Nudge down",
      "pamela-nudge-left": "Nudge left",
      "pamela-nudge-right": "Nudge right",

      // VIEW row
      "pamela-zoom-out": "Zoom out",
      "pamela-zoom-in": "Zoom in",
      "pamela-reset-view": "Reset zoom and pan to fit the whole design",
      "pamela-toggle-grid": "Toggle alignment grid",
      "pamela-fullscreen": "Toggle full screen view",

      // Mobile-only buttons
      "pamela-mobile-reset-view": "Reset zoom and pan to fit the whole design",
      "pamela-mobile-undo-btn": "Undo last change. Does not undo image placement",
      "pamela-mobile-redo-btn": "Redo last undone change. Does not redo image placement",
    };

    Object.keys(tipsById).forEach((id) => {
      let el = null;
      try {
        el = rootEl.querySelector(`#${CSS.escape(id)}`);
      } catch (_) {
        // fallback if CSS.escape not supported
        el = rootEl.querySelector("#" + id);
      }
      if (!el) return;

      el.setAttribute("data-pamela-tip", tipsById[id]);

      if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", tipsById[id]);

      // Prevent native title tooltip fighting with ours
      if (el.getAttribute("title")) el.removeAttribute("title");
    });
  };

// -----------------------------
// Button labels reflect REAL open/close state (best + stable)
// -----------------------------
(function pamelaWireButtonLabelsByVisibility() {
  if (window.__pamelaBtnLabelsVisWired) return;
  window.__pamelaBtnLabelsVisWired = true;

  // Ã¢Å“â€¦ BUTTON IDS (make sure these match your actual toolbar button IDs)
  const clipBtnId = "pamela-view-clipart";
  const panelsBtnId = "pamela-view-panels"; // <-- CHANGE THIS if your button id is different

  // Ã¢Å“â€¦ CONTAINER IDS (these match your current systems)
  const CLIP_CONT = "pamela-clipart-panel";
  const PANELS_CONT = "pamela-panels-dock";

  const CLIP_VIEW = "View Library";
  const CLIP_CLOSE = "Close Library";
  const PANELS_VIEW = "View Panels";
  const PANELS_CLOSE = "Close Panels";

  function isOpenByVisibility(el) {
    if (!el) return false;
    const hiddenByAria = el.getAttribute("aria-hidden") === "true";
    const hiddenByDisplay = el.style.display === "none";
    // if display is empty string, treat as open only if aria-hidden isn't true
    return !hiddenByAria && !hiddenByDisplay;
  }

  function syncOne(btnId, contId, viewTxt, closeTxt) {
    const btn = document.getElementById(btnId);
    const cont = document.getElementById(contId);
    if (!btn || !cont) return;

    const open = isOpenByVisibility(cont);
    btn.textContent = open ? closeTxt : viewTxt;
  }

  function syncAll() {
    syncOne(clipBtnId, CLIP_CONT, CLIP_VIEW, CLIP_CLOSE);
    syncOne(panelsBtnId, PANELS_CONT, PANELS_VIEW, PANELS_CLOSE);
  }

  // Observe BOTH containers so labels flip immediately when UI hides/shows
  function watch(contId) {
    const cont = document.getElementById(contId);
    if (!cont) return;
    const mo = new MutationObserver(syncAll);
    mo.observe(cont, { attributes: true, attributeFilter: ["style", "aria-hidden"] });
  }

  // Initial sync + start watchers
  syncAll();
  watch(CLIP_CONT);
  watch(PANELS_CONT);

  // Also sync after any click (covers cases where some other code changes state indirectly)
  document.addEventListener(
  "click",
  () => {
    // immediate sync
    syncAll();

    // delayed sync (for animated / async UI)
    setTimeout(syncAll, 60);
    setTimeout(syncAll, 180);
  },
  true
);
})();

      // Ã¢Å“â€¦ Ensure delayed tooltips are wired + tips are applied (even if ensureToolbar doesn't call them)
      try { window.pamelaWireDelayedTooltips(); } catch (_) {}
      try { window.pamelaApplyToolbarTips(document.getElementById("pamela-editor-toolbar")); } catch (_) {}

      this.wrapEl.style.width = this.wrapEl.style.width || "100%";
      if (!this.wrapEl.style.minHeight) this.wrapEl.style.minHeight = "450px";

      this.stage = new Konva.Stage({
        container: this.wrapEl,
        width: 10,
        height: 10,
      });

      this.stage.on("dragend transformend", () => {
      try {
        pamelaQueueDraftSave();
      } catch (_) {}
      });

      // Ã¢Å“â€¦ Layer order (important):
      // background (stone) -> grid (optional) -> content (text+art) -> overlay (border PNG, click-through) -> ui (transformer)

      const bg = new Konva.Layer({ listening: false });

      // ============================
      // Ã¢Å“â€¦ SIDE-SPLIT GROUPS (FRONT/BACK), one shared Layer per type
      // Panels/Content/Vase/Portraits each get a FRONT group + BACK group,
      // both living in ONE Konva.Layer per type instead of two separate
      // Layers. Konva warns above ~5 layers because every Layer is its own
      // <canvas> the browser has to composite every frame; the old 8
      // separate front/back layers (+ background/border/grid/ui = 12 total)
      // were a real, measurable performance cost. Groups have no canvas of
      // their own and are otherwise API-compatible with everything this file
      // already does to them (add/getChildren/find/visible/etc.) -- the one
      // exception is batchDraw(), which only Layer implements, so each group
      // gets a shim that proxies to its shared layer. Nothing about how
      // panels/content/portraits/vase are built, knocked-out, dragged, or
      // rendered changes -- only which canvas they're composited onto.
      // this.layers.panels/content/portraits stay "ACTIVE SIDE" aliases
      // exactly as before, now pointing at groups instead of layers.
      // ============================

      const makeSideLayer = () => {
        const layer = new Konva.Layer();
        const front = new Konva.Group();
        const back = new Konva.Group();
        front.batchDraw = () => layer.batchDraw();
        back.batchDraw = () => layer.batchDraw();
        front.draw = () => layer.draw();
        back.draw = () => layer.draw();
        layer.add(front);
        layer.add(back);
        return { layer, front, back };
      };

      // Panels (panels only live here, behind clipart + text)
      const panelsSplit = makeSideLayer();
      const panelsFront = panelsSplit.front;
      const panelsBack  = panelsSplit.back;

      // Content (clipart/text live here)
      const contentSplit = makeSideLayer();
      const contentFront = contentSplit.front;
      const contentBack  = contentSplit.back;

      // Portraits (portraits live here)
      const portraitsSplit = makeSideLayer();
      const portraitsFront = portraitsSplit.front;
      const portraitsBack  = portraitsSplit.back;

      // Vase engraving (vase-only text/clipart live here)
      const vaseSplit = makeSideLayer();
      const vaseFront = vaseSplit.front;
      const vaseBack  = vaseSplit.back;

      // Border overlay layer (visual-only, click-through, above content)
      const border = new Konva.Layer({ listening: false });

      // Grid layer (visual-only, should sit ABOVE the border)
      const grid = new Konva.Layer({ listening: false });

      // UI layer (transformer, etc.)
      const ui = new Konva.Layer();

      // Ã¢Å“â€¦ Start with BACK hidden (so FRONT stays exactly as it works today)
      panelsBack.visible(false);
      contentBack.visible(false);
      vaseBack.visible(false);
      portraitsBack.visible(false);

      // Order matters (bottom -> top). Each merged layer occupies the slot
      // its FRONT half used to occupy -- panels behind content behind vase
      // behind portraits, identical to before, regardless of active side
      // since only one group per layer is ever visible at a time.
      this.stage.add(bg);
      this.stage.add(panelsSplit.layer);
      this.stage.add(contentSplit.layer);
      this.stage.add(vaseSplit.layer);
      this.stage.add(portraitsSplit.layer);

      // Shared overlays
      this.stage.add(border);
      this.stage.add(grid);
      this.stage.add(ui);

      // Store references
      this.layers.background = bg;

      // Side buckets
      this.layers.sides = this.layers.sides || {};
      this.layers.sides.front = {
        panels: panelsFront,
        content: contentFront,
        vase: vaseFront,
        portraits: portraitsFront
      };

      this.layers.sides.back = {
        panels: panelsBack,
        content: contentBack,
        vase: vaseBack,
        portraits: portraitsBack
      };
      // Ã¢Å“â€¦ ACTIVE SIDE aliases (existing code keeps using these)
      this.layers.panels = panelsFront;
      this.layers.content = contentFront;
      this.layers.portraits = portraitsFront;

      this.layers.border = border;
      this.layers.grid = grid;
      this.layers.ui = ui;

      //this._initPortraitSystem();

      // Auto-enforce z-order inside the ACTIVE content layer:
      // Clipart stays behind, Text stays on top
      const restackActiveContent = () => {
        try { this._pamelaRestackContentLayer(); } catch (_) {}
      };

      // IMPORTANT: bind to BOTH front/back content layers so each side keeps its own correct stacking
      contentFront.off("add.pamelaZ remove.pamelaZ");
      contentBack.off("add.pamelaZ remove.pamelaZ");
      contentFront.on("add.pamelaZ remove.pamelaZ", restackActiveContent);
      contentBack.on("add.pamelaZ remove.pamelaZ", restackActiveContent);
      // ================================
      // Overlap-color live update wiring
      // ================================
      this._scheduleOverlapColors = pamelaMakeRafScheduler(() => {
        pamelaUpdateAllTextColorsForOverlap(this);
        try { this._refreshClipButtons?.(); } catch (_) {}
        try { pamelaSyncMobileToolbars(this); } catch (_) {}
      });

      // When ANY node moves in the CONTENT layer:
      // - if it's Text: update that text immediately
      // - if it's clipart image: re-evaluate all texts
      this.layers.content.off(
        "dragmove.pamelaOverlap dragend.pamelaOverlap transform.pamelaOverlap transformend.pamelaOverlap"
      );
      this.layers.content.on(
        "dragmove.pamelaOverlap dragend.pamelaOverlap transform.pamelaOverlap transformend.pamelaOverlap",
        (e) => {
          const n = e?.target;
          if (!n) return;

          if (pamelaIsTextLikeNode(n)) {
            pamelaUpdateOneTextColorForOverlap(this, n);
            this.layers.content.batchDraw();
            try { this._refreshClipButtons?.(); } catch (_) {}
            try { pamelaSyncMobileToolbars(this); } catch (_) {}
            return;
          }

          if (n.className === "Image" && (n.attrs?.pamelaClipart === true || n.attrs?.pamelaLaserEtching === true)) {
            this._scheduleOverlapColors();
          }
        }
      );

      // When ANY node moves in the PANELS layer:
      // - panels affect overlap colors, so re-evaluate all texts
      this.layers.panels?.off(
        "dragmove.pamelaOverlap dragend.pamelaOverlap transform.pamelaOverlap transformend.pamelaOverlap"
      );
      this.layers.panels?.on(
        "dragmove.pamelaOverlap dragend.pamelaOverlap transform.pamelaOverlap transformend.pamelaOverlap",
        (e) => {
          const n = e?.target;
          if (!n) return;

          if (n.className === "Image" && n.attrs?.pamelaPanel === true) {
            this._scheduleOverlapColors();
          }
        }
      );

      this._buildGrid();

      this.nodes.tr = new Konva.Transformer({
        rotateEnabled: true,
        enabledAnchors: [], // no resize handles
        keepRatio: true,
        ignoreStroke: true,
      });
      this.layers.ui.add(this.nodes.tr);

      // =============================
      // History hooks for TEXT handle transforms (resize/rotate via tr)
      // =============================
      try { this.nodes.tr.off("transformstart.pamelaHist transformend.pamelaHist"); } catch (_) {}

      this.nodes.tr.on("transformstart.pamelaHist", () => {
        try {
          const n = this.selected;
          if (pamelaIsPanelKnockoutBlocker(n)) pamelaRestorePanelKnockouts(this);
        } catch (_) {}

        // Ã¢Å“â€¦ Commit BEFORE so Ctrl+Z can revert the first handle change
        this._historyCommit("text-transform-before");
      });

      this.nodes.tr.on("transformend.pamelaHist", () => {
        // Keep text rules consistent after handle action
        try {
          const n = this.selected;
          if (n && pamelaIsTextLikeNode(n)) {
            if (n.className === "Text") pamelaFixTextMetrics(n);
            pamelaSnapTextToCenterX(this, n);
            pamelaClampNodeIntoSafeRect(this, n);
            pamelaAutoPairLinkFromDrag?.(this, n);
            pamelaUpdateOneTextColorForOverlap?.(this, n);
            pamelaEnforceSpacingForText?.(this);
          } else if (pamelaIsPanelKnockoutBlocker(n)) {
            pamelaClampNodeIntoSafeRect(this, n);
            pamelaSchedulePanelKnockoutRebuild(this);
          }
        } catch (_) {}

        // Ã¢Å“â€¦ Commit AFTER so Redo re-applies the handle change
        this._historyCommit("text-transform-after");

        try { this.layers?.content?.batchDraw?.(); } catch (_) {}
        try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
      });

// Ã¢Å“â€¦ Panel transformer: free-stretch handles, NO aspect lock, NO flip (prevents snap-huge)
this.nodes.trPanel = new Konva.Transformer({
  rotateEnabled: true,
  keepRatio: false,
  ignoreStroke: true,
  centeredScaling: true,

  // Ã¢Å“â€¦ THIS is the snap-huge fix:
  // Prevent Konva from allowing negative width/height while dragging handles.
  flipEnabled: false,

  // Default = all handles (we will auto-switch to width-only for lastname)
  enabledAnchors: [
    "top-left",
    "top-center",
    "top-right",
    "middle-left",
    "middle-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
  ],

  // Ã¢Å“â€¦ Enforce minimum size, and keep the opposite edge fixed when clamping
  boundBoxFunc: (oldBox, newBox) => {
    const tr = this.nodes.trPanel;
    const a = tr && typeof tr.getActiveAnchor === "function" ? tr.getActiveAnchor() : "";

    // Which panel is being transformed right now?
    const sel = this.selected;

    // Ã¢Å“â€¦ Width-only mode ONLY for lastname panels
    const isLastNamePanel =
      !!sel &&
      (sel?.getAttr?.("pamelaPanelGroup") === "lastname" ||
       String(sel?.getAttr?.("pamelaPanelCategory") || "").startsWith("Last Name"));

    // Ã¢Å“â€¦ Dynamically switch anchors so UI matches the rule
    // (donÃ¢â‚¬â„¢t spam resets; only change when needed)
    try {
      const mode = isLastNamePanel ? "lastname" : "free";
      if (tr._pamelaMode !== mode) {
        tr._pamelaMode = mode;
        try { tr.centeredScaling(!isLastNamePanel); } catch (_) {}
        tr.enabledAnchors(
          isLastNamePanel
            ? ["middle-left", "middle-right"] // Ã¢Å“â€¦ width-only handles
            : [
                "top-left",
                "top-center",
                "top-right",
                "middle-left",
                "middle-right",
                "bottom-left",
                "bottom-center",
                "bottom-right",
              ]
        );
        tr.getLayer()?.batchDraw?.();
      }
    } catch (_) {}

    // Ã¢Å“â€¦ Min must be in the SAME units as oldBox/newBox.
    // Because your UI layer is scaled with the view, the transformer box is scaled too.
    const s = this.state?.view?.scale || 1;

    const MIN_W = (isLastNamePanel ? pamelaGetLastNamePanelMinWidth(this, sel) : PANEL_MIN_W_PX) * s;
    const MIN_H = PANEL_MIN_H_PX * s;

    let box = { ...newBox };

    // ---- MIN WIDTH (always enforced) ----
    if (box.width < MIN_W) {
      const minW = MIN_W;

      if (a === "middle-left" || a === "top-left" || a === "bottom-left") {
        const right = oldBox.x + oldBox.width;
        box.x = right - minW;
      }
      box.width = minW;
    }

    if (isLastNamePanel) {
      // Ã¢Å“â€¦ LASTNAME RULE:
      // Lock height AND lock Y so they cannot stretch taller/shorter or slide vertically via handles.
      box.height = oldBox.height;
      box.y = oldBox.y;

      // (We still allow rotate, and width changes via left/right.)
      return box;
    }

    // ---- MIN HEIGHT (normal panels only) ----
    if (box.height < MIN_H) {
      const minH = MIN_H;

      if (a === "top-center" || a === "top-left" || a === "top-right") {
        const bottom = oldBox.y + oldBox.height;
        box.y = bottom - minH;
      }
      box.height = minH;
    }

    return box;
  },
});

this.layers.ui.add(this.nodes.trPanel);

// =============================
// PANEL transformer wiring (ONE history step per handle action)
// - Commit BEFORE at transformstart
// - Bake + clamp on transformend
// - NO extra transformend commits anywhere else
// =============================
try { this.nodes.trPanel.off(".pamelaPanel"); } catch (_) {}

this.nodes.trPanel.on("transformstart.pamelaPanel", () => {
  try { pamelaRestorePanelKnockouts(this); } catch (_) {}

  // Ã¢Å“â€¦ Commit BEFORE so Ctrl+Z reverts the first handle movement
  this._historyCommit("panel-transform-before");
});

// Ã¢Å“â€¦ Live transform: clamp only (DO NOT bake width/height here)
this.nodes.trPanel.on("transform.pamelaPanel", () => {
  const n = this.nodes.trPanel.nodes()[0];
  if (!n || n.className !== "Image" || n.attrs?.pamelaPanel !== true) return;

  pamelaClampNodeIntoSafeRect(this, n);

  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { this.layers.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers.ui?.batchDraw?.(); } catch (_) {}
});

this.nodes.trPanel.on("transformend.pamelaPanel", () => {
  const n = this.nodes.trPanel.nodes()[0];
  if (!n || n.className !== "Image" || n.attrs?.pamelaPanel !== true) return;

  const layer = this.layers?.panels || n.getLayer?.();

  // Ã¢Å“â€¦ Capture visual center BEFORE bake (prevents Ã¢â‚¬Å“jumpÃ¢â‚¬Â on release)
  const rBefore = n.getClientRect({ relativeTo: layer, skipTransform: false });
  const cxBefore = rBefore.x + rBefore.width / 2;
  const cyBefore = rBefore.y + rBefore.height / 2;

  // Ã¢Å“â€¦ Preserve mirror state while baking real pixel size
  const sx = n.scaleX() || 1;
  const sy = n.scaleY() || 1;

  const signX = sx < 0 ? -1 : 1; // keep mirrored panels mirrored
  const signY = sy < 0 ? -1 : 1;

  const newW = Math.max(
    pamelaIsLastNamePanelNode(n) ? pamelaGetLastNamePanelMinWidth(this, n) : PANEL_MIN_W_PX,
    (n.width() || 0) * Math.abs(sx)
  );
  const newH = Math.max(PANEL_MIN_H_PX, (n.height() || 0) * Math.abs(sy));

  // Ã¢Å“â€¦ Bake size
  n.size({ width: newW, height: newH });
  try { pamelaUpdateTraditionalPanelSliceImage(n); } catch (_) {}

  // Ã¢Å“â€¦ Keep rotation pivot centered after resize
  try { n.offsetX(newW / 2); n.offsetY(newH / 2); } catch (_) {}

  // Ã¢Å“â€¦ Reset magnitude to 1 but keep the sign
  n.scale({ x: signX, y: signY });

  // Ã¢Å“â€¦ Restore visual center AFTER bake (this is the jump fix)
  const rAfter = n.getClientRect({ relativeTo: layer, skipTransform: false });
  const cxAfter = rAfter.x + rAfter.width / 2;
  const cyAfter = rAfter.y + rAfter.height / 2;

  n.x(n.x() + (cxBefore - cxAfter));
  n.y(n.y() + (cyBefore - cyAfter));

  pamelaClampNodeIntoSafeRect(this, n);

  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { pamelaRebuildPanelKnockouts(this); } catch (_) {}
  try { this.layers.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers.ui?.batchDraw?.(); } catch (_) {}

  // Ã¢Å“â€¦ Commit AFTER exactly once
  this._historyCommit("panel-transform-after");
});


this._wireViewportControls();
this._wirePointerCaptureFix(); // Ã¢Å“â€¦ makes drag release 100% reliable
window.addEventListener("resize", this._onResize, { passive: true });

// --- Toolbar wiring ---
document
  .getElementById("pamela-add-lastname")
  ?.addEventListener("click", () => this.addField("lastname"));
document
  .getElementById("pamela-add-name")
  ?.addEventListener("click", () => this.addField("name"));
document
  .getElementById("pamela-add-date")
  ?.addEventListener("click", async () => {
    if (!this.canAdd("date")) return;
    if (pamelaShouldShowDateHelper(this)) {
      await pamelaShowDateFormatHelper();
    }
    this.addField("date");
  });
document
  .getElementById("pamela-add-wedding")
  ?.addEventListener("click", () => this.addField("wedding"));
document
  .getElementById("pamela-add-verse")
  ?.addEventListener("click", () => this.addField("verse"));

// Ã¢Å“â€¦ BACK flyout buttons (TRUE side swap first, then add field)
document.getElementById("pamela-delete")?.addEventListener("click", () => this.deleteSelected());
document.getElementById("pamela-size-up")?.addEventListener("click", () => this.bumpSize(+1));
document.getElementById("pamela-size-down")?.addEventListener("click", () => this.bumpSize(-1));
document.getElementById("pamela-nudge-left")?.addEventListener("click", () => pamelaNudgeSelected(this, -PAMELA_NUDGE_STEP_IN, 0));
document.getElementById("pamela-nudge-up")?.addEventListener("click", () => pamelaNudgeSelected(this, 0, -PAMELA_NUDGE_STEP_IN));
document.getElementById("pamela-nudge-down")?.addEventListener("click", () => pamelaNudgeSelected(this, 0, PAMELA_NUDGE_STEP_IN));
document.getElementById("pamela-nudge-right")?.addEventListener("click", () => pamelaNudgeSelected(this, PAMELA_NUDGE_STEP_IN, 0));
document.getElementById("pamela-follow-shape")?.addEventListener("click", (e) => pamelaOpenCurveTextMenu(this, e.currentTarget));
document.getElementById("pamela-laser-text-style")?.addEventListener("click", () => pamelaToggleSelectedLaserText(this));
document.getElementById("pamela-zoom-in")?.addEventListener("click", () => this.zoomByStep(+1));
document.getElementById("pamela-zoom-out")?.addEventListener("click", () => this.zoomByStep(-1));
document.getElementById("pamela-reset-view")?.addEventListener("click", () => this._resize(true));
document.getElementById("pamela-mobile-reset-view")?.addEventListener("click", () => this._resize(true));
// Undo / Redo buttons
document.getElementById("pamela-undo-btn")?.addEventListener("click", () => this.undo());
document.getElementById("pamela-redo-btn")?.addEventListener("click", () => this.redo());
document.getElementById("pamela-mobile-undo-btn")?.addEventListener("click", () => this.undo());
document.getElementById("pamela-mobile-redo-btn")?.addEventListener("click", () => this.redo());
// Enable/disable Undo/Redo buttons based on stacks
this._refreshUndoRedoUI = () => {
  const u = this._undo || [];
  const r = this._redo || [];

  const canUndo = u.length >= 2; // need current + previous
  const canRedo = r.length > 0;

  const undoBtn = document.getElementById("pamela-undo-btn");
  const redoBtn = document.getElementById("pamela-redo-btn");
  const mobileUndoBtn = document.getElementById("pamela-mobile-undo-btn");
  const mobileRedoBtn = document.getElementById("pamela-mobile-redo-btn");

  if (undoBtn) {
    undoBtn.disabled = !canUndo;
    undoBtn.style.opacity = canUndo ? "1" : "0.35";
    undoBtn.style.cursor = canUndo ? "pointer" : "not-allowed";
  }
  if (mobileUndoBtn) {
    mobileUndoBtn.disabled = !canUndo;
    mobileUndoBtn.style.opacity = canUndo ? "1" : "0.35";
    mobileUndoBtn.style.cursor = canUndo ? "pointer" : "not-allowed";
  }

  if (redoBtn) {
    redoBtn.disabled = !canRedo;
    redoBtn.style.opacity = canRedo ? "1" : "0.35";
    redoBtn.style.cursor = canRedo ? "pointer" : "not-allowed";
  }
  if (mobileRedoBtn) {
    mobileRedoBtn.disabled = !canRedo;
    mobileRedoBtn.style.opacity = canRedo ? "1" : "0.35";
    mobileRedoBtn.style.cursor = canRedo ? "pointer" : "not-allowed";
  }
};

document
  .getElementById("pamela-mirror-clone-btn")
  ?.addEventListener("click", () => this.mirrorCloneSelected());

const gridBtn = document.getElementById("pamela-toggle-grid");
if (gridBtn) {
  gridBtn.addEventListener("click", () => {
    this.gridOn = !this.gridOn;
    this._setGridVisible(this.gridOn);
    gridBtn.textContent = this.gridOn ? "Grid: On" : "Grid: Off";
  });
}

const fsBtn = document.getElementById("pamela-fullscreen");
if (fsBtn) {
  const syncFsBtn = () => {
    const on = !!document.fullscreenElement;
    fsBtn.textContent = on ? "Exit Full Screen" : "Full Screen";
  };

  document.addEventListener("fullscreenchange", () => {
    syncFsBtn();
    this._resize(true);
  });

  fsBtn.addEventListener("click", () => {
    toggleFullscreenFor(this.wrapEl);
  });

  if (!this._pamelaFullscreenResizeBound) {
    this._pamelaFullscreenResizeBound = true;

document.addEventListener("fullscreenchange", () => {
  if (document.fullscreenElement) {
    this.wrapEl.style.height = "100vh";
  } else {
    this.wrapEl.style.height = "450px";
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      this._resize(true);
    });
  });
});
  }

  syncFsBtn();
}

// -----------------------------
// Mutual exclusion (FORCE CLOSE OTHER, no guards, no state checks)
// Put this inside init() in the toolbar wiring area
// -----------------------------

var clipBtnEl = document.getElementById("pamela-view-clipart");
if (clipBtnEl) {
  clipBtnEl.addEventListener(
    "click",
    function () {
      // ALWAYS close panels whenever Clipart button is clicked
      try {
        if (window.PAMELA_CLOSE_PANELS_UI && typeof window.PAMELA_CLOSE_PANELS_UI === "function") {
          window.PAMELA_CLOSE_PANELS_UI();
        } else if (typeof closePanelsUI === "function") {
          closePanelsUI();
        } else {
          // last-resort: trigger your existing panels toggle event (many UIs treat it as close when open)
          window.dispatchEvent(new CustomEvent("pamela:togglePanels"));
        }
      } catch (_) {}
    },
    true
  );
}

// -----------------------------
// Library notice + Add-ons flyout auto-close
// - Clicking Panels/Clipart always closes Optional Add-ons flyout
// - If BACK is active, show back-side pricing notice
// - If user switches TO BACK while Panels/Clipart is already open, show the notice then too
// -----------------------------
function pamelaIsBackSideActive() {
  try {
    return !!(window.PAMELA_SIDES && window.PAMELA_SIDES.current === "back");
  } catch (_) {}
  return false;
}

function pamelaCloseAddonsFlyoutIfOpen() {
  try {
    if (window.PAMELA_CLOSE_ADDONS_UI && typeof window.PAMELA_CLOSE_ADDONS_UI === "function") {
      window.PAMELA_CLOSE_ADDONS_UI();
      return;
    }
  } catch (_) {}

  try {
    const fly = document.getElementById("pamela-addon-flyout");
    if (fly) fly.style.display = "none";
  } catch (_) {}
}

function pamelaIsPanelsOpen() {
  try {
    const dock = document.getElementById("pamela-panels-dock");
    if (!dock) return false;
    return dock.style.display !== "none" && dock.getAttribute("aria-hidden") !== "true";
  } catch (_) {}
  return false;
}

function pamelaIsClipartOpen() {
  try {
    const panel = document.getElementById("pamela-clipart-panel");
    if (!panel) return false;
    return panel.style.display !== "none";
  } catch (_) {}
  return false;
}

function pamelaShowBackArtNotice(msg) {
  let box = document.getElementById("pamela-back-art-notice");
  if (!box) {
    box = document.createElement("div");
    box.id = "pamela-back-art-notice";
    box.style.cssText =
      "position:fixed;top:450px;left:50%;transform:translateX(-50%) translateX(320px);" +
      "z-index:2147483647;max-width:min(92vw,760px);" +
      "padding:12px 16px;border-radius:14px;border:1px solid rgba(0,0,0,0.18);" +
      "background:#111;color:#fff;box-shadow:0 12px 32px rgba(0,0,0,0.24);" +
      "font:900 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "text-align:center;opacity:0;pointer-events:none;" +
      "transition:transform 1000ms ease, opacity 1000ms ease;";
    document.body.appendChild(box);
  }

  box.textContent = msg || "";

  clearTimeout(box.__pamelaShowTimer);
  clearTimeout(box.__pamelaHideTimer);

  box.style.opacity = "0";
  box.style.transform = "translateX(-50%) translateX(120px)";

  box.__pamelaShowTimer = setTimeout(() => {
    try {
      box.style.opacity = "1";
      box.style.transform = "translateX(-50%) translateX(0)";
    } catch (_) {}
  }, 20);

  box.__pamelaHideTimer = setTimeout(() => {
    try {
      box.style.opacity = "0";
      box.style.transform = "translateX(-50%) translateX(120px)";
    } catch (_) {}
  }, 6000);
}

function pamelaMaybeShowBackLibraryNotice() {
  if (!pamelaIsBackSideActive()) return;

  if (pamelaIsPanelsOpen()) {
    pamelaShowBackArtNotice("Frames are optional on the back of your monument. Each frame added here costs $75.");
    return;
  }

  if (pamelaIsClipartOpen()) {
    pamelaShowBackArtNotice("Clipart is optional on the back of your monument. Each piece added here costs $50.");
  }
}

if (!window.__pamelaBackArtNoticeWired) {
  window.__pamelaBackArtNoticeWired = true;

document.addEventListener(
  "click",
  (e) => {
    const btn = e?.target?.closest?.("#pamela-panels-frames, #pamela-view-clipart");
    if (!btn) return;

    const wasBack = pamelaIsBackSideActive();
    const wasPanelsOpen = pamelaIsPanelsOpen();
    const wasClipartOpen = pamelaIsClipartOpen();

    // Always close Optional Add-ons flyout
    pamelaCloseAddonsFlyoutIfOpen();

    setTimeout(() => {
      if (!wasBack) return;

      if (btn.id === "pamela-panels-frames") {
        const isPanelsOpenNow = pamelaIsPanelsOpen();
        if (!wasPanelsOpen && isPanelsOpenNow) {
          pamelaShowBackArtNotice(
            "Frames are optional on the back of your monument. Each frame added here costs $75."
          );
        }
        return;
      }

      if (btn.id === "pamela-view-clipart") {
        const isClipartOpenNow = pamelaIsClipartOpen();
        if (!wasClipartOpen && isClipartOpenNow) {
          pamelaShowBackArtNotice(
            "Clipart is optional on the back of your monument. Each piece added here costs $50."
          );
        }
      }
    }, 80);
  },
  true
);

  // Back-view notices are intentionally tied to opening the relevant library,
  // not merely switching to the back view.
}

// Ã¢Å“â€¦ This MUST exist or Panels won't open
// -----------------------------
const panelsBtn = document.getElementById("pamela-panels-frames");
if (panelsBtn) {
  panelsBtn.addEventListener("click", () => {
    // Close clipart first (safe even if already closed)
    try { window.PAMELA_CLOSE_CLIPART_UI?.(); } catch (_) {}
    try { closeClipartUI?.(); } catch (_) {}

    // Now toggle panels (your original working logic)
    if (window.PAMELA_PANELS_UI && typeof window.PAMELA_PANELS_UI.toggle === "function") {
      try {
        window.PAMELA_PANELS_UI.toggle();
        return;
      } catch (_) {}
    }

    if (typeof window.PAMELA_TOGGLE_PANELS_UI === "function") {
      try {
        window.PAMELA_TOGGLE_PANELS_UI();
        return;
      } catch (_) {}
    }

    try {
      window.dispatchEvent(new CustomEvent("pamela:togglePanels"));
    } catch (_) {}
  });
}

// Clipart tools (selected clipart only)
document.getElementById("pamela-flip-x")?.addEventListener("click", () => this.flipSelected("x"));
document.getElementById("pamela-rot-left")?.addEventListener("click", () => {
  const sel = this.selected;
  let portraitEditWrapper = null;
  try {
    const st = this.stage;
    const tr = st && st.findOne && st.findOne("#cmPortraitTransformer");
    const nodes = tr && tr.nodes && tr.nodes();
    const portraitPhoto = nodes && nodes[0] ? nodes[0] : null;
    portraitEditWrapper =
      (typeof findPortraitWrapperUp === "function" && portraitPhoto)
        ? findPortraitWrapperUp(portraitPhoto)
        : null;
  } catch (_) {}

  const wrapper =
    portraitEditWrapper ||
    this.__cmSelectedPortraitWrapper ||
    window.__CM_SELECTED_PORTRAIT_WRAPPER__ ||
    window.__CM_LAST_PORTRAIT_WRAPPER__ ||
    (
      (sel?.attrs?.pamelaType === "portrait" || sel?.getAttr?.("__isPortraitGroup") === true)
        ? sel
        : (typeof findPortraitWrapperUp === "function" ? findPortraitWrapperUp(sel) : null)
    );

  if (wrapper) {
    this.rotatePortraitMask90(wrapper, -1);
    return;
  }

  if (pamelaHasGroupedSideText(this) && pamelaCanGroupSideText(this, this.selected)) {
    if (pamelaRotateGroupedSideText(this, this.groupedTextSide, -CLIP_ROTATE_STEP_DEG)) {
      pamelaRefreshTextGroupOverlay(this);
      return;
    }
  }

  this.rotateSelected(-CLIP_ROTATE_STEP_DEG);
});

document.getElementById("pamela-rot-right")?.addEventListener("click", () => {
  const sel = this.selected;
  let portraitEditWrapper = null;
  try {
    const st = this.stage;
    const tr = st && st.findOne && st.findOne("#cmPortraitTransformer");
    const nodes = tr && tr.nodes && tr.nodes();
    const portraitPhoto = nodes && nodes[0] ? nodes[0] : null;
    portraitEditWrapper =
      (typeof findPortraitWrapperUp === "function" && portraitPhoto)
        ? findPortraitWrapperUp(portraitPhoto)
        : null;
  } catch (_) {}

  const wrapper =
    portraitEditWrapper ||
    this.__cmSelectedPortraitWrapper ||
    window.__CM_SELECTED_PORTRAIT_WRAPPER__ ||
    window.__CM_LAST_PORTRAIT_WRAPPER__ ||
    (
      (sel?.attrs?.pamelaType === "portrait" || sel?.getAttr?.("__isPortraitGroup") === true)
        ? sel
        : (typeof findPortraitWrapperUp === "function" ? findPortraitWrapperUp(sel) : null)
    );

  if (wrapper) {
    this.rotatePortraitMask90(wrapper, +1);
    return;
  }

  if (pamelaHasGroupedSideText(this) && pamelaCanGroupSideText(this, this.selected)) {
    if (pamelaRotateGroupedSideText(this, this.groupedTextSide, +CLIP_ROTATE_STEP_DEG)) {
      pamelaRefreshTextGroupOverlay(this);
      return;
    }
  }

  this.rotateSelected(+CLIP_ROTATE_STEP_DEG);
});

// Panel/clipart +/- is wired once by bumpImageSize when the toolbar is built.

// Font dropdown
const fontSel = document.getElementById("pamela-font-select");
if (fontSel) {
  fontSel.onchange = async () => {
    if (!this.selected || !pamelaIsTextLikeNode(this.selected)) return;

    const chosen = String(fontSel.value || "").trim();
    if (!chosen) return;

    await pamelaRegisterFontFace(chosen);

    if (document.fonts && document.fonts.load) {
      try {
        await document.fonts.load(`16px "${chosen}"`);
        await document.fonts.ready;
      } catch (_) {}
    }

    const type = this.selected.attrs?.pamelaFieldType;
    let typesToUpdate = [];

    if (type === "lastname") {
      this.fontState.lastname = chosen;
      typesToUpdate = ["lastname"];
    } else if (type === "name" || type === "date") {
      this.fontState.namedate = chosen;
      typesToUpdate = ["name", "date"];
    } else if (type === "wedding") {
      this.fontState.wedding = chosen;
      typesToUpdate = ["wedding"];
    } else if (type === "verse") {
      this.fontState.verse = chosen;
      typesToUpdate = ["verse"];
    } else {
      this.selected.fontFamily(chosen);
      pamelaFixTextMetrics(this.selected);

      // Ã¢Å“â€¦ keep inside safe area
      pamelaClampNodeIntoSafeRect(this, this.selected);

      this.layers.content.batchDraw();
      this.selectNode(this.selected);
      pamelaEnforceSpacingForText(this);
      return;
    }

    const nodes = this.layers.content.getChildren(
      (n) => n && pamelaIsTextLikeNode(n) && typesToUpdate.includes(n.attrs?.pamelaFieldType)
    );

    nodes.forEach((n) => {
      const singleBackLastName = pamelaIsSingleBackLastNameNode(n, this);
      const keepCapIn = singleBackLastName
        ? clamp(
            pamelaCapHeightInForTextNode(n),
            PAMELA_SINGLE_BACK_LASTNAME_MIN_IN,
            PAMELA_SINGLE_BACK_LASTNAME_MAX_IN
          )
        : null;
      n.fontFamily(chosen);
      if (keepCapIn != null) pamelaApplyCapHeightInToTextNode(n, keepCapIn);
      pamelaFixTextMetrics(n);
      pamelaClampNodeIntoSafeRect(this, n);
    });

    this.layers.content.batchDraw();
    this.selectNode(this.selected);
    pamelaEnforceSpacingForText(this);
  };
}

// Fit
this._resize(true);
this.refreshButtons();
this.refreshSizeButtons();
this._refreshClipButtons();
this._historyReset();
this._historySeedInitialState();
this._refreshUndoRedoUI?.();

// Keyboard Undo/Redo (Ctrl/Cmd+Z, Ctrl/Cmd+Y, Ctrl/Cmd+Shift+Z)
if (!this.__pamelaUndoRedoKeysWired) {
  this.__pamelaUndoRedoKeysWired = true;

  window.addEventListener(
    "keydown",
    (e) => {
      // DonÃ¢â‚¬â„¢t steal keys while typing in inline edit
      if (this._inlineEditActive) return;

      const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      if (!ctrlOrCmd) return;

      const k = String(e.key || "").toLowerCase();

      // Undo: Ctrl/Cmd + Z
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        this.undo();
        return;
      }

      // Redo: Ctrl/Cmd + Y OR Ctrl/Cmd + Shift + Z
      if (k === "y" || (k === "z" && e.shiftKey)) {
        e.preventDefault();
        this.redo();
        return;
      }
    },
    true
  );
}
}

// -----------------------------
// Grid + center guide (only while dragging text)
// -----------------------------
    _buildGrid() {
      const layer = this.layers.grid;
      if (!layer) return;

      layer.destroyChildren();

      const g = new Konva.Group({ listening: false });
      layer.add(g);
      this.nodes.gridGroup = g;

      const step = DPI * 1;
      const majorEvery = 5;

      const w = this.state.design.width;
      const h = this.state.design.height;

      for (let x = 0, i = 0; x <= w; x += step, i++) {
        const isMajor = i % majorEvery === 0;
        g.add(
          new Konva.Line({
            points: [x, 0, x, h],
            stroke: isMajor ? "rgba(255,215,0,0.60)" : "rgba(255,215,0,0.35)",
            strokeWidth: isMajor ? 5.5 : 4.5,
            listening: false,
          })
        );
      }

      for (let y = 0, i = 0; y <= h; y += step, i++) {
        const isMajor = i % majorEvery === 0;
        g.add(
          new Konva.Line({
            points: [0, y, w, y],
            stroke: isMajor ? "rgba(255,215,0,0.60)" : "rgba(255,215,0,0.35)",
            strokeWidth: isMajor ? 5.5 : 4.5,
            listening: false,
          })
        );
      }

      g.add(
        new Konva.Rect({
          x: PADDING_PX,
          y: PADDING_PX,
          width: w - PADDING_PX * 2,
          height: h - PADDING_PX * 2,
          stroke: "rgba(255,215,0,0.75)",
          strokeWidth: 4,
          listening: false,
        })
      );

      const guide = new Konva.Line({
        points: [w / 2, 0, w / 2, h],
        stroke: "rgba(255,215,0,0.95)",
        strokeWidth: 6,
        dash: [18, 14],
        listening: false,
        visible: false,
      });
      layer.add(guide);
      this.nodes.centerGuideLine = guide;

      const verseGuide = new Konva.Line({
        points: [0, h / 2, w, h / 2],
        stroke: "rgba(255,215,0,0.95)",
        strokeWidth: 5,
        listening: false,
        visible: false,
      });
      layer.add(verseGuide);
      this.nodes.verseBaselineGuideLine = verseGuide;

      const clipartCenterGuide = new Konva.Line({
        points: [w / 2, 0, w / 2, h],
        stroke: "rgba(255,215,0,0.95)",
        strokeWidth: 5,
        listening: false,
        visible: false,
      });
      layer.add(clipartCenterGuide);
      this.nodes.clipartCenterGuideLine = clipartCenterGuide;

      if (!this.__pamelaCenterGuideStageBound && this.stage) {
        this.stage.off("dragstart.pamelaGuide dragmove.pamelaGuide dragend.pamelaGuide");

        this.stage.on("dragstart.pamelaGuide", (e) => {
          const n = e?.target;
          if (n && pamelaIsTextLikeNode(n)) {
            this._showCenterGuideForNode(n);
          }
          this._showHorizontalAlignGuideForNode(n);
          this._showClipartCenterGuideForNode(n);
        });

        this.stage.on("dragmove.pamelaGuide", (e) => {
          const n = e?.target;
          if (n && pamelaIsTextLikeNode(n)) {
            this._showCenterGuideForNode(n);
          }
          this._showHorizontalAlignGuideForNode(n);
          this._showClipartCenterGuideForNode(n);
        });

        this.stage.on("dragend.pamelaGuide", () => {
          this._hideCenterGuide();
        });

        this.__pamelaCenterGuideStageBound = true;
      }

      if (!this.__pamelaVerticalGuideReleaseBound) {
        this.__pamelaVerticalGuideReleaseBound = true;
        const hideVerticalGuide = () => {
          try {
            this.nodes?.clipartCenterGuideLine?.visible?.(false);
            this.nodes?.mobileLaserSnapXGuide?.visible?.(false);
            this.layers?.grid?.batchDraw?.();
            this.layers?.ui?.batchDraw?.();
          } catch (_) {}
        };
        const hideVerticalGuideAfterRelease = () => {
          window.setTimeout(hideVerticalGuide, 0);
          requestAnimationFrame(() => requestAnimationFrame(hideVerticalGuide));
        };
        window.addEventListener("pointerup", hideVerticalGuideAfterRelease, true);
        window.addEventListener("pointercancel", hideVerticalGuideAfterRelease, true);
        window.addEventListener("mouseup", hideVerticalGuideAfterRelease, true);
        window.addEventListener("touchend", hideVerticalGuideAfterRelease, true);
        window.addEventListener("touchcancel", hideVerticalGuideAfterRelease, true);
        window.addEventListener("blur", hideVerticalGuideAfterRelease, true);
      }

      this._setGridVisible(false);
      layer.draw();
    }

    _setGridVisible(on) {
      if (!this.nodes.gridGroup) return;
      this.nodes.gridGroup.visible(!!on);
      this.layers.grid.draw();
    }

    _showCenterGuideForNode(node) {
      try {
        const guide = this.nodes.centerGuideLine;
        if (!guide || !node || !pamelaIsTextLikeNode(node)) return;

        const type = String(node.attrs?.pamelaFieldType || "");
        const w = this.state.design.width;
        const h = this.state.design.height;

        if (!(type === "lastname" || type === "name" || type === "date" || type === "verse" || type === "wedding")) {
          guide.visible(false);
          this.layers.grid.batchDraw();
          return;
        }

        const isCompanion = this.layout === "companion";
        const { fullCenter } = pamelaGetCenters(this);
        const leftCenter = w * 0.25;
        const rightCenter = w * 0.75;

        let targetX = fullCenter;
        const panelMatch = pamelaGetNearestPanelCenterX(this, node);
        const textMatch = pamelaGetNearestTextCenterXMatch(this, node);
        const currentX = pamelaGetNodeCenterX(node);

        if (type === "lastname") {
          targetX = fullCenter;
        } else if (type === "name" && textMatch && textMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
          targetX = textMatch.centerX;
        } else if (panelMatch && panelMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
          targetX = panelMatch.centerX;
        } else if (textMatch && textMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
          targetX = textMatch.centerX;
        } else if (!isCompanion) {
          targetX = fullCenter;
        } else {
          targetX = currentX < fullCenter ? leftCenter : rightCenter;
        }

        if (Math.abs(currentX - targetX) <= PAMELA_SNAP_THRESHOLD_PX) {
          guide.points([targetX, 0, targetX, h]);
          guide.visible(true);
        } else {
          guide.visible(false);
        }

        this.layers.grid.batchDraw();
      } catch (_) {}
    }

    _showHorizontalAlignGuideForNode(node) {
      try {
        const guide = this.nodes.verseBaselineGuideLine;
        if (!guide || !node) return;
        const w = this.state.design.width;
        const portraitCenterMatch = pamelaGetPortraitCenterSnap(this, node);
        if (portraitCenterMatch) {
          if (portraitCenterMatch.snapY) {
            guide.points([0, portraitCenterMatch.centerY, w, portraitCenterMatch.centerY]);
            guide.visible(true);
          } else {
            guide.visible(false);
          }
          this.layers.grid.batchDraw();
          return;
        }

        const singleCenterMatch = pamelaGetSingleBackgroundCenterSnap(this, node);
        if (singleCenterMatch && singleCenterMatch.snapY) {
          guide.points([0, singleCenterMatch.centerY, w, singleCenterMatch.centerY]);
          guide.visible(true);
          this.layers.grid.batchDraw();
          return;
        }

        if (!pamelaIsGuideAlignNode(node)) {
          guide.visible(false);
          this.layers.grid.batchDraw();
          return;
        }

        const match = pamelaGetNearestHorizontalAlignment(this, node);
        if (match && match.absDeltaY <= pamelaGetEffectiveSnapThreshold(this, PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX)) {
          guide.points([0, match.alignY, w, match.alignY]);
          guide.visible(true);
        } else {
          guide.visible(false);
        }

        this.layers.grid.batchDraw();
      } catch (_) {}
    }

    _showClipartCenterGuideForNode(node) {
      try {
        const guide = this.nodes.clipartCenterGuideLine;
        if (!guide || !node) return;
        const h = this.state.design.height;
        const cls = node.getClassName?.() || node.className || "";
        const isClipOrPanel =
          cls === "Image" &&
          (node.getAttr?.("pamelaClipart") === true || node.getAttr?.("pamelaPanel") === true || pamelaIsLaserArtworkNode(node));
        const portraitCenterMatch = pamelaGetPortraitCenterSnap(this, node);
        if (!isClipOrPanel && !portraitCenterMatch) {
          guide.visible(false);
          this.layers.grid.batchDraw();
          return;
        }

        if (portraitCenterMatch) {
          if (portraitCenterMatch.snapX) {
            guide.points([portraitCenterMatch.centerX, 0, portraitCenterMatch.centerX, h]);
            guide.visible(true);
          } else {
            guide.visible(false);
          }
          this.layers.grid.batchDraw();
          return;
        }

        if (pamelaIsPanelLikeImageNode(node)) {
          const panelMatch = pamelaGetPanelCenterXMatch(this, node);
          if (panelMatch && panelMatch.absDeltaX <= PAMELA_SNAP_THRESHOLD_PX) {
            guide.points([panelMatch.centerX, 0, panelMatch.centerX, h]);
            guide.visible(true);
          } else {
            guide.visible(false);
          }
          this.layers.grid.batchDraw();
          return;
        }

        const singleCenterMatch = pamelaGetSingleBackgroundCenterSnap(this, node);
        if (singleCenterMatch && singleCenterMatch.snapX) {
          guide.points([singleCenterMatch.centerX, 0, singleCenterMatch.centerX, h]);
          guide.visible(true);
          this.layers.grid.batchDraw();
          return;
        }

        if (pamelaIsLaserArtworkNode(node)) {
          const { fullCenter } = pamelaGetCenters(this);
          const currentX = pamelaGetNodeCenterX(node);
          if (
            Number.isFinite(fullCenter) &&
            Number.isFinite(currentX) &&
            Math.abs(currentX - fullCenter) <= pamelaGetEffectiveSnapThreshold(this, PAMELA_SNAP_THRESHOLD_PX)
          ) {
            guide.points([fullCenter, 0, fullCenter, h]);
            guide.visible(true);
          } else {
            guide.visible(false);
          }
          this.layers.grid.batchDraw();
          return;
        }

        const match = pamelaGetNearestClipartCenterAlignment(this, node);
        if (match && match.absDeltaX <= pamelaGetEffectiveSnapThreshold(this, PAMELA_VERSE_BASELINE_SNAP_THRESHOLD_PX)) {
          guide.points([match.alignX, 0, match.alignX, h]);
          guide.visible(true);
        } else {
          guide.visible(false);
        }

        this.layers.grid.batchDraw();
      } catch (_) {}
    }

    _hideCenterGuide() {
      try {
        const guide = this.nodes.centerGuideLine;
        const verseGuide = this.nodes.verseBaselineGuideLine;
        const clipartGuide = this.nodes.clipartCenterGuideLine;
        if (guide) guide.visible(false);
        if (verseGuide) verseGuide.visible(false);
        if (clipartGuide) clipartGuide.visible(false);
        this.layers.grid.batchDraw();
        window.PAMELA_hideMobileLaserSnapGuides?.(this);
      } catch (_) {}
    }
    

    // -----------------------------
    // Variant-driven text color
    // -----------------------------
   setTextFill(fill) {
  this.currentTextFill = fill || "#111";

  const contentLayers = pamelaGetSideLayers(this, "content");
  contentLayers.forEach((layer) => {
    const nodes = layer.getChildren((n) => n && pamelaIsTextLikeNode(n));
    nodes.forEach((n) => {
      // record the variant-default fill so we can revert when not overlapping
      n.setAttr("pamelaVariantDefaultFill", this.currentTextFill);
      n.fill(this.currentTextFill);
    });
    layer.batchDraw();
  });

  // Ã¢Å“â€¦ If default is white, force black ONLY where overlapping panel/clipart
  if (this._scheduleOverlapColors) this._scheduleOverlapColors();
}


// -----------------------------
// Background
// -----------------------------
async setBackgroundImage(url, opts = {}) {
  const resetView = opts.resetView === true;
  const mirrorX = opts.mirrorX === true;

  setStatus(this.wrapEl, "");
  const img = await loadImage(url);
  if (!img) throw new Error("Image failed to load.");
  const letteringRect = pamelaDetectLetteringRectFromImage(img);

  if (!this.nodes.bg) {

    const bgW = img.naturalWidth;
    const bgH = img.naturalHeight;
    const bgX = (this.state.design.width - bgW) / 2;
    const bgY = (this.state.design.height - bgH) / 2;
    const drawX = mirrorX ? bgX + bgW : bgX;

    this.nodes.bg = new Konva.Image({
      image: img,
      x: drawX,
      y: bgY,
      width: bgW,
      height: bgH,
      scaleX: mirrorX ? -1 : 1,
      listening: false,
      perfectDrawEnabled: false,
    });

    this.nodes.bg.setAttr("pamelaIsBg", true);
    this.nodes.bg.setAttr("pamelaMirrorX", mirrorX);
    this.nodes.bg.setAttr("pamelaLetteringRect", letteringRect);
    this.layers.background.add(this.nodes.bg);

  } else {
    const bgW = img.naturalWidth;
    const bgH = img.naturalHeight;
    const bgX = (this.state.design.width - bgW) / 2;
    const bgY = (this.state.design.height - bgH) / 2;
    const drawX = mirrorX ? bgX + bgW : bgX;

    this.nodes.bg.image(img);
    this.nodes.bg.x(drawX);
    this.nodes.bg.y(bgY);
    this.nodes.bg.width(bgW);
    this.nodes.bg.height(bgH);
    this.nodes.bg.scaleX(mirrorX ? -1 : 1);
    this.nodes.bg.setAttr("pamelaMirrorX", mirrorX);
    this.nodes.bg.setAttr("pamelaLetteringRect", letteringRect);
  }

  this._resize(resetView);
  this.layers.background.batchDraw();
  setStatus(this.wrapEl, "");
}

// -----------------------------
// Border Overlay (TOP, click-through, above content, below grid + transformer)
// -----------------------------
async setBorderOverlayImage(url, opts = {}) {
  try {
    const u = String(url || "").trim();
    const mirrorX = opts.mirrorX === true;

    // If no URL, remove any existing overlay cleanly
    if (!u) {
      if (this.nodes.borderOverlay) {
        this.nodes.borderOverlay.destroy();
        this.nodes.borderOverlay = null;
        this.layers.border?.batchDraw();
        if (this.nodes.tr) this.nodes.tr.moveToTop();
        this.layers.ui?.batchDraw();
      }
      return;
    }

    const img = await loadImage(u);
    if (!img) return;

    const ovW = img.naturalWidth;
    const ovH = img.naturalHeight;
    const ovX = (this.state.design.width - ovW) / 2;
    const ovY = (this.state.design.height - ovH) / 2;
    const drawX = mirrorX ? ovX + ovW : ovX;

    // Create once
    if (!this.nodes.borderOverlay) {
      this.nodes.borderOverlay = new Konva.Image({
        image: img,
        x: drawX,
        y: ovY,
        width: ovW,
        height: ovH,
        scaleX: mirrorX ? -1 : 1,
        listening: false,
        perfectDrawEnabled: false,
      });

      this.nodes.borderOverlay.setAttr("pamelaIsBorderOverlay", true);
      this.nodes.borderOverlay.setAttr("pamelaMirrorX", mirrorX);

      if (this.layers.border) this.layers.border.add(this.nodes.borderOverlay);
    } else {
      this.nodes.borderOverlay.image(img);
      this.nodes.borderOverlay.x(drawX);
      this.nodes.borderOverlay.y(ovY);
      this.nodes.borderOverlay.width(ovW);
      this.nodes.borderOverlay.height(ovH);
      this.nodes.borderOverlay.scaleX(mirrorX ? -1 : 1);
      this.nodes.borderOverlay.setAttr("pamelaMirrorX", mirrorX);
    }

    this.nodes.borderOverlay.moveToTop();
    this.layers.border?.batchDraw();

    if (this.nodes.tr) this.nodes.tr.moveToTop();
    this.layers.ui?.batchDraw();

  } catch (e) {
    console.error("[BorderOverlay] Failed:", e);
  }
}
  // -----------------------------
  // Keep Text above Panels/Clipart (within content layer)
  // -----------------------------
  _pamelaRestackContentLayer() {
    try {
      const layer = this.layers?.content;
      if (!layer) return;

      const kids = layer.getChildren() || [];
      if (!kids.length) return;

      const images = [];
      const portraits = [];
      const texts = [];

      const isPortraitishNode = (n) => {
        try {
          const a = n.attrs || {};
          if (a.__isPortraitGroup === true) return true;
          if (a.__pamelaKind === "portrait") return true;
          if (a.pamelaType === "portrait") return true;
          if (a.pamelaPortrait === true) return true;
          if (typeof n.name === "function") {
            const nm = String(n.name() || "");
            if (nm.includes("pamela-portrait-group")) return true;
            if (nm.includes("pamela-portrait-inner")) return true;
            if (nm.includes("pamela-hit")) return true;
            if (nm.includes("pamela-portrait-photo")) return true;
          }
        } catch (_) {}
        return false;
      };

      kids.forEach((n) => {
        if (!n) return;
        if (pamelaIsTextLikeNode(n)) texts.push(n);
        else if (isPortraitishNode(n)) portraits.push(n); // porcelain/laser portrait wrappers stay above other images
        else images.push(n); // panels + clipart + laser scenes (and any other images)
      });

      // Stacking order (bottom -> top): images, then portraits, then text
      images.forEach((n, i) => n.zIndex(i));
      portraits.forEach((n, i) => n.zIndex(images.length + i));
      texts.forEach((n, i) => n.zIndex(images.length + portraits.length + i));

      layer.batchDraw();
    } catch (_) {}
  }

// -----------------------------
// Selection (Text + Clipart + Panels + Portraits)
// -----------------------------
selectNode(node) {

  this.selected = node || null;
  if (!node) {
    try { this.__cmSelectedPortraitWrapper = null; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__CM_LAST_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.pamelaForceHidePortraitImagesToolbar?.(this); } catch (_) {}
  }
  try {
    const topToolbar = document.getElementById("pamela-editor-toolbar");
    if (topToolbar) delete topToolbar.dataset.mobileGroupManual;
  } catch (_) {}
  try {
    const isSelectedLaser =
      this.selected &&
      (this.selected.getClassName?.() || this.selected.className) === "Image" &&
      (this.selected.getAttr?.("pamelaLaserEtching") === true || this.selected.attrs?.pamelaLaserEtching === true);
    if (isSelectedLaser) {
      window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ = this.selected;
    } else {
      window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ = null;
    }
  } catch (_) {}

  if (this.groupedTextSide) {
    if (!pamelaIsGroupedSideTextActive(this, this.selected)) {
      this.groupedTextSide = null;
    }
  }

  if (!pamelaCanMoveSideIndependently(this, this.selected)) {
    this.horizontalMirrorIndependentMode = false;
  }

  // Fix text metrics when selecting text
  if (this.selected && this.selected.className === "Text") {
    pamelaFixTextMetrics(this.selected);
  }

  // ---------- helpers ----------
  const isPortraitNode = (n) => {

    try {

      if (!n) return false;

      // Any of these signals = portrait system
      const a = n.attrs || {};

      if (a.__isPortraitGroup === true) return true;
      if (a.__pamelaKind === "portrait") return true;
      if (a.pamelaType === "portrait") return true;
      if (a.pamelaPortrait === true) return true;

      // Name-based fallback
      if (typeof n.name === "function") {

        const nm = String(n.name() || "");

        if (nm.includes("pamela-portrait-group")) return true;
        if (nm.includes("pamela-portrait-inner")) return true;
        if (nm.includes("pamela-hit")) return true;
        if (nm.includes("pamela-portrait-photo")) return true;

      }

    } catch (_) {}

    return false;

  };

  const isPortraitWrapperNode = (n) => {
    try {
      if (!n) return false;
      const a = n.attrs || {};
      if (a.__isPortraitGroup === true) return true;
      if (typeof n.name === "function") {
        const nm = String(n.name() || "");
        if (nm.includes("pamela-portrait-group")) return true;
      }
    } catch (_) {}
    return false;
  };

  const findPortraitWrapperUp = (n) => {

    let cur = n;
    let hops = 0;

    while (cur && hops < 14) {

      if (isPortraitWrapperNode(cur)) return cur;

      try {
        cur = cur.getParent && cur.getParent();
      } catch (_) {
        cur = null;
      }

      hops++;
    }

    return null;

  };

  // ---------- detect edit-mode selection (portrait transformer) ----------
let portraitTR = null;
let portraitTRNode = null;

try {
  const st = this.stage;
  portraitTR = st && st.findOne && st.findOne("#cmPortraitTransformer");
  const nodes = portraitTR && portraitTR.nodes && portraitTR.nodes();
  portraitTRNode = nodes && nodes[0] ? nodes[0] : null;
} catch (_) {}

// ---------- Identify panels ----------
const isPanel = !!(
  this.selected &&
  this.selected.className === "Image" &&
  this.selected.attrs?.pamelaPanel === true
);

// Identify "Last Name" panels (single-slot group)
const panelGroup =
  isPanel && typeof this.selected.getAttr === "function"
    ? String(this.selected.getAttr("pamelaPanelGroup") || "")
    : "";

const isLastNamePanel = isPanel && panelGroup === "lastname";

// ---------- Portrait logic ----------
// If we're in portrait EDIT mode (photo transformer has a node),
// NEVER let the mask get a normal transformer (prevents Ã¢â‚¬Å“both selectedÃ¢â‚¬Â ghosting + mask rotate handle).
// Clicking the mask while editing should simply keep edit-mode active on the photo.
if (portraitTRNode) {
  const pw = findPortraitWrapperUp(node);

  // If the new target is still inside the same portrait edit flow,
  // keep portrait edit mode active and do not attach normal transformers.
  if (pw) {
    this.nodes.tr?.nodes([]);
    this.nodes.trPanel?.nodes([]);

    try { portraitTR.visible(true); } catch (_) {}
    try { portraitTR.forceUpdate?.(); } catch (_) {}
    try { portraitTR.getLayer?.()?.batchDraw?.(); } catch (_) {}

    this.selected = pw;
    try { this.__cmSelectedPortraitWrapper = pw; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = pw; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = pw; } catch (_) {}
    try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}

    this.layers.ui?.batchDraw?.();
    this.refreshButtons?.();
    this.refreshSizeButtons?.();
    this._refreshClipButtons?.();
    this._refreshFontUI?.();
    pamelaSyncMobileToolbars(this);
    pamelaRefreshImagesToolbar(this);
    this._pamelaRestackContentLayer?.();
    return;
  }

  // Otherwise, user clicked something that is NOT part of the portrait,
  // so exit portrait edit mode first and allow normal selection to continue.
  try { portraitTR.nodes([]); } catch (_) {}
  try { portraitTR.visible(false); } catch (_) {}
  try { portraitTR.forceUpdate?.(); } catch (_) {}
  try { portraitTR.getLayer?.()?.batchDraw?.(); } catch (_) {}
  try { this.__cmSelectedPortraitWrapper = null; } catch (_) {}
  try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
  try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}

  try {
    const handles = this.stage?.find?.(".cm-portrait-move-handle") || [];
    handles.forEach((h) => {
      try { h.visible(false); } catch (_) {}
    });
    const handleLayer = handles[0]?.getLayer?.();
    if (handleLayer?.batchDraw) handleLayer.batchDraw();
    else if (handleLayer?.draw) handleLayer.draw();
  } catch (_) {}
}

// If not in edit mode: detect if selection is a portrait wrapper / portrait part
const portraitWrapper = findPortraitWrapperUp(node);
const isPortrait = !!portraitWrapper;

// Clear both transformers first
this.nodes.tr?.nodes([]);
this.nodes.trPanel?.nodes([]);

// --- transformer defaults (prevents portrait logic from "poisoning" other selections) ---
const ALL_ANCHORS = [
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];
const CORNER_ANCHORS = ["top-left", "top-right", "bottom-left", "bottom-right"];

function normalizeTR(tr) {
  if (!tr) return;
  try { tr.rotateEnabled && tr.rotateEnabled(true); } catch (_) {}
  try { tr.rotationEnabled && tr.rotationEnabled(true); } catch (_) {}
  try { tr.borderEnabled && tr.borderEnabled(true); } catch (_) {}
  try { tr.enabledAnchors && tr.enabledAnchors(ALL_ANCHORS); } catch (_) {}
}

function normalizeTRPanel(trp) {
  if (!trp) return;
  try { trp.rotateEnabled && trp.rotateEnabled(true); } catch (_) {}
  try { trp.rotationEnabled && trp.rotationEnabled(true); } catch (_) {}
  try { trp.borderEnabled && trp.borderEnabled(true); } catch (_) {}
}

if (node) {

  if (isPortrait) {

    // Ã¢Å“â€¦ Portrait mask/wrapper: MOVE ONLY (blue box ok), NO rotation, NO resize
    if (this.nodes.tr) {

      normalizeTR(this.nodes.tr);

      try { this.nodes.tr.enabledAnchors && this.nodes.tr.enabledAnchors([]); } catch (_) {}
      try { this.nodes.tr.rotateEnabled && this.nodes.tr.rotateEnabled(false); } catch (_) {}
      try { this.nodes.tr.rotationEnabled && this.nodes.tr.rotationEnabled(false); } catch (_) {}

      this.nodes.tr.nodes([portraitWrapper]);

    } else {

      normalizeTRPanel(this.nodes.trPanel);

      try { this.nodes.trPanel.enabledAnchors && this.nodes.trPanel.enabledAnchors([]); } catch (_) {}
      try { this.nodes.trPanel.rotateEnabled && this.nodes.trPanel.rotateEnabled(false); } catch (_) {}
      try { this.nodes.trPanel.rotationEnabled && this.nodes.trPanel.rotationEnabled(false); } catch (_) {}

      this.nodes.trPanel?.nodes([portraitWrapper]);
    }

    this.selected = portraitWrapper;
    try { this.__cmSelectedPortraitWrapper = portraitWrapper; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = portraitWrapper; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = portraitWrapper; } catch (_) {}
    try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}

  } else if (isPanel) {
    try { this.__cmSelectedPortraitWrapper = null; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}

    // Ã¢Å“â€¦ Panels: rotate always allowed
    normalizeTRPanel(this.nodes.trPanel);

    if (this.nodes.trPanel && typeof this.nodes.trPanel.enabledAnchors === "function") {
      this.nodes.trPanel.enabledAnchors(
        isLastNamePanel ? ["middle-left", "middle-right"] : ALL_ANCHORS
      );
    }

    try { this.nodes.trPanel?.rotateEnabled?.(true); } catch (_) {}
    try { this.nodes.trPanel?.rotationEnabled?.(true); } catch (_) {}

    this.nodes.trPanel?.nodes([node]);

  } else {
    try { this.__cmSelectedPortraitWrapper = null; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}

    normalizeTR(this.nodes.tr);

    const cls =
      (node && (node.getClassName ? node.getClassName() : node.className)) || "";

    const isTextNode = (cls === "Text" || cls === "TextPath");
    const isLaserArtwork = node?.attrs?.pamelaLaserEtching === true || node?.getAttr?.("pamelaLaserEtching") === true;

    const isClipart = !isTextNode;

    if (isTextNode) {

      try { this.nodes.tr.enabledAnchors && this.nodes.tr.enabledAnchors([]); } catch (_) {}

    } else if (isLaserArtwork) {

      try { this.nodes.tr.keepRatio && this.nodes.tr.keepRatio(true); } catch (_) {}
      try { this.nodes.tr.enabledAnchors && this.nodes.tr.enabledAnchors(CORNER_ANCHORS); } catch (_) {}
      try { this.nodes.tr.rotateEnabled && this.nodes.tr.rotateEnabled(true); } catch (_) {}
      try { this.nodes.tr.rotationEnabled && this.nodes.tr.rotationEnabled(true); } catch (_) {}

    } else if (isClipart) {

      try { this.nodes.tr.enabledAnchors && this.nodes.tr.enabledAnchors([]); } catch (_) {}
      try { this.nodes.tr.rotateEnabled && this.nodes.tr.rotateEnabled(true); } catch (_) {}
      try { this.nodes.tr.rotationEnabled && this.nodes.tr.rotationEnabled(true); } catch (_) {}

    }

    this.nodes.tr?.nodes([node]);
  }
}

// Ã¢Å“â€¦ CRITICAL: prevents first-handle drag from snapping
try { this.nodes.tr?.forceUpdate(); } catch (_) {}
try { this.nodes.trPanel?.forceUpdate(); } catch (_) {}

this.layers.ui?.batchDraw?.();

this.refreshButtons?.();
this.refreshSizeButtons?.();
this._refreshClipButtons?.();
this._refreshFontUI?.();
try { pamelaRefreshCurveTextMenuState(this); } catch (_) {}
pamelaRefreshIndependentMoveUI(this);
pamelaRefreshTextGroupUI(this);
pamelaRefreshTextGroupOverlay(this);
pamelaSyncMobileToolbars(this);
pamelaRefreshImagesToolbar(this);
this._pamelaRestackContentLayer?.();
try {
  if (node?.getAttr?.("pamelaLaserEtching") === true) {
    window.__PAMELA_SELECTED_LASER_NODE__ = node;
    syncLaserSizeFieldsFromArtwork(node);
    requestAnimationFrame(() => {
      try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    });
    setTimeout(() => {
      try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    }, 60);
  } else {
    window.PAMELA_refreshLaserUi?.();
  }
} catch (_) {}

// Keep transformers on top
this.nodes.tr?.moveToTop?.();
this.nodes.trPanel?.moveToTop?.();

this.layers.ui?.batchDraw?.();
}

// Direct Pointer Drag (Fixes: wonÃ¢â‚¬â„¢t drag on first click, jump-on-release, sticky drag)
// - Works in WORLD coords (respects your view scale/x/y)
// - Uses ONLY pointer events (no mousedown/touchstart duplicates)
// - Does NOT use Konva drag engine at all
// -----------------------------
_enableDirectPointerDrag(node, layerName) {

  if (!node || !this.stage) return;

  // Never let KonvaÃ¢â‚¬â„¢s internal drag engine compete
  try { node.draggable(false); } catch (_) {}

  // Prevent double-binding
  try { node.off("pointerdown.pamelaDirectDrag"); } catch (_) {}

  const getWorldPointer = () => {
    const p = this.stage.getPointerPosition();
    if (!p) return null;

    const s = this.state.view.scale || 1;
    const vx = this.state.view.x || 0;
    const vy = this.state.view.y || 0;

    return {
      x: (p.x - vx) / s,
      y: (p.y - vy) / s,
    };
  };

  const drawLayer = () => {

    const lyr =
      layerName === "panels"
        ? this.layers.panels
        : layerName === "content"
        ? this.layers.content
        : this.layers.content;

    try { lyr?.batchDraw?.(); } catch (_) {}
    try { this.layers.ui?.batchDraw?.(); } catch (_) {}
  };

  node.on("pointerdown.pamelaDirectDrag", (e) => {

    try { e.cancelBubble = true; } catch (_) {}

    // DonÃ¢â‚¬â„¢t move objects while Pan Mode is on
    if (this.panMode) return;
    if (this._multiTouchGestureActive) return;

    // Ignore right-click
    const evt = e?.evt;
    if (evt && typeof evt.button === "number" && evt.button === 2) return;

    // Prevent browser gestures on touch
    if (evt && evt.cancelable && evt.pointerType === "touch") {
      try { evt.preventDefault(); } catch (_) {}
    }

    // Select immediately on first down
    this.selectNode(node);
    if (pamelaIsPanelKnockoutBlocker(node)) {
      try { pamelaRestorePanelKnockouts(this); } catch (_) {}
    }
        try {
      const isPanelNow =
        node &&
        node.className === "Image" &&
        node.attrs?.pamelaPanel === true;

      if (isPanelNow) {
        try { this.nodes?.trPanel?.forceUpdate?.(); } catch (_) {}
      } else {
        try { this.nodes?.tr?.forceUpdate?.(); } catch (_) {}
      }

      try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
    } catch (_) {}

    const startWorld = getWorldPointer();
    if (!startWorld) return;

    const startX = node.x();
    const startY = node.y();

    // Ã¢Å“â€¦ Save FULL pickup state (fixes mirrored items)
    node._pamelaPickupPos = {
      x: startX,
      y: startY,
      sx: node.scaleX(),
      sy: node.scaleY(),
    };
    node._pamelaMirrorDrag = pamelaBuildHorizontalMirrorDragState(this, node);
    node._pamelaTextGroupDrag = pamelaBuildTextGroupDragState(this, node);

    const pid = evt?.pointerId;

    // Pointer capture MUST be on the real event target (canvas), not the wrap div
    const targetEl = evt?.target;

    if (pid != null && targetEl && typeof targetEl.setPointerCapture === "function") {
      try { targetEl.setPointerCapture(pid); } catch (_) {}
    }

    let raf = null;
    let lastWorld = startWorld;
    let lastShift = !!evt?.shiftKey || !!this.horizontalMirrorShiftKeyDown;
    let moved = false;

    const onMove = (ev) => {

      if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;

      // Prevent scroll/gesture while dragging
      if (ev.cancelable && ev.pointerType === "touch") {
        try { ev.preventDefault(); } catch (_) {}
      }

      const w = getWorldPointer();
      if (!w) return;

      lastWorld = w;
      lastShift = !!ev?.shiftKey || !!this.horizontalMirrorShiftKeyDown;

      if (raf) return;

      raf = requestAnimationFrame(() => {

        raf = null;

        const dx = (lastWorld.x - startWorld.x);
        const dy = (lastWorld.y - startWorld.y);

        // Track whether anything actually moved (prevents pointless history commits)
        if (Math.abs(dx) > 0.0001 || Math.abs(dy) > 0.0001) moved = true;

        node.x(startX + dx);
        node.y(startY + dy);

        // Clamp inside safe area
        pamelaClampNodeIntoSafeRect(this, node);

        if (pamelaIsTextLikeNode(node)) {
          if (!node._pamelaTextGroupDrag && pamelaIsGroupedSideTextActive(this, node)) {
            node._pamelaTextGroupDrag = pamelaBuildTextGroupDragState(this, node);
          }
          if (node._pamelaTextGroupDrag) {
            pamelaApplyTextGroupDrag(this, node._pamelaTextGroupDrag, dx, dy);
            try { pamelaUpdateAllTextColorsForOverlap(this); } catch (_) {}
            pamelaRefreshTextGroupOverlay(this);
          } else {
          const independentTextDrag = pamelaShouldMoveSideIndependently(this, lastShift);

          pamelaMaybeSnapTextToCenterX(this, node, { skipDateSnap: independentTextDrag });
          pamelaSnapNodeHorizontalAlignment(this, node);
          pamelaApplyHorizontalMirrorDrag(
            this,
            node,
            node._pamelaMirrorDrag,
            dx,
            dy,
            independentTextDrag
          );
          this._showCenterGuideForNode(node);
          this._showHorizontalAlignGuideForNode(node);

          pamelaAutoPairLinkFromDrag(this, node);
          pamelaEnforceMirrorPairSeparation(this);
          pamelaTryRelinkHorizontalMirror(this);

          pamelaUpdateOneTextColorForOverlap(this, node);
          }

        } else if (node.className === "Image") {
          const isLaserDrag = pamelaIsLaserArtworkNode(node);
          const snappedY = pamelaSnapNodeHorizontalAlignment(this, node);
          let snappedX = false;
          if (pamelaIsPanelLikeImageNode(node)) {
            pamelaSnapPanelToNearestTextCenter(this, node);
          } else {
            snappedX = pamelaSnapClipartCenterXAlignment(this, node);
            if (isLaserDrag) {
              snappedX = pamelaSnapLaserArtworkCenterX(this, node) || snappedX;
            }
          }
          pamelaSnapImageToSingleBackgroundCenter(this, node);
          this._showHorizontalAlignGuideForNode(node);
          this._showClipartCenterGuideForNode(node);
          if (isLaserDrag) {
            const singleCenterMatch = pamelaGetSingleBackgroundCenterSnap(this, node);
            const guideSnappedX = snappedX || singleCenterMatch?.snapX === true;
            const guideSnappedY = snappedY || singleCenterMatch?.snapY === true;
            window.PAMELA_showMobileLaserSnapGuides?.(
              this,
              node,
              guideSnappedX,
              guideSnappedY
            );
          }

          // Clipart/Panel/Laser overlap-color recompute is expensive (scans
          // every text node against every image). Deferred to dragend below
          // instead of running on every frame while dragging.

        }

        // Draw during move (smooth)
        drawLayer();
      });
    };

    const end = (ev) => {

      if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;

      try { targetEl?.removeEventListener?.("pointermove", onMove, true); } catch (_) {}
      try { targetEl?.removeEventListener?.("pointerup", end, true); } catch (_) {}
      try { targetEl?.removeEventListener?.("pointercancel", end, true); } catch (_) {}

      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);


      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }

      // Release pointer capture
      if (pid != null && targetEl && typeof targetEl.releasePointerCapture === "function") {
        try { targetEl.releasePointerCapture(pid); } catch (_) {}
      }

      // Finalize
      pamelaClampNodeIntoSafeRect(this, node);

      if (pamelaIsTextLikeNode(node)) {
        if (node._pamelaTextGroupDrag) {
          const groupedNodes = pamelaGetSideTextGroupNodes(this, node._pamelaTextGroupDrag.side);
          groupedNodes.forEach((n) => {
            try { pamelaFixTextMetrics(n); } catch (_) {}
            try { pamelaClampNodeIntoSafeRect(this, n); } catch (_) {}
          });
          try { pamelaUpdateAllTextColorsForOverlap(this); } catch (_) {}
          pamelaRefreshTextGroupOverlay(this);
        } else {
        const independentTextDrag = pamelaShouldMoveSideIndependently(this, lastShift);

        if (node.className === "Text") pamelaFixTextMetrics(node);
        pamelaMaybeSnapTextToCenterX(this, node, { skipDateSnap: independentTextDrag });
        pamelaSnapNodeHorizontalAlignment(this, node);
        pamelaApplyHorizontalMirrorDrag(
          this,
          node,
          node._pamelaMirrorDrag,
          node.x() - startX,
          node.y() - startY,
          independentTextDrag
        );
        pamelaAutoPairLinkFromDrag(this, node);
        pamelaEnforceMirrorPairSeparation(this);
        pamelaTryRelinkHorizontalMirror(this);
        pamelaEnforceSpacingForText(this);
        pamelaUpdateOneTextColorForOverlap(this, node);
        }

      } else {
        pamelaSnapNodeHorizontalAlignment(this, node);
        if (pamelaIsPanelLikeImageNode(node)) {
          pamelaSnapPanelToNearestTextCenter(this, node);
        } else {
          pamelaSnapClipartCenterXAlignment(this, node);
          if (pamelaIsLaserArtworkNode(node)) {
            pamelaSnapLaserArtworkCenterX(this, node);
          }
        }
        pamelaSnapImageToSingleBackgroundCenter(this, node);
        if (node.getAttr?.("pamelaPanel") === true) {
          pamelaClampNodeIntoSafeRect(this, node);
        }

        this._scheduleOverlapColors && this._scheduleOverlapColors();
        if (pamelaIsPanelKnockoutBlocker(node)) {
          try { pamelaRebuildPanelKnockouts(this); } catch (_) {}
        }
      }

      // Safety: ensure Konva never stays in a drag state
      pamelaForceReleaseKonvaDrag();
      if (pamelaIsLaserArtworkNode(node)) {
        window.PAMELA_hideMobileLaserSnapGuides?.(this);
        try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
        try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
      }

      if (moved && node._pamelaPickupPos) {

// Ã¢Å“â€¦ Snapshot full transform so mirrored items undo correctly
const fx = node.x();
const fy = node.y();
const fsx = node.scaleX();
const fsy = node.scaleY();

// --- commit pickup state ---
node.x(node._pamelaPickupPos.x);
node.y(node._pamelaPickupPos.y);
node.scaleX(node._pamelaPickupPos.sx);
node.scaleY(node._pamelaPickupPos.sy);

//this._historyCommit("move"); // pickup state

// --- commit final state ---
node.x(fx);
node.y(fy);
node.scaleX(fsx);
node.scaleY(fsy);

this._historyCommit("move"); // final state
}

delete node._pamelaPickupPos;
delete node._pamelaMirrorDrag;
delete node._pamelaTextGroupDrag;
if (this.__pamelaActiveDirectDragCancel === cancelDirectDrag) {
  this.__pamelaActiveDirectDragCancel = null;
}
try { this._hideCenterGuide(); } catch (_) {}

};

const cancelDirectDrag = () => {
  try { end({ pointerId: pid }); } catch (_) {}
};
this.__pamelaActiveDirectDragCancel = cancelDirectDrag;

// Capture phase so nothing can swallow events
try { targetEl?.addEventListener?.("pointermove", onMove, { passive: false, capture: true }); } catch (_) {}
try { targetEl?.addEventListener?.("pointerup", end, true); } catch (_) {}
try { targetEl?.addEventListener?.("pointercancel", end, true); } catch (_) {}

    window.addEventListener("pointermove", onMove, { passive: false, capture: true });
window.addEventListener("pointerup", end, true);
window.addEventListener("pointercancel", end, true);

});
}

_refreshFontUI() {
  const fontSel = document.getElementById("pamela-font-select");
  if (!fontSel) return;

  if (!this.selected || !pamelaIsTextLikeNode(this.selected)) {
    fontSel.innerHTML = `<option value="">Select text to change font</option>`;
    fontSel.disabled = true;
    fontSel.style.opacity = "0.6";
    return;
  }

  const type = this.selected.attrs?.pamelaFieldType;
  const allowed =
    type === "verse" || type === "wedding" ? PAMELA_LIMITED_FONTS : PAMELA_ALL_FONTS;

  fontSel.innerHTML = "";
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "Choose font...";
  fontSel.appendChild(placeholder);

  allowed.forEach((f) => {
    const opt = document.createElement("option");
    opt.value = f;
    opt.textContent = f;
    fontSel.appendChild(opt);
  });

  const groupFont =
    type === "lastname"
      ? this.fontState.lastname
      : type === "name" || type === "date"
      ? this.fontState.namedate
      : type === "wedding"
      ? this.fontState.wedding
      : type === "verse"
      ? this.fontState.verse
      : this.selected.fontFamily?.();

  fontSel.value = groupFont && allowed.includes(groupFont) ? groupFont : "";
  fontSel.disabled = false;
  fontSel.style.opacity = "1";
}

_wireHorizontalMirrorShiftKeyOnce() {
  if (this.__pamelaMirrorShiftKeyWired) return;
  this.__pamelaMirrorShiftKeyWired = true;

  const setShift = (down) => {
    this.horizontalMirrorShiftKeyDown = !!down;
  };

  window.addEventListener(
    "keydown",
    (e) => {
      if (String(e.key || "") === "Shift") setShift(true);
    },
    true
  );

  window.addEventListener(
    "keyup",
    (e) => {
      if (String(e.key || "") === "Shift") setShift(false);
    },
    true
  );

  window.addEventListener(
    "blur",
    () => setShift(false),
    true
  );
}

countField(fieldType) {

return this.layers.content
.getChildren((n) => n?.attrs?.pamelaFieldType === fieldType)
.length;

}

maxFor(fieldType) {

const spec = FIELD_SPECS[fieldType];
const m = spec?.max?.[this.layout];

return typeof m === "number" ? m : 0;

}

canAdd(fieldType) {

return this.countField(fieldType) < this.maxFor(fieldType);

}

refreshButtons() {
const selectedPortraitWrapper = this.__cmSelectedPortraitWrapper || null;
const hasSelection = !!(this.selected || selectedPortraitWrapper);
const selectedClass = this.selected?.getClassName?.() || this.selected?.className || "";
const canMirrorClone =
  selectedClass === "Image" &&
  (
    this.selected?.attrs?.pamelaPanel === true ||
    this.selected?.attrs?.pamelaClipart === true ||
    (
      this.selected?.attrs?.pamelaLaserEtching === true &&
      String(this.selected?.attrs?.pamelaLaserKind || "spot").toLowerCase() === "spot"
    )
  );

// Ã¢Å“â€¦ BACK view: never allow FRONT add-text buttons to re-enable
try {

if (window.PAMELA_SIDES && window.PAMELA_SIDES.current === "back") {

setBtnEnabled("pamela-add-lastname", false);
setBtnEnabled("pamela-add-name", false);
setBtnEnabled("pamela-add-date", false);
setBtnEnabled("pamela-add-wedding", false);
setBtnEnabled("pamela-add-verse", false);

setBtnEnabled("pamela-delete", hasSelection);
setBtnEnabled("pamela-mirror-clone-btn", canMirrorClone);
pamelaUpdateStartGuidance(this);
pamelaRefreshIndependentMoveUI(this);
pamelaRefreshTextGroupUI(this);
pamelaSyncMobileToolbars(this);

return;

}

} catch (_) {

// If anything about window/PAMELA_SIDES is weird during boot, fail open to FRONT logic

}

// Ã¢Å“â€¦ FRONT logic
const btnLastname = document.getElementById("pamela-add-lastname");
const btnWedding = document.getElementById("pamela-add-wedding");
const btnName = document.getElementById("pamela-add-name");

if (btnLastname) {
  btnLastname.style.display = this.layout === "single" ? "none" : "";
}
if (btnWedding) {
  btnWedding.style.display = this.layout === "single" ? "none" : "";
}
if (btnName) {
  const addNameTip =
    this.layout === "companion"
      ? "Add up to 2 lines for the name on each side"
      : "Add up to 2 lines for the name";
  btnName.setAttribute("data-pamela-tip", addNameTip);
  btnName.setAttribute("aria-label", addNameTip);
}

setBtnEnabled("pamela-add-lastname", this.layout === "companion" && this.canAdd("lastname"));
setBtnEnabled("pamela-add-name", this.canAdd("name"));
setBtnEnabled("pamela-add-date", this.canAdd("date"));
setBtnEnabled("pamela-add-wedding", this.layout === "companion" && this.canAdd("wedding"));
setBtnEnabled("pamela-add-verse", this.canAdd("verse"));

setBtnEnabled("pamela-delete", hasSelection);
setBtnEnabled("pamela-mirror-clone-btn", canMirrorClone);
pamelaUpdateStartGuidance(this);
pamelaRefreshIndependentMoveUI(this);
pamelaRefreshTextGroupUI(this);
pamelaSyncMobileToolbars(this);
}

refreshSizeButtons() {

// Enable text size buttons ONLY when editable text is selected
if (!this.selected || !pamelaIsTextLikeNode(this.selected)) {

setBtnEnabled("pamela-size-up", false);
setBtnEnabled("pamela-size-down", false);

return;

}

const n = this.selected;

// ==========================================================
// Ã¢Å“â€¦ SPECIAL RULE: BACK VERSE (enable A-/A+ correctly)
  // - Base: 0.75 in
  // - + once only => 1.00 in
  // - - once only => back to 0.75 in (never smaller)
  // ==========================================================
  const nm = (typeof n.name === "function") ? String(n.name() || "") : String(n.attrs?.name || "");
  const id = (typeof n.id === "function") ? String(n.id() || "") : String(n.attrs?.id || "");
  const isBackVerse = (nm === "__cm_back_verse" || id === "__cm_back_verse");
  const isSingleBackLastName = pamelaIsSingleBackLastNameNode(n, this);

  if (isSingleBackLastName) {
    if (pamelaNormalizeSingleBackLastNameDefault(n, this)) {
      pamelaFixTextMetrics(n);
      pamelaClampNodeIntoSafeRect(this, n);
      pamelaSnapTextToCenterX(this, n);
      try { this.layers.content?.batchDraw?.(); } catch (_) {}
    }

    let curIn = pamelaCapHeightInForTextNode(n);
    if (curIn > PAMELA_SINGLE_BACK_LASTNAME_MAX_IN + 0.001) {
      pamelaApplyCapHeightInToTextNode(n, PAMELA_SINGLE_BACK_LASTNAME_MAX_IN);
      pamelaFixTextMetrics(n);
      pamelaClampNodeIntoSafeRect(this, n);
      pamelaSnapTextToCenterX(this, n);
      try { this.layers.content?.batchDraw?.(); } catch (_) {}
      curIn = PAMELA_SINGLE_BACK_LASTNAME_MAX_IN;
    }
    setBtnEnabled("pamela-size-up", curIn < PAMELA_SINGLE_BACK_LASTNAME_MAX_IN - 0.001);
    setBtnEnabled("pamela-size-down", curIn > PAMELA_SINGLE_BACK_LASTNAME_MIN_IN + 0.001);
    return;
  }

  if (isBackVerse) {
    const BASE_IN = 0.75;
    const STEP_IN = 0.25;
    const MAX_IN  = BASE_IN + STEP_IN; // 1.00 in

    const bumped = !!n.getAttr?.("__cmBackVerseBumped");
    const curIn = inFromPx(n.fontSize());

    // Up is enabled only if NOT bumped yet
    const canUp = !bumped && curIn < (MAX_IN - 0.001);

    // Down is enabled only if bumped and currently above base
    const canDown = bumped && curIn > (BASE_IN + 0.001);

    setBtnEnabled("pamela-size-up", canUp);
    setBtnEnabled("pamela-size-down", canDown);
    return;
  }

  // ==========================================================
  // Ã¢Å“â€¦ NORMAL RULES (unchanged)
  // ==========================================================
  const type = n.attrs?.pamelaFieldType;
  const spec = type ? FIELD_SPECS?.[type] : null;

  // If a field is explicitly marked non-resizable, honor it
  if (spec && spec.resizable === false) {
    setBtnEnabled("pamela-size-up", false);
    setBtnEnabled("pamela-size-down", false);
    return;
  }

  // If pamelaFieldType is missing, fall back to a safe general range
  const minIn = typeof spec?.minIn === "number" ? spec.minIn : 0.25;
  const maxIn = typeof spec?.maxIn === "number" ? spec.maxIn : 6.0;
  const baseIn = typeof spec?.sizeIn === "number" ? spec.sizeIn : minIn;

  const sizeTargets = [n];
  if (type === "date") {
    const allDates = this.layers?.content?.getChildren?.(
      (node) =>
        node &&
        node.className === "Text" &&
        String(node.attrs?.pamelaFieldType || "") === "date"
    ) || [];
    sizeTargets.length = 0;
    sizeTargets.push(...allDates);
  } else if (type === "name" && this.layout === "companion") {
    const slot = Number(n.getAttr?.("pamelaSlotIndex"));
    const mateSlot = Number.isFinite(slot) ? pamelaMateSlotFor(slot) : null;
    const mate =
      mateSlot == null
        ? null
        : pamelaFindTextNodeByTypeSlot(this, "name", mateSlot);
    if (mate && mate !== n) sizeTargets.push(mate);
  }

  const sizeValuesIn = sizeTargets
    .map((node) => {
      try {
        return inFromPx(Number(node?.fontSize?.() || 0) || 0);
      } catch (_) {
        return NaN;
      }
    })
    .filter((v) => Number.isFinite(v));

  const largestCurIn = sizeValuesIn.length ? Math.max(...sizeValuesIn) : inFromPx(n.fontSize());
  const smallestCurIn = sizeValuesIn.length ? Math.min(...sizeValuesIn) : inFromPx(n.fontSize());

  const canUp = smallestCurIn < maxIn - 0.001;
  const canDown = largestCurIn > minIn + 0.001 || largestCurIn > baseIn + 0.001;

  setBtnEnabled("pamela-size-up", canUp);
  setBtnEnabled("pamela-size-down", canDown);
}

// -----------------------------
// Text size buttons (A- / A+)
// -----------------------------
bumpSize(dir) {
  const n = this.selected;
  if (!n || !pamelaIsTextLikeNode(n)) return;

  // ==========================================================
  // Ã¢Å“â€¦ SPECIAL RULE: BACK VERSE
  // - Enter/newlines handled by your textarea (already correct)
  // - Base: 0.75 in
  // - + once only => 1.00 in
  // - - once only => back to 0.75 in (never smaller)
  // ==========================================================
  const nm = (typeof n.name === "function") ? String(n.name() || "") : String(n.attrs?.name || "");
  const id = (typeof n.id === "function") ? String(n.id() || "") : String(n.attrs?.id || "");
  const isBackVerse = (nm === "__cm_back_verse" || id === "__cm_back_verse");
  const isSingleBackLastName = pamelaIsSingleBackLastNameNode(n, this);

  if (isSingleBackLastName) {
    const curIn = pamelaCapHeightInForTextNode(n);
    const nextIn = clamp(
      curIn + (dir > 0 ? PAMELA_SINGLE_BACK_LASTNAME_STEP_IN : -PAMELA_SINGLE_BACK_LASTNAME_STEP_IN),
      PAMELA_SINGLE_BACK_LASTNAME_MIN_IN,
      PAMELA_SINGLE_BACK_LASTNAME_MAX_IN
    );

    if (Math.abs(nextIn - curIn) < 0.0001) {
      this.refreshSizeButtons?.();
      return;
    }

    pamelaApplyCapHeightInToTextNode(n, nextIn);
    try { n.setAttr("__cmBackLastNameSizeTouched", true); } catch (_) {}
    pamelaFixTextMetrics(n);
    pamelaClampNodeIntoSafeRect(this, n);
    pamelaSnapTextToCenterX(this, n);
    pamelaEnforceSpacingForText(this);
    pamelaUpdateOneTextColorForOverlap(this, n);

    this.layers.content?.batchDraw?.();
    this.selectNode(n);
    this.refreshSizeButtons?.();
    this._historyCommit?.("resize");
    return;
  }

  if (isBackVerse) {
    const BASE_IN = 0.75;
    const STEP_IN = 0.25;
    const MAX_IN  = BASE_IN + STEP_IN; // 1.00 in

    // Track whether we've already allowed the +1 step
    const bumped = !!n.getAttr?.("__cmBackVerseBumped");

    const curIn = inFromPx(n.fontSize());
    let nextIn = curIn;

    if (dir > 0) {
      // Allow increase only once
      if (!bumped && curIn < MAX_IN - 0.0001) {
        nextIn = MAX_IN;
        try { n.setAttr("__cmBackVerseBumped", true); } catch (_) {}
      } else {
        this.refreshSizeButtons?.();
        return;
      }
    } else {
      // Allow decrease only if previously bumped; never below base
      if (bumped && curIn > BASE_IN + 0.0001) {
        nextIn = BASE_IN;
        try { n.setAttr("__cmBackVerseBumped", false); } catch (_) {}
      } else {
        this.refreshSizeButtons?.();
        return;
      }
    }

    // Apply
    n.fontSize(pxFromIn(nextIn));

    // Ã¢Å“â€¦ enforce 0.5" line gap (via lineHeight) for back verse
    this.CM_applyBackVerseLineSpacing(n);

    // Re-measure + keep inside safe area + keep your rules consistent
    pamelaFixTextMetrics(n);
    pamelaClampNodeIntoSafeRect(this, n);
    pamelaEnforceSpacingForText(this);
    pamelaUpdateOneTextColorForOverlap(this, n);

    this.layers.content?.batchDraw?.();

    // Keep transformer + UI in sync
    this.selectNode(n);
    this.refreshSizeButtons?.();

    // Track as a resize for undo
    this._historyCommit?.("resize");
    return;
  }

  // ==========================================================
  // Ã¢Å“â€¦ NORMAL RULES (unchanged)
  // ==========================================================
  const type = n.attrs?.pamelaFieldType;
  const spec = type ? FIELD_SPECS?.[type] : null;

  // Honor "non-resizable" fields
  if (spec && spec.resizable === false) {
    this.refreshSizeButtons?.();
    return;
  }

  const stepIn = typeof spec?.stepIn === "number" ? spec.stepIn : 0.25;
  const minIn  = typeof spec?.minIn === "number" ? spec.minIn : 0.25;
  const maxIn  = typeof spec?.maxIn === "number" ? spec.maxIn : 6.0;

  const curIn = inFromPx(n.fontSize());
  const nextIn = clamp(curIn + (dir > 0 ? stepIn : -stepIn), minIn, maxIn);

  // If weÃ¢â‚¬â„¢re already at the limit, do nothing
  if (Math.abs(nextIn - curIn) < 0.0001) {
    this.refreshSizeButtons?.();
    return;
  }

  const resizeTargets = [n];
  if (type === "date") {
    const allDates = this.layers?.content?.getChildren?.(
      (node) =>
        node &&
        node.className === "Text" &&
        String(node.attrs?.pamelaFieldType || "") === "date"
    ) || [];
    resizeTargets.length = 0;
    resizeTargets.push(...allDates);
  } else if (type === "name" && this.layout === "companion") {
    const slot = Number(n.getAttr?.("pamelaSlotIndex"));
    const mateSlot = Number.isFinite(slot) ? pamelaMateSlotFor(slot) : null;
    const mate =
      mateSlot == null
        ? null
        : pamelaFindTextNodeByTypeSlot(this, "name", mateSlot);
    if (mate && mate !== n) resizeTargets.push(mate);
  }

  resizeTargets.forEach((node) => {
    try { node.fontSize(pxFromIn(nextIn)); } catch (_) {}
    try { pamelaFixTextMetrics(node); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(this, node); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(this, node); } catch (_) {}
  });

  pamelaEnforceSpacingForText(this);

  this.layers.content?.batchDraw?.();

  // Keep transformer + UI in sync
  this.selectNode(n);
  this.refreshSizeButtons?.();

  // Track as a resize for undo
  this._historyCommit?.("resize");
}

CM_applyBackVerseLineSpacing(t) {
  try {
    if (!t || t.className !== "Text") return;

    const nm = (typeof t.name === "function") ? String(t.name() || "") : String(t.attrs?.name || "");
    const id = (typeof t.id === "function") ? String(t.id() || "") : String(t.attrs?.id || "");
    const isBackVerse = (nm === "__cm_back_verse" || id === "__cm_back_verse");
    if (!isBackVerse) return;

    const fsPx = Number(t.fontSize?.() || 0);
    if (!fsPx) return;

    const gapPx = pxFromIn(0.5);
    const minLH = 1 + (gapPx / fsPx);

    const cur = Number(t.lineHeight?.() || 1.0);
    t.lineHeight(Math.max(cur, minLH));
  } catch (_) {}
}

_refreshClipButtons() {
  
  const sel = this.selected;

  const isText = !!(sel && pamelaIsTextLikeNode(sel));
  const isImage = !!(sel && sel.className === "Image");

  const isClipOrPanel =
    !!(isImage && (
      sel.attrs?.pamelaClipart === true ||
      sel.attrs?.pamelaPanel === true ||
      pamelaIsLaserArtworkNode(sel)
    ));

  const setBtn = (id, enabled) => {
    const b = document.getElementById(id);
    if (!b) return;
    b.disabled = !enabled;
    b.style.opacity = enabled ? "1" : "0.35";
    b.style.cursor = enabled ? "pointer" : "not-allowed";
  };

  // These always stay enabled (they open the libraries)
  //closePanelsUI();
  setBtn("pamela-view-clipart", true);
  setBtn("pamela-panels-frames", true);

  // ROTATE: enable for Text + Clipart + Panels + eligible Portraits
  let portraitEditWrapperForButtons = null;
  try {
    const st = this.stage;
    const tr = st && st.findOne && st.findOne("#cmPortraitTransformer");
    const nodes = tr && tr.nodes && tr.nodes();
    const portraitPhoto = nodes && nodes[0] ? nodes[0] : null;
    portraitEditWrapperForButtons =
      (typeof findPortraitWrapperUp === "function" && portraitPhoto)
        ? findPortraitWrapperUp(portraitPhoto)
        : null;
  } catch (_) {}

  const portraitWrapperForButtons =
    portraitEditWrapperForButtons ||
    this.__cmSelectedPortraitWrapper ||
    window.__CM_SELECTED_PORTRAIT_WRAPPER__ ||
    window.__CM_LAST_PORTRAIT_WRAPPER__ ||
    (
      (sel?.attrs?.pamelaType === "portrait" || sel?.getAttr?.("__isPortraitGroup") === true)
        ? sel
        : (typeof findPortraitWrapperUp === "function" ? findPortraitWrapperUp(sel) : null)
    );

  const portraitShape =
    String(
      portraitWrapperForButtons?.getAttr?.("__portraitShape") ||
      portraitWrapperForButtons?.getAttr?.("shape") ||
      portraitWrapperForButtons?.getAttr?.("pamelaPortraitShape") ||
      portraitWrapperForButtons?.attrs?.__portraitShape ||
      portraitWrapperForButtons?.attrs?.shape ||
      portraitWrapperForButtons?.attrs?.pamelaPortraitShape ||
      ""
    ).toLowerCase();

  const canRotatePortrait90 =
    !!portraitWrapperForButtons &&
    (portraitShape === "oval" || portraitShape === "rect" || portraitShape === "rectangle");


  // Ã¢Å“â€¦ AUTOSAVE (safe + throttled)
  if (!window.__pamelaLastSaveTriggerTime) {
    window.__pamelaLastSaveTriggerTime = 0;
  }

  const now = Date.now();

  if (now - window.__pamelaLastSaveTriggerTime > 2000) {
    window.__pamelaLastSaveTriggerTime = now;

    try {
      pamelaQueueDraftSave();
    } catch (_) {}
  }

  setBtn("pamela-rot-left", isText || isClipOrPanel || canRotatePortrait90);
  setBtn("pamela-rot-right", isText || isClipOrPanel || canRotatePortrait90);
  setBtn("pamela-nudge-toggle", isText || isClipOrPanel);
  setBtn("pamela-nudge-left", isText || isClipOrPanel);
  setBtn("pamela-nudge-up", isText || isClipOrPanel);
  setBtn("pamela-nudge-down", isText || isClipOrPanel);
  setBtn("pamela-nudge-right", isText || isClipOrPanel);
  setBtn("pamela-follow-shape", !!(isText || (sel && sel.className === "TextPath")));
  const shapeBtn = document.getElementById("pamela-follow-shape");
  if (shapeBtn && isText) {
    shapeBtn.textContent = "Curve Text";
  } else if (shapeBtn) {
    shapeBtn.textContent = "Curve Text";
  }

  const laserTextStyleBtn = document.getElementById("pamela-laser-text-style");
  const canReverseLaserText = pamelaCanReverseSelectedLaserText(this);
  if (laserTextStyleBtn) {
    laserTextStyleBtn.style.display = canReverseLaserText ? "" : "none";
    laserTextStyleBtn.setAttribute("aria-hidden", canReverseLaserText ? "false" : "true");
    laserTextStyleBtn.textContent =
      sel?.getAttr?.("pamelaLaserTextReversed") === true
        ? "Use White Text"
        : "Use Black Text";
  }
  setBtn("pamela-laser-text-style", canReverseLaserText);

  // MIRROR (Flip X): enable ONLY for Images (clipart/panels), NOT text
  setBtn("pamela-flip-x", isClipOrPanel);

  // Clip size +/-: ONLY for Images (clipart/panels)
  setBtn("pamela-clip-size-up", isClipOrPanel);

  if (!isClipOrPanel) {
    setBtn("pamela-clip-size-down", false);
    return;
  }

  let atMin = true;
  if (sel.getAttr && sel.getAttr("pamelaPanel") === true) {
    const baseW = Number(sel.getAttr("pamelaPanelBaseWidth") || sel.width?.() || 0);
    const baseH = Number(sel.getAttr("pamelaPanelBaseHeight") || sel.height?.() || 0);
    const curW = Number(sel.width?.() || 0);
    const curH = Number(sel.height?.() || 0);
    atMin = curW <= baseW + 1e-6 && curH <= baseH + 1e-6;
  } else {
    const base = Number(sel.getAttr("pamelaClipartBaseScale") || 1);
    const magX = Math.abs(sel.scaleX() || 1);
    const magY = Math.abs(sel.scaleY() || 1);
    atMin = magX <= base + 1e-6 && magY <= base + 1e-6;
  }

  setBtn("pamela-clip-size-down", !atMin);
}

// -----------------------------
// Text fields
// -----------------------------
addField(fieldType) {

  if (!FIELD_SPECS[fieldType]) return;
  if (!this.canAdd(fieldType)) return;

  const spec = FIELD_SPECS[fieldType];

  const defaultText =
    fieldType === "lastname"
      ? "LastName"
      : fieldType === "name"
      ? "Name"
      : fieldType === "date"
      ? "Date"
      : fieldType === "wedding"
      ? "Wedding Date"
      : fieldType === "verse"
      ? "Verse text"
      : "Text";

  const START_Y_RATIO = {
    lastname: 0.18,
    name: 0.32,
    date: 0.4,
    wedding: 0.55,
    verse: 0.62,
  };

  const LASTNAME_DROP_PX = 180; // adjust anytime
  const NAME_DATE_DROP_PX = 288; // 4 yellow lines (4 inches * 72dpi)
  const RIGHT_VERSE_NUDGE_PX = 18; // raise RIGHT verse slightly (adjust anytime)

  const w = this.state?.design?.width || DESIGN_PX.w;
  const h = this.state?.design?.height || DESIGN_PX.h;

  const canvasSafe = pamelaGetSafeRect(w, h);

  // ---------------------------------------------------------
  // Monument-relative landing zone
  // - If background monument exists, land relative to it
  // - If not, fall back to canvas safe rect
  // ---------------------------------------------------------
  const bg = this.nodes?.bg;
  const detectedLetteringRect =
    this.layout === "single"
      ? pamelaGetLetteringRectForBackground(bg)
      : null;
  const bgRect = pamelaGetImageNodeDrawRect(bg);

  const monumentRect =
    detectedLetteringRect ||
    bgRect || {
      left: canvasSafe.left,
      top: canvasSafe.top,
      width: canvasSafe.right - canvasSafe.left,
      height: canvasSafe.bottom - canvasSafe.top,
      right: canvasSafe.right,
      bottom: canvasSafe.bottom,
    };

  // Inner monument-safe area so text does not land too close to tablet edges
  const monumentInsetX = monumentRect.width * 0.08;
  const monumentInsetTop = monumentRect.height * 0.10;
  const monumentInsetBottom = monumentRect.height * 0.12;

  const safe = {
    left: monumentRect.left + monumentInsetX,
    right: monumentRect.right - monumentInsetX,
    top: monumentRect.top + monumentInsetTop,
    bottom: monumentRect.bottom - monumentInsetBottom,
  };

  const fullCenter = monumentRect.left + monumentRect.width / 2;

  const SIDE_CENTER_T = 0.62; // bigger = closer to center

  const leftCenter =
    safe.left + (fullCenter - safe.left) * SIDE_CENTER_T;

  const rightCenter =
    safe.right - (safe.right - fullCenter) * SIDE_CENTER_T;

  const maxSlots = this.maxFor(fieldType);

  let slotIndex = 0;
  let startX = fullCenter;
  let companionPanelAnchor = null;

  // SINGLE: keep Name / Date / Verse horizontally centered
  if (this.layout === "single" && (fieldType === "name" || fieldType === "date" || fieldType === "verse")) {

    slotIndex = pamelaNextSlotIndex(this, fieldType, maxSlots);
    startX = fullCenter;

  }

  // Companion NAME/DATE pattern: L, R, L, R
  if (this.layout === "companion" && (fieldType === "name" || fieldType === "date")) {

    slotIndex = pamelaNextSlotIndex(this, fieldType, maxSlots);

    startX = slotIndex % 2 === 0
      ? leftCenter
      : rightCenter;

    companionPanelAnchor = pamelaGetPanelAnchorForSide(this, slotIndex % 2 === 0 ? "left" : "right");
    if (companionPanelAnchor) startX = companionPanelAnchor.x;
  }

  // Companion verse pattern: Center, Left, Right
  if (fieldType === "verse" && this.layout === "companion") {

    slotIndex = pamelaNextSlotIndex(this, fieldType, maxSlots);

    startX =
      slotIndex === 1
        ? leftCenter
        : slotIndex === 2
        ? rightCenter
        : fullCenter;
  }

  // Default landing Y
  let startY =
    monumentRect.top +
    monumentRect.height *
    (START_Y_RATIO[fieldType] ?? 0.3);

  // =========================================================
  // SINGLE layout fixed stack order:
  // Verse
  // Name
  // Name
  // Date
  // Date
  // Verse
  // =========================================================
  if (this.layout === "single") {
    const singleStack = {
      verse: [0.18, 0.72], // top verse, bottom verse
      name:  [0.32, 0.44],
      date:  [0.56, 0.66],
    };

    const ratios = singleStack[fieldType];

    if (ratios && Number.isInteger(slotIndex) && ratios[slotIndex] != null) {
      startY = monumentRect.top + monumentRect.height * ratios[slotIndex];
    }
  }

  if (fieldType === "date" && this.layout === "single") {
    const layer = this.layers?.content;
    const existingDates = layer
      ? layer.getChildren((n) => pamelaIsDateTextNode(n))
      : [];
    const existingNames = layer
      ? layer.getChildren(
          (n) =>
            n &&
            pamelaIsTextLikeNode(n) &&
            String(n.attrs?.pamelaFieldType || "") === "name"
        )
      : [];

    const rectOf = (node) =>
      node.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

    const bottomItemOf = (nodes) => {
      let best = null;
      for (const n of nodes || []) {
        if (!n || !pamelaIsTextLikeNode(n)) continue;
        pamelaFixTextMetrics(n);
        const r = rectOf(n);
        const bottom = r.y + r.height;
        if (!best || bottom > best.bottom) best = { node: n, rect: r, bottom };
      }
      return best;
    };

    if (slotIndex === 0) {
      const lowestName = bottomItemOf(existingNames);
      if (lowestName) {
        startX = Number(lowestName.node.x?.() ?? startX) || startX;
        startY = lowestName.bottom + MIN_TEXT_GAP_PX;
      }
    } else if (existingDates.length) {
      const firstDate = existingDates[0];
      pamelaFixTextMetrics(firstDate);
      const firstRect = rectOf(firstDate);

      if (pamelaDateIsStacked(firstDate)) {
        startX = (2 * fullCenter) - (Number(firstDate.x?.()) || fullCenter);
        startY = Number(firstDate.y?.() ?? startY) || startY;
      } else {
        startX = Number(firstDate.x?.() ?? startX) || startX;
        startY = firstRect.y + firstRect.height + MIN_TEXT_GAP_PX;
      }
    }
  }

  // Companion-only verse side tweak
  if (fieldType === "verse" && this.layout === "companion" && slotIndex === 2)
    startY -= RIGHT_VERSE_NUDGE_PX;

  if (fieldType === "lastname")
    startY += LASTNAME_DROP_PX;

  // Companion-only drop for initial NAME/DATE placements
  if (this.layout === "companion" && (fieldType === "name" || fieldType === "date"))
    startY += NAME_DATE_DROP_PX;

  if (companionPanelAnchor && this.layout === "companion" && (fieldType === "name" || fieldType === "date")) {
    startX = companionPanelAnchor.x;
    startY = companionPanelAnchor.y;
  }

// ---------------------------------------------------------
// Companion NAME placement:
// - First right-side name mirrors moved Name0 instead of using the default.
// - Later names align under the lowest existing name on their own side.
// ---------------------------------------------------------
if (fieldType === "name" && this.layout === "companion") {

  const layer = this.layers?.content;
  const targetSide =
    slotIndex % 2 === 0
      ? "left"
      : "right";

  if (layer) {
    const rectOf = (n) =>
      n.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

    const allTexts =
      layer.getChildren((n) => n && pamelaIsTextLikeNode(n));

    const namesOnSide = allTexts.filter(
      (n) =>
        n.attrs?.pamelaFieldType === "name" &&
        pamelaGetSide(this, n) === targetSide
    );

    let lowestName = null;
    for (const n of namesOnSide) {
      pamelaFixTextMetrics(n);
      const r = rectOf(n);
      const b = r.y + r.height;
      if (!lowestName || b > lowestName.bottom) {
        lowestName = { node: n, rect: r, bottom: b };
      }
    }

    if (lowestName) {
      startX = Number(lowestName.node.x?.() ?? startX) || startX;
      startY = lowestName.bottom + MIN_TEXT_GAP_PX;
    } else if (!companionPanelAnchor) {
      const mateSlot = pamelaMateSlotFor(slotIndex);
      const mateName =
        mateSlot == null
          ? null
          : pamelaFindTextNodeByTypeSlot(this, "name", mateSlot);

      if (mateName) {
        startX = (2 * fullCenter) - (Number(mateName.x?.()) || 0);
        startY = Number(mateName.y?.() ?? startY) || startY;
      }
    }
  }
}
  
// ---------------------------------------------------------
// Ã¢Å“â€¦ FIX: DATE ordering per side (prevents pushing older dates)
// Rule:
// - If a date already exists on that side, land below the lowest date on that side.
// - Otherwise, land below the lowest name on that side (so first date sits under names).
// ---------------------------------------------------------
if (fieldType === "date" && this.layout === "companion") {

  const layer = this.layers?.content;
  const slotName = pamelaFindTextNodeByTypeSlot(this, "name", slotIndex);

  if (slotName && layer) {
    pamelaFixTextMetrics(slotName);
    const nameRect = slotName.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

    startX = Number(slotName.x?.() ?? startX) || startX;
    startY = nameRect.y + nameRect.height + MIN_TEXT_GAP_PX;
  }

  const targetSide =
    slotIndex % 2 === 0
      ? "left"
      : "right";

  const rectOf = (n) =>
    n.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

  const bottomItemOf = (nodes) => {

    let best = null;

    for (const n of nodes) {

      if (!n || !pamelaIsTextLikeNode(n)) continue;

      pamelaFixTextMetrics(n);

      const r = rectOf(n);
      const b = r.y + r.height;

      if (!best || b > best.bottom) best = { node: n, rect: r, bottom: b };
    }

    return best;
  };

  if (layer) {

    const allTexts =
      layer.getChildren((n) => n && pamelaIsTextLikeNode(n));

    const datesOnSide = allTexts.filter(
      (n) =>
        n.attrs?.pamelaFieldType === "date" &&
        pamelaGetSide(this, n) === targetSide
    );

    const namesOnSide = allTexts.filter(
      (n) =>
        n.attrs?.pamelaFieldType === "name" &&
        pamelaGetSide(this, n) === targetSide
    );

    const lowestDate = bottomItemOf(datesOnSide);
    const lowestName = bottomItemOf(namesOnSide);

    if (!slotName) {
      const xAnchor = lowestDate?.node || lowestName?.node || null;
      if (xAnchor) startX = Number(xAnchor.x?.() ?? startX) || startX;
    }

    if (lowestDate) {

      // Next date goes under existing date on same side
      startY = lowestDate.bottom + MIN_TEXT_GAP_PX;

    } else if (lowestName) {

      // First date always goes under the full names stack on that side
      startY = lowestName.bottom + MIN_TEXT_GAP_PX;
    }
  }
}

const t = new Konva.Text({

  x: startX,
  y: startY,
  text: defaultText,

  fontFamily:
    fieldType === "lastname"
      ? this.fontState.lastname
      : fieldType === "name" || fieldType === "date"
      ? this.fontState.namedate
      : fieldType === "wedding"
      ? this.fontState.wedding
      : fieldType === "verse"
      ? this.fontState.verse
      : "MDRoman",

  fontSize: pxFromIn(spec.sizeIn),

  lineHeight: typeof spec.lineHeight === "number" ? spec.lineHeight : 1,

  fill: this.currentTextFill,

  align: "center",

  draggable: false,

});

t.setAttr("pamelaFieldType", fieldType);
t.setAttr("pamelaSlotIndex", slotIndex);
if (fieldType === "date") {
  t.setAttr("pamelaDateFormat", "single");
  pamelaApplyDateLineSpacing(t);
}

this._ensurePamelaUid(t, "t");

// Verse lines: initially participate in auto-spacing, but become independent after user moves them once
if (fieldType === "verse") {

  t.setAttr("pamelaIndependent", false);
  t.setAttr("pamelaIndependentArmed", true);

}

this.layers.content.add(t);

pamelaFixTextMetrics(t);
pamelaEnsureNodeFontReady(this, t);
pamelaEnsureNodeFontReady(this, t, 400);

if (fieldType === "verse" && this.layout === "single" && slotIndex >= 2) {
  const layer = this.layers.content;
  const existingVerses = layer
    .getChildren(
      (n) =>
        n &&
        n !== t &&
        pamelaIsTextLikeNode(n) &&
        String(n.attrs?.pamelaFieldType || "") === "verse"
    );

  const rectOf = (n) =>
    n.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

  let lowestVerse = null;
  for (const n of existingVerses) {
    pamelaFixTextMetrics(n);
    const r = rectOf(n);
    const bottom = r.y + r.height;
    if (!lowestVerse || bottom > lowestVerse.bottom) {
      lowestVerse = { node: n, rect: r, bottom };
    }
  }

  if (lowestVerse) {
    const targetCenterX = pamelaGetNodeCenterX(lowestVerse.node);
    const currentCenterX = pamelaGetNodeCenterX(t);
    if (Number.isFinite(targetCenterX) && Number.isFinite(currentCenterX)) {
      t.x(t.x() + (targetCenterX - currentCenterX));
    }

    pamelaFixTextMetrics(t);
    const newRect = rectOf(t);
    const targetTop = lowestVerse.bottom + MIN_TEXT_GAP_PX;
    if (Number.isFinite(newRect?.y) && Number.isFinite(targetTop)) {
      t.y(t.y() + (targetTop - newRect.y));
    }
  }
}

if (fieldType === "name" && this.layout === "companion") {
  const layer = this.layers.content;
  const targetSide =
    slotIndex % 2 === 0
      ? "left"
      : "right";

  const rectOf = (n) =>
    n.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

  const allTexts =
    layer.getChildren((n) => n && pamelaIsTextLikeNode(n));

  const namesOnSide = allTexts.filter(
    (n) =>
      n !== t &&
      n.attrs?.pamelaFieldType === "name" &&
      pamelaGetSide(this, n) === targetSide
  );

  const datesOnSide = allTexts.filter(
    (n) =>
      n.attrs?.pamelaFieldType === "date" &&
      pamelaGetSide(this, n) === targetSide
  );

  if (namesOnSide.length) {
    let namesBottom = null;
    for (const n of namesOnSide) {
      pamelaFixTextMetrics(n);
      const r = rectOf(n);
      const b = r.y + r.height;
      if (namesBottom == null || b > namesBottom) namesBottom = b;
    }

    if (namesBottom != null) {
      t.y(namesBottom + MIN_TEXT_GAP_PX);
      pamelaFixTextMetrics(t);
    }
  }

  if (datesOnSide.length) {
    const nameRect = rectOf(t);
    const minDateTop = nameRect.y + nameRect.height + MIN_TEXT_GAP_PX;

    const dateItems = datesOnSide
      .map((n) => {
        pamelaFixTextMetrics(n);
        return { n, r: rectOf(n) };
      })
      .sort((a, b) => a.r.y - b.r.y);

    const firstDate = dateItems[0];
    if (firstDate && firstDate.r.y < minDateTop) {
      const shiftDown = minDateTop - firstDate.r.y;
      dateItems.forEach((it) => it.n.y(it.n.y() + shiftDown));
    }
  }
}

if (fieldType === "name" && this.layout === "single") {
  const layer = this.layers.content;

  const rectOf = (n) =>
    n.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

  const dateItems = layer
    .getChildren(
      (n) =>
        n &&
        pamelaIsTextLikeNode(n) &&
        String(n.attrs?.pamelaFieldType || "") === "date"
    )
    .map((n) => {
      pamelaFixTextMetrics(n);
      return { n, r: rectOf(n) };
    })
    .sort((a, b) => a.r.y - b.r.y);

  if (dateItems.length) {
    pamelaFixTextMetrics(t);
    const nameRect = rectOf(t);
    const minDateTop = nameRect.y + nameRect.height + MIN_TEXT_GAP_PX;
    const firstDate = dateItems[0];

    if (firstDate && firstDate.r.y < minDateTop) {
      const shiftDown = minDateTop - firstDate.r.y;
      dateItems.forEach((it) => it.n.y(it.n.y() + shiftDown));
    }
  }
}

// Clamp immediately into safe area
pamelaClampNodeIntoSafeRect(this, t);

// Auto-drop to avoid stacking (NAME/DATE/VERSE only)
if (pamelaShouldEnforceSpacing(t)) {

  const layer = this.layers.content;

  const maxY =
    this.state.design.height - PADDING_PX;

  let ok = false;
  let guard = 0;

  while (!ok && guard < 40) {

    guard++;

    pamelaFixTextMetrics(t);

    const r = t.getClientRect({
      relativeTo: layer,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false,
    });

    ok = true;

    const others =
      layer.getChildren(
        (n) =>
          n !== t &&
          pamelaIsTextLikeNode(n) &&
          pamelaShouldEnforceSpacing(n)
      );

    for (const o of others) {

      const or = o.getClientRect({
        relativeTo: layer,
        skipStroke: true,
        skipShadow: true,
        skipTransform: false,
      });

      const xOverlap =
        Math.min(r.x + r.width, or.x + or.width) - Math.max(r.x, or.x);

      if (xOverlap <= PAMELA_TEXT_STACK_X_OVERLAP_PX) continue;

      const overlap =
        r.y < or.y + or.height + MIN_TEXT_GAP_PX &&
        r.y + r.height > or.y - MIN_TEXT_GAP_PX;

      if (overlap) {

        t.y(or.y + or.height + MIN_TEXT_GAP_PX);

        ok = false;

        break;
      }
    }

    if (t.y() + r.height > maxY) break;
  }
}

pamelaSnapTextToCenterX(this, t);

pamelaClampNodeIntoSafeRect(this, t);

// Interaction
pamelaBindTextEditHandlers(this, t);

// Ã¢Å“â€¦ First-click drag fix (Text)
this._enableDirectPointerDrag(t, "content");

// Ã¢Å“â€¦ History: adding a text field must create its own undo step
this._historyCommit("add-text");

t.on("dragmove", () => {

  pamelaSnapTextToCenterX(this, t);
  pamelaApplyHorizontalMirrorDrag(this, t, t._pamelaMirrorDrag, 0, 0, pamelaShouldMoveSideIndependently(this, false));

  pamelaClampNodeIntoSafeRect(this, t);
  pamelaTryRelinkHorizontalMirror(this);

  pamelaUpdateOneTextColorForOverlap(this, t);

  this.layers.content.batchDraw();

});

t.on("dragend", () => {

  pamelaFixTextMetrics(t);

  pamelaSnapTextToCenterX(this, t);
  pamelaApplyHorizontalMirrorDrag(this, t, t._pamelaMirrorDrag, 0, 0, pamelaShouldMoveSideIndependently(this, false));

  pamelaClampNodeIntoSafeRect(this, t);
  pamelaTryRelinkHorizontalMirror(this);

  pamelaEnforceSpacingForText(this);

  pamelaUpdateOneTextColorForOverlap(this, t);

});

// Verse LEFT/RIGHT become independent after the first time the customer places them
if (t.getAttr("pamelaFieldType") === "verse") {

  const slot = t.getAttr("pamelaSlotIndex"); // 0=center, 1=left, 2=right

  if ((slot === 1 || slot === 2) && t.getAttr("pamelaIndependentArmed")) {

    t.setAttr("pamelaIndependent", true);

    t.setAttr("pamelaIndependentArmed", false);

  }
}

this.layers.content.draw();
this.layers.ui?.draw?.();

this.selectNode(t);

this.refreshButtons();
this.refreshSizeButtons();
this._refreshClipButtons();

pamelaEnforceSpacingForText(this);

this.layers.content.draw();
this.layers.ui?.draw?.();

if (this._scheduleOverlapColors)
  this._scheduleOverlapColors();
}

// =============================
// Panels (place onto PANELS layer)
// =============================
async addPanelFromUrl(url, meta = {}) {
  const newPanelMode = pamelaGetModeForNewPanel(this);
  const initialPanelMode =
    newPanelMode.explicit
      ? newPanelMode.panelMode
      : pamelaGetEffectivePanelMode(this);
  const img = await pamelaLoadSvgImageForGranite(url, initialPanelMode);
  if (!img) return;
  const textPathMeta = await pamelaLoadSvgTextPathMeta(url);
  const svgWidthIn = await pamelaLoadSvgWidthIn(url);
  let knockoutMaskImg = img;
  try { knockoutMaskImg = await pamelaLoadSvgImageForGranite(url, "standard"); } catch (_) {}

  const replaceGroup = String(meta?.replaceGroup || "");
  const requestedPanelGroup = String(meta?.panelGroup || "");

  // Ã¢Å“â€¦ If this is a "Last Name" panel, enforce single-slot: replace existing one(s)
  if (replaceGroup === "lastname") {
    try {
      const layer = this.layers?.panels;
      const kids = layer?.getChildren?.() || [];
      const existing = kids.filter((n) => {
        try {
          return n?.getAttr?.("pamelaPanelGroup") === "lastname";
        } catch (_) {
          return false;
        }
      });

      existing.forEach((n) => {
        try {
          if (this.selected === n) {
            try { this.selectNode(null); } catch (_) {}
          }
          n.destroy();
        } catch (_) {}
      });

      try { layer?.batchDraw?.(); } catch (_) {}
    } catch (e) {
      console.warn("[addPanelFromUrl] lastname replace cleanup failed:", e);
    }
  }

  const w = this.state.design.width;
  const h = this.state.design.height;
  const safe = pamelaGetSafeRect(w, h);
  const safeW = safe.right - safe.left;
  const safeH = safe.bottom - safe.top;

  // Start from natural pixel size, but never below minimums
  const SVG_TO_DESIGN_SCALE = 72 / 96;

  let startW = Math.max(PANEL_MIN_W_PX, Math.round(img.width * SVG_TO_DESIGN_SCALE));
  let startH = Math.max(PANEL_MIN_H_PX, Math.round(img.height * SVG_TO_DESIGN_SCALE));

  // Scale DOWN only if larger than safe area (never scale up)
  const fitScale = Math.min(1, safeW / startW, safeH / startH);
  startW = Math.max(PANEL_MIN_W_PX, Math.round(startW * fitScale));
  startH = Math.max(PANEL_MIN_H_PX, Math.round(startH * fitScale));
  const sliceBaseW = startW;
  const sliceBaseH = startH;
  const panelWidthIn = (() => {
    const metaWidthIn = pamelaGetPanelMetaWidthIn(meta);
    if (Number.isFinite(metaWidthIn) && metaWidthIn > 0) return metaWidthIn;
    if (Number.isFinite(svgWidthIn) && svgWidthIn > 0) return svgWidthIn;
    return startW / DPI;
  })();

  // Default drop = safe center (center-anchored placement)
  const safeCx = (safe.left + safe.right) / 2;
  const safeCy = (safe.top + safe.bottom) / 2;
  let dropX = Math.round(safeCx);
  let dropY = Math.round(safeCy);
  let normalPanelSlot = "";
  const centerWidePanel = !replaceGroup && panelWidthIn > PANEL_CENTER_IF_WIDER_THAN_IN;

  if (centerWidePanel) {
    normalPanelSlot = "center";
  } else if (!replaceGroup && this.layout === "companion") {
    try {
      const targetSide = this.__pamelaNextNameDatePanelSlot === "right" ? "right" : "left";
      this.__pamelaNextNameDatePanelSlot = targetSide === "left" ? "right" : "left";
      normalPanelSlot = targetSide;
      const { leftCenter, rightCenter } = pamelaGetCenters(this);
      const sideLandingX = pamelaGetPanelSideLandingX(this, targetSide, startW);

      dropX = Math.round(sideLandingX ?? (targetSide === "left" ? leftCenter : rightCenter));
      dropY = Math.round(safeCy);
    } catch (_) {}
  }

  if (!centerWidePanel && !replaceGroup && this.layout !== "companion") {
    normalPanelSlot = "single";
  }

  if (!replaceGroup && normalPanelSlot) {
    try {
      const layer = this.layers?.panels;
      const kids = layer?.getChildren?.() || [];
      kids.forEach((n) => {
        try {
          if (n?.getAttr?.("pamelaPanel") !== true) return;
          if (String(n?.getAttr?.("pamelaPanelGroup") || "") !== "name_date") return;
          if (String(n?.getAttr?.("pamelaPanelSlot") || "") !== normalPanelSlot) return;
          if (this.selected === n) {
            try { this.selectNode(null); } catch (_) {}
          }
          n.destroy();
        } catch (_) {}
      });
      try { layer?.batchDraw?.(); } catch (_) {}
    } catch (e) {
      console.warn("[addPanelFromUrl] name/date panel slot replace failed:", e);
    }
  }

  // Last-name panels keep their sliced rendering but use a neutral initial landing.
  if (replaceGroup === "lastname") {
    dropX = Math.round(safeCx);
    dropY = Math.round(safe.top + safeH * 0.28);
  }

  const node = new Konva.Image({
    image: img,

    // Ã¢Å“â€¦ place by CENTER
    x: dropX,
    y: dropY,

    width: startW,
    height: startH,

    // Ã¢Å“â€¦ CENTER rotation pivot (Corel-style)
    offsetX: startW / 2,
    offsetY: startH / 2,

    // Ã¢Å“â€¦ we do NOT use Konva drag engine for panels
    draggable: false,

    name: "pamela-panel",
  });

  // Ã¢Å“â€¦ Clean initial transform state
  node.scale({ x: 1, y: 1 });
  node.rotation(0);

  node.setAttr("pamelaPanel", true);
  this._ensurePamelaUid(node, "p");

  node.setAttr("pamelaPanelUrl", String(url || ""));
  node.setAttr("pamelaPanelId", meta.id || "");
  node.setAttr("pamelaPanelName", meta.name || "");
  node.setAttr("pamelaPanelMode", newPanelMode.panelMode);
  node.setAttr("pamelaSvgModeExplicit", newPanelMode.explicit);
  node.setAttr(
    "pamelaSvgMode",
    newPanelMode.explicit
      ? newPanelMode.panelMode
      : pamelaGetEffectivePanelMode(this)
  );
  node.setAttr("pamelaPanelGroup", replaceGroup || requestedPanelGroup || (normalPanelSlot ? "name_date" : ""));
  node.setAttr("pamelaPanelSlot", normalPanelSlot || "");
  node.setAttr("pamelaPanelBaseWidth", sliceBaseW);
  node.setAttr("pamelaPanelBaseHeight", sliceBaseH);
  node.setAttr("pamelaPanelWidthIn", panelWidthIn);
  pamelaAttachTextPathMetaToImage(node, textPathMeta);
  if (replaceGroup === "lastname") {
    node.__pamelaLastNameSliceSourceImage = img;
    node.__pamelaLastNameSliceMaskSourceImage = knockoutMaskImg;
  }
  if ((replaceGroup || requestedPanelGroup || "") === "name_date") {
    node.__pamelaNameDateSliceSourceImage = img;
    node.__pamelaNameDateSliceMaskSourceImage = knockoutMaskImg;
  }
  node.__pamelaPanelOriginalImage = img;
  node.__pamelaPanelKnockoutSourceImage = img;
  node.__pamelaPanelKnockoutImage = null;
  node.__pamelaPanelKnockoutMaskImage = knockoutMaskImg;
  try { pamelaUpdateTraditionalPanelSliceImage(node); } catch (_) {}

  node.hitStrokeWidth(20);
  node.visible(true);
  node.opacity(1);

  // Add to PANELS layer (behind clipart/text)
  this.layers.panels.add(node);
  node.moveToTop();

  // Keep initial panel landing on the tablet, then inside the broader safe area.
  pamelaClampImageCenterIntoRect(node, pamelaGetTabletLandingRect(this));
  pamelaClampNodeIntoSafeRect(this, node);

  // Ã¢Å“â€¦ IMPORTANT: select on pointerdown and force-update transformer BEFORE resizing
  node.off("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect");
  node.on("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect", (e) => {
    try { e.cancelBubble = true; } catch (_) {}

    if (this.panMode) return;

    const evt = e?.evt;
    if (evt && typeof evt.button === "number" && evt.button === 2) return;

    if (evt && evt.cancelable) {
      try { evt.preventDefault(); } catch (_) {}
    }

    if (this.selected !== node) this.selectNode(node);

    try { this.nodes?.trPanel?.forceUpdate?.(); } catch (_) {}
    try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
  });

  // Ã¢Å“â€¦ Panel-only direct pointer drag
  this._enableDirectPointerDrag(node, "panels");

  // Draw + select so handles show immediately
  this.layers.panels.batchDraw();
  this.selectNode(node);
  if (pamelaIsMobileEditorViewport()) {
    try { window.PAMELA_CLOSE_PANELS_UI?.(); } catch (_) {}
  }

  // Panels affect overlap colors
  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { await pamelaRefreshPlacedSvgNodesForGranite(this); } catch (_) {}

  // Ã¢Å“â€¦ HISTORY: adding a panel is its own undo step (mirrors add-clipart)
  this._historyCommit("add-panel");

  return node;
}


// =============================
// Clipart (place onto CONTENT layer)
// =============================

async addClipartFromUrl(url, meta = {}) {
  const img = await pamelaLoadSvgImageForGranite(url, pamelaGetActiveSvgMode());
  if (!img) return;
  const textPathMeta = await pamelaLoadSvgTextPathMeta(url);
  const svgWidthIn = await pamelaLoadSvgWidthIn(url);
  let knockoutMaskImg = img;
  try { knockoutMaskImg = await pamelaLoadSvgImageForGranite(url, "standard"); } catch (_) {}
  const isPrebuiltPanel =
    String(meta?.categoryId || "").trim().toLowerCase() === "panels" ||
    String(meta?.category || "").trim().toLowerCase() === "panels";

  const w = this.state.design.width;
  const h = this.state.design.height;
  const safe = pamelaGetSafeRect(w, h);
  const safeW = safe.right - safe.left;
  const safeH = safe.bottom - safe.top;

  const naturalW = img.width;
  const naturalH = img.height;
  const aspect = naturalW > 0 && naturalH > 0 ? (naturalW / naturalH) : 1;

  const SVG_TO_CANVAS_SCALE = 72 / 96; // standard browser SVG DPI Ã¢â€ â€™ 72 DPI canvas

  let startW = Math.round(naturalW * SVG_TO_CANVAS_SCALE);
  let startH = Math.round(naturalH * SVG_TO_CANVAS_SCALE);

  // If that makes it too wide, cap by width and recalc height
  const capW = safeW * 0.35;
  if (startW > capW) {
    startW = Math.round(capW);
    startH = Math.round(startW / aspect);
  }
  const panelWidthIn = (() => {
    if (!isPrebuiltPanel) return NaN;
    const metaWidthIn = pamelaGetPanelMetaWidthIn(meta);
    if (Number.isFinite(metaWidthIn) && metaWidthIn > 0) return metaWidthIn;
    if (Number.isFinite(svgWidthIn) && svgWidthIn > 0) return svgWidthIn;
    return startW / DPI;
  })();

  // Center in safe area
  let cx = (safe.left + safe.right) / 2;
  let cy = (safe.top + safe.bottom) / 2;
  let prebuiltPanelSlot = "";
  const centerWidePrebuiltPanel = isPrebuiltPanel && panelWidthIn > PANEL_CENTER_IF_WIDER_THAN_IN;

  if (centerWidePrebuiltPanel) {
    prebuiltPanelSlot = "center";
  } else if (isPrebuiltPanel && this.layout === "companion") {
    try {
      const targetSide = this.__pamelaNextPrebuiltPanelSlot === "right" ? "right" : "left";
      this.__pamelaNextPrebuiltPanelSlot = targetSide === "left" ? "right" : "left";
      prebuiltPanelSlot = targetSide;
      const { leftCenter, rightCenter } = pamelaGetCenters(this);
      const sideLandingX = pamelaGetPanelSideLandingX(this, targetSide, startW);
      cx = Math.round(sideLandingX ?? (targetSide === "left" ? leftCenter : rightCenter));
    } catch (_) {}
  } else if (isPrebuiltPanel) {
    prebuiltPanelSlot = "single";
  }

  if (isPrebuiltPanel && prebuiltPanelSlot) {
    try {
      const layer = this.layers?.content;
      const kids = layer?.getChildren?.() || [];
      kids.forEach((n) => {
        try {
          if (n?.className !== "Image") return;
          if (n?.getAttr?.("pamelaClipartLibraryPanel") !== true) return;
          if (String(n?.getAttr?.("pamelaClipartPanelSlot") || "") !== prebuiltPanelSlot) return;
          if (this.selected === n) {
            try { this.selectNode(null); } catch (_) {}
          }
          n.destroy();
        } catch (_) {}
      });
      try { layer?.batchDraw?.(); } catch (_) {}
    } catch (e) {
      console.warn("[addClipartFromUrl] prebuilt panel slot replace failed:", e);
    }
  }

  const node = new Konva.Image({
    image: img,
    x: cx,
    y: cy,
    width: startW,
    height: startH,
    offsetX: startW / 2,
    offsetY: startH / 2,
    draggable: false,
    name: "pamela-clipart",
  });

  node.setAttr("pamelaClipart", true);
  this._ensurePamelaUid(node, "c");
  node.setAttr("pamelaClipartUrl", String(url || ""));
  node.setAttr("pamelaClipartId", meta.id || "");
  node.setAttr("pamelaClipartName", meta.name || "");
  node.setAttr("pamelaClipartCategory", meta.category || "");
  node.setAttr("pamelaClipartCategoryId", meta.categoryId || "");
  node.setAttr("pamelaClipartSubcategory", meta.subcategory || "");
  node.setAttr("pamelaClipartLibraryPanel", isPrebuiltPanel);
  node.setAttr("pamelaClipartPanelSlot", prebuiltPanelSlot);
  if (Number.isFinite(panelWidthIn) && panelWidthIn > 0) {
    node.setAttr("pamelaClipartPanelWidthIn", panelWidthIn);
  }
  node.setAttr("pamelaClipartMode", "auto");
  node.setAttr("pamelaSvgModeExplicit", false);
  node.setAttr("pamelaSvgMode", pamelaGetEffectiveClipartMode(this, node));
  pamelaAttachTextPathMetaToImage(node, textPathMeta);
  node.__pamelaPanelKnockoutSourceImage = img;
  node.__pamelaPanelKnockoutMaskImage = knockoutMaskImg;

  // Minimum size baseline (original = scale 1)
  node.setAttr("pamelaClipartBaseScale", 1);

  node.hitStrokeWidth(20);

  // Ã¢Å“â€¦ First-click drag fix (Clipart)
  this._enableDirectPointerDrag(node, "content");

  this.layers.content.add(node);
  node.moveToTop();

  if (isPrebuiltPanel) {
    pamelaClampImageCenterIntoRect(node, pamelaGetTabletLandingRect(this));
  }
  pamelaClampNodeIntoSafeRect(this, node);

  node.on("pointerdown mousedown touchstart", (e) => {
    e.cancelBubble = true;

    // If pan mode is on, we don't drag objects
    if (this.panMode) return;

    const evt = e?.evt;

    // Ignore right-click
    if (evt && typeof evt.button === "number" && evt.button === 2) return;

    // For touch, prevent the browser from treating this as scroll/gesture
    if (evt && evt.cancelable) {
      try { evt.preventDefault(); } catch (_) {}
    }

    // Select first (this attaches transformer, refreshes UI, etc.)
    if (this.selected !== node) this.selectNode(node);

    // Ã¢Å“â€¦ DO NOT call startDrag here.
    // Konva will start the drag naturally on pointer move.
  });

  node.on("dragmove", () => {
    pamelaClampNodeIntoSafeRect(this, node);
    this._scheduleOverlapColors && this._scheduleOverlapColors();
    this.layers.content.batchDraw();
    this.layers.ui.batchDraw();
  });

  node.on("dragend", () => {
    pamelaClampNodeIntoSafeRect(this, node);
    this._scheduleOverlapColors && this._scheduleOverlapColors();
    this.layers.content.batchDraw();
    this.layers.ui.batchDraw();
    try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
  });

  this.layers.content.batchDraw();
  this.selectNode(node);
  if (pamelaIsMobileEditorViewport()) {
    try { window.PAMELA_CLOSE_CLIPART_UI?.(); } catch (_) {}
  }

  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { pamelaSchedulePanelKnockoutRebuild(this); } catch (_) {}

  // Ã¢Å“â€¦ HISTORY: adding clipart is its own undo step
  this._historyCommit("add-clipart");
  try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
}

// -----------------------------
// Rotate (Text + Clipart + Panels)
// Called by toolbar Ã¢Å¸Â² / Ã¢Å¸Â³ buttons
// -----------------------------
rotateSelected(deltaDeg) {
  const n = this.selected;
  if (!n) return;

  const cls = n.getClassName?.() || n.className || "";

  const isText = cls === "Text";
  const isImage = cls === "Image";
  const isClipOrPanel =
    isImage && (
      n.attrs?.pamelaClipart === true ||
      n.attrs?.pamelaPanel === true ||
      pamelaIsLaserArtworkNode(n)
    );

  // Only rotate Text, Clipart, Panels, and Laser artwork
  if (!isText && !isClipOrPanel) return;

  const d = Number(deltaDeg || 0);
  if (!Number.isFinite(d) || d === 0) return;

  // Ã¢Å“â€¦ Commit BEFORE state (so Ctrl+Z works)
  this._historyCommit("rotate");
  if (isClipOrPanel) {
    try { pamelaRestorePanelKnockouts(this); } catch (_) {}
  }

  // Apply rotation
  const next = (Number(n.rotation?.() ?? 0) || 0) + d;

  if (isText) {
    if (pamelaHasGroupedSideText(this) && pamelaCanGroupSideText(this, n)) {
      if (pamelaRotateGroupedSideText(this, this.groupedTextSide, d)) {
        try { this.selectNode(n); } catch (_) {}
        this._historyCommit("rotate");
        return;
      }
    }
    // Text snaps to your 1Ã‚Â° rule
    n.rotation(snapDeg(next));
  } else {
    // Clipart/Panel: no snap (your buttons already step in 5Ã‚Â°)
    n.rotation(next);
  }

  // Keep inside safe area
  pamelaClampNodeIntoSafeRect(this, n);

  // Text-specific housekeeping
  if (isText) {
    pamelaFixTextMetrics(n);
    pamelaSnapTextToCenterX(this, n);
    pamelaAutoPairLinkFromDrag(this, n);
    pamelaUpdateOneTextColorForOverlap(this, n);
    pamelaEnforceSpacingForText(this);
  } else {
    // Images can affect overlap colors
    this._scheduleOverlapColors && this._scheduleOverlapColors();
    try { pamelaSchedulePanelKnockoutRebuild(this); } catch (_) {}
  }

  // Redraw the correct layer(s)
  try { this.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers?.content?.batchDraw?.(); } catch (_) {}
  try { this.layers?.ui?.batchDraw?.(); } catch (_) {}

  // Keep transformer aligned/updated
  try { this.selectNode(n); } catch (_) {}
  if (n.attrs?.pamelaLaserEtching === true) {
    try { syncLaserSizeFieldsFromArtwork(n); } catch (_) {}
  }

  // Ã¢Å“â€¦ Commit AFTER state
  this._historyCommit("rotate");
}
rotatePortraitMask90(wrapper, dir) {
  if (!wrapper) return;

  const layer = wrapper.getLayer?.();
  const oldWIn = Number(wrapper.getAttr("__portraitWIn")) || 4;
  const oldHIn = Number(wrapper.getAttr("__portraitHIn")) || 4;
  const ppi = Number(wrapper.getAttr("__portraitPPI")) || 72;
  const shape = String(wrapper.getAttr("__portraitShape") || "").toLowerCase();

  // only do 90Ã‚Â° mask rotate for oval / rect
  if (shape !== "oval" && shape !== "rect" && shape !== "rectangle") return;

  // keep original/base size once
  const baseWIn = Number(wrapper.getAttr("__portraitBaseWIn")) || oldWIn;
  const baseHIn = Number(wrapper.getAttr("__portraitBaseHIn")) || oldHIn;
  wrapper.setAttr("__portraitBaseWIn", baseWIn);
  wrapper.setAttr("__portraitBaseHIn", baseHIn);

  const current = Number(wrapper.getAttr("__portraitOrientation") || 0);
  const next = (current + (dir > 0 ? 90 : -90) + 360) % 180;
  wrapper.setAttr("__portraitOrientation", next);

  const newWIn = (next === 90) ? baseHIn : baseWIn;
  const newHIn = (next === 90) ? baseWIn : baseHIn;

  wrapper.setAttr("__portraitWIn", newWIn);
  wrapper.setAttr("__portraitHIn", newHIn);

  const oldW = oldWIn * ppi;
  const oldH = oldHIn * ppi;
  const newW = newWIn * ppi;
  const newH = newHIn * ppi;

  // keep visual center in the same place
  const dxWrap = (oldW - newW) / 2;
  const dyWrap = (oldH - newH) / 2;
  wrapper.position({
    x: (Number(wrapper.x?.() || 0) + dxWrap),
    y: (Number(wrapper.y?.() || 0) + dyWrap)
  });

  const inner = wrapper.findOne?.(".pamela-portrait-inner") || null;
  const hit = wrapper.findOne?.(".pamela-hit") || null;
  const photo = inner?.findOne?.(".pamela-portrait-photo") || null;

  if (!inner || !hit) return;

  // keep photo center visually stable inside the new mask box
  if (photo) {
    photo.position({
      x: Number(photo.x?.() || 0) + (newW - oldW) / 2,
      y: Number(photo.y?.() || 0) + (newH - oldH) / 2
    });
  }

  // remove old visible guide shapes only
  const kids = (wrapper.getChildren?.() || []).slice();
  kids.forEach((node) => {
    if (!node) return;
    if (node === inner) return;
    if (node === hit) return;
    try { node.destroy(); } catch (_) {}
  });

  // rebuild dashed guide
  if (shape === "oval") {
    wrapper.add(new Konva.Ellipse({
      x: newW / 2,
      y: newH / 2,
      radiusX: newW / 2,
      radiusY: newH / 2,
      stroke: "rgba(255,255,255,0.95)",
      strokeWidth: 4,
      dash: [18, 12],
      listening: false
    }));
  } else {
    wrapper.add(new Konva.Rect({
      x: 0,
      y: 0,
      width: newW,
      height: newH,
      stroke: "rgba(255,255,255,0.95)",
      strokeWidth: 4,
      dash: [18, 12],
      cornerRadius: 10,
      listening: false
    }));
  }

  // rebuild crosshair
  wrapper.add(new Konva.Line({
    points: [newW / 2 - 18, newH / 2, newW / 2 + 18, newH / 2],
    stroke: "rgba(255,255,255,0.9)",
    strokeWidth: 3,
    listening: false
  }));

  wrapper.add(new Konva.Line({
    points: [newW / 2, newH / 2 - 18, newW / 2, newH / 2 + 18],
    stroke: "rgba(255,255,255,0.9)",
    strokeWidth: 3,
    listening: false
  }));

  // update clip
  inner.clipFunc(function (ctx) {
    ctx.beginPath();

    if (shape === "oval") {
      ctx.ellipse(newW / 2, newH / 2, newW / 2, newH / 2, 0, 0, Math.PI * 2);
    } else {
      ctx.rect(0, 0, newW, newH);
    }

    ctx.closePath();
  });

  // resize inner background rect(s), but do not touch the photo size/rotation
  try {
    const innerKids = inner.getChildren?.() || [];
    innerKids.forEach((node) => {
      const cls = node.getClassName?.() || node.className || "";
      const nm = typeof node.name === "function" ? String(node.name() || "") : String(node.attrs?.name || "");
      if (cls === "Rect" && nm !== "pamela-portrait-photo") {
        node.width?.(newW);
        node.height?.(newH);
      }
    });
  } catch (_) {}

  // resize hit area
  hit.x?.(0);
  hit.y?.(0);
  hit.width?.(newW);
  hit.height?.(newH);

  // keep hit on top
  try { hit.moveToTop?.(); } catch (_) {}

  // refresh selection / handles
  try { this.selectNode?.(wrapper); } catch (_) {}
  try {
    if (typeof syncCenterHandleToPhoto === "function") {
      syncCenterHandleToPhoto(wrapper);
    }
  } catch (_) {}

  try { layer?.batchDraw?.(); } catch (_) {}
  try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
}
// -----------------------------
// Clipart tools
// -----------------------------
flipSelected(axis) {
  const n = this.selected;
  if (!n) return;

  // Allow Clipart, Panel, or Laser artwork
  if (
    n.className !== "Image" ||
    !(
      n.attrs?.pamelaClipart === true ||
      n.attrs?.pamelaPanel === true ||
      n.attrs?.pamelaLaserEtching === true
    )
  ) {
    return;
  }

  const ax = String(axis || "x").toLowerCase();

  // Ã¢Å“â€¦ Commit BEFORE state so Ctrl+Z works
  this._historyCommit("flip");
  try { pamelaRestorePanelKnockouts(this); } catch (_) {}

  const sx = Number(n.scaleX?.() ?? 1) || 1;
  const sy = Number(n.scaleY?.() ?? 1) || 1;

  if (ax === "x") n.scaleX(-sx);
  else if (ax === "y") n.scaleY(-sy);
  else n.scaleX(-sx);

  pamelaClampNodeIntoSafeRect(this, n);
  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { pamelaSchedulePanelKnockoutRebuild(this); } catch (_) {}

  try { this.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers?.content?.batchDraw?.(); } catch (_) {}
  try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
  if (n.attrs?.pamelaLaserEtching === true) {
    try { syncLaserSizeFieldsFromArtwork(n); } catch (_) {}
  }

  // Ã¢Å“â€¦ Commit AFTER state
  this._historyCommit("flip");
}

// -----------------------------
// Mirror + Clone (creates a new node that Undo can remove)
// -----------------------------
mirrorCloneSelected() {
  const n = this.selected;
  if (!n) return;

  const cls = n.getClassName?.() || n.className || "";

  const isPanel = (cls === "Image" && n.attrs?.pamelaPanel === true);
  const isClip  = (cls === "Image" && n.attrs?.pamelaClipart === true);
  const isLaserSpot =
    cls === "Image" &&
    n.attrs?.pamelaLaserEtching === true &&
    String(n.attrs?.pamelaLaserKind || n.getAttr?.("pamelaLaserKind") || "spot").toLowerCase() === "spot";
  const isText  = (cls === "Text");

  // Mirror clone only for panels, clipart, and spot laser artwork.
  if (!isPanel && !isClip && !isLaserSpot) return;

  const layer = isPanel ? this.layers?.panels : this.layers?.content;
  if (!layer) return;

  // Ã¢Å“â€¦ Commit BEFORE so Undo can go back to "no clone"
  this._historyCommit("mirror-before");
  try { pamelaRestorePanelKnockouts(this); } catch (_) {}

  /// Clone (Konva clone does NOT copy event listeners)
const clone = n.clone({});
layer.add(clone);

// Ã¢Å“â€¦ IMPORTANT: Konva clone copies attrs, including pamelaUid.
// Force a NEW uid so Undo/Redo can distinguish clone vs original.
try {
  const prefix = isPanel ? "p" : isClip ? "c" : isLaserSpot ? "l" : "t";
  clone.setAttr("pamelaUid", this._makePamelaUid(prefix));
} catch (_) {}

// Ã¢Å“â€¦ Ensure clone is tagged so Undo/Redo can SEE it (snapshot/prune depend on these)
try {
  if (isPanel) {
    clone.setAttr("pamelaPanel", true);
    clone.setAttr(
      "pamelaPanelUrl",
      String(n.getAttr?.("pamelaPanelUrl") || n.attrs?.pamelaPanelUrl || "")
    );
  } else if (isClip) {
    clone.setAttr("pamelaClipart", true);
    clone.setAttr(
      "pamelaClipartUrl",
      String(n.getAttr?.("pamelaClipartUrl") || n.attrs?.pamelaClipartUrl || "")
    );
  } else if (isLaserSpot) {
    clone.setAttr("pamelaLaserEtching", true);
    clone.setAttr("pamelaLaserMirrorClone", true);
    clone.setAttr("__pamelaKind", "laser");
    clone.setAttr("pamelaLaserKind", "spot");
    clone.setAttr("pamelaLaserUrl", String(n.getAttr?.("pamelaLaserUrl") || n.attrs?.pamelaLaserUrl || ""));
    clone.setAttr("pamelaLaserSide", String(n.getAttr?.("pamelaLaserSide") || window.PAMELA_SIDES?.current || "front"));
    clone.setAttr("pamelaLaserPricingSource", String(n.getAttr?.("pamelaLaserPricingSource") || "custom"));
    clone.setAttr("pamelaLaserSource", String(n.getAttr?.("pamelaLaserSource") || ""));
    clone.setAttr("pamelaLaserOriginalName", String(n.getAttr?.("pamelaLaserOriginalName") || ""));
    clone.setAttr("pamelaLaserPreview", String(n.getAttr?.("pamelaLaserPreview") || "black-and-white"));
    clone.setAttr("pamelaLaserFeatherPx", Number(n.getAttr?.("pamelaLaserFeatherPx") || 0) || 0);
    clone._pamelaLaserBaseImage = cloneLaserCanvasSource(n._pamelaLaserBaseImage || n.image?.()) || n._pamelaLaserBaseImage || n.image?.();
  }
} catch (_) {}


  // Ã¢Å“â€¦ Layer ordering rules:
  // - Panels layer: OK to moveToTop inside panels layer
  // - Content layer: do NOT force above text; restack instead
  try {
    if (isPanel) clone.moveToTop();
  } catch (_) {}

  // Mirror horizontally around the *selected node*'s visual center,
  // using your stage/world coords approach.
  const rot = Number(n.rotation?.() || 0);
  const sx  = Number(n.scaleX?.() || 1);
  const sy  = Number(n.scaleY?.() || 1);

  clone.rotation(-rot);
  clone.scaleX(-sx);
  clone.scaleY(sy);

  // Make hit area easier
  try {
    if (cls === "Image") clone.hitStrokeWidth(20);
  } catch (_) {}

// Compute the original's visual center, then mirror the clone across the tablet centerline.
const r1 = n.getClientRect({ relativeTo: layer, skipTransform: false });
const mid1 = r1.x + r1.width / 2;

const tabletRect = pamelaGetTabletLandingRect(this);
const centerX = tabletRect
  ? tabletRect.left + tabletRect.width / 2
  : Number(this.state?.design?.width || 0) / 2;

const r2 = clone.getClientRect({ relativeTo: layer, skipTransform: false });
const mid2 = r2.x + r2.width / 2;

// Move clone so its center becomes the mirrored position
const targetMid = 2 * centerX - mid1;
clone.x(clone.x() + (targetMid - mid2));


  // Clamp into safe rect
  try { pamelaClampNodeIntoSafeRect(this, clone); } catch (_) {}

  // Enable drag + ensure drag end commits
  if (isPanel) this._enableDirectPointerDrag(clone, "panels");
  else this._enableDirectPointerDrag(clone, "content");
  if (isLaserSpot) {
    try { pamelaEnableLaserArtworkDrag(this, clone); } catch (_) {}
  }

  // Retap fix: cloned image nodes do not inherit the original's explicit
  // select-on-touch handler, so rebind it on the clone. Checked by actual
  // touch capability, not just mobile viewport, so touchscreen laptops
  // (trackpad present, so "mobile viewport" reports false) get it too.
  if (pamelaIsMobileEditorViewport() || pamelaIsTouchCapableDevice()) {
    if (isPanel) {
      clone.off("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect");
      clone.on("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect", (e) => {
        try { e.cancelBubble = true; } catch (_) {}
        if (this.panMode) return;
        const evt = e?.evt;
        if (evt && typeof evt.button === "number" && evt.button === 2) return;
        if (evt && evt.cancelable) {
          try { evt.preventDefault(); } catch (_) {}
        }
        if (this.selected !== clone) this.selectNode(clone);
        try { this.nodes?.trPanel?.forceUpdate?.(); } catch (_) {}
        try { this.layers?.ui?.batchDraw?.(); } catch (_) {}
      });
    } else if (isClip || isLaserSpot) {
      clone.off("pointerdown.pamelaCloneClipartSelect mousedown.pamelaCloneClipartSelect touchstart.pamelaCloneClipartSelect");
      clone.on("pointerdown.pamelaCloneClipartSelect mousedown.pamelaCloneClipartSelect touchstart.pamelaCloneClipartSelect", (e) => {
        try { e.cancelBubble = true; } catch (_) {}
        if (this.panMode) return;
        const evt = e?.evt;
        if (evt && typeof evt.button === "number" && evt.button === 2) return;
        if (evt && evt.cancelable) {
          try { evt.preventDefault(); } catch (_) {}
        }
        if (this.selected !== clone) this.selectNode(clone);
      });
    }
  }

  if (isLaserSpot && !(pamelaIsMobileEditorViewport() || pamelaIsTouchCapableDevice())) {
    clone.off("pointerdown.pamelaCloneLaserSelect mousedown.pamelaCloneLaserSelect touchstart.pamelaCloneLaserSelect");
    clone.on("pointerdown.pamelaCloneLaserSelect mousedown.pamelaCloneLaserSelect touchstart.pamelaCloneLaserSelect", (e) => {
      try { e.cancelBubble = true; } catch (_) {}
      if (this.panMode) return;
      try { if (e?.evt?.cancelable) e.evt.preventDefault(); } catch (_) {}
      if (this.selected !== clone) this.selectNode(clone);
    });
  }

  // Select clone
  this.selectNode(clone);

  // Important: keep your Ã¢â‚¬Å“images behind textÃ¢â‚¬Â rule
  try { this._pamelaRestackContentLayer?.(); } catch (_) {}

  this._scheduleOverlapColors && this._scheduleOverlapColors();
  try { pamelaSchedulePanelKnockoutRebuild(this); } catch (_) {}

  try { this.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { this.layers?.content?.batchDraw?.(); } catch (_) {}
  try { this.layers?.ui?.batchDraw?.(); } catch (_) {}

  // Ã¢Å“â€¦ Commit AFTER so Redo restores it
  this._historyCommit("mirror-after");
  if (isLaserSpot) {
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  }

  // Ã¢Å“â€¦ Debug state right now (what you asked for)
  try {
    const uid = String(clone.getAttr("pamelaUid") || "");
    const onPanels = !!(clone.getLayer && clone.getLayer() === this.layers?.panels);
    const onContent = !!(clone.getLayer && clone.getLayer() === this.layers?.content);

  } catch (_) {}
}


// -----------------------------
// Delete
// -----------------------------
deleteSelected() {
  // Ã¢Å“â€¦ Effective selection (normal selection OR portrait-edit transformer selection)
  let sel = this.selected || this.__cmSelectedPortraitWrapper || window.__CM_SELECTED_PORTRAIT_WRAPPER__ || null;

  // If portrait edit transformer has a photo selected, use that as sel
  try {
    const st = this.stage || (sel && sel.getStage && sel.getStage());
    const tr2 = st && st.findOne && st.findOne("#cmPortraitTransformer");
    const nodes2 = tr2 && tr2.nodes && tr2.nodes();
    const photoNode = nodes2 && nodes2[0] ? nodes2[0] : null;
    if (photoNode) sel = photoNode;
  } catch (_) {}

  if (!sel) return;

  // Ã¢Å“â€¦ If deleting while in portrait-edit mode, always clear that transformer first
  const clearPortraitTransformer = () => {
    try {
      const st = this.stage || (sel && sel.getStage && sel.getStage());
      const tr2 = st && st.findOne && st.findOne("#cmPortraitTransformer");
      if (tr2 && typeof tr2.nodes === "function") {
        tr2.nodes([]);
        try { tr2.forceUpdate && tr2.forceUpdate(); } catch (_) {}
        const lyr = tr2.getLayer && tr2.getLayer();
        if (lyr && lyr.batchDraw) lyr.batchDraw();
        else if (lyr && lyr.draw) lyr.draw();
      }
    } catch (_) {}
  };

  // Ã¢Å“â€¦ Helper: walk up to portrait wrapper (mask+photo group)
  const findPortraitWrapper = (node) => {
    let n = node;
    let hops = 0;
    while (n && hops < 14) {
      try {
        const a = n.attrs || {};
        const nm = (typeof n.name === "function") ? String(n.name() || "") : "";
        const isWrapper =
          a.__isPortraitGroup === true ||
          a.pamelaType === "portrait" ||
          a.__pamelaKind === "portrait" ||
          nm.includes("pamela-portrait-group");

        if (isWrapper) return n;
        n = n.getParent ? n.getParent() : null;
      } catch (_) {
        n = null;
      }
      hops++;
    }
    return null;
  };

  // Ã¢Å“â€¦ Determine what weÃ¢â‚¬â„¢re deleting
  const cls = sel.getClassName?.() || sel.className || "";
  const isPanel = (cls === "Image" && sel.attrs?.pamelaPanel === true);
  const isLaserArtwork = (cls === "Image" && sel.attrs?.pamelaLaserEtching === true);

  const portraitWrapper = findPortraitWrapper(sel);
  const isPortrait = !!portraitWrapper;

  // Ã¢Å“â€¦ Back lettering delete flags (decide BEFORE destroy)
  const BACK_VERSE = "__cm_back_verse";
  const BACK_LAST  = "__cm_back_lastname";

  const isKeyOnNodeOrParents = (node, key) => {
    let n = node;
    let hops = 0;
    while (n && hops < 12) {
      try {
        const nid = (typeof n.id === "function") ? String(n.id() || "") : "";
        const nnm = (typeof n.name === "function") ? String(n.name() || "") : "";
        if (nid === key || nnm === key) return true;
        n = n.getParent ? n.getParent() : null;
      } catch (_) {
        n = null;
      }
      hops++;
    }
    return false;
  };

  const deletingBackVerse =
    isKeyOnNodeOrParents(sel, BACK_VERSE) ||
    (portraitWrapper && isKeyOnNodeOrParents(portraitWrapper, BACK_VERSE));

  const deletingBackLast =
    isKeyOnNodeOrParents(sel, BACK_LAST) ||
    (portraitWrapper && isKeyOnNodeOrParents(portraitWrapper, BACK_LAST));

  // Ã¢Å“â€¦ Clear selection transformers BEFORE destroy to prevent leftover boxes
  clearPortraitTransformer();
  try { this.nodes?.tr?.nodes && this.nodes.tr.nodes([]); } catch (_) {}
  try { this.nodes?.trPanel?.nodes && this.nodes.trPanel.nodes([]); } catch (_) {}
  try { this.nodes?.tr?.forceUpdate && this.nodes.tr.forceUpdate(); } catch (_) {}
  try { this.nodes?.trPanel?.forceUpdate && this.nodes.trPanel.forceUpdate(); } catch (_) {}

  // Ã¢Å“â€¦ Destroy the correct thing
  if (isPortrait) {
    // Stop any portrait edit UI/mode
    try { this._setPortraitCropMode && this._setPortraitCropMode(false); } catch (_) {}
    try { this._hidePortraitPopup && this._hidePortraitPopup(); } catch (_) {}

    // If you track slots, try to notify using wrapper attrs (safe if missing)
    try {
      const slot = Number(portraitWrapper.getAttr && portraitWrapper.getAttr("pamelaPortraitSlot")) || 0;
      if (this._onPortraitRemovedFromCanvas) this._onPortraitRemovedFromCanvas(slot);
    } catch (_) {}

    try { portraitWrapper.destroy(); } catch (_) {}
    try { this.__cmSelectedPortraitWrapper = null; } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
    try { window.__CM_LAST_PORTRAIT_WRAPPER__ = null; } catch (_) {}
  } else {
    try { sel.destroy(); } catch (_) {}
  }

  // Ã¢Å“â€¦ Clear editor selection state + redraw correct layers
  this.selectNode(null);
  if (isPortrait) {
    try { window.pamelaForceHidePortraitImagesToolbar?.(this); } catch (_) {}
  } else {
    try { pamelaRefreshImagesToolbar(this); } catch (_) {}
  }

  try {
    if (isPanel) {
      this.layers?.panels?.batchDraw?.();
    } else if (isPortrait) {
      // Portraits may live in content/panels depending on your setup Ã¢â‚¬â€ redraw safely
      this.layers?.portraits?.batchDraw?.();
      this.layers?.content?.batchDraw?.();
      this.layers?.ui?.batchDraw?.();
      this.layers?.panels?.batchDraw?.();
    } else {
      this.layers?.content?.batchDraw?.();
    }
  } catch (_) {}

  try { this._scheduleOverlapColors && this._scheduleOverlapColors(); } catch (_) {}
  try { pamelaSchedulePanelKnockoutRebuild(this); } catch (_) {}
  try { pamelaEnforceSpacingForText(this); } catch (_) {}
  if (isLaserArtwork) {
    try { window.__PAMELA_SELECTED_LASER_NODE__ = null; } catch (_) {}
    try { window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ = null; } catch (_) {}
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  }

  // Ã¢Å“â€¦ Back lettering: after delete, REMOVE any lingering back nodes anywhere + re-enable buttons
  try {
    const st = this.stage;
    if (st && typeof setBtnDisabled === "function") {

      const destroyAllByKey = (key) => {
        try {
          const all = st.find ? st.find("*") : [];
          (all || []).forEach((n) => {
            try {
              const nid = (typeof n.id === "function") ? String(n.id() || "") : "";
              const nnm = (typeof n.name === "function") ? String(n.name() || "") : "";
              const a = n.attrs || {};
              const aid = a.id ? String(a.id) : "";
              const anm = a.name ? String(a.name) : "";
              if (nid === key || nnm === key || aid === key || anm === key) {
                n.destroy();
              }
            } catch (_) {}
          });
        } catch (_) {}
      };

      if (deletingBackVerse) {
        destroyAllByKey(BACK_VERSE);
        setBtnDisabled("pamela-add-back-verse", false);
      }

      if (deletingBackLast) {
        destroyAllByKey(BACK_LAST);
        setBtnDisabled("pamela-add-back-lastname", false);
      }

      try { st.draw && st.draw(); } catch (_) {}
    }
  } catch (_) {}
// Ã¢Å“â€¦ Back lettering: flip UI flags OFF when those items are deleted
try {
  window.__CM_BACK_UI__ = window.__CM_BACK_UI__ || { hasLast: false, hasVerse: false };

  if (deletingBackVerse) {
    window.__CM_BACK_UI__.hasVerse = false;
    setBtnDisabled("pamela-add-back-verse", false);
  }

  if (deletingBackLast) {
    window.__CM_BACK_UI__.hasLast = false;
    setBtnDisabled("pamela-add-back-lastname", false);
  }

  // keep buttons synced to flags
  if (typeof syncButtonStates === "function") syncButtonStates(this);
} catch (_) {}
  try { checkIfBackShouldDeactivate(this); } catch (_) {}
  try { window.__CM_BACK_LETTERING__?.syncButtonStates?.(this); } catch (_) {}

  // Ã¢Å“â€¦ HISTORY: deleting a panel/clipart/laser node is its own undo step.
  // Portrait deletes aren't tracked by the snapshot, so skip committing for
  // those rather than push a no-op entry onto the stack.
  if (!isPortrait) {
    try { this._historyCommit("delete"); } catch (_) {}
  }
}
    // -----------------------------
    // Zoom / Pan Mode
    // -----------------------------
    zoomByStep(dir) {
      const oldScale = this.state.view.scale;
      const step = dir > 0 ? ZOOM_STEP : 1 / ZOOM_STEP;

      const minScale = Math.max(this.state.fit.scale * 0.4, VIEW_MIN_SCALE);
      const maxScale = VIEW_MAX_SCALE;

      const newScale = clamp(oldScale * step, minScale, maxScale);
      if (Math.abs(newScale - oldScale) < 1e-6) return;

      const stageW = this.stage.width();
      const stageH = this.stage.height();
      const center = { x: stageW / 2, y: stageH / 2 };

      const worldPos = {
        x: (center.x - this.state.view.x) / oldScale,
        y: (center.y - this.state.view.y) / oldScale,
      };

      this.state.view.scale = newScale;
      this.state.view.x = center.x - worldPos.x * newScale;
      this.state.view.y = center.y - worldPos.y * newScale;

      this._userMovedView = true;
      this._applyView();
    }

setPanMode(on) {
  this.panMode = !!on;

  try {
    // Content layer: text + clipart
    const contentNodes = this.layers.content.getChildren(
      (n) => n && (pamelaIsTextLikeNode(n) || (n.className === "Image" && n.attrs?.pamelaClipart))
    );
    contentNodes.forEach((n) => n.draggable(!this.panMode));
  } catch (_) {}

  try {
    // Ã¢Å“â€¦ Panels layer: panels
    const panelNodes = this.layers.panels.getChildren(
      (n) => n && n.className === "Image" && n.attrs?.pamelaPanel === true
    );
    panelNodes.forEach((n) => n.draggable(!this.panMode));
  } catch (_) {}

  pamelaForceReleaseKonvaDrag();
  this._isPanning = false;
}


// -----------------------------
// Viewport
// -----------------------------
_applyView() {

  const s = this.state.view.scale;
  const x = this.state.view.x;
  const y = this.state.view.y;

  // Panels + content + UI all live in the same world space.
  // this.layers.panels/content/portraits are GROUP aliases for whichever
  // side is currently active (see makeSideLayer in the stage setup) -- the
  // pan/zoom transform has to land on the underlying SHARED LAYER so both
  // front and back groups stay in sync. Applying it to the active-side
  // group alone leaves the other side's group on a stale/default transform
  // until it happens to be active the next time _applyView() runs, which
  // corrupts any getClientRect()/relativeTo math for nodes on that side in
  // the meantime (this is what broke back-lettering placement).
  const targets = new Set([
    this.layers.background,
    this.layers.panels?.getLayer?.() || this.layers.panels,
    this.layers.content?.getLayer?.() || this.layers.content,
    this.layers.portraits?.getLayer?.() || this.layers.portraits,
    this.layers.border,
    this.layers.grid,
    this.layers.ui,
  ]);
  targets.forEach((lyr) => {
    if (!lyr) return;
    lyr.scale({ x: s, y: s });
    lyr.position({ x, y });
    lyr.batchDraw();
  });

}

_resize(resetToFit = false) {

const rect = this.wrapEl.getBoundingClientRect();
const parentRect = this.wrapEl.parentElement
  ? this.wrapEl.parentElement.getBoundingClientRect()
  : null;

  const w = Math.max(320, rect.width);
  const h = Math.max(420, rect.height);

  this.stage.size({ width: w, height: h });

  // Keep the old full-design fit available as fallback
  const designFitScale = Math.min(
    w / this.state.design.width,
    h / this.state.design.height
  );

  this.state.fit.scale = designFitScale;

  const bg = this.nodes.bg;
  if (bg) {
    const bgX = typeof bg.x === "function" ? bg.x() : 0;
    const bgY = typeof bg.y === "function" ? bg.y() : 0;
    const bgScaleX = Number(bg.scaleX?.() || 1) || 1;
    const bgScaleY = Number(bg.scaleY?.() || 1) || 1;
    const bgW = Math.abs(bg.width() * bgScaleX);
    const bgH = Math.abs(bg.height() * bgScaleY);

    const PAD = 0.94;
    const fitScale = Math.min(
      (w * PAD) / bgW,
      (h * PAD) / bgH
    );

    this.state.fit.scale = fitScale;
  }

  if (resetToFit) {

    if (bg) {
      const bgX = typeof bg.x === "function" ? bg.x() : 0;
      const bgY = typeof bg.y === "function" ? bg.y() : 0;
      const bgScaleX = Number(bg.scaleX?.() || 1) || 1;
      const bgScaleY = Number(bg.scaleY?.() || 1) || 1;
      const bgW = Math.abs(bg.width() * bgScaleX);
      const bgH = Math.abs(bg.height() * bgScaleY);
      const bgLeft = Math.min(bgX, bgX + bg.width() * bgScaleX);
      const bgTop = Math.min(bgY, bgY + bg.height() * bgScaleY);

      // Small padding so the monument fills most of the view
      const PAD = 0.94;

      const fitScale = Math.min(
        (w * PAD) / bgW,
        (h * PAD) / bgH
      );

      this.state.fit.scale = fitScale;
      this.state.view.scale = fitScale;

      this.state.view.x =
        (w - bgW * fitScale) / 2 - bgLeft * fitScale;

      this.state.view.y =
        (h - bgH * fitScale) / 2 - bgTop * fitScale;

      if (pamelaIsMobileEditorViewport()) {
        const MOBILE_TOP_PAD = 16;
        this.state.view.y = MOBILE_TOP_PAD - bgTop * fitScale;
      }

    } else {
      this.state.view.scale = designFitScale;

      this.state.view.x =
        (w - this.state.design.width * designFitScale) / 2;

      this.state.view.y =
        (h - this.state.design.height * designFitScale) / 2;

      if (pamelaIsMobileEditorViewport()) {
        const MOBILE_TOP_PAD = 16;
        this.state.view.y = MOBILE_TOP_PAD;
      }
    }

    this._userMovedView = false;
  }

  this._applyView();
}

_wireViewportControls() {

  const container = this.stage.container();

  const isMobile =
    (window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches) ||
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (container) container.style.touchAction = isMobile ? "pan-y" : "none";

  const isEmptyOrBg = (t) => t === this.stage || t === this.nodes.bg;

  const showMobileGestureHint = () => {
    try {
      if (!container || !isMobile || container.__pamelaGestureHintShown) return;
      container.__pamelaGestureHintShown = true;

      if (!container.style.position || container.style.position === "static") {
        container.style.position = "relative";
      }

      const hint = document.createElement("div");
      hint.id = "pamela-mobile-gesture-hint";
      hint.innerHTML =
        '<div style="font-weight:900;font-size:14px;line-height:1.2;">Pinch to zoom</div>' +
        '<div style="font-weight:800;font-size:13px;line-height:1.2;margin-top:3px;">Two fingers to move</div>';
      hint.style.cssText =
        "position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);" +
        "z-index:20;pointer-events:none;background:rgba(17,17,17,0.82);color:#fff;" +
        "border-radius:14px;padding:12px 16px;text-align:center;box-shadow:0 10px 30px rgba(0,0,0,0.25);" +
        "font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial;max-width:78%;transition:opacity 260ms ease;";

      container.appendChild(hint);

      const dismiss = () => {
        try {
          hint.style.opacity = "0";
          window.setTimeout(() => hint.remove(), 280);
        } catch (_) {}
      };

      window.setTimeout(dismiss, 2800);
      container.addEventListener("touchstart", dismiss, { once: true, passive: true });
    } catch (_) {}
  };

  showMobileGestureHint();

  if (container && this.__pamelaMobilePinchHandlers) {
    try { container.removeEventListener("touchstart", this.__pamelaMobilePinchHandlers.start, { passive: false }); } catch (_) {}
    try { container.removeEventListener("touchmove", this.__pamelaMobilePinchHandlers.move, { passive: false }); } catch (_) {}
    try { container.removeEventListener("touchend", this.__pamelaMobilePinchHandlers.end); } catch (_) {}
    try { container.removeEventListener("touchcancel", this.__pamelaMobilePinchHandlers.end); } catch (_) {}
    this.__pamelaMobilePinchHandlers = null;
  }

  if (container && isMobile) {
    let pinchState = null;
    this._multiTouchGestureActive = false;

    const getGesture = (touches) => {
      if (!touches || touches.length !== 2) return null;
      const rect = container.getBoundingClientRect();
      const a = touches[0];
      const b = touches[1];
      const ax = Number(a.clientX || 0) - rect.left;
      const ay = Number(a.clientY || 0) - rect.top;
      const bx = Number(b.clientX || 0) - rect.left;
      const by = Number(b.clientY || 0) - rect.top;
      return {
        center: {
          x: (ax + bx) / 2,
          y: (ay + by) / 2,
        },
        distance: Math.hypot(bx - ax, by - ay),
      };
    };

    const onTouchStart = (e) => {
      if (this._inlineEditActive) return;
      if (!e.touches || e.touches.length !== 2) return;

      const gesture = getGesture(e.touches);
      if (!gesture || gesture.distance <= 0) return;

      const oldScale = Number(this.state.view.scale || 1) || 1;
      pinchState = {
        startDistance: gesture.distance,
        startScale: oldScale,
        worldCenter: {
          x: (gesture.center.x - this.state.view.x) / oldScale,
          y: (gesture.center.y - this.state.view.y) / oldScale,
        },
      };

      this._multiTouchGestureActive = true;
      try { this.__pamelaActiveDirectDragCancel?.(); } catch (_) {}
      this._isPanning = false;
      try { e.preventDefault(); } catch (_) {}
    };

    const onTouchMove = (e) => {
      if (!pinchState) return;
      if (!e.touches || e.touches.length !== 2) {
        pinchState = null;
        this._multiTouchGestureActive = false;
        return;
      }

      const gesture = getGesture(e.touches);
      if (!gesture || gesture.distance <= 0) return;

      const minScale = Math.max(this.state.fit.scale * 0.25, VIEW_MIN_SCALE * 0.5);
      const maxScale = VIEW_MAX_SCALE;
      const nextScale = clamp(
        pinchState.startScale * (gesture.distance / pinchState.startDistance),
        minScale,
        maxScale
      );

      this.state.view.scale = nextScale;
      this.state.view.x = gesture.center.x - pinchState.worldCenter.x * nextScale;
      this.state.view.y = gesture.center.y - pinchState.worldCenter.y * nextScale;

      this._userMovedView = true;
      this._applyView();

      pinchState.startDistance = gesture.distance;
      pinchState.startScale = nextScale;
      pinchState.worldCenter = {
        x: (gesture.center.x - this.state.view.x) / nextScale,
        y: (gesture.center.y - this.state.view.y) / nextScale,
      };

      try { e.preventDefault(); } catch (_) {}
    };

    const onTouchEnd = (e) => {
      if (!e.touches || e.touches.length < 2) {
        pinchState = null;
        this._multiTouchGestureActive = false;
      }
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchEnd);

    this.__pamelaMobilePinchHandlers = {
      start: onTouchStart,
      move: onTouchMove,
      end: onTouchEnd,
    };
  }

  this.stage.off(
    "pointerdown.pamela pointermove.pamela pointerup.pamela pointercancel.pamela wheel.pamela"
  );

  this.stage.on("pointerdown.pamela", (e) => {

    if (this._inlineEditActive) return;

    if (isEmptyOrBg(e.target)) {

      this.selectNode(null);

      if (isMobile) {
        this._isPanning = false;
        return;
      }

      const p = this.stage.getPointerPosition();
      if (!p) return;

      this._isPanning = true;

      this._panStart = {
        x: p.x,
        y: p.y,
      };

      this._viewStart = {
        x: this.state.view.x,
        y: this.state.view.y,
      };

    }

  });

  this.stage.on("pointermove.pamela", () => {

    if (this._inlineEditActive) return;
    if (!this._isPanning) return;

    const p = this.stage.getPointerPosition();
    if (!p) return;

    this.state.view.x =
      this._viewStart.x + (p.x - this._panStart.x);

    this.state.view.y =
      this._viewStart.y + (p.y - this._panStart.y);

    this._userMovedView = true;

    this._applyView();

  });

  this.stage.on("pointerup.pamela pointercancel.pamela", () => {

    this._isPanning = false;

    pamelaForceReleaseKonvaDrag();

  });

  this.stage.on("wheel.pamela", (e) => {

    if (this._inlineEditActive) return;
    if (!e?.evt?.ctrlKey) return;

    e.evt.preventDefault();

    const oldScale = this.state.view.scale;

    const pointer = this.stage.getPointerPosition();
    if (!pointer) return;

    const dir = e.evt.deltaY > 0 ? -1 : 1;

    const factor =
      dir > 0
        ? ZOOM_STEP
        : 1 / ZOOM_STEP;

    const minScale = Math.max(
      this.state.fit.scale,
      VIEW_MIN_SCALE
    );

    const maxScale = VIEW_MAX_SCALE;

    const newScale = clamp(
      oldScale * factor,
      minScale,
      maxScale
    );

    if (Math.abs(newScale - oldScale) < 1e-6) return;

    const worldPos = {
      x: (pointer.x - this.state.view.x) / oldScale,
      y: (pointer.y - this.state.view.y) / oldScale,
    };

    this.state.view.scale = newScale;

    this.state.view.x =
      pointer.x - worldPos.x * newScale;

    this.state.view.y =
      pointer.y - worldPos.y * newScale;

    this._userMovedView = true;

    this._applyView();

  });

}

// -----------------------------
// Drag reliability fix (Pointer Capture)
// Ensures Konva ALWAYS receives pointerup/cancel so drag releases correctly.
// Fixes: sticky drag, drag not releasing, weird Ã¢â‚¬Å“second clickÃ¢â‚¬Â behavior.
// -----------------------------
_wirePointerCaptureFix() {
  if (this.__pamelaPointerCaptureWired) return;
  this.__pamelaPointerCaptureWired = true;

  const container = this.stage?.container?.();
  if (!container) return;

  // Only helpful if the browser supports pointer capture
  const canCapture = typeof container.setPointerCapture === "function";
  if (!canCapture) return;

  const shouldCaptureForTarget = (t) => {
    if (!t) return false;
    if (t === this.stage) return false;
    if (t === this.nodes?.bg) return false;
    if (this._inlineEditActive) return false;
    if (this.panMode) return false;

    // Capture for draggable nodes that the user can move:
    // - Text
    // - Clipart images
    // - Panels images
    if (t.className === "Text") return !!t.draggable?.();
    if (t.className === "Image") {
      const a = t.attrs || {};
      if (a.pamelaClipart === true || a.pamelaPanel === true) return !!t.draggable?.();
    }
    return false;
  };

  // When pointer goes down on a draggable node, capture it so pointerup always arrives.
  this.stage.off("pointerdown.pamelaCap");
  this.stage.on("pointerdown.pamelaCap", (e) => {
    const t = e?.target;
    if (!shouldCaptureForTarget(t)) return;

    const pid = e?.evt?.pointerId;
    if (pid == null) return;

    try {
      container.setPointerCapture(pid);
    } catch (_) {
      // ignore
    }
  });

  // Release capture on up/cancel (capture phase so we win even if something stops bubbling)
  const release = (ev) => {
    const pid = ev?.pointerId;
    if (pid == null) return;

    try {
      container.releasePointerCapture(pid);
    } catch (_) {
      // ignore
    }
  };

  container.addEventListener("pointerup", release, true);
  container.addEventListener("pointercancel", release, true);
}


} // Ã¢Â¬â€¦Ã¯Â¸Â END OF CLASS PamelaMonumentEditorV2

function pamelaCloseAddonsFlyoutAfterMobileLaserSelection() {
  try {
    const isMobile = !!(
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    );
    if (!isMobile) return;

    if (typeof window.PAMELA_CLOSE_ADDONS_UI === "function") {
      window.PAMELA_CLOSE_ADDONS_UI();
      return;
    }

    const flyout = document.getElementById("pamela-addon-flyout");
    if (flyout) flyout.style.display = "none";
  } catch (_) {}
}

// ============================================================
// Global hooks used by the Clipart Library + Panels Library
// ============================================================

window.PAMELA_ADD_CLIPART_TO_STAGE = async (item) => {
  try {
    const editor =
      window.pamelaMonumentEditor || window.pamelaEditor;
    if (!editor)
      throw new Error("Editor instance not found on window.");

    if (typeof editor.addClipartFromUrl !== "function") {
      throw new Error("editor.addClipartFromUrl is not a function");
    }

    const url = String(item?.url || "");
    if (!url) throw new Error("Clipart item missing url.");

    if (window.__PAMELA_LASER_LIBRARY_PICK_MODE__ === true) {
      window.__PAMELA_LASER_LIBRARY_PICK_MODE__ = false;
      const targetSide =
        String(window.__PAMELA_LASER_LIBRARY_TARGET_SIDE__ || window.PAMELA_SIDES?.current || "front") === "back"
          ? "back"
          : "front";
      try { window.__PAMELA_LASER_LIBRARY_TARGET_SIDE__ = ""; } catch (_) {}
      try {
        if (typeof window.switchToSide === "function") {
          await window.switchToSide(targetSide);
        } else if (window.__CM_BACK_LETTERING__?.setSide) {
          window.__CM_BACK_LETTERING__.setSide(targetSide);
        }
      } catch (thumbErr) {
        throw thumbErr;
      }
      await window.PAMELA_placeLaserArtworkFromUrl(url, {
        source: "library",
        name: item?.name || "",
        original_name: item?.name || "",
        content_type: "image/svg+xml",
        side: targetSide
      });
      pamelaCloseAddonsFlyoutAfterMobileLaserSelection();
      try { window.PAMELA_CLOSE_CLIPART_UI?.(); } catch (_) {}
      return;
    }

    await editor.addClipartFromUrl(url, {
      id: item?.id || "",
      name: item?.name || "",
      category: item?.category || "",
      categoryId: item?.categoryId || "",
      subcategory: item?.subcategory || "",
    });
  } catch (e) {
    console.error(e);
    alert("Could not place clipart. Check console.");
  }
};

window.PAMELA_ADD_PANEL_TO_STAGE = async (item) => {
  try {
    const editor = window.pamelaMonumentEditor || window.pamelaEditor;
    if (!editor) throw new Error("Editor instance not found on window.");

    if (typeof editor.addPanelFromUrl !== "function") {
      throw new Error("editor.addPanelFromUrl is not a function");
    }

    const url = String(item?.url || "");
    if (!url) throw new Error("Panel item missing url.");

    // Ã¢Å“â€¦ Only enforce single-slot behavior for "Last Name Ã¢â‚¬â€ ..." categories
    const category = String(item?.category || "");
    const isLastName = category.startsWith("Last Name");
    const isNameDate = category.startsWith("Name/Dates");

    await editor.addPanelFromUrl(url, {
      ...item,
      id: item?.id || "",
      name: item?.name || "",
      category,
      // Ã¢Å“â€¦ tells addPanelFromUrl to replace existing last-name panel
      replaceGroup: isLastName ? "lastname" : "",
      panelGroup: isNameDate ? "name_date" : "",
    });
  } catch (e) {
    console.error(e);
    alert("Could not place panel/frame. Check console.");
  }
};
// -----------------------------
// Production Manifest Builder
// - Exports DESIGN-space transforms (viewport/zoom stripped out)
// - Deterministic placement for PDF using abs_transform
// - Captures ONLY the requested visible side by temporarily toggling side visibility
// - Injects portrait metadata into the manifest
// -----------------------------
function buildProductionManifest(editorInstance, sideOverride) {
  let manifest = {
    version: 1,
    created_at: new Date().toISOString(),
    product_handle: "",
    layout: "",
    variant_id: "",
    variant_title: "",
    editor_url: (function () { try { return window.location.href; } catch (_) { return ""; } })(),
    texts: [],
    images: [],
    notes: ""
  };

  const stage = editorInstance?.stage || editorInstance?.konvaStage || null;
  if (!stage || typeof stage.find !== "function") {
    manifest.notes =
      "Konva stage not found on editorInstance. Ensure window.pamelaEditor.stage exists.";
    return manifest;
  }

  const requestedSide =
    (sideOverride === "front" || sideOverride === "back")
      ? sideOverride
      : (window.PAMELA_SIDES?.current || "front");

  const originalSide =
    (window.PAMELA_SIDES?.current === "front" || window.PAMELA_SIDES?.current === "back")
      ? window.PAMELA_SIDES.current
      : "front";

  function setVisibleSide(side) {
    try {
      if (window.__CM_BACK_LETTERING__?.setSide) {
        window.__CM_BACK_LETTERING__.setSide(side);
      }
    } catch (e) {
      console.warn("Manifest side switch failed:", e);
    }
  }

  // ---- STRIP VIEWPORT (fit/zoom/pan) FROM ABSOLUTE MATRIX ----
  function stripViewFromAbsMatrix(m) {
    if (!Array.isArray(m) || m.length < 6) return null;

    const view = editorInstance?.state?.view || {};
    const s = Number(view.scale || 1) || 1;
    const vx = Number(view.x || 0) || 0;
    const vy = Number(view.y || 0) || 0;

    const ia = 1 / s, ib = 0, ic = 0, id = 1 / s, ie = -vx / s, iff = -vy / s;

    const a = m[0], b = m[1], c = m[2], d = m[3], e = m[4], f = m[5];

    return [
      ia * a + ic * b,
      ib * a + id * b,
      ia * c + ic * d,
      ib * c + id * d,
      ia * e + ic * f + ie,
      ib * e + id * f + iff
    ];
  }

  try {
    if (requestedSide !== originalSide) {
      setVisibleSide(requestedSide);
    }

    // ---- TEXT NODES ----
    try {
      const textNodes = (stage.find((n) => n && pamelaIsTextLikeNode(n)) || []);

      const BASELINE_SHIFT_RATIO_BY_FONT = Object.fromEntries(
        (Array.isArray(window.PAMELA_FONT_REGISTRY) ? window.PAMELA_FONT_REGISTRY : [])
          .map(f => [String(f.key || ""), Number(f.baseline_ratio || 0)])
          .filter(([k]) => k)
      );

      textNodes.forEach((n) => {
        try {
          if (typeof n.isVisible === "function" ? !n.isVisible() : (typeof n.visible === "function" && !n.visible())) return;

          const tr = (typeof n.getAbsoluteTransform === "function") ? n.getAbsoluteTransform() : null;
          const mRaw = tr && typeof tr.getMatrix === "function" ? tr.getMatrix() : null;
          const m = stripViewFromAbsMatrix(mRaw);

          const txt = (typeof n.text === "function" ? n.text() : n.attrs?.text || "");
          if (!txt) return;

          const fontFamily = (typeof n.fontFamily === "function" ? n.fontFamily() : n.attrs?.fontFamily || "");
          const fontStyle  = (typeof n.fontStyle === "function" ? n.fontStyle() : n.attrs?.fontStyle || "");
          const fontSize   = (typeof n.fontSize === "function" ? n.fontSize() : n.attrs?.fontSize || 0);

          const w = (typeof n.width === "function" ? n.width() : n.attrs?.width) ?? 0;
          const h = (typeof n.height === "function" ? n.height() : n.attrs?.height) ?? 0;

          const padding = (typeof n.padding === "function" ? n.padding() : n.attrs?.padding || 0);
          const lineHeight = (typeof n.lineHeight === "function" ? n.lineHeight() : n.attrs?.lineHeight || 1);
          const letterSpacing = (typeof n.letterSpacing === "function" ? n.letterSpacing() : n.attrs?.letterSpacing || 0);

          const baselineShiftY = Math.round(
            (BASELINE_SHIFT_RATIO_BY_FONT[fontFamily] || 0) * Number(fontSize || 0)
          );

          manifest.texts.push({
            id: n.id ? n.id() : "",
            name: n.name ? n.name() : "",
            node_type: n.className === "TextPath" ? "textPath" : "text",
            field_type: n.attrs?.pamelaFieldType || n.attrs?.fieldType || "",
            text: txt,

            font_family: fontFamily,
            font_size: Number(fontSize) || 0,
            font_style: fontStyle,
            fill: (typeof n.fill === "function" ? n.fill() : n.attrs?.fill || ""),
            align: (typeof n.align === "function" ? n.align() : n.attrs?.align || ""),

            padding,
            line_height: lineHeight,
            letter_spacing: letterSpacing,

            width: Number(w) || 0,
            height: Number(h) || 0,

            baseline_shift_y: Number(baselineShiftY) || 0,
            text_path_data: n.attrs?.pamelaTextPathData || "",
            text_path_view_box: n.attrs?.pamelaTextPathViewBox || null,

            abs_transform: Array.isArray(m) ? m.slice(0, 6) : null,
          });
        } catch (e) {
          console.warn("Manifest: skipping text node", e);
        }
      });
    } catch (e) {
      console.warn("Manifest: text scan failed", e);
    }

    // ---- IMAGE NODES ----
    try {
      const imageNodes = stage.find("Image") || [];
      imageNodes.forEach((n) => {
        try {
          if (typeof n.isVisible === "function" ? !n.isVisible() : (typeof n.visible === "function" && !n.visible())) return;
          const nodeName =
            (typeof n.name === "function"
              ? String(n.name() || "")
              : String(n.attrs?.name || "")
            ).toLowerCase();

          const isForegroundOverlay =
            nodeName.includes("foreground") ||
            nodeName.includes("overlay") ||
            nodeName.includes("fg");

          const isPortraitPhoto =
            nodeName.includes("pamela-portrait-photo") ||
            nodeName === "pamela-portrait-photo";

          const isInsidePortrait =
            (() => {
              try {
                let cur = n;
                let hops = 0;
                while (cur && hops < 12) {
                  if (
                    cur.getAttr?.("__isPortraitGroup") === true ||
                    cur.getAttr?.("pamelaType") === "portrait" ||
                    cur.getAttr?.("pamelaPortrait") === true ||
                    cur.getAttr?.("__pamelaKind") === "portrait" ||
                    String(cur.name?.() || "").includes("pamela-portrait-group")
                  ) {
                    return true;
                  }
                  cur = cur.getParent ? cur.getParent() : null;
                  hops++;
                }
              } catch (_) {}
              return false;
            })();

          if (isForegroundOverlay || isPortraitPhoto || isInsidePortrait) return;

          const srcFromAttrs =
            n.attrs?.src ||
            n.attrs?.url ||
            n.attrs?.href ||
            n.attrs?.imageUrl ||
            n.attrs?.pamelaSrc ||
            n.attrs?.pamelaClipartUrl ||
            n.attrs?.pamelaPanelUrl ||
            n.attrs?.pamelaLaserUrl ||
            "";

          const htmlImg = n.image && typeof n.image === "function" ? n.image() : null;
          const srcFromImage = (htmlImg && htmlImg.src) ? htmlImg.src : "";

          const src = srcFromAttrs || srcFromImage || "";
          const isLaserNode = n.attrs?.pamelaLaserEtching === true;
          const laserProcessedDataUrl =
            isLaserNode && htmlImg
              ? CM_imageSourceToDataUrl(htmlImg)
              : "";

          let filename = "";
          try {
            if (src) {
              const clean = src.split("?")[0];
              filename = clean.substring(clean.lastIndexOf("/") + 1);
            }
          } catch (_) {}

          const tr = n.getAbsoluteTransform && n.getAbsoluteTransform();
          const mRaw = tr && tr.getMatrix ? tr.getMatrix() : null;
          const m = stripViewFromAbsMatrix(mRaw);

          const w = (typeof n.width === "function" ? n.width() : n.attrs?.width) ?? 0;
          const h = (typeof n.height === "function" ? n.height() : n.attrs?.height) ?? 0;

          const offX = (typeof n.offsetX === "function" ? n.offsetX() : n.attrs?.offsetX) ?? 0;
          const offY = (typeof n.offsetY === "function" ? n.offsetY() : n.attrs?.offsetY) ?? 0;

          manifest.images.push({
            id: n.id ? n.id() : "",
            name: n.name ? n.name() : "",
            kind: isLaserNode ? "laser" : (n.attrs?.pamelaKind || n.attrs?.kind || ""),
            library_key: n.attrs?.pamelaKey || n.attrs?.key || "",
            src,
            original_src: isLaserNode ? src : "",
            processedDataUrl: laserProcessedDataUrl,
            laser_preview: isLaserNode ? (n.attrs?.pamelaLaserPreview || "") : "",
            laser_feather_px: isLaserNode ? Number(n.attrs?.pamelaLaserFeatherPx || 0) || 0 : 0,
            filename,

            width: Number(w) || 0,
            height: Number(h) || 0,
            offset_x: Number(offX) || 0,
            offset_y: Number(offY) || 0,

            abs_transform: Array.isArray(m) ? m.slice(0, 6) : null,
          });
        } catch (e) {
          console.warn("Manifest: skipping image node", e);
        }
      });
    } catch (e) {
      console.warn("Manifest: image scan failed", e);
    }

    // ---- PORTRAITS ----
    try {
      const rawPortraits = (window.CM_collectPortraitAssetsFromStage)
        ? window.CM_collectPortraitAssetsFromStage(editorInstance)
        : [];

      const portraits = Array.isArray(rawPortraits) ? rawPortraits : [];

      manifest = (window.CM_injectPortraitsIntoManifest)
        ? window.CM_injectPortraitsIntoManifest(manifest, portraits)
        : manifest;
    } catch (e) {
      console.warn("Manifest: portrait injection failed", e);
    }

  } finally {
    if (requestedSide !== originalSide) {
      setVisibleSide(originalSide);
    }
  }

  return manifest;
}
window.buildProductionManifest = buildProductionManifest;

async function buildFromProductionManifest(editorInstance, manifest) {
  console.warn(
    "buildFromProductionManifest is disabled for editor resume. " +
    "Use CM_restoreEditorNativeState(...) for editable draft restore."
  );
  return false;
}

// =============================
// Boot
// =============================
async function boot() {

  const wrapEl = document.getElementById("pamela-stageWrap");

  if (!wrapEl) {
    console.warn("pamela-stageWrap not found.");
    return;
  }

  wireGlobalAntiStickyDrag(); // Ã¢Å“â€¦ once per page load

  const params = new URLSearchParams(window.location.search);

  const handle = params.get("product");
  const variantIdFromUrl = params.get("variant");
  const draftTokenFromUrl = params.get("token");

  const rawLayout = String(params.get("layout") || "").toLowerCase().trim();
  const inferredLayoutFromHandle = (() => {
    const productContext = [
      handle || "",
      params.get("style") || "",
      document.querySelector(".product__title, .product-title, h1")?.textContent || ""
    ].join(" ");
    return pamelaInferLayoutFromText(productContext);
  })();

  const layout =
    rawLayout === "single"
      ? "single"
      : rawLayout === "companion"
      ? "companion"
      : inferredLayoutFromHandle === "single" || rawLayout.includes("single")
      ? "single"
      : inferredLayoutFromHandle === "companion"
      ? "companion"
      : "companion";

  window.PAMELA_EDITOR_LAYOUT = layout;

  const editor = new PamelaMonumentEditorV2({
    wrapEl,
    layout
  });

  editor.monumentStyle = String(params.get("style") || "").toLowerCase().trim();
  
  await editor.init();

  window.pamelaEditor = editor;
  window.pamelaMonumentEditor = editor;
  window.PAMELA_EDITOR_LAYOUT = editor.layout;
  try {
    window.requestAnimationFrame(() => pamelaShowStartGuidance(editor));
  } catch (_) {}

  ensureTopBar(wrapEl);
  ensureToolbar(wrapEl);
  ensureStatusLine(wrapEl);
  try {
const topTitleEl = document.getElementById("pamela-product-title");
const priceEl = document.getElementById("pamela-variant-price");
const selectEl = document.getElementById("pamela-variant-select");

const [
  productJson,
  graniteToneMapResult,
  variantImageMapResult,
  editorAssetMapResult,
  storedEditorAssetMapResult
] = await Promise.allSettled([
  fetchProductJsonByHandle(handle),
  fetchGraniteToneMapByHandle(handle),
  fetchVariantImageMapByHandle(handle),
  fetchEditorAssetMapByHandle(handle),
  fetchStoredEditorAssetMapByHandle(handle)
]);

if (!window.__PAMELA_VARIANT_BORDER_MAP__) {
  try {
    const apiBase =
      window.CM_SVG_API_BASE ||
      "https://cm-editor-server.onrender.com";

    const url =
      apiBase.replace(/\/+$/, "") +
      "/api/variant-border-map?handle=" +
      encodeURIComponent(handle);

    const res = await fetch(url, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });

    if (res.ok) {
      window.__PAMELA_VARIANT_BORDER_MAP__ = await res.json();
    } else {
      console.warn("[Variant Border Map] failed to load:", res.status);
      window.__PAMELA_VARIANT_BORDER_MAP__ = {};
    }
  } catch (e) {
    console.warn("[Variant Border Map] failed to load:", e);
    window.__PAMELA_VARIANT_BORDER_MAP__ = {};
  }
}

if (productJson.status !== "fulfilled") {
  throw productJson.reason || new Error("Failed to load product JSON.");
}

const resolvedProductJson = productJson.value;

if (graniteToneMapResult.status === "fulfilled") {
  window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ =
    graniteToneMapResult.value && typeof graniteToneMapResult.value === "object"
      ? graniteToneMapResult.value
      : {};
} else {
  console.warn("[Granite Tone Map] failed to load:", graniteToneMapResult.reason);
  window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ = {};
}

if (variantImageMapResult.status === "fulfilled") {
  window.__PAMELA_VARIANT_IMAGE_MAP__ =
    variantImageMapResult.value && typeof variantImageMapResult.value === "object"
      ? variantImageMapResult.value
      : {};
} else {
  console.warn("[Variant Image Map] failed to load:", variantImageMapResult.reason);
  window.__PAMELA_VARIANT_IMAGE_MAP__ = {};
}

if (editorAssetMapResult.status === "fulfilled" || storedEditorAssetMapResult.status === "fulfilled") {
  const serverAssetMap =
    editorAssetMapResult.status === "fulfilled" &&
    editorAssetMapResult.value &&
    typeof editorAssetMapResult.value === "object"
      ? editorAssetMapResult.value
      : {};

  const storedAssetMap =
    storedEditorAssetMapResult.status === "fulfilled" &&
    storedEditorAssetMapResult.value &&
    typeof storedEditorAssetMapResult.value === "object"
      ? storedEditorAssetMapResult.value
      : {};

  window.__PAMELA_VARIANT_ASSET_MAP__ = {
    ...serverAssetMap,
    ...storedAssetMap
  };
} else {
  console.warn("[Editor Asset Map] failed to load:", editorAssetMapResult.reason);
  window.__PAMELA_VARIANT_ASSET_MAP__ = {};
}

window.__PAMELA_PRODUCT_TITLE__ =
  String(resolvedProductJson?.title || "");

window.__PAMELA_PRODUCT_HANDLE__ =
  String(resolvedProductJson?.handle || handle || "");

if (topTitleEl)
  topTitleEl.textContent =
    resolvedProductJson?.title || "Monument";

if (topTitleEl)
  topTitleEl.style.visibility = "visible";

if (!selectEl)
  throw new Error("Variant select missing.");

    const variants =
      Array.isArray(resolvedProductJson?.variants)
        ? resolvedProductJson.variants
        : [];

    if (!variants.length)
      throw new Error("No variants found.");

    selectEl.innerHTML = "";

    variants.forEach((v) => {

      const opt = document.createElement("option");

      opt.value = String(v.id);
      opt.textContent =
        v.title || `Variant ${v.id}`;

      selectEl.appendChild(opt);

    });

    const initialVariant =
      pickVariant(resolvedProductJson, variantIdFromUrl);

    if (!initialVariant)
      throw new Error("Could not pick a starting variant.");

    selectEl.value = String(initialVariant.id);

    const applyVariantSideAssets = async (variant, side, opts = {}) => {
      const urls = pickSideVariantAssetUrls(resolvedProductJson, variant, side);
      const stageContainer = editor?.stage?.container?.() || null;

      if (stageContainer && opts.hideDuringSwap !== false) {
        stageContainer.style.visibility = "visible";
        stageContainer.style.opacity = "0";
      }

      try {
        if (urls.background) {
          await editor.setBackgroundImage(urls.background, {
            resetView: opts.resetView === true,
            mirrorX: urls.mirrorBackground === true
          });
        }

        await editor.setBorderOverlayImage(urls.foreground || "", {
          mirrorX: urls.mirrorForeground === true
        });
      } finally {
        if (stageContainer && opts.hideDuringSwap !== false) {
          try { editor.layers?.background?.batchDraw?.(); } catch (_) {}
          try { editor.layers?.border?.batchDraw?.(); } catch (_) {}
          try { editor.stage?.batchDraw?.(); } catch (_) {}
          const revealStage = () => {
            stageContainer.style.visibility = "visible";
            stageContainer.style.opacity = "1";
          };
          requestAnimationFrame(revealStage);
          setTimeout(revealStage, 250);
        }
      }
    };

    window.__PAMELA_APPLY_SIDE_ASSETS__ = async (side, opts = {}) => {
      const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || initialVariant || null;
      if (!selectedVariant) return;
      await applyVariantSideAssets(selectedVariant, side, opts);
    };

    // Exposed for the laser "Fit to Stone" modal (defined later in this file,
    // outside this closure) so it can fetch the true foreground overlay for
    // the current variant/side without duplicating the asset-map logic.
    window.__PAMELA_GET_FOREGROUND_URL__ = (side) => {
      try {
        const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || initialVariant || null;
        if (!selectedVariant) return "";
        return pickSideVariantAssetUrls(resolvedProductJson, selectedVariant, side || "front").foreground || "";
      } catch (_) {
        return "";
      }
    };

    const applyVariant = async (variantId, opts = {}) => {
      const resetView = opts.resetView === true;
      const previousVariant = window.__PAMELA_SELECTED_VARIANT__ || null;

      const v =
        variants.find((x) => String(x.id) === String(variantId)) ||
        initialVariant;
      const resetSvgModesForToneChange = !!(
        previousVariant &&
        String(previousVariant.id) !== String(v.id) &&
        pamelaGetDefaultSvgModeForVariant(previousVariant) !==
          pamelaGetDefaultSvgModeForVariant(v)
      );

 window.__PAMELA_SELECTED_VARIANT__ = v || null;
 try { pamelaSyncDarkGraniteFullscreenBackground(editor, v); } catch (_) {}

if (priceEl)
  priceEl.textContent =
    formatMoneyFromCents(v.price);

const currentSide =
  (window.PAMELA_SIDES?.current === "back")
    ? "back"
    : "front";

await applyVariantSideAssets(v, currentSide, { resetView });

window.__PAMELA_SELECTED_VARIANT__ = v || null;

await pamelaApplyVariantAppearance(editor, v, {
  saveSideState: false,
  resetSvgModesForToneChange
});
try { saveCurrentSideState(); } catch (_) {}
pamelaUpdateLightGraniteToggleVisibility();
try { pamelaUpdateSvgModeButtonLabels?.(); } catch (_) {}

try {

  const u = new URL(window.location.href);

  u.searchParams.set("product", handle || "");
  u.searchParams.set("variant", String(v.id));

  window.history.replaceState({}, "", u.toString());

} catch (_) {}

try { selectEl.dataset.pamelaAppliedVariantId = String(v.id); } catch (_) {}

};

    // Draft resume restores editable nodes after the initial variant is applied.
    // Keep this shared pass available so the restored nodes receive the same
    // text and automatic SVG treatment as artwork placed in a fresh editor.
    async function pamelaApplyVariantAppearance(editorInstance, variant, opts = {}) {
      if (!editorInstance || !variant) return false;

      const fill = getAutoTextFillForVariant(variant);
      editorInstance.setTextFill(fill);

      try { pamelaSyncVariantIntoStoredSideDrafts(fill); } catch (_) {}
      if (opts.resetSvgModesForToneChange) {
        try { pamelaSyncVariantSvgIntoStoredSideDrafts(variant); } catch (_) {}
      }

      // Only a light <-> dark transition replaces existing SVG styling.
      try {
        if (opts.resetSvgModesForToneChange) {
          const automaticNodes = pamelaGetAllPlacedSvgNodes(editorInstance);

          automaticNodes.forEach((node) => {
            if (!node) return;

            if (node.getAttr?.("pamelaPanel") === true) {
              node.setAttr("pamelaPanelMode", "auto");
            } else if (node.getAttr?.("pamelaClipart") === true) {
              node.setAttr("pamelaClipartMode", "auto");
            }
            node.setAttr("pamelaSvgModeExplicit", false);
          });
        }
      } catch (_) {}

      await pamelaRefreshPlacedSvgNodesForGranite(editorInstance);
      // Refresh replaces the SVG images asynchronously. Rebuild the panel
      // cutouts only after replacement so the restored panel is immediately
      // knocked out behind clipart—without requiring a customer to move it.
      try { pamelaRebuildPanelKnockouts(editorInstance); } catch (_) {}
      try { pamelaSchedulePanelKnockoutRebuild(editorInstance); } catch (_) {}

      if (opts.saveSideState !== false) {
        try { saveCurrentSideState(); } catch (_) {}
      }

      pamelaUpdateLightGraniteToggleVisibility();
      return true;
    }

    window.PAMELA_applyVariantAppearance = pamelaApplyVariantAppearance;

await applyVariant(initialVariant.id, { resetView: true });
try {
  const forceStageVisible = () => {
    const c = editor?.stage?.container?.();
    if (!c) return;
    c.style.visibility = "visible";
    c.style.opacity = "1";
  };
  forceStageVisible();
  setTimeout(forceStageVisible, 300);
  setTimeout(forceStageVisible, 1000);
} catch (_) {}
window.__PAMELA_EDITOR_BOOT_DONE__ = true;
window.buildFromProductionManifest = buildFromProductionManifest;

// =============================
// Finalize & Add to Cart (AUTO-LOADS + PRICES + TRUE FRONT/BACK)
// =============================

const finalizeBtn = document.getElementById("pamela-finalize-addtocart");
const finalizeStatus = document.getElementById("pamela-finalize-status");

const setFinalizeStatus = (msg) => {
  if (finalizeStatus) finalizeStatus.textContent = msg || "";
};

let finalizeOverlayEl = null;
function setFinalizeOverlay(visible, message) {
  try {
    const stageWrap = document.getElementById("pamela-stageWrap");
    if (!stageWrap || !stageWrap.parentNode) return;

    if (visible) {
      if (finalizeOverlayEl && finalizeOverlayEl.isConnected) {
        const existingCard = finalizeOverlayEl.querySelector("[data-pamela-finalize-card]");
        if (existingCard) existingCard.textContent = message || "Saving your design and updating your cart...";
        return;
      }

      const host = stageWrap.parentNode;
      if (host && host.style && !host.style.position) {
        host.style.position = "relative";
      }

      const overlay = document.createElement("div");
      overlay.id = "pamela-finalize-overlay";
      overlay.setAttribute("aria-live", "polite");
      overlay.style.cssText =
        "position:absolute;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;" +
        "background:rgba(255,255,255,0.82);backdrop-filter:blur(2px);";

      const card = document.createElement("div");
      card.setAttribute("data-pamela-finalize-card", "true");
      card.style.cssText =
        "padding:14px 18px;border:1px solid #ddd;border-radius:12px;background:#fff;color:#111;" +
        "font:800 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-shadow:0 10px 30px rgba(0,0,0,0.08);";
      card.textContent = message || "Saving your design and updating your cart...";

      overlay.appendChild(card);
      host.appendChild(overlay);
      finalizeOverlayEl = overlay;
      return;
    }

    if (finalizeOverlayEl && finalizeOverlayEl.parentNode) {
      finalizeOverlayEl.parentNode.removeChild(finalizeOverlayEl);
    }
    finalizeOverlayEl = null;
  } catch (_) {}
}

let laserAiOverlayEl = null;
function getLaserAiOverlayMessage(kind) {
  const laserKind = String(kind || "").toLowerCase();
  if (laserKind === "scene") {
    return "Reviewing your image and preparing it for scene etching. This can take a few seconds.";
  }
  return "Reading your image and looking for people. This can take a few seconds.";
}

function setLaserAiOverlay(visible, message) {
  try {
    if (visible) {
      if (laserAiOverlayEl && laserAiOverlayEl.isConnected) {
        const msg = laserAiOverlayEl.querySelector("[data-pamela-laser-ai-message]");
        if (msg) msg.textContent = message || getLaserAiOverlayMessage();
        return;
      }

      const overlay = document.createElement("div");
      overlay.id = "pamela-laser-ai-overlay";
      overlay.setAttribute("aria-live", "polite");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;" +
        "background:rgba(17,17,17,0.38);backdrop-filter:blur(3px);padding:18px;box-sizing:border-box;";

      const card = document.createElement("div");
      card.style.cssText =
        "max-width:520px;width:min(520px,100%);padding:24px 26px;border-radius:14px;background:#fff;color:#111;" +
        "border:1px solid rgba(0,0,0,0.12);box-shadow:0 20px 60px rgba(0,0,0,0.24);" +
        "font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial;text-align:center;";

      const spinner = document.createElement("div");
      spinner.style.cssText =
        "width:34px;height:34px;margin:0 auto 13px;border-radius:999px;border:4px solid #ddd;border-top-color:#111;" +
        "animation:pamelaLaserAiSpin .8s linear infinite;";

      const style = document.createElement("style");
      style.textContent = "@keyframes pamelaLaserAiSpin{to{transform:rotate(360deg)}}";

      const title = document.createElement("div");
      title.textContent = "Checking your laser image";
      title.style.cssText = "font:900 25px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;margin-bottom:10px;";

      const body = document.createElement("div");
      body.setAttribute("data-pamela-laser-ai-message", "true");
      body.textContent = message || getLaserAiOverlayMessage();
      body.style.cssText = "font:800 17px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;";

      const fine = document.createElement("div");
      fine.textContent = "Please keep this page open. AI results should be checked for accuracy.";
      fine.style.cssText = "font:800 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-top:12px;";

      card.appendChild(spinner);
      card.appendChild(title);
      card.appendChild(body);
      card.appendChild(fine);
      overlay.appendChild(style);
      overlay.appendChild(card);
      document.body.appendChild(overlay);
      laserAiOverlayEl = overlay;
      return;
    }

    if (laserAiOverlayEl && laserAiOverlayEl.parentNode) {
      laserAiOverlayEl.parentNode.removeChild(laserAiOverlayEl);
    }
      laserAiOverlayEl = null;
  } catch (_) {}
}

function pamelaIsHeicLikeFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return type.includes("heic") || type.includes("heif") || /\.(heic|heif)$/i.test(name);
}

function pamelaIsSupportedCustomerImageFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return (
    type === "image/png" ||
    type === "image/jpeg" ||
    type === "image/jpg" ||
    type === "image/pjpeg" ||
    type === "image/heic" ||
    type === "image/heif" ||
    /\.(png|jpe?g|heic|heif)$/i.test(name)
  );
}

function pamelaReadImageDimensionsFromFile(file) {
  return new Promise((resolve) => {
    if (!file || pamelaIsHeicLikeFile(file)) return resolve(null);
    let url = "";
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      try { if (url) URL.revokeObjectURL(url); } catch (_) {}
      resolve(value);
    };
    try {
      url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        done({
          width: Number(img.naturalWidth || img.width || 0) || 0,
          height: Number(img.naturalHeight || img.height || 0) || 0
        });
      };
      img.onerror = () => {
        done(null);
      };
      img.src = url;
      setTimeout(() => done(null), 1200);
    } catch (_) {
      done(null);
    }
  });
}

function pamelaGetLaserQualityTargetInches(options = {}) {
  const kind = String(options?.laser_kind || options?.kind || document.getElementById("pamela_laser_kind")?.value || "spot").toLowerCase();
  if (kind === "scene") {
    try {
      const ed = getPamelaEditorInstance();
      const rect = typeof pamelaGetLaserPlacementRect === "function" ? pamelaGetLaserPlacementRect(ed) : null;
      const widthIn = Math.max(1, Number(rect?.width || 0) / DPI);
      const heightIn = Math.max(1, Number(rect?.height || 0) / DPI);
      return {
        kind,
        widthIn: Math.max(1, widthIn - 2),
        heightIn,
        targetDpi: 150
      };
    } catch (_) {}
  }
  return {
    kind,
    widthIn: 10,
    heightIn: 10,
    targetDpi: 150
  };
}

async function pamelaGetUploadQualityInfo(file, options = {}) {
  const isHeic = pamelaIsHeicLikeFile(file);
  const dims = await pamelaReadImageDimensionsFromFile(file);
  const width = Number(dims?.width || 0) || 0;
  const height = Number(dims?.height || 0) || 0;
  const target = pamelaGetLaserQualityTargetInches(options);
  const aspect = width > 0 && height > 0 ? width / height : 1;
  let placedWIn = Number(target.widthIn || 10) || 10;
  let placedHIn = placedWIn / (aspect || 1);
  const maxHIn = Number(target.heightIn || 0) || 0;
  if (maxHIn > 0 && placedHIn > maxHIn) {
    placedHIn = maxHIn;
    placedWIn = placedHIn * (aspect || 1);
  }
  const effectiveDpi = width && height
    ? Math.min(width / Math.max(0.01, placedWIn), height / Math.max(0.01, placedHIn))
    : 0;
  const targetDpi = Number(target.targetDpi || 150) || 150;
  const lowRes = !!(width && height && effectiveDpi < targetDpi);
  return { isHeic, lowRes, width, height, effectiveDpi, targetDpi, placedWIn, placedHIn, targetKind: target.kind };
}

function pamelaShowUploadQualityChoice(file, info = {}, context = {}) {
  return new Promise((resolve) => {
    try {
      if (!info.isHeic && !info.lowRes) return resolve({ action: "accept", enhancement: false, info });

      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;" +
        "background:rgba(17,17,17,.48);backdrop-filter:blur(3px);padding:18px;box-sizing:border-box;";

      const card = document.createElement("div");
      card.style.cssText =
        "width:min(620px,100%);max-height:min(86vh,720px);overflow:auto;border-radius:14px;background:#fff;color:#111;" +
        "border:1px solid rgba(0,0,0,.14);box-shadow:0 24px 70px rgba(0,0,0,.28);padding:24px 26px;" +
        "font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial;";

      const title = document.createElement("div");
      title.style.cssText = "font:900 25px/1.15 system-ui,-apple-system,Segoe UI,Roboto,Arial;margin-bottom:12px;";
      title.textContent = info.isHeic ? "HEIC Image Detected" : "Low Resolution Image Detected";

      const body = document.createElement("div");
      body.style.cssText = "font:800 16px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;margin-bottom:16px;";
      if (info.isHeic) {
        body.textContent =
          "We've detected your image is a HEIC file type. These files are often stored at lower resolution to save space on phones and other devices. While they can look great on phones and tablets, they are not always ideal for laser etching or portrait preparation. Would you like to include a $25 image enhancement fee with your purchase?";
      } else {
        const dpiText = info.effectiveDpi
          ? ` For the size this image will be placed, it is about ${Math.round(info.effectiveDpi)} DPI. We recommend at least ${Math.round(Number(info.targetDpi || 150) || 150)} DPI for cleaner laser etching.`
          : "";
        body.textContent =
          `This image appears to be low resolution.${dpiText} Low-resolution images may not prepare as cleanly for laser etching or portrait work. Would you like to include a $25 image enhancement fee with your purchase?`;
      }

      const fileLine = document.createElement("div");
      fileLine.style.cssText = "font:800 13px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-bottom:16px;";
      fileLine.textContent = String(file?.name || context.label || "Selected image");

      const actions = document.createElement("div");
      actions.style.cssText = "display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;";
      const mkBtn = (text, primary) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = text;
        btn.style.cssText =
          "padding:11px 14px;border-radius:10px;border:1px solid #111;cursor:pointer;" +
          `background:${primary ? "#111" : "#fff"};color:${primary ? "#fff" : "#111"};` +
          "font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
        return btn;
      };

      const chooseNew = mkBtn("Choose New Photo", false);
      const accept = mkBtn("Accept As Is", false);
      const enhance = mkBtn("Add $25 Image Enhancement", true);
      const close = (result) => {
        try { overlay.remove(); } catch (_) {}
        resolve(result);
      };
      chooseNew.addEventListener("click", () => close({ action: "choose-new", enhancement: false, info }));
      accept.addEventListener("click", () => close({ action: "accept", enhancement: false, info }));
      enhance.addEventListener("click", () => close({ action: "enhance", enhancement: true, info }));

      actions.appendChild(chooseNew);
      actions.appendChild(accept);
      actions.appendChild(enhance);
      card.appendChild(title);
      card.appendChild(body);
      card.appendChild(fileLine);
      card.appendChild(actions);
      overlay.appendChild(card);
      document.body.appendChild(overlay);
    } catch (_) {
      resolve({ action: "accept", enhancement: false, info });
    }
  });
}

function showLaserPeopleConfirmModal(node, options = {}) {
  return new Promise((resolve) => {
    try {
      if (!node || node.getAttr?.("pamelaLaserEtching") !== true) return resolve({ action: "skip" });
      const kind = String(node.getAttr?.("pamelaLaserKind") || options?.laser_kind || "spot").toLowerCase();
      const rawCount = Math.max(0, Number(node.getAttr?.("pamelaLaserFaces") || 0) || 0);
      const count = kind === "portrait" ? Math.max(1, rawCount || 1) : rawCount;
      const isSceneReview = kind === "scene" && count > 2;
      const showPeopleControls = !isSceneReview && !(kind === "spot" && count <= 0);
      const needsReview = node.getAttr?.("pamelaLaserFacesNeedsReview") === true;
      const alreadyConfirmed = node.getAttr?.("pamelaLaserPeopleConfirmed") === true;
      const shouldPrompt =
        options?.force === true ||
        (options?.afterUpload === true && kind !== "scene") ||
        kind === "portrait" ||
        isSceneReview ||
        (kind === "spot" && count > 0) ||
        (kind !== "scene" && needsReview);
      if (!shouldPrompt || alreadyConfirmed) return resolve({ action: "skip" });
      const needsQualityAck = window.__PAMELA_LASER_QUALITY_ACKED__ !== true;
      const existingNotes = String(node.getAttr?.("pamelaLaserNotes") || "");

      const overlay = document.createElement("div");
      overlay.id = "pamela-laser-people-confirm";
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;" +
        "background:rgba(17,17,17,0.48);backdrop-filter:blur(3px);padding:18px;box-sizing:border-box;";

      const card = document.createElement("div");
      card.style.cssText =
        "width:min(620px,100%);max-height:min(86vh,720px);overflow:auto;border-radius:14px;background:#fff;color:#111;border:1px solid rgba(0,0,0,0.14);" +
        "box-shadow:0 24px 70px rgba(0,0,0,0.28);padding:24px 26px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial;";

      const title = document.createElement("div");
      title.style.cssText = "font:900 26px/1.15 system-ui,-apple-system,Segoe UI,Roboto,Arial;margin-bottom:12px;";
      title.textContent = kind === "spot"
        ? (count > 0 ? "People detected in spot etching" : "Confirm spot etching")
        : (kind === "scene" ? "Review this scene image" : "Confirm People in This Portrait");

      const body = document.createElement("div");
      body.style.cssText = "font:800 17px/1.42 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;margin-bottom:16px;";
      if (kind === "spot") {
        body.textContent = count > 0
          ? "People were detected in this spot etching image. Spot pricing does not include people in the finished artwork. If people should be removed and only the object, house, vehicle, symbol, or background should remain, choose Continue as Spot and tell us what to keep or remove. If any people should remain in the finished artwork, switch to portrait."
          : "Please confirm this spot etching image and add any special requests before continuing.";
      } else if (kind === "scene") {
        body.textContent = "Scene etching is meant for one complete photo or artwork, such as a landscape, place, vehicle, pet, or a natural moment with people in it. It is not meant for portrait collages or multiple separate photos combined into one scene.";
      } else {
        body.textContent = "Portrait pricing includes 1 person. Each additional person included in the finished artwork is $75 per person.";
      }

      const label = document.createElement("label");
      label.style.cssText = "display:grid;gap:7px;font:900 16px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:14px;";
      label.textContent = "How many people should be included in this laser etching?";
      const input = document.createElement("input");
      input.type = "number";
      input.min = kind === "portrait" ? "1" : "0";
      input.step = "1";
      input.value = String(count);
      input.style.cssText =
        "width:100%;box-sizing:border-box;padding:12px 13px;border:1px solid #ccc;border-radius:10px;" +
        "font:900 20px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
      label.appendChild(input);

      const fine = document.createElement("div");
      fine.style.cssText = "font:750 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin:-4px 0 16px;";
      fine.textContent = "AI may miss people or count extra background people. Please verify or adjust this number based on how many people you want included in the finished artwork.";

      const notesLabel = document.createElement("label");
      notesLabel.style.cssText = "display:grid;gap:7px;font:900 16px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin:0 0 14px;";
      notesLabel.textContent = "Special Requests for This Image";
      const notesInput = document.createElement("textarea");
      notesInput.rows = 3;
      notesInput.value = existingNotes;
      notesInput.placeholder = showPeopleControls
        ? "Tell us if you would like anything removed, softened, adjusted, or if certain people should not be included."
        : "Tell us if you would like anything removed, softened, adjusted, or if anything specific should be kept.";
      notesInput.style.cssText =
        "width:100%;box-sizing:border-box;padding:12px 13px;border:1px solid #ccc;border-radius:10px;resize:vertical;" +
        "font:800 15px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
      notesLabel.appendChild(notesInput);

      let qualityAck = null;
      let qualityBox = null;
      if (needsQualityAck) {
        qualityBox = document.createElement("label");
        qualityBox.style.cssText =
          "display:block;padding:13px 14px;border-radius:12px;border:1px solid #d6c18b;" +
          "background:#fff8e6;color:#2b2112;font:800 14px/1.42 system-ui,-apple-system,Segoe UI,Roboto,Arial;margin:0 0 16px;";
        qualityAck = document.createElement("input");
        qualityAck.type = "checkbox";
        qualityAck.style.cssText = "margin:1px 8px 0 0;transform:scale(1.25);";
        const qualityText = document.createElement("div");
        qualityText.innerHTML =
          `<div style="font:900 17px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:8px;">Before You Continue</div>` +
          `<p style="margin:0 0 8px;">Laser etching is a beautiful way to place a photo or scene on black granite, but it is not the same as printing a photograph on paper. The final etched appearance depends on the quality, contrast, lighting, and detail of the image provided, along with the natural characteristics of the granite.</p>` +
          `<p style="margin:0 0 8px;">The preview shown here is a guide to the artwork layout and preparation. It will be close, but it will not show every natural variation in the stone or every difference created by the laser etching process.</p>` +
          `<p style="margin:0 0 10px;">If additional paid editing is needed, Creative Monument will contact you before completing those edits.</p>`;
        const qualityAcceptRow = document.createElement("div");
        qualityAcceptRow.style.cssText = "display:flex;gap:8px;align-items:flex-start;font:900 14px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
        const qualityAcceptText = document.createElement("span");
        qualityAcceptText.textContent = "I have read and accept the statement above";
        qualityAcceptRow.appendChild(qualityAck);
        qualityAcceptRow.appendChild(qualityAcceptText);
        qualityBox.appendChild(qualityText);
        qualityBox.appendChild(qualityAcceptRow);
        qualityAck.addEventListener("change", () => {
          if (!qualityAck.checked) return;
          try {
            qualityBox.style.borderColor = "#d6c18b";
            qualityBox.style.boxShadow = "";
          } catch (_) {}
        });
      }
      card.appendChild(title);
      card.appendChild(body);
      if (showPeopleControls) {
        card.appendChild(label);
        card.appendChild(fine);
      }

      const actions = document.createElement("div");
      actions.style.cssText = "display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;";
      const spotActions = document.createElement("div");
      spotActions.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:10px;width:100%;";
      const spotTopRow = document.createElement("div");
      spotTopRow.style.cssText = "grid-column:1 / -1;display:flex;justify-content:center;";
      const makeBtn = (text, primary) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = text;
        btn.style.cssText =
          "padding:11px 14px;border-radius:10px;border:1px solid #111;cursor:pointer;" +
          `background:${primary ? "#111" : "#fff"};color:${primary ? "#fff" : "#111"};` +
          "font:900 15px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;";
        return btn;
      };

      const close = (result) => {
        try { overlay.remove(); } catch (_) {}
        resolve(result || { action: "skip" });
      };
      const readPeople = () => {
        const raw = parseInt(input.value || (kind === "portrait" ? "1" : "0"), 10);
        return kind === "portrait" ? Math.max(1, raw || 1) : Math.max(0, Number.isFinite(raw) ? raw : 0);
      };
      const markFieldNeeded = (el) => {
        try {
          el?.animate?.(
            [
              { transform: "translateX(0)", borderColor: "#ccc" },
              { transform: "translateX(-8px)", borderColor: "#b00020" },
              { transform: "translateX(8px)", borderColor: "#b00020" },
              { transform: "translateX(-6px)", borderColor: "#b00020" },
              { transform: "translateX(6px)", borderColor: "#b00020" },
              { transform: "translateX(0)", borderColor: "#b00020" }
            ],
            { duration: 420, easing: "ease-in-out" }
          );
          el.style.borderColor = "#b00020";
          el.style.boxShadow = "0 0 0 3px rgba(176,0,32,0.12)";
        } catch (_) {}
        try { el?.focus?.(); } catch (_) {}
      };
      const confirmPeople = (newKind, overridePeople) => {
        if (qualityAck && !qualityAck.checked) {
          try {
            qualityBox?.animate?.(
              [
                { transform: "translateX(0)", borderColor: "#d6c18b" },
                { transform: "translateX(-8px)", borderColor: "#b00020" },
                { transform: "translateX(8px)", borderColor: "#b00020" },
                { transform: "translateX(-6px)", borderColor: "#b00020" },
                { transform: "translateX(6px)", borderColor: "#b00020" },
                { transform: "translateX(0)", borderColor: "#b00020" }
              ],
              { duration: 420, easing: "ease-in-out" }
            );
            qualityBox.style.borderColor = "#b00020";
            qualityBox.style.boxShadow = "0 0 0 3px rgba(176,0,32,0.12)";
          } catch (_) {}
          try { qualityAck.focus(); } catch (_) {}
          return;
        }
        const notes = String(notesInput.value || "").trim();
        if (newKind === "spot" && Number(overridePeople) === 0 && count > 0 && !notes) {
          markFieldNeeded(notesInput);
          notesInput.placeholder = "Please tell us what parts of the image are important to keep.";
          return;
        }
        const people = Number.isFinite(Number(overridePeople)) ? Math.max(0, Number(overridePeople) || 0) : readPeople();
        try {
          if (newKind) node.setAttr("pamelaLaserKind", newKind);
          node.setAttr("pamelaLaserFaces", newKind === "portrait" ? Math.max(1, people || 1) : people);
          node.setAttr("pamelaLaserFacesManualOverride", true);
          node.setAttr("pamelaLaserFacesNeedsReview", false);
          node.setAttr("pamelaLaserFaceDetectionStatus", "manual");
          node.setAttr("pamelaLaserPeopleConfirmed", true);
          node.setAttr("pamelaLaserNotes", notes);
          window.__PAMELA_LASER_QUALITY_ACKED__ = true;
          if (newKind === "portrait") {
            const kindEl = document.getElementById("pamela_laser_kind");
            if (kindEl) kindEl.value = "portrait";
          }
          try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
          try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
          try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
          try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
        } catch (_) {}
        close({ action: "confirmed", kind: newKind || kind, people });
      };

      const confirmSceneReview = (isCollage) => {
        if (qualityAck && !qualityAck.checked) {
          try {
            qualityBox?.animate?.(
              [
                { transform: "translateX(0)", borderColor: "#d6c18b" },
                { transform: "translateX(-8px)", borderColor: "#b00020" },
                { transform: "translateX(8px)", borderColor: "#b00020" },
                { transform: "translateX(-6px)", borderColor: "#b00020" },
                { transform: "translateX(6px)", borderColor: "#b00020" },
                { transform: "translateX(0)", borderColor: "#b00020" }
              ],
              { duration: 420, easing: "ease-in-out" }
            );
            qualityBox.style.borderColor = "#b00020";
            qualityBox.style.boxShadow = "0 0 0 3px rgba(176,0,32,0.12)";
          } catch (_) {}
          try { qualityAck.focus(); } catch (_) {}
          return;
        }

        const notes = String(notesInput.value || "").trim();
        try {
          node.setAttr("pamelaLaserFaces", count);
          node.setAttr("pamelaLaserFacesManualOverride", true);
          node.setAttr("pamelaLaserFacesNeedsReview", false);
          node.setAttr("pamelaLaserFaceDetectionStatus", "manual");
          node.setAttr("pamelaLaserPeopleConfirmed", true);
          node.setAttr(
            "pamelaLaserNotes",
            [
              notes,
              isCollage
                ? "Customer indicated this scene may include a portrait collage or group portrait; review pricing before production."
                : "Customer confirmed this is one complete scene, not a portrait collage."
            ].filter(Boolean).join(" ")
          );
          window.__PAMELA_LASER_QUALITY_ACKED__ = true;
          try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
          try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
          try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
        } catch (_) {}
        close({ action: "confirmed", kind: "scene", people: count, collage: !!isCollage });
      };

      if (isSceneReview) {
        const noCollage = makeBtn("No, this is one scene", true);
        const yesCollage = makeBtn("Yes, this is a collage or group portrait", false);
        noCollage.addEventListener("click", () => confirmSceneReview(false));
        yesCollage.addEventListener("click", () => confirmSceneReview(true));
        actions.appendChild(yesCollage);
        actions.appendChild(noCollage);
      } else if (kind === "spot" && count > 0) {
        const continueSpot = makeBtn("Continue as Spot - Remove People", true);
        const choose = makeBtn("Choose Different File", false);
        const portrait = makeBtn("Switch to Portrait", false);
        continueSpot.style.minWidth = "min(100%,360px)";
        choose.style.width = "100%";
        portrait.style.width = "100%";
        continueSpot.addEventListener("click", () => confirmPeople("spot", 0));
        choose.addEventListener("click", () => close({ action: "choose-file" }));
        portrait.addEventListener("click", () => confirmPeople("portrait"));
        spotTopRow.appendChild(continueSpot);
        spotActions.appendChild(spotTopRow);
        spotActions.appendChild(choose);
        spotActions.appendChild(portrait);
        actions.appendChild(spotActions);
      } else {
        const confirm = makeBtn("Confirm and Continue", true);
        confirm.addEventListener("click", () => confirmPeople(kind));
        actions.appendChild(confirm);
      }

      card.appendChild(notesLabel);
      if (qualityBox) card.appendChild(qualityBox);
      card.appendChild(actions);
      overlay.appendChild(card);
      document.body.appendChild(overlay);
      try {
        if (showPeopleControls) {
          input.focus();
          input.select();
        } else {
          notesInput.focus();
        }
      } catch (_) {}
    } catch (_) {
      resolve({ action: "skip" });
    }
  });
}

function chunkString(str, chunkSize) {
  const out = [];
  for (let i = 0; i < str.length; i += chunkSize) out.push(str.slice(i, i + chunkSize));
  return out;
}

function b64EncodeUnicode(s) {
  return btoa(unescape(encodeURIComponent(s)));
}

function isInlineImagePayload(value) {
  return /^data:image\//i.test(String(value || "").trim());
}

function isCartSafeImageReference(value) {
  const v = String(value || "").trim();
  return !!v && !isInlineImagePayload(v);
}

function getCartSafeImageReference() {
  for (let i = 0; i < arguments.length; i += 1) {
    const v = String(arguments[i] || "").trim();
    if (isCartSafeImageReference(v)) return v;
  }
  return "";
}

function compactRequiredAddonProps(properties) {
  const src = properties && typeof properties === "object" ? properties : {};
  const keep = [
    "_cm_addon",
    "_cm_addon_kind",
    "_cm_parent_design_id",
    "_cm_parent_variant_id",
    "_cm_editor_url",
    "_cm_edit_url",
    "_cm_draft_token",
    "_Back Last Name",
    "_Back Inscription",
    "_Back Words",
    "_Back Included Words",
    "_Back Extra Words",
    "_Back Clipart Count",
    "_Back Panel Count",
    "_Back Panel Upgrade Count",
    "_Back Panel Type",
    "_Vase Qty",
    "Additional Laser People",
    "Laser Etching",
    "Laser Artwork #",
    "Laser Side",
    "Artwork Source",
    "Laser Artwork",
    "_Laser Thumb",
    "Laser Artwork Name",
    "Laser Artwork Count",
    "Laser Artwork URLs",
    "Laser Artwork Names",
    "Laser Preview",
    "Laser Feather Px",
    "Laser Feather Px Values",
    "Laser Customer Notes",
    "Laser Library Category",
    "Laser Library Subcategory",
    "Laser Width",
    "Laser Height",
    "Laser Area Sq In",
    "Laser Extra Area Sq In",
    "Detected Laser People",
    "Laser Requested Type",
    "Laser Priced Type",
    "Laser Pricing Note"
  ];
  const out = {};
  keep.forEach((key) => {
    if (src[key] == null) return;
    const value = String(src[key]);
    if (isInlineImagePayload(value) || value.length > 1200) return;
    out[key] = value;
  });
  return out;
}

function cropThumbDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const url = String(dataUrl || "").trim();
    if (!url) {
      resolve("");
      return;
    }

    const img = new Image();
    img.onload = function () {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width || 0;
        canvas.height = img.naturalHeight || img.height || 0;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx || !canvas.width || !canvas.height) {
          resolve(url);
          return;
        }

        ctx.drawImage(img, 0, 0);
        const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const samplePixel = (x, y) => {
          const i = (y * width + x) * 4;
          return [data[i], data[i + 1], data[i + 2], data[i + 3]];
        };
        const corners = [
          samplePixel(0, 0),
          samplePixel(width - 1, 0),
          samplePixel(0, height - 1),
          samplePixel(width - 1, height - 1)
        ];
        const bg = corners.reduce(
          (sum, px) => {
            sum[0] += px[0];
            sum[1] += px[1];
            sum[2] += px[2];
            sum[3] += px[3];
            return sum;
          },
          [0, 0, 0, 0]
        ).map((v) => v / corners.length);
        const cornerSpread = corners.reduce((max, px) => {
          const delta =
            Math.abs(px[0] - bg[0]) +
            Math.abs(px[1] - bg[1]) +
            Math.abs(px[2] - bg[2]) +
            Math.abs(px[3] - bg[3]);
          return Math.max(max, delta);
        }, 0);
        const hasUniformBackground = cornerSpread < 54;
        const differsFromBg = (r, g, b, a) => {
          if (a <= 20) return false;
          if (!hasUniformBackground) return true;
          const delta =
            Math.abs(r - bg[0]) +
            Math.abs(g - bg[1]) +
            Math.abs(b - bg[2]) +
            Math.abs(a - bg[3]);
          return delta > 44;
        };

        let top = height;
        let left = width;
        let right = -1;
        let bottom = -1;

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const i = (y * width + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            const visible = differsFromBg(r, g, b, a);
            if (!visible) continue;

            if (x < left) left = x;
            if (x > right) right = x;
            if (y < top) top = y;
            if (y > bottom) bottom = y;
          }
        }

        if (right <= left || bottom <= top) {
          resolve(url);
          return;
        }

        const padX = Math.max(2, Math.round((right - left + 1) * 0.03));
        const padY = Math.max(2, Math.round((bottom - top + 1) * 0.03));
        const cropX = Math.max(0, left - padX);
        const cropY = Math.max(0, top - padY);
        const cropW = Math.min(width - cropX, right - left + 1 + padX * 2);
        const cropH = Math.min(height - cropY, bottom - top + 1 + padY * 2);

        const out = document.createElement("canvas");
        out.width = cropW;
        out.height = cropH;
        const outCtx = out.getContext("2d");
        if (!outCtx) {
          resolve(url);
          return;
        }

        outCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
        resolve(out.toDataURL("image/png"));
      } catch (_) {
        resolve(url);
      }
    };
    img.onerror = function () {
      resolve(url);
    };
    img.src = url;
  });
}

function rotateThumbDataUrl(dataUrl, degrees) {
  return new Promise((resolve) => {
    const url = String(dataUrl || "").trim();
    const deg = Number(degrees || 0) || 0;
    if (!url || Math.abs(deg % 360) < 1) {
      resolve(url);
      return;
    }

    const img = new Image();
    img.onload = function () {
      try {
        const normalized = ((deg % 360) + 360) % 360;
        const swap = normalized === 90 || normalized === 270;
        const w = img.naturalWidth || img.width || 0;
        const h = img.naturalHeight || img.height || 0;
        if (!w || !h) {
          resolve(url);
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = swap ? h : w;
        canvas.height = swap ? w : h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(url);
          return;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalized * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2);
        resolve(canvas.toDataURL("image/png"));
      } catch (_) {
        resolve(url);
      }
    };
    img.onerror = function () {
      resolve(url);
    };
    img.src = url;
  });
}

function resizeThumbDataUrl(dataUrl, maxDim) {
  return new Promise((resolve) => {
    const url = String(dataUrl || "").trim();
    const limit = Math.max(1, Number(maxDim || 0) || 0);
    if (!url || !limit || !url.startsWith("data:image/")) {
      resolve(url);
      return;
    }

    const img = new Image();
    img.onload = function () {
      try {
        const w = img.naturalWidth || img.width || 0;
        const h = img.naturalHeight || img.height || 0;
        if (!w || !h || Math.max(w, h) <= limit) {
          resolve(url);
          return;
        }
        const scale = Math.min(1, limit / Math.max(w, h));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(url);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      } catch (_) {
        resolve(url);
      }
    };
    img.onerror = function () {
      resolve(url);
    };
    img.src = url;
  });
}

function pamelaStoreLocalCartThumb(designId, side, dataUrl) {
  try {
    const id = String(designId || "").trim();
    const sideKey = side === "back" ? "back" : "front";
    const src = String(dataUrl || "").trim();
    if (!id || !src) return "";
    const key = `cm_cart_thumb:${id}:${sideKey}`;
    window.localStorage?.setItem(key, src);
    return key;
  } catch (e) {
    console.warn("Local cart thumbnail storage failed:", e);
    return "";
  }
}

const PAMELA_THUMB_CACHE = window.PAMELA_THUMB_CACHE || {
  front: { dataUrl: "", dirty: true, pending: null, version: 0 },
  back: { dataUrl: "", dirty: true, pending: null, version: 0 }
};
window.PAMELA_THUMB_CACHE = PAMELA_THUMB_CACHE;

function pamelaGetThumbCacheSide(side) {
  return side === "back" ? "back" : "front";
}

function pamelaMarkSideThumbDirty(side) {
  const sideKey = pamelaGetThumbCacheSide(side);
  PAMELA_THUMB_CACHE[sideKey] = PAMELA_THUMB_CACHE[sideKey] || { dataUrl: "", dirty: true, pending: null, version: 0 };
  PAMELA_THUMB_CACHE[sideKey].dirty = true;
  PAMELA_THUMB_CACHE[sideKey].version = Number(PAMELA_THUMB_CACHE[sideKey].version || 0) + 1;
}

function pamelaClearSideThumbDirty(side, dataUrl, version) {
  const sideKey = pamelaGetThumbCacheSide(side);
  PAMELA_THUMB_CACHE[sideKey] = PAMELA_THUMB_CACHE[sideKey] || { dataUrl: "", dirty: true, pending: null, version: 0 };
  if (version != null && Number(version) !== Number(PAMELA_THUMB_CACHE[sideKey].version || 0)) {
    PAMELA_THUMB_CACHE[sideKey].pending = null;
    return;
  }
  PAMELA_THUMB_CACHE[sideKey].dataUrl = String(dataUrl || "").trim();
  PAMELA_THUMB_CACHE[sideKey].dirty = false;
  PAMELA_THUMB_CACHE[sideKey].pending = null;
}

function pamelaScheduleThumbCapture(side, opts = {}) {
  const sideKey = pamelaGetThumbCacheSide(side);
  const cache = PAMELA_THUMB_CACHE[sideKey] || (PAMELA_THUMB_CACHE[sideKey] = { dataUrl: "", dirty: true, pending: null, version: 0 });
  if (!opts.force && cache.pending) return cache.pending;

  const run = () => {
    cache.pending = captureThumbForSide(sideKey, {
      width: opts.width || 360,
      force: !!opts.force,
      hideEditor: opts.hideEditor
    }).catch((e) => {
      console.warn(`Scheduled thumbnail capture failed for ${sideKey}:`, e);
      return "";
    });
    return cache.pending;
  };

  const start = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      window.setTimeout(run, Number(opts.delay || 200));
    }
    return cache.pending;
  };

  return start();
}

async function pamelaGetCartThumbForSide(side, opts = {}) {
  const sideKey = pamelaGetThumbCacheSide(side);
  const cache = PAMELA_THUMB_CACHE[sideKey] || (PAMELA_THUMB_CACHE[sideKey] = { dataUrl: "", dirty: true, pending: null, version: 0 });
  if (!opts.force && cache.dataUrl && cache.dirty !== true) return cache.dataUrl;
  if (!opts.force && cache.pending) {
    const pending = await cache.pending;
    if (pending && cache.dataUrl && cache.dirty !== true) return cache.dataUrl;
  }
  return captureThumbForSide(sideKey, {
    width: opts.width || 360,
    force: true,
    hideEditor: opts.hideEditor
  });
}

async function captureThumbForSide(targetSide, opts = {}) {
  const requestedSide = targetSide === "back" ? "back" : "front";
  const cache = PAMELA_THUMB_CACHE[requestedSide] || (PAMELA_THUMB_CACHE[requestedSide] = { dataUrl: "", dirty: true, pending: null, version: 0 });
  if (!opts.force && cache.dataUrl && cache.dirty !== true) return cache.dataUrl;
  const captureVersion = Number(cache.version || 0);

  const thumbWidth = Math.max(1, Number(opts.width || 480) || 480);
  const stageContainer = editor?.stage?.container?.() || null;
  const stageWrap = document.getElementById("pamela-stageWrap") || stageContainer?.parentElement || null;
  const previousStageVisibility = stageContainer ? stageContainer.style.visibility : "";
  const previousStageWrapVisibility = stageWrap ? stageWrap.style.visibility : "";

  const previousSide =
    (window.PAMELA_SIDES?.current === "front" || window.PAMELA_SIDES?.current === "back")
      ? window.PAMELA_SIDES.current
      : "front";

  const waitForPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

  const forceSideLayerVisibility = (side) => {
    try {
      const sides = editor?.layers?.sides || null;
      if (!sides?.front || !sides?.back) return;
      const isBack = side === "back";

      try { sides.front.panels?.visible?.(!isBack); } catch (_) {}
      try { sides.front.content?.visible?.(!isBack); } catch (_) {}
      try { sides.front.portraits?.visible?.(!isBack); } catch (_) {}
      try { sides.front.vase?.visible?.(!isBack); } catch (_) {}

      try { sides.back.panels?.visible?.(isBack); } catch (_) {}
      try { sides.back.content?.visible?.(isBack); } catch (_) {}
      try { sides.back.portraits?.visible?.(isBack); } catch (_) {}
      try { sides.back.vase?.visible?.(isBack); } catch (_) {}

      editor.layers.panels = isBack ? sides.back.panels : sides.front.panels;
      editor.layers.content = isBack ? sides.back.content : sides.front.content;
      editor.layers.portraits = isBack ? sides.back.portraits : sides.front.portraits;

      try { sides.front.panels?.batchDraw?.(); } catch (_) {}
      try { sides.front.content?.batchDraw?.(); } catch (_) {}
      try { sides.front.portraits?.batchDraw?.(); } catch (_) {}
      try { sides.front.vase?.batchDraw?.(); } catch (_) {}
      try { sides.back.panels?.batchDraw?.(); } catch (_) {}
      try { sides.back.content?.batchDraw?.(); } catch (_) {}
      try { sides.back.portraits?.batchDraw?.(); } catch (_) {}
      try { sides.back.vase?.batchDraw?.(); } catch (_) {}
    } catch (_) {}
  };

  try {
    try {
      const liveSide =
        (window.PAMELA_SIDES?.current === "front" || window.PAMELA_SIDES?.current === "back")
          ? window.PAMELA_SIDES.current
          : previousSide;
      if (window.PAMELA_SIDES) window.PAMELA_SIDES.current = liveSide;
      if (typeof saveCurrentSideState === "function") saveCurrentSideState();
    } catch (_) {}

    if (stageContainer && opts.hideEditor !== false) {
      stageContainer.style.visibility = "hidden";
    }
    if (stageWrap && opts.hideEditor !== false) {
      stageWrap.style.visibility = "hidden";
    }

    if (window.PAMELA_SIDES) window.PAMELA_SIDES.current = requestedSide;
    forceSideLayerVisibility(requestedSide);

    try {
      const applySideAssets = window.__PAMELA_APPLY_SIDE_ASSETS__;
      if (typeof applySideAssets === "function") {
        await applySideAssets(requestedSide, { resetView: false, hideDuringSwap: false });
      }
    } catch (e) {
      console.warn(`Thumb side asset swap failed for ${requestedSide}:`, e);
    }

    forceSideLayerVisibility(requestedSide);

    try {
      await pamelaRefreshPlacedSvgNodesForGranite(editor);
    } catch (_) {}

    try { editor?.layers?.background?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.panels?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.content?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.portraits?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.border?.batchDraw?.(); } catch (_) {}
    try { editor?.layers?.ui?.batchDraw?.(); } catch (_) {}
    try { editor?.stage?.batchDraw?.(); } catch (_) {}

    await waitForPaint();
    forceSideLayerVisibility(requestedSide);
    await waitForPaint();

    const st = editor?.stage || null;
    if (!st || typeof st.toDataURL !== "function" || typeof st.width !== "function") {
      return "";
    }

    const fullWidth = Number(st.width() || 0);
    const scale = fullWidth > 0 ? (thumbWidth / fullWidth) : 1;
    const rawThumb = String(
      st.toDataURL({
        mimeType: "image/png",
        pixelRatio: scale
      }) || ""
    );
    let croppedThumb = rawThumb ? (await cropThumbDataUrl(rawThumb)) || rawThumb : "";
    if (croppedThumb) pamelaClearSideThumbDirty(requestedSide, croppedThumb, captureVersion);
    return croppedThumb;
  } catch (e) {
    console.warn(`Thumb capture failed for ${requestedSide}:`, e);
    return "";
  } finally {
    try {
      if (window.PAMELA_SIDES) window.PAMELA_SIDES.current = previousSide;
      forceSideLayerVisibility(previousSide);
      try {
        const applySideAssets = window.__PAMELA_APPLY_SIDE_ASSETS__;
        if (typeof applySideAssets === "function") {
          await applySideAssets(previousSide, { resetView: false, hideDuringSwap: false });
        }
      } catch (assetErr) {
        console.warn(`Thumb side asset restore failed for ${previousSide}:`, assetErr);
      }
      forceSideLayerVisibility(previousSide);
      try { if (typeof updateSideToolbarUI === "function") updateSideToolbarUI(previousSide === "back"); } catch (_) {}
      await waitForPaint();
    } catch (e) {
      console.warn(`Thumb restore failed for ${previousSide}:`, e);
    }

    if (stageWrap && opts.hideEditor !== false) {
      stageWrap.style.visibility = previousStageWrapVisibility;
    }
    if (stageContainer && opts.hideEditor !== false) {
      stageContainer.style.visibility = previousStageVisibility;
    }
  }
}

function makeDesignId() {
  return `cm_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getCartEditContext() {
  try {
    const url = new URL(window.location.href);
    const flag = String(url.searchParams.get("cm_cart_edit") || "").trim().toLowerCase();
    const replaceDesignId = String(url.searchParams.get("cm_replace_design_id") || "").trim();
    return {
      enabled: flag === "1" || flag === "true" || flag === "yes",
      replaceDesignId
    };
  } catch (_) {
    return { enabled: false, replaceDesignId: "" };
  }
}

function buildEditorReturnUrl(opts = {}) {
  try {
    const url = new URL(window.location.href);
    const token = String(opts.token || "").trim();
    const replaceDesignId = String(opts.replaceDesignId || "").trim();
    const readOnly = !!opts.readOnly;

    if (token) {
      url.searchParams.set("token", token);
    } else {
      url.searchParams.delete("token");
    }

    if (replaceDesignId) {
      url.searchParams.set("cm_cart_edit", "1");
      url.searchParams.set("cm_replace_design_id", replaceDesignId);
    } else {
      url.searchParams.delete("cm_cart_edit");
      url.searchParams.delete("cm_replace_design_id");
    }

    if (readOnly) {
      url.searchParams.set("readonly", "1");
      url.searchParams.set("mode", "readonly");
    } else {
      url.searchParams.delete("readonly");
      if (String(url.searchParams.get("mode") || "").trim().toLowerCase() === "readonly") {
        url.searchParams.delete("mode");
      }
    }

    return url.toString();
  } catch (_) {
    return String(window.location.href || "");
  }
}

async function removeCartDesignGroup(designId) {
  const targetDesignId = String(designId || "").trim();
  if (!targetDesignId) return { ok: true, removed: 0 };

  const cartRes = await fetch("/cart.js", {
    method: "GET",
    headers: { "Accept": "application/json" },
    credentials: "same-origin"
  });

  if (!cartRes.ok) {
    throw new Error("Could not load the existing cart for editing.");
  }

  const cart = await cartRes.json();
  const lines = [];

  (cart.items || []).forEach((item, index) => {
    const props = item?.properties || {};
    const mainId = String(props._cm_design_id || "").trim();
    const parentId = String(props._cm_parent_design_id || "").trim();
    if (mainId === targetDesignId || parentId === targetDesignId) {
      lines.push(index + 1);
    }
  });

  lines.sort((a, b) => b - a);

  for (const line of lines) {
    const changeRes = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ line, quantity: 0 })
    });

    if (!changeRes.ok) {
      const txt = await changeRes.text().catch(() => "");
      throw new Error(txt || "Could not remove the previous cart design.");
    }
  }

  return { ok: true, removed: lines.length };
}

async function cartHasDesignGroup(designId) {
  const targetDesignId = String(designId || "").trim();
  if (!targetDesignId) return false;

  const res = await fetch("/cart.js", {
    method: "GET",
    headers: { "Accept": "application/json" },
    credentials: "same-origin"
  });
  if (!res.ok) throw new Error("Could not verify the updated cart design.");

  const cart = await res.json();
  return (cart.items || []).some((item) =>
    String(item?.properties?._cm_design_id || "").trim() === targetDesignId
  );
}

function countWords(text) {
  const t = String(text || "").trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

function getChosenVariantObj() {
  const chosenVariantId = selectEl?.value;
  const v = (variants || []).find(vv => String(vv.id) === String(chosenVariantId));
  return v || null;
}

function isCompanionVariant(variantObj) {
  const ed =
    window.pamelaMonumentEditor ||
    window.pamelaEditor ||
    window.editor ||
    window.PAMELA_EDITOR ||
    window.__PAMELA_EDITOR__ ||
    null;
  const layoutKind = String(ed?.layout || ed?.productLayout || layout || "").trim().toLowerCase();
  if (layoutKind === "single") return false;
  if (layoutKind === "companion") return true;

  const t = String(variantObj?.title || "").toLowerCase();
  return (
    /\bcompanion\b/.test(t) ||
    /\bdouble\b/.test(t) ||
    /\btwo\b/.test(t) ||
    /\b2\s*(person|people)\b/.test(t)
  );
}

function isLaserEtchingVariantEligible(variantObj) {
  const t = String(variantObj?.title || variantObj?.public_title || "").toLowerCase();
  return t.includes("jet black");
}

function pamelaDraftStateHasLaser(state) {
  return !!(state && Array.isArray(state.laser) && state.laser.length > 0);
}

function pamelaEditorHasLaserArtwork(editorInstance) {
  try {
    if (getLaserArtworkNodes(editorInstance).length > 0) return true;
  } catch (_) {}
  try {
    return (
      pamelaDraftStateHasLaser(window.PAMELA_SIDES?.front?.draft_state) ||
      pamelaDraftStateHasLaser(window.PAMELA_SIDES?.back?.draft_state)
    );
  } catch (_) {
    return false;
  }
}

function pamelaClearLaserFromDraftState(state) {
  if (!state || typeof state !== "object") return state;
  if (Array.isArray(state.laser)) state.laser = [];
  return state;
}

function pamelaClearLaserArtwork(editorInstance) {
  const ed = editorInstance || getPamelaEditorInstance();

  try {
    const nodes = getLaserArtworkNodes(ed);
    nodes.forEach((node) => {
      try { node.destroy(); } catch (_) {}
    });
  } catch (_) {}

  try { pamelaClearLaserFromDraftState(window.PAMELA_SIDES?.front?.draft_state); } catch (_) {}
  try { pamelaClearLaserFromDraftState(window.PAMELA_SIDES?.back?.draft_state); } catch (_) {}
  try { pamelaClearLaserFromDraftState(window.PAMELA_SIDES?.baseline?.draft_state); } catch (_) {}

  try { if (ed?.selected?.getAttr?.("pamelaLaserEtching") === true) ed.selectNode?.(null); } catch (_) {}
  try { ed?.nodes?.tr?.nodes?.([]); } catch (_) {}
  try { window.__PAMELA_SELECTED_LASER_NODE__ = null; } catch (_) {}
  try { document.getElementById("pamela_laser_kind").value = ""; } catch (_) {}
  try { document.getElementById("pamela_laser_source").value = ""; } catch (_) {}
  try { document.getElementById("pamela_laser_width").value = ""; } catch (_) {}
  try { document.getElementById("pamela_laser_height").value = ""; } catch (_) {}
  try { document.getElementById("pamela_laser_faces").value = "1"; } catch (_) {}

  try { saveCurrentSideState(); } catch (_) {}
  try { ed?.layers?.content?.batchDraw?.(); } catch (_) {}
  try { ed?.layers?.sides?.front?.content?.batchDraw?.(); } catch (_) {}
  try { ed?.layers?.sides?.back?.content?.batchDraw?.(); } catch (_) {}
  try { ed?.layers?.ui?.batchDraw?.(); } catch (_) {}
  try { ed?.stage?.batchDraw?.(); } catch (_) {}
  try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
  try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
}

function laserFitsSpotSize(widthIn, heightIn, cfg) {
  const w = Number(widthIn || 0);
  const h = Number(heightIn || 0);
  const maxW = Number(cfg?.laserEtching?.spotMaxWidth || 11);
  const maxH = Number(cfg?.laserEtching?.spotMaxHeight || 14);
  if (!(w > 0) || !(h > 0)) return false;
  return (w <= maxW && h <= maxH) || (w <= maxH && h <= maxW);
}

function getLaserPricePlan(laserState, catalog) {
  const cfg = window.PAMELA_ADDON_CONFIG || {};
  const laserCfg = cfg.laserEtching || {};
  const kind = String(laserState?.kind || "").toLowerCase();
  const source = String(laserState?.source || "").toLowerCase();
  const widthIn = Number(laserState?.widthIn || 0);
  const heightIn = Number(laserState?.heightIn || 0);
  const faces = Math.max(0, Number(laserState?.faces || 0) || 0);
  const area = Number(laserState?.areaSqIn || 0) > 0
    ? Number(laserState.areaSqIn || 0)
    : Math.max(0, widthIn * heightIn);
  const fitsSpot = laserFitsSpotSize(widthIn, heightIn, cfg);
  const isCustom = source === "custom";
  const hasDetectedFaces = isCustom && faces > 0;
  let effectiveKind = (kind === "scene" || !fitsSpot) ? "scene" : kind;
  if (hasDetectedFaces && effectiveKind === "spot") {
    effectiveKind = "portrait";
  }
  const laser = catalog?.laser || {};

  let minVariant = null;
  let extraVariant = null;
  let prepCents = 0;

  if (effectiveKind === "portrait") {
    minVariant = laser.portraitMin;
    extraVariant = laser.portraitExtraArea;
  } else if (effectiveKind === "scene") {
    minVariant = isCustom ? laser.sceneCustomMin : laser.sceneLibraryMin;
    extraVariant = laser.sceneExtraArea;
    prepCents = isCustom ? Number(laserCfg?.prepFees?.sceneCustom || 0) * 100 : 0;
  } else {
    minVariant = isCustom ? laser.spotCustomMin : laser.spotLibraryMin;
    extraVariant = laser.spotExtraArea;
    prepCents = isCustom ? Number(laserCfg?.prepFees?.spotCustom || 0) * 100 : 0;
  }

  const fallbackMinDollars =
    effectiveKind === "portrait"
      ? Number(laserCfg?.minimums?.portrait || 0)
      : (effectiveKind === "scene"
          ? Number((isCustom ? laserCfg?.minimums?.sceneCustom : laserCfg?.minimums?.sceneLibrary) || 0)
          : Number((isCustom ? laserCfg?.minimums?.spotCustom : laserCfg?.minimums?.spotLibrary) || 0));
  const fallbackRateDollars =
    effectiveKind === "portrait"
      ? Number(laserCfg?.rates?.portrait || 0)
      : (effectiveKind === "scene"
          ? Number((isCustom ? laserCfg?.rates?.sceneCustom : laserCfg?.rates?.scene) || 0)
          : Number(laserCfg?.rates?.spot || 0));
  const fallbackFaceDollars = Number(laserCfg?.additionalFaceFee || 0);
  const minCents = Number(minVariant?.price || 0) || Math.round(fallbackMinDollars * 100);
  const rateCents = Number(extraVariant?.price || 0) || Math.round(fallbackRateDollars * 100);
  // Included area is the PHYSICAL size covered by the minimum price (e.g.
  // 24in x 14in for a "Full Scene"), not a value backed into existence from
  // price / rate -- that backsolve produced an included area far larger than
  // the real base size, so customer scenes well past the intended free size
  // were still landing inside the "minimum" zone and barely getting charged.
  const includedArea =
    effectiveKind === "portrait"
      ? Number(laserCfg?.portraitMaxWidth || 11) * Number(laserCfg?.portraitMaxHeight || 14)
      : (effectiveKind === "scene"
          ? Number(laserCfg?.sceneBaseWidthIn || 24) * Number(laserCfg?.sceneBaseHeightIn || 14)
          : Number(laserCfg?.spotMaxWidth || 11) * Number(laserCfg?.spotMaxHeight || 14));
  const extraArea = Math.max(0, Math.ceil(area - includedArea));
  const additionalFaces = effectiveKind === "portrait"
    ? Math.max(0, faces - 1)
    : 0;
  const facePricingNote =
    hasDetectedFaces && kind === "spot"
      ? "Customer-uploaded spot artwork contains detected people and is priced as portrait etching."
      : "";

  return {
    enabled: laserState?.enabled === true,
    requestedKind: kind,
    effectiveKind,
    source: isCustom ? "custom" : "library",
    widthIn,
    heightIn,
    area,
    fitsSpot,
    minVariant,
    extraVariant,
    fallbackMinDollars,
    fallbackRateDollars,
    fallbackFaceDollars,
    extraArea,
    additionalFaces,
    detectedFaces: faces,
    faceVariant: laser.additionalFace,
    facePricingNote,
    switchedToScene: kind !== "scene" && effectiveKind === "scene"
  };
}

function getPamelaEditorInstance() {
  return (
    window.pamelaMonumentEditor ||
    window.pamelaEditor ||
    window.editor ||
    window.PAMELA_EDITOR ||
    window.__PAMELA_EDITOR__ ||
    null
  );
}

function isPamelaLaserArtworkNode(node) {
  try {
    return !!(node && node.getAttr?.("pamelaLaserEtching") === true);
  } catch (_) {
    return false;
  }
}

function getSelectedLaserArtworkNode(editor) {
  try {
    const ed = editor || getPamelaEditorInstance();
    // The transformer is the user's visible selection. Trust it first so the
    // flyout follows cloned/resized laser artwork instead of an older node.
    const trNode = ed?.nodes?.tr?.nodes?.()?.[0] || null;
    if (isPamelaLaserArtworkNode(trNode)) {
      try { window.__PAMELA_SELECTED_LASER_NODE__ = trNode; } catch (_) {}
      return trNode;
    }

    const selected = ed?.selected || null;
    if (isPamelaLaserArtworkNode(selected)) {
      try { window.__PAMELA_SELECTED_LASER_NODE__ = selected; } catch (_) {}
      return selected;
    }

    const last = window.__PAMELA_SELECTED_LASER_NODE__ || null;
    if (isPamelaLaserArtworkNode(last) && last.destroyed?.() !== true) return last;
  } catch (_) {}
  return null;
}

function loadLaserHtmlImage(url, opts = {}) {
  return new Promise((resolve, reject) => {
    const src = String(url || "");
    const img = new Image();
    const useCors = opts.crossOrigin !== false;
    if (useCors) img.crossOrigin = "anonymous";
    img.onload = () => {
      try { img.__pamelaCorsSafe = useCors; } catch (_) {}
      resolve(img);
    };
    img.onerror = () => reject(new Error(useCors ? "Laser image could not be loaded with export headers." : "Laser image could not be loaded."));
    img.src = src;
  });
}

function addLaserImageCacheBust(url) {
  const src = String(url || "").trim();
  if (!src) return src;
  try {
    const u = new URL(src, window.location.href);
    u.searchParams.set("cm_cors", String(Date.now()));
    return u.toString();
  } catch (_) {
    const joiner = src.includes("?") ? "&" : "?";
    return `${src}${joiner}cm_cors=${Date.now()}`;
  }
}

async function loadLaserHtmlImageForPlacement(url) {
  try {
    return await loadLaserHtmlImage(url, { crossOrigin: true });
  } catch (corsErr) {
    console.warn("[Laser Preview] CORS-safe load failed; retrying without cached headers:", corsErr);
  }
  try {
    return await loadLaserHtmlImage(addLaserImageCacheBust(url), { crossOrigin: true });
  } catch (retryErr) {
    console.warn("[Laser Preview] CORS-safe retry failed; falling back for placement only:", retryErr);
  }
  return loadLaserHtmlImage(url, { crossOrigin: false });
}

function removeLaserEdgeWhiteBackground(imageData, options = {}) {
  try {
    if (!imageData || !imageData.data) return imageData;

    const data = imageData.data;
    const width = Number(imageData.width || 0);
    const height = Number(imageData.height || 0);
    if (!(width > 0) || !(height > 0)) return imageData;

    const threshold = Math.max(0, Math.min(255, Number(options.threshold || 245) || 245));
    const maxColorSpread = Math.max(0, Math.min(255, Number(options.maxColorSpread || 18) || 18));
    const minAlpha = Math.max(0, Math.min(255, Number(options.minAlpha || 8) || 8));
    const seen = new Uint8Array(width * height);
    const stack = [];

    const isEdgeWhite = (idx) => {
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      return a >= minAlpha && r >= threshold && g >= threshold && b >= threshold && (max - min) <= maxColorSpread;
    };

    const push = (x, y) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const p = y * width + x;
      if (seen[p]) return;
      seen[p] = 1;
      const idx = p * 4;
      if (isEdgeWhite(idx)) stack.push(p);
    };

    for (let x = 0; x < width; x += 1) {
      push(x, 0);
      push(x, height - 1);
    }
    for (let y = 0; y < height; y += 1) {
      push(0, y);
      push(width - 1, y);
    }

    while (stack.length) {
      const p = stack.pop();
      const idx = p * 4;
      data[idx + 3] = 0;

      const x = p % width;
      const y = Math.floor(p / width);
      push(x + 1, y);
      push(x - 1, y);
      push(x, y + 1);
      push(x, y - 1);
    }

    const fringePasses = Math.max(0, Math.min(4, Math.round(Number(options.fringePasses || 2) || 2)));
    const fringeThreshold = Math.max(0, Math.min(255, Number(options.fringeThreshold || 232) || 232));
    const fringeMaxColorSpread = Math.max(0, Math.min(255, Number(options.fringeMaxColorSpread || 34) || 34));
    const isLightFringe = (idx) => {
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      return a >= minAlpha && r >= fringeThreshold && g >= fringeThreshold && b >= fringeThreshold && (max - min) <= fringeMaxColorSpread;
    };
    const touchesTransparent = (p) => {
      const x = p % width;
      const y = Math.floor(p / width);
      const neighbors = [
        x > 0 ? p - 1 : -1,
        x < width - 1 ? p + 1 : -1,
        y > 0 ? p - width : -1,
        y < height - 1 ? p + width : -1
      ];
      return neighbors.some((n) => n < 0 || data[(n * 4) + 3] < minAlpha);
    };

    for (let pass = 0; pass < fringePasses; pass += 1) {
      const kill = [];
      for (let p = 0; p < width * height; p += 1) {
        const idx = p * 4;
        if (data[idx + 3] < minAlpha || !isLightFringe(idx) || !touchesTransparent(p)) continue;
        kill.push(idx);
      }
      if (!kill.length) break;
      kill.forEach((idx) => { data[idx + 3] = 0; });
    }

    return imageData;
  } catch (e) {
    console.warn("[Laser Preview] edge white removal failed:", e);
    return imageData;
  }
}

function makeLaserBlackWhitePreviewImage(sourceImg, options = {}) {
  try {
    const srcW = Number(sourceImg?.naturalWidth || sourceImg?.width || 0);
    const srcH = Number(sourceImg?.naturalHeight || sourceImg?.height || 0);
    if (!(srcW > 0) || !(srcH > 0)) return sourceImg;

    const targetW = Math.max(0, Number(options?.targetWidthPx || 0) || 0);
    const targetH = Math.max(0, Number(options?.targetHeightPx || 0) || 0);
    const maxDim = Math.max(DPI, Number(options?.maxDimPx || 0) || (10 * DPI));
    const scale = targetW > 0 && targetH > 0
      ? Math.min(1, targetW / srcW, targetH / srcH)
      : Math.min(1, maxDim / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return sourceImg;

    ctx.drawImage(sourceImg, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    removeLaserEdgeWhiteBackground(imageData, {
      threshold: options?.edgeWhiteThreshold,
      maxColorSpread: options?.edgeWhiteMaxColorSpread
    });
    const data = imageData.data;
    const contrast = 1.25;

    for (let i = 0; i < data.length; i += 4) {
      const gray = (data[i] * 0.299) + (data[i + 1] * 0.587) + (data[i + 2] * 0.114);
      const adjusted = Math.max(0, Math.min(255, ((gray - 128) * contrast) + 128));
      data[i] = adjusted;
      data[i + 1] = adjusted;
      data[i + 2] = adjusted;
    }

    ctx.putImageData(imageData, 0, 0);
    try {
      canvas.toDataURL("image/png");
    } catch (exportErr) {
      console.warn("[Laser Preview] image is not export-safe yet; using original image until server headers are live:", exportErr);
      return sourceImg;
    }
    return canvas;
  } catch (e) {
    console.warn("[Laser Preview] black-and-white conversion failed:", e);
    return sourceImg;
  }
}

async function pamelaDetectFaceCountFromImageSource(source) {
  try {
    const Detector = window.FaceDetector;
    if (typeof Detector !== "function") return null;

    const detector = new Detector({ fastMode: true, maxDetectedFaces: 20 });
    let input = source || null;
    if (!input) return null;

    const w = Number(input.naturalWidth || input.videoWidth || input.width || 0);
    const h = Number(input.naturalHeight || input.videoHeight || input.height || 0);
    if (!(w > 0) || !(h > 0)) return null;

    if (!(input instanceof HTMLCanvasElement) && Math.max(w, h) > 1200) {
      const scale = 1200 / Math.max(w, h);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(input, 0, 0, canvas.width, canvas.height);
      input = canvas;
    }

    const faces = await detector.detect(input);
    return Math.max(0, Array.isArray(faces) ? faces.length : 0);
  } catch (e) {
    console.warn("[Laser Faces] automatic detection failed:", e);
    return null;
  }
}

async function pamelaAutoSetLaserFaces(node, imageSource) {
  try {
    if (!node || node.getAttr?.("pamelaLaserEtching") !== true) return 0;

    node.setAttr("pamelaLaserFaceDetectionStatus", "detecting");
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}

    const detected = await pamelaDetectFaceCountFromImageSource(imageSource || node.image?.());
    const kind = String(node.getAttr?.("pamelaLaserKind") || "").toLowerCase();
    const currentFaces = Math.max(0, Number(node.getAttr?.("pamelaLaserFaces") || 0) || 0);
    if (node.getAttr?.("pamelaLaserPeopleConfirmed") === true || node.getAttr?.("pamelaLaserFacesManualOverride") === true) {
      return currentFaces;
    }
    const faces = detected == null
      ? (kind === "portrait" ? Math.max(1, currentFaces || 1) : currentFaces)
      : (kind === "portrait"
          ? Math.max(1, Number(detected || 0) || 1)
          : Math.max(0, Number(detected || 0) || 0));

    node.setAttr("pamelaLaserFaces", faces);
    node.setAttr("pamelaLaserFacesAutoDetected", detected != null && detected > 0);
    node.setAttr("pamelaLaserFacesNeedsReview", detected == null);
    node.setAttr("pamelaLaserFaceDetectionStatus", detected == null ? "unavailable" : "detected");
    try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    return faces;
  } catch (_) {
    try { node?.setAttr?.("pamelaLaserFaceDetectionStatus", "unavailable"); } catch (_) {}
    return 0;
  }
}

const pamelaAutoSetLaserPortraitFaces = pamelaAutoSetLaserFaces;

function pamelaScheduleAutoSetLaserFaces(node, imageSource, delayMs = 180) {
  return new Promise((resolve) => {
    try {
      if (!node || node.getAttr?.("pamelaLaserEtching") !== true) {
        resolve(0);
        return;
      }
      if (node.__pamelaLaserFacesTimer) {
        clearTimeout(node.__pamelaLaserFacesTimer);
      }
      node.__pamelaLaserFacesTimer = setTimeout(async () => {
        try {
          node.__pamelaLaserFacesTimer = null;
          resolve(await pamelaAutoSetLaserFaces(node, imageSource));
        } catch (_) {
          resolve(0);
        }
      }, Math.max(0, Number(delayMs || 0) || 0));
    } catch (_) {
      resolve(0);
    }
  });
}

function getLaserArtworkNode(editor) {
  try {
    const selected = getSelectedLaserArtworkNode(editor);
    if (selected) return selected;
    const stage = editor?.stage || editor?.konvaStage || null;
    if (!stage?.find) return null;
    const nodes = stage.find("Image") || [];
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      const n = nodes[i];
      if (n?.getAttr?.("pamelaLaserEtching") === true) return n;
    }
  } catch (_) {}
  return null;
}

function getLaserArtworkNodes(editor) {
  try {
    const stage = editor?.stage || editor?.konvaStage || null;
    if (!stage?.find) return [];
    return (stage.find("Image") || []).filter((n) => n?.getAttr?.("pamelaLaserEtching") === true);
  } catch (_) {}
  return [];
}

function pamelaShowMobileLaserSnapGuides(editor, node, snappedX, snappedY) {
  try {
    if (!editor || !node || typeof Konva === "undefined") return;
    if (!editor.__pamelaMobileLaserGuideReleaseBound) {
      editor.__pamelaMobileLaserGuideReleaseBound = true;
      const hideOnRelease = () => {
        try { editor._hideCenterGuide?.(); } catch (_) {}
        pamelaHideMobileLaserSnapGuides(editor);
      };
      window.addEventListener("pointerup", hideOnRelease, true);
      window.addEventListener("pointercancel", hideOnRelease, true);
      window.addEventListener("mouseup", hideOnRelease, true);
      window.addEventListener("touchend", hideOnRelease, true);
      window.addEventListener("touchcancel", hideOnRelease, true);
    }
    if (node.getAttr?.("pamelaLaserMirrorClone") === true) {
      pamelaHideMobileLaserSnapGuides(editor);
      return;
    }
    const designW = Number(editor.state?.design?.width || DESIGN_PX.w || 0);
    const designH = Number(editor.state?.design?.height || DESIGN_PX.h || 0);
    const uiLayer = editor.layers?.ui;
    if (!uiLayer) return;

    let xGuide = editor.nodes?.mobileLaserSnapXGuide;
    if (!xGuide || xGuide.destroyed?.()) {
      xGuide = new Konva.Line({
        stroke: "#ffd700",
        strokeWidth: 5,
        strokeScaleEnabled: true,
        listening: false,
        visible: false
      });
      uiLayer.add(xGuide);
      editor.nodes.mobileLaserSnapXGuide = xGuide;
    }
    xGuide.strokeWidth(5);
    xGuide.strokeScaleEnabled(true);

    let yGuide = editor.nodes?.mobileLaserSnapYGuide;
    if (!yGuide || yGuide.destroyed?.()) {
      yGuide = new Konva.Line({
        stroke: "#ffd700",
        strokeWidth: 5,
        strokeScaleEnabled: true,
        listening: false,
        visible: false
      });
      uiLayer.add(yGuide);
      editor.nodes.mobileLaserSnapYGuide = yGuide;
    }
    yGuide.strokeWidth(5);
    yGuide.strokeScaleEnabled(true);

    if (snappedX) {
      const x = Number(node.x?.() ?? node.attrs?.x);
      if (Number.isFinite(x)) {
        xGuide.points([x, 0, x, designH]);
        xGuide.visible(true);
        xGuide.moveToTop();
      }
    } else {
      xGuide.visible(false);
    }
    if (snappedY) {
      const y = Number(node.y?.() ?? node.attrs?.y);
      if (Number.isFinite(y)) {
        yGuide.points([0, y, designW, y]);
        yGuide.visible(true);
        yGuide.moveToTop();
      }
    } else {
      yGuide.visible(false);
    }
    uiLayer.batchDraw?.();
  } catch (_) {}
}

function pamelaHideMobileLaserSnapGuides(editor) {
  try {
    editor?.nodes?.mobileLaserSnapXGuide?.visible?.(false);
    editor?.nodes?.mobileLaserSnapYGuide?.visible?.(false);
    editor?.layers?.ui?.batchDraw?.();
  } catch (_) {}
}

// The editor's pointer-drag wiring lives in an earlier scope. Publish these
// helpers explicitly so mobile laser dragging can show/hide guides without
// throwing an out-of-scope ReferenceError.
window.PAMELA_showMobileLaserSnapGuides = pamelaShowMobileLaserSnapGuides;
window.PAMELA_hideMobileLaserSnapGuides = pamelaHideMobileLaserSnapGuides;

function pamelaEnableLaserArtworkDrag(editor, node) {
  try {
    const ed = editor || getPamelaEditorInstance();
    if (!ed || !node || node.getAttr?.("pamelaLaserEtching") !== true) return;

    node.off(
      "dragstart.pamelaLaserNativeDrag dragmove.pamelaLaserNativeDrag dragend.pamelaLaserNativeDrag " +
      "pointerdown.pamelaLaserNativeDrag mousedown.pamelaLaserNativeDrag touchstart.pamelaLaserNativeDrag"
    );
    try { delete node.__pamelaSuppressLaserDragUntil; } catch (_) {}
    try { delete node.__pamelaLaserTextPositionLock; } catch (_) {}
    try { node.draggable(false); } catch (_) {}
    try { ed._enableDirectPointerDrag?.(node, "content"); } catch (_) {}
  } catch (_) {}
}

function getLaserArtworkRectInches(node, editor) {
  try {
    const ed = editor || getPamelaEditorInstance();
    const ppi = Number(ed?.designDpi || ed?.dpi || ed?.DPI || DPI || 72) || 72;
    const directW = Math.max(0, Number(node?.width?.() || 0) * Math.abs(Number(node?.scaleX?.() || 1) || 1));
    const directH = Math.max(0, Number(node?.height?.() || 0) * Math.abs(Number(node?.scaleY?.() || 1) || 1));
    if (directW > 0 && directH > 0) {
      return {
        widthIn: directW / ppi,
        heightIn: directH / ppi
      };
    }

    const layer = node?.getLayer?.() || ed?.layers?.content || null;
    const rect = node?.getClientRect?.({
      relativeTo: layer || undefined,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false
    });
    return {
      widthIn: Math.max(0, Number(rect?.width || 0) / ppi),
      heightIn: Math.max(0, Number(rect?.height || 0) / ppi)
    };
  } catch (_) {
    return { widthIn: 0, heightIn: 0 };
  }
}

function getLaserArtworkTotals(editor) {
  const ed = editor || getPamelaEditorInstance();
  const nodes = getLaserArtworkNodes(ed);
  let totalArea = 0;
  nodes.forEach((n) => {
    const r = getLaserArtworkRectInches(n, ed);
    totalArea += Math.max(0, r.widthIn * r.heightIn);
  });
  return { count: nodes.length, totalArea };
}

// Panels/content/portraits front+back now share one Konva.Layer per type
// (see makeSideLayer in the stage setup) with separate front/back Groups, so
// node.getLayer() resolves to the same shared layer either way and can no
// longer tell front from back. Walk up to find which tracked Group the node
// actually lives in instead.
function pamelaFindAncestorSideGroup(node, sides) {
  try {
    let n = node?.getParent?.();
    let hops = 0;
    while (n && hops < 20) {
      if (sides?.back && (n === sides.back.content || n === sides.back.panels || n === sides.back.portraits || n === sides.back.vase)) {
        return "back";
      }
      if (sides?.front && (n === sides.front.content || n === sides.front.panels || n === sides.front.portraits || n === sides.front.vase)) {
        return "front";
      }
      n = n.getParent?.();
      hops += 1;
    }
  } catch (_) {}
  return "";
}

function getLaserArtworkSide(node, editor) {
  try {
    const attrSide = String(node?.getAttr?.("pamelaLaserSide") || "").toLowerCase();
    if (attrSide === "front" || attrSide === "back") return attrSide;
    const ed = editor || getPamelaEditorInstance();
    const sideFromGroup = pamelaFindAncestorSideGroup(node, ed?.layers?.sides);
    if (sideFromGroup === "back") return "back";
    return "front";
  } catch (_) {
    return "front";
  }
}

function getLaserArtworkPricingItems(editor, catalog) {
  const ed = editor || getPamelaEditorInstance();
  const nodes = getLaserArtworkNodes(ed);
  return nodes.map((node, index) => {
    const r = getLaserArtworkRectInches(node, ed);
    const kind = String(node?.getAttr?.("pamelaLaserKind") || node?.getAttr?.("pamelaLaserLibraryType") || "spot").toLowerCase();
    const source = String(node?.getAttr?.("pamelaLaserPricingSource") || node?.getAttr?.("pamelaLaserSource") || "custom").toLowerCase() === "library"
      ? "library"
      : "custom";
    const faces = Math.max(1, Number(node?.getAttr?.("pamelaLaserFaces") || 1) || 1);
    const state = {
      enabled: true,
      kind,
      source,
      widthIn: r.widthIn,
      heightIn: r.heightIn,
      areaSqIn: Math.max(0, r.widthIn * r.heightIn),
      faces
    };
    return {
      index,
      node,
      plan: getLaserPricePlan(state, catalog || window.PAMELA_ADDON_CATALOG || {}),
      side: getLaserArtworkSide(node, ed),
      kind,
      source,
      faces,
      notes: String(node?.getAttr?.("pamelaLaserNotes") || ""),
    };
  });
}

function getLaserArtworkPricingItemsFromDraftState(state, side, catalog) {
  const laserItems = Array.isArray(state?.laser) ? state.laser : [];
  return laserItems.map((item, index) => {
    const attrs = item?.attrs || {};
    const widthPx = Math.max(0, Number(item?.width || 0) * Math.abs(Number(item?.scaleX || 1) || 1));
    const heightPx = Math.max(0, Number(item?.height || 0) * Math.abs(Number(item?.scaleY || 1) || 1));
    const ppi = Number(DPI || 72) || 72;
    const widthIn = widthPx / ppi;
    const heightIn = heightPx / ppi;
    const kind = String(attrs.pamelaLaserKind || attrs.pamelaLaserLibraryType || "spot").toLowerCase();
    const source = String(attrs.pamelaLaserPricingSource || attrs.pamelaLaserSource || "custom").toLowerCase() === "library"
      ? "library"
      : "custom";
    const faces = Math.max(1, Number(attrs.pamelaLaserFaces || 1) || 1);
    const pricingState = {
      enabled: true,
      kind,
      source,
      widthIn,
      heightIn,
      areaSqIn: Math.max(0, widthIn * heightIn),
      faces
    };
    return {
      index,
      item,
      attrs,
      plan: getLaserPricePlan(pricingState, catalog || window.PAMELA_ADDON_CATALOG || {}),
      side: side === "back" ? "back" : "front",
      kind,
      source,
      faces,
      notes: String(attrs.pamelaLaserNotes || "")
    };
  });
}

function getLaserArtworkPricingItemsForAllSides(editor, catalog) {
  const ed = editor || getPamelaEditorInstance();
  const current = String(window.PAMELA_SIDES?.current || "front") === "back" ? "back" : "front";
  const out = [];
  ["front", "back"].forEach((side) => {
    let state = null;
    try {
      if (side === current && ed && typeof window.CM_captureEditorNativeState === "function") {
        state = window.CM_captureEditorNativeState(ed) || null;
      }
    } catch (_) {}
    if (!state) {
      try { state = window.PAMELA_SIDES?.[side]?.draft_state || null; } catch (_) {}
    }
    if (state && Array.isArray(state.laser)) {
      out.push(...getLaserArtworkPricingItemsFromDraftState(state, side, catalog));
    }
  });
  if (!out.length) return getLaserArtworkPricingItems(ed, catalog);
  return out.map((entry, index) => ({ ...entry, index }));
}

function getLaserEntryAttr(entry, key, fallback = "") {
  try {
    if (entry?.node?.getAttr) {
      const v = entry.node.getAttr(key);
      return v == null ? fallback : v;
    }
    const attrs = entry?.attrs || entry?.item?.attrs || {};
    const v = attrs[key];
    return v == null ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

function syncLaserSizeFieldsFromArtwork(node) {
  try {
    const ed = getPamelaEditorInstance();
    const r = getLaserArtworkRectInches(node, ed);
    const wIn = r.widthIn;
    const hIn = r.heightIn;
    const wEl = document.getElementById("pamela_laser_width");
    const hEl = document.getElementById("pamela_laser_height");
    if (wEl && wIn > 0) wEl.value = String(Math.round(wIn * 100) / 100);
    if (hEl && hIn > 0) hEl.value = String(Math.round(hIn * 100) / 100);
    try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  } catch (_) {}
}

function pamelaSaveLaserArtworkSideState(node) {
  try {
    const ed = getPamelaEditorInstance();
    const sides = ed?.layers?.sides || null;
    let side = pamelaFindAncestorSideGroup(node, sides);

    if (!side) {
      side = String(
        node?.getAttr?.("pamelaLaserSide") ||
        window.PAMELA_SIDES?.current ||
        "front"
      ) === "back" ? "back" : "front";
    }

    if (typeof pamelaSaveSpecificSideState === "function") {
      pamelaSaveSpecificSideState(side);
    } else {
      if (window.PAMELA_SIDES) window.PAMELA_SIDES.current = side;
      if (typeof saveCurrentSideState === "function") saveCurrentSideState();
    }
  } catch (_) {}
}

function getLaserInitialPlacementSizePx(kind, naturalW, naturalH, safeW, safeH, designW, monumentW, source, hasExplicitLibraryInchSize = false) {
  const aspect = Number(naturalW || 0) > 0 && Number(naturalH || 0) > 0
    ? Number(naturalW) / Number(naturalH)
    : 1;
  let startW = Math.max(1, Number(naturalW || 1));
  let startH = Math.max(1, Number(naturalH || 1));
  const effectiveKind = String(kind || "spot").toLowerCase() === "scene" ? "scene" : String(kind || "spot").toLowerCase();
  const isCustom = String(source || "").toLowerCase() !== "library";
  const maxW = Math.max(DPI, Math.min(
    Math.max(DPI, Number(safeW || 0)),
    Math.max(DPI, Number(monumentW || designW || 0) - (2 * DPI))
  ));
  const maxH = Math.max(DPI, Number(safeH || 0));

  if (isCustom) {
    const laserCfg = window.PAMELA_ADDON_CONFIG?.laserEtching || {};
    const minDollars =
      effectiveKind === "portrait"
        ? Number(laserCfg?.minimums?.portrait || 0)
        : (effectiveKind === "scene"
            ? Number(laserCfg?.minimums?.sceneCustom || 0)
            : Number(laserCfg?.minimums?.spotCustom || 0));
    const prepDollars =
      effectiveKind === "scene"
        ? Number(laserCfg?.prepFees?.sceneCustom || 0)
        : (effectiveKind === "portrait" ? 0 : Number(laserCfg?.prepFees?.spotCustom || 0));
    const rateDollars =
      effectiveKind === "portrait"
        ? Number(laserCfg?.rates?.portrait || 0)
        : (effectiveKind === "scene" ? Number(laserCfg?.rates?.scene || 0) : Number(laserCfg?.rates?.spot || 0));
    const includedAreaIn = rateDollars > 0
      ? Math.max(1, (minDollars - prepDollars) / rateDollars)
      : 100;
    startW = Math.sqrt(includedAreaIn * Math.max(0.01, aspect)) * DPI;
    startH = startW / Math.max(0.01, aspect);
    const scale = Math.min(1, maxW / startW, maxH / startH);
    startW *= scale;
    startH *= scale;
  } else {
    if (!hasExplicitLibraryInchSize && effectiveKind !== "portrait") {
      const laserCfg = window.PAMELA_ADDON_CONFIG?.laserEtching || {};
      const minDollars =
        effectiveKind === "scene"
          ? Number(laserCfg?.minimums?.sceneLibrary || 0)
          : Number(laserCfg?.minimums?.spotLibrary || 0);
      const rateDollars =
        effectiveKind === "scene"
          ? Number(laserCfg?.rates?.scene || 0)
          : Number(laserCfg?.rates?.spot || 0);
      const includedAreaIn = rateDollars > 0
        ? Math.max(1, minDollars / rateDollars)
        : 100;
      const currentAreaIn = (startW / DPI) * (startH / DPI);
      if (currentAreaIn > 0 && currentAreaIn < includedAreaIn) {
        startW = Math.sqrt(includedAreaIn * Math.max(0.01, aspect)) * DPI;
        startH = startW / Math.max(0.01, aspect);
      }
    }
    const scale = Math.min(1, maxW / startW, maxH / startH);
    startW *= scale;
    startH *= scale;
  }

  if (!(startW > 0) || !(startH > 0)) {
    startW = effectiveKind === "scene" ? Math.min(Number(safeW || 1), 12 * DPI) : 10 * DPI;
    startH = startW / (aspect || 1);
  }

  return {
    width: Math.max(1, Math.round(startW)),
    height: Math.max(1, Math.round(startH))
  };
}

async function ensureLaserPreviewExportSafe(previewImg, index, previewOptions = {}) {
  if (!previewImg || typeof previewImg.toDataURL !== "function") return previewImg;
  try {
    previewImg.toDataURL("image/png");
    return previewImg;
  } catch (_) {}

  if (typeof window.CM_uploadProcessedLaserPng === "function") {
    try {
      const dataUrl = previewImg.toDataURL("image/png");
      const hosted = await window.CM_uploadProcessedLaserPng(dataUrl, index || "preview");
      if (hosted) {
        const hostedImg = await loadLaserHtmlImage(hosted, { crossOrigin: true });
        const hostedPreview = makeLaserBlackWhitePreviewImage(hostedImg, previewOptions);
        if (hostedPreview) return hostedPreview;
      }
    } catch (e) {
      console.warn("[Laser Preview] hosted export-safe fallback failed:", e);
    }
  }
  return previewImg;
}

async function placeLaserArtworkFromUrl(url, meta = {}) {
  const ed = getPamelaEditorInstance();
  if (!ed || !ed.layers?.content || typeof Konva === "undefined") {
    throw new Error("The editor is not ready for laser artwork placement.");
  }

  const img = await loadLaserHtmlImageForPlacement(url);
  const naturalW = Number(img.naturalWidth || img.width || 1);
  const naturalH = Number(img.naturalHeight || img.height || 1);
  const aspect = naturalW > 0 && naturalH > 0 ? naturalW / naturalH : 1;
  let placementSourceW = naturalW;
  let placementSourceH = naturalH;
  let hasExplicitLibraryInchSize = false;
  if (String(meta.source || "").toLowerCase() === "library") {
    const metaW = Number(
      meta.widthIn ??
      meta.width_in ??
      meta.width_inches ??
      meta.wIn ??
      meta.dimensions?.widthIn ??
      meta.dimensions?.width_in ??
      0
    );
    const metaH = Number(
      meta.heightIn ??
      meta.height_in ??
      meta.height_inches ??
      meta.hIn ??
      meta.dimensions?.heightIn ??
      meta.dimensions?.height_in ??
      0
    );
    const hasExplicitInchSize = Number.isFinite(metaW) && metaW > 0;
    hasExplicitLibraryInchSize = hasExplicitInchSize;
    if (hasExplicitInchSize) {
      placementSourceW = metaW * DPI;
      placementSourceH = (Number.isFinite(metaH) && metaH > 0)
        ? metaH * DPI
        : placementSourceW / Math.max(0.01, aspect);
    }
  }
  const designW = Number(ed.state?.design?.width || DESIGN_PX.w || 0);
  const designH = Number(ed.state?.design?.height || DESIGN_PX.h || 0);
  const safe = typeof pamelaGetSafeRect === "function"
    ? pamelaGetSafeRect(designW, designH)
    : { left: 0, top: 0, right: designW, bottom: designH };
  const safeW = Math.max(1, safe.right - safe.left);
  const safeH = Math.max(1, safe.bottom - safe.top);
  const laserPlacementRect = typeof pamelaGetLaserPlacementRect === "function"
    ? pamelaGetLaserPlacementRect(ed)
    : null;
  const placementW = Math.max(1, Number(laserPlacementRect?.width || safeW));
  const placementH = Math.max(1, Number(laserPlacementRect?.height || safeH));
  const requestedKind = String(meta.laser_kind || meta.laser_type || meta.type || document.getElementById("pamela_laser_kind")?.value || "spot").toLowerCase();

  const initialSize = getLaserInitialPlacementSizePx(
    requestedKind,
    placementSourceW,
    placementSourceH,
    Math.min(safeW, placementW),
    Math.min(safeH, placementH),
    designW,
    placementW,
    meta.source,
    hasExplicitLibraryInchSize
  );
  let startW = initialSize.width;
  let startH = initialSize.height;
  const previewOptions = {
    targetWidthPx: startW,
    targetHeightPx: startH
  };
  let previewImg = makeLaserBlackWhitePreviewImage(img, previewOptions);
  if (!previewImg) {
    throw new Error("This laser image could not be prepared. Please try again.");
  }
  previewImg = await ensureLaserPreviewExportSafe(previewImg, meta.name || meta.original_name || "laser", previewOptions);

  const cx = laserPlacementRect
    ? laserPlacementRect.left + laserPlacementRect.width / 2
    : (safe.left + safe.right) / 2;
  const cy = laserPlacementRect
    ? laserPlacementRect.top + laserPlacementRect.height / 2
    : (safe.top + safe.bottom) / 2;

  const replaceNode = meta.replaceNode && meta.replaceNode.getAttr?.("pamelaLaserEtching") === true
    ? meta.replaceNode
    : null;
  const preserveReplacementSize = meta.preserveReplacementSize === true;
  const replaceTransform = replaceNode
    ? {
        x: Number(replaceNode.x?.() || 0),
        y: Number(replaceNode.y?.() || 0),
        width: preserveReplacementSize ? Number(replaceNode.width?.() || startW) : startW,
        height: preserveReplacementSize ? Number(replaceNode.height?.() || startH) : startH,
        rotation: Number(replaceNode.rotation?.() || 0),
        scaleX: preserveReplacementSize ? Number(replaceNode.scaleX?.() || 1) : 1,
        scaleY: preserveReplacementSize ? Number(replaceNode.scaleY?.() || 1) : 1
      }
    : null;

  const node = new Konva.Image({
    image: previewImg,
    x: replaceTransform ? replaceTransform.x : cx,
    y: replaceTransform ? replaceTransform.y : cy,
    width: replaceTransform ? replaceTransform.width : startW,
    height: replaceTransform ? replaceTransform.height : startH,
    offsetX: (replaceTransform ? replaceTransform.width : startW) / 2,
    offsetY: (replaceTransform ? replaceTransform.height : startH) / 2,
    rotation: replaceTransform ? replaceTransform.rotation : 0,
    scaleX: replaceTransform ? replaceTransform.scaleX : 1,
    scaleY: replaceTransform ? replaceTransform.scaleY : 1,
    draggable: false,
    name: "pamela-laser-artwork"
  });

  node.setAttr("pamelaLaserEtching", true);
  node.setAttr("__pamelaKind", "laser");
  node.setAttr("pamelaLaserUrl", String(url || ""));
  node.setAttr("pamelaLaserSource", String(meta.source || ""));
  node.setAttr("pamelaLaserPricingSource", String(meta.source || "") === "library" ? "library" : "custom");
  node.setAttr("pamelaLaserKind", requestedKind || "spot");
  node.setAttr("pamelaLaserFaces", Math.max(1, Number(meta.faces || document.getElementById("pamela_laser_faces")?.value || 1) || 1));
  node.setAttr("pamelaLaserSide", String(meta.side || window.PAMELA_SIDES?.current || "front") === "back" ? "back" : "front");
  node.setAttr("pamelaLaserOriginalName", String(meta.original_name || meta.name || ""));
  node.setAttr("pamelaLaserContentType", String(meta.content_type || ""));
  node.setAttr("pamelaLaserImageWidth", Number(meta.width || naturalW || 0) || 0);
  node.setAttr("pamelaLaserImageHeight", Number(meta.height || naturalH || 0) || 0);
  node.setAttr("pamelaLaserLibraryType", String(meta.laser_type || meta.type || ""));
  node.setAttr("pamelaLaserCategory", String(meta.category || ""));
  node.setAttr("pamelaLaserSubcategory", String(meta.subcategory || ""));
  node.setAttr("pamelaLaserPreview", "black-and-white");
  node.setAttr("pamelaLaserPeopleConfirmed", false);
  node.setAttr("pamelaLaserNotes", String(meta.notes || ""));
  pamelaMarkLaserArtworkNode(node);
  node._pamelaLaserBaseImage = cloneLaserCanvasSource(previewImg) || previewImg;
  node.hitStrokeWidth(20);

  if (replaceNode) {
    try { if (ed.selected === replaceNode) ed.selectNode?.(null); } catch (_) {}
    try { replaceNode.destroy(); } catch (_) {}
  }
  ed.layers.content.add(node);
  node.moveToTop();
  try { ed._pamelaRestackContentLayer?.(); } catch (_) {}
  try { ed._enableDirectPointerDrag?.(node, "content"); } catch (_) {}
  try { pamelaEnableLaserArtworkDrag(ed, node); } catch (_) {}

  node.on("dblclick dbltap", async (e) => {
    try { e.cancelBubble = true; } catch (_) {}
    try { ed.selectNode?.(node); } catch (_) {}
    try {
      await window.PAMELA_replaceLaserArtworkNode?.(node);
    } catch (_) {}
  });

  node.on("dragmove transform", () => {
    syncLaserSizeFieldsFromArtwork(node);
    try { ed._scheduleOverlapColors?.(); } catch (_) {}
  });
  node.on("dragend transformend", () => {
    syncLaserSizeFieldsFromArtwork(node);
    try { ed._scheduleOverlapColors?.(); } catch (_) {}
    pamelaSaveLaserArtworkSideState(node);
  });

  try { pamelaClampNodeIntoSafeRect(ed, node); } catch (_) {}
  try { ed.layers.content.batchDraw(); } catch (_) {}
  try { ed.selectNode?.(node); } catch (_) {}
  try { window.__PAMELA_SELECTED_LASER_NODE__ = node; } catch (_) {}
  try { window.__PAMELA_MOBILE_SELECTED_LASER_NODE__ = node; } catch (_) {}
  try { pamelaSyncMobileToolbars?.(ed); } catch (_) {}
  try { ed._historyCommit?.("add-laser-artwork"); } catch (_) {}
  syncLaserSizeFieldsFromArtwork(node);
  try { ed._scheduleOverlapColors?.(); } catch (_) {}
  pamelaSaveLaserArtworkSideState(node);
  if (String(meta.source || "").toLowerCase() !== "library") {
    pamelaScheduleAutoSetLaserFaces(node, img).catch(() => {});
  } else {
    pamelaCloseAddonsFlyoutAfterMobileLaserSelection();
  }

  return node;
}

async function uploadLaserArtworkFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", "laser");
  fd.append("heic_ack", "true");

  const endpoints = [
    "https://cm-editor-server.onrender.com/api/laser/upload"
  ];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const txt = await res.text().catch(() => "");
      let data = {};
      try { data = JSON.parse(txt); } catch (_) { data = { raw: txt }; }
      if (res.ok && data && data.ok && data.url) return data;
      lastError = new Error((data && (data.error || data.message)) || txt || "Upload failed.");
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError || new Error("Laser artwork upload failed.");
}

function applyLaserUploadResultToNode(node, data, file, options = {}) {
  if (!node || node.getAttr?.("pamelaLaserEtching") !== true || !data) return false;
  try {
    node.setAttr("pamelaLaserUrl", String(data.url || node.getAttr?.("pamelaLaserUrl") || ""));
    node.setAttr("pamelaLaserSource", "upload");
    node.setAttr("pamelaLaserPricingSource", "custom");
    node.setAttr("pamelaLaserKind", String(options?.laser_kind || options?.kind || node.getAttr?.("pamelaLaserKind") || "spot"));
    node.setAttr("pamelaLaserFaces", Math.max(1, Number(options?.faces || node.getAttr?.("pamelaLaserFaces") || 1) || 1));
    node.setAttr("pamelaLaserSide", String(options?.side || node.getAttr?.("pamelaLaserSide") || window.PAMELA_SIDES?.current || "front") === "back" ? "back" : "front");
    node.setAttr("pamelaLaserOriginalName", String(data.original_name || file?.name || node.getAttr?.("pamelaLaserOriginalName") || ""));
    node.setAttr("pamelaLaserContentType", String(data.content_type || file?.type || node.getAttr?.("pamelaLaserContentType") || ""));
    node.setAttr("pamelaLaserImageWidth", Number(data.width || node.getAttr?.("pamelaLaserImageWidth") || 0) || 0);
    node.setAttr("pamelaLaserImageHeight", Number(data.height || node.getAttr?.("pamelaLaserImageHeight") || 0) || 0);
    node.setAttr("pamelaLaserOriginalStored", "Cloudflare");
    node.setAttr("pamelaLaserEditorPreview", "downsampled-black-and-white");
    node.setAttr("pamelaLaserPeopleConfirmed", false);
    node.setAttr("pamelaLaserNotes", "");
    const serverFaceCountRaw = data.face_count;
    const hasServerFaceCount =
      serverFaceCountRaw !== null &&
      serverFaceCountRaw !== undefined &&
      serverFaceCountRaw !== "" &&
      Number.isFinite(Number(serverFaceCountRaw));
    if (hasServerFaceCount) {
      const kind = String(node.getAttr?.("pamelaLaserKind") || "").toLowerCase();
      const serverFaceCount = kind === "portrait"
        ? Math.max(1, Number(serverFaceCountRaw) || 1)
        : Math.max(0, Number(serverFaceCountRaw) || 0);
      node.setAttr("pamelaLaserFaces", serverFaceCount);
      node.setAttr("pamelaLaserFacesAutoDetected", true);
      node.setAttr("pamelaLaserFacesNeedsReview", data.face_count_needs_review === true);
      node.setAttr("pamelaLaserFaceDetectionStatus", "server");
      node.setAttr("pamelaLaserFaceDetectionProvider", String(data.face_count_provider || "openai"));
      node.setAttr("pamelaLaserFaceDetectionConfidence", String(data.face_count_confidence || ""));
    } else if (data.ai_face_analysis && typeof data.ai_face_analysis === "object") {
      node.setAttr("pamelaLaserFacesNeedsReview", true);
      node.setAttr("pamelaLaserFaceDetectionStatus", "server-unavailable");
      node.setAttr("pamelaLaserFaceDetectionProvider", String(data.ai_face_analysis.provider || data.face_count_provider || "openai"));
      node.setAttr("pamelaLaserFaceDetectionReason", String(data.ai_face_analysis.reason || data.ai_face_analysis.error || "No server people count returned.").slice(0, 180));
    }
    try { node.getLayer?.()?.batchDraw?.(); } catch (_) {}
    try { getPamelaEditorInstance()?._historyCommit?.("laser-upload-url"); } catch (_) {}
    try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
    try {
      if (!hasServerFaceCount && !data.ai_face_analysis) {
        pamelaScheduleAutoSetLaserFaces(node, node._pamelaLaserBaseImage || node.image?.()).catch(() => {});
      }
    } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

function getLaserCropSourceSize(source) {
  return {
    width: Number(source?.width || source?.naturalWidth || source?.videoWidth || 0) || 0,
    height: Number(source?.height || source?.naturalHeight || source?.videoHeight || 0) || 0
  };
}

function buildLaserCropHandle(name) {
  const handle = document.createElement("button");
  handle.type = "button";
  handle.dataset.handle = name;
  handle.setAttribute("aria-label", "Resize crop area");
  handle.style.cssText = [
    "position:absolute",
    "width:34px",
    "height:34px",
    "border:2px solid #111",
    "background:#fff",
    "border-radius:999px",
    "padding:0",
    "box-sizing:border-box",
    "z-index:3"
  ].join(";");

  const positions = {
    nw: "left:6px;top:6px;cursor:nwse-resize",
    n: "left:50%;top:6px;transform:translateX(-50%);cursor:ns-resize",
    ne: "right:6px;top:6px;cursor:nesw-resize",
    e: "right:6px;top:50%;transform:translateY(-50%);cursor:ew-resize",
    se: "right:6px;bottom:6px;cursor:nwse-resize",
    s: "left:50%;bottom:6px;transform:translateX(-50%);cursor:ns-resize",
    sw: "left:6px;bottom:6px;cursor:nesw-resize",
    w: "left:6px;top:50%;transform:translateY(-50%);cursor:ew-resize"
  };
  handle.style.cssText += ";" + (positions[name] || "");
  return handle;
}

function applyLaserEdgeFeather(canvas, featherPx) {
  const px = Math.max(0, Math.round(Number(featherPx || 0)));
  if (!canvas || px <= 0) return canvas;

  const w = Number(canvas.width || 0);
  const h = Number(canvas.height || 0);
  if (w <= 0 || h <= 0) return canvas;

  const feather = Math.min(px, Math.floor(Math.min(w, h) / 2));
  if (feather <= 0) return canvas;

  const ctx = canvas.getContext("2d");
  let imageData = null;
  try {
    imageData = ctx.getImageData(0, 0, w, h);
  } catch (e) {
    console.warn("[Laser Crop] feather read failed:", e);
    return canvas;
  }

  const data = imageData.data;
  const total = w * h;
  const solid = new Uint8Array(total);
  const dist = new Float32Array(total);
  const INF = 1e9;
  for (let p = 0, idx = 3; p < total; p += 1, idx += 4) {
    solid[p] = data[idx] > 8 ? 1 : 0;
    dist[p] = INF;
  }

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const p = y * w + x;
      if (!solid[p]) continue;
      const touchesTransparent =
        x === 0 || y === 0 || x === w - 1 || y === h - 1 ||
        !solid[p - 1] ||
        !solid[p + 1] ||
        !solid[p - w] ||
        !solid[p + w];
      if (touchesTransparent) dist[p] = 0;
    }
  }

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const p = y * w + x;
      if (!solid[p]) continue;
      let d = dist[p];
      if (x > 0) d = Math.min(d, dist[p - 1] + 1);
      if (y > 0) d = Math.min(d, dist[p - w] + 1);
      if (x > 0 && y > 0) d = Math.min(d, dist[p - w - 1] + 1.4142);
      if (x < w - 1 && y > 0) d = Math.min(d, dist[p - w + 1] + 1.4142);
      dist[p] = d;
    }
  }

  for (let y = h - 1; y >= 0; y -= 1) {
    for (let x = w - 1; x >= 0; x -= 1) {
      const p = y * w + x;
      if (!solid[p]) continue;
      let d = dist[p];
      if (x < w - 1) d = Math.min(d, dist[p + 1] + 1);
      if (y < h - 1) d = Math.min(d, dist[p + w] + 1);
      if (x < w - 1 && y < h - 1) d = Math.min(d, dist[p + w + 1] + 1.4142);
      if (x > 0 && y < h - 1) d = Math.min(d, dist[p + w - 1] + 1.4142);
      dist[p] = d;
    }
  }

  for (let p = 0; p < total; p += 1) {
    if (!solid[p] || dist[p] >= feather) continue;
    const alphaScale = Math.max(0, Math.min(1, dist[p] / feather));
    const idx = (p * 4) + 3;
    data[idx] = Math.round(data[idx] * alphaScale);
  }

  try {
    ctx.putImageData(imageData, 0, 0);
  } catch (e) {
    console.warn("[Laser Crop] feather write failed:", e);
  }

  return canvas;
}

function cloneLaserCanvasSource(source) {
  const size = getLaserCropSourceSize(source);
  if (!source || !(size.width > 0) || !(size.height > 0)) return null;
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  try {
    canvas.getContext("2d").drawImage(source, 0, 0, size.width, size.height);
    return canvas;
  } catch (_) {
    return null;
  }
}

function setLaserNodeFeather(node, featherPx) {
  const px = Math.max(0, Math.round(Number(featherPx || 0)));
  const base = node?._pamelaLaserBaseImage || cloneLaserCanvasSource(node?.image?.());
  if (!node || !base) return false;
  node._pamelaLaserBaseImage = base;
  const canvas = cloneLaserCanvasSource(base);
  if (!canvas) return false;
  applyLaserEdgeFeather(canvas, px);
  node.image(canvas);
  node.setAttr("pamelaLaserFeatherPx", px);
  try { node.getLayer?.()?.batchDraw?.(); } catch (_) {}
  try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
  try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  return true;
}

// Optional full-tablet treatment. Rectangular/collage etchings keep the normal
// crop workflow; this is only used when the customer intentionally chooses a
// full etch that follows the current monument silhouette.
//
// Bakes the contents already drawn into `canvas` (sized to rect.width x
// rect.height) onto the laser node, matching the monument's stone-face rect.
// Shared by the auto-fit fallback and the Fit to Stone modal's Apply button.
function applyLaserArtworkFitToStone(ed, node, source, sourceCrop) {
  const rect = pamelaGetLaserEngravingRect(ed);
  const bgNode = ed?.nodes?.bg || null;
  const bgImage = bgNode?.image?.() || null;
  const sourceSize = getLaserCropSourceSize(source);
  if (!rect || !bgNode || !bgImage || sourceSize.width <= 0 || sourceSize.height <= 0) return false;

  const crop = {
    x: Math.max(0, Number(sourceCrop?.x || 0)),
    y: Math.max(0, Number(sourceCrop?.y || 0)),
    width: Math.max(1, Number(sourceCrop?.width || sourceSize.width)),
    height: Math.max(1, Number(sourceCrop?.height || sourceSize.height)),
  };
  crop.width = Math.min(crop.width, sourceSize.width - crop.x);
  crop.height = Math.min(crop.height, sourceSize.height - crop.y);

  // Customers may raise the lower fit boundary, but never extend it beneath
  // the monument opening detected from the foreground artwork.
  const fitBottomRatio = Math.max(0.25, Math.min(1, Number(sourceCrop?.fitBottomRatio || 1) || 1));

  const outW = Math.max(1, Math.round(rect.width));
  const outH = Math.max(1, Math.round(rect.height));
  // True crop: the baked canvas/node only ever covers the visible (kept)
  // height. There is no transparent "dead zone" below it, so the node's own
  // bounding box (and its selection handles) always match what's actually
  // on the stone -- nothing to clip away later.
  const cropOutH = Math.max(1, Math.round(outH * fitBottomRatio));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = cropOutH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;

  // Always use one uniform scale. Any sub-pixel ratio difference is clipped,
  // never corrected by stretching one axis independently.
  ctx.clearRect(0, 0, outW, cropOutH);
  const fitScale = Math.max(outW / crop.width, cropOutH / crop.height);
  const fittedW = crop.width * fitScale;
  const fittedH = crop.height * fitScale;
  ctx.drawImage(
    source,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    (outW - fittedW) / 2,
    (cropOutH - fittedH) / 2,
    fittedW,
    fittedH
  );

  // Mask against only the TOP slice of the full engraving rect (matching the
  // cropped canvas height) so the background/foreground aren't stretched to
  // fit a shorter canvas.
  const croppedRect = {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: Number(rect.height || outH) * fitBottomRatio,
  };

  // Use the same generic node-transform draw as the foreground overlay below
  // (it correctly accounts for scaleX/scaleY, including mirrored companion
  // sides) instead of a naive x/y/width/height draw, which previously
  // produced a fully-transparent result whenever the background was mirrored.
  ctx.globalCompositeOperation = "destination-in";
  pamelaDrawForegroundInRect(ctx, bgNode, bgImage, croppedRect, outW, cropOutH);
  ctx.globalCompositeOperation = "source-over";

  // Remove any foreground-covered pixels as well. For dynamically measured
  // openings this keeps the final image and the blue preview line identical.
  const foregroundNode = ed?.nodes?.borderOverlay || null;
  const foregroundImage = foregroundNode?.image?.() || null;
  if (foregroundNode && foregroundImage) {
    ctx.globalCompositeOperation = "destination-out";
    pamelaDrawForegroundInRect(ctx, foregroundNode, foregroundImage, croppedRect, outW, cropOutH);
    ctx.globalCompositeOperation = "source-over";
  }

  if (!node._pamelaLaserPreFitImage) {
    node._pamelaLaserPreFitImage = cloneLaserCanvasSource(source) || source;
  }
  node._pamelaLaserBaseImage = cloneLaserCanvasSource(canvas) || canvas;
  node._pamelaLaserCropRootImage = cloneLaserCanvasSource(canvas) || canvas;
  node._pamelaLaserCropRootWidth = outW;
  node._pamelaLaserCropRootHeight = cropOutH;
  node.image(canvas);
  node.position({
    x: Number(rect.left || 0) + outW / 2,
    y: Number(rect.top || 0) + cropOutH / 2,
  });
  node.size({ width: outW, height: cropOutH });
  node.offset({ x: outW / 2, y: cropOutH / 2 });
  node.rotation(0);
  node.scale({ x: 1, y: 1 });
  node.setAttr("pamelaLaserFitToStone", true);
  node.setAttr("pamelaLaserFitCrop", crop);
  node.setAttr("pamelaLaserFitBottomRatio", fitBottomRatio);
  node.setAttr("pamelaLaserCrop", null);
  node.setAttr("pamelaLaserFeatherPx", 0);
  pamelaMarkLaserArtworkNode(node);
  try { ed.selectNode?.(node); } catch (_) {}
  try { syncLaserSizeFieldsFromArtwork(node); } catch (_) {}
  try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
  try { node.getLayer?.()?.batchDraw?.(); } catch (_) {}
  try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
  try { window.PAMELA_syncMobileToolbars?.(ed); } catch (_) {}
  try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  return true;
}

// Modal: shows the artwork against the monument's actual detected opening
// shape (via pamelaGetLaserEngravingRect), draggable for full repositioning,
// with a zoom slider and a separate bottom-boundary handle that controls how
// much of the frame (from the top) stays active before a shade covers the
// rest. `source` is always the node's existing (small, already-downsampled)
// preview image -- never a full-resolution re-fetch.
function openLaserFitToStoneModal(node) {
  const ed = getPamelaEditorInstance();
  const rect = pamelaGetLaserEngravingRect(ed);
  const bgNode = ed?.nodes?.bg || null;
  const bgImage = bgNode?.image?.() || null;
  const foregroundNode = ed?.nodes?.borderOverlay || null;
  const foregroundImage = foregroundNode?.image?.() || null;
  const source = node?._pamelaLaserPreFitImage || node?._pamelaLaserBaseImage || node?.image?.();
  const sourceSize = getLaserCropSourceSize(source);
  if (!ed || !node || !rect || !bgNode || !bgImage || sourceSize.width <= 0 || sourceSize.height <= 0) {
    alert("Fit to Stone is not available for this monument.");
    return false;
  }

  document.getElementById("pamela_laser_fit_overlay")?.remove?.();
  const overlay = document.createElement("div");
  overlay.id = "pamela_laser_fit_overlay";
  overlay.dataset.fitBoundarySource = String(rect.source || "configured-insets");
  try { overlay.dataset.fitInsetsIn = JSON.stringify(rect.insetsIn || {}); } catch (_) {}
  overlay.style.cssText = "position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,.62);display:flex;align-items:center;justify-content:center;padding:18px;box-sizing:border-box;";
  const modal = document.createElement("div");
  modal.style.cssText = "width:min(920px,calc(100vw - 36px));max-height:calc(100vh - 36px);background:#fff;border-radius:14px;box-shadow:0 24px 70px rgba(0,0,0,.38);display:flex;flex-direction:column;overflow:hidden;font-family:inherit;";

  const header = document.createElement("div");
  header.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:12px;padding:18px 20px;border-bottom:1px solid #ddd;";
  const title = document.createElement("div");
  title.textContent = node.getAttr?.("pamelaLaserFitToStone") === true ? "Adjust Image on Stone" : "Position Image on Stone";
  title.style.cssText = "font-weight:900;font-size:22px;line-height:1.1;color:#111;";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close image positioning");
  closeBtn.textContent = "x";
  closeBtn.style.cssText = "width:44px;height:44px;border:1px solid #ccc;background:#fff;border-radius:10px;font-size:24px;font-weight:900;cursor:pointer;";
  header.append(title, closeBtn);

  const body = document.createElement("div");
  body.style.cssText = "padding:18px 20px;overflow-y:auto;overflow-x:hidden;";
  const hint = document.createElement("div");
  hint.textContent = "Only the area inside the blue line will appear on the stone. Drag the image to position it, use Zoom if needed, or drag the bottom-center handle up and down to adjust the lower edge. The image will keep its original proportions.";
  hint.style.cssText = "margin:0 0 14px;color:#333;font-weight:700;line-height:1.4;";

  const targetAspect = Math.max(0.1, Math.round(Number(rect.width || 1)) / Math.max(1, Math.round(Number(rect.height || 1))));
  // Leave room for the header, hint text, zoom row, and footer buttons below
  // the frame so the whole modal fits without scrolling on smaller screens.
  const maxW = Math.min(760, Math.max(240, window.innerWidth - 128));
  const maxH = Math.min(440, Math.max(170, window.innerHeight - 380));
  const sideContextRatio = 0.08;
  const topContextRatio = 0.08;
  const bottomContextRatio = 0.28;
  let frameW = maxW / (1 + sideContextRatio * 2);
  let frameH = frameW / targetAspect;
  if (frameH * (1 + topContextRatio + bottomContextRatio) > maxH) {
    frameH = maxH / (1 + topContextRatio + bottomContextRatio);
    frameW = frameH * targetAspect;
  }
  frameW = Math.max(1, Math.round(frameW));
  frameH = Math.max(1, Math.round(frameH));

  const contextPadX = Math.max(12, Math.round(frameW * sideContextRatio));
  const contextPadTop = Math.max(10, Math.round(frameH * topContextRatio));
  const contextPadBottom = Math.max(28, Math.round(frameH * bottomContextRatio));
  const previewW = frameW + contextPadX * 2;
  const previewH = frameH + contextPadTop + contextPadBottom;
  const preview = document.createElement("div");
  preview.style.cssText = `position:relative;width:${previewW}px;height:${previewH}px;max-width:100%;margin:0 auto;background:linear-gradient(#dedad7,#c8c2bd);overflow:hidden;border-radius:10px;box-shadow:inset 0 0 0 1px rgba(0,0,0,.25);`;

  // Draw the transparent monument artwork behind the active image area. This
  // gives customers enough context to see the tablet edges and base below it.
  const contextCanvas = document.createElement("canvas");
  contextCanvas.width = previewW;
  contextCanvas.height = previewH;
  contextCanvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";
  const contextCtx = contextCanvas.getContext("2d");
  const contextScaleX = frameW / Math.max(1, Number(rect.width || 1));
  const contextScaleY = frameH / Math.max(1, Number(rect.height || 1));
  try {
    const contextNode = foregroundImage ? foregroundNode : bgNode;
    const contextImage = foregroundImage || bgImage;
    const contextNodeX = Number(contextNode?.x?.() || 0);
    const contextNodeY = Number(contextNode?.y?.() || 0);
    const contextNodeW = Number(contextNode?.width?.() || contextImage?.naturalWidth || 0);
    const contextNodeH = Number(contextNode?.height?.() || contextImage?.naturalHeight || 0);
    const drawContextX = contextPadX + (contextNodeX - Number(rect.left || 0)) * contextScaleX;
    const drawContextY = contextPadTop + (contextNodeY - Number(rect.top || 0)) * contextScaleY;
    contextCtx.globalAlpha = 0.48;
    contextCtx.drawImage(contextImage, drawContextX, drawContextY, contextNodeW * contextScaleX, contextNodeH * contextScaleY);
    contextCtx.globalAlpha = 1;
  } catch (_) {}

  const frame = document.createElement("div");
  frame.style.cssText = `position:absolute;left:${contextPadX}px;top:${contextPadTop}px;width:${frameW}px;height:${frameH}px;background:#111;overflow:hidden;touch-action:none;user-select:none;cursor:grab;border-radius:7px;box-shadow:0 0 0 2px rgba(0,0,0,.75);`;
  frame.setAttribute("role", "application");
  frame.setAttribute("aria-label", "Drag image to position it on the stone");
  const canvas = document.createElement("canvas");
  canvas.width = frameW;
  canvas.height = frameH;
  canvas.style.cssText = `display:block;width:${frameW}px;height:${frameH}px;`;
  const ctx = canvas.getContext("2d");

  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = frameW;
  maskCanvas.height = frameH;
  maskCanvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";
  const maskCtx = maskCanvas.getContext("2d");
  const openingMaskCanvas = document.createElement("canvas");
  openingMaskCanvas.width = frameW;
  openingMaskCanvas.height = frameH;
  const openingMaskCtx = openingMaskCanvas.getContext("2d");
  if (foregroundNode && foregroundImage && rect.source === "foreground-detected") {
    openingMaskCtx.fillStyle = "#fff";
    openingMaskCtx.fillRect(0, 0, frameW, frameH);
    openingMaskCtx.globalCompositeOperation = "destination-out";
    pamelaDrawForegroundInRect(openingMaskCtx, foregroundNode, foregroundImage, rect, frameW, frameH);
    openingMaskCtx.globalCompositeOperation = "source-over";
  } else {
    const fallbackScaleX = frameW / Math.max(1, Number(rect.width || 1));
    const fallbackScaleY = frameH / Math.max(1, Number(rect.height || 1));
    const fallbackBgX = (Number(bgNode.x?.() || 0) - Number(rect.left || 0)) * fallbackScaleX;
    const fallbackBgY = (Number(bgNode.y?.() || 0) - Number(rect.top || 0)) * fallbackScaleY;
    openingMaskCtx.drawImage(bgImage, fallbackBgX, fallbackBgY, Number(bgNode.width?.() || 0) * fallbackScaleX, Number(bgNode.height?.() || 0) * fallbackScaleY);
  }
  maskCtx.fillStyle = "rgba(0,0,0,.58)";
  maskCtx.fillRect(0, 0, frameW, frameH);
  maskCtx.globalCompositeOperation = "destination-out";
  maskCtx.drawImage(openingMaskCanvas, 0, 0);
  maskCtx.globalCompositeOperation = "source-over";

  // Build an exact, high-contrast boundary from the monument's transparency.
  // A white edge remains visible over dark photos; blue remains visible over
  // light photos and matches the editor's normal selection language.
  const outlineCanvas = document.createElement("canvas");
  outlineCanvas.width = frameW;
  outlineCanvas.height = frameH;
  outlineCanvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";
  let outlineEdgeData = null;
  try {
    const alphaCanvas = document.createElement("canvas");
    alphaCanvas.width = frameW;
    alphaCanvas.height = frameH;
    const alphaCtx = alphaCanvas.getContext("2d");
    alphaCtx.drawImage(openingMaskCanvas, 0, 0);
    const alphaData = alphaCtx.getImageData(0, 0, frameW, frameH);
    const edgeData = outlineCanvas.getContext("2d").createImageData(frameW, frameH);
    const alphaAt = (x, y) => {
      if (x < 0 || y < 0 || x >= frameW || y >= frameH) return 0;
      return alphaData.data[(y * frameW + x) * 4 + 3];
    };
    for (let y = 0; y < frameH; y += 1) {
      for (let x = 0; x < frameW; x += 1) {
        if (alphaAt(x, y) < 20) continue;
        let edgeDistance = 0;
        for (let radius = 1; radius <= 5 && !edgeDistance; radius += 1) {
          if (
            alphaAt(x - radius, y) < 20 || alphaAt(x + radius, y) < 20 ||
            alphaAt(x, y - radius) < 20 || alphaAt(x, y + radius) < 20 ||
            alphaAt(x - radius, y - radius) < 20 || alphaAt(x + radius, y - radius) < 20 ||
            alphaAt(x - radius, y + radius) < 20 || alphaAt(x + radius, y + radius) < 20
          ) edgeDistance = radius;
        }
        if (!edgeDistance) continue;
        const p = (y * frameW + x) * 4;
        const whiteEdge = edgeDistance <= 2;
        edgeData.data[p] = whiteEdge ? 255 : 26;
        edgeData.data[p + 1] = whiteEdge ? 255 : 115;
        edgeData.data[p + 2] = whiteEdge ? 255 : 232;
        edgeData.data[p + 3] = 255;
      }
    }
    outlineCanvas.getContext("2d").putImageData(edgeData, 0, 0);
    outlineEdgeData = edgeData;
  } catch (e) {
    console.warn("[Laser Fit] stone outline preview failed:", e);
  }

  const savedBottomRatio = Math.max(0.25, Math.min(1, Number(node.getAttr?.("pamelaLaserFitBottomRatio") || 1) || 1));
  const minimumBoundaryH = Math.min(frameH, Math.max(56, Math.round(frameH * 0.25)));
  let boundaryH = Math.max(minimumBoundaryH, Math.min(frameH, Math.round(frameH * savedBottomRatio)));

  const lowerBoundaryLine = document.createElement("div");
  lowerBoundaryLine.style.cssText = "position:absolute;left:0;right:0;height:4px;background:#1a73e8;box-shadow:0 -1px 0 #fff,0 1px 0 #fff;pointer-events:none;z-index:5;";
  const boundaryHandle = document.createElement("button");
  boundaryHandle.type = "button";
  boundaryHandle.setAttribute("aria-label", "Adjust bottom fit boundary");
  boundaryHandle.title = "Drag up or down to adjust the bottom edge";
  boundaryHandle.textContent = "↕";
  boundaryHandle.style.cssText = "position:absolute;width:48px;height:48px;margin:0;padding:0;border:3px solid #1a73e8;border-radius:999px;background:#fff;color:#1459b8;box-shadow:0 3px 12px rgba(0,0,0,.35);font:900 25px/1 system-ui,sans-serif;cursor:ns-resize;touch-action:none;user-select:none;z-index:8;transform:translate(-50%,-50%);";

  const baseScale = Math.max(frameW / sourceSize.width, frameH / sourceSize.height);
  const savedCrop = node.getAttr?.("pamelaLaserFitCrop") || null;
  let zoom = 1;
  let drawW = sourceSize.width * baseScale;
  let drawH = sourceSize.height * baseScale;
  let drawX = (frameW - drawW) / 2;
  let drawY = (frameH - drawH) / 2;
  if (savedCrop && Number(savedCrop.width) > 0 && Number(savedCrop.height) > 0) {
    const savedScale = frameW / Number(savedCrop.width);
    zoom = Math.max(1, Math.min(4, savedScale / baseScale));
    drawW = sourceSize.width * baseScale * zoom;
    drawH = sourceSize.height * baseScale * zoom;
    drawX = -Number(savedCrop.x || 0) * baseScale * zoom;
    drawY = -Number(savedCrop.y || 0) * baseScale * zoom;
  }

  const clampPosition = () => {
    drawX = Math.min(0, Math.max(frameW - drawW, drawX));
    drawY = Math.min(0, Math.max(boundaryH - drawH, drawY));
  };
  // Real pixel-level crop: only paint the artwork (and only keep the
  // precomputed outline) above boundaryH, via canvas clip()/clearRect rather
  // than CSS clip-path -- on this site something outside our control was
  // overriding clip-path so it never visibly cropped anything.
  const render = () => {
    clampPosition();
    ctx.clearRect(0, 0, frameW, frameH);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, frameW, boundaryH);
    ctx.clip();
    ctx.drawImage(source, drawX, drawY, drawW, drawH);
    ctx.restore();

    const outlineCtx = outlineCanvas.getContext("2d");
    outlineCtx.clearRect(0, 0, frameW, frameH);
    if (outlineEdgeData) outlineCtx.putImageData(outlineEdgeData, 0, 0);
    outlineCtx.clearRect(0, boundaryH, frameW, Math.max(0, frameH - boundaryH));
  };
  const updateBottomBoundaryPreview = () => {
    lowerBoundaryLine.style.top = `${Math.max(0, boundaryH - 2)}px`;
    boundaryHandle.style.left = `${contextPadX + frameW / 2}px`;
    boundaryHandle.style.top = `${contextPadTop + boundaryH}px`;
    render();
  };
  updateBottomBoundaryPreview();
  frame.append(canvas, maskCanvas, outlineCanvas, lowerBoundaryLine);
  preview.append(contextCanvas, frame, boundaryHandle);

  const zoomRow = document.createElement("div");
  zoomRow.style.cssText = "display:flex;align-items:center;gap:12px;margin:16px auto 0;max-width:620px;";
  const zoomLabel = document.createElement("label");
  zoomLabel.textContent = "Zoom";
  zoomLabel.style.cssText = "font-weight:900;color:#111;";
  const zoomInput = document.createElement("input");
  zoomInput.type = "range";
  zoomInput.min = "1";
  zoomInput.max = "4";
  zoomInput.step = "0.01";
  zoomInput.value = String(zoom);
  zoomInput.setAttribute("aria-label", "Image zoom");
  zoomInput.style.cssText = "flex:1;min-width:150px;accent-color:#111;";
  const zoomValue = document.createElement("div");
  zoomValue.style.cssText = "min-width:54px;text-align:right;font-weight:900;color:#111;";
  const renderZoomValue = () => { zoomValue.textContent = Math.round(zoom * 100) + "%"; };
  renderZoomValue();
  zoomRow.append(zoomLabel, zoomInput, zoomValue);

  let drag = null;
  frame.addEventListener("pointerdown", (evt) => {
    evt.preventDefault();
    try { frame.setPointerCapture(evt.pointerId); } catch (_) {}
    frame.style.cursor = "grabbing";
    drag = { pointerId: evt.pointerId, x: evt.clientX, y: evt.clientY, drawX, drawY };
  });
  frame.addEventListener("pointermove", (evt) => {
    if (!drag || (drag.pointerId != null && evt.pointerId !== drag.pointerId)) return;
    evt.preventDefault();
    drawX = drag.drawX + (evt.clientX - drag.x);
    drawY = drag.drawY + (evt.clientY - drag.y);
    render();
  });
  const endDrag = (evt) => {
    if (!drag) return;
    try { frame.releasePointerCapture(drag.pointerId); } catch (_) {}
    drag = null;
    frame.style.cursor = "grab";
  };
  frame.addEventListener("pointerup", endDrag);
  frame.addEventListener("pointercancel", endDrag);

  let boundaryDrag = null;
  boundaryHandle.addEventListener("pointerdown", (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    try { boundaryHandle.setPointerCapture(evt.pointerId); } catch (_) {}
    boundaryDrag = { pointerId: evt.pointerId };
  });
  boundaryHandle.addEventListener("pointermove", (evt) => {
    if (!boundaryDrag || (boundaryDrag.pointerId != null && evt.pointerId !== boundaryDrag.pointerId)) return;
    evt.preventDefault();
    evt.stopPropagation();
    const frameBox = frame.getBoundingClientRect();
    boundaryH = Math.max(minimumBoundaryH, Math.min(frameH, evt.clientY - frameBox.top));
    updateBottomBoundaryPreview();
  });
  const endBoundaryDrag = () => {
    if (!boundaryDrag) return;
    try { boundaryHandle.releasePointerCapture(boundaryDrag.pointerId); } catch (_) {}
    boundaryDrag = null;
  };
  boundaryHandle.addEventListener("pointerup", endBoundaryDrag);
  boundaryHandle.addEventListener("pointercancel", endBoundaryDrag);

  zoomInput.addEventListener("input", () => {
    const oldW = drawW;
    const oldH = drawH;
    const focusX = oldW > 0 ? (frameW / 2 - drawX) / oldW : 0.5;
    const focusY = oldH > 0 ? (frameH / 2 - drawY) / oldH : 0.5;
    zoom = Math.max(1, Math.min(4, Number(zoomInput.value || 1)));
    drawW = sourceSize.width * baseScale * zoom;
    drawH = sourceSize.height * baseScale * zoom;
    drawX = frameW / 2 - focusX * drawW;
    drawY = frameH / 2 - focusY * drawH;
    renderZoomValue();
    render();
  });

  body.append(hint, preview, zoomRow);
  const footer = document.createElement("div");
  footer.style.cssText = "display:flex;justify-content:flex-end;gap:10px;padding:16px 20px;border-top:1px solid #ddd;background:#fafafa;";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.textContent = "Cancel";
  cancelBtn.style.cssText = "border:1px solid #bbb;background:#fff;border-radius:999px;padding:12px 18px;font-weight:900;cursor:pointer;";
  const applyBtn = document.createElement("button");
  applyBtn.type = "button";
  applyBtn.textContent = "Apply to Stone";
  applyBtn.style.cssText = "border:0;background:#111;color:#fff;border-radius:999px;padding:12px 20px;font-weight:900;cursor:pointer;";
  footer.append(cancelBtn, applyBtn);
  modal.append(header, body, footer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const close = () => { try { overlay.remove(); } catch (_) {} };
  closeBtn.addEventListener("click", close);
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("pointerdown", (evt) => { if (evt.target === overlay) close(); });
  applyBtn.addEventListener("click", () => {
    const effectiveScale = baseScale * zoom;
    const crop = {
      x: Math.max(0, -drawX / effectiveScale),
      y: Math.max(0, -drawY / effectiveScale),
      width: Math.min(sourceSize.width, frameW / effectiveScale),
      height: Math.min(sourceSize.height, boundaryH / effectiveScale),
      fitBottomRatio: Math.max(0.25, Math.min(1, boundaryH / frameH)),
    };
    try { ed._historyCommit?.("laser-fit-before"); } catch (_) {}
    if (applyLaserArtworkFitToStone(ed, node, source, crop)) {
      try { ed._historyCommit?.("laser-fit-to-stone"); } catch (_) {}
      close();
    }
  });
  return true;
}

// Fit is a guided positioning action so customers never need to discover that
// they should crop first. The separate Crop command remains available for
// ordinary rectangular artwork editing.
function fitSelectedLaserArtworkToStone() {
  const ed = getPamelaEditorInstance();
  const node = getSelectedLaserArtworkNode(ed);
  if (!ed || !node) {
    alert("Select a laser artwork image first.");
    return false;
  }
  return openLaserFitToStoneModal(node);
}

// Exposed globally: the document-level click delegate and other call sites
// for this button live in a different closure than this definition, which
// caused a silently-swallowed "fitSelectedLaserArtworkToStone is not defined"
// ReferenceError every time Fit to Stone was clicked.
window.fitSelectedLaserArtworkToStone = fitSelectedLaserArtworkToStone;

function openLaserCropModal(node) {
  const ed = getPamelaEditorInstance();
  const currentBase = node?._pamelaLaserBaseImage || node?.image?.();
  if (!node?._pamelaLaserCropRootImage && currentBase) {
    node._pamelaLaserCropRootImage = cloneLaserCanvasSource(currentBase) || currentBase;
    node._pamelaLaserCropRootWidth = Math.max(1, Number(node.width?.() || 1));
    node._pamelaLaserCropRootHeight = Math.max(1, Number(node.height?.() || 1));
  }
  const source = node?._pamelaLaserCropRootImage || currentBase;
  const srcSize = getLaserCropSourceSize(source);
  if (!source || srcSize.width <= 0 || srcSize.height <= 0) {
    alert("Could not read this laser artwork image for cropping.");
    return;
  }

  const overlay = document.createElement("div");
  overlay.id = "pamela_laser_crop_overlay";
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483000",
    "background:rgba(0,0,0,.58)",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:18px",
    "box-sizing:border-box"
  ].join(";");

  const modal = document.createElement("div");
  modal.style.cssText = [
    "width:min(920px,calc(100vw - 36px))",
    "max-height:calc(100vh - 36px)",
    "background:#fff",
    "border-radius:12px",
    "box-shadow:0 24px 70px rgba(0,0,0,.35)",
    "display:flex",
    "flex-direction:column",
    "overflow:hidden",
    "font-family:inherit"
  ].join(";");

  const header = document.createElement("div");
  header.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:12px;padding:18px 20px;border-bottom:1px solid #ddd;";
  const title = document.createElement("div");
  title.textContent = "Crop Laser Artwork";
  title.style.cssText = "font-weight:900;font-size:22px;line-height:1.1;color:#111;";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.textContent = "x";
  closeBtn.style.cssText = "width:44px;height:44px;border:1px solid #ccc;background:#fff;border-radius:10px;font-size:24px;font-weight:900;cursor:pointer;";
  header.appendChild(title);
  header.appendChild(closeBtn);

  const body = document.createElement("div");
  body.style.cssText = "padding:18px 20px;overflow:auto;";

  const hint = document.createElement("div");
  hint.textContent = "Drag the crop outline around the part you want to keep. Use the white dots to resize it.";
  hint.style.cssText = "margin:0 0 12px;color:#333;font-weight:700;line-height:1.35;";
  let featherPx = Math.max(0, Math.round(Number(node?.getAttr?.("pamelaLaserFeatherPx") || 0)));

  const featherControls = document.createElement("div");
  featherControls.style.cssText = [
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "gap:10px",
    "flex-wrap:wrap",
    "margin:0 0 14px"
  ].join(";");

  const featherMinusBtn = document.createElement("button");
  featherMinusBtn.type = "button";
  featherMinusBtn.textContent = "-5 px feather";
  featherMinusBtn.style.cssText = "border:1px solid #bbb;background:#fff;border-radius:999px;padding:10px 14px;font-weight:900;cursor:pointer;";

  const featherLabel = document.createElement("div");
  featherLabel.style.cssText = "min-width:130px;text-align:center;font-weight:900;color:#111;";

  const featherPlusBtn = document.createElement("button");
  featherPlusBtn.type = "button";
  featherPlusBtn.textContent = "+5 px feather";
  featherPlusBtn.style.cssText = "border:1px solid #111;background:#111;color:#fff;border-radius:999px;padding:10px 14px;font-weight:900;cursor:pointer;";

  featherControls.appendChild(featherMinusBtn);
  featherControls.appendChild(featherLabel);
  featherControls.appendChild(featherPlusBtn);

  const maxW = Math.min(760, Math.max(260, window.innerWidth - 100));
  const maxH = Math.min(520, Math.max(240, window.innerHeight - 230));
  const displayScale = Math.min(maxW / srcSize.width, maxH / srcSize.height, 1);
  const displayW = Math.max(1, Math.round(srcSize.width * displayScale));
  const displayH = Math.max(1, Math.round(srcSize.height * displayScale));

  const frame = document.createElement("div");
  frame.style.cssText = [
    "position:relative",
    "width:" + displayW + "px",
    "height:" + displayH + "px",
    "max-width:100%",
    "margin:0 auto",
    "background:#111",
    "overflow:hidden",
    "touch-action:none",
    "user-select:none"
  ].join(";");

  const canvas = document.createElement("canvas");
  canvas.width = displayW;
  canvas.height = displayH;
  canvas.style.cssText = "display:block;width:" + displayW + "px;height:" + displayH + "px;";
  const ctx = canvas.getContext("2d");
  try {
    ctx.clearRect(0, 0, displayW, displayH);
    ctx.drawImage(source, 0, 0, displayW, displayH);
  } catch (e) {
    console.warn("[Laser Crop] preview draw failed:", e);
    alert("Could not open this image for cropping.");
    return;
  }

  const cropBox = document.createElement("div");
  cropBox.style.cssText = [
    "position:absolute",
    "left:0",
    "top:0",
    "width:" + displayW + "px",
    "height:" + displayH + "px",
    "border:3px solid #fff",
    "box-shadow:0 0 0 9999px rgba(0,0,0,.48)",
    "box-sizing:border-box",
    "cursor:move",
    "touch-action:none"
  ].join(";");

  ["nw", "n", "ne", "e", "se", "s", "sw", "w"].forEach((name) => {
    cropBox.appendChild(buildLaserCropHandle(name));
  });

  frame.appendChild(canvas);
  frame.appendChild(cropBox);
  body.appendChild(hint);
  body.appendChild(frame);

  const footer = document.createElement("div");
  footer.style.cssText = "display:flex;justify-content:flex-end;gap:10px;padding:16px 20px;border-top:1px solid #ddd;background:#fafafa;";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.textContent = "Cancel";
  cancelBtn.style.cssText = "border:1px solid #bbb;background:#fff;border-radius:999px;padding:12px 18px;font-weight:900;cursor:pointer;";
  const applyBtn = document.createElement("button");
  applyBtn.type = "button";
  applyBtn.textContent = "Apply Crop";
  applyBtn.style.cssText = "border:0;background:#111;color:#fff;border-radius:999px;padding:12px 20px;font-weight:900;cursor:pointer;";
  footer.appendChild(cancelBtn);
  footer.appendChild(applyBtn);

  modal.appendChild(header);
  modal.appendChild(body);
  modal.appendChild(footer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const savedCrop = node?.getAttr?.("pamelaLaserCrop") || null;
  let box = savedCrop && Number(savedCrop.width) > 0 && Number(savedCrop.height) > 0
    ? {
        x: Number(savedCrop.x || 0) * displayW / srcSize.width,
        y: Number(savedCrop.y || 0) * displayH / srcSize.height,
        w: Number(savedCrop.width) * displayW / srcSize.width,
        h: Number(savedCrop.height) * displayH / srcSize.height,
      }
    : { x: 0, y: 0, w: displayW, h: displayH };
  let drag = null;
  const minSize = Math.min(44, Math.max(24, Math.floor(Math.min(displayW, displayH) * 0.18)));

  const renderBox = () => {
    cropBox.style.left = box.x + "px";
    cropBox.style.top = box.y + "px";
    cropBox.style.width = box.w + "px";
    cropBox.style.height = box.h + "px";
    const previewFeather = Math.max(0, Math.round(featherPx * displayScale));
    cropBox.style.boxShadow = [
      "0 0 0 9999px rgba(0,0,0,.48)",
      previewFeather > 0 ? "inset 0 0 " + previewFeather + "px rgba(255,255,255,.72)" : ""
    ].filter(Boolean).join(",");
  };

  const renderFeather = () => {
    featherLabel.textContent = "Feather: " + featherPx + " px";
    featherMinusBtn.disabled = featherPx <= 0;
    featherMinusBtn.style.opacity = featherMinusBtn.disabled ? ".45" : "1";
    renderBox();
  };

  const clampBox = (next) => {
    let x = Number(next.x || 0);
    let y = Number(next.y || 0);
    let w = Number(next.w || minSize);
    let h = Number(next.h || minSize);
    w = Math.max(minSize, Math.min(displayW, w));
    h = Math.max(minSize, Math.min(displayH, h));
    x = Math.max(0, Math.min(displayW - w, x));
    y = Math.max(0, Math.min(displayH - h, y));
    return { x, y, w, h };
  };

  const updateForPointer = (evt) => {
    if (!drag) return;
    const dx = evt.clientX - drag.startX;
    const dy = evt.clientY - drag.startY;
    const start = drag.box;
    let next = { x: start.x, y: start.y, w: start.w, h: start.h };
    const handle = drag.handle;

    if (handle === "move") {
      next.x = start.x + dx;
      next.y = start.y + dy;
    } else {
      if (handle.includes("w")) {
        next.x = start.x + dx;
        next.w = start.w - dx;
      }
      if (handle.includes("e")) next.w = start.w + dx;
      if (handle.includes("n")) {
        next.y = start.y + dy;
        next.h = start.h - dy;
      }
      if (handle.includes("s")) next.h = start.h + dy;
    }

    box = clampBox(next);
    renderBox();
  };

  const endDrag = () => {
    try {
      if (drag?.pointerId != null) cropBox.releasePointerCapture(drag.pointerId);
    } catch (_) {}
    drag = null;
    window.removeEventListener("pointermove", updateForPointer);
    window.removeEventListener("pointerup", endDrag);
    window.removeEventListener("pointercancel", endDrag);
  };

  cropBox.addEventListener("pointerdown", (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    try { cropBox.setPointerCapture(evt.pointerId); } catch (_) {}
    const handle = evt.target?.dataset?.handle || "move";
    drag = {
      handle,
      pointerId: evt.pointerId,
      startX: evt.clientX,
      startY: evt.clientY,
      box: { x: box.x, y: box.y, w: box.w, h: box.h }
    };
    window.addEventListener("pointermove", updateForPointer);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  });

  featherMinusBtn.addEventListener("click", () => {
    featherPx = Math.max(0, featherPx - 5);
    renderFeather();
  });

  featherPlusBtn.addEventListener("click", () => {
    const maxFeather = Math.max(0, Math.floor(Math.min(srcSize.width, srcSize.height) / 2));
    featherPx = Math.min(maxFeather, featherPx + 5);
    renderFeather();
  });

  const close = () => {
    endDrag();
    try { overlay.remove(); } catch (_) {}
  };

  closeBtn.addEventListener("click", close);
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("pointerdown", (evt) => {
    if (evt.target === overlay) close();
  });

  applyBtn.addEventListener("click", () => {
    try {
      const scaleX = srcSize.width / displayW;
      const scaleY = srcSize.height / displayH;
      const crop = {
        x: Math.max(0, Math.round(box.x * scaleX)),
        y: Math.max(0, Math.round(box.y * scaleY)),
        width: Math.max(1, Math.round(box.w * scaleX)),
        height: Math.max(1, Math.round(box.h * scaleY))
      };
      crop.width = Math.min(crop.width, srcSize.width - crop.x);
      crop.height = Math.min(crop.height, srcSize.height - crop.y);

      const cropped = document.createElement("canvas");
      cropped.width = crop.width;
      cropped.height = crop.height;
      cropped.getContext("2d").drawImage(
        source,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
      );
      const baseCropped = cloneLaserCanvasSource(cropped) || cropped;
      const displayCropped = cloneLaserCanvasSource(baseCropped) || cropped;
      applyLaserEdgeFeather(displayCropped, featherPx);

      const rootW = Math.max(1, Number(node._pamelaLaserCropRootWidth || node.width?.() || 1));
      const rootH = Math.max(1, Number(node._pamelaLaserCropRootHeight || node.height?.() || 1));
      const newW = Math.max(1, rootW * (crop.width / srcSize.width));
      const newH = Math.max(1, rootH * (crop.height / srcSize.height));

      node._pamelaLaserBaseImage = baseCropped;
      node.image(displayCropped);
      node.width(newW);
      node.height(newH);
      node.offsetX(newW / 2);
      node.offsetY(newH / 2);
      node.setAttr("pamelaLaserPreview", "black-and-white-cropped");
      node.setAttr("pamelaLaserCrop", crop);
      node.setAttr("pamelaLaserFeatherPx", featherPx);
      try { node.crop(null); } catch (_) {}
      try { pamelaClampNodeIntoSafeRect(ed, node); } catch (_) {}
      try { ed?.selectNode?.(node); } catch (_) {}
      try { ed?.nodes?.tr?.forceUpdate?.(); } catch (_) {}
      try { ed?.layers?.content?.batchDraw?.(); } catch (_) {}
      try { ed?.layers?.ui?.batchDraw?.(); } catch (_) {}
      try { ed?._historyCommit?.("crop-laser-artwork"); } catch (_) {}
      syncLaserSizeFieldsFromArtwork(node);
      pamelaSaveLaserArtworkSideState(node);
      close();
    } catch (e) {
      console.warn("[Laser Crop] apply failed:", e);
      alert("Could not apply that crop. Please try a different crop area.");
    }
  });

  renderFeather();
}

function cropSelectedLaserArtwork() {
  const ed = getPamelaEditorInstance();
  const node = getLaserArtworkNode(ed);
  if (!node) {
    alert("Select a laser artwork image first.");
    return;
  }

  try {
    ed?.selectNode?.(node);
    openLaserCropModal(node);
  } catch (e) {
    console.warn("[Laser Crop] modal failed:", e);
    alert("Could not open crop for this image.");
  }
}

function ensureLaserFileInput() {
  let input = document.getElementById("pamela_laser_file_input");
  if (input) {
    try { input.remove(); } catch (_) {}
  }
  input = document.createElement("input");
  input.type = "file";
  input.id = "pamela_laser_file_input";
  input.accept = ".png,.jpg,.jpeg,.heic,.heif,image/png,image/jpeg,image/jpg,image/pjpeg,image/heic,image/heif";
  input.style.position = "fixed";
  input.style.left = "-99999px";
  input.style.top = "-99999px";
  input.style.opacity = "0";
  input.style.pointerEvents = "none";
  document.body.appendChild(input);
  return input;
}

// Personal photo uploads (often 12-48MP straight off a phone) make the local
// preview step painfully slow: new Image() + a full-resolution canvas draw
// has to decode every pixel of the original before makeLaserBlackWhitePreviewImage
// ever gets a chance to shrink it. createImageBitmap decodes off the main
// thread in supporting browsers, so decode through that first and shrink the
// result to a generously-sized canvas (well above any real placement size)
// before handing back an object URL. Any failure (older browser, HEIC, etc.)
// returns null and the caller falls back to the original, unchanged behavior.
async function pamelaCreateFastLocalPreviewUrl(file, maxDim = 1800) {
  let bitmap = null;
  try {
    if (typeof createImageBitmap !== "function" || !file) return null;
    bitmap = await createImageBitmap(file);
    const srcW = bitmap.width;
    const srcH = bitmap.height;
    if (!(srcW > 0) || !(srcH > 0)) return null;

    const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return null;
    return URL.createObjectURL(blob);
  } catch (_) {
    return null;
  } finally {
    try { bitmap?.close?.(); } catch (_) {}
  }
}

async function openLaserArtworkUploader(options = {}) {
  const input = ensureLaserFileInput();
  try { input.value = ""; } catch (_) {}
  const replaceNode = options?.replaceNode && options.replaceNode.getAttr?.("pamelaLaserEtching") === true
    ? options.replaceNode
    : null;

  return new Promise((resolve) => {
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return resolve(false);
      if (!pamelaIsSupportedCustomerImageFile(file)) {
        alert("Unsupported file type. Please use PNG, JPG, JPEG, HEIC, or HEIF.");
        return resolve(false);
      }

      const qualityInfo = await pamelaGetUploadQualityInfo(file, options);
      let qualityChoice = { action: "accept", enhancement: false };

      let previewNode = replaceNode;
      let localUrl = "";
      let laserUploadOk = false;
      try {
        localUrl = (await pamelaCreateFastLocalPreviewUrl(file)) || URL.createObjectURL(file);
        previewNode = await placeLaserArtworkFromUrl(localUrl, {
          source: "local-preview",
          laser_kind: options?.laser_kind || options?.kind || "",
          faces: options?.faces || 1,
          side: options?.side || "",
          original_name: file.name,
          content_type: file.type || "",
          replaceNode
        });
        try {
          previewNode?.setAttr?.("pamelaImageQualityIssue", qualityInfo.isHeic ? "heic" : (qualityInfo.lowRes ? "low-resolution" : ""));
          previewNode?.setAttr?.("pamelaImageEnhancementRequested", false);
          previewNode?.setAttr?.("pamelaImageEnhancementFee", "");
          previewNode?.setAttr?.("pamelaImagePixelWidth", qualityInfo.width || 0);
          previewNode?.setAttr?.("pamelaImagePixelHeight", qualityInfo.height || 0);
        } catch (_) {}
      } catch (thumbErr) {
        console.warn("[Laser Upload] local preview failed:", thumbErr);
      }
      try { if (localUrl) URL.revokeObjectURL(localUrl); } catch (_) {}

      qualityChoice = await pamelaShowUploadQualityChoice(file, qualityInfo, { label: "laser artwork" });
      if (qualityChoice?.action === "choose-new") {
        try {
          if (!replaceNode && previewNode && previewNode.getAttr?.("pamelaLaserEtching") === true) {
            previewNode.destroy();
            getPamelaEditorInstance()?.layers?.content?.batchDraw?.();
          }
        } catch (_) {}
        window.setTimeout(() => {
          try { window.PAMELA_openLaserArtworkUploader?.(options); } catch (_) {}
        }, 0);
        return resolve(false);
      }
      try {
        previewNode?.setAttr?.("pamelaImageEnhancementRequested", qualityChoice?.enhancement === true);
        previewNode?.setAttr?.("pamelaImageEnhancementFee", qualityChoice?.enhancement === true ? "25" : "");
      } catch (_) {}

      try {
        if (previewNode) {
          previewNode.setAttr("pamelaLaserFaceDetectionStatus", "uploading");
          previewNode.setAttr("pamelaLaserFacesNeedsReview", true);
          try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
        }
        // Laser-only: porcelain portrait uploads do not need people-count analysis.
        setLaserAiOverlay(true, getLaserAiOverlayMessage(options?.laser_kind || options?.kind || ""));
        const data = await uploadLaserArtworkFile(file);
        if (!applyLaserUploadResultToNode(previewNode, data, file, options)) {
          previewNode = await placeLaserArtworkFromUrl(data.url, {
            source: "upload",
            laser_kind: options?.laser_kind || options?.kind || "",
            faces: options?.faces || 1,
            side: options?.side || "",
            original_name: data.original_name || file.name,
            content_type: data.content_type || file.type || "",
            width: data.width || 0,
            height: data.height || 0,
            replaceNode: previewNode
          });
        }
        try {
          previewNode?.setAttr?.("pamelaImageQualityIssue", qualityInfo.isHeic ? "heic" : (qualityInfo.lowRes ? "low-resolution" : ""));
          previewNode?.setAttr?.("pamelaImageEnhancementRequested", qualityChoice?.enhancement === true);
          previewNode?.setAttr?.("pamelaImageEnhancementFee", qualityChoice?.enhancement === true ? "25" : "");
          previewNode?.setAttr?.("pamelaImagePixelWidth", qualityInfo.width || data.width || 0);
          previewNode?.setAttr?.("pamelaImagePixelHeight", qualityInfo.height || data.height || 0);
          try { pamelaSaveLaserArtworkSideState(previewNode); } catch (_) {}
        } catch (_) {}
        laserUploadOk = true;
        pamelaCloseAddonsFlyoutAfterMobileLaserSelection();
      } catch (err) {
        console.warn("[Laser Upload] failed:", err);
        alert((err && err.message) ? err.message : "Laser artwork upload failed.");
      } finally {
        setLaserAiOverlay(false);
      }

      try {
        if (laserUploadOk && previewNode && previewNode.getAttr?.("pamelaLaserEtching") === true) {
          const confirmResult = await showLaserPeopleConfirmModal(previewNode, { afterUpload: true });
          if (confirmResult?.action === "choose-file") {
            const retryOptions = Object.assign({}, options, {
              replaceNode: null,
              laser_kind: previewNode.getAttr?.("pamelaLaserKind") || options?.laser_kind || options?.kind || "spot",
              source: "custom",
              side: previewNode.getAttr?.("pamelaLaserSide") || options?.side || window.PAMELA_SIDES?.current || "front"
            });
            try {
              if (previewNode === getSelectedLaserArtworkNode(getPamelaEditorInstance())) {
                getPamelaEditorInstance()?.selectNode?.(null);
              }
            } catch (_) {}
            try { previewNode.destroy(); } catch (_) {}
            try { getPamelaEditorInstance()?.layers?.content?.batchDraw?.(); } catch (_) {}
            try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
            window.setTimeout(() => {
              try { window.PAMELA_openLaserArtworkUploader?.(retryOptions); } catch (_) {}
            }, 0);
          }
        }
      } catch (_) {}

      resolve(true);
    };

    input.click();
  });
}

window.PAMELA_placeLaserArtworkFromUrl = placeLaserArtworkFromUrl;
window.PAMELA_openLaserArtworkUploader = openLaserArtworkUploader;

(function pamelaWireLaserDoubleClickReplaceFallback() {
  if (window.__PAMELA_LASER_DBL_REPLACE_FALLBACK__) return;
  window.__PAMELA_LASER_DBL_REPLACE_FALLBACK__ = true;

  function findLaserNodeFromTarget(target) {
    let cur = target || null;
    let hops = 0;
    while (cur && hops < 12) {
      try {
        if (cur.getAttr?.("pamelaLaserEtching") === true) return cur;
      } catch (_) {}
      try { cur = cur.getParent?.() || null; } catch (_) { cur = null; }
      hops += 1;
    }
    return null;
  }

  function pointIsInsideNode(stage, node) {
    try {
      const pt = stage?.getPointerPosition?.();
      if (!pt || !node?.getClientRect) return false;
      const rect = node.getClientRect({ skipStroke: false, skipShadow: false });
      const pad = 10;
      return (
        pt.x >= rect.x - pad &&
        pt.x <= rect.x + rect.width + pad &&
        pt.y >= rect.y - pad &&
        pt.y <= rect.y + rect.height + pad
      );
    } catch (_) {
      return false;
    }
  }

  function isInsideNodeAtPoint(node, pt) {
    try {
      if (!pt || !node?.getClientRect) return false;
      const rect = node.getClientRect({ skipStroke: false, skipShadow: false });
      const pad = 10;
      return (
        pt.x >= rect.x - pad &&
        pt.x <= rect.x + rect.width + pad &&
        pt.y >= rect.y - pad &&
        pt.y <= rect.y + rect.height + pad
      );
    } catch (_) {
      return false;
    }
  }

  function replaceLaserNode(node) {
    if (!node || node.getAttr?.("pamelaLaserEtching") !== true) return;
    const ed = getPamelaEditorInstance();
    try { ed?.selectNode?.(node); } catch (_) {}
    try {
      const source = String(node.getAttr?.("pamelaLaserPricingSource") || node.getAttr?.("pamelaLaserSource") || "custom").toLowerCase();
      const kind = String(node.getAttr?.("pamelaLaserKind") || node.getAttr?.("pamelaLaserLibraryType") || "spot").toLowerCase();
      const side = String(node.getAttr?.("pamelaLaserSide") || window.PAMELA_SIDES?.current || "front") === "back" ? "back" : "front";
      if (source === "library") {
        if (!window.PAMELA_LASER_LIBRARY_UI?.open) {
          alert("Laser library is not connected yet.");
          return;
        }
        window.__PAMELA_LASER_LIBRARY_TARGET_SIDE__ = side;
        window.PAMELA_LASER_LIBRARY_UI.open({
          type: kind === "scene" ? "scene" : "spot",
          side,
          replaceNode: node
        });
        return;
      }
      window.PAMELA_openLaserArtworkUploader?.({
        replaceNode: node,
        laser_kind: kind || "spot",
        source: "custom",
        faces: node.getAttr?.("pamelaLaserFaces") || 1,
        side
      });
    } catch (e) {
      console.warn("[Laser Upload] double-click replace failed:", e);
    }
  }

  window.PAMELA_replaceLaserArtworkNode = replaceLaserNode;

  const bind = () => {
    const ed = getPamelaEditorInstance();
    const stage = ed?.stage || ed?.konvaStage || null;
    if (!stage) return false;

    // Local equivalent of the editor's own isEmptyOrBg -- that one lives in a
    // different closure, so calling it directly here threw a silently
    // swallowed ReferenceError and double-click-to-replace never worked when
    // clicking on empty canvas/background instead of directly on the node.
    const isEmptyOrBg = (t) => t === stage || (t && t === (getPamelaEditorInstance()?.nodes?.bg));

    if (!stage.__pamelaLaserDblReplaceBound) {
      stage.__pamelaLaserDblReplaceBound = true;
      stage.on("dblclick.pamelaLaserReplace dbltap.pamelaLaserReplace", (e) => {
        try {
          let node = findLaserNodeFromTarget(e?.target);
          if (!node && isEmptyOrBg(e?.target)) {
            const selected = getSelectedLaserArtworkNode(ed);
            if (selected && pointIsInsideNode(stage, selected)) node = selected;
          }
          if (!node) return;
          try { e.cancelBubble = true; } catch (_) {}
          replaceLaserNode(node);
        } catch (_) {}
      });
    }

    const container = stage.container?.();
    if (container && !container.__pamelaLaserDomDblReplaceBound) {
      container.__pamelaLaserDomDblReplaceBound = true;
      container.addEventListener("dblclick", (evt) => {
        try {
          if (ed?.panMode) return;
          try { stage.setPointersPositions?.(evt); } catch (_) {}
          const pt = stage.getPointerPosition?.();
          let node = null;
          const hit = pt ? stage.getIntersection?.(pt) : null;
          if (hit) node = findLaserNodeFromTarget(hit);
          if (!node && isEmptyOrBg(hit)) {
            const selected = getSelectedLaserArtworkNode(getPamelaEditorInstance());
            if (selected && isInsideNodeAtPoint(selected, pt)) node = selected;
          }
          if (!node) return;
          try { evt.preventDefault(); } catch (_) {}
          try { evt.stopPropagation(); } catch (_) {}
          replaceLaserNode(node);
        } catch (e) {
          console.warn("[Laser Upload] canvas double-click replace failed:", e);
        }
      }, true);
    }

    return true;
  };

  if (bind()) return;
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    if (bind() || tries > 200) clearInterval(timer);
  }, 50);
})();

window.PAMELA_ADD_LASER_LIBRARY_TO_STAGE = async (item, options = {}) => {
  const url = String(item?.url || "");
  if (!url) throw new Error("Laser library item missing url.");
  const node = await placeLaserArtworkFromUrl(url, {
    source: "library",
    name: item?.name || "",
    original_name: item?.name || "",
    content_type: item?.contentType || item?.content_type || "",
    width: item?.width || 0,
    height: item?.height || 0,
    laser_type: item?.type || "",
    category: item?.category || "",
    subcategory: item?.subcategory || "",
    replaceNode: options?.replaceNode || null,
    preserveReplacementSize: options?.preserveReplacementSize === true
  });
  pamelaCloseAddonsFlyoutAfterMobileLaserSelection();
  return node;
};

// --- Your existing summary + snapshot functions (kept) ---
function buildOrderSummary(editorInstance) {
  if (typeof editorInstance.getOrderSummary === "function") {
    try { return editorInstance.getOrderSummary(); } catch (_) {}
  }
  const s = editorInstance?.state || editorInstance?.getState?.() || {};
  return {
    layout: layout,
    product_handle: handle || "",
    variant_id: String(selectEl?.value || ""),
    variant_title: (variants.find(v => String(v.id) === String(selectEl?.value)) || {}).title || "",
    style: s?.style || s?.engraveStyle || s?.finishStyle || "",
    text_fill: s?.textFill || "",
    notes: "",
  };
}

function getDesignSnapshot(editorInstance) {
  const candidateFns = ["serialize","toJSON","exportJSON","getDesignJSON","exportState","getState"];
  for (const fn of candidateFns) {
    if (typeof editorInstance?.[fn] === "function") {
      try { const val = editorInstance[fn](); if (val) return val; } catch (_) {}
    }
  }
  if (editorInstance?.state) return editorInstance.state;
  return {};
}

// Attempt to LOAD a snapshot back into the editor (best-effort)
function loadDesignSnapshot(editorInstance, snapshotObj) {
  const candidateFns = ["deserialize","fromJSON","importJSON","loadJSON","loadState","setState","importState"];
  for (const fn of candidateFns) {
    if (typeof editorInstance?.[fn] === "function") {
      try {
        editorInstance[fn](snapshotObj);

        const savedPortraits = Array.isArray(snapshotObj?.portraits)
          ? snapshotObj.portraits.slice().sort((a, b) => (Number(a?.slot ?? 0) - Number(b?.slot ?? 0)))
          : [];

        if (savedPortraits.length) {
          (async () => {
            try {
              editorInstance.__cmRestoringPortraits = true;

              for (const savedPortrait of savedPortraits) {
                const p = {
                  shape: String(savedPortrait?.shape || "rect"),
                  wIn: Number(savedPortrait?.wIn || 4) || 4,
                  hIn: Number(savedPortrait?.hIn || 4) || 4,
                  label: String(
                    savedPortrait?.label ||
                    savedPortrait?.wrapper?.name ||
                    savedPortrait?.meta?.original_name ||
                    ""
                  )
                };

                addPlaceholder(editorInstance, p);

                const stage = editorInstance?.stage;
                const wrappers = stage?.find?.(".pamela-portrait-group") || [];
                const wrapper = wrappers
                  .slice()
                  .sort((a, b) => ((a?._id || 0) - (b?._id || 0)))
                  .pop();

                if (!wrapper) continue;

                try {
                  if (savedPortrait?.slot != null) {
                    wrapper.setAttr("__portraitSlot", Number(savedPortrait.slot));
                    wrapper.setAttr("pamelaPortraitSlot", Number(savedPortrait.slot));
                  }
                } catch (_) {}

                if (savedPortrait?.url || savedPortrait?.croppedDataUrl) {
                  await rebuildOnePortrait(wrapper, savedPortrait);
                } else {
                  try {
                    wrapper.x(savedPortrait?.wrapper?.x || 0);
                    wrapper.y(savedPortrait?.wrapper?.y || 0);
                    wrapper.scaleX(savedPortrait?.wrapper?.scaleX || 1);
                    wrapper.scaleY(savedPortrait?.wrapper?.scaleY || 1);
                    wrapper.rotation(savedPortrait?.wrapper?.rotation || 0);
                  } catch (_) {}
                }
              }
            } finally {
              try { delete editorInstance.__cmRestoringPortraits; } catch (_) {
                editorInstance.__cmRestoringPortraits = false;
              }
            }

            try {
              const lyr = editorInstance?.layers?.portraits;
              if (lyr?.batchDraw) lyr.batchDraw();
              else if (lyr?.draw) lyr.draw();
              else editorInstance?.stage?.draw?.();
            } catch (_) {}
          })();
        }

        return true;
      } catch (_) {}
    }
  }

  console.warn("Editor does not expose an import method; front/back swap will be UI-only until import is available.");
  return false;
}

async function addItemsToCart(items) {
  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ items })
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error("Cart add failed. " + txt);
  }
  return await res.json();
}

// -----------------------------
// AUTO-LOAD ADDON PRODUCTS + PRICES FROM SHOPIFY
// -----------------------------
async function fetchProductByHandle(handle) {
  const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Could not load product: ${handle}`);
  return await res.json();
}

async function fetchProductByHandleOrTitle(handle, title) {
  try {
    if (handle) return await fetchProductByHandle(handle);
  } catch (_) {}

  const q = String(title || "").trim();
  if (!q) return null;

  try {
    const url =
      `/search/suggest.json?q=${encodeURIComponent(q)}` +
      "&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last";
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) return null;
    const data = await res.json();
    const products = data?.resources?.results?.products || [];
    const target = q.toLowerCase();
    const match = products.find((p) => String(p?.title || "").trim().toLowerCase() === target) || products[0];
    const foundHandle = String(match?.handle || "").trim();
    return foundHandle ? await fetchProductByHandle(foundHandle) : null;
  } catch (_) {
    return null;
  }
}

function moneyFromCents(cents, symbol = "$") {
  const n = (Number(cents || 0) / 100).toFixed(2);
  return `${symbol}${n}`;
}

function findVariantBySku(productJson, sku) {
  const s = String(sku || "").trim();
  return (productJson?.variants || []).find(v => String(v.sku || "").trim() === s) || null;
}

function firstVariant(productJson) {
  return Array.isArray(productJson?.variants) && productJson.variants.length
    ? productJson.variants[0]
    : null;
}

// Cache addon catalog
const PAMELA_ADDON_CATALOG = {
  loaded: false,
  backSingle: null,
  backCompanion: null,
  backWord: null,
  vaseFront: null,
  vaseBack: null,
  panel: null,
  panelUpgrade: null,
  clipart: null,
  laser: {
    spotLibraryMin: null,
    spotCustomMin: null,
    spotExtraArea: null,
    sceneLibraryMin: null,
    sceneCustomMin: null,
    sceneExtraArea: null,
    portraitMin: null,
    portraitExtraArea: null,
    additionalFace: null
  },
  imageEnhancement: null,
  porcelainVariants: [] // {id,title,price_cents}
};

// ============================================================
// STEP 1: PORTRAIT CART + MANIFEST WIRING (paste once)
// - Collects portrait WRAPPERS + PHOTO transforms from your current system
// - Uses wrapper attrs you already set: __portraitUrl, __portraitHasPhoto, etc.
// - Exposes:
//   window.CM_collectPortraitAssetsFromStage(editor)
//   window.CM_injectPortraitsIntoManifest(manifest, portraits)
//   window.CM_addPortraitProps(properties, editor)
// ============================================================

(function CM_PortraitCartWiring() {
  if (window.__CM_PortraitCartWiring_On) return;
  window.__CM_PortraitCartWiring_On = true;

  const PROP_PORTRAITS = "_cm_portraits";
  const WRAPPER_TOKEN = "pamela-portrait-group";
  const INNER_SEL = ".pamela-portrait-inner";
  const PHOTO_SEL = ".pamela-portrait-photo"; // you set name: "pamela-portrait-photo"

  function safeJsonStringify(o) {
    try { return JSON.stringify(o); } catch (_) { return "[]"; }
  }

  function getStage(editorInstance) {
    return editorInstance?.stage || editorInstance?.konvaStage || null;
  }

  function isPortraitWrapper(node) {
    try {
      if (!node) return false;
      if (typeof node.getAttr === "function") {
        if (node.getAttr("__isPortraitGroup") === true) return true;
        if (node.getAttr("pamelaType") === "portrait") return true;
        if (node.getAttr("pamelaPortrait") === true) return true;
        if (node.getAttr("__pamelaKind") === "portrait") return true;
      }
      if (typeof node.name === "function") {
        const nm = String(node.name() || "");
        if (nm.includes(WRAPPER_TOKEN)) return true;
      }
    } catch (_) {}
    return false;
  }

  function findPortraitWrappers(stage) {
    if (!stage || typeof stage.find !== "function") return [];

    // Try the fast selector first (works if the wrapper name includes "pamela-portrait-group")
    let wrappers = [];
    try { wrappers = stage.find("." + WRAPPER_TOKEN) || []; } catch (_) {}

    // Fallback: scan Groups and filter by attrs/name
    if (!wrappers || !wrappers.length) {
      let groups = [];
      try { groups = stage.find("Group") || []; } catch (_) { groups = []; }
      wrappers = (groups || []).filter(isPortraitWrapper);
    }

    // De-dupe
    const out = [];
    const seen = new Set();
    (wrappers || []).forEach(w => {
      const id = (w && (w._id || w._konvaId || w._cacheId)) || w;
      if (seen.has(id)) return;
      seen.add(id);
      out.push(w);
    });

    return out;
  }

  function getInner(wrapper) {
    try { return wrapper?.findOne?.(INNER_SEL) || null; } catch (_) {}
    return null;
  }

  function getPhoto(wrapper) {
    const inner = getInner(wrapper);
    if (!inner) return null;

    // Photo is Konva.Image with name: "pamela-portrait-photo"
    try {
      return inner.findOne(PHOTO_SEL) || null;
    } catch (_) {}
    return null;
  }

  function getAbsMatrix(node) {
    try {
      const t = node?.getAbsoluteTransform?.();
      if (t && typeof t.getMatrix === "function") return t.getMatrix();
    } catch (_) {}
    return null;
  }

  function collectPortraitAssetsFromStage(editorInstance) {
    const stage = getStage(editorInstance);
    if (!stage) return [];

    const wrappers = findPortraitWrappers(stage);
    const out = [];

    wrappers.forEach((wrapper, idx) => {
      try {
        if (typeof wrapper.isVisible === "function" ? !wrapper.isVisible() : (typeof wrapper.visible === "function" && !wrapper.visible())) return;

const inner = getInner(wrapper);
const photo = getPhoto(wrapper);

const rawUrl =
  (wrapper.getAttr && wrapper.getAttr("__portraitUrl"))
    ? String(wrapper.getAttr("__portraitUrl") || "")
    : "";

let url = rawUrl;

// If wrapper URL is still a blob preview, try to fall back to the live image src
try {
  const liveSrc =
    (photo && typeof photo.image === "function" && photo.image() && photo.image().src)
      ? String(photo.image().src || "")
      : "";

  if ((!url || url.startsWith("blob:")) && /^https?:\/\//i.test(liveSrc)) {
    url = liveSrc;
  }
} catch (_) {}

// Never save a blob: URL into draft resume
if (url.startsWith("blob:")) {
  url = "";
}

const hasPhoto = !!(wrapper.getAttr && wrapper.getAttr("__portraitHasPhoto") === true);

        const slot =
          Number(wrapper.getAttr?.("pamelaPortraitSlot") ?? wrapper.getAttr?.("__portraitSlot") ?? idx) || 0;

        const wIn = Number(wrapper.getAttr?.("__portraitWIn") ?? wrapper.getAttr?.("wIn") ?? 0) || 0;
        const hIn = Number(wrapper.getAttr?.("__portraitHIn") ?? wrapper.getAttr?.("hIn") ?? 0) || 0;
        const ppi = Number(wrapper.getAttr?.("__portraitPPI") ?? 72) || 72;

        const shape = String(wrapper.getAttr?.("__portraitShape") ?? wrapper.getAttr?.("shape") ?? "") || "";

        const meta = {
          original_name: String(wrapper.getAttr?.("__portraitOriginalName") ?? ""),
          content_type: String(wrapper.getAttr?.("__portraitContentType") ?? ""),
          img_w: Number(wrapper.getAttr?.("__portraitImgW") ?? 0) || 0,
          img_h: Number(wrapper.getAttr?.("__portraitImgH") ?? 0) || 0,
          quality_issue: String(wrapper.getAttr?.("__portraitQualityIssue") ?? ""),
          enhancement_requested: String(wrapper.getAttr?.("__portraitEnhancementRequested") ?? "").toLowerCase() === "true",
          enhancement_fee: String(wrapper.getAttr?.("__portraitEnhancementFee") ?? ""),
          uploaded_at: String(wrapper.getAttr?.("__portraitUploadedAt") ?? ""),
          variant_id: Number(
            wrapper.getAttr?.("__porcelainVariantId") ??
            wrapper.getAttr?.("__portraitVariantId") ??
            wrapper.getAttr?.("pamelaPortraitVariantId") ??
            0
          ) || 0,
          title: String(
            wrapper.getAttr?.("__portraitVariantTitle") ??
            wrapper.getAttr?.("__porcelainLabel") ??
            ""
          ),
          price_cents: Number(wrapper.getAttr?.("__portraitVariantPriceCents") ?? 0) || 0
        };

        // wrapper placement (world)
        const wAbs = getAbsMatrix(wrapper);

        // photo placement (inside inner clip space) + abs
        let photoPayload = null;
        if (photo) {
          const pAbs = getAbsMatrix(photo);
          photoPayload = {
            x: Number(photo.x?.() ?? 0) || 0,
            y: Number(photo.y?.() ?? 0) || 0,
            width: Number(photo.width?.() ?? 0) || 0,
            height: Number(photo.height?.() ?? 0) || 0,
            scaleX: Number(photo.scaleX?.() ?? 1) || 1,
            scaleY: Number(photo.scaleY?.() ?? 1) || 1,
            rotation: Number(photo.rotation?.() ?? 0) || 0,
            abs_transform: pAbs || undefined
          };
        }

        out.push({
          i: idx,
          slot,
          url,
          hasPhoto,
          variant_id: meta.variant_id,
          title: meta.title,
          price_cents: meta.price_cents,
          shape,
          wIn,
          hIn,
          ppi,
          wrapper: {
            x: Number(wrapper.x?.() ?? 0) || 0,
            y: Number(wrapper.y?.() ?? 0) || 0,
            scaleX: Number(wrapper.scaleX?.() ?? 1) || 1,
            scaleY: Number(wrapper.scaleY?.() ?? 1) || 1,
            rotation: Number(wrapper.rotation?.() ?? 0) || 0,
            abs_transform: wAbs || undefined,
            name: (typeof wrapper.name === "function") ? String(wrapper.name() || "") : ""
          },
          photo: photoPayload,
        // See skipImageEncode in CM_collectImageNodes: the dirty-check hash
        // poll never reads this data URL, only whether something changed
        // (wrapper transform/meta above already cover that), so skip the
        // canvas rasterize+PNG-encode for every portrait on every poll tick.
        croppedDataUrl: window.__PAMELA_LITE_DRAFT_CAPTURE__ === true ? "" : (() => {
          try {
            const inner = getInner(wrapper);
            if (!inner || typeof inner.toDataURL !== "function") return "";

            return inner.toDataURL({
              pixelRatio: 2
            });
          } catch (_) {
            return "";
          }
        })(),
          croppedUrl: "",
          meta
        });
      } catch (_) {}
    });

    return out;
  }

  function injectPortraitsIntoManifest(manifestObj, portraitAssets) {
    if (!manifestObj || typeof manifestObj !== "object") return manifestObj;
    manifestObj.portraits = Array.isArray(portraitAssets) ? portraitAssets : [];
    return manifestObj;
  }

  function addPortraitProps(properties, editorInstance) {
    const props = properties || {};
    const payload = collectPortraitAssetsFromStage(editorInstance);
    props[PROP_PORTRAITS] = safeJsonStringify(payload);
    return props;
  }
    async function uploadCroppedPortraitPng(dataUrl, index) {
      try {
        const src = String(dataUrl || "").trim();
        if (!src || !src.startsWith("data:image/png;base64,")) return "";

        const res = await fetch(src);
        const blob = await res.blob();

        const form = new FormData();
        form.append("file", blob, `portrait-cropped-${index || 0}.png`);

        let up = await fetch("https://cm-editor-server.onrender.com/api/portraits/upload-cropped", {
          method: "POST",
          body: form
        });

        if (!up.ok) {
          let msg = "Cropped portrait upload failed.";
          try {
            const j = await up.json();
            msg = j?.error || msg;
          } catch (_) {
            try {
              msg = await up.text();
            } catch (_) {}
          }
          throw new Error(msg);
        }

        const json = await up.json();
        return String(json?.url || "").trim();
      } catch (e) {
        console.warn("uploadCroppedPortraitPng failed:", e);
        return "";
      }
    }

  async function uploadCartDesignThumbPng(dataUrl, sideLabel) {
    try {
      const src = String(dataUrl || "").trim();
      if (isCartSafeImageReference(src)) return src;
      if (!src || !src.startsWith("data:image/png;base64,")) return "";

      const res = await fetch(src);
      const blob = await res.blob();

      const form = new FormData();
      const side = String(sideLabel || "design").replace(/[^a-z0-9_-]+/gi, "-").toLowerCase() || "design";
      form.append("file", blob, `design-thumb-${side}.png`);

      const up = await fetch("https://cm-editor-server.onrender.com/api/portraits/upload-cropped", {
        method: "POST",
        body: form
      });

      if (!up.ok) {
        let msg = "Design thumbnail upload failed.";
        try {
          const j = await up.json();
          msg = j?.error || msg;
        } catch (_) {
          try {
            msg = await up.text();
          } catch (_) {}
        }
        throw new Error(msg);
      }

      const json = await up.json();
      return String(json?.url || "").trim();
    } catch (e) {
      console.warn("uploadCartDesignThumbPng failed:", e);
      return "";
    }
  }

  async function uploadCroppedPortraitsInManifest(manifest) {
    if (!manifest || !Array.isArray(manifest.portraits) || !manifest.portraits.length) {
      return manifest;
    }

    for (let i = 0; i < manifest.portraits.length; i++) {
      const p = manifest.portraits[i];
      const croppedDataUrl = String(p?.croppedDataUrl || "").trim();
      if (!croppedDataUrl) continue;

      const croppedUrl = await uploadCroppedPortraitPng(croppedDataUrl, i + 1);
      if (croppedUrl) {
        p.croppedUrl = croppedUrl;
      }

      delete p.croppedDataUrl;
    }

    return manifest;
  }

  async function uploadProcessedLaserPng(dataUrl, index) {
    try {
      const src = String(dataUrl || "").trim();
      if (!src || !src.startsWith("data:image/png;base64,")) return "";

      const res = await fetch(src);
      const blob = await res.blob();

      const form = new FormData();
      form.append("file", blob, `laser-processed-${index || 0}.png`);

      const up = await fetch("https://cm-editor-server.onrender.com/api/laser/upload-processed", {
        method: "POST",
        body: form
      });

      if (!up.ok) {
        let msg = "Processed laser upload failed.";
        try {
          const j = await up.json();
          msg = j?.error || msg;
        } catch (_) {
          try {
            msg = await up.text();
          } catch (_) {}
        }
        throw new Error(msg);
      }

      const json = await up.json();
      return String(json?.url || "").trim();
    } catch (e) {
      console.warn("uploadProcessedLaserPng failed:", e);
      return "";
    }
  }

  function getLaserCartThumbDataUrlFromNode(node) {
    try {
      const img = typeof node?.image === "function" ? node.image() : null;
      return CM_imageSourceToDataUrl(img, 420);
    } catch (_) {
      return "";
    }
  }

  async function uploadLaserCartThumbFromNode(node, index) {
    try {
      if (!node || node.getAttr?.("pamelaLaserEtching") !== true) return "";
      const existing = String(node.getAttr?.("pamelaLaserThumbUrl") || "").trim();
      if (isCartSafeImageReference(existing)) return existing;

      const dataUrl = getLaserCartThumbDataUrlFromNode(node);
      const uploaded = await uploadProcessedLaserPng(dataUrl, index);
      if (uploaded) {
        node.setAttr("pamelaLaserThumbUrl", uploaded);
        if (!node.getAttr("pamelaLaserProcessedUrl")) node.setAttr("pamelaLaserProcessedUrl", uploaded);
      }
      return uploaded;
    } catch (e) {
      console.warn("uploadLaserCartThumbFromNode failed:", e);
      return "";
    }
  }

  async function uploadLaserCartThumbFromDraftItem(item, index) {
    try {
      if (!item || String(item.kind || "") !== "laser") return "";
      const attrs = item.attrs && typeof item.attrs === "object" ? item.attrs : (item.attrs = {});
      const existing = String(attrs.pamelaLaserThumbUrl || attrs.pamelaLaserProcessedUrl || item.processedSrc || "").trim();
      if (isCartSafeImageReference(existing)) return existing;

      const dataUrl = String(item.displaySrc || item.processedDataUrl || "").trim();
      if (!dataUrl || !dataUrl.startsWith("data:image/png;base64,")) return "";

      const uploaded = await uploadProcessedLaserPng(dataUrl, index);
      if (uploaded) {
        attrs.pamelaLaserThumbUrl = uploaded;
        attrs.pamelaLaserProcessedUrl = attrs.pamelaLaserProcessedUrl || uploaded;
        item.thumbSrc = uploaded;
        item.processedSrc = item.processedSrc || uploaded;
      }
      return uploaded;
    } catch (e) {
      console.warn("uploadLaserCartThumbFromDraftItem failed:", e);
      return "";
    }
  }

  async function uploadLaserCartThumbsForState(state, startIndex = 0) {
    if (!state || !Array.isArray(state.laser) || !state.laser.length) return startIndex;
    let index = Number(startIndex || 0) || 0;
    for (const item of state.laser) {
      index += 1;
      await uploadLaserCartThumbFromDraftItem(item, index);
    }
    return index;
  }

  async function uploadLaserCartThumbsForEditor(editorInstance) {
    const ed = editorInstance || getPamelaEditorInstance();
    let index = 0;

    try {
      const sides = window.PAMELA_SIDES || {};
      index = await uploadLaserCartThumbsForState(sides.front?.draft_state, index);
      index = await uploadLaserCartThumbsForState(sides.back?.draft_state, index);
    } catch (_) {}

    try {
      const nodes = getLaserArtworkNodes(ed);
      for (const node of nodes) {
        index += 1;
        await uploadLaserCartThumbFromNode(node, index);
      }
    } catch (_) {}

    return true;
  }

  async function uploadProcessedLasersInManifest(manifest) {
    if (!manifest || !Array.isArray(manifest.images) || !manifest.images.length) {
      return manifest;
    }

    let laserIndex = 0;
    for (const im of manifest.images) {
      if (!im || String(im.kind || "") !== "laser") continue;
      laserIndex += 1;
      const processedDataUrl = String(im.processedDataUrl || "").trim();
      if (!processedDataUrl) continue;

      const processedUrl = await uploadProcessedLaserPng(processedDataUrl, laserIndex);
      if (processedUrl) {
        im.original_src = im.original_src || im.src || "";
        im.processed_src = processedUrl;
        im.src = processedUrl;
      }

      delete im.processedDataUrl;
    }

    return manifest;
  }

  window.CM_uploadCroppedPortraitPng = uploadCroppedPortraitPng;
  window.CM_uploadCartDesignThumbPng = uploadCartDesignThumbPng;
  window.CM_uploadCroppedPortraitsInManifest = uploadCroppedPortraitsInManifest;
  window.CM_uploadProcessedLaserPng = uploadProcessedLaserPng;
  window.CM_uploadProcessedLasersInManifest = uploadProcessedLasersInManifest;
  window.CM_uploadLaserCartThumbsForEditor = uploadLaserCartThumbsForEditor;
  window.CM_collectPortraitAssetsFromStage = collectPortraitAssetsFromStage;
  window.CM_injectPortraitsIntoManifest = injectPortraitsIntoManifest;
  window.CM_addPortraitProps = addPortraitProps;

})();

window.loadAddonCatalogOnce = async function () {
  if (PAMELA_ADDON_CATALOG.loaded) return PAMELA_ADDON_CATALOG;

  const CFG = window.PAMELA_ADDON_CONFIG;
  if (!CFG) throw new Error("pamela-addon-config.js not loaded.");

  const [backP, backWordP, vaseP, porcelainP, panelP, panelUpgradeP, clipartP, laserSpotP, laserSceneP, laserPortraitP] = await Promise.all([
    fetchProductByHandle(CFG.handles.back),
    fetchProductByHandle(CFG.handles.back_word),
    fetchProductByHandle(CFG.handles.vase),
    fetchProductByHandle(CFG.handles.porcelain),
    fetchProductByHandle(CFG.handles.panel),
    fetchProductByHandleOrTitle(CFG.handles.panel_upgrade, "Back Engraved Panel (Upgrade)"),
    fetchProductByHandle(CFG.handles.clipart),
    fetchProductByHandle(CFG.handles.laser_spot),
    fetchProductByHandle(CFG.handles.laser_scene),
    fetchProductByHandle(CFG.handles.laser_portrait),
  ]);

  const sku = CFG.skuRoles;

  const backSingle = findVariantBySku(backP, sku.back_single);
  const backCompanion = findVariantBySku(backP, sku.back_companion);
  const backWord = findVariantBySku(backWordP, sku.back_word);

  const vaseFront = findVariantBySku(vaseP, sku.vase_front);
  const vaseBack = findVariantBySku(vaseP, sku.vase_back);

  const panel = firstVariant(panelP);
  const panelUpgrade = firstVariant(panelUpgradeP);
  const clipart = firstVariant(clipartP);
  const laserSpotLibraryMin = findVariantBySku(laserSpotP, sku.laser_spot_library_min);
  const laserSpotCustomMin = findVariantBySku(laserSpotP, sku.laser_spot_custom_min);
  const laserSpotExtraArea = findVariantBySku(laserSpotP, sku.laser_spot_extra_area);
  const laserSceneLibraryMin = findVariantBySku(laserSceneP, sku.laser_scene_library_min);
  const laserSceneCustomMin = findVariantBySku(laserSceneP, sku.laser_scene_custom_min);
  const laserSceneExtraArea = findVariantBySku(laserSceneP, sku.laser_scene_extra_area);
  const laserPortraitMin = findVariantBySku(laserPortraitP, sku.laser_portrait_min);
  const laserPortraitExtraArea = findVariantBySku(laserPortraitP, sku.laser_portrait_extra_area);
  const laserAdditionalFace = findVariantBySku(laserPortraitP, sku.laser_additional_face);
  let imageEnhancement = null;
  try {
    const imageEnhancementProduct = await fetchProductByHandleOrTitle(
      CFG.handles.image_enhancement,
      "Photo Editing Service"
    );
    imageEnhancement =
      findVariantBySku(imageEnhancementProduct, sku.image_enhancement) ||
      firstVariant(imageEnhancementProduct);
  } catch (_) {
    imageEnhancement = null;
  }

  if (!backSingle || !backCompanion) {
    throw new Error("Back Engraving SKUs not set correctly (single/companion).");
  }
  if (!backWord) {
    throw new Error("Back Additional Word SKU not set correctly.");
  }
  if (!vaseFront || !vaseBack) {
    throw new Error("Vase SKUs not set correctly (front/back).");
  }
  if (!panel) {
    throw new Error("Back Engraved Panel product has no variant.");
  }
  if (!clipart) {
    throw new Error("Back Decorative Art product has no variant.");
  }
  if (!laserSpotLibraryMin || !laserSpotCustomMin || !laserSpotExtraArea) {
    throw new Error("Laser Spot SKUs not set correctly.");
  }
  if (!laserSceneLibraryMin || !laserSceneCustomMin || !laserSceneExtraArea) {
    throw new Error("Laser Scene SKUs not set correctly.");
  }
  if (!laserPortraitMin || !laserPortraitExtraArea || !laserAdditionalFace) {
    throw new Error("Laser Portrait SKUs not set correctly.");
  }

  PAMELA_ADDON_CATALOG.backSingle = backSingle;
  PAMELA_ADDON_CATALOG.backCompanion = backCompanion;
  PAMELA_ADDON_CATALOG.backWord = backWord;
  PAMELA_ADDON_CATALOG.vaseFront = vaseFront;
  PAMELA_ADDON_CATALOG.vaseBack = vaseBack;
  PAMELA_ADDON_CATALOG.panel = panel;
  PAMELA_ADDON_CATALOG.panelUpgrade = panelUpgrade;
  PAMELA_ADDON_CATALOG.clipart = clipart;
  PAMELA_ADDON_CATALOG.laser = {
    spotLibraryMin: laserSpotLibraryMin,
    spotCustomMin: laserSpotCustomMin,
    spotExtraArea: laserSpotExtraArea,
    sceneLibraryMin: laserSceneLibraryMin,
    sceneCustomMin: laserSceneCustomMin,
    sceneExtraArea: laserSceneExtraArea,
    portraitMin: laserPortraitMin,
    portraitExtraArea: laserPortraitExtraArea,
    additionalFace: laserAdditionalFace
  };
  PAMELA_ADDON_CATALOG.imageEnhancement = imageEnhancement;

  // Porcelain: list all variants automatically (uses real Shopify prices)
  PAMELA_ADDON_CATALOG.porcelainVariants = (porcelainP.variants || []).map(v => ({
    id: Number(v.id),
    title: v.public_title || v.title || "Portrait",
    price_cents: Number(v.price || 0)
  }));

  PAMELA_ADDON_CATALOG.loaded = true;
  return PAMELA_ADDON_CATALOG;
};

// ============================================================
// LIVE TOTAL PRICE BOX
// - Base monument variant price
// - Back lettering minimum + extra words
// - Back panels (back only)
// - Back clipart (back only)
// - Portraits (all placements)
// - Vase engraving
// ============================================================

(function PamelaLiveTotalBox() {
  if (window.__PAMELA_LIVE_TOTAL_BOX_ON__) return;
  window.__PAMELA_LIVE_TOTAL_BOX_ON__ = true;

  const PRODUCT_CACHE = new Map();
  let lastSignature = "";
  let lastTotalCents = null;
  let lastAddonPricingResult = null;
  let cartEditPricingSeedPromise = null;
  let toastTimer = null;
  let nextPriceToastAnchorMode = "";

  function safeNum(v, d = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  }

  function getEditor() {
    return window.pamelaMonumentEditor || window.pamelaEditor || null;
  }

  function getUrlParams() {
    return new URLSearchParams(window.location.search);
  }

  async function fetchProductCached(handle) {
    const h = String(handle || "").trim();
    if (!h) return null;
    if (PRODUCT_CACHE.has(h)) return PRODUCT_CACHE.get(h);
    const p = fetchProductByHandle(h).catch((e) => {
      console.warn("[LiveTotal] Could not load product handle:", h, e);
      return null;
    });
    PRODUCT_CACHE.set(h, p);
    return p;
  }

  async function getBaseVariantInfo() {
    const params = getUrlParams();
    const handle = String(params.get("product") || "").trim();
    const variantId = safeNum(params.get("variant"), 0);

    // Fallback if URL doesn't have it
    const selectEl = document.getElementById("pamela-variant-select");
    const variantIdFallback = safeNum(selectEl?.value, 0);

    const finalVariantId = variantId || variantIdFallback;
    if (!handle || !finalVariantId) {
      return {
        handle,
        variantId: finalVariantId,
        title: "Monument",
        price_cents: 0
      };
    }

    const product = await fetchProductCached(handle);
    const variant = (product?.variants || []).find(v => Number(v.id) === Number(finalVariantId));

    return {
      handle,
      variantId: finalVariantId,
      title: variant?.public_title || variant?.title || product?.title || "Monument",
      price_cents: safeNum(variant?.price, 0)
    };
  }

function ensureTotalBox() {
  let box = document.getElementById("pamela-live-total-box");

  const stageWrap = document.getElementById("pamela-stageWrap");
  const toolbar = document.getElementById("pamela-editor-toolbar");
  const host =
    stageWrap?.parentNode ||
    stageWrap ||
    document.body;

  if (host && host.style) {
    host.style.position = "relative";
  }

  if (!box) {
    box = document.createElement("div");
    box.id = "pamela-live-total-box";
    box.style.cssText =
      "position:absolute;top:12px;left:0;z-index:20;" +
      "padding:10px 12px;border:1px solid #111;border-radius:10px;background:#fff;" +
      "display:grid;gap:6px;min-width:150px;box-shadow:none;" +
      "opacity:0;transform:translateY(8px);transition:opacity .22s ease, transform .22s ease;" +
      "text-align:center;";

    box.style.display = "none";
    box.innerHTML = `
      <div id="pamela-live-total-pricewrap" style="display:grid;gap:6px;">
        <div id="pamela-live-total-label" style="font:800 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#444;letter-spacing:.04em;text-transform:uppercase;">
          Price with Additions
        </div>
        <div id="pamela-live-total-amount" style="font:950 18px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;">
          $0.00
        </div>
      </div>
      <button id="pamela-live-total-finalize" type="button" style="padding:12px 14px;border-radius:10px;border:1px solid #111;background:#111;color:#fff;font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;display:block;width:100%;">
        Finalize & Add to Cart
      </button>
      <div id="pamela-live-total-status" style="font:800 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#444;display:none;"></div>
    `;

    host.appendChild(box);
  }

// Align box to toolbar (LEFT + directly BELOW)
try {
  if (toolbar && host) {
    const toolbarRect = toolbar.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();

    const left = Math.round(toolbarRect.left - hostRect.left);
    const top  = Math.round(toolbarRect.bottom - hostRect.top);

    box.style.left = `${Math.max(0, left)}px`;
    box.style.top  = `${Math.max(0, top)}px`;
  }
} catch (_) {}

  return box;
}
    function renderBreakdownRows(lines, symbol) {
      return;
    }

  function countWords(text) {
    return String(text || "")
      .replace(/\n/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .length;
  }

  function getDraftStateForPricing(side, ed) {
    try {
      const current = String(window.PAMELA_SIDES?.current || "front");
      if (current === side && ed && typeof window.CM_captureEditorNativeState === "function") {
        return window.CM_captureEditorNativeState(ed) || null;
      }
    } catch (_) {}

    try {
      const st = window.PAMELA_SIDES?.[side]?.draft_state || null;
      if (st) return st;
    } catch (_) {}

    return null;
  }

  function getBackTextPayload(ed) {
    const backState = getDraftStateForPricing("back", ed);
    if (backState && Array.isArray(backState.texts)) {
      const texts = backState.texts || [];
      const letteringTexts = texts.filter((t) => {
        try {
          const txt = String(t?.text || "").trim();
          return txt.length > 0;
        } catch (_) {
          return false;
        }
      });

      const hasLettering = letteringTexts.length > 0;
      const inscriptionWords = letteringTexts.reduce((sum, t) => {
        try {
          const nm = String(t?.name || "");
          const id = String(t?.id || "");
          const attrs = (t && t.attrs && typeof t.attrs === "object") ? t.attrs : {};
          const fieldType = String(
            t?.pamelaFieldType ||
            t?.fieldType ||
            t?.type ||
            attrs.pamelaFieldType ||
            attrs.fieldType ||
            attrs.type ||
            ""
          ).trim();
          if (nm === "__cm_back_lastname" || id === "__cm_back_lastname" || fieldType === "lastname") return sum;
          return sum + countWords(t?.text || "");
        } catch (_) {
          return sum;
        }
      }, 0);

      return { hasLettering, inscriptionWords };
    }

    const backContent = ed?.layers?.sides?.back?.content;
    if (!backContent?.find) {
      return {
        hasLettering: false,
        inscriptionWords: 0
      };
    }

    let texts = [];
    try {
      texts = backContent.find("Text") || [];
    } catch (_) {
      texts = [];
    }

    const letteringTexts = texts.filter((n) => {
      try {
        if (!n) return false;
        const txt = String(n.text?.() || "").trim();
        return txt.length > 0;
      } catch (_) {
        return !!n;
      }
    });

    const hasLettering = letteringTexts.length > 0;

    // Exclude back last name from word count
    const inscriptionTexts = letteringTexts.filter((n) => {
      try {
        const nm = String(n.name?.() || "");
        const id = String(n.id?.() || "");
        return nm !== "__cm_back_lastname" && id !== "__cm_back_lastname";
      } catch (_) {
        return true;
      }
    });

    const inscriptionWords = inscriptionTexts.reduce((sum, n) => {
      try {
        return sum + countWords(n.text?.() || "");
      } catch (_) {
        return sum;
      }
    }, 0);

    return { hasLettering, inscriptionWords };
  }

  function getBackPanelCount(ed) {
    const backState = getDraftStateForPricing("back", ed);
    if (backState && Array.isArray(backState.panels)) return backState.panels.length;

    const layer = ed?.layers?.sides?.back?.panels;
    if (!layer?.getChildren) return 0;

    const kids = layer.getChildren() || [];
    return kids.filter((n) => {
      try {
        return n?.getAttr?.("pamelaPanel") === true || String(n?.name?.() || "") === "pamela-panel";
      } catch (_) {
        return false;
      }
    }).length;
  }

  function isPanelsCategoryClipart(item) {
    try {
      const attr = (key) => {
        if (item?.getAttr) return item.getAttr(key);
        const attrs = item?.attrs || {};
        return attrs[key] ?? item?.[key];
      };

      if (attr("pamelaClipartLibraryPanel") === true) return true;
      const categoryId = String(attr("pamelaClipartCategoryId") || attr("categoryId") || "").trim().toLowerCase();
      const category = String(attr("pamelaClipartCategory") || attr("category") || "").trim().toLowerCase();
      return categoryId === "panels" || category === "panels";
    } catch (_) {
      return false;
    }
  }

  function getBackPanelUpgradeCount(ed) {
    const backState = getDraftStateForPricing("back", ed);
    if (backState && Array.isArray(backState.clipart)) {
      return backState.clipart.filter(isPanelsCategoryClipart).length;
    }

    const layer = ed?.layers?.sides?.back?.content;
    if (!layer?.getChildren) return 0;

    const kids = layer.getChildren() || [];
    return kids.filter((n) => {
      try {
        if (n?.className !== "Image") return false;
        if (!(n?.getAttr?.("pamelaClipart") === true || String(n?.name?.() || "") === "pamela-clipart")) return false;
        return isPanelsCategoryClipart(n);
      } catch (_) {
        return false;
      }
    }).length;
  }

  function getBackClipartCount(ed) {
    const backState = getDraftStateForPricing("back", ed);
    if (backState && Array.isArray(backState.clipart)) {
      return backState.clipart.filter((it) => !isPanelsCategoryClipart(it)).length;
    }

    const layer = ed?.layers?.sides?.back?.content;
    if (!layer?.getChildren) return 0;

    const kids = layer.getChildren() || [];
    return kids.filter((n) => {
      try {
        if (n?.className !== "Image") return false;
        if (!(n?.getAttr?.("pamelaClipart") === true || String(n?.name?.() || "") === "pamela-clipart")) return false;
        return !isPanelsCategoryClipart(n);
      } catch (_) {
        return false;
      }
    }).length;
  }

  function getVaseState() {
    const selects = [...document.querySelectorAll("select")];
    const vaseSel = selects.find((sel) => {
      const vals = [...sel.options].map(o => String(o.value || "").toLowerCase());
      return vals.includes("front") && vals.includes("back") && vals.includes("both");
    });

    const numberInputs = [...document.querySelectorAll('input[type="number"]')];
    const vaseQtyInput = numberInputs.find((inp) => {
      const v = String(inp.value || "").trim();
      return inp.min === "1" && inp.step === "1" && v !== "";
    });

    return {
      mode: String(vaseSel?.value || "").toLowerCase(),
      qty: Math.max(1, safeNum(vaseQtyInput?.value, 1))
    };
  }

  function getLaserStateForPricing() {
    try {
      const ui = window.__PAMELA_ADDON_UI__ || {};
      if (typeof ui.getLaser === "function") return ui.getLaser();
    } catch (_) {}
    return { enabled: false, kind: "spot", source: "library", widthIn: 0, heightIn: 0, faces: 1 };
  }

function getPortraitVariantIdFromNode(node) {
  if (!node?.getAttr) return 0;

  const keys = [
    "__porcelainVariantId",
    "__portraitVariantId",
    "pamelaPortraitVariantId",
    "portraitVariantId",
    "porcelainVariantId",
    "variantId"
  ];

  for (const k of keys) {
    const raw = node.getAttr(k);
    const v = safeNum(raw, 0);
    if (v) return v;
  }

  return 0;
}

  function findPortraitWrappers(stage) {
    if (!stage?.find) return [];

    let groups = [];
    try {
      groups = stage.find("Group") || [];
    } catch (_) {
      groups = [];
    }

    return groups.filter((g) => {
      try {
        return (
          g?.getAttr?.("__isPortraitGroup") === true ||
          g?.getAttr?.("pamelaType") === "portrait" ||
          g?.getAttr?.("pamelaPortrait") === true ||
          g?.getAttr?.("__pamelaKind") === "portrait" ||
          String(g?.name?.() || "").includes("pamela-portrait-group")
        );
      } catch (_) {
        return false;
      }
    });
  }

  function getPortraitsState(ed, catalog) {
    const frontState = getDraftStateForPricing("front", ed);
    const backState = getDraftStateForPricing("back", ed);
    const stage = ed?.stage || ed?.konvaStage;
    const porcelainById = new Map(
      (catalog?.porcelainVariants || []).map(v => [Number(v.id), v])
    );

    // Fallback if wrappers do not store variant id:
    const portraitSelects = [...document.querySelectorAll("select")];
    const fallbackPortraitVariantId = (() => {
      for (const sel of portraitSelects) {
        const val = safeNum(sel.value, 0);
        if (val && porcelainById.has(val)) return val;
      }
      return 0;
    })();

    const collectItemsFromDraftState = (state) => {
      if (!state || !Array.isArray(state.portraits)) return [];
      return state.portraits.map((p) => {
        const variantId = Number(
          p?.variant_id ??
          p?.variantId ??
          p?.meta?.variant_id ??
          p?.meta?.variantId ??
          0
        ) || fallbackPortraitVariantId;
        const variant = porcelainById.get(Number(variantId)) || null;
        const savedTitle = String(
          p?.title ??
          p?.meta?.title ??
          p?.meta?.label ??
          p?.__porcelainLabel ??
          ""
        ).trim();
        const savedPriceCents = safeNum(
          p?.price_cents ??
          p?.priceCents ??
          p?.meta?.price_cents ??
          p?.meta?.priceCents ??
          0,
          0
        );

        return {
          variant_id: Number(variantId || 0),
          title: variant?.title || savedTitle || "Portrait",
          price_cents: safeNum(variant?.price_cents, savedPriceCents)
        };
      });
    };

    let items = [];
    const wrappers = findPortraitWrappers(stage).filter((w) => {
      try {
        const hasPhoto = !!(
          w?.getAttr?.("__portraitHasPhoto") === true ||
          w?.findOne?.(".pamela-portrait-photo")
        );
        return hasPhoto;
      } catch (_) {
        return false;
      }
    });

    if (wrappers.length) {
      const seen = new Set();
      items = wrappers.map((w) => {
        const variantId = getPortraitVariantIdFromNode(w) || fallbackPortraitVariantId;
        const variant = porcelainById.get(Number(variantId)) || null;
        const savedTitle = String(
          w?.getAttr?.("__portraitVariantTitle") ??
          w?.getAttr?.("__porcelainLabel") ??
          w?.attrs?.__portraitVariantTitle ??
          w?.attrs?.__porcelainLabel ??
          ""
        ).trim();
        const savedPriceCents = safeNum(
          w?.getAttr?.("__portraitVariantPriceCents") ??
          w?.attrs?.__portraitVariantPriceCents ??
          0,
          0
        );

        const side = (() => {
          try {
            const layer = w?.getLayer?.();
            if (layer && layer === ed?.layers?.sides?.back?.portraits) return "back";
            return "front";
          } catch (_) {
            return "front";
          }
        })();
        const slot = Number(w?.getAttr?.("pamelaPortraitSlot") ?? w?.getAttr?.("__portraitSlot") ?? 0) || 0;
        const key = `${side}|${slot}|${Number(variantId || 0)}`;
        if (seen.has(key)) return null;
        seen.add(key);

        return {
          variant_id: Number(variantId || 0),
          title: variant?.title || savedTitle || "Portrait",
          price_cents: safeNum(variant?.price_cents, savedPriceCents)
        };
      }).filter(Boolean);
    } else if (frontState || backState) {
      items = [
        ...collectItemsFromDraftState(frontState),
        ...collectItemsFromDraftState(backState)
      ];
    }

    return {
      count: items.length,
      items
    };
  }

  function getLayoutKind() {
    const params = getUrlParams();
    const rawLayout = String(params.get("layout") || "").toLowerCase().trim();
    return rawLayout === "single" ? "single" : "companion";
  }

  function buildSignature(snapshot) {
    try {
      return JSON.stringify(snapshot);
    } catch (_) {
      return String(Date.now());
    }
  }

  function priceCentsFromCartItem(item) {
    try {
      const qty = Math.max(1, Number(item?.quantity || 1) || 1);
      const candidates = [
        item?.final_line_price,
        item?.line_price,
        item?.original_line_price
      ];
      for (const raw of candidates) {
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) return n;
      }
      const unit = Number(item?.final_price ?? item?.price ?? 0);
      return Number.isFinite(unit) ? unit * qty : 0;
    } catch (_) {
      return 0;
    }
  }

  async function getCartEditPricingSeed() {
    if (cartEditPricingSeedPromise) return cartEditPricingSeedPromise;

    cartEditPricingSeedPromise = (async () => {
      try {
        const ctx = typeof getCartEditContext === "function"
          ? getCartEditContext()
          : { enabled: false, replaceDesignId: "" };
        const designId = String(ctx?.replaceDesignId || "").trim();
        if (!ctx?.enabled || !designId) return null;

        const res = await fetch("/cart.js", {
          credentials: "same-origin",
          headers: { Accept: "application/json" }
        });
        if (!res.ok) return null;
        const cart = await res.json();
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const main = items.find((item) => String(item?.properties?._cm_design_id || "").trim() === designId) || null;
        if (!main) return null;

        const addons = items.filter((item) => String(item?.properties?._cm_parent_design_id || "").trim() === designId);
        const baseCents = priceCentsFromCartItem(main);
        const addonCents = addons.reduce((sum, item) => sum + priceCentsFromCartItem(item), 0);
        if (!(baseCents > 0) || !(addonCents > 0)) return null;

        return {
          currencySymbol: window.PAMELA_ADDON_CONFIG?.currencySymbol || "$",
          base_cents: baseCents,
          addon_cents: addonCents,
          total_cents: baseCents + addonCents
        };
      } catch (_) {
        return null;
      }
    })();

    return cartEditPricingSeedPromise;
  }

  function pricingResultFromCartSeed(result, seed) {
    if (!seed || !(Number(seed.total_cents || 0) > Number(seed.base_cents || 0))) return result;
    return {
      currencySymbol: seed.currencySymbol || result.currencySymbol,
      total_cents: Number(seed.total_cents || 0),
      lines: [
        {
          key: "base",
          label: "Monument",
          amount_cents: Number(seed.base_cents || 0)
        },
        {
          key: "cart_saved_addons",
          label: "Saved design add-ons",
          amount_cents: Number(seed.addon_cents || 0)
        }
      ]
    };
  }

  function draftStatesHavePricedAddons() {
    try {
      const states = [
        { side: "front", state: window.PAMELA_SIDES?.front?.draft_state },
        { side: "back", state: window.PAMELA_SIDES?.back?.draft_state }
      ].filter((entry) => entry.state);

      return states.some((entry) => {
        const state = entry.state;
        const texts = Array.isArray(state?.texts) ? state.texts : [];
        const panels = Array.isArray(state?.panels) ? state.panels : [];
        const clipart = Array.isArray(state?.clipart) ? state.clipart : [];
        const portraits = Array.isArray(state?.portraits) ? state.portraits : [];
        const laser = Array.isArray(state?.laser) ? state.laser : [];

        const hasText = entry.side === "back" && texts.some((t) => String(t?.text || "").trim());
        return hasText || panels.length > 0 || clipart.length > 0 || portraits.length > 0 || laser.length > 0;
      });
    } catch (_) {
      return false;
    }
  }

  async function buildSnapshot() {
    const catalog = await window.loadAddonCatalogOnce();
    const ed = getEditor();

    const base = await getBaseVariantInfo();
    const layout = getLayoutKind();
    const backText = getBackTextPayload(ed);
    const backPanelCount = getBackPanelCount(ed);
    const backPanelUpgradeCount = getBackPanelUpgradeCount(ed);
    const backClipartCount = getBackClipartCount(ed);
    const vase = getVaseState();
    const laser = getLaserStateForPricing();
    const portraits = getPortraitsState(ed, catalog);

    return {
      currencySymbol: window.PAMELA_ADDON_CONFIG?.currencySymbol || "$",
      layout,
      base,
      backText,
      backPanelCount,
      backPanelUpgradeCount,
      backClipartCount,
      vase,
      laser,
      portraits,
      catalog
    };
  }

  function calculatePricing(snapshot) {
    const cfg = window.PAMELA_ADDON_CONFIG || {};
    const includedWords = cfg.includedWords || { single: 10, companion: 20 };
    const lines = [];

    // Base monument
    lines.push({
      key: "base",
      label: "Monument",
      amount_cents: safeNum(snapshot.base?.price_cents, 0)
    });

    // Back lettering minimum
    if (snapshot.backText.hasLettering) {
      const minVariant =
        snapshot.layout === "single"
          ? snapshot.catalog.backSingle
          : snapshot.catalog.backCompanion;

      lines.push({
        key: "back_min",
        label:
          snapshot.layout === "single"
            ? "Back lettering (single minimum)"
            : "Back lettering (companion minimum)",
        amount_cents: safeNum(minVariant?.price, 0)
      });

      const included = safeNum(includedWords[snapshot.layout], 0);
      const extraWords = Math.max(0, safeNum(snapshot.backText.inscriptionWords, 0) - included);

      if (extraWords > 0) {
        lines.push({
          key: "back_words",
          label: `Additional back words (${extraWords})`,
          amount_cents: safeNum(snapshot.catalog.backWord?.price, 0) * extraWords
        });
      }
    }

    // Back panels
    if (snapshot.backPanelUpgradeCount > 0) {
      const upgradedPanelCount = safeNum(snapshot.backPanelCount, 0) + safeNum(snapshot.backPanelUpgradeCount, 0);
      const panelUpgradePrice = safeNum(snapshot.catalog.panelUpgrade?.price, 0);
      const panelPrice = safeNum(snapshot.catalog.panel?.price, 0);
      lines.push({
        key: "back_panels",
        label: panelUpgradePrice > 0
          ? `Back panels (${upgradedPanelCount})`
          : `Back panels (${upgradedPanelCount}) - upgrade product price missing`,
        amount_cents: (panelUpgradePrice || panelPrice) * upgradedPanelCount
      });
    } else if (snapshot.backPanelCount > 0) {
      lines.push({
        key: "back_panels",
        label: `Back panels (${snapshot.backPanelCount})`,
        amount_cents: safeNum(snapshot.catalog.panel?.price, 0) * snapshot.backPanelCount
      });
    }

    // Back clipart
    if (snapshot.backClipartCount > 0) {
      lines.push({
        key: "back_clipart",
        label: `Back decorative art (${snapshot.backClipartCount})`,
        amount_cents: safeNum(snapshot.catalog.clipart?.price, 0) * snapshot.backClipartCount
      });
    }

    // Portraits (all placements)
    if (snapshot.portraits.count > 0) {
      const grouped = new Map();

      snapshot.portraits.items.forEach((it) => {
        const k = `${it.variant_id || 0}||${it.title || "Portrait"}||${it.price_cents || 0}`;
        grouped.set(k, (grouped.get(k) || 0) + 1);
      });

      grouped.forEach((qty, key) => {
        const parts = key.split("||");
        const title = parts[1] || "Portrait";
        const price_cents = safeNum(parts[2], 0);

        lines.push({
          key: `portrait_${key}`,
          label: `${title} (${qty})`,
          amount_cents: price_cents * qty
        });
      });

    }

    // Vase engraving
    if (snapshot.vase.mode === "front") {
      lines.push({
        key: "vase_front",
        label: `Vase engraving front (${snapshot.vase.qty})`,
        amount_cents: safeNum(snapshot.catalog.vaseFront?.price, 0) * snapshot.vase.qty
      });
    } else if (snapshot.vase.mode === "back") {
      lines.push({
        key: "vase_back",
        label: `Vase engraving back (${snapshot.vase.qty})`,
        amount_cents: safeNum(snapshot.catalog.vaseBack?.price, 0) * snapshot.vase.qty
      });
    } else if (snapshot.vase.mode === "both") {
      lines.push({
        key: "vase_both",
        label: `Vase engraving front + back (${snapshot.vase.qty})`,
        amount_cents:
          (safeNum(snapshot.catalog.vaseFront?.price, 0) + safeNum(snapshot.catalog.vaseBack?.price, 0)) *
          snapshot.vase.qty
      });
    }

    // Laser etching
    const laserItems = Array.isArray(snapshot.laser?.items) ? snapshot.laser.items : [];
    if (snapshot.laser?.enabled && laserItems.length) {
      laserItems.forEach((laserItem, idx) => {
        const plan = laserItem.plan || getLaserPricePlan(laserItem, snapshot.catalog);
        if (!(plan.widthIn > 0 && plan.heightIn > 0)) return;
        const kindLabel =
          plan.effectiveKind === "portrait"
            ? "Portrait"
            : (plan.effectiveKind === "scene" ? "Scene" : "Spot");
        const sourceLabel = plan.source === "custom" ? "custom upload" : "library";
        const minCents = safeNum(plan.minVariant?.price, 0) || Math.round(Number(plan.fallbackMinDollars || 0) * 100);
        const extraCents = safeNum(plan.extraVariant?.price, 0) || Math.round(Number(plan.fallbackRateDollars || 0) * 100);
        const faceCents = safeNum(plan.faceVariant?.price, 0) || Math.round(Number(plan.fallbackFaceDollars || 0) * 100);

        lines.push({
          key: `laser_min_${idx}`,
          label: minCents > 0
            ? `Laser ${kindLabel} (${sourceLabel}) #${idx + 1}`
            : `Laser ${kindLabel} (${sourceLabel}) #${idx + 1} - minimum product price missing`,
          amount_cents: minCents
        });

        if (plan.extraArea > 0) {
          lines.push({
            key: `laser_extra_area_${idx}`,
            label: extraCents > 0
              ? `Laser #${idx + 1} extra area (${plan.extraArea} sq in)`
              : `Laser #${idx + 1} extra area (${plan.extraArea} sq in) - extra area product price missing`,
            amount_cents: extraCents * plan.extraArea
          });
        }

        if (plan.additionalFaces > 0) {
          lines.push({
            key: `laser_faces_${idx}`,
            label: faceCents > 0
              ? `Laser #${idx + 1} additional people (${plan.additionalFaces})`
              : `Laser #${idx + 1} additional people (${plan.additionalFaces}) - people product price missing`,
            amount_cents: faceCents * plan.additionalFaces
          });
        }

        const enhancementRequested =
          getLaserEntryAttr(laserItem, "pamelaImageEnhancementRequested", false) === true ||
          String(getLaserEntryAttr(laserItem, "pamelaImageEnhancementRequested", "")).toLowerCase() === "true";
        if (enhancementRequested) {
          const enhancementCents = safeNum(snapshot.catalog.imageEnhancement?.price, 0) || 2500;
          lines.push({
            key: `image_enhancement_${idx}`,
            label: `Image enhancement #${idx + 1}`,
            amount_cents: enhancementCents
          });
        }
      });
    }

    const total_cents = lines.reduce((sum, line) => sum + safeNum(line.amount_cents, 0), 0);

    return {
      currencySymbol: snapshot.currencySymbol,
      lines,
      total_cents
    };
  }

      function renderPricing(result) {
        const box = ensureTotalBox();
        if (window.__PAMELA_SIDE_SWITCHING__) return;

        const topPriceEl = document.getElementById("pamela-variant-price");
        if (topPriceEl) {
          const newTopPriceText = moneyFromCents(result.total_cents, result.currencySymbol);
          if (topPriceEl.textContent !== newTopPriceText) {
            topPriceEl.textContent = newTopPriceText;
            topPriceEl.style.transform = "scale(1.08)";
            topPriceEl.style.transition = "transform .18s ease";
            setTimeout(() => {
              topPriceEl.style.transform = "scale(1)";
            }, 180);
          }
        }

        const amountEl = document.getElementById("pamela-live-total-amount");
        if (amountEl) {
          const newText = moneyFromCents(result.total_cents, result.currencySymbol);

      if (amountEl.textContent !== newText) {
        amountEl.textContent = newText;

        amountEl.style.transform = "scale(1.12)";
        amountEl.style.transition = "transform .18s ease";

        setTimeout(() => {
          amountEl.style.transform = "scale(1)";
        }, 180);
      }
        }

        const priceWrap = document.getElementById("pamela-live-total-pricewrap");
        const floatingBtn = document.getElementById("pamela-live-total-finalize");
        const topBtn = document.getElementById("pamela-finalize-addtocart");

        const baseLine = (result.lines || []).find((line) => line.key === "base");
        const baseCents = Number(baseLine?.amount_cents || 0);
        const totalCents = Number(result.total_cents || 0);
        const hasAddons = totalCents > baseCents;
        const boxAnchorBefore = getLiveTotalToastAnchor(box);

        if (box) {
          box.style.display = "none";
          box.style.opacity = "0";
          box.style.transform = "translateY(8px)";
        }

        if (priceWrap) {
          priceWrap.style.display = "none";
        }

        if (floatingBtn) {
          floatingBtn.style.display = "none";
        }

        if (topBtn) {
          topBtn.style.display = "block";
        }

const boxAnchorAfter = getLiveTotalToastAnchor(box);
const variantAnchor = nextPriceToastAnchorMode === "variant" ? getVariantPriceToastAnchor() : null;
const topPriceAnchor = getVariantPriceToastAnchor();
nextPriceToastAnchorMode = "";
const suppressPriceToast = !!(
  window.__PAMELA_SUPPRESS_PRICE_TOASTS__ ||
  window.__PAMELA_SIDE_SWITCHING__ ||
  window.__PAMELA_RESTORING_SIDE_STATE__ ||
  window.__PAMELA_DRAFT_RESTORING__
);
if (suppressPriceToast) {
  lastTotalCents = result.total_cents;
} else if (lastTotalCents !== null && result.total_cents !== lastTotalCents) {
  const delta = result.total_cents - lastTotalCents;
  if (delta !== 0) {
    showPriceToast(
      delta,
      result.currencySymbol,
      variantAnchor || topPriceAnchor || (delta > 0 ? (boxAnchorAfter || boxAnchorBefore) : (boxAnchorBefore || boxAnchorAfter))
    );
  }
  lastTotalCents = result.total_cents;
} else {
  lastTotalCents = result.total_cents;
}
        renderBreakdownRows(result.lines, result.currencySymbol);
      }

function getLiveTotalToastAnchor(box) {
  if (!box) return null;
  return {
    left: box.offsetLeft + box.offsetWidth / 2,
    top: box.offsetTop + box.offsetHeight + 8
  };
}

function getVariantPriceToastAnchor() {
  const priceWrap =
    document.getElementById("pamela-topbar-price-wrap") ||
    document.getElementById("pamela-variant-price");
  const box = document.getElementById("pamela-live-total-box");
  const host =
    box?.parentNode ||
    document.getElementById("pamela-stageWrap")?.parentNode ||
    document.getElementById("pamela-stageWrap") ||
    document.body;

  if (!priceWrap || !host?.getBoundingClientRect) return null;

  const priceRect = priceWrap.getBoundingClientRect();
  const hostRect = host.getBoundingClientRect();
  return {
    left: priceRect.left - hostRect.left + priceRect.width / 2,
    top: priceRect.top - hostRect.top - 36
  };
}

      function showPriceToast(deltaCents, symbol, anchor) {
  let toast = document.getElementById("pamela-live-total-toast");
  const box = document.getElementById("pamela-live-total-box");
  const host =
    box?.parentNode ||
    document.getElementById("pamela-stageWrap")?.parentNode ||
    document.getElementById("pamela-stageWrap") ||
    document.body;

  if (host && host.style && !host.style.position) {
    host.style.position = "relative";
  }

  if (!toast) {
    toast = document.createElement("div");
    toast.id = "pamela-live-total-toast";

    toast.style.cssText =
      "position:absolute;left:0;top:0;z-index:21;" +
      "padding:8px 10px;border-radius:10px;background:#111;color:#fff;" +
      "font:800 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
      "opacity:0;transform:translateX(-50%) translateY(6px);transition:opacity .2s ease, transform .2s ease;" +
      "pointer-events:none;white-space:nowrap;";

    host.appendChild(toast);
  } else if (toast.parentNode !== host) {
    host.appendChild(toast);
  }

  const resolvedAnchor = anchor || getLiveTotalToastAnchor(box);
  if (resolvedAnchor) {
    toast.style.left = `${resolvedAnchor.left}px`;
    toast.style.top = `${resolvedAnchor.top}px`;
  }

  const sign = deltaCents >= 0 ? "+" : "-";
    toast.textContent = `${sign}${moneyFromCents(Math.abs(deltaCents), symbol)}`;

    if (deltaCents > 0) {
      toast.style.background = "#1f8b3a"; // green
    } else {
      toast.style.background = "#b42318"; // red
    }

  clearTimeout(toastTimer);
  toast.style.opacity = "1";
  toast.style.transform = "translateX(-50%) translateY(0)";

  toastTimer = setTimeout(() => {
    toast.style.opacity = "0";
    toast.style.transform = "translateX(-50%) translateY(6px)";
  }, 1200);
}

    async function refresh(options = {}) {
    try {
      const force = options === true || !!(options && options.force);
      if (window.__PAMELA_DRAFT_RESTORING__ && !force) return;
      const snapshot = await buildSnapshot();
      const signature = buildSignature(snapshot);
      if (!force && signature === lastSignature) return;

      lastSignature = signature;

      const result = calculatePricing(snapshot);
      const baseLine = (result.lines || []).find((line) => line.key === "base");
      const hasAddons = Number(result.total_cents || 0) > Number(baseLine?.amount_cents || 0);
      const renderResult = hasAddons ? result : result;

      if (Number(renderResult.total_cents || 0) > Number((renderResult.lines || []).find((line) => line.key === "base")?.amount_cents || 0)) {
        lastAddonPricingResult = renderResult;
      }
      renderPricing(renderResult);

      window.__PAMELA_LIVE_TOTAL_LAST__ = {
        snapshot,
        result: renderResult
      };
    } catch (e) {
      console.warn("[LiveTotal] refresh failed:", e);
    }
  }

  function scheduleRefresh(options = {}) {
    const target = options && options.target;
    if (
      (options && options.anchorMode === "variant") ||
      (target && target.id === "pamela-variant-select")
    ) {
      nextPriceToastAnchorMode = "variant";
    }
    clearTimeout(scheduleRefresh._t);
    scheduleRefresh._t = setTimeout(() => {
      refresh().catch(() => {});
    }, 16);
  }
  window.PAMELA_scheduleLiveTotalRefresh = scheduleRefresh;
  window.PAMELA_refreshLiveTotalAfterRestore = function () {
    try { refresh({ force: true }).catch(() => {}); } catch (_) {}
    try {
      requestAnimationFrame(() => {
        try { refresh({ force: true }).catch(() => {}); } catch (_) {}
        requestAnimationFrame(() => {
          try { refresh({ force: true }).catch(() => {}); } catch (_) {}
        });
      });
    } catch (_) {}
    [150, 450, 900, 1500].forEach((delay) => {
      try {
        setTimeout(() => {
          try { refresh({ force: true }).catch(() => {}); } catch (_) {}
        }, delay);
      } catch (_) {}
    });
  };

  function patchEditorMethods() {
    const ed = getEditor();
    if (!ed || ed.__pamelaLiveTotalPatched) return;
    ed.__pamelaLiveTotalPatched = true;

    const wrapAsync = (name) => {
      if (typeof ed[name] !== "function") return;
      const orig = ed[name];
      ed[name] = async function (...args) {
        const out = await orig.apply(this, args);
        scheduleRefresh();
        return out;
      };
    };

    wrapAsync("addClipartFromUrl");
    wrapAsync("addPanelFromUrl");

    const wrapSync = (name) => {
      if (typeof ed[name] !== "function") return;
      const orig = ed[name];
      ed[name] = function (...args) {
        const out = orig.apply(this, args);
        scheduleRefresh();
        return out;
      };
    };

    wrapSync("selectNode");
    wrapSync("_historyCommit");
  }

  function bindUiEvents() {
    const variantSelect = document.getElementById("pamela-variant-select");
    if (variantSelect && !variantSelect.__pamelaVariantToastBound) {
      variantSelect.__pamelaVariantToastBound = true;
      variantSelect.addEventListener("change", () => scheduleRefresh({ anchorMode: "variant" }));
    }
    document.addEventListener("change", scheduleRefresh, true);
    document.addEventListener("input", scheduleRefresh, true);
    document.addEventListener("click", scheduleRefresh, true);
  }

    function bootLiveTotal() {
      ensureTotalBox();
      bindUiEvents();
      patchEditorMethods();
      refresh().catch(() => {});

      const floatingBtn = document.getElementById("pamela-live-total-finalize");
      const topBtn = document.getElementById("pamela-finalize-addtocart");

      if (floatingBtn && topBtn && !floatingBtn.__pamelaBoundToTopBtn) {
        floatingBtn.__pamelaBoundToTopBtn = true;
        floatingBtn.addEventListener("click", () => {
          topBtn.click();
        });
      }

      // Safety net for deletions / undo / history rebuilds
      setInterval(() => {
        patchEditorMethods();
        refresh().catch(() => {});
      }, 700);
    }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootLiveTotal);
  } else {
    bootLiveTotal();
  }

  window.PAMELA_REFRESH_LIVE_TOTAL = scheduleRefresh;
})();

// -----------------------------
// EDITOR-NATIVE DRAFT SERIALIZER / RESTORER
// This is ONLY for editable save/resume.
// It does NOT replace production manifest logic.
// -----------------------------
function CM_clonePlain(obj) {
  try {
    return JSON.parse(JSON.stringify(obj ?? null));
  } catch (_) {
    return null;
  }
}

function CM_pickAttrs(node, keys) {
  const out = {};
  (keys || []).forEach((k) => {
    try {
      const v = typeof node.getAttr === "function" ? node.getAttr(k) : node?.attrs?.[k];
      if (v !== undefined) out[k] = v;
    } catch (_) {}
  });
  return out;
}

// Keep every serializable editor-owned attribute, not just the original
// hand-picked list. New behavior flags are intentionally written as pamela*
// or __cm* attributes, so this keeps a resumed design functionally identical
// as those features evolve (date formatting, laser overlap state, panel slots,
// clipart sizing, and similar state all round-trip automatically).
function CM_pickPersistentAttrs(node, keys) {
  const out = CM_pickAttrs(node, keys);
  const attrs = node?.attrs || {};

  Object.entries(attrs).forEach(([key, value]) => {
    if (!/^(pamela|__cm|__portrait|__isPortrait)/.test(String(key || ""))) return;
    const copy = CM_clonePlain(value);
    if (copy !== null || value === null) out[key] = copy;
  });

  return out;
}

function CM_safeNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function CM_safeStr(v, d = "") {
  return v == null ? d : String(v);
}

function CM_imageSourceToDataUrl(source, maxDim = 0) {
  try {
    if (!source) return "";
    const w = Number(source.naturalWidth || source.videoWidth || source.width || 0);
    const h = Number(source.naturalHeight || source.videoHeight || source.height || 0);
    if (!(w > 0) || !(h > 0)) return "";
    const maxSize = Math.max(0, Number(maxDim || 0) || 0);
    if (!maxSize && typeof source.toDataURL === "function") return source.toDataURL("image/png");
    const scale = maxSize > 0 ? Math.min(1, maxSize / Math.max(w, h)) : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } catch (_) {
    return "";
  }
}

function CM_getStage(editor) {
  return editor?.stage || null;
}

function CM_getDesignSize(editor) {
  return {
    width: editor?.state?.design?.width || DESIGN_PX.w,
    height: editor?.state?.design?.height || DESIGN_PX.h
  };
}

function CM_collectTextNodes(editor) {
  const layer = editor?.layers?.content;
  if (!layer?.getChildren) return [];

  return layer.getChildren((n) => n && pamelaIsTextLikeNode(n)).map((t) => ({
    kind: "text",
    textNodeType: t.className === "TextPath" ? "textPath" : "text",
    text: CM_safeStr(typeof t.text === "function" ? t.text() : ""),
    x: CM_safeNum(typeof t.x === "function" ? t.x() : 0),
    y: CM_safeNum(typeof t.y === "function" ? t.y() : 0),
    width: CM_safeNum(typeof t.width === "function" ? t.width() : 0),
    height: CM_safeNum(typeof t.height === "function" ? t.height() : 0),
    rotation: CM_safeNum(typeof t.rotation === "function" ? t.rotation() : 0),
    scaleX: CM_safeNum(typeof t.scaleX === "function" ? t.scaleX() : 1, 1),
    scaleY: CM_safeNum(typeof t.scaleY === "function" ? t.scaleY() : 1, 1),
    offsetX: CM_safeNum(typeof t.offsetX === "function" ? t.offsetX() : 0),
    offsetY: CM_safeNum(typeof t.offsetY === "function" ? t.offsetY() : 0),
    opacity: CM_safeNum(typeof t.opacity === "function" ? t.opacity() : 1, 1),
    visible: typeof t.visible === "function" ? !!t.visible() : true,

    fontFamily: CM_safeStr(typeof t.fontFamily === "function" ? t.fontFamily() : ""),
    fontSize: CM_safeNum(typeof t.fontSize === "function" ? t.fontSize() : 0),
    fill: CM_safeStr(typeof t.fill === "function" ? t.fill() : ""),
    align: CM_safeStr(typeof t.align === "function" ? t.align() : "center"),
    padding: CM_safeNum(typeof t.padding === "function" ? t.padding() : 0),
    lineHeight: CM_safeNum(typeof t.lineHeight === "function" ? t.lineHeight() : 1, 1),
    letterSpacing: CM_safeNum(typeof t.letterSpacing === "function" ? t.letterSpacing() : 0),

    attrs: CM_pickPersistentAttrs(t, [
      "pamelaUid",
      "pamelaFieldType",
      "pamelaSlotIndex",
      "pamelaIndependent",
      "pamelaIndependentArmed",
      "pamelaVariantDefaultFill",
      "__cmBackLastNameLayout",
      "__cmBackLastNameInitialized",
      "__cmBackLastNameSizeTouched",
      "pamelaTextPathApplied",
      "pamelaTextPathSourceUid",
      "pamelaTextPathKind",
      "pamelaTextPathData",
      "pamelaTextPathViewBox",
      "pamelaArcWidth",
      "pamelaArcStrength",
      "pamelaArcSlide",
      "stroke",
      "strokeWidth",
      "strokeEnabled",
      "lineJoin",
      "fillAfterStrokeEnabled",
      "shadowEnabled",
      "shadowColor",
      "shadowBlur",
      "shadowOpacity",
      "shadowOffsetX",
      "shadowOffsetY",
      "id",
      "name"
    ])
  }));
}

function CM_collectImageNodes(editor, which) {
  const layer =
    which === "panels"
      ? editor?.layers?.panels
      : editor?.layers?.content;

  if (!layer?.getChildren) return [];

  return layer.getChildren((n) => {
    if (!n || n.className !== "Image") return false;

    const isPanel = n.getAttr?.("pamelaPanel") === true;
    const isClipart = n.getAttr?.("pamelaClipart") === true;
    const isLaser = n.getAttr?.("pamelaLaserEtching") === true;

    if (which === "panels") return isPanel;
    if (which === "clipart") return isClipart;
    if (which === "laser") return isLaser;
    return false;
  }).map((n) => {
    const isLaserItem = which === "laser";
    const displayImage = isLaserItem && typeof n.image === "function" ? n.image() : null;
    const baseImage = isLaserItem ? (n._pamelaLaserBaseImage || null) : null;
    // The dirty-check hash poll only needs to detect that *something* changed
    // (position/size/crop attrs below already cover that) -- it never reads
    // these data URLs, so re-encoding a laser image to PNG 3x per node on
    // every poll tick (on top of doing it for BOTH sides every time) was pure
    // wasted CPU competing with things like image-upload decode. Real saves
    // still need the actual pixel data to restore a resumed draft.
    const skipImageEncode = window.__PAMELA_LITE_DRAFT_CAPTURE__ === true;
    return {
    kind: which === "panels" ? "panel" : (isLaserItem ? "laser" : "clipart"),
    src: CM_safeStr(n.getAttr?.("pamelaPanelUrl") || n.getAttr?.("pamelaClipartUrl") || n.getAttr?.("pamelaLaserUrl") || ""),
    // Capped at 1800px (matches the editor's own working-resolution cap
    // elsewhere) -- a baked Fit-to-Stone image can be several thousand
    // pixels wide at its real on-stone size, and three UNCAPPED PNG data
    // URLs per laser node (times both sides) was enough to blow past the
    // server's request-size limit on save (413 Payload Too Large), with
    // every failed attempt still paying the full encode+stringify cost.
    displaySrc: isLaserItem && !skipImageEncode ? CM_imageSourceToDataUrl(displayImage, 1800) : "",
    baseSrc: isLaserItem && !skipImageEncode ? CM_imageSourceToDataUrl(baseImage, 1800) : "",
    cropRootSrc: isLaserItem && !skipImageEncode
      ? CM_imageSourceToDataUrl(n._pamelaLaserCropRootImage || baseImage || displayImage, 1200)
      : "",
    cropRootWidth: isLaserItem ? CM_safeNum(n._pamelaLaserCropRootWidth || n.width?.(), 0) : 0,
    cropRootHeight: isLaserItem ? CM_safeNum(n._pamelaLaserCropRootHeight || n.height?.(), 0) : 0,
    x: CM_safeNum(typeof n.x === "function" ? n.x() : 0),
    y: CM_safeNum(typeof n.y === "function" ? n.y() : 0),
    width: CM_safeNum(typeof n.width === "function" ? n.width() : 0),
    height: CM_safeNum(typeof n.height === "function" ? n.height() : 0),
    rotation: CM_safeNum(typeof n.rotation === "function" ? n.rotation() : 0),
    scaleX: CM_safeNum(typeof n.scaleX === "function" ? n.scaleX() : 1, 1),
    scaleY: CM_safeNum(typeof n.scaleY === "function" ? n.scaleY() : 1, 1),
    offsetX: CM_safeNum(typeof n.offsetX === "function" ? n.offsetX() : 0),
    offsetY: CM_safeNum(typeof n.offsetY === "function" ? n.offsetY() : 0),
    opacity: CM_safeNum(typeof n.opacity === "function" ? n.opacity() : 1, 1),
    visible: typeof n.visible === "function" ? !!n.visible() : true,
    attrs: CM_pickPersistentAttrs(n, [
      "pamelaUid",
      "pamelaPanel",
      "pamelaPanelUrl",
      "pamelaPanelId",
      "pamelaPanelName",
      "pamelaPanelMode",
      "pamelaPanelGroup",
      "pamelaPanelSlot",
      "pamelaPanelBaseWidth",
      "pamelaPanelBaseHeight",
      "pamelaPanelWidthIn",
      "pamelaSvgMode",
      "pamelaSvgModeExplicit",
      "pamelaHasTextPath",
      "pamelaTextPathData",
      "pamelaTextPathViewBox",

      "pamelaClipart",
      "pamelaClipartUrl",
      "pamelaClipartId",
      "pamelaClipartName",
      "pamelaClipartCategory",
      "pamelaClipartCategoryId",
      "pamelaClipartSubcategory",
      "pamelaClipartLibraryPanel",
      "pamelaClipartMode",

      "pamelaLaserEtching",
      "pamelaLaserUrl",
      "pamelaLaserName",
      "pamelaLaserKind",
      "pamelaLaserPricingKind",
      "pamelaLaserPricingSource",
      "pamelaLaserSource",
      "pamelaLaserCategory",
      "pamelaLaserSubcategory",
      "pamelaLaserSide",
      "pamelaLaserFaces",
      "pamelaLaserFacesAutoDetected",
      "pamelaLaserFacesManualOverride",
      "pamelaLaserFacesNeedsReview",
      "pamelaLaserPeopleConfirmed",
      "pamelaLaserFaceDetectionStatus",
      "pamelaLaserFaceDetectionProvider",
      "pamelaLaserFaceDetectionConfidence",
      "pamelaLaserFaceDetectionReason",
      "pamelaLaserNotes",
      "pamelaLaserFeatherPx",
      "pamelaLaserFitToStone",
      "pamelaLaserOriginalName",
      "pamelaLaserProcessedUrl",
      "pamelaLaserThumbUrl",
      "pamelaImageQualityIssue",
      "pamelaImageEnhancementRequested",
      "pamelaImageEnhancementFee",
      "pamelaImagePixelWidth",
      "pamelaImagePixelHeight",

      "pamelaType",
      "id",
      "name"
    ])
  };
  });
}

function CM_collectPortraitNodes(editor) {
  try {
    if (typeof window.CM_collectPortraitAssetsFromStage === "function") {
      return CM_clonePlain(window.CM_collectPortraitAssetsFromStage(editor) || []);
    }
  } catch (_) {}
  return [];
}

function CM_clearEditableLayers(editor) {
  try { editor.selected = null; } catch (_) {}
  try { editor.nodes?.tr?.nodes?.([]); } catch (_) {}
  try { editor.nodes?.trPanel?.nodes?.([]); } catch (_) {}

  const buckets = [
    editor?.layers?.panels,
    editor?.layers?.content,
    editor?.layers?.portraits
  ].filter(Boolean);

  const seen = new Set();
  for (const lyr of buckets) {
    if (!lyr || seen.has(lyr)) continue;
    seen.add(lyr);
    try { lyr.destroyChildren?.(); } catch (_) {}
  }

  // Also remove stray restored portrait groups/photos that were attached
  // outside the real front/back portrait layers during earlier resume attempts.
  // Never destroy nodes that belong to a hidden side layer.
  try {
    const stage = editor?.stage || editor?.konvaStage;
    const protectedLayers = new Set([
      editor?.layers?.sides?.front?.portraits,
      editor?.layers?.sides?.back?.portraits,
      editor?.layers?.sides?.front?.content,
      editor?.layers?.sides?.back?.content
    ].filter(Boolean));
    const isProtectedSideNode = (n) => {
      try {
        const layer = n?.getLayer?.();
        return !!(layer && protectedLayers.has(layer));
      } catch (_) {
        return false;
      }
    };

    const strayGroups = stage?.find?.(".pamela-portrait-group") || [];
    strayGroups.forEach((n) => {
      if (isProtectedSideNode(n)) return;
      try { n.destroy?.(); } catch (_) {}
    });

    const strayPhotos = stage?.find?.(".pamela-portrait-photo") || [];
    strayPhotos.forEach((n) => {
      if (isProtectedSideNode(n)) return;
      try { n.destroy?.(); } catch (_) {}
    });
  } catch (_) {}

// HARD RESET portrait slot system
try {
  if (typeof ensureSlots === "function") {
    const slots = ensureSlots(editor);
    if (Array.isArray(slots)) {
      for (let i = 0; i < slots.length; i++) {
        slots[i] = null;
      }
    }
  }
} catch (e) {
  console.warn("Failed to reset portrait slots:", e);
}
}

function CM_captureEditorNativeState(editor) {
  if (!editor) return null;

  return {
    version: 1,
    kind: "pamela-editor-native",
    layout: CM_safeStr(editor.layout || ""),
    design: CM_clonePlain(CM_getDesignSize(editor)),
    currentTextFill: CM_safeStr(editor.currentTextFill || ""),
    fontState: CM_clonePlain(editor.fontState || {}),
    pairLinkOverrides: CM_clonePlain(editor.pairLinkOverrides || {}),
    horizontalMirrorLinked: editor.horizontalMirrorLinked !== false,

    texts: CM_collectTextNodes(editor),
    panels: CM_collectImageNodes(editor, "panels"),
    clipart: CM_collectImageNodes(editor, "clipart"),
    laser: CM_collectImageNodes(editor, "laser"),
    portraits: CM_collectPortraitNodes(editor)
  };
}

async function CM_restoreOneText(editor, item) {
  const wantsTextPath =
    item?.textNodeType === "textPath" ||
    item?.attrs?.pamelaTextPathApplied === true ||
    !!String(item?.attrs?.pamelaTextPathData || "").trim();

  const baseTextConfig = {
    x: CM_safeNum(item.x),
    y: CM_safeNum(item.y),
    text: CM_safeStr(item.text),
    fontFamily: CM_safeStr(item.fontFamily || "MDRoman"),
    fontSize: CM_safeNum(item.fontSize, pxFromIn(1)),
    fill: CM_safeStr(item.fill || editor.currentTextFill || "#111"),
    align: CM_safeStr(item.align || "center"),
    padding: CM_safeNum(item.padding, 0),
    lineHeight: CM_safeNum(item.lineHeight, 1),
    letterSpacing: CM_safeNum(item.letterSpacing, 0),
    draggable: false,
    listening: true,
    name: wantsTextPath ? "pamela-text pamela-text-path" : "pamela-text"
  };

  const t = wantsTextPath && typeof Konva.TextPath === "function"
    ? new Konva.TextPath({
        ...baseTextConfig,
        data: CM_safeStr(item?.attrs?.pamelaTextPathData || ""),
      })
    : new Konva.Text(baseTextConfig);

  // Restore transform/state. Straight text recalculates box metrics; text-path keeps its saved guide transform.
  try { t.rotation(CM_safeNum(item.rotation, 0)); } catch (_) {}
  try { t.scaleX(CM_safeNum(item.scaleX, 1)); } catch (_) {}
  try { t.scaleY(CM_safeNum(item.scaleY, 1)); } catch (_) {}
  try { t.opacity(CM_safeNum(item.opacity, 1)); } catch (_) {}
  try { t.visible(item.visible !== false); } catch (_) {}

  Object.entries(item.attrs || {}).forEach(([k, v]) => {
    try { t.setAttr(k, v); } catch (_) {}
  });

  try { pamelaNormalizeSingleBackLastNameDefault(t, editor); } catch (_) {}

  t.setAttr("__pamelaSelectable", true);
  t.setAttr("__pamelaKind", "text");
  t.setAttr("pamelaDrag", true);
  t.setAttr("pamelaDraggable", true);

  if (!t.getAttr("pamelaUid") && typeof editor._ensurePamelaUid === "function") {
    editor._ensurePamelaUid(t, "t");
  }

  editor.layers.content.add(t);

  // Rebuild fresh live metrics exactly like a real editor text node needs.
  pamelaEnsureNodeFontReady(editor, t);
  pamelaEnsureNodeFontReady(editor, t, 400);
  if (t.className === "Text") {
    try { pamelaFixTextMetrics(t); } catch (_) {}
  }

  if (t.className === "Text") {
    try {
      const r = t.getSelfRect ? t.getSelfRect() : { x: 0, y: 0, width: t.width(), height: t.height() };
      t.offsetX(r.x + r.width / 2);
      t.offsetY(r.y + r.height / 2);
    } catch (_) {}
  }

  try { pamelaBindTextEditHandlers(editor, t); } catch (_) {}
  try { pamelaClampNodeIntoSafeRect(editor, t); } catch (_) {}

  t.off("pointerdown.cmDraftRestoreTextSelect mousedown.cmDraftRestoreTextSelect touchstart.cmDraftRestoreTextSelect");
  t.on("pointerdown.cmDraftRestoreTextSelect mousedown.cmDraftRestoreTextSelect touchstart.cmDraftRestoreTextSelect", (e) => {
    try { e.cancelBubble = true; } catch (_) {}
    try { editor.selectNode?.(t); } catch (_) {}
  });

  t.off("dragmove.cmDraftRestoreText");
  t.on("dragmove.cmDraftRestoreText", () => {
    try { pamelaSnapTextToCenterX(editor, t); } catch (_) {}
    try { pamelaApplyHorizontalMirrorDrag(editor, t, t._pamelaMirrorDrag, 0, 0, pamelaShouldMoveSideIndependently(editor, false)); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, t); } catch (_) {}
    try { pamelaTryRelinkHorizontalMirror(editor); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(editor, t); } catch (_) {}
    try { editor.layers.content.batchDraw(); } catch (_) {}
  });

  t.off("dragend.cmDraftRestoreText");
  t.on("dragend.cmDraftRestoreText", () => {
    if (t.className === "Text") {
      try { pamelaFixTextMetrics(t); } catch (_) {}
    }
    try { pamelaSnapTextToCenterX(editor, t); } catch (_) {}
    try { pamelaApplyHorizontalMirrorDrag(editor, t, t._pamelaMirrorDrag, 0, 0, pamelaShouldMoveSideIndependently(editor, false)); } catch (_) {}
    try { pamelaClampNodeIntoSafeRect(editor, t); } catch (_) {}
    try { pamelaTryRelinkHorizontalMirror(editor); } catch (_) {}
    try { pamelaEnforceSpacingForText(editor); } catch (_) {}
    try { pamelaUpdateOneTextColorForOverlap(editor, t); } catch (_) {}
  });

  try { editor._enableDirectPointerDrag?.(t, "content"); } catch (_) {}

  return t;
}

async function CM_restoreOneImage(editor, item) {
  const isLaser = item?.kind === "laser" || item?.attrs?.pamelaLaserEtching === true;
  const isPanel = item.kind === "panel";
  const explicitMode = item?.attrs?.pamelaSvgModeExplicit === true;
  const rawModeHint =
    isPanel
      ? CM_safeStr(item?.attrs?.pamelaPanelMode || "auto")
      : CM_safeStr(item?.attrs?.pamelaClipartMode || "auto");
  let modeHint = explicitMode
    ? rawModeHint
    : (isPanel ? pamelaGetEffectivePanelMode(editor) : pamelaGetActiveSvgMode());
  if (
    modeHint !== "standard" &&
    modeHint !== "white-outline" &&
    modeHint !== "reverse-process"
  ) {
    modeHint = pamelaSelectedGraniteIsLight() ? "standard" : "white-outline";
  }

  let img = null;
  if (isLaser) {
    const displaySrc = CM_safeStr(item.displaySrc || "");
    const processedSrc = CM_safeStr(item?.attrs?.pamelaLaserProcessedUrl || item?.processedSrc || "");
    const thumbSrc = CM_safeStr(item?.attrs?.pamelaLaserThumbUrl || item?.thumbSrc || "");
    const baseSrc = CM_safeStr(item.baseSrc || "");
    const originalSrc = CM_safeStr(item.src || item?.attrs?.pamelaLaserUrl || "");
    const laserSources = [displaySrc, processedSrc, thumbSrc, baseSrc, originalSrc].filter(Boolean);

    for (const src of laserSources) {
      if (img) break;
      try {
        img = await loadImage(src);
      } catch (_) {}
      if (!img && src === originalSrc) {
        try {
          const original = await loadLaserHtmlImage(src);
          img = makeLaserBlackWhitePreviewImage(original);
        } catch (_) {}
      }
    }
  } else {
    try {
      img = await pamelaLoadSvgImageForGranite(item.src, modeHint);
    } catch (_) {
      try { img = await loadImage(item.src); } catch (_) {}
    }
  }
  if (!img) return null;
  let nodeKnockoutMaskImage = img;
  if (!isLaser) {
    try {
      nodeKnockoutMaskImage = await pamelaLoadSvgImageForGranite(item.src, "standard");
    } catch (_) {
      nodeKnockoutMaskImage = img;
    }
  }
  const textPathMeta = isLaser ? null : await pamelaLoadSvgTextPathMeta(item.src);

  const node = new Konva.Image({
    image: img,
    x: CM_safeNum(item.x),
    y: CM_safeNum(item.y),
    width: CM_safeNum(item.width),
    height: CM_safeNum(item.height),
    offsetX: CM_safeNum(item.offsetX, 0),
    offsetY: CM_safeNum(item.offsetY, 0),
    draggable: false,
    listening: true,
    name: isPanel ? "pamela-panel" : (isLaser ? "pamela-laser-etching" : "pamela-clipart")
  });

  try { node.rotation(CM_safeNum(item.rotation, 0)); } catch (_) {}
  try { node.scaleX(CM_safeNum(item.scaleX, 1)); } catch (_) {}
  try { node.scaleY(CM_safeNum(item.scaleY, 1)); } catch (_) {}
  try { node.opacity(CM_safeNum(item.opacity, 1)); } catch (_) {}
  try { node.visible(item.visible !== false); } catch (_) {}

  Object.entries(item.attrs || {}).forEach(([k, v]) => {
    try { node.setAttr(k, v); } catch (_) {}
  });
  if (!isLaser) pamelaAttachTextPathMetaToImage(node, textPathMeta);

  try {
    if (!explicitMode) {
      node.setAttr("pamelaSvgModeExplicit", false);
      if (isPanel) node.setAttr("pamelaPanelMode", "auto");
      else if (!isLaser) node.setAttr("pamelaClipartMode", "auto");
    }
  } catch (_) {}
  if (!isLaser) {
    try {
      node.setAttr("pamelaSvgMode", modeHint);
    } catch (_) {}
  }

  node.setAttr("__pamelaSelectable", true);
  node.setAttr("__pamelaKind", isPanel ? "panel" : (isLaser ? "laser" : "clipart"));
  node.setAttr("pamelaDrag", true);
  node.setAttr("pamelaDraggable", true);

  if (isPanel) {
    node.setAttr("pamelaPanel", true);
    if (!node.getAttr("pamelaPanelBaseWidth")) node.setAttr("pamelaPanelBaseWidth", CM_safeNum(item.width));
    if (!node.getAttr("pamelaPanelBaseHeight")) node.setAttr("pamelaPanelBaseHeight", CM_safeNum(item.height));
    node.__pamelaPanelOriginalImage = img;
    if (pamelaIsLastNamePanelNode(node)) {
      node.__pamelaLastNameSliceSourceImage = img;
      node.__pamelaLastNameSliceMaskSourceImage = nodeKnockoutMaskImage;
    }
    if (pamelaIsNameDatePanelNode(node)) {
      node.__pamelaNameDateSliceSourceImage = img;
      node.__pamelaNameDateSliceMaskSourceImage = nodeKnockoutMaskImage;
    }
    node.__pamelaPanelKnockoutSourceImage = img;
    node.__pamelaPanelKnockoutImage = null;
  } else if (isLaser) {
    node.setAttr("pamelaLaserEtching", true);
    if (!node.getAttr("pamelaLaserUrl")) node.setAttr("pamelaLaserUrl", CM_safeStr(item.src));
    if (item.baseSrc) {
      try { node._pamelaLaserBaseImage = await loadImage(item.baseSrc); } catch (_) {}
    }
    if (!node._pamelaLaserBaseImage) node._pamelaLaserBaseImage = cloneLaserCanvasSource(img) || img;
    if (item.cropRootSrc) {
      try { node._pamelaLaserCropRootImage = await loadImage(item.cropRootSrc); } catch (_) {}
    }
    if (!node._pamelaLaserCropRootImage) {
      node._pamelaLaserCropRootImage = cloneLaserCanvasSource(node._pamelaLaserBaseImage) || node._pamelaLaserBaseImage;
    }
    node._pamelaLaserCropRootWidth = CM_safeNum(item.cropRootWidth, item.width || node.width?.() || 1);
    node._pamelaLaserCropRootHeight = CM_safeNum(item.cropRootHeight, item.height || node.height?.() || 1);
    const featherPx = CM_safeNum(node.getAttr("pamelaLaserFeatherPx"), 0);
    if (featherPx > 0 && item.baseSrc) {
      try { setLaserNodeFeather(node, featherPx); } catch (_) {}
    }
    pamelaMarkLaserArtworkNode(node);
  } else {
    node.setAttr("pamelaClipart", true);
    node.__pamelaPanelKnockoutSourceImage = img;
    node.__pamelaPanelKnockoutImage = null;
  }
  if (!isLaser) node.__pamelaPanelKnockoutMaskImage = nodeKnockoutMaskImage;
  if (isPanel) {
    try { pamelaUpdateTraditionalPanelSliceImage(node); } catch (_) {}
  }

  if (!node.getAttr("pamelaUid") && typeof editor._ensurePamelaUid === "function") {
    editor._ensurePamelaUid(node, isPanel ? "p" : (isLaser ? "l" : "c"));
  }

  if (isPanel) {
    editor.layers.panels.add(node);
    try { node.moveToTop(); } catch (_) {}
  } else {
    editor.layers.content.add(node);
  }

  try { node.hitStrokeWidth(20); } catch (_) {}
  try { pamelaClampNodeIntoSafeRect(editor, node); } catch (_) {}

  if (isPanel) {
    node.off("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect");
    node.on("pointerdown.pamelaPanelSelect mousedown.pamelaPanelSelect touchstart.pamelaPanelSelect", (e) => {
      try { e.cancelBubble = true; } catch (_) {}
      if (editor.panMode) return;
      const evt = e?.evt;
      if (evt && typeof evt.button === "number" && evt.button === 2) return;
      if (evt?.cancelable) {
        try { evt.preventDefault(); } catch (_) {}
      }
      try { editor.selectNode?.(node); } catch (_) {}
      try { editor.nodes?.trPanel?.forceUpdate?.(); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
    });
  } else {
    node.off("pointerdown.cmDraftRestoreClipartSelect mousedown.cmDraftRestoreClipartSelect touchstart.cmDraftRestoreClipartSelect");
    node.on("pointerdown.cmDraftRestoreClipartSelect mousedown.cmDraftRestoreClipartSelect touchstart.cmDraftRestoreClipartSelect", (e) => {
      try { e.cancelBubble = true; } catch (_) {}
      if (editor.panMode) return;
      const evt = e?.evt;
      if (evt && typeof evt.button === "number" && evt.button === 2) return;
      if (evt?.cancelable) {
        try { evt.preventDefault(); } catch (_) {}
      }
      try { editor.selectNode?.(node); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
      if (isLaser) {
        try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
        try { window.PAMELA_syncMobileToolbars?.(editor); } catch (_) {}
      }
    });
  }

  try { editor._enableDirectPointerDrag?.(node, isPanel ? "panels" : "content"); } catch (_) {}

  // These are the same movement rules applied to new clipart. Without them a
  // restored node could move, but skip clamping, overlap refresh, and the
  // normal post-drag state capture.
  if (!isPanel && !isLaser) {
    node.off("dragmove.cmDraftRestoreImage dragend.cmDraftRestoreImage");
    node.on("dragmove.cmDraftRestoreImage", () => {
      try { pamelaClampNodeIntoSafeRect(editor, node); } catch (_) {}
      try { editor._scheduleOverlapColors?.(); } catch (_) {}
      try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
    });
    node.on("dragend.cmDraftRestoreImage", () => {
      try { pamelaClampNodeIntoSafeRect(editor, node); } catch (_) {}
      try { editor._scheduleOverlapColors?.(); } catch (_) {}
      try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
      try { editor.layers?.ui?.batchDraw?.(); } catch (_) {}
      try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
    });
  }

  if (isLaser) {
    try { pamelaEnableLaserArtworkDrag(editor, node); } catch (_) {}
  }
  if (!isLaser) {
    try { pamelaSchedulePanelKnockoutRebuild(editor); } catch (_) {}
  } else {
    try { editor._scheduleOverlapColors?.(); } catch (_) {}
  }

  return node;
}

function CM_makePortraitClipFunc(shape, w, h) {
  return function (ctx) {
    if (shape === "circle") {
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
      ctx.closePath();
      return;
    }
    if (shape === "oval") {
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.closePath();
      return;
    }
    if (shape === "heart") {
      const x = w / 2;
      const y = h / 2;
      const top = h * 0.28;
      ctx.beginPath();
      ctx.moveTo(x, y + h * 0.33);
      ctx.bezierCurveTo(x + w * 0.50, y + h * 0.05, x + w * 0.50, y - top, x, y - top);
      ctx.bezierCurveTo(x - w * 0.50, y - top, x - w * 0.50, y + h * 0.05, x, y + h * 0.33);
      ctx.closePath();
      return;
    }
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.closePath();
  };
}

function CM_makeHeartGuideNode(w, h) {
  const g = new Konva.Shape({
    sceneFunc: function (ctx, shape) {
      const x = w / 2;
      const y = h / 2;
      const top = h * 0.28;
      ctx.beginPath();
      ctx.moveTo(x, y + h * 0.33);
      ctx.bezierCurveTo(x + w * 0.50, y + h * 0.05, x + w * 0.50, y - top, x, y - top);
      ctx.bezierCurveTo(x - w * 0.50, y - top, x - w * 0.50, y + h * 0.05, x, y + h * 0.33);
      ctx.closePath();
      ctx.fillStrokeShape(shape);
    },
    fillEnabled: false,
    listening: false
  });
  return g;
}

async function CM_restoreOnePortrait(editor, portrait, idx) {
  try {
    if (!editor || !portrait) return null;

    const world =
      (typeof findWorldContainer === "function")
        ? findWorldContainer(editor)
        : null;

    const layer =
      editor?.layers?.portraits ||
      (world && typeof world.add === "function" && world) ||
      editor?.layers?.content ||
      editor?.stage;

    if (!layer || typeof layer.add !== "function") {
      console.warn("CM_restoreOnePortrait: no target layer");
      return null;
    }

    const slot = Number(portrait?.slot ?? idx) || idx;
    const shape = String(portrait?.shape || "rect").toLowerCase();
    const wIn = Number(portrait?.wIn || 4);
    const hIn = Number(portrait?.hIn || 4);
    const ppi = Number(portrait?.ppi || 72) || 72;

    const pxPerIn =
      (typeof getDesignPixelsPerInch === "function")
        ? Number(getDesignPixelsPerInch(editor) || 72)
        : 72;

    const safePxPerIn = Number.isFinite(pxPerIn) && pxPerIn > 0 ? pxPerIn : 72;

    const w = Math.max(1, Number(wIn) * safePxPerIn);
    const h = Math.max(1, Number(hIn) * safePxPerIn);

    const photoUrl = String(portrait?.url || "").trim();
    const croppedDataUrl = String(portrait?.croppedDataUrl || "").trim();
    const restoreFromCroppedDataUrl = !photoUrl && !!croppedDataUrl;
    const restoreImageUrl = restoreFromCroppedDataUrl ? croppedDataUrl : photoUrl;
    const photoMeta = portrait?.photo || null;

    const wx = Number.isFinite(Number(portrait?.wrapper?.x))
      ? Number(portrait.wrapper.x)
      : (Number.isFinite(Number(portrait?.x)) ? Number(portrait.x) : 100);

    const wy = Number.isFinite(Number(portrait?.wrapper?.y))
      ? Number(portrait.wrapper.y)
      : (Number.isFinite(Number(portrait?.y)) ? Number(portrait.y) : 100);

    const wsx = Number.isFinite(Number(portrait?.wrapper?.scaleX))
      ? Number(portrait.wrapper.scaleX)
      : 1;

    const wsy = Number.isFinite(Number(portrait?.wrapper?.scaleY))
      ? Number(portrait.wrapper.scaleY)
      : 1;

    const wrot = Number.isFinite(Number(portrait?.wrapper?.rotation))
      ? Number(portrait.wrapper.rotation)
      : 0;

    const wrapper = new Konva.Group({
      x: wx,
      y: wy,
      name: "pamela-portrait-group pamela pamela-clipart pamela-draggable",
      draggable: true,
      listening: true,
      visible: true,
      rotation: wrot,
      scaleX: wsx,
      scaleY: wsy
    });

    try { safeAddName(wrapper, "pamela"); } catch (_) {}
    try { safeAddName(wrapper, "pamela-clipart"); } catch (_) {}
    try { safeAddName(wrapper, "pamela-draggable"); } catch (_) {}

    wrapper.setAttr("pamelaType", "portrait");
    wrapper.setAttr("pamelaDrag", true);
    wrapper.setAttr("pamelaDraggable", true);
    wrapper.setAttr("__pamelaSelectable", true);
    wrapper.setAttr("__pamelaKind", "portrait");

    wrapper.setAttr("pamelaPortrait", true);
    wrapper.setAttr("pamelaPortraitSlot", slot);

    wrapper.setAttr("__isPortraitGroup", true);
    wrapper.setAttr("__portraitSlot", slot);
    wrapper.setAttr("__portraitShape", shape);
    wrapper.setAttr("__portraitWIn", String(wIn));
    wrapper.setAttr("__portraitHIn", String(hIn));
    wrapper.setAttr("__portraitLabel", String(
      portrait?.label ||
      portrait?.title ||
      portrait?.meta?.label ||
      portrait?.meta?.title ||
      ""
    ));
    wrapper.setAttr("__portraitPPI", String(ppi));
    wrapper.setAttr("__portraitUrl", restoreImageUrl);
    wrapper.setAttr("__portraitHasPhoto", !!(photoMeta || restoreImageUrl));

    const variantId = Number(
      portrait?.variant_id ??
      portrait?.variantId ??
      portrait?.meta?.variant_id ??
      portrait?.meta?.variantId ??
      portrait?.__porcelainVariantId ??
      0
    ) || 0;

    const variantTitle = String(
      portrait?.title ??
      portrait?.meta?.title ??
      portrait?.meta?.label ??
      portrait?.__porcelainLabel ??
      ""
    );

    const variantPriceCents = Number(
      portrait?.price_cents ??
      portrait?.priceCents ??
      portrait?.meta?.price_cents ??
      portrait?.meta?.priceCents ??
      0
    ) || 0;

    if (variantId > 0) {
      wrapper.setAttr("__porcelainVariantId", variantId);
      wrapper.setAttr("__portraitVariantId", variantId);
      wrapper.setAttr("pamelaPortraitVariantId", variantId);
    }
    if (variantTitle) {
      wrapper.setAttr("__portraitVariantTitle", variantTitle);
      wrapper.setAttr("__porcelainLabel", variantTitle);
    }
    if (variantPriceCents > 0) {
      wrapper.setAttr("__portraitVariantPriceCents", variantPriceCents);
    }

    const guideStroke = "rgba(255,255,255,0.95)";
    const guideDash = [18, 12];
    const guideStrokeWidth = 4;

    if (shape === "circle" || shape === "oval") {
      const maskGuide = new Konva.Ellipse({
        x: w / 2,
        y: h / 2,
        radiusX: w / 2,
        radiusY: h / 2,
        stroke: guideStroke,
        strokeWidth: guideStrokeWidth,
        dash: guideDash,
        name: "pamela-portrait-mask",
        listening: false
      });
      maskGuide.setAttr("pamelaType", "portrait");
      maskGuide.setAttr("__pamelaKind", "portrait");
      wrapper.add(maskGuide);
    } else if (shape === "heart") {
      const heart = makeHeartShapeNode(w, h);
      heart.name("pamela-portrait-mask");
      heart.stroke(guideStroke);
      heart.strokeWidth(guideStrokeWidth);
      heart.dash(guideDash);
      heart.setAttr("pamelaType", "portrait");
      heart.setAttr("__pamelaKind", "portrait");
      wrapper.add(heart);
    } else {
      const maskGuide = new Konva.Rect({
        x: 0,
        y: 0,
        width: w,
        height: h,
        stroke: guideStroke,
        dash: guideDash,
        strokeWidth: guideStrokeWidth,
        cornerRadius: 10,
        name: "pamela-portrait-mask",
        listening: false
      });
      maskGuide.setAttr("pamelaType", "portrait");
      maskGuide.setAttr("__pamelaKind", "portrait");
      wrapper.add(maskGuide);
    }

    wrapper.add(new Konva.Line({
      points: [w / 2 - 18, h / 2, w / 2 + 18, h / 2],
      stroke: "rgba(255,255,255,0.9)",
      strokeWidth: 3,
      listening: false
    }));

    wrapper.add(new Konva.Line({
      points: [w / 2, h / 2 - 18, w / 2, h / 2 + 18],
      stroke: "rgba(255,255,255,0.9)",
      strokeWidth: 3,
      listening: false
    }));

    const inner = new Konva.Group({
      x: 0,
      y: 0,
      name: "pamela-portrait-inner",
      visible: true,
      opacity: 1
    });

    inner.clipFunc(function (ctx) {
      if (shape === "circle") {
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
        ctx.closePath();
        return;
      }
      if (shape === "oval") {
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.closePath();
        return;
      }
      if (shape === "heart") {
        const x = w / 2;
        const y = h / 2;
        const top = h * 0.28;
        ctx.beginPath();
        ctx.moveTo(x, y + h * 0.33);
        ctx.bezierCurveTo(x + w * 0.50, y + h * 0.05, x + w * 0.50, y - top, x, y - top);
        ctx.bezierCurveTo(x - w * 0.50, y - top, x - w * 0.50, y + h * 0.05, x, y + h * 0.33);
        ctx.closePath();
        return;
      }
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.closePath();
    });

    inner.add(new Konva.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: "rgba(255,255,255,0.06)",
      listening: false
    }));

    wrapper.add(inner);

    const hit = new Konva.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: "rgba(0,0,0,0.01)",
      opacity: 0.001,
      listening: true,
      name: "pamela-hit"
    });

    hit.setAttr("pamelaType", "portrait");
    hit.setAttr("pamelaDrag", true);
    hit.setAttr("pamelaDraggable", true);
    hit.setAttr("__pamelaSelectable", true);
    hit.setAttr("__pamelaKind", "portrait");

    try { safeAddName(hit, "pamela"); } catch (_) {}
    try { safeAddName(hit, "pamela-draggable"); } catch (_) {}
    try { safeAddName(hit, "pamela-clipart"); } catch (_) {}

    wrapper.add(hit);

    try {
      if (typeof attachPortraitDrag === "function") {
        attachPortraitDrag(editor, wrapper, hit, layer);
      }
    } catch (_) {}

    const applySavedPortraitPhotoTransform = () => {
      try {
        const restoredPhoto =
          inner?.findOne?.(".pamela-portrait-photo") ||
          inner?.findOne?.("pamela-portrait-photo") ||
          null;

        if (restoredPhoto && portrait?.photo) {
          restoredPhoto.x(Number(portrait.photo.x || 0) || 0);
          restoredPhoto.y(Number(portrait.photo.y || 0) || 0);
          restoredPhoto.width(Number(portrait.photo.width || restoredPhoto.width()) || restoredPhoto.width());
          restoredPhoto.height(Number(portrait.photo.height || restoredPhoto.height()) || restoredPhoto.height());
          restoredPhoto.scaleX(Number(portrait.photo.scaleX || 1) || 1);
          restoredPhoto.scaleY(Number(portrait.photo.scaleY || 1) || 1);
          restoredPhoto.rotation(Number(portrait.photo.rotation || 0) || 0);
          try { restoredPhoto.listening(false); } catch (_) {}
          try { restoredPhoto.draggable(false); } catch (_) {}
        }
      } catch (_) {}
    };

    if (restoreImageUrl) {
      try {
        const img = restoreFromCroppedDataUrl
          ? await new Promise((resolve, reject) => {
              const dataImg = new Image();
              dataImg.onload = () => resolve(dataImg);
              dataImg.onerror = () => reject(new Error("Failed to load portrait croppedDataUrl"));
              dataImg.src = croppedDataUrl;
            })
          : await loadImage(photoUrl);
        if (img) {
          if (typeof setPortraitImage === "function") {
            setPortraitImage(wrapper, img, {
              url: restoreImageUrl,
              original_name: String(portrait?.meta?.original_name || ""),
              content_type: String(portrait?.meta?.content_type || ""),
              width: Number(
                portrait?.meta?.img_w ??
                portrait?.meta?.width ??
                img.width ??
                0
              ) || 0,
              height: Number(
                portrait?.meta?.img_h ??
                portrait?.meta?.height ??
                img.height ??
                0
              ) || 0,
              heic_ack: portrait?.meta?.heic_ack ?? false
            });
            applySavedPortraitPhotoTransform();
          } else {
            const scale = Math.min(w / img.width, h / img.height);
            const newW = img.width * scale;
            const newH = img.height * scale;
            const offsetX = (w - newW) / 2;
            const offsetY = (h - newH) / 2;

            const photo = new Konva.Image({
              image: img,
              x: offsetX,
              y: offsetY,
              width: newW,
              height: newH,
              name: "pamela-portrait-photo",
              draggable: false,
              listening: true,
              visible: true,
              opacity: 1
            });

            try { safeAddName(photo, "pamela-portrait-photo"); } catch (_) {}
            inner.add(photo);
          }
        }
      } catch (e) {
        console.warn("Portrait image restore failed:", e);
      }
    }

    try { wrapper.rotation(wrot); } catch (_) {}

    layer.add(wrapper);

    try { wrapper.moveToTop(); } catch (_) {}
    try { editor.stage?.draw?.(); } catch (_) {}
    try { requestAnimationFrame(() => applySavedPortraitPhotoTransform()); } catch (_) {}

    try {
      editor.__cmPortraitSlots = editor.__cmPortraitSlots || [];
      editor.__cmPortraitSlots[slot] = wrapper;
    } catch (_) {}

    try { window.CM_resumePortraitEditing?.(wrapper, { select: false }); } catch (_) {}
    try { window.CM_patchPortraitWrapperPriority?.(wrapper); } catch (_) {}

    try { wrapper.moveToTop(); } catch (_) {}
    try { editor.layers?.portraits?.moveToTop?.(); } catch (_) {}
    try { layer?.batchDraw?.(); } catch (_) {}
    try { layer?.draw?.(); } catch (_) {}
    try { editor.stage?.batchDraw?.(); } catch (_) {}

    return wrapper;
  } catch (e) {
    console.warn("CM_restoreOnePortrait failed:", e);
    return null;
  }
}
window.CM_restoreOnePortrait = CM_restoreOnePortrait;

window.PAMELA_ensureSidePortraitsRestored = async function PAMELA_ensureSidePortraitsRestored(side) {
  return false;
  const normalized = side === "back" ? "back" : "front";
  try {
    const ed =
      window.pamelaMonumentEditor ||
      window.pamelaEditor ||
      window.editor ||
      window.PAMELA_EDITOR ||
      window.__PAMELA_EDITOR__ ||
      null;
    const state = window.PAMELA_SIDES?.[normalized]?.draft_state || null;
    const savedPortraits = Array.isArray(state?.portraits) ? state.portraits : [];
    if (!ed || !savedPortraits.length || typeof window.CM_restoreOnePortrait !== "function") return false;

    const targetLayer = ed.layers?.sides?.[normalized]?.portraits || null;
    if (!targetLayer || typeof targetLayer.add !== "function") return false;

    const existing = targetLayer.find?.(".pamela-portrait-group") || [];
    const existingWithPhoto = existing.filter((wrapper) => {
      try {
        return !!(
          wrapper?.getAttr?.("__portraitHasPhoto") === true ||
          wrapper?.findOne?.(".pamela-portrait-photo")
        );
      } catch (_) {
        return false;
      }
    });
    if (existingWithPhoto.length >= savedPortraits.length) return false;

    existing.forEach((wrapper) => {
      try {
        const hasPhoto = !!(
          wrapper?.getAttr?.("__portraitHasPhoto") === true ||
          wrapper?.findOne?.(".pamela-portrait-photo")
        );
        if (!hasPhoto) wrapper.destroy?.();
      } catch (_) {}
    });

    const previousLayers = {
      panels: ed.layers?.panels,
      content: ed.layers?.content,
      portraits: ed.layers?.portraits
    };

    try {
      ed.layers.panels = ed.layers?.sides?.[normalized]?.panels || previousLayers.panels;
      ed.layers.content = ed.layers?.sides?.[normalized]?.content || previousLayers.content;
      ed.layers.portraits = targetLayer;

      for (let i = existingWithPhoto.length; i < savedPortraits.length; i += 1) {
        await window.CM_restoreOnePortrait(ed, savedPortraits[i], i);
      }
    } finally {
      if (window.PAMELA_SIDES?.current === "back") {
        ed.layers.panels = ed.layers?.sides?.back?.panels || previousLayers.panels;
        ed.layers.content = ed.layers?.sides?.back?.content || previousLayers.content;
        ed.layers.portraits = ed.layers?.sides?.back?.portraits || previousLayers.portraits;
      } else {
        ed.layers.panels = ed.layers?.sides?.front?.panels || previousLayers.panels;
        ed.layers.content = ed.layers?.sides?.front?.content || previousLayers.content;
        ed.layers.portraits = ed.layers?.sides?.front?.portraits || previousLayers.portraits;
      }
    }

    try { targetLayer.batchDraw?.(); } catch (_) {}
    try { ed.stage?.batchDraw?.(); } catch (_) {}
    return true;
  } catch (e) {
    console.warn("PAMELA_ensureSidePortraitsRestored failed:", e);
    return false;
  }
};


async function CM_restoreEditorNativeState(editor, state) {
  if (!editor || !state) return false;

  CM_clearEditableLayers(editor);

  try {
    editor.pairLinkOverrides = CM_clonePlain(state.pairLinkOverrides || {});
  } catch (_) {}

  try {
    editor.horizontalMirrorLinked = state.horizontalMirrorLinked !== false;
  } catch (_) {}

  try {
    if (state.fontState && typeof state.fontState === "object") {
      editor.fontState = CM_clonePlain(state.fontState);
    }
  } catch (_) {}

  try {
    if (state.currentTextFill) {
      editor.currentTextFill = String(state.currentTextFill);
    }
  } catch (_) {}

const texts = Array.isArray(state.texts) ? state.texts : [];
const panels = Array.isArray(state.panels) ? state.panels : [];
const clipart = Array.isArray(state.clipart) ? state.clipart : [];
const laser = Array.isArray(state.laser) ? state.laser : [];
const portraits = Array.isArray(state.portraits) ? state.portraits : [];

for (const t of texts) {
  await CM_restoreOneText(editor, t);
}

for (const p of panels) {
  await CM_restoreOneImage(editor, p);
}

for (const c of clipart) {
  await CM_restoreOneImage(editor, c);
}

for (const l of laser) {
  await CM_restoreOneImage(editor, l);
}

for (let i = 0; i < portraits.length; i++) {
  await CM_restoreOnePortrait(editor, portraits[i], i);
}

  try { pamelaEnforceSpacingForText(editor); } catch (_) {}
  try { pamelaMakeRightMatchLeft(editor); } catch (_) {}
  try { pamelaSyncHorizontalMirror(editor); } catch (_) {}
  try { await pamelaRefreshPlacedSvgNodesForGranite(editor); } catch (_) {}

  try { editor.layers?.panels?.batchDraw?.(); } catch (_) {}
  try { editor.layers?.content?.batchDraw?.(); } catch (_) {}
  try { editor.layers?.portraits?.batchDraw?.(); } catch (_) {}
  try { editor.stage?.batchDraw?.(); } catch (_) {}

  return true;
}
window.CM_captureEditorNativeState = CM_captureEditorNativeState;
window.CM_restoreEditorNativeState = CM_restoreEditorNativeState;

// -----------------------------
// TRUE FRONT/BACK STORAGE (draft-native, editor-native)
// IMPORTANT:
// - This is for editable save/resume only
// - DO NOT use production manifests here
// - PDF/cart production stays separate
// -----------------------------
const PAMELA_SIDES = {
  current: "front",
  // baseline = blank editor-native state captured once when editor is ready
  baseline: { snapshot: null, draft_state: null },
  front: { snapshot: null, draft_state: null },
  back: { snapshot: null, draft_state: null }
};

window.PAMELA_SIDES = PAMELA_SIDES;

function pamelaCaptureNativeDraftState() {
  try {
    if (typeof window.CM_captureEditorNativeState === "function") {
      return window.CM_captureEditorNativeState(editor) || null;
    }
  } catch (e) {
    console.warn("Native draft capture failed:", e);
  }
  return null;
}

function saveCurrentSideState() {
  if (window.__PAMELA_RESTORING_SIDE_STATE__ === true) return;
  if (window.__PAMELA_DRAFT_RESTORING__ === true) return;
  const sideKey =
    (PAMELA_SIDES.current === "front" || PAMELA_SIDES.current === "back")
      ? PAMELA_SIDES.current
      : "front";

  try {
    PAMELA_SIDES[sideKey].snapshot = getDesignSnapshot(editor);
  } catch (e) {
    console.warn("Snapshot capture failed:", e);
    PAMELA_SIDES[sideKey].snapshot = null;
  }

  PAMELA_SIDES[sideKey].draft_state = pamelaCaptureNativeDraftState();
  try { pamelaMarkSideThumbDirty(sideKey); } catch (_) {}
}

function pamelaSaveSpecificSideState(side) {
  const sideKey = side === "back" ? "back" : "front";
  const sides = editor?.layers?.sides || null;
  if (!sides?.[sideKey]) {
    if (window.PAMELA_SIDES) window.PAMELA_SIDES.current = sideKey;
    saveCurrentSideState();
    return;
  }

  const previousSide =
    (PAMELA_SIDES.current === "front" || PAMELA_SIDES.current === "back")
      ? PAMELA_SIDES.current
      : "front";
  const previousLayers = {
    panels: editor.layers?.panels,
    content: editor.layers?.content,
    portraits: editor.layers?.portraits
  };

  try {
    const previousPortraits = Array.isArray(PAMELA_SIDES?.[sideKey]?.draft_state?.portraits)
      ? CM_clonePlain(PAMELA_SIDES[sideKey].draft_state.portraits)
      : null;
    const shouldPreservePortraits = previousSide !== sideKey;

    PAMELA_SIDES.current = sideKey;
    editor.layers.panels = sides[sideKey].panels || previousLayers.panels;
    editor.layers.content = sides[sideKey].content || previousLayers.content;
    editor.layers.portraits = sides[sideKey].portraits || previousLayers.portraits;
    saveCurrentSideState();
    if (
      shouldPreservePortraits &&
      previousPortraits &&
      PAMELA_SIDES?.[sideKey]?.draft_state
    ) {
      PAMELA_SIDES[sideKey].draft_state.portraits = previousPortraits;
    }
  } finally {
    PAMELA_SIDES.current = previousSide;
    editor.layers.panels = previousLayers.panels;
    editor.layers.content = previousLayers.content;
    editor.layers.portraits = previousLayers.portraits;
  }
}

function pamelaSaveAllSideStates() {
  try { pamelaSaveSpecificSideState("front"); } catch (_) {}
  try { pamelaSaveSpecificSideState("back"); } catch (_) {}
}

function pamelaSyncVariantFillIntoDraftState(state, fill) {
  if (!state || typeof state !== "object") return state;

  const normalizedFill = String(fill || "#111");
  state.currentTextFill = normalizedFill;

  const texts = Array.isArray(state.texts) ? state.texts : [];
  texts.forEach((t) => {
    if (!t || typeof t !== "object") return;
    t.fill = normalizedFill;
    if (!t.attrs || typeof t.attrs !== "object") t.attrs = {};
    t.attrs.pamelaVariantDefaultFill = normalizedFill;
  });

  return state;
}

function pamelaGetDefaultSvgModeForVariant(variantLike) {
  const variantId = String(
    variantLike && typeof variantLike === "object"
      ? (variantLike.id || "")
      : variantLike || ""
  ).trim();

  try {
    const toneMap = window.__PAMELA_GRANITE_TONE_BY_VARIANT_ID__ || {};
    const tone = String(toneMap?.[variantId] || "").trim().toLowerCase();
    if (tone === "light") return "standard";
  } catch (_) {}

  return "white-outline";
}

function pamelaSyncVariantSvgModesIntoDraftState(state, variantLike) {
  if (!state || typeof state !== "object") return state;

  const defaultMode = pamelaGetDefaultSvgModeForVariant(variantLike);

  const panels = Array.isArray(state.panels) ? state.panels : [];
  panels.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (!item.attrs || typeof item.attrs !== "object") item.attrs = {};

    item.attrs.pamelaPanelMode = "auto";
    item.attrs.pamelaSvgModeExplicit = false;
    item.attrs.pamelaSvgMode = defaultMode;
  });

  const clipart = Array.isArray(state.clipart) ? state.clipart : [];
  clipart.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (!item.attrs || typeof item.attrs !== "object") item.attrs = {};

    item.attrs.pamelaClipartMode = "auto";
    item.attrs.pamelaSvgModeExplicit = false;
    item.attrs.pamelaSvgMode = defaultMode;
  });

  return state;
}

function pamelaSyncVariantIntoStoredSideDrafts(fill) {
  try { pamelaSyncVariantFillIntoDraftState(PAMELA_SIDES?.front?.draft_state, fill); } catch (_) {}
  try { pamelaSyncVariantFillIntoDraftState(PAMELA_SIDES?.back?.draft_state, fill); } catch (_) {}
  try { pamelaSyncVariantFillIntoDraftState(PAMELA_SIDES?.baseline?.draft_state, fill); } catch (_) {}
}

function pamelaSyncVariantSvgIntoStoredSideDrafts(variantLike) {
  try { pamelaSyncVariantSvgModesIntoDraftState(PAMELA_SIDES?.front?.draft_state, variantLike); } catch (_) {}
  try { pamelaSyncVariantSvgModesIntoDraftState(PAMELA_SIDES?.back?.draft_state, variantLike); } catch (_) {}
  try { pamelaSyncVariantSvgModesIntoDraftState(PAMELA_SIDES?.baseline?.draft_state, variantLike); } catch (_) {}
}
// DEBUG ONLY: expose helpers to console
window.getDesignSnapshot = getDesignSnapshot;
window.loadDesignSnapshot = loadDesignSnapshot;
window.saveCurrentSideState = saveCurrentSideState;
window.pamelaSaveSpecificSideState = pamelaSaveSpecificSideState;
window.pamelaSaveAllSideStates = pamelaSaveAllSideStates;
window.PAMELA_SIDES = PAMELA_SIDES;

// Capture a one-time BLANK baseline state.
// IMPORTANT: This must run BEFORE the customer adds anything (or as early as possible).
function captureBaselineIfMissing() {
  try {
    if (PAMELA_SIDES.baseline.snapshot || PAMELA_SIDES.baseline.draft_state) return;

    try {
      PAMELA_SIDES.baseline.snapshot = getDesignSnapshot(editor);
    } catch (_) {
      PAMELA_SIDES.baseline.snapshot = null;
    }

    PAMELA_SIDES.baseline.draft_state = pamelaCaptureNativeDraftState();
  } catch (_) {}
}

async function switchToSide(targetSide, opts = {}) {
  if (targetSide !== "front" && targetSide !== "back") return false;
  window.__PAMELA_SIDE_SWITCHING__ = true;
  window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = true;

  const previousSide =
    (PAMELA_SIDES.current === "front" || PAMELA_SIDES.current === "back")
      ? PAMELA_SIDES.current
      : "front";

  try {
    if (!opts.skipSaveCurrent && previousSide !== targetSide) {
      try {
        PAMELA_SIDES.current = previousSide;
        saveCurrentSideState();
      } catch (e) {
        console.warn("saveCurrentSideState before switch failed:", e);
      }
    }

    try {
      if (window.__CM_BACK_LETTERING__?.setSide) {
        window.__CM_BACK_LETTERING__.setSide(targetSide);
      } else if (typeof window.setSide === "function") {
        window.setSide(targetSide);
      } else {
        PAMELA_SIDES.current = targetSide;
      }
    } catch (e) {
      console.warn("switchToSide setSide failed:", e);
    }

    PAMELA_SIDES.current = targetSide;

    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    return true;
  } finally {
    try {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.__PAMELA_SIDE_SWITCHING__ = false;
          window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
          try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
        });
      });
    } catch (_) {
      window.__PAMELA_SIDE_SWITCHING__ = false;
      window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
      try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    }
  }
}

window.saveCurrentSideState = saveCurrentSideState;
window.captureBaselineIfMissing = captureBaselineIfMissing;
window.switchToSide = switchToSide;

// -----------------------------
// UI: Add-ons + prices + porcelain qty + vase front/back/both + front/back toggle
// -----------------------------
(function ensureAddOnUI() {
  if (document.getElementById("pamela-addon-panel")) return;

  const flyout = ensureAddOnFlyout();
  const host = document.getElementById("pamela-addon-body") || flyout;

  const panel = document.createElement("div");
  panel.id = "pamela-addon-panel";
  panel.style.cssText =
    "margin-top:10px;padding:10px;border:1px solid #ddd;border-radius:12px;background:#fff;" +
    "display:none;gap:10px;width:100%;box-sizing:border-box;";

  // Side toggle
  const sideRow = document.createElement("div");
  sideRow.style.cssText = "display:none;gap:8px;align-items:center;flex-wrap:wrap;";

  const pill = document.createElement("div");
  pill.id = "pamela-side-pill";
  pill.textContent = "FRONT";
  pill.style.cssText =
    "padding:6px 10px;border-radius:999px;border:1px solid #111;background:#111;color:#fff;" +
    "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;";

  const btnBack = document.createElement("button");
  btnBack.type = "button";
  btnBack.textContent = "View/Design Back (+$)";
  btnBack.style.cssText =
    "padding:8px 10px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
    "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

  const btnFront = document.createElement("button");
  btnFront.type = "button";
  btnFront.textContent = "Back to Front";
  btnFront.style.cssText =
    "padding:8px 10px;border-radius:10px;border:1px solid #ddd;background:#fff;color:#222;" +
    "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;display:none;";

  sideRow.appendChild(pill);
  sideRow.appendChild(btnBack);
  sideRow.appendChild(btnFront);
  panel.appendChild(sideRow);

  // Back engraving inputs + price labels
  const backBox = document.createElement("div");
  backBox.style.cssText = "display:none;gap:8px;border-top:1px solid #eee;padding-top:10px;";

  const backPriceLine = document.createElement("div");
  backPriceLine.id = "pamela-back-prices";
  backPriceLine.style.cssText = "font:900 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";

  const ln = document.createElement("input");
  ln.placeholder = "Back Last Name (not counted as words)";
  ln.style.cssText =
    "width:100%;padding:10px 12px;border:1px solid #ddd;border-radius:10px;" +
    "font:700 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;";

  const ins = document.createElement("textarea");
  ins.rows = 4;
  ins.placeholder = "Back inscription (multi-line)...";
  ins.style.cssText =
    "width:100%;padding:10px 12px;border:1px solid #ddd;border-radius:10px;" +
    "font:700 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;resize:vertical;";

  const wcRow = document.createElement("div");
  wcRow.style.cssText = "display:flex;justify-content:space-between;gap:8px;align-items:center;";
  const wcLeft = document.createElement("div");
  wcLeft.style.cssText = "font:900 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  const wcRight = document.createElement("div");
  wcRight.style.cssText = "font:900 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#b00020;";
  wcRow.appendChild(wcLeft);
  wcRow.appendChild(wcRight);

  backBox.appendChild(backPriceLine);
  backBox.appendChild(ln);
  backBox.appendChild(ins);
  backBox.appendChild(wcRow);
  panel.appendChild(backBox);

  // Vase engraving selector + qty + prices
  const vaseBox = document.createElement("div");
  vaseBox.id = "pamela-vase-box";
  vaseBox.style.cssText = "display:none;gap:6px;border-top:1px solid #eee;padding-top:10px;";

  const vaseTitle = document.createElement("div");
  vaseTitle.id = "pamela-vase-title";
  vaseTitle.style.cssText = "font:900 12px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  vaseBox.appendChild(vaseTitle);

  const vaseRow = document.createElement("div");
  vaseRow.style.cssText = "display:flex;gap:8px;align-items:center;";

  const vaseSel = document.createElement("select");
  vaseSel.style.cssText =
    "flex:1;padding:10px 12px;border:1px solid #ddd;border-radius:10px;" +
    "font:800 13px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;background:#fff;";

  [
    { v: "", t: "No vase engraving" },
    { v: "front", t: "Vase: Front" },
    { v: "back", t: "Vase: Back" },
    { v: "both", t: "Vase: Front + Back" }
  ].forEach(o => {
    const opt = document.createElement("option");
    opt.value = o.v;
    opt.textContent = o.t;
    vaseSel.appendChild(opt);
  });

  const vaseQty = document.createElement("input");
  vaseQty.type = "number";
  vaseQty.min = "1";
  vaseQty.step = "1";
  vaseQty.value = "1";
  vaseQty.style.cssText =
    "width:90px;padding:10px 12px;border:1px solid #ddd;border-radius:10px;" +
    "font:900 13px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;";

  vaseRow.appendChild(vaseSel);
  vaseRow.appendChild(vaseQty);
  vaseBox.appendChild(vaseRow);
  panel.appendChild(vaseBox);

  // ---------------------------------------------
  // Portraits (Porcelain) Ã¢â‚¬â€ dropdown (NO qty/buttons yet)
  // ---------------------------------------------
  const porBox = document.createElement("div");
  porBox.style.cssText = "display:none;gap:8px;border-top:1px solid #eee;padding-top:10px;";

  const porHdr = document.createElement("div");
  porHdr.textContent = "Color Porcelain Portraits";
  porHdr.style.cssText = "font:900 18px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  porBox.appendChild(porHdr);

  const porNote = document.createElement("div");
  porNote.style.cssText = "font:700 12px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;";
  porNote.innerHTML =
    "Porcelain portraits can be added to any granite color. Prices vary by shape and size. They are recessed into the granite for long-lasting durability. See terms and conditions for details and tips for ensuring the best .";
  porBox.appendChild(porNote);

  const heicLabel = document.createElement("label");
  heicLabel.style.cssText =
    "display:flex;gap:8px;align-items:flex-start;" +
    "font:800 12px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  heicLabel.innerHTML = `
    <input type="checkbox" id="cm_portrait_heicAck_compact" style="margin-top:2px;">
    <span><b>HEIC notice:</b> HEIC is allowed, but may look grainy/pixelated after conversion. Check this box before uploading HEIC.</span>
  `;
  porBox.appendChild(heicLabel);

  const porSel = document.createElement("select");
  porSel.id = "cm_porcelain_dropdown_compact";
  porSel.style.cssText =
    "width:100%;padding:10px 12px;border:1px solid #ddd;border-radius:10px;" +
    "font:800 13px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;background:#fff;";
  porBox.appendChild(porSel);

  panel.appendChild(porBox);

  // ---------------------------------------------
  // Laser etching - Jet Black only
  // ---------------------------------------------
  const laserBox = document.createElement("div");
  laserBox.id = "pamela-laser-box";
  laserBox.style.cssText = "display:none;gap:10px;";

  const laserHdr = document.createElement("div");
  laserHdr.textContent = "Laser Etching";
  laserHdr.style.cssText = "font:900 18px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  laserBox.appendChild(laserHdr);

  const laserIntro = document.createElement("div");
  laserIntro.style.cssText = "font:800 15px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;";
  laserIntro.innerHTML =
    "Laser etching can be added only to Jet Black granite. Minimum cost is $190 and final price depends on size and type.";
  laserBox.appendChild(laserIntro);

  const laserSideRow = document.createElement("div");
  laserSideRow.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:6px;flex-wrap:nowrap;";
  const laserSideQuestion = document.createElement("div");
  laserSideQuestion.textContent = "Will this laser etching be on front or back?";
  laserSideQuestion.style.cssText = "font:900 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;white-space:normal;min-width:0;";
  const laserSideBtns = document.createElement("div");
  laserSideBtns.style.cssText = "display:flex;gap:6px;flex:0 0 auto;";
  const mkLaserSideBtn = (side, label) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.dataset.laserSide = side;
    btn.textContent = label;
    btn.style.cssText = "padding:8px 11px;border-radius:999px;border:1px solid #111;background:#fff;color:#111;font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";
    btn.addEventListener("click", async () => {
      try {
        if (typeof window.switchToSide === "function") await window.switchToSide(side);
        else if (typeof window.setSide === "function") window.setSide(side);
      } catch (_) {}
      try { window.PAMELA_SIDES.current = side; } catch (_) {}
      try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}
    });
    return btn;
  };
  laserSideBtns.appendChild(mkLaserSideBtn("front", "Front"));
  laserSideBtns.appendChild(mkLaserSideBtn("back", "Back"));
  laserSideRow.appendChild(laserSideQuestion);
  laserSideRow.appendChild(laserSideBtns);
  laserBox.appendChild(laserSideRow);

  const laserEnableLabel = document.createElement("label");
  laserEnableLabel.style.cssText =
    "display:none;";
  laserEnableLabel.innerHTML = `<input type="checkbox" id="pamela_laser_enabled"> <span>Add laser etching</span>`;
  laserBox.appendChild(laserEnableLabel);

  const laserFields = document.createElement("div");
  laserFields.id = "pamela-laser-fields";
  laserFields.style.cssText = "display:grid;gap:8px;";

  // Pricing config used to label both the kind and source dropdowns directly,
  // so the standalone price summary box is no longer needed.
  const laserPricingCfg = window.PAMELA_ADDON_CONFIG?.laserEtching || {};
  const laserCurrency = window.PAMELA_ADDON_CONFIG?.currencySymbol || "$";
  const laserMoney = (amount) => `${laserCurrency}${Number(amount || 0).toFixed(2)}`;
  const laserMinimums = laserPricingCfg.minimums || {};

  const laserKind = document.createElement("select");
  laserKind.id = "pamela_laser_kind";
  laserKind.style.cssText =
    "width:100%;padding:12px 13px;border:1px solid #ddd;border-radius:10px;" +
    "font:900 15px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;background:#fff;";
  [
    { v: "", t: "Choose laser etched option" },
    { v: "spot", t: `Small Spot - Not People: From ${laserMoney(Math.min(laserMinimums.spotLibrary || 0, laserMinimums.spotCustom || 0))}` },
    { v: "portrait", t: `Portrait etching: From ${laserMoney(laserMinimums.portrait)}` },
    { v: "scene", t: `Scene etching: From ${laserMoney(Math.min(laserMinimums.sceneLibrary || 0, laserMinimums.sceneCustom || 0))}` }
  ].forEach(o => {
    const opt = document.createElement("option");
    opt.value = o.v;
    opt.textContent = o.t;
    laserKind.appendChild(opt);
  });
  laserFields.appendChild(laserKind);

  const laserSource = document.createElement("select");
  laserSource.id = "pamela_laser_source";
  laserSource.style.cssText = laserKind.style.cssText;
  laserFields.appendChild(laserSource);

  // The source dropdown's prices depend on which kind is currently selected
  // (spot vs scene have different library/upload minimums), so its options
  // are rebuilt every time the kind changes rather than being static.
  const rebuildLaserSourceOptions = () => {
    const kind = String(laserKind.value || "spot");
    const libraryMin = kind === "scene" ? laserMinimums.sceneLibrary : laserMinimums.spotLibrary;
    const customMin = kind === "scene" ? laserMinimums.sceneCustom : laserMinimums.spotCustom;
    const previousValue = laserSource.value;
    laserSource.innerHTML = "";
    [
      { v: "", t: "Please choose artwork source" },
      { v: "library", t: `Monument Library: From ${laserMoney(libraryMin)}` },
      { v: "custom", t: `Upload your own: From ${laserMoney(customMin)}` }
    ].forEach(o => {
      const opt = document.createElement("option");
      opt.value = o.v;
      opt.textContent = o.t;
      laserSource.appendChild(opt);
    });
    laserSource.value = previousValue || "";
  };
  rebuildLaserSourceOptions();

  const laserActionRow = document.createElement("div");
  laserActionRow.style.cssText = "display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;";

  const laserLibraryBtn = document.createElement("button");
  laserLibraryBtn.type = "button";
  laserLibraryBtn.id = "pamela_laser_library_btn";
  laserLibraryBtn.textContent = "Open Laser Library";
  laserLibraryBtn.style.cssText =
    "flex:1 1 160px;padding:10px 12px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
    "font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

  const laserUploadBtn = document.createElement("button");
  laserUploadBtn.type = "button";
  laserUploadBtn.id = "pamela_laser_upload_btn";
  laserUploadBtn.textContent = "Upload Laser Artwork";
  laserUploadBtn.style.cssText =
    "flex:1 1 160px;padding:10px 12px;border-radius:10px;border:1px solid #111;background:#111;color:#fff;" +
    "font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;";

  const laserCropBtn = document.createElement("button");
  laserCropBtn.type = "button";
  laserCropBtn.id = "pamela_laser_crop_btn";
  laserCropBtn.textContent = "Crop Selected";
  laserCropBtn.style.cssText =
    "min-width:0;padding:9px 8px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
    "font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;";

  const laserFitStoneBtn = document.createElement("button");
  laserFitStoneBtn.type = "button";
  laserFitStoneBtn.id = "pamela_laser_fit_stone_btn";
  laserFitStoneBtn.textContent = "Fit to Stone";
  laserFitStoneBtn.style.cssText =
    "min-width:0;padding:9px 8px;border-radius:10px;border:1px solid #111;background:#fff;color:#111;" +
    "font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;";

  laserLibraryBtn.style.display = "none";
  laserUploadBtn.style.display = "none";

  const laserFadeDownBtn = document.createElement("button");
  laserFadeDownBtn.type = "button";
  laserFadeDownBtn.id = "pamela_laser_fade_down_btn";
  laserFadeDownBtn.textContent = "Reduce Feather";
  laserFadeDownBtn.style.cssText = laserCropBtn.style.cssText;

  const laserFadeUpBtn = document.createElement("button");
  laserFadeUpBtn.type = "button";
  laserFadeUpBtn.id = "pamela_laser_fade_up_btn";
  laserFadeUpBtn.textContent = "Feather Edges";
  laserFadeUpBtn.style.cssText =
    "min-width:0;padding:9px 8px;border-radius:10px;border:1px solid #111;background:#111;color:#fff;" +
    "font:900 12px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;white-space:nowrap;";

  laserActionRow.appendChild(laserFitStoneBtn);
  laserActionRow.appendChild(laserCropBtn);
  laserActionRow.appendChild(laserFadeUpBtn);
  laserActionRow.appendChild(laserFadeDownBtn);
  pamelaMoveButtonToImagesToolbar(laserFitStoneBtn);
  pamelaMoveButtonToImagesToolbar(laserCropBtn);
  pamelaMoveButtonToImagesToolbar(laserFadeUpBtn);
  pamelaMoveButtonToImagesToolbar(laserFadeDownBtn);
  laserFields.appendChild(laserActionRow);

  const laserToolbarHint = document.createElement("div");
  laserToolbarHint.textContent = "Main toolbar has move, rotate, mirror, resize, and delete.";
  laserToolbarHint.style.cssText = "font:800 13px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;white-space:normal;";
  laserFields.appendChild(laserToolbarHint);

  const laserSizeRow = document.createElement("div");
  laserSizeRow.style.cssText = "display:grid;grid-template-columns:1fr 84px 84px;gap:6px;align-items:center;";
  const laserSizeLabel = document.createElement("div");
  laserSizeLabel.textContent = "Approximate Size of Selected Laser Image";
  laserSizeLabel.style.cssText = "font:900 13px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;";

  const laserWidth = document.createElement("input");
  laserWidth.id = "pamela_laser_width";
  laserWidth.type = "number";
  laserWidth.min = "0";
  laserWidth.step = "0.25";
  laserWidth.placeholder = "Width in.";
  laserWidth.readOnly = true;
  laserWidth.style.cssText =
    "width:100%;padding:10px 9px;border:1px solid #ddd;border-radius:10px;" +
    "font:900 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;";

  const laserHeight = document.createElement("input");
  laserHeight.id = "pamela_laser_height";
  laserHeight.type = "number";
  laserHeight.min = "0";
  laserHeight.step = "0.25";
  laserHeight.placeholder = "Height in.";
  laserHeight.readOnly = true;
  laserHeight.style.cssText = laserWidth.style.cssText;

  laserSizeRow.appendChild(laserSizeLabel);
  laserSizeRow.appendChild(laserWidth);
  laserSizeRow.appendChild(laserHeight);
  laserFields.appendChild(laserSizeRow);

  const laserFacesRow = document.createElement("div");
  laserFacesRow.style.cssText = "display:none;gap:6px;";
  const laserFacesLabel = document.createElement("label");
  laserFacesLabel.textContent = "How many people should be included in this laser etching?";
  laserFacesLabel.style.cssText = "font:800 12px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;";
  const laserFaces = document.createElement("input");
  laserFaces.id = "pamela_laser_faces";
  laserFaces.type = "number";
  laserFaces.min = "1";
  laserFaces.step = "1";
  laserFaces.value = "1";
  laserFaces.style.cssText = laserWidth.style.cssText;
  laserFacesRow.appendChild(laserFacesLabel);
  laserFacesRow.appendChild(laserFaces);
  laserFields.appendChild(laserFacesRow);

  const laserFacesNote = document.createElement("div");
  laserFacesNote.id = "pamela_laser_faces_note";
  laserFacesNote.style.cssText = "display:none;font:800 11px/1.25 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#555;";
  laserFields.appendChild(laserFacesNote);

  const laserPeopleNotice = document.createElement("div");
  laserPeopleNotice.id = "pamela_laser_people_notice";
  laserPeopleNotice.style.cssText = "display:none;padding:9px 10px;border-radius:10px;background:#fff7e6;border:1px solid #f0c36a;font:800 11px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#5c3900;";
  laserPeopleNotice.textContent = "Small spots are for simple items like crosses, flowers, rings, animals, cars, or symbols. Photos with people should use Portrait etching. Full backgrounds should use Scene etching.";
  laserFields.appendChild(laserPeopleNotice);

  const laserMessage = document.createElement("div");
  laserMessage.id = "pamela_laser_message";
  laserMessage.style.cssText = "font:800 12px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#7a4b00;";
  laserFields.appendChild(laserMessage);

  laserBox.appendChild(laserFields);
  const legacyScroller = document.getElementById("pamela-addon-scroller");
  const makeAccordion = window.PAMELA_makeAddonAccordionSection;
  const laserAccordion = (legacyScroller && typeof makeAccordion === "function")
    ? makeAccordion("Laser Etching", "laser", [laserBox])
    : null;
  if (laserAccordion && legacyScroller) {
    const porcelainSection = legacyScroller.querySelector('[data-pamela-addon-section="porcelain"]');
    if (porcelainSection && porcelainSection.nextSibling) {
      legacyScroller.insertBefore(laserAccordion, porcelainSection.nextSibling);
    } else if (porcelainSection) {
      legacyScroller.appendChild(laserAccordion);
    } else {
      legacyScroller.insertBefore(laserAccordion, legacyScroller.firstChild);
    }
  } else {
    panel.appendChild(laserBox);
  }

  const readLaserState = () => {
    const ed = getPamelaEditorInstance();
    const totals = getLaserArtworkTotals(ed);
    const pricingItems = getLaserArtworkPricingItemsForAllSides(ed, window.PAMELA_ADDON_CATALOG || {});
    const enabled = pricingItems.length > 0;
    const kind = String(document.getElementById("pamela_laser_kind")?.value || "");
    const source = kind === "portrait"
      ? "custom"
      : String(document.getElementById("pamela_laser_source")?.value || "");
    const widthIn = Number(document.getElementById("pamela_laser_width")?.value || 0);
    const heightIn = Number(document.getElementById("pamela_laser_height")?.value || 0);
    const faces = Math.max(1, parseInt(document.getElementById("pamela_laser_faces")?.value || "1", 10) || 1);
    return {
      enabled,
      kind,
      source,
      widthIn,
      heightIn,
      areaSqIn: totals.totalArea > 0 ? totals.totalArea : 0,
      artworkCount: totals.count,
      items: pricingItems,
      faces
    };
  };

  const refreshLaserUi = () => {
    const selectedVariant = window.__PAMELA_SELECTED_VARIANT__ || null;
    const eligible = isLaserEtchingVariantEligible(selectedVariant);
    panel.style.display = "none";
    if (laserAccordion) laserAccordion.style.display = eligible ? "" : "none";
    laserBox.style.display = eligible ? "grid" : "none";
    if (!eligible) {
      const en = document.getElementById("pamela_laser_enabled");
      if (en) en.checked = false;
    }

    const state = readLaserState();
    laserFields.style.display = eligible ? "grid" : "none";
    const edForLaserUi = getPamelaEditorInstance();
    const selectedLaser = getSelectedLaserArtworkNode(edForLaserUi);
    const hasSelectedLaser = !!selectedLaser;
    const selectedLaserKind = String(selectedLaser?.getAttr?.("pamelaLaserKind") || "").toLowerCase();
    const selectedLaserSource = String(selectedLaser?.getAttr?.("pamelaLaserPricingSource") || selectedLaser?.getAttr?.("pamelaLaserSource") || "").toLowerCase();
    const selectedFaces = Math.max(0, Number(selectedLaser?.getAttr?.("pamelaLaserFaces") || 0) || 0);
    const selectedCustomPeopleCandidate =
      selectedLaserSource === "custom" &&
      (selectedLaserKind === "scene" || selectedLaserKind === "spot");
    const showFaces = state.kind === "portrait" || selectedLaserKind === "portrait" || selectedCustomPeopleCandidate || selectedFaces > 0;
    laserFacesRow.style.display = showFaces ? "grid" : "none";
    laserFacesNote.style.display = showFaces ? "" : "none";
    laserSource.style.display = state.kind && state.kind !== "portrait" ? "" : "none";
    laserPeopleNotice.style.display = state.kind === "spot" ? "" : "none";
    laserLibraryBtn.style.display = "none";
    laserUploadBtn.style.display = eligible && (state.kind === "portrait" || (state.kind && state.source === "custom")) ? "" : "none";
    if (hasSelectedLaser) {
      try {
        const r = getLaserArtworkRectInches(selectedLaser, edForLaserUi);
        if (laserWidth && r.widthIn > 0) laserWidth.value = String(Math.round(r.widthIn * 100) / 100);
        if (laserHeight && r.heightIn > 0) laserHeight.value = String(Math.round(r.heightIn * 100) / 100);
        if (selectedLaserKind === "portrait" || selectedCustomPeopleCandidate || selectedFaces > 0) {
          const nodeFaces = selectedLaserKind === "portrait"
            ? Math.max(1, Number(selectedLaser.getAttr?.("pamelaLaserFaces") || 1) || 1)
            : Math.max(0, Number(selectedLaser.getAttr?.("pamelaLaserFaces") || 0) || 0);
          try { laserFaces.min = selectedLaserKind === "portrait" ? "1" : "0"; } catch (_) {}
          if (laserFaces && String(laserFaces.value || "") !== String(nodeFaces)) laserFaces.value = String(nodeFaces);
          const status = String(selectedLaser.getAttr?.("pamelaLaserFaceDetectionStatus") || "");
          const autoDetected = selectedLaser.getAttr?.("pamelaLaserFacesAutoDetected") === true;
          const manual = selectedLaser.getAttr?.("pamelaLaserFacesManualOverride") === true;
          const needsReview = selectedLaser.getAttr?.("pamelaLaserFacesNeedsReview") === true;
          const confirmed = selectedLaser.getAttr?.("pamelaLaserPeopleConfirmed") === true;
          const provider = String(selectedLaser.getAttr?.("pamelaLaserFaceDetectionProvider") || "");
          const reason = String(selectedLaser.getAttr?.("pamelaLaserFaceDetectionReason") || "");
          if (status === "uploading") {
            laserFacesNote.textContent = "Reading your image and looking for people. Please note this is an AI service; check the result for accuracy.";
          } else if (status === "detecting") {
            laserFacesNote.textContent = "Reading your image and looking for people. Please note this is an AI service; check the result for accuracy.";
          } else if (confirmed || manual) {
            laserFacesRow.style.display = "none";
            laserFacesNote.textContent = "";
            laserFacesNote.style.display = "none";
          } else if (status === "server") {
            laserFacesNote.textContent = needsReview
              ? `AI counted ${nodeFaces} ${nodeFaces === 1 ? "person" : "people"} but needs review. Confirm before adding to cart.`
              : `AI counted ${nodeFaces} ${nodeFaces === 1 ? "person" : "people"}${provider ? ` (${provider})` : ""}. Confirm before adding to cart.`;
          } else if (status === "server-unavailable") {
            laserFacesNote.textContent = `AI people count is unavailable${reason ? `: ${reason}` : ""}. Please confirm the people count.`;
          } else if (needsReview) {
            laserFacesNote.textContent = selectedLaserKind === "portrait"
              ? "Automatic people detection is unavailable here. Please confirm the number of people in this portrait."
              : "Automatic people detection is unavailable here. Enter 0 if there are no people, or enter the number of people in this uploaded image.";
          } else if (autoDetected) {
            laserFacesNote.textContent = selectedLaserKind === "portrait"
              ? `Detected ${nodeFaces} ${nodeFaces === 1 ? "person" : "people"} in selected portrait. Confirm before adding to cart.`
              : `Detected ${nodeFaces} ${nodeFaces === 1 ? "person" : "people"} in this ${selectedLaserKind || "laser"} image. Confirm before adding to cart.`;
          } else {
            laserFacesNote.textContent = selectedLaserKind === "portrait"
              ? "Automatic people detection is unavailable here. Please enter the number of people in this portrait."
              : "Automatic people detection is unavailable here. Enter people if this customer-uploaded image includes people.";
          }
        } else {
          laserFacesNote.textContent = "";
        }
      } catch (_) {}
    } else {
      try { laserWidth.value = ""; } catch (_) {}
      try { laserHeight.value = ""; } catch (_) {}
      if (showFaces) {
        laserFacesNote.textContent = "Upload/select a portrait etching to count people for that photo.";
      }
    }
    const selectedFeatherPx = Math.max(0, Math.round(Number(selectedLaser?.getAttr?.("pamelaLaserFeatherPx") || 0)));
    laserFitStoneBtn.style.display = hasSelectedLaser ? "" : "none";
    [laserFitStoneBtn, laserCropBtn, laserFadeUpBtn].forEach((btn) => {
      btn.disabled = !hasSelectedLaser;
      btn.style.opacity = hasSelectedLaser ? "1" : ".45";
      btn.style.cursor = hasSelectedLaser ? "pointer" : "not-allowed";
    });
    laserFadeDownBtn.disabled = !hasSelectedLaser || selectedFeatherPx <= 0;
    laserFadeDownBtn.style.opacity = (!hasSelectedLaser || selectedFeatherPx <= 0) ? ".45" : "1";
    laserFadeDownBtn.style.cursor = (!hasSelectedLaser || selectedFeatherPx <= 0) ? "not-allowed" : "pointer";
    laserFadeDownBtn.textContent = "Reduce Feather";
    laserFadeUpBtn.textContent = "Feather Edges";
    laserSideBtns.querySelectorAll("button").forEach((btn) => {
      const on = String(btn.dataset.laserSide || "") === String(window.PAMELA_SIDES?.current || "front");
      btn.style.background = on ? "#111" : "#fff";
      btn.style.color = on ? "#fff" : "#111";
    });

    const plan = getLaserPricePlan(state, window.PAMELA_ADDON_CATALOG || {});
    const laserItems = Array.isArray(state.items) ? state.items : [];
    if (!eligible) {
      laserMessage.textContent = "";
    } else if (hasSelectedLaser && String(selectedLaser.getAttr?.("pamelaLaserFaceDetectionStatus") || "") === "uploading") {
      const selectedKind = String(selectedLaser?.getAttr?.("pamelaLaserKind") || state.kind || "").toLowerCase();
      laserMessage.textContent = getLaserAiOverlayMessage(selectedKind).replace(" This can take a few seconds.", "...");
    } else if (!state.kind) {
      laserMessage.textContent = laserItems.length
        ? `${laserItems.length} laser image${laserItems.length === 1 ? "" : "s"} placed. Select another laser type to add more.`
        : "Choose a laser etching type to add artwork.";
    } else if (state.kind !== "portrait" && !state.source) {
      laserMessage.textContent = "Choose an artwork source to continue.";
    } else if (!(state.areaSqIn > 0) && (!(state.widthIn > 0) || !(state.heightIn > 0))) {
      laserMessage.textContent = state.source === "library"
        ? "Choose a laser design, then place and resize it on the monument."
        : "Upload laser artwork, then place and resize it on the monument.";
    } else if (plan.switchedToScene) {
      laserMessage.textContent = "This design is larger than a spot/portrait etching and will be priced as a scene.";
    } else {
      const totalExtra = laserItems.reduce((sum, it) => sum + Math.max(0, Number(it.plan?.extraArea || 0)), 0);
      laserMessage.textContent = `${laserItems.length} laser image${laserItems.length === 1 ? "" : "s"} placed. Extra billable area: ${totalExtra} sq in.`;
    }

    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    try {
      if (hasSelectedLaser) pamelaSyncMobileToolbars?.(edForLaserUi);
    } catch (_) {}
  };

  [
    laserEnableLabel.querySelector("input"),
    laserKind,
    laserSource,
    laserWidth,
    laserHeight
  ].forEach((el) => {
    if (!el) return;
    el.addEventListener("change", refreshLaserUi);
    el.addEventListener("input", refreshLaserUi);
  });

  const syncManualLaserFacesToSelected = () => {
    try {
      const node = getSelectedLaserArtworkNode(getPamelaEditorInstance());
      if (!node) {
        refreshLaserUi();
        return;
      }
      const kind = String(node.getAttr?.("pamelaLaserKind") || "").toLowerCase();
      const rawFaces = parseInt(laserFaces.value || (kind === "portrait" ? "1" : "0"), 10);
      const faces = kind === "portrait"
        ? Math.max(1, rawFaces || 1)
        : Math.max(0, Number.isFinite(rawFaces) ? rawFaces : 0);
      node.setAttr("pamelaLaserFaces", faces);
      node.setAttr("pamelaLaserFacesManualOverride", true);
      node.setAttr("pamelaLaserFacesNeedsReview", false);
      node.setAttr("pamelaLaserFaceDetectionStatus", "manual");
      node.setAttr("pamelaLaserPeopleConfirmed", true);
      try { pamelaSaveLaserArtworkSideState(node); } catch (_) {}
      try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    } catch (_) {}
    refreshLaserUi();
  };

  laserFaces.addEventListener("change", syncManualLaserFacesToSelected);
  laserFaces.addEventListener("input", syncManualLaserFacesToSelected);

  let suppressLaserAutoOpen = false;
  const resetLaserPickers = () => {
    suppressLaserAutoOpen = true;
    try { laserKind.value = ""; } catch (_) {}
    try { laserSource.value = ""; } catch (_) {}
    window.setTimeout(() => {
      suppressLaserAutoOpen = false;
      refreshLaserUi();
    }, 0);
  };

  const openLaserLibraryFromState = async () => {
    const type = String(laserKind.value || "") === "scene" ? "scene" : "spot";
    if (window.PAMELA_LASER_LIBRARY_UI?.open) {
      const targetSide = String(window.PAMELA_SIDES?.current || "front") === "back" ? "back" : "front";
      window.__PAMELA_LASER_LIBRARY_TARGET_SIDE__ = targetSide;
      await window.PAMELA_LASER_LIBRARY_UI.open({ type, side: targetSide });
      return;
    }
    alert("Laser library is not connected yet. Use upload for now.");
  };

  laserKind.addEventListener("change", async () => {
    if (suppressLaserAutoOpen) return;
    if (String(laserKind.value || "") === "portrait") {
      try {
        await window.PAMELA_openLaserArtworkUploader?.({
          laser_kind: "portrait",
          source: "custom",
          faces: Math.max(1, parseInt(laserFaces.value || "1", 10) || 1),
          side: window.PAMELA_SIDES?.current || "front"
        });
        resetLaserPickers();
      } catch (e) {
        console.warn("Laser portrait upload open failed:", e);
        alert(e?.message || "Could not open laser artwork uploader.");
      }
    } else {
      try { rebuildLaserSourceOptions(); } catch (_) {}
      try { laserSource.value = ""; } catch (_) {}
      refreshLaserUi();
    }
  });

  laserSource.addEventListener("change", async () => {
    if (suppressLaserAutoOpen) return;
    const source = String(laserSource.value || "");
    if (!source) return;
    try {
      if (source === "library") {
        await openLaserLibraryFromState();
      } else if (source === "custom") {
        await window.PAMELA_openLaserArtworkUploader?.({
          laser_kind: laserKind.value || "spot",
          source: "custom",
          faces: Math.max(1, parseInt(laserFaces.value || "1", 10) || 1),
          side: window.PAMELA_SIDES?.current || "front"
        });
      }
      resetLaserPickers();
    } catch (e) {
      console.warn("Laser source open failed:", e);
      alert(e?.message || "Could not open laser artwork.");
    }
  });

  laserUploadBtn.addEventListener("click", async () => {
    try {
      await window.PAMELA_openLaserArtworkUploader?.({
        laser_kind: laserKind.value || "spot",
        source: "custom",
        faces: Math.max(1, parseInt(laserFaces.value || "1", 10) || 1),
        side: window.PAMELA_SIDES?.current || "front"
      });
      resetLaserPickers();
    } catch (e) {
      console.warn("Laser upload open failed:", e);
      alert(e?.message || "Could not open laser artwork uploader.");
    }
  });

  laserCropBtn.addEventListener("click", () => {
    cropSelectedLaserArtwork();
  });

  laserFitStoneBtn.addEventListener("click", () => {
    window.fitSelectedLaserArtworkToStone();
  });

  const changeSelectedLaserFade = (delta) => {
    const node = getSelectedLaserArtworkNode(getPamelaEditorInstance());
    if (!node) {
      alert("Select a laser artwork image first.");
      return;
    }
    const current = Math.max(0, Math.round(Number(node.getAttr?.("pamelaLaserFeatherPx") || 0)));
    const next = Math.max(0, current + Number(delta || 0));
    if (setLaserNodeFeather(node, next)) {
      try { getPamelaEditorInstance()?._historyCommit?.("laser-feather"); } catch (_) {}
      syncLaserSizeFieldsFromArtwork(node);
      pamelaSaveLaserArtworkSideState(node);
    }
  };

  laserFadeDownBtn.addEventListener("click", () => changeSelectedLaserFade(-5));
  laserFadeUpBtn.addEventListener("click", () => changeSelectedLaserFade(5));

  laserLibraryBtn.addEventListener("click", async () => {
    try {
      await openLaserLibraryFromState();
      resetLaserPickers();
      return;
    } catch (e) {
      console.warn("Laser library open failed:", e);
    }
    alert("Laser library is not connected yet. Use Upload Laser Artwork for now.");
  });

  window.PAMELA_refreshLaserUi = refreshLaserUi;

  host.insertBefore(panel, host.firstChild || null);
  try { window.refreshAddonInfoVisibility?.(); } catch (_) {}
  try { window.PAMELA_refreshLaserUi?.(); } catch (_) {}

  // --- Load catalog + fill labels + fill porcelain dropdown ---
  (async () => {
    const CFG = window.PAMELA_ADDON_CONFIG;
    const cat = await loadAddonCatalogOnce();
    const sym = CFG.currencySymbol || "$";

    // Fill Porcelain dropdown
    porSel.innerHTML = "";
    const o0 = document.createElement("option");
    o0.value = "";
    o0.textContent = "Select a portrait...";
    porSel.appendChild(o0);

    (cat.porcelainVariants || []).forEach(v => {
      const opt = document.createElement("option");
      opt.value = String(v.id);
      opt.textContent = `${v.title} - ${moneyFromCents(v.price_cents, sym)}`;
      porSel.appendChild(opt);
    });

    // Back engraving label
    const chosen = getChosenVariantObj();
    const companion = isCompanionVariant(chosen);
    const included = companion ? CFG.includedWords.companion : CFG.includedWords.single;

    backPriceLine.textContent =
      `Back engraving: ${moneyFromCents(companion ? cat.backCompanion.price : cat.backSingle.price, sym)} ` +
      `| Extra words: ${moneyFromCents(cat.backWord.price, sym)} each | Included words: ${included}`;

    // Vase label
    vaseTitle.textContent =
      `Vase engraving: Front ${moneyFromCents(cat.vaseFront.price, sym)} / Back ${moneyFromCents(cat.vaseBack.price, sym)} (per vase, per side)`;

    // Word count refresh
    const refreshCounts = () => {
      const chosen2 = getChosenVariantObj();
      const companion2 = isCompanionVariant(chosen2);
      const included2 = companion2 ? CFG.includedWords.companion : CFG.includedWords.single;

      const wc = countWords(ins.value);
      wcLeft.textContent = `Words: ${wc} (included: ${included2})`;

      const extra = Math.max(0, wc - included2);
      wcRight.textContent = extra > 0 ? `Extra words: ${extra}` : "";

      backPriceLine.textContent =
        `Back engraving: ${moneyFromCents(companion2 ? cat.backCompanion.price : cat.backSingle.price, sym)} ` +
        `| Extra words: ${moneyFromCents(cat.backWord.price, sym)} each | Included words: ${included2}`;
    };

    ins.addEventListener("input", refreshCounts);
    selectEl?.addEventListener("change", refreshCounts);
    refreshCounts();
  })().catch(e => console.warn("Addon catalog UI:", e));

  // Side toggle actions
  btnBack.addEventListener("click", async () => {
    await switchToSide("back");
    pill.textContent = "BACK";
    btnBack.style.display = "none";
    btnFront.style.display = "inline-block";
    backBox.style.display = "grid";
    pamelaCloseAddonFlyoutOnMobile();
  });

  btnFront.addEventListener("click", async () => {
    await switchToSide("front");
    pill.textContent = "FRONT";
    btnBack.style.display = "inline-block";
    btnFront.style.display = "none";
    backBox.style.display = "none";
  });

  // Expose getters for finalize
  window.__PAMELA_ADDON_UI__ = {
    getSide: () => PAMELA_SIDES.current,
    getBackLastName: () => String(ln.value || "").trim(),
    getBackText: () => String(ins.value || "").trim(),
    getBackWordCounts: () => {
      const CFG = window.PAMELA_ADDON_CONFIG;
      const chosen = getChosenVariantObj();
      const companion = isCompanionVariant(chosen);
      const included = companion ? CFG.includedWords.companion : CFG.includedWords.single;
      const wc = countWords(ins.value);
      const extra = Math.max(0, wc - included);
      return { included, words: wc, extra, companion };
    },
    getVase: () => {
      const side = String(vaseSel.value || "");
      const qty = Math.max(1, parseInt(vaseQty.value || "1", 10) || 1);
      return { side, qty };
    },
    getLaser: () => readLaserState(),
getPorcelain: function () {
  try {
    var ed = window.pamelaMonumentEditor || window.pamelaEditor || window.editor || window.PAMELA_EDITOR || window.__PAMELA_EDITOR__;
    if (!ed || !ed.stage) return [];

    var st = ed.stage;
    if (!st || typeof st.find !== "function") return [];

    var groups = st.find("Group") || [];
    var portraits = groups.filter(function (g) {
      var a = (g && g.attrs) ? g.attrs : {};
      return !!(a && (a.pamelaType === "portrait" || a.__isPortraitGroup === true || a.pamelaPortrait === true));
    });

    var out = [];
    portraits.forEach(function (g) {
      var a = (g && g.attrs) ? g.attrs : {};
      var vid = a.__porcelainVariantId ? Number(a.__porcelainVariantId) : null;
      if (!vid) return;
      var label = a.__porcelainLabel ? String(a.__porcelainLabel) : "";
      out.push({ variantId: vid, qty: 1, label: label });
    });

    return out;
  } catch (e) {
    return [];
  }
},
    getHeicAck: () => {
      const el = document.getElementById("cm_portrait_heicAck");
      return !!(el && el.checked);
    }
  };
})();

// -----------------------------
// FINALIZE CLICK: adds main item + add-ons, saves BOTH sides for PDF
// -----------------------------

if (finalizeBtn) {
  finalizeBtn.addEventListener("click", async () => {
    let navigatingToCart = false;
    try {
      if (window.__CM_DRAFT_READONLY__ === true) {
        throw new Error("This design is read only because the order has already been placed.");
      }

      setFinalizeOverlay(true, "Saving your design and updating your cart...");

      const CFG = window.PAMELA_ADDON_CONFIG;
      if (!CFG) throw new Error("pamela-addon-config.js not loaded.");

      setFinalizeStatus("Loading add-on prices...");
      const cat = await loadAddonCatalogOnce();

      setFinalizeStatus("Finalizing...");
      finalizeBtn.disabled = true;

      const chosenVariantId = selectEl?.value;
      if (!chosenVariantId) throw new Error("No variant selected.");

      // Always save current side before finalize
      saveCurrentSideState();
      // Also ensure the OTHER side has at least a saved state if they ever switched
      // (If they never clicked back, back state may be null Ã¢â‚¬â€ thatÃ¢â‚¬â„¢s ok.)
      const cartEdit = getCartEditContext();
      let designId = "";

      // A cart edit is a replacement transaction. Its newly added lines must
      // have a new group ID; otherwise cleanup would match the replacement and
      // remove the entire cart group along with the old design.
      if (cartEdit.enabled && cartEdit.replaceDesignId) {
        window.cmDesignId = makeDesignId();
      }

      let draftToken = "";
      try {
        if (typeof window.CM_DRAFTS_SAVE_NOW === "function") {
        setFinalizeStatus("Saving your design...");
          const saveResult = await window.CM_DRAFTS_SAVE_NOW();
          if (!saveResult?.ok) {
            console.warn("Draft save before cart did not fully succeed:", saveResult);
          }
        }
      } catch (e) {
        console.warn("Draft save before cart failed:", e);
      }
      draftToken = String(window.cmDraftToken || "").trim();
      // Keep Shopify's parent design ID aligned with the saved draft. A new
      // editor session receives a new draft identity, so every distinct design
      // gets its own token/link while all of its add-ons remain grouped.
      designId = String(window.cmDesignId || "").trim() || makeDesignId();
      const editUrl = buildEditorReturnUrl({
        token: draftToken,
        replaceDesignId: designId
      });
      const resumeUrl = buildEditorReturnUrl({
        token: draftToken
      });

      // MAIN summary props (attach FRONT snapshot by default)
      const summaryObj = buildOrderSummary(editor);

      // We will store BOTH snapshots + BOTH manifests as hidden properties
      const frontSnap = PAMELA_SIDES.front.snapshot || null;
      const backSnap = PAMELA_SIDES.back.snapshot || null;

      let frontMan = buildProductionManifest(editor, "front") || null;
      let backMan = buildProductionManifest(editor, "back") || null;
      try {
        const laserNotesForManifest = getLaserArtworkPricingItemsForAllSides(editor, cat)
          .map((entry, idx) => {
            const note = String(entry?.notes || getLaserEntryAttr(entry, "pamelaLaserNotes", "") || "").trim();
            const side = entry?.side === "back" ? "Back" : "Front";
            const name = String(getLaserEntryAttr(entry, "pamelaLaserOriginalName", "") || "").trim();
            return note ? `Laser #${idx + 1} (${side}${name ? ` - ${name}` : ""}): ${note}` : "";
          })
          .filter(Boolean)
          .join(" | ");
        if (laserNotesForManifest) {
          if (frontMan && typeof frontMan === "object") frontMan.laser_notes = laserNotesForManifest;
          if (backMan && typeof backMan === "object") backMan.laser_notes = laserNotesForManifest;
        }
      } catch (_) {
      }

      [frontMan, backMan] = await Promise.all([
        window.CM_uploadCroppedPortraitsInManifest(frontMan),
        window.CM_uploadCroppedPortraitsInManifest(backMan)
      ]);
      if (typeof window.CM_uploadProcessedLasersInManifest === "function") {
        [frontMan, backMan] = await Promise.all([
          window.CM_uploadProcessedLasersInManifest(frontMan),
          window.CM_uploadProcessedLasersInManifest(backMan)
        ]);
      }
      if (typeof window.CM_uploadLaserCartThumbsForEditor === "function") {
        setFinalizeStatus("Uploading laser cart previews...");
        await window.CM_uploadLaserCartThumbsForEditor(editor);
      }

      let props = {
        
        "Layout": String(summaryObj.layout || ""),
        "Variant": String(summaryObj.variant_title || ""),

        "_cm_design_id": designId,
        "_Product Handle": String(summaryObj.product_handle || ""),
        "_Variant ID": String(summaryObj.variant_id || ""),
        "_Editor URL": resumeUrl,
        "_cm_editor_url": resumeUrl,
        "_cm_edit_url": editUrl,
        "_cm_draft_token": draftToken,
      };

      function writeB64Chunks(prefix, obj) {
        if (!obj) {
          props[`${prefix} Chunks`] = "0";
          return;
        }
        const json = JSON.stringify(obj);
        const b64 = b64EncodeUnicode(json);
        const chunks = chunkString(b64, 900);
        props[`${prefix} Encoding`] = "base64";
        props[`${prefix} Chunks`] = String(chunks.length);
        chunks.forEach((c, i) => (props[`${prefix} ${i + 1}`] = c));
      }

      writeB64Chunks("_Front Snapshot", frontSnap);
      writeB64Chunks("_Back Snapshot", backSnap);
      writeB64Chunks("_Front Manifest", frontMan);
      writeB64Chunks("_Back Manifest", backMan);

      // Ã¢Å“â€¦ ADD PORTRAIT PAYLOAD TO MAIN ITEM PROPERTIES
      // (uses wrapper attrs: __portraitUrl, plus photo x/y/scale/rotation)
      try {
        if (window.CM_addPortraitProps) {
          props = window.CM_addPortraitProps(props, editor);
        }
      } catch (e) {
        console.warn("Portrait props failed:", e);
      }

// Add-ons chosen 
      const ui = window.__PAMELA_ADDON_UI__ || {};

      // Prefer flyout values if they exist, but we will ALSO detect back text from the BACK canvas layer
      const backLastName = ui.getBackLastName ? String(ui.getBackLastName() || "").trim() : "";
      const backText = ui.getBackText ? String(ui.getBackText() || "").trim() : "";

      // ---- Back canvas detection (counts Text nodes on the BACK content layer) ----
      const ed =
        (typeof editor !== "undefined" && editor) ||
        window.pamelaMonumentEditor ||
        window.pamelaEditor ||
        window.editor ||
        window.PAMELA_EDITOR ||
        window.__PAMELA_EDITOR__ ||
        null;

      const backContent =
        (ed && ed.layers && ed.layers.sides && ed.layers.sides.back && ed.layers.sides.back.content) ||
        null;
      const backPanels =
        (ed && ed.layers && ed.layers.sides && ed.layers.sides.back && ed.layers.sides.back.panels) ||
        null;

      const backTextCount = (function () {
        try {
          if (!backContent || typeof backContent.getChildren !== "function") return 0;
          const kids = backContent.getChildren() || [];
          return kids.filter(n => n && (n.className === "Text" || (n.getClassName && n.getClassName() === "Text"))).length;
        } catch (_) {
          return 0;
        }
      })();

      const backArtCount = (function () {
        let count = 0;
        try {
          if (backContent && typeof backContent.getChildren === "function") {
            const kids = backContent.getChildren() || [];
            count += kids.filter(n => n && (n.className === "Image" || (n.getClassName && n.getClassName() === "Image"))).length;
          }
        } catch (_) {}
        try {
          if (backPanels && typeof backPanels.getChildren === "function") {
            count += (backPanels.getChildren() || []).length;
          }
        } catch (_) {}
        return count;
      })();

      // Keep your existing word-count logic if provided; otherwise compute from backText
      const backCounts = (function () {
        try {
          if (ui.getBackWordCounts && typeof ui.getBackWordCounts === "function") return ui.getBackWordCounts();
        } catch (_) {}
        const CFG = window.PAMELA_ADDON_CONFIG || {};
        const chosen = (typeof getChosenVariantObj === "function") ? getChosenVariantObj() : null;
        const companion = (typeof isCompanionVariant === "function") ? !!isCompanionVariant(chosen) : false;
        const included = companion
          ? Number((CFG.includedWords && CFG.includedWords.companion) != null ? CFG.includedWords.companion : 20)
          : Number((CFG.includedWords && CFG.includedWords.single) != null ? CFG.includedWords.single : 10);

        const wc = (typeof countWords === "function")
          ? countWords(backText)
          : (backText ? backText.trim().split(/\s+/).filter(Boolean).length : 0);

        const extra = Math.max(0, wc - included);
        return { included, words: wc, extra, companion };
      })();

      // Capture latest state before deciding which cart add-ons exist.
      try { saveCurrentSideState && saveCurrentSideState(); } catch (_) {}

      const storedBackState = (function () {
        try {
          return (window.PAMELA_SIDES && window.PAMELA_SIDES.back && window.PAMELA_SIDES.back.draft_state) || null;
        } catch (_) {
          return null;
        }
      })();

      const storedBackTexts = Array.isArray(storedBackState && storedBackState.texts)
        ? storedBackState.texts
        : [];

      const storedBackInscription = storedBackTexts
        .map((entry) => {
          const attrs = (entry && entry.attrs && typeof entry.attrs === "object") ? entry.attrs : {};
          const fieldType = String(
            (entry && (entry.pamelaFieldType || entry.fieldType || entry.type)) ||
            attrs.pamelaFieldType ||
            attrs.fieldType ||
            attrs.type ||
            ""
          ).trim();
          if (fieldType === "lastname") return "";
          return String(entry && entry.text || "").replace(/\s+/g, " ").trim();
        })
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      const storedBackLastName = storedBackTexts
        .map((entry) => {
          const attrs = (entry && entry.attrs && typeof entry.attrs === "object") ? entry.attrs : {};
          const fieldType = String(
            (entry && (entry.pamelaFieldType || entry.fieldType || entry.type)) ||
            attrs.pamelaFieldType ||
            attrs.fieldType ||
            attrs.type ||
            ""
          ).trim();
          return fieldType === "lastname" ? String(entry && entry.text || "").replace(/\s+/g, " ").trim() : "";
        })
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      const hasStoredBackText = storedBackTexts.some((entry) => {
        return String(entry && entry.text || "").replace(/\s+/g, " ").trim() !== "";
      });

      const hasStoredBackObjects = (() => {
        try {
          if (!storedBackState || typeof storedBackState !== "object") return false;
          return (
            (Array.isArray(storedBackState.panels) && storedBackState.panels.length > 0) ||
            (Array.isArray(storedBackState.clipart) && storedBackState.clipart.length > 0) ||
            (Array.isArray(storedBackState.portraits) && storedBackState.portraits.length > 0) ||
            (Array.isArray(storedBackState.laser) && storedBackState.laser.length > 0)
          );
        } catch (_) {
          return false;
        }
      })();

      // Back exists if flyout state, stored state, snapshot, or canvas content says it exists.
      const wantsBack = (function () {
        try {
          if (!!backLastName || !!backText || hasStoredBackText || hasStoredBackObjects) return true;
          if (window.PAMELA_SIDES && window.PAMELA_SIDES.back && window.PAMELA_SIDES.back.snapshot != null) return true;
          if (backTextCount > 0) return true;
          if (backArtCount > 0) return true;
        } catch (_) {}
        return false;
      })();

      // IMPORTANT: capture latest state (including portrait transforms) before building items
      try { saveCurrentSideState && saveCurrentSideState(); } catch (_) {}

      let cartBackThumb = "";
      let cartBackThumbLocalKey = "";
      try {
        const frontThumbData = await pamelaGetCartThumbForSide("front", { width: 360, force: true });
        const backThumbData = wantsBack
          ? await pamelaGetCartThumbForSide("back", { width: 360, force: true })
          : "";

        const uploadDesignThumb =
          typeof window.CM_uploadCartDesignThumbPng === "function"
            ? window.CM_uploadCartDesignThumbPng
            : null;
        let frontThumb = uploadDesignThumb
          ? await uploadDesignThumb(frontThumbData, "front")
          : frontThumbData;
        if (!frontThumb && uploadDesignThumb && frontThumbData) {
          const smallerFrontThumb = await resizeThumbDataUrl(frontThumbData, 240);
          frontThumb = await uploadDesignThumb(smallerFrontThumb, "front-small");
        }

        let backThumb = backThumbData && uploadDesignThumb
          ? await uploadDesignThumb(backThumbData, "back")
          : backThumbData;
        if (!backThumb && uploadDesignThumb && backThumbData) {
          const smallerBackThumb = await resizeThumbDataUrl(backThumbData, 240);
          backThumb = await uploadDesignThumb(smallerBackThumb, "back-small");
        }

        const frontLocalKey = pamelaStoreLocalCartThumb(designId, "front", frontThumbData);
        const backLocalKey = backThumbData ? pamelaStoreLocalCartThumb(designId, "back", backThumbData) : "";

        if (!frontThumb || (uploadDesignThumb && !isCartSafeImageReference(frontThumb))) {
          frontThumb = "";
        }

        if (frontThumb) {
          props["_Front Thumb"] = frontThumb;
          props["_Design Thumb"] = frontThumb;
        }
        if (frontLocalKey) {
          props["_Front Thumb Local Key"] = frontLocalKey;
        }
        if (backThumb) {
          cartBackThumb = backThumb;
          props["_Back Thumb"] = backThumb;
        }
        if (backLocalKey) {
          cartBackThumbLocalKey = backLocalKey;
          props["_Back Thumb Local Key"] = backLocalKey;
        }
      } catch (thumbErr) {
        console.warn("Cart thumbnail capture failed; cart will use standard product image fallback:", thumbErr);
      }

      const vase = ui.getVase ? ui.getVase() : { side: "", qty: 1 };
      const vaseSide = String(vase.side || "");
      const vaseQty = Math.max(1, Number(vase.qty || 1));

      // --- PORCELAIN (multi) ---
      let porcelainList = (ui && typeof ui.getPorcelain === "function")
        ? ui.getPorcelain()
        : [];
      let stagePortraits = [];
      const manifestPortraits = [frontMan, backMan].flatMap((manifest) => {
        const portraits = Array.isArray(manifest && manifest.portraits) ? manifest.portraits : [];
        return portraits.filter(Boolean);
      });

      // Fallback: if flyout/UI state lost the portrait list, rebuild it from the stage
      if (
        (!porcelainList || (Array.isArray(porcelainList) && porcelainList.length === 0)) &&
        (typeof window.CM_collectPortraitAssetsFromStage === "function" || manifestPortraits.length)
      ) {
        try {
          stagePortraits = typeof window.CM_collectPortraitAssetsFromStage === "function"
            ? (window.CM_collectPortraitAssetsFromStage(editor) || [])
            : [];
          const portraitSource = stagePortraits.length ? stagePortraits : manifestPortraits;
          porcelainList = portraitSource.map((p) => ({
            variantId:
              (p && p.variantId != null) ? p.variantId :
              (p && p.variant_id != null) ? p.variant_id :
              (p && p.meta && p.meta.variant_id != null) ? p.meta.variant_id :
              (p && p.__porcelainVariantId != null) ? p.__porcelainVariantId :
              null,
            qty: 1,
            label:
              (p && p.label) ? String(p.label) :
              (p && p.__porcelainLabel) ? String(p.__porcelainLabel) :
              "Porcelain Portrait",
            thumb: String(
              (p && p.croppedUrl) ||
              (p && p.croppedDataUrl) ||
              (p && p.url) ||
              ""
            )
          }));
        } catch (e) {
          console.warn("Porcelain stage fallback failed:", e);
        }
      }

      if (
        (!Array.isArray(stagePortraits) || !stagePortraits.length) &&
        typeof window.CM_collectPortraitAssetsFromStage === "function"
      ) {
        try {
          stagePortraits = window.CM_collectPortraitAssetsFromStage(editor) || [];
        } catch (e) {
          console.warn("Porcelain stage collect failed:", e);
          stagePortraits = [];
        }
      }

      // Normalize to array of {variantId, qty, label}
      const porcelainItems = Array.isArray(porcelainList)
        ? porcelainList
        : (porcelainList && porcelainList.variantId ? [porcelainList] : []);

      const cleanPorcelain = porcelainItems
        .map(p => {
          const vid = (p && p.variantId != null && String(p.variantId).trim() !== "") ? Number(p.variantId) : null;
          const qty = Math.max(1, Number((p && p.qty) || 1));
          const label = (p && p.label) ? String(p.label) : "";
          const thumb = String(
            (p && p.thumb) ||
            (p && p.croppedUrl) ||
            (p && p.croppedDataUrl) ||
            (p && p.url) ||
            ""
          ).trim();
          if (!vid) return null;
          return { variantId: vid, qty, label, thumb };
        })
        .filter(Boolean);

      const portraitThumbSource = manifestPortraits.length
        ? manifestPortraits
        : stagePortraits;

      const activePortraits = portraitThumbSource.filter((p) => {
        if (!p || typeof p !== "object") return false;
        const hasPhoto = p.hasPhoto === true;
        const hasUrl = String(p.url || "").trim() !== "";
        const hasCropped = String(p.croppedDataUrl || p.croppedUrl || "").trim() !== "";
        return hasPhoto || hasUrl || hasCropped;
      });

      const unpricedPortraits = activePortraits.filter((p) => {
        const variantId =
          (p && p.variant_id != null) ? Number(p.variant_id) :
          (p && p.variantId != null) ? Number(p.variantId) :
          (p && p.meta && p.meta.variant_id != null) ? Number(p.meta.variant_id) :
          0;
        return !(variantId > 0);
      });

      if (unpricedPortraits.length) {
        throw new Error(
          "A portrait was added to the design, but its portrait option has not been fully saved yet. Please wait a moment and try Finalize & Add to Cart again."
        );
      }

      if (activePortraits.length && cleanPorcelain.length < activePortraits.length) {
        throw new Error(
          "A portrait on the design is missing its cart add-on. Please reopen the portrait selection and try Finalize & Add to Cart again."
        );
      }

      const addonProps = (kind, side) => ({
        "_cm_addon": "required",
        "_cm_addon_kind": String(kind || ""),
        "_cm_addon_side": side === "back" ? "back" : (side === "front" ? "front" : ""),
        "_cm_parent_design_id": designId,
        "_cm_parent_variant_id": String(chosenVariantId),
        "_cm_editor_url": editUrl,
        "_cm_edit_url": editUrl,
        "_cm_draft_token": draftToken
      });

      const items = [];
      items.push({ id: Number(chosenVariantId), quantity: 1, properties: props });
      // Porcelain portraits (each portrait = separate line item)
      if (cleanPorcelain && cleanPorcelain.length) {
        cleanPorcelain.forEach((p, idx) => {
          items.push({
            id: Number(p.variantId),
            quantity: Number(p.qty || 1),
            properties: {
              ...addonProps("porcelain_portrait", "front"),
              "Porcelain Portrait": String(p.label || "Porcelain Portrait"),
              "Portrait #": String(idx + 1),
              "_Portrait Thumb": String(
                p.thumb ||
                activePortraits[idx]?.croppedUrl ||
                activePortraits[idx]?.croppedDataUrl ||
                activePortraits[idx]?.url ||
                ""
              )
            }
          });
        });
      }

      // Laser etching
      const laserState = ui.getLaser ? ui.getLaser() : { enabled: false };
      if (laserState?.enabled) {
        const selectedVariant = getChosenVariantObj();
        if (!isLaserEtchingVariantEligible(selectedVariant)) {
          throw new Error("Laser etching is only available on Jet Black granite.");
        }
        const laserEditor = getPamelaEditorInstance();
        const laserPricingItems = getLaserArtworkPricingItemsForAllSides(laserEditor, cat);
        if (!laserPricingItems.length) {
          throw new Error("Please upload or choose laser artwork and place it on the monument before adding to cart.");
        }
        const needsFaceReview = laserPricingItems.filter((entry) => {
          const source = String(entry?.source || getLaserEntryAttr(entry, "pamelaLaserPricingSource", "") || "").toLowerCase();
          if (source !== "custom") return false;
          const kind = String(entry?.kind || getLaserEntryAttr(entry, "pamelaLaserKind", "") || "").toLowerCase();
          if (kind !== "portrait" && kind !== "scene" && kind !== "spot") return false;
          const status = String(getLaserEntryAttr(entry, "pamelaLaserFaceDetectionStatus", "") || "").toLowerCase();
          const manual = getLaserEntryAttr(entry, "pamelaLaserFacesManualOverride", false) === true ||
            String(getLaserEntryAttr(entry, "pamelaLaserFacesManualOverride", "")).toLowerCase() === "true";
          const confirmed = getLaserEntryAttr(entry, "pamelaLaserPeopleConfirmed", false) === true ||
            String(getLaserEntryAttr(entry, "pamelaLaserPeopleConfirmed", "")).toLowerCase() === "true";
          const needsReview = getLaserEntryAttr(entry, "pamelaLaserFacesNeedsReview", false) === true ||
            String(getLaserEntryAttr(entry, "pamelaLaserFacesNeedsReview", "")).toLowerCase() === "true";
          const people = Math.max(0, Number(entry?.faces || getLaserEntryAttr(entry, "pamelaLaserFaces", 0) || 0) || 0);
          const requiresPeopleConfirmation = kind === "portrait" || (kind === "scene" && people > 2) || (kind === "spot" && people > 0);
          return (!confirmed && requiresPeopleConfirmation) || (kind !== "scene" && !manual && (needsReview || status === "unavailable" || status === "detecting" || !status));
        });
        if (needsFaceReview.length) {
          throw new Error("Please select each customer-uploaded laser image that needs review before adding to cart. Portraits and spots with people need a confirmed people count; scenes with more than two detected people need collage/group review.");
        }
        const laserArtworkUrls = laserPricingItems
          .map((entry) => getCartSafeImageReference(
            getLaserEntryAttr(entry, "pamelaLaserUrl", ""),
            entry?.item?.src,
            entry?.item?.url
          ))
          .filter(Boolean)
          .join(" | ");
        const laserArtworkNames = laserPricingItems
          .map((entry) => String(getLaserEntryAttr(entry, "pamelaLaserOriginalName", "")))
          .filter(Boolean)
          .join(" | ");
        const laserFeatherValues = laserPricingItems
          .map((entry) => String(Math.max(0, Math.round(Number(getLaserEntryAttr(entry, "pamelaLaserFeatherPx", 0) || 0)))))
          .join(" | ");
        const laserCustomerNotes = laserPricingItems
          .map((entry, idx) => {
            const note = String(entry?.notes || getLaserEntryAttr(entry, "pamelaLaserNotes", "") || "").trim();
            const side = entry?.side === "back" ? "Back" : "Front";
            const name = String(getLaserEntryAttr(entry, "pamelaLaserOriginalName", "") || "").trim();
            return note ? `Laser #${idx + 1} (${side}${name ? ` - ${name}` : ""}): ${note}` : "";
          })
          .filter(Boolean)
          .join(" | ");
        props["Laser Etching"] = "Yes";
        props["Laser Artwork Count"] = String(laserPricingItems.length);
        props["Laser Artwork URLs"] = laserArtworkUrls;
        props["Laser Artwork Names"] = laserArtworkNames;
        props["Laser Feather Px Values"] = laserFeatherValues;
        if (laserCustomerNotes) props["Laser Customer Notes"] = laserCustomerNotes;

        for (const [idx, entry] of laserPricingItems.entries()) {
          const laserPlan = entry.plan;
          if (!laserPlan.minVariant || !laserPlan.extraVariant) {
            throw new Error("Laser etching products are not fully configured.");
          }
          const kindLabel =
            laserPlan.effectiveKind === "portrait"
              ? "Portrait"
              : (laserPlan.effectiveKind === "scene" ? "Scene" : "Spot");
          const pricingSource = String(entry?.source || laserPlan.source || "custom").toLowerCase() === "library"
            ? "library"
            : "custom";
          const sourceLabel = pricingSource === "custom" ? "Customer Upload" : "Library";
          const laserArtworkUrl = getCartSafeImageReference(
            getLaserEntryAttr(entry, "pamelaLaserUrl", ""),
            entry?.item?.src,
            entry?.item?.url
          );
          const laserArtworkName = String(getLaserEntryAttr(entry, "pamelaLaserOriginalName", ""));
          const laserPreviewMode = String(getLaserEntryAttr(entry, "pamelaLaserPreview", ""));
          const laserFeatherPx = Math.max(0, Math.round(Number(getLaserEntryAttr(entry, "pamelaLaserFeatherPx", 0) || 0)));
          const laserLibraryCategory = String(getLaserEntryAttr(entry, "pamelaLaserCategory", ""));
          const laserLibrarySubcategory = String(getLaserEntryAttr(entry, "pamelaLaserSubcategory", ""));
          const laserEntryNotes = String(entry?.notes || getLaserEntryAttr(entry, "pamelaLaserNotes", "") || "").trim();
          const processedLaserThumb = getCartSafeImageReference(
            getLaserEntryAttr(entry, "pamelaLaserThumbUrl", ""),
            getLaserEntryAttr(entry, "pamelaLaserProcessedUrl", ""),
            entry?.item?.processedUrl,
            entry?.item?.thumbSrc,
            entry?.item?.processedSrc
          );
          let laserThumb = processedLaserThumb;
          if (!laserThumb && pricingSource === "custom") {
            const inlinePreview = String(entry?.item?.displaySrc || entry?.item?.processedDataUrl || "").trim();
            if (inlinePreview && inlinePreview.startsWith("data:image/png;base64,") && typeof uploadProcessedLaserPng === "function") {
              const uploadedPreview = await uploadProcessedLaserPng(inlinePreview, `cart-laser-${idx + 1}`);
              if (uploadedPreview) {
                laserThumb = uploadedPreview;
                try {
                  if (entry?.attrs) {
                    entry.attrs.pamelaLaserThumbUrl = uploadedPreview;
                    entry.attrs.pamelaLaserProcessedUrl = entry.attrs.pamelaLaserProcessedUrl || uploadedPreview;
                  }
                  if (entry?.item) {
                    entry.item.thumbSrc = uploadedPreview;
                    entry.item.processedSrc = entry.item.processedSrc || uploadedPreview;
                  }
                } catch (_) {}
              }
            } else if (entry?.node && typeof uploadLaserCartThumbFromNode === "function") {
              laserThumb = await uploadLaserCartThumbFromNode(entry.node, idx + 1);
            }
          }
          if (!laserThumb && pricingSource !== "custom") {
            laserThumb = getCartSafeImageReference(
              laserArtworkUrl,
              entry?.item?.url,
              entry?.item?.src,
              entry?.item?.baseSrc
            );
          }
          const placementNode = entry?.node || null;
          const placementItem = entry?.item || {};
          const placementValue = (name, fallback) => {
            let value;
            try {
              if (placementNode && typeof placementNode[name] === "function") {
                value = placementNode[name]();
              }
            } catch (_) {}
            if (!Number.isFinite(Number(value))) value = placementItem?.[name];
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric : fallback;
          };
          const laserCommonProps = {
            ...addonProps("laser_etching", entry.side === "back" ? "back" : "front"),
            "Laser Etching": kindLabel,
            "Laser Artwork #": String(idx + 1),
            "Laser Side": entry.side === "back" ? "Back" : "Front",
            "Artwork Source": sourceLabel,
            "Laser Artwork": laserArtworkUrl,
            "Laser Artwork Name": laserArtworkName,
            "Laser Artwork Count": String(laserPricingItems.length),
            "Laser Artwork URLs": laserArtworkUrls,
            "Laser Artwork Names": laserArtworkNames,
            "Laser Preview": laserPreviewMode,
            "Laser Feather Px": String(laserFeatherPx),
            "Laser Feather Px Values": laserFeatherValues,
            "Laser Customer Notes": laserEntryNotes || laserCustomerNotes,
            "Laser Library Category": laserLibraryCategory,
            "Laser Library Subcategory": laserLibrarySubcategory,
            "Laser Width": `${laserPlan.widthIn}`,
            "Laser Height": `${laserPlan.heightIn}`,
            // Preserve exact editor geometry on the Shopify order so production
            // placement remains recoverable even if the manifest or draft fails.
            "Laser Placement X": String(placementValue("x", 0)),
            "Laser Placement Y": String(placementValue("y", 0)),
            "Laser Node Width": String(placementValue("width", 0)),
            "Laser Node Height": String(placementValue("height", 0)),
            "Laser Scale X": String(placementValue("scaleX", 1)),
            "Laser Scale Y": String(placementValue("scaleY", 1)),
            "Laser Rotation": String(placementValue("rotation", 0)),
            "Laser Offset X": String(placementValue("offsetX", 0)),
            "Laser Offset Y": String(placementValue("offsetY", 0)),
            "Laser Area Sq In": `${laserPlan.area.toFixed(2)}`,
            "Laser Extra Area Sq In": `${laserPlan.extraArea}`,
            "Detected Laser People": `${laserPlan.detectedFaces || 0}`,
            "Laser Requested Type": String(laserPlan.requestedKind || ""),
            "Laser Priced Type": String(laserPlan.effectiveKind || ""),
            "Laser Pricing Note": laserPlan.facePricingNote ||
              (laserPlan.switchedToScene
                ? "Customer selected spot/portrait size, but final size is priced as scene."
                : "")
          };
          if (laserThumb) laserCommonProps["_Laser Thumb"] = laserThumb;

          items.push({
            id: Number(laserPlan.minVariant.id),
            quantity: 1,
            properties: {
              ...laserCommonProps,
              "_cm_addon_kind": "laser_minimum"
            }
          });

          if (laserPlan.extraArea > 0) {
            items.push({
              id: Number(laserPlan.extraVariant.id),
              quantity: Number(laserPlan.extraArea),
              properties: {
                ...laserCommonProps,
                "_cm_addon_kind": "laser_extra_area"
              }
            });
          }

          if (laserPlan.additionalFaces > 0) {
            if (!laserPlan.faceVariant || !laserPlan.faceVariant.id) {
              throw new Error("Laser additional people product is not configured.");
            }
            items.push({
              id: Number(laserPlan.faceVariant.id),
              quantity: Number(laserPlan.additionalFaces),
              properties: {
                ...laserCommonProps,
                "_cm_addon_kind": "laser_additional_face",
                "Additional Laser People": `${laserPlan.additionalFaces}`
              }
            });
          }

          const enhancementRequested =
            getLaserEntryAttr(entry, "pamelaImageEnhancementRequested", false) === true ||
            String(getLaserEntryAttr(entry, "pamelaImageEnhancementRequested", "")).toLowerCase() === "true";
          if (enhancementRequested) {
            if (!cat.imageEnhancement || !cat.imageEnhancement.id) {
              throw new Error("Photo Editing Service product is not configured.");
            }
            items.push({
              id: Number(cat.imageEnhancement.id),
              quantity: 1,
              properties: {
                ...laserCommonProps,
                "_cm_addon_kind": "image_enhancement",
                "Image Enhancement": "Yes",
                "Image Enhancement Fee": "$25"
              }
            });
          }
        }
      }
      // Back base + extra words + BACK ART CHARGES (clipart + panels)
      if (wantsBack) {
        // Determine single vs companion from the editor layout
        const isCompanionMonument =
          !!(typeof editor !== "undefined" && editor && editor.layout === "companion");

        // Included words come from PAMELA_ADDON_CONFIG
        const CFG2 = window.PAMELA_ADDON_CONFIG || {};
        const included = isCompanionMonument
          ? Number((CFG2.includedWords && CFG2.includedWords.companion) != null ? (CFG2.includedWords.companion) : 20)
          : Number((CFG2.includedWords && CFG2.includedWords.single) != null ? (CFG2.includedWords.single) : 10);

        // Ã¢Å“â€¦ Pull inscription words from the BACK CANVAS text nodes (your workflow)
        const edX =
          (typeof editor !== "undefined" && editor) ||
          window.pamelaMonumentEditor ||
          window.pamelaEditor ||
          window.editor ||
          window.PAMELA_EDITOR ||
          window.__PAMELA_EDITOR__ ||
          null;

        const backContentLayer = edX?.layers?.sides?.back?.content || null;
        const backPanelsLayer  = edX?.layers?.sides?.back?.panels  || null;
        const pricedBackText = (typeof getBackTextPayload === "function")
          ? getBackTextPayload(edX)
          : { hasLettering: false, inscriptionWords: 0 };
        const pricedBackClipartCount = (typeof getBackClipartCount === "function")
          ? Number(getBackClipartCount(edX) || 0)
          : 0;
        const pricedBackPanelUpgradeCount = (typeof getBackPanelUpgradeCount === "function")
          ? Number(getBackPanelUpgradeCount(edX) || 0)
          : 0;
        const pricedBackPanelCount = (typeof getBackPanelCount === "function")
          ? Number(getBackPanelCount(edX) || 0)
          : 0;

        const backTextNodes = (() => {
          try {
            if (!backContentLayer || typeof backContentLayer.getChildren !== "function") return [];
            return backContentLayer.getChildren().filter(n => {
              const cls = n && (n.className || (n.getClassName && n.getClassName()));
              return cls === "Text";
            });
          } catch (_) { return []; }
        })();

        // Build text from canvas, but exclude the back last-name field if present
        const canvasInscription = (() => {
          try {
            const parts = [];
            backTextNodes.forEach(n => {
              try {
                const ft = n?.attrs?.pamelaFieldType ? String(n.attrs.pamelaFieldType) : "";
                if (ft === "lastname") return; // don't charge per-word for the last-name block

                const t = (typeof n.text === "function") ? String(n.text() || "") : "";
                const clean = t.replace(/\s+/g, " ").trim();
                if (clean) parts.push(clean);
              } catch (_) {}
            });
            return parts.join(" ").replace(/\s+/g, " ").trim();
          } catch (_) { return ""; }
        })();

        const hasAnyBackTextNode = (() => {
          try {
            return backTextNodes.some(n => {
              const t = (typeof n?.text === "function") ? String(n.text() || "") : "";
              return t.replace(/\s+/g, " ").trim() !== "";
            });
          } catch (_) {
            return false;
          }
        })();

        // Prefer actual canvas inscription; fall back to existing backText string if needed
        const txt = String((canvasInscription || backText || storedBackInscription || "")).replace(/\s+/g, " ").trim();

        const detectedWordCount = (typeof countWords === "function")
          ? Number(countWords(txt) || 0)
          : (txt ? txt.split(/\s+/).filter(Boolean).length : 0);
        const wc = Math.max(detectedWordCount, Number(pricedBackText?.inscriptionWords || 0));

        const extraWords = Math.max(0, wc - included);
        const backLastNameValue = String(backLastName || storedBackLastName || "").trim();
        const hasBackLettering =
          !!pricedBackText?.hasLettering ||
          hasAnyBackTextNode ||
          hasStoredBackText ||
          !!backLastNameValue ||
          !!txt ||
          wc > 0;

        if (hasBackLettering) {
          const base = isCompanionMonument ? cat.backCompanion : cat.backSingle;
          const backThumbProps = {
            ...(cartBackThumb ? { "_Back Thumb": cartBackThumb } : {}),
            ...(cartBackThumbLocalKey ? { "_Back Thumb Local Key": cartBackThumbLocalKey } : {})
          };

          items.push({
            id: Number(base.id),
            quantity: 1,
            properties: {
              ...addonProps("back_base", "back"),
              ...backThumbProps,
              "_Back Last Name": backLastNameValue,
              "_Back Inscription": String(txt || "").trim(),
              "_Back Words": String(wc || 0),
              "_Back Included Words": String(included || 0),
              "_Back Extra Words": String(extraWords || 0),
            }
          });

          if (extraWords > 0) {
            items.push({
              id: Number(cat.backWord.id),
              quantity: extraWords,
              properties: {
                ...addonProps("back_extra_word", "back"),
                ...backThumbProps,
                "_Back Extra Words": String(extraWords)
              }
            });
          }
        }

        // -----------------------------------------
        // Ã¢Å“â€¦ BACK ART CHARGES (qty-based on Basic Shopify)
        // Back Decorative Art: $50 each (clipart on back)
        // Back Engraved Panel: $75 each (panels on back)
        // -----------------------------------------
        const BACK_DECORATIVE_ART_ID = 48324927815907;
        const BACK_ENGRAVED_PANEL_ID = 48324928078051;
        const backPanelUpgrade = cat.panelUpgrade || null;

        const isClipartLibraryPanel = (n) => {
          try {
            if (!n) return false;
            if (n.getAttr?.("pamelaClipartLibraryPanel") === true) return true;
            const categoryId = String(n.getAttr?.("pamelaClipartCategoryId") || "").trim().toLowerCase();
            const category = String(n.getAttr?.("pamelaClipartCategory") || "").trim().toLowerCase();
            return categoryId === "panels" || category === "panels";
          } catch (_) {
            return false;
          }
        };

        const backClipartCount = (() => {
          if (pricedBackClipartCount > 0) return pricedBackClipartCount;
          try {
            if (!backContentLayer || typeof backContentLayer.getChildren !== "function") return 0;
            const kids = backContentLayer.getChildren() || [];
            // Count only real back clipart. Laser and portrait images are priced separately.
            let c = 0;
            kids.forEach(n => {
              if (!n) return;
              const cls = n.className || (n.getClassName && n.getClassName());
              if (cls !== "Image") return;
              if (typeof n.isVisible === "function" && !n.isVisible()) return;
              if (typeof n.destroyed === "function" && n.destroyed()) return;
              if (n.getAttr?.("pamelaLaserEtching") === true || n.attrs?.pamelaLaserEtching === true) return;
              if (n.getAttr?.("pamelaClipart") !== true && String(n.name?.() || n.attrs?.name || "") !== "pamela-clipart") return;

              // Exclude portraits by your portrait naming convention
              const nm = (typeof n.name === "function") ? String(n.name() || "") : (n.attrs?.name ? String(n.attrs.name) : "");
              if (nm === "pamela-portrait-photo" || nm === "pamela-portrait-mask" || nm === "pamela-portrait-wrapper" || nm.indexOf("portrait") !== -1) return;

              // If you tag clipart explicitly, respect it
              const t1 = n?.attrs?.pamelaType ? String(n.attrs.pamelaType) : "";
              const t2 = n?.attrs?.type ? String(n.attrs.type) : "";
              if (t1 && t1.toLowerCase() === "panel") return;
              if (t2 && t2.toLowerCase() === "panel") return;
              if (isClipartLibraryPanel(n)) return;

              c += 1;
            });
            return c;
          } catch (_) { return 0; }
        })();

        const backPanelUpgradeCount = (() => {
          if (pricedBackPanelUpgradeCount > 0) return pricedBackPanelUpgradeCount;
          try {
            if (!backContentLayer || typeof backContentLayer.getChildren !== "function") return 0;
            const kids = backContentLayer.getChildren() || [];
            let c = 0;
            kids.forEach(n => {
              if (!n) return;
              const cls = n.className || (n.getClassName && n.getClassName());
              if (cls !== "Image") return;
              if (typeof n.isVisible === "function" && !n.isVisible()) return;
              if (typeof n.destroyed === "function" && n.destroyed()) return;
              if (isClipartLibraryPanel(n)) c += 1;
            });
            return c;
          } catch (_) { return 0; }
        })();

        const backPanelCount = (() => {
          if (pricedBackPanelCount > 0) return pricedBackPanelCount;
          try {
            if (!backPanelsLayer || typeof backPanelsLayer.getChildren !== "function") return 0;
            const kids = backPanelsLayer.getChildren() || [];
            // Panels are shapes/groups/rects/etc. We count all visible, non-destroyed nodes.
            // If you later want to exclude specific panel types (e.g., last-name panel),
            // we can add a filter by attrs/name here.
            let c = 0;
            kids.forEach(n => {
              if (!n) return;
              if (typeof n.isVisible === "function" && !n.isVisible()) return;
              if (typeof n.destroyed === "function" && n.destroyed()) return;
              c += 1;
            });
            return c;
          } catch (_) { return 0; }
        })();

        if (backClipartCount > 0) {
          items.push({
            id: Number(BACK_DECORATIVE_ART_ID),
            quantity: Number(backClipartCount),
            properties: {
              ...addonProps("back_decorative_art", "back"),
              ...(cartBackThumb ? { "_Back Thumb": cartBackThumb } : {}),
              ...(cartBackThumbLocalKey ? { "_Back Thumb Local Key": cartBackThumbLocalKey } : {}),
              "_Back Clipart Count": String(backClipartCount)
            }
          });
        }

        if (backPanelUpgradeCount > 0) {
          if (!backPanelUpgrade || !backPanelUpgrade.id) {
            throw new Error("Back Engraved Panel (Upgrade) product has no variant.");
          }

          const upgradedPanelCount = backPanelCount + backPanelUpgradeCount;
          items.push({
            id: Number(backPanelUpgrade.id),
            quantity: Number(upgradedPanelCount),
            properties: {
              ...addonProps("back_engraved_panel", "back"),
              ...(cartBackThumb ? { "_Back Thumb": cartBackThumb } : {}),
              ...(cartBackThumbLocalKey ? { "_Back Thumb Local Key": cartBackThumbLocalKey } : {}),
              "_Back Panel Count": String(upgradedPanelCount),
              "_Back Panel Upgrade Count": String(backPanelUpgradeCount),
              "_Back Panel Type": "Upgrade"
            }
          });
        } else if (backPanelCount > 0) {
          items.push({
            id: Number(BACK_ENGRAVED_PANEL_ID),
            quantity: Number(backPanelCount),
            properties: {
              ...addonProps("back_engraved_panel", "back"),
              ...(cartBackThumb ? { "_Back Thumb": cartBackThumb } : {}),
              ...(cartBackThumbLocalKey ? { "_Back Thumb Local Key": cartBackThumbLocalKey } : {}),
              "_Back Panel Count": String(backPanelCount)
            }
          });
        }
      }

           // Vase engraving
      if (vaseSide === "front" || vaseSide === "both") {
        items.push({
          id: Number(cat.vaseFront.id),
          quantity: vaseQty,
          properties: { ...addonProps("vase_front", "front"), "_Vase Qty": String(vaseQty) }
        });
      }
      if (vaseSide === "back" || vaseSide === "both") {
        items.push({
          id: Number(cat.vaseBack.id),
          quantity: vaseQty,
          properties: { ...addonProps("vase_back", "back"), "_Vase Qty": String(vaseQty) }
        });
      }

      setFinalizeStatus("Adding to cart...");

      try {
        const requiredAddonRecipe = items
          .slice(1)
          .filter((it) => it && it.id != null)
          .map((it) => ({
            id: Number(it.id),
            quantity: Number(it.quantity || 1),
            properties: compactRequiredAddonProps(it.properties || {})
          }));
        writeB64Chunks("_CM Required Addons", requiredAddonRecipe);
        items[0].properties = props;
      } catch (e) {
        console.warn("Required add-on recipe capture failed:", e);
      }

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          items: items.map(it => ({
            id: Number(it.id),
            quantity: Number(it.quantity || 1),
            properties: it.properties || {}
          }))
        })
      });

      if (!res.ok) {
        let msg = "Cart add failed.";
        try {
          const j = await res.json();
          msg = (j && (j.description || j.message)) ? (j.description || j.message) : msg;
        } catch (_) {
          try {
            const txt = await res.text();
            if (txt) msg = txt;
          } catch (_) {}
        }
        throw new Error(msg);
      }

      if (cartEdit.enabled && cartEdit.replaceDesignId) {
        try {
          // Never remove the original group unless Shopify confirms the
          // replacement group is present in the cart first.
          const replacementPresent = await cartHasDesignGroup(designId);
          if (!replacementPresent) {
            throw new Error("The updated design was not found in the cart; the original design was kept.");
          }

          setFinalizeStatus("Updating your cart...");
          await removeCartDesignGroup(cartEdit.replaceDesignId);
        } catch (e) {
          console.error("Cart replacement cleanup failed; original cart design was kept:", e);
          throw e;
        }
      }

      try {
        window.CM_RESET_ORDER_SESSION_STATE?.();
      } catch (e) {
        console.warn("Order session reset failed:", e);
      }

      setFinalizeStatus("Opening cart...");
      navigatingToCart = true;
      window.location.href = "/cart";
      
    } catch (err) {
      console.error(err);
      setFinalizeStatus("");
      setFinalizeOverlay(false);
      alert(err.message || "Could not finalize and add to cart.");
    } finally {
      if (!navigatingToCart) setFinalizeOverlay(false);
      finalizeBtn.disabled = false;
    }
  });
}

      selectEl.addEventListener("change", () => {
        const nextVariantId = String(selectEl.value || "");
        const previousVariantId = String(
          selectEl.dataset.pamelaAppliedVariantId ||
          window.__PAMELA_SELECTED_VARIANT__?.id ||
          initialVariant?.id ||
          ""
        );
        const nextVariant = variants.find((x) => String(x.id) === nextVariantId) || null;

        if (
          nextVariant &&
          !isLaserEtchingVariantEligible(nextVariant) &&
          pamelaEditorHasLaserArtwork(editor)
        ) {
          const ok = window.confirm(
            "Changing to this granite color will remove laser etching and any uploaded laser artwork. This cannot be undone unless you upload/edit it again. Continue?"
          );
          if (!ok) {
            if (previousVariantId) selectEl.value = previousVariantId;
            return;
          }
        }

        applyVariant(nextVariantId, { resetView: false })
          .then(() => {
            const appliedVariant = window.__PAMELA_SELECTED_VARIANT__ || nextVariant || null;
            if (appliedVariant && !isLaserEtchingVariantEligible(appliedVariant)) {
              pamelaClearLaserArtwork(editor);
            }
            try { window.refreshAddonInfoVisibility?.(); } catch (_) {}
          })
          .catch((err) => {
            console.error(err);
            setStatus(wrapEl, "Could not load that variant. Check its image.");
          });
      });

    editor.refreshButtons();
    editor.refreshSizeButtons();
  } catch (err) {
    console.error(err);
    setStatus(wrapEl, "Could not load product/variants. Check the ?product=HANDLE in the URL.");
    if (topTitleEl) topTitleEl.textContent = "Editor";
    if (priceEl) priceEl.textContent = "$0.00";
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => boot().catch(console.error));
} else {
  boot().catch(console.error);
}

// ============================================================
// PORTRAIT STEP 2 (REBIND): Drop a porcelain placeholder that
// 1) is correct physical size relative to the monument, and
// 2) lives INSIDE the zoom/pan "world" so it moves/zooms correctly.
// - No alerts
// - Does NOT touch dropdown population
// - Re-binds even if UI re-renders button
// ============================================================
(function CM_PortraitStep2_Rebind() {
  if (window.__CM_PortraitStep2_RebindOn) return;
  window.__CM_PortraitStep2_RebindOn = true;

  const MAX = 9999;

  function findEditor() {
    const cands = [
      window.pamelaEditor,
      window.pamelaMonumentEditor,
      window.editor,
      window.PAMELA_EDITOR,
      window.__PAMELA_EDITOR__
    ].filter(Boolean);

    for (const ed of cands) {
      if (ed && ed.stage && ed.layers) return ed;
    }

    for (const k in window) {
      try {
        const v = window[k];
        if (v && v.stage && v.layers && typeof v.stage.find === "function") return v;
      } catch (_) {}
    }
    return null;
  }

function nearestDropdown(btn) {
  const scope =
    btn.closest("section, .shopify-section, .cm-addons, .card, form, div") || document;
  return scope.querySelector("#cm_porcelain_dropdown") || document.querySelector("#cm_porcelain_dropdown");
}

  function parsePortrait(dd) {
    if (!dd || !dd.options || dd.selectedIndex < 0) return null;
    const opt = dd.options[dd.selectedIndex];
    const txt = opt ? (opt.textContent || opt.innerText || "") : "";
    const lower = txt.toLowerCase();
    if (!txt || lower.includes("select a portrait")) return null;

    let shape = "rect";
    if (lower.includes("oval")) shape = "oval";
    else if (lower.includes("heart")) shape = "heart";
    else if (lower.includes("circle")) shape = "circle";
    else if (lower.includes("rectangle")) shape = "rect";

    if (shape === "circle") {
      const dm = txt.match(/circle\s*-\s*(\d+(?:\.\d+)?)/i);
      const dIn = dm ? Number(dm[1]) : 3.93;
      return { shape: "circle", wIn: dIn, hIn: dIn, label: txt };
    }

  const m = txt.match(/(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)/i);
    const wIn = m ? Number(m[1]) : 4.0;
    const hIn = m ? Number(m[2]) : 4.0;
    return { shape, wIn, hIn, label: txt };
  }

  function ensureSlots(ed) {
    if (!Array.isArray(ed.__cmPortraitSlots)) {
      ed.__cmPortraitSlots = new Array(MAX).fill(null);
    } else if (ed.__cmPortraitSlots.length < MAX) {
      ed.__cmPortraitSlots.length = MAX;
      for (let i = 0; i < MAX; i++) {
        if (typeof ed.__cmPortraitSlots[i] === "undefined") ed.__cmPortraitSlots[i] = null;
      }
    }
    return ed.__cmPortraitSlots;
  }

  function firstEmpty(slots) {
    for (let i = 0; i < slots.length; i++) if (!slots[i]) return i;
    return -1;
  }

  function findMonumentImageNode(ed) {
    try {
      const st = ed.stage;
      if (!st || typeof st.find !== "function") return null;

      const byName =
        st.findOne(".pamela-monument") ||
        st.findOne("#pamela-monument") ||
        st.findOne(".monument") ||
        st.findOne("#monument") ||
        st.findOne(".pamela-stone") ||
        st.findOne("#pamela-stone");

      if (byName) return byName;

      const imgs = st.find("Image");
      if (imgs && imgs.length) return imgs[0];

      return null;
    } catch (_) {
      return null;
    }
  }

  function getLayoutInches(ed) {
    try {
      const l =
        ed.currentLayout ||
        ed.layout ||
        (typeof ed.getCurrentLayout === "function" ? ed.getCurrentLayout() : null) ||
        window.__PAMELA_CURRENT_LAYOUT__ ||
        window.PAMELA_CURRENT_LAYOUT ||
        null;

      const wIn = Number(l?.wIn ?? l?.widthIn ?? l?.width_in ?? l?.width ?? NaN);
      const hIn = Number(l?.hIn ?? l?.heightIn ?? l?.height_in ?? l?.height ?? NaN);

      if (Number.isFinite(wIn) && wIn > 0 && Number.isFinite(hIn) && hIn > 0) {
        return { wIn, hIn };
      }
    } catch (_) {}

    return null;
  }

  function getDesignPixelsPerInch(ed) {
    const direct =
      Number(ed?.DESIGN_DPI) ||
      Number(ed?.designDpi) ||
      Number(ed?.dpi) ||
      Number(ed?.DPI);

    if (Number.isFinite(direct) && direct > 0) return direct;

    const layout = getLayoutInches(ed);
    const mon = findMonumentImageNode(ed);
    if (layout && mon && typeof mon.width === "function") {
      const monWpx = Number(mon.width());
      const wIn = Number(layout.wIn);
      if (Number.isFinite(monWpx) && monWpx > 0 && Number.isFinite(wIn) && wIn > 0) {
        return monWpx / wIn;
      }
    }

    return 72;
  }

  function findWorldContainer(ed) {
    const direct =
      ed.world ||
      ed.worldGroup ||
      ed.contentGroup ||
      ed.viewportGroup ||
      ed.zoomGroup ||
      ed.sceneGroup;

    if (direct && typeof direct.add === "function") return direct;

    try {
      const c = ed.layers && ed.layers.content;
      if (c && typeof c.findOne === "function") {
        const g =
          c.findOne(".pamela-viewport") ||
          c.findOne("#pamela-viewport") ||
          c.findOne(".viewport") ||
          c.findOne(".pamela-world") ||
          c.findOne("#pamela-world") ||
          c.findOne(".world") ||
          c.findOne(".scene") ||
          c.findOne("#scene");

        if (g && typeof g.add === "function") return g;
        if (typeof c.add === "function") return c;
      }
    } catch (_) {}

    try {
      const p = ed.layers && ed.layers.portraits;
      if (p && typeof p.add === "function") return p;
    } catch (_) {}

    return ed.stage || null;
  }

  function makeHeartShapeNode(w, h) {
    return new Konva.Shape({
      x: 0,
      y: 0,
      listening: false,
      sceneFunc: function (ctx, shape) {
        const x = w / 2;
        const y = h / 2;
        const top = h * 0.28;

        ctx.beginPath();
        ctx.moveTo(x, y + h * 0.33);
        ctx.bezierCurveTo(x + w * 0.50, y + h * 0.05, x + w * 0.50, y - top, x, y - top);
        ctx.bezierCurveTo(x - w * 0.50, y - top, x - w * 0.50, y + h * 0.05, x, y + h * 0.33);
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      }
    });
  }

  function safeAddName(node, name) {
    try {
      if (node && typeof node.addName === "function") node.addName(name);
    } catch (_) {}
  }

  function attachPortraitDrag(ed, wrapper, hit, parent) {
    if (!hit || hit.__cmPortraitDragBound) return;
    hit.__cmPortraitDragBound = true;

    const TAP_MAX_MS = 450;
    const TAP_MAX_CLIENT_MOVE = 8;

    const markPortraitSelected = () => {
      try { if (ed) ed.__cmSelectedPortraitWrapper = wrapper; } catch (_) {}
      try { if (ed && typeof ed.selectNode === "function") ed.selectNode(wrapper); } catch (_) {}
      try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
      try { window.__CM_LAST_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
      try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
      try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}
      try { ed?.refreshButtons?.(); } catch (_) {}
      try { ed?.refreshSizeButtons?.(); } catch (_) {}
      try { ed?._refreshClipButtons?.(); } catch (_) {}
      try { ed?._refreshFontUI?.(); } catch (_) {}
      try { window.pamelaRefreshImagesToolbar?.(ed); } catch (_) {}
    };

    const bindSelectionSurface = (node) => {
      try {
        if (!node || node.__cmPortraitSelectBound) return;
        node.__cmPortraitSelectBound = true;

        node.on("click tap", (e) => {
          try { e.cancelBubble = true; } catch (_) {}
          markPortraitSelected();
        });

        node.on("dblclick dbltap", async (e) => {
          try { e.cancelBubble = true; } catch (_) {}
          markPortraitSelected();
          try {
            if (window.CM_PortraitUpload && window.CM_PortraitUpload.openPickerForWrapper) {
              await window.CM_PortraitUpload.openPickerForWrapper(wrapper);
            }
          } catch (_) {}
        });
      } catch (_) {}
    };

    bindSelectionSurface(wrapper);
    try { bindSelectionSurface(wrapper.findOne?.(".pamela-portrait-inner")); } catch (_) {}

    try {
      hit.on("click tap", (e) => {
        try { e.cancelBubble = true; } catch (_) {}
        markPortraitSelected();
      });
    } catch (_) {}

    hit.on("pointerdown", (e) => {
      try { e.cancelBubble = true; } catch (_) {}
      try { if (e.evt) e.evt.preventDefault(); } catch (_) {}

      // Ã¢Å“â€¦ Select portrait on click so toolbar Delete can delete it
      markPortraitSelected();

      try { if (ed && ed._isPanning) ed._isPanning = false; } catch (_) {}

      const containerEl =
        (ed && ed.stage && typeof ed.stage.container === "function") ? ed.stage.container() : null;
      const pid = e?.evt?.pointerId;

      try {
        if (containerEl && pid != null) containerEl.setPointerCapture(pid);
      } catch (_) {}

      const parentForMove =
        parent || (wrapper && typeof wrapper.getParent === "function" ? wrapper.getParent() : null);

      const toParentLocal = (ptStage) => {
        try {
          if (parentForMove && typeof parentForMove.getAbsoluteTransform === "function") {
            const inv = parentForMove.getAbsoluteTransform().copy().invert();
            return inv.point(ptStage);
          }
        } catch (_) {}
        return ptStage;
      };

      const getStagePointer = (ev) => {
        try {
          const r = containerEl ? containerEl.getBoundingClientRect() : null;
          if (r && ev && typeof ev.clientX === "number") {
            return { x: ev.clientX - r.left, y: ev.clientY - r.top };
          }
        } catch (_) {}

        try {
          if (ed && ed.stage && typeof ed.stage.getPointerPosition === "function") {
            const p = ed.stage.getPointerPosition();
            if (p) return { x: p.x, y: p.y };
          }
        } catch (_) {}

        return { x: 0, y: 0 };
      };

      const tapStartTime = Date.now();
      const tapStartClient = { x: e?.evt?.clientX ?? null, y: e?.evt?.clientY ?? null };

      const startStage = getStagePointer(e.evt);
      const startLocal = toParentLocal(startStage);

      const startX = wrapper.x();
      const startY = wrapper.y();

      let raf = null;
      let didDrag = false;

      const onMove = (ev) => {
        if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;
        try { ev.preventDefault(); } catch (_) {}

        try {
          if (ed && ed.stage && typeof ed.stage.setPointersPositions === "function") {
            ed.stage.setPointersPositions(ev);
          }
        } catch (_) {}

        const curStage = getStagePointer(ev);
        const curLocal = toParentLocal(curStage);

        const dx = curLocal.x - startLocal.x;
        const dy = curLocal.y - startLocal.y;

        if (!didDrag && (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5)) didDrag = true;

        if (!raf) {
          raf = requestAnimationFrame(() => {
            raf = null;
            if (!didDrag) return;

            wrapper.position({ x: startX + dx, y: startY + dy });
            try { pamelaSnapPortraitWrapperCenter(ed, wrapper); } catch (_) {}
            try { ed?._showHorizontalAlignGuideForNode?.(wrapper); } catch (_) {}
            try { ed?._showClipartCenterGuideForNode?.(wrapper); } catch (_) {}

            try {
              const layer = wrapper.getLayer && wrapper.getLayer();
              if (layer && typeof layer.batchDraw === "function") layer.batchDraw();
              else if (layer && typeof layer.draw === "function") layer.draw();
            } catch (_) {}
          });
        }
      };

      const end = async (ev) => {
        if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;

      try { targetEl?.removeEventListener?.("pointermove", onMove, true); } catch (_) {}
      try { targetEl?.removeEventListener?.("pointerup", end, true); } catch (_) {}
      try { targetEl?.removeEventListener?.("pointercancel", end, true); } catch (_) {}

      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", end, true);
      window.removeEventListener("pointercancel", end, true);
      window.removeEventListener("blur", end, true);

        if (raf) { cancelAnimationFrame(raf); raf = null; }

        try {
          if (containerEl && pid != null) containerEl.releasePointerCapture(pid);
        } catch (_) {}

        if (!didDrag) {
          markPortraitSelected();
        } else {
          try { pamelaSnapPortraitWrapperCenter(ed, wrapper); } catch (_) {}
          try {
            const layer = wrapper.getLayer && wrapper.getLayer();
            if (layer && typeof layer.batchDraw === "function") layer.batchDraw();
            else if (layer && typeof layer.draw === "function") layer.draw();
          } catch (_) {}
          try { ed?._hideCenterGuide?.(); } catch (_) {}
        }

        // --- DOUBLE-TAP => UPLOAD (unchanged) ---
        try {
          const dt = Date.now() - tapStartTime;

          let clientMoveOk = true;
          if (
            tapStartClient.x != null &&
            typeof ev?.clientX === "number" &&
            typeof ev?.clientY === "number"
          ) {
            const dpx = Math.abs(ev.clientX - tapStartClient.x);
            const dpy = Math.abs(ev.clientY - tapStartClient.y);
            clientMoveOk = (dpx <= TAP_MAX_CLIENT_MOVE && dpy <= TAP_MAX_CLIENT_MOVE);
          }

          const isPortrait = wrapper?.getAttr && wrapper.getAttr("__isPortraitGroup") === true;

          if (isPortrait && !didDrag && dt <= TAP_MAX_MS && clientMoveOk) {
            markPortraitSelected();
            const DBL_TAP_MAX_MS = 380;
            const DBL_TAP_MAX_DIST = 14;

            const now = Date.now();
            const cx = (typeof ev?.clientX === "number") ? ev.clientX : null;
            const cy = (typeof ev?.clientY === "number") ? ev.clientY : null;

            const last = hit.__cmLastTap || 0;
            const lastPt = hit.__cmLastTapPt || null;

            let isSecondTap = false;
            if (last && (now - last) <= DBL_TAP_MAX_MS && lastPt && cx != null && cy != null) {
              const dx = Math.abs(cx - lastPt.x);
              const dy = Math.abs(cy - lastPt.y);
              if (dx <= DBL_TAP_MAX_DIST && dy <= DBL_TAP_MAX_DIST) isSecondTap = true;
            }

            if (isSecondTap) {
              hit.__cmLastTap = 0;
              hit.__cmLastTapPt = null;

              if (window.CM_PortraitUpload && window.CM_PortraitUpload.openPickerForWrapper) {
                await window.CM_PortraitUpload.openPickerForWrapper(wrapper);
              }
            } else {
              hit.__cmLastTap = now;
              hit.__cmLastTapPt = (cx != null && cy != null) ? { x: cx, y: cy } : null;
            }
          }
        } catch (err) {
          console.warn("[Portrait Upload] double-tap upload error:", err);
        }
      };

      window.addEventListener("pointermove", onMove, { passive: false, capture: true });
      window.addEventListener("pointerup", end, true);
      window.addEventListener("pointercancel", end, true);
      window.addEventListener("blur", end, true);
    });
  }

  function addPlaceholder(ed, p) {
    const slots = ensureSlots(ed);

    let idx = firstEmpty(slots);

// Ã¢Å“â€¦ Allow restore to bypass slot limits
if (idx === -1) {
  if (ed && ed.__cmRestoringPortraits) {
    idx = slots.length; // force next slot during restore
  } else {
    return console.warn("[Portrait] Max portraits reached.");
  }
}

    const ppi = getDesignPixelsPerInch(ed);
    const w = Math.max(1, Number(p.wIn || 4) * ppi);
    const h = Math.max(1, Number(p.hIn || 4) * ppi);

    const mon = findMonumentImageNode(ed);

    const world = findWorldContainer(ed);
    const restoringPortraits = !!ed.__cmRestoringPortraits;

    const parent =
      (restoringPortraits &&
        ed && ed.layers && ed.layers.portraits &&
        typeof ed.layers.portraits.add === "function" &&
        ed.layers.portraits) ||
      (world && typeof world.add === "function" && world) ||
      (ed && ed.layers && ed.layers.portraits && typeof ed.layers.portraits.add === "function" && ed.layers.portraits) ||
      (ed && ed.layers && ed.layers.content && typeof ed.layers.content.add === "function" && ed.layers.content) ||
      ed.stage;

    let stageCx = (ed.stage && typeof ed.stage.width === "function") ? ed.stage.width() / 2 : 0;
    let stageCy = (ed.stage && typeof ed.stage.height === "function") ? ed.stage.height() / 2 : 0;

    try {
      if (mon && typeof mon.getClientRect === "function") {
        const rStage = mon.getClientRect({ skipStroke: true, skipShadow: true });
        if (rStage && isFinite(rStage.x) && isFinite(rStage.y) && isFinite(rStage.width) && isFinite(rStage.height)) {
          stageCx = rStage.x + rStage.width / 2;
          stageCy = rStage.y + rStage.height / 2;
        }
      }
    } catch (_) {}

    let cx = stageCx;
    let cy = stageCy;

    try {
      if (parent && typeof parent.getAbsoluteTransform === "function") {
        const inv = parent.getAbsoluteTransform().copy().invert();
        const localPt = inv.point({ x: stageCx, y: stageCy });
        cx = localPt.x;
        cy = localPt.y;
      }
    } catch (_) {}

    const wrapper = new Konva.Group({
      x: cx - w / 2,
      y: cy - h / 2,
      name: "pamela-portrait-group",
      listening: true
    });

    safeAddName(wrapper, "pamela");
    safeAddName(wrapper, "pamela-clipart");
    safeAddName(wrapper, "pamela-draggable");

    wrapper.setAttr("pamelaType", "portrait");
    wrapper.setAttr("pamelaDrag", true);
    wrapper.setAttr("pamelaDraggable", true);
    wrapper.setAttr("__pamelaSelectable", true);
    wrapper.setAttr("__pamelaKind", "portrait");

    // Ã¢Å“â€¦ MUST MATCH your deleteSelected() detection
    wrapper.setAttr("pamelaPortrait", true);
    wrapper.setAttr("pamelaPortraitSlot", idx);

    wrapper.setAttr("__isPortraitGroup", true);
    wrapper.setAttr("__portraitSlot", idx);
    wrapper.setAttr("__portraitShape", p.shape);
    wrapper.setAttr("__portraitWIn", String(p.wIn));
    wrapper.setAttr("__portraitHIn", String(p.hIn));
    wrapper.setAttr("__portraitLabel", String(p.label || ""));
    wrapper.setAttr("__portraitPPI", String(ppi));

    const guideStroke = "rgba(255,255,255,0.95)";
    const guideDash = [18, 12];
    const guideStrokeWidth = 4;

    if (p.shape === "circle" || p.shape === "oval") {
      const maskGuide = new Konva.Ellipse({
        x: w / 2, y: h / 2,
        radiusX: w / 2, radiusY: h / 2,
        stroke: guideStroke,
        strokeWidth: guideStrokeWidth,
        dash: guideDash,
        name: "pamela-portrait-mask",
        listening: false
      });
      maskGuide.setAttr("pamelaType", "portrait");
      maskGuide.setAttr("__pamelaKind", "portrait");
      wrapper.add(maskGuide);
    } else if (p.shape === "heart") {
      const heart = makeHeartShapeNode(w, h);
      heart.name("pamela-portrait-mask");
      heart.stroke(guideStroke);
      heart.strokeWidth(guideStrokeWidth);
      heart.dash(guideDash);
      heart.setAttr("pamelaType", "portrait");
      heart.setAttr("__pamelaKind", "portrait");
      wrapper.add(heart);
    } else {
      const maskGuide = new Konva.Rect({
        x: 0, y: 0,
        width: w, height: h,
        stroke: guideStroke,
        dash: guideDash,
        strokeWidth: guideStrokeWidth,
        cornerRadius: 10,
        name: "pamela-portrait-mask",
        listening: false
      });
      maskGuide.setAttr("pamelaType", "portrait");
      maskGuide.setAttr("__pamelaKind", "portrait");
      wrapper.add(maskGuide);
    }

    wrapper.add(new Konva.Line({
      points: [w / 2 - 18, h / 2, w / 2 + 18, h / 2],
      stroke: "rgba(255,255,255,0.9)",
      strokeWidth: 3,
      listening: false
    }));
    wrapper.add(new Konva.Line({
      points: [w / 2, h / 2 - 18, w / 2, h / 2 + 18],
      stroke: "rgba(255,255,255,0.9)",
      strokeWidth: 3,
      listening: false
    }));

    const inner = new Konva.Group({ x: 0, y: 0, name: "pamela-portrait-inner" });

    inner.clipFunc(function (ctx) {
      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
        ctx.closePath();
        return;
      }
      if (p.shape === "oval") {
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.closePath();
        return;
      }
      if (p.shape === "heart") {
        const x = w / 2;
        const y = h / 2;
        const top = h * 0.28;
        ctx.beginPath();
        ctx.moveTo(x, y + h * 0.33);
        ctx.bezierCurveTo(x + w * 0.50, y + h * 0.05, x + w * 0.50, y - top, x, y - top);
        ctx.bezierCurveTo(x - w * 0.50, y - top, x - w * 0.50, y + h * 0.05, x, y + h * 0.33);
        ctx.closePath();
        return;
      }
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.closePath();
    });

    inner.add(new Konva.Rect({
      x: 0, y: 0,
      width: w, height: h,
      fill: "rgba(255,255,255,0.06)",
      listening: false
    }));

    wrapper.add(inner);

    const hit = new Konva.Rect({
      x: 0, y: 0,
      width: w, height: h,
      fill: "rgba(0,0,0,0.01)",
      opacity: 0.001,
      listening: true,
      name: "pamela-hit"
    });

    hit.setAttr("pamelaType", "portrait");
    hit.setAttr("pamelaDrag", true);
    hit.setAttr("pamelaDraggable", true);
    hit.setAttr("__pamelaSelectable", true);
    hit.setAttr("__pamelaKind", "portrait");

    safeAddName(hit, "pamela");
    safeAddName(hit, "pamela-draggable");
    safeAddName(hit, "pamela-clipart");

    wrapper.add(hit);

    attachPortraitDrag(ed, wrapper, hit, parent);

    if (parent && typeof parent.add === "function") parent.add(wrapper);

    try {
      const layer = wrapper.getLayer && wrapper.getLayer();
      if (layer && typeof layer.draw === "function") layer.draw();
      else if (ed && ed.stage && typeof ed.stage.draw === "function") ed.stage.draw();
    } catch (_) {}

    slots[idx] = wrapper;
    return wrapper;

  }
window.CM_addPortraitPlaceholder = addPlaceholder;

  function bindOnce(btn) {
    if (btn.__cmPortraitBound) return;
    btn.__cmPortraitBound = true;

btn.addEventListener("click", (e) => {
  e.preventDefault();
  e.stopPropagation();

  const ed = findEditor();
  if (!ed) return console.warn("[Portrait] Editor not ready.");

  const dd = nearestDropdown(btn);
  const p = parsePortrait(dd);
  if (!p) return console.warn("[Portrait] Choose a portrait first.");

  // --- capture porcelain selection (variant + label-without-price) ---
  const variantId = dd && dd.value ? Number(dd.value) : null;

  let label = "";
  try {
    const opt = dd && dd.selectedOptions && dd.selectedOptions[0] ? dd.selectedOptions[0] : null;
    label = opt ? String(opt.textContent || "") : "";
  } catch (_) {}

  // Remove the " Ã¢â‚¬â€ $XXX.XX" part (your choice A)
  label = label.replace(/\s-\s\$.*$/, "").trim();

  const wrapper = addPlaceholder(ed, p);

  try {
    if (wrapper && variantId) {
      wrapper.setAttr("__porcelainVariantId", variantId);
      wrapper.setAttr("__portraitVariantId", variantId);
      wrapper.setAttr("__porcelainLabel", label || (p && p.label ? String(p.label) : ""));
      wrapper.setAttr("__portraitVariantTitle", label || (p && p.label ? String(p.label) : ""));

      const lyr = wrapper.getLayer && wrapper.getLayer();
      if (lyr && lyr.batchDraw) lyr.batchDraw();
    }
  } catch (e2) {
    console.warn("[Porcelain] stamp failed:", e2);
  }

  if (wrapper) {
    try {
      ed.__cmSelectedPortraitWrapper = wrapper;
      window.__CM_SELECTED_PORTRAIT_WRAPPER__ = wrapper;
      window.__CM_LAST_PORTRAIT_WRAPPER__ = wrapper;
      window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = wrapper;
      ed.selectNode?.(wrapper);
    } catch (_) {}

    try {
      const fly = document.getElementById("pamela-addon-flyout");
      if (fly) fly.style.display = "none";
    } catch (_) {}

    try {
      if (window.CM_PortraitUpload?.openPickerForWrapper) {
        window.CM_PortraitUpload.openPickerForWrapper(wrapper);
      }
    } catch (pickErr) {
      console.warn("[Porcelain] auto-open photo picker failed:", pickErr);
    }
  }
}, true);
  }

  const obs = new MutationObserver(() => {
    const btn = document.getElementById("cm_porcelain_add_to_order");
    if (btn) bindOnce(btn);
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });

  const btnNow = document.getElementById("cm_porcelain_add_to_order");
  if (btnNow) bindOnce(btnNow);
})();

// NOTE: Keep this extra close ONLY if your file truly has an outer wrapper IIFE above this block.


})();

// ============================================================
// PORTRAIT UPLOAD HELPERS (global, used by patched drag)
// ============================================================
(function CM_PortraitUploadHelpers() {
  if (window.CM_PortraitUpload) return;

  const UPLOAD_URL = "https://cm-editor-server.onrender.com/api/portraits/upload";
  const HEIC_ACK_ID = "cm_heicAck";
  const FILE_INPUT_ID = "cm_portraitFile";

  function $(id) { return document.getElementById(id); }

  function ensureHiddenFileInput() {
    let input = $(FILE_INPUT_ID);
    if (input) return input;

    input = document.createElement("input");
    input.type = "file";
    input.id = FILE_INPUT_ID;
    input.accept = "image/*";
    input.style.position = "fixed";
    input.style.left = "-99999px";
    input.style.top = "-99999px";
    input.style.opacity = "0";
    input.style.pointerEvents = "none";
    document.body.appendChild(input);
    return input;
  }

  function findInnerGroup(wrapper) {
    if (!wrapper || typeof wrapper.findOne !== "function") return null;
    return wrapper.findOne(".pamela-portrait-inner") || null;
  }

  function getPortraitWH(wrapper) {
    try {
      const ppi = Number(wrapper.getAttr("__portraitPPI")) || 72;
      const wIn = Number(wrapper.getAttr("__portraitWIn")) || 4;
      const hIn = Number(wrapper.getAttr("__portraitHIn")) || 4;
      return { w: Math.max(1, wIn * ppi), h: Math.max(1, hIn * ppi) };
    } catch (_) {}
    return { w: 288, h: 288 };
  }

  function coverFit(imgW, imgH, boxW, boxH) {
    const scale = Math.max(boxW / imgW, boxH / imgH);
    const w = imgW * scale;
    const h = imgH * scale;
    return { x: (boxW - w) / 2, y: (boxH - h) / 2, w, h, scale };
  }

  function loadHtmlImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  }

  function setPortraitImage(wrapper, htmlImg, meta) {
    const inner = findInnerGroup(wrapper);
    if (!inner) return;

    const { w: boxW, h: boxH } = getPortraitWH(wrapper);

    try {
      const old = inner.findOne(".pamela-portrait-photo");
      if (old) old.destroy();
    } catch (_) {}

    const imgW = htmlImg.naturalWidth || htmlImg.width || 1;
    const imgH = htmlImg.naturalHeight || htmlImg.height || 1;
    const fit = coverFit(imgW, imgH, boxW, boxH);

    const kImg = new Konva.Image({
      x: fit.x,
      y: fit.y,
      width: fit.w,
      height: fit.h,
      image: htmlImg,
      listening: false,
      name: "pamela-portrait-photo"
    });

    inner.add(kImg);
    try { kImg.moveToBottom(); } catch (_) {}

    try {
      if (meta && typeof meta === "object") {
        if (meta.url) wrapper.setAttr("__portraitUrl", String(meta.url));
        if (meta.original_name) wrapper.setAttr("__portraitOriginalName", String(meta.original_name));
        if (meta.content_type) wrapper.setAttr("__portraitContentType", String(meta.content_type));
        if (meta.width) wrapper.setAttr("__portraitImgW", String(meta.width));
        if (meta.height) wrapper.setAttr("__portraitImgH", String(meta.height));
        if (meta.heic_ack != null) wrapper.setAttr("__portraitHeicAck", String(meta.heic_ack));
        wrapper.setAttr("__portraitUploadedAt", new Date().toISOString());
      }
      wrapper.setAttr("__portraitHasPhoto", true);
    } catch (_) {}

    try {
      const layer = wrapper.getLayer && wrapper.getLayer();
      if (layer && typeof layer.batchDraw === "function") layer.batchDraw();
      else if (layer && typeof layer.draw === "function") layer.draw();
    } catch (_) {}
  }

    async function rebuildOnePortrait(wrapper, savedPortrait) {
    const hostedUrl = String(savedPortrait?.url || "").trim();
    const croppedDataUrl = String(savedPortrait?.croppedDataUrl || "").trim();
    const sourceUrl = hostedUrl || croppedDataUrl;
    if (!sourceUrl) return;

    const hostedImg = await loadHtmlImage(sourceUrl);

    setPortraitImage(wrapper, hostedImg, {
      url: sourceUrl,
      original_name: savedPortrait.meta?.original_name || "",
      content_type: savedPortrait.meta?.content_type || "",
      width: savedPortrait.meta?.img_w || 0,
      height: savedPortrait.meta?.img_h || 0,
      heic_ack: savedPortrait.meta?.heic_ack
    });

    wrapper.x(savedPortrait.wrapper?.x || 0);
    wrapper.y(savedPortrait.wrapper?.y || 0);
    wrapper.scaleX(savedPortrait.wrapper?.scaleX || 1);
    wrapper.scaleY(savedPortrait.wrapper?.scaleY || 1);
    wrapper.rotation(savedPortrait.wrapper?.rotation || 0);

    const inner = wrapper.findOne(".pamela-portrait-inner");
    const photo = inner?.findOne(".pamela-portrait-photo");

    if (photo && savedPortrait.photo) {
      photo.x(savedPortrait.photo.x || 0);
      photo.y(savedPortrait.photo.y || 0);
      photo.width(savedPortrait.photo.width || photo.width());
      photo.height(savedPortrait.photo.height || photo.height());
      photo.scaleX(savedPortrait.photo.scaleX || 1);
      photo.scaleY(savedPortrait.photo.scaleY || 1);
      photo.rotation(savedPortrait.photo.rotation || 0);
    }

    const layer = wrapper.getLayer?.();
    if (layer?.batchDraw) layer.batchDraw();
    else if (layer?.draw) layer.draw();
  }

  async function restorePortraitsFromPayload(editorInstance, portraits) {
  if (!Array.isArray(portraits)) return;

  for (const savedPortrait of portraits) {
    const p = {
      shape: String(savedPortrait?.shape || "rect"),
      wIn: Number(savedPortrait?.wIn || 4) || 4,
      hIn: Number(savedPortrait?.hIn || 4) || 4,
      label: String(
        savedPortrait?.label ||
        savedPortrait?.wrapper?.name ||
        savedPortrait?.meta?.original_name ||
        ""
      )
    };

    addPlaceholder(editorInstance, p);

    const stage = editorInstance?.stage;
    const wrappers = stage?.find?.(".pamela-portrait-group") || [];
    const wrapper = wrappers
      .slice()
      .sort((a, b) => ((a?._id || 0) - (b?._id || 0)))
      .pop();

    if (!wrapper) continue;

    if (savedPortrait?.url) {
      await rebuildOnePortrait(wrapper, savedPortrait);
    } else {
      wrapper.x(savedPortrait?.wrapper?.x || 0);
      wrapper.y(savedPortrait?.wrapper?.y || 0);
    }
  }

  const lyr = editorInstance?.layers?.portraits;
  if (lyr?.batchDraw) lyr.batchDraw();
  else editorInstance?.stage?.draw?.();
}

  async function uploadFile(file, heicAck) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("heic_ack", heicAck ? "true" : "false");

    const res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = JSON.parse(txt); } catch (_) { data = { raw: txt }; }

    if (!res.ok || !data || !data.ok || !data.url) {
      const msg =
        (data && (data.error || data.message)) ||
        (typeof txt === "string" && txt.trim() ? txt.trim() : "Upload failed.");
      throw new Error(msg);
    }
    return data;
  }

  async function openPickerForWrapper(wrapper) {
    const input = ensureHiddenFileInput();
    const heicAck = !!($(HEIC_ACK_ID) && $(HEIC_ACK_ID).checked);

    try { input.value = ""; } catch (_) {}

    return new Promise((resolve) => {
      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return resolve(false);

        // local preview immediately
        try {
          const localUrl = URL.createObjectURL(file);
          const localImg = await loadHtmlImage(localUrl);
          setPortraitImage(wrapper, localImg, {
            url: localUrl,
            original_name: file.name,
            content_type: file.type || "",
            width: localImg.naturalWidth || 0,
            height: localImg.naturalHeight || 0,
            heic_ack: heicAck
          });
          try {
            const layer = wrapper.getLayer?.();
            if (layer?.batchDraw) layer.batchDraw();
            else if (layer?.draw) layer.draw();
            else wrapper.getStage?.()?.draw?.();
          } catch (_) {}
        } catch (_) {}

        // upload + replace with hosted URL
        try {
          const data = await uploadFile(file, heicAck);
          const hostedImg = await loadHtmlImage(data.url);
          setPortraitImage(wrapper, hostedImg, {
            url: data.url,
            original_name: data.original_name || file.name,
            content_type: data.content_type || file.type || "",
            width: data.width || hostedImg.naturalWidth || 0,
            height: data.height || hostedImg.naturalHeight || 0,
            heic_ack: heicAck
          });
          try {
            const layer = wrapper.getLayer?.();
            if (layer?.batchDraw) layer.batchDraw();
            else if (layer?.draw) layer.draw();
            else wrapper.getStage?.()?.draw?.();
          } catch (_) {}
        } catch (err) {
          console.warn("[Portrait Upload] Upload failed:", err && err.message ? err.message : err);
        }

        resolve(true);
      };

      input.click(); // must be called from user gesture
    });
  }

  window.CM_PortraitUpload = { openPickerForWrapper };
})();

// ============================================================
// PORTRAIT CENTER HANDLE (MOVE PHOTO BEHIND MASK)
// - Drag anywhere in mask => moves the wrapper (your existing behavior)
// - Drag CENTER HANDLE => moves ONLY the photo inside the clipped inner group
// - Click CENTER HANDLE (no drag) => selects photo + stabilizes transformer
// ============================================================
(function CM_PortraitCenterHandle_MovePhoto() {
  if (window.__CM_PortraitCenterHandle_On) return;
  window.__CM_PortraitCenterHandle_On = true;

  const HANDLE_NAME = "cm-portrait-move-handle";
  const PHOTO_NAME = "pamela-portrait-photo";
  const INNER_SEL = ".pamela-portrait-inner";

  // Gesture thresholds
  const TAP_MAX_MS = 450;
  const MOVE_START_PX = 4;

  let __cmLastSelectedPortraitWrapper = null;

  function getInner(wrapper) {
    return wrapper?.findOne?.(INNER_SEL) || null;
  }

  function getPhoto(wrapper) {
    const inner = getInner(wrapper);
    return inner?.findOne?.("." + PHOTO_NAME) || inner?.findOne?.(PHOTO_NAME) || null;
  }

  function getPortraitWH(wrapper) {
    try {
      const ppi = Number(wrapper.getAttr("__portraitPPI")) || 72;
      const wIn = Number(wrapper.getAttr("__portraitWIn")) || 4;
      const hIn = Number(wrapper.getAttr("__portraitHIn")) || 4;
      return { w: Math.max(1, wIn * ppi), h: Math.max(1, hIn * ppi) };
    } catch (_) {}
    return { w: 288, h: 288 };
  }

  // Create/reuse transformer on a safe layer (photo layer preferred; fallbacks allowed)
function ensureTransformerForPhoto(photo) {
  const stage = photo?.getStage?.();
  if (!stage || typeof stage.findOne !== "function") return null;

  let tr = stage.findOne("#cmPortraitTransformer");
  if (tr) {
    try {
      const wantedLayer =
        stage.findOne?.("#cmPortraitUiLayer") ||
        stage.findOne?.(".cm-portrait-ui-layer") ||
        null;

      const currentLayer = tr.getLayer?.() || null;

      if (wantedLayer && currentLayer !== wantedLayer && typeof wantedLayer.add === "function") {
        wantedLayer.add(tr);
        try { tr.moveToTop?.(); } catch (_) {}
        try { wantedLayer.batchDraw?.(); } catch (_) {}
      }
    } catch (_) {}

    return tr;
  }

  let layer = null;

  try {
    layer =
      stage.findOne?.("#cmPortraitUiLayer") ||
      stage.findOne?.(".cm-portrait-ui-layer") ||
      null;
  } catch (_) {}

  if (!layer) {
    try {
      const uiLayer =
        window.pamelaMonumentEditor?.layers?.ui ||
        window.pamelaEditor?.layers?.ui ||
        null;

      if (uiLayer && typeof uiLayer.add === "function") {
        layer = uiLayer;

        try { layer.id?.("cmPortraitUiLayer"); } catch (_) {}
        try { layer.addName?.("cm-portrait-ui-layer"); } catch (_) {}
      }
    } catch (_) {}
  }

  if (!layer) {
    try { layer = photo.getLayer?.() || null; } catch (_) {}
  }

  if (!layer) {
    try {
      const parent = photo.getParent?.();
      layer = parent?.getLayer?.() || null;
    } catch (_) {}
  }

  if (!layer) {
    try {
      const layers = stage.getLayers?.() || [];
      layer = layers[0] || null;
    } catch (_) {}
  }

  if (!layer || typeof layer.add !== "function") return null;

  tr = new Konva.Transformer({
    id: "cmPortraitTransformer",
    rotateEnabled: true,
    keepRatio: true,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
    ignoreStroke: true
  });

  layer.add(tr);

  try { tr.visible(true); } catch (_) {}
  try { tr.listening(true); } catch (_) {}
  try { tr.moveToTop?.(); } catch (_) {}
  try { layer.draw?.(); } catch (_) {}

  return tr;
}

  function getCenterHandle(wrapper) {
    try { return wrapper?.findOne?.("." + HANDLE_NAME) || null; } catch (_) {}
    return null;
  }

  function setCenterHandleVisible(wrapper, on) {
    try {
      const h = getCenterHandle(wrapper);
      if (!h) return;
      h.visible(!!on);

      const lyr = h.getLayer && h.getLayer();
      if (lyr && typeof lyr.batchDraw === "function") lyr.batchDraw();
      else if (lyr && typeof lyr.draw === "function") lyr.draw();
    } catch (_) {}
  }

  function hideAllPortraitHandles(stage) {
    try {
      if (!stage?.find) return;
      const handles = stage.find("." + HANDLE_NAME);
      if (!handles || !handles.length) return;

      handles.forEach((h) => {
        try { h.visible(false); } catch (_) {}
      });

      try {
        const lyr = handles[0].getLayer?.();
        if (lyr?.batchDraw) lyr.batchDraw();
        else if (lyr?.draw) lyr.draw();
      } catch (_) {}
    } catch (_) {}
  }

  function showOnlyThisHandle(wrapper) {
    try {
      const stage = wrapper?.getStage?.();
      if (!stage) return;

      hideAllPortraitHandles(stage);

      const h = getCenterHandle(wrapper);
      if (!h) return;

      h.visible(true);
      try { h.moveToTop(); } catch (_) {}

      const lyr = h.getLayer?.();
      if (lyr?.batchDraw) lyr.batchDraw();
      else if (lyr?.draw) lyr.draw();
    } catch (_) {}
  }

  // Walk up parents to see if a node is inside another node
  function isInSubtree(node, root) {
    try {
      let n = node;
      while (n) {
        if (n === root) return true;
        n = n.getParent && n.getParent();
      }
    } catch (_) {}
    return false;
  }

  // Keep the dot sitting on the PHOTO center (not the mask center)
function syncCenterHandleToPhoto(wrapper) {
  try {
    const inner = getInner(wrapper);
    const photo = getPhoto(wrapper);
    const h = getCenterHandle(wrapper);
    if (!inner || !photo || !h) return;

    try {
      const stage = wrapper.getStage && wrapper.getStage();
      let s = 1;
      if (stage) {
        if (typeof stage.scaleX === "function") s = Number(stage.scaleX() || 1) || 1;
        else if (stage.scale && typeof stage.scale === "function") s = Number(stage.scale().x || 1) || 1;
      }
      h.scale({ x: 1 / s, y: 1 / s });
    } catch (_) {}

    const { w, h: maskH } = getPortraitWH(wrapper);

    // Visible mask center
    const maskCx = w / 2;
    const maskCy = maskH / 2;

    // If the photo is smaller than the mask for any reason, fall back to mask center
    let r = null;
    try { r = photo.getClientRect({ relativeTo: inner }); } catch (_) {}

    if (!r) {
      h.position({ x: maskCx, y: maskCy });
      try { h.rotation(0); } catch (_) {}
      return;
    }

    // Keep the drag dot INSIDE the visible mask area.
    // It should stay at the visible mask center, not follow the photo center off-screen.
    h.position({ x: maskCx, y: maskCy });

    // Keep dot upright
    try { h.rotation(0); } catch (_) {}
  } catch (_) {}
}

  // Single, stable "deselect => hide dot" binding
  function ensureSinglePortraitDeselectBinding(stage) {
    if (!stage || stage.__cmPortraitDeselectBound) return;
    stage.__cmPortraitDeselectBound = true;

    const clickedInside = (target, node) => {
      try {
        let n = target;
        while (n) {
          if (n === node) return true;
          n = n.getParent?.();
        }
      } catch (_) {}
      return false;
    };

    const clearSelectionAndHide = () => {
      try {
        const tr = stage.findOne?.("#cmPortraitTransformer");
        if (tr) {
          tr.nodes([]);
          try { tr.forceUpdate?.(); } catch (_) {}
          const trLayer = tr.getLayer?.();
          if (trLayer?.batchDraw) trLayer.batchDraw();
          else if (trLayer?.draw) trLayer.draw();
        }
      } catch (_) {}

      hideAllPortraitHandles(stage);
      __cmLastSelectedPortraitWrapper = null;
      try {
        const ed =
          (typeof getPamelaEditorInstance === "function" ? getPamelaEditorInstance() : null) ||
          window.pamelaMonumentEditor ||
          window.pamelaEditor ||
          window.editor ||
          window.PAMELA_EDITOR ||
          window.__PAMELA_EDITOR__ ||
          null;
        if (ed) {
          try { ed.__cmSelectedPortraitWrapper = null; } catch (_) {}
          try { ed.selectNode?.(null); } catch (_) { try { ed.selected = null; } catch (_) {} }
          try { window.pamelaForceHidePortraitImagesToolbar?.(ed); } catch (_) {}
        }
        window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null;
        window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null;
        window.__CM_LAST_PORTRAIT_WRAPPER__ = null;
      } catch (_) {}
    };

    stage.on("pointerdown tap click", (e) => {
      try {
        const t = e?.target || null;
        const tr = stage.findOne?.("#cmPortraitTransformer");
        const nodes = tr?.nodes?.() || [];
        const photo = nodes[0] || null;

        // Nothing selected: ensure dots are hidden
        if (!photo) {
          hideAllPortraitHandles(stage);
          __cmLastSelectedPortraitWrapper = null;
          return;
        }

        // Is click on ANY handle?
        const isHandleClick = (() => {
          try {
            const name = t?.name?.() || "";
            if ((name || "").includes(HANDLE_NAME)) return true;

            // Find a handle on the last wrapper if we have it (best), otherwise any handle on stage
            const handleNode =
              (__cmLastSelectedPortraitWrapper && getCenterHandle(__cmLastSelectedPortraitWrapper)) ||
              stage.findOne?.("." + HANDLE_NAME) ||
              null;

            return handleNode ? clickedInside(t, handleNode) : false;
          } catch (_) { return false; }
        })();

        const clickedPhoto = (t && (t === photo || clickedInside(t, photo)));
        const clickedTransformer = (t && tr && (t === tr || clickedInside(t, tr)));

        if (clickedPhoto || clickedTransformer || isHandleClick) return;

        // Outside => deselect and hide all dots
        clearSelectionAndHide();
      } catch (_) {
        hideAllPortraitHandles(stage);
        __cmLastSelectedPortraitWrapper = null;
      }
    });

    // ESC clears selection and hides dot
    if (!window.__CM_PortraitEscHideDotsBound) {
      window.__CM_PortraitEscHideDotsBound = true;
      window.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape") {
          try {
            const tr = stage.findOne?.("#cmPortraitTransformer");
            if (tr) {
              tr.nodes([]);
              try { tr.forceUpdate?.(); } catch (_) {}
              const trLayer = tr.getLayer?.();
              if (trLayer?.batchDraw) trLayer.batchDraw();
              else if (trLayer?.draw) trLayer.draw();
            }
          } catch (_) {}

          hideAllPortraitHandles(stage);
          __cmLastSelectedPortraitWrapper = null;
        }
      });
    }
  }

  // ---- you already call selectPhoto(wrapper) elsewhere in this block ----
function selectPhoto(wrapper) {
  try {
    const photo = getPhoto(wrapper);
    if (!photo) return;

    const tr = ensureTransformerForPhoto(photo);
    if (!tr) {
      requestAnimationFrame(() => selectPhoto(wrapper));
      return;
    }

    const stage = photo.getStage && photo.getStage();
    ensureSinglePortraitDeselectBinding(stage);

    // Photo itself should not drag directly
    try { photo.draggable(false); } catch (_) {}
    try { photo.listening(false); } catch (_) {}

    // Select only the photo for portrait edit mode
    tr.nodes([photo]);

    ensureCenterHandle(wrapper);
    showOnlyThisHandle(wrapper);
    syncCenterHandleToPhoto(wrapper);
    __cmLastSelectedPortraitWrapper = wrapper;

    try { tr.visible(true); } catch (_) {}
    try { tr.rotateEnabled(true); } catch (_) {}
    try { tr.keepRatio(true); } catch (_) {}
    try { tr.enabledAnchors(["top-left", "top-right", "bottom-left", "bottom-right"]); } catch (_) {}
    try { tr.forceUpdate(); } catch (_) {}
    try { tr.moveToTop(); } catch (_) {}

    // Keep the editor's normal transformers cleared while portrait edit mode is active
    try {
      const ed =
        window.pamelaMonumentEditor ||
        window.pamelaEditor ||
        window.editor ||
        window.PAMELA_EDITOR ||
        window.__PAMELA_EDITOR__ ||
        null;

      if (ed) {
        try { ed.nodes?.tr?.nodes([]); } catch (_) {}
        try { ed.nodes?.trPanel?.nodes([]); } catch (_) {}
        try { ed.__cmSelectedPortraitWrapper = wrapper; } catch (_) {}
        try {
          if (typeof ed.selectNode === "function") ed.selectNode(wrapper);
          else ed.selected = wrapper;
        } catch (_) {
          try { ed.selected = wrapper; } catch (_) {}
        }
        try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
        try { window.__CM_LAST_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
        try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
        try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}
        try { window.pamelaShowPortraitImagesToolbar?.(ed, wrapper); } catch (_) {}
        try { window.pamelaRefreshImagesToolbar?.(ed); } catch (_) {}
        try { ed.layers?.ui?.batchDraw?.(); } catch (_) {}
      }
    } catch (_) {}

    // Keep dot synced while transforming
    if (!tr.__cmPortraitHandleSyncBound) {
      tr.__cmPortraitHandleSyncBound = true;

      const sync = () => {
        try {
          const nodes = tr.nodes && tr.nodes();
          if (!nodes || !nodes.length) {
            if (__cmLastSelectedPortraitWrapper) setCenterHandleVisible(__cmLastSelectedPortraitWrapper, false);
            return;
          }
          if (__cmLastSelectedPortraitWrapper) syncCenterHandleToPhoto(__cmLastSelectedPortraitWrapper);
        } catch (_) {}
      };

      tr.on("transform transformend dragmove", sync);
      tr.on("nodeChange", sync);
    }

    const photoLayer = (photo && typeof photo.getLayer === "function") ? photo.getLayer() : null;
    const trLayer = (tr && typeof tr.getLayer === "function") ? tr.getLayer() : null;

    if (photoLayer && typeof photoLayer.batchDraw === "function") photoLayer.batchDraw();
    else if (photoLayer && typeof photoLayer.draw === "function") photoLayer.draw();

    if (trLayer && trLayer !== photoLayer) {
      if (typeof trLayer.batchDraw === "function") trLayer.batchDraw();
      else if (typeof trLayer.draw === "function") trLayer.draw();
    }

    requestAnimationFrame(() => {
      try { tr.forceUpdate(); } catch (_) {}

      const photoLayer2 = (photo && typeof photo.getLayer === "function") ? photo.getLayer() : null;
      const trLayer2 = (tr && typeof tr.getLayer === "function") ? tr.getLayer() : null;

      if (photoLayer2 && typeof photoLayer2.batchDraw === "function") photoLayer2.batchDraw();
      if (trLayer2 && trLayer2 !== photoLayer2 && typeof trLayer2.batchDraw === "function") trLayer2.batchDraw();
    });
  } catch (_) {}
}

  window.CM_resumePortraitEditing = function (wrapper, opts = {}) {
    try {
      if (!wrapper) return false;

      const photo = getPhoto(wrapper);
      if (!photo) return false;

      try { photo.listening(false); } catch (_) {}
      try { photo.draggable(false); } catch (_) {}

      ensureCenterHandle(wrapper);
      syncCenterHandleToPhoto(wrapper);

      if (opts && opts.select) {
        selectPhoto(wrapper);
      }

      return true;
    } catch (_) {
      return false;
    }
  };

  function ensureCenterHandle(wrapper) {
    if (!wrapper || typeof wrapper.findOne !== "function") return null;

    let h = wrapper.findOne("." + HANDLE_NAME);
    if (h) return h;

    const inner = getInner(wrapper);
    if (!inner) return null;

    const g = new Konva.Group({
      x: 0,
      y: 0,
      name: HANDLE_NAME,
      listening: true
    });

    // Start hidden until photo is selected
    g.visible(false);

    // Big invisible hit target (easy to grab)
    const hit = new Konva.Circle({
      x: 0,
      y: 0,
      radius: 44,
      fill: "rgba(59,130,246,0.01)",
      listening: true
    });

    // Visible node
    const node = new Konva.Circle({
      x: 0,
      y: 0,
      radius: 14,
      fill: "#3b82f6",
      stroke: "#ffffff",
      strokeWidth: 4,
      listening: true
    });

    g.add(hit, node);

    // Zoom-independent sizing (matches your existing approach)
    try {
      const stage = wrapper.getStage && wrapper.getStage();
      if (stage) {
        const updateScale = () => {
          let s = 1;
          try {
            if (typeof stage.scaleX === "function") s = stage.scaleX();
            else if (stage.scale && typeof stage.scale === "function") s = stage.scale().x || 1;
          } catch (_) {}
          if (!s || s === 0) s = 1;
          g.scale({ x: 1 / s, y: 1 / s });
        };

        updateScale();

        if (!stage.__cmCenterHandleScaleBound) {
          stage.__cmCenterHandleScaleBound = true;
          stage.on("scaleXChange scaleYChange scaleChange", updateScale);
        }
      }
    } catch (_) {}

    // Add handle to INNER group so it lives with photo space
    inner.add(g);
    try { g.moveToTop(); } catch (_) {}
    try { g.listening(true); } catch (_) {}
    try { hit.listening(true); } catch (_) {}
    try { node.listening(true); } catch (_) {}
    try { hit.moveToTop?.(); } catch (_) {}
    try { node.moveToTop?.(); } catch (_) {}

    // Bind behavior once
    if (!g.__cmBound) {
      g.__cmBound = true;

      g.on("pointerdown", (e) => {
        try { e.cancelBubble = true; } catch (_) {}
        try { if (e.evt) e.evt.preventDefault(); } catch (_) {}

        const stage = wrapper.getStage && wrapper.getStage();
        const photo = getPhoto(wrapper);
        if (!stage || !photo) return;

        // Select photo immediately (stabilizes transformer + dot visibility)
        selectPhoto(wrapper);

        const containerEl = stage.container && stage.container();
        const pid = e?.evt?.pointerId;
        const hitNode = wrapper.findOne?.(".pamela-hit") || null;
        const handleHit = hit;
        const handleDot = node;

        // While dragging the blue handle, temporarily disable the wrapper hit area
        // so the photo-drag handle owns the gesture.
        try { if (hitNode) hitNode.listening(false); } catch (_) {}
        try { hitNode?.moveToBottom?.(); } catch (_) {}
        try { g.moveToTop?.(); } catch (_) {}
        try { handleHit?.moveToTop?.(); } catch (_) {}
        try { handleDot?.moveToTop?.(); } catch (_) {}

        try { if (containerEl && pid != null) containerEl.setPointerCapture(pid); } catch (_) {}

        const startTime = Date.now();

        const toWrapperLocal = (ptStage) => {
          try {
            const inv = wrapper.getAbsoluteTransform().copy().invert();
            return inv.point(ptStage);
          } catch (_) {}
          return ptStage;
        };

        const getStagePointer = (ev) => {
          try {
            if (containerEl && ev && typeof ev.clientX === "number") {
              const r = containerEl.getBoundingClientRect();
              return { x: ev.clientX - r.left, y: ev.clientY - r.top };
            }
          } catch (_) {}

          try {
            const p = stage.getPointerPosition && stage.getPointerPosition();
            if (p) return { x: p.x, y: p.y };
          } catch (_) {}

          return { x: 0, y: 0 };
        };

        const startStage = getStagePointer(e.evt);
        const startLocal = toWrapperLocal(startStage);

        const startPX = photo.x();
        const startPY = photo.y();

        let moved = false;
        let raf = null;

        const onMove = (ev) => {
          if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;
          try { ev.preventDefault(); } catch (_) {}
          try { stage.setPointersPositions && stage.setPointersPositions(ev); } catch (_) {}

          const curStage = getStagePointer(ev);
          const curLocal = toWrapperLocal(curStage);

          const dx = curLocal.x - startLocal.x;
          const dy = curLocal.y - startLocal.y;

          if (!moved && (Math.abs(dx) > MOVE_START_PX || Math.abs(dy) > MOVE_START_PX)) moved = true;

          if (!raf) {
            raf = requestAnimationFrame(() => {
              raf = null;
              if (!moved) return;

              // Move photo only
              photo.position({ x: startPX + dx, y: startPY + dy });

              // Keep dot centered on photo while moving
              syncCenterHandleToPhoto(wrapper);

              // Keep transformer updated smoothly
              const st = wrapper.getStage && wrapper.getStage();
              const tr = st && st.findOne && st.findOne("#cmPortraitTransformer");
              try { tr && tr.forceUpdate && tr.forceUpdate(); } catch (_) {}

              const lyr = photo.getLayer && photo.getLayer();
              if (lyr && typeof lyr.batchDraw === "function") lyr.batchDraw();
              else if (lyr && typeof lyr.draw === "function") lyr.draw();
            });
          }
        };

        const end = (ev) => {
          if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;

          window.removeEventListener("pointermove", onMove, true);
          window.removeEventListener("pointerup", end, true);
          window.removeEventListener("pointercancel", end, true);
          window.removeEventListener("blur", end, true);

          if (raf) { cancelAnimationFrame(raf); raf = null; }

          try { if (hitNode) hitNode.listening(true); } catch (_) {}
          try { hitNode?.moveToBottom?.(); } catch (_) {}
          try { g.moveToTop?.(); } catch (_) {}
          try { handleHit?.moveToTop?.(); } catch (_) {}
          try { handleDot?.moveToTop?.(); } catch (_) {}
          try { if (containerEl && pid != null) containerEl.releasePointerCapture(pid); } catch (_) {}

          const dt = Date.now() - startTime;
          if (!moved && dt <= TAP_MAX_MS) {
            // click (no drag) just re-selects (keeps dot shown)
            selectPhoto(wrapper);
          }
        };

        window.addEventListener("pointermove", onMove, { passive: false, capture: true });
        window.addEventListener("pointerup", end, true);
        window.addEventListener("pointercancel", end, true);
        window.addEventListener("blur", end, true);
      });
    }

    // Initial sync position (safe)
    syncCenterHandleToPhoto(wrapper);

    return g;
  }

  // ---- Hook point: after an upload places/updates the photo, add the handle + stabilize transformer ----
  const upl = window.CM_PortraitUpload;
  if (!upl || typeof upl.openPickerForWrapper !== "function") {
    console.warn("[Portrait Handle] CM_PortraitUpload not found yet.");
    return;
  }

  const originalOpen = upl.openPickerForWrapper;

  upl.openPickerForWrapper = function (wrapper) {
    const p = originalOpen(wrapper);

    const start = Date.now();
    const TRY_MS = 60000;
    const TICK_MS = 40;

    const tryAttachNow = () => {
      try {
        const photo = getPhoto(wrapper);
        if (!photo) return false;

        // Keep photo from stealing drags
        try { photo.listening(false); } catch (_) {}
        try { photo.draggable(false); } catch (_) {}

        ensureCenterHandle(wrapper);
        selectPhoto(wrapper);
        return true;
      } catch (_) {
        return false;
      }
    };

    const timer = setInterval(() => {
      if (tryAttachNow()) {
        clearInterval(timer);
        return;
      }
      if (Date.now() - start > TRY_MS) clearInterval(timer);
    }, TICK_MS);

    Promise.resolve(p).then(() => {
      tryAttachNow();
    });

    return p;
  };


})();

// ============================================================
// PATCH: Ensure center handle wins events over hit-rect
// ============================================================
(function CM_PortraitCenterHandle_EventPriorityPatch() {
  if (window.__CM_PortraitCenterHandle_EventPriorityPatchOn) return;
  window.__CM_PortraitCenterHandle_EventPriorityPatchOn = true;

  function patchWrapper(wrapper) {
    try {
      if (!wrapper?.findOne) return;

      const handle = wrapper.findOne(".cm-portrait-move-handle");
      const hit = wrapper.findOne(".pamela-hit");
      if (!handle || !hit) return;

      // Put hit UNDER everything; keep handle ABOVE everything
      try { hit.moveToBottom(); } catch (_) {}
      try { handle.moveToTop(); } catch (_) {}

      if (handle.__cmPriorityBound) return;
      handle.__cmPriorityBound = true;

      handle.on("pointerdown", (e) => {
        try { e.cancelBubble = true; } catch (_) {}
        try { if (e.evt) e.evt.preventDefault(); } catch (_) {}

        // Disable hit listening during handle gesture so wrapper drag cannot start
        try { hit.listening(false); } catch (_) {}

        const stage = wrapper.getStage && wrapper.getStage();
        const containerEl = stage?.container?.();
        const pid = e?.evt?.pointerId;

        const restore = () => {
          try { hit.listening(true); } catch (_) {}
          try { hit.moveToBottom(); } catch (_) {}
          try { handle.moveToTop(); } catch (_) {}
          try {
            const layer = wrapper.getLayer && wrapper.getLayer();
            if (layer?.batchDraw) layer.batchDraw();
            else if (layer?.draw) layer.draw();
          } catch (_) {}
        };

        const end = (ev) => {
          if (pid != null && ev?.pointerId != null && ev.pointerId !== pid) return;
          window.removeEventListener("pointerup", end, true);
          window.removeEventListener("pointercancel", end, true);
          window.removeEventListener("blur", end, true);
          restore();
          try { if (containerEl && pid != null) containerEl.releasePointerCapture(pid); } catch (_) {}
        };

        try { if (containerEl && pid != null) containerEl.setPointerCapture(pid); } catch (_) {}

        window.addEventListener("pointerup", end, true);
        window.addEventListener("pointercancel", end, true);
        window.addEventListener("blur", end, true);
      });
    } catch (_) {}
  }

  window.CM_patchPortraitWrapperPriority = function (wrapper) {
    try {
      patchWrapper(wrapper);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Re-check wrappers after interactions (lightweight)
  document.addEventListener(
    "pointerup",
    () => {
      setTimeout(() => {
        const st = window.pamelaEditor?.stage;
        if (!st?.find) return;
        st.find(".pamela-portrait-group").forEach(patchWrapper);
      }, 0);
    },
    true
  );

})();

// ============================================================
// PORTRAIT CLICK-TO-SELECT (stage hit-test fallback)
// Ensures clicking the visible portrait selects the mask/wrapper
// after reload and for newly added portraits.
// ============================================================
(function CM_PortraitStageClickSelect() {
  if (window.__CM_PortraitStageClickSelect_On) return;
  window.__CM_PortraitStageClickSelect_On = true;

  function findEditor() {
    const cands = [
      window.pamelaMonumentEditor,
      window.pamelaEditor,
      window.editor,
      window.PAMELA_EDITOR,
      window.__PAMELA_EDITOR__
    ].filter(Boolean);

    for (const ed of cands) {
      if (ed && ed.stage && ed.layers) return ed;
    }
    return null;
  }

  function findPortraitWrapperFromNode(node) {
    let cur = node;
    let hops = 0;
    while (cur && hops < 20) {
      try {
        const a = cur.attrs || {};
        const nm = (typeof cur.name === "function") ? String(cur.name() || "") : String(a.name || "");
        if (
          a.__isPortraitGroup === true ||
          nm.includes("pamela-portrait-group")
        ) return cur;
      } catch (_) {}

      try { cur = cur.getParent ? cur.getParent() : null; } catch (_) { cur = null; }
      hops++;
    }
    return null;
  }

  function selectPortraitWrapper(wrapper, ed) {
    if (!wrapper || !ed) return;
    try { ed.__cmSelectedPortraitWrapper = wrapper; } catch (_) {}
    try { ed.selected = wrapper; } catch (_) {}
    try { ed.selectNode?.(wrapper); } catch (_) {}
    try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
    try { window.__CM_LAST_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
    try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = wrapper; } catch (_) {}
    try { window.__PAMELA_IMAGES_FORCE_HIDDEN__ = false; } catch (_) {}
    try { ed.refreshButtons?.(); } catch (_) {}
    try { ed.refreshSizeButtons?.(); } catch (_) {}
    try { ed._refreshClipButtons?.(); } catch (_) {}
    try { ed._refreshFontUI?.(); } catch (_) {}
    try { window.pamelaRefreshImagesToolbar?.(ed); } catch (_) {}
  }

  const bind = () => {
    const ed = findEditor();
    const stage = ed?.stage || null;
    const container = stage?.container?.() || null;
    if (!ed || !stage || !container || container.__cmPortraitStageClickBound) return !!container;

    container.__cmPortraitStageClickBound = true;

    stage.off("click.cmPortraitSelect tap.cmPortraitSelect dblclick.cmPortraitSelect dbltap.cmPortraitSelect");

    const selectFromStageEvent = (e, allowReplace) => {
      try {
        const hit = e?.target || null;
        const wrapper = findPortraitWrapperFromNode(hit);
        if (!wrapper) return;

        try { e.cancelBubble = true; } catch (_) {}

        selectPortraitWrapper(wrapper, ed);
        if (allowReplace && window.CM_PortraitUpload?.openPickerForWrapper) {
          Promise.resolve(window.CM_PortraitUpload.openPickerForWrapper(wrapper)).catch(() => {});
        }
      } catch (_) {}
    };

    stage.on("click.cmPortraitSelect tap.cmPortraitSelect", (e) => {
      selectFromStageEvent(e, false);
    });

    stage.on("dblclick.cmPortraitSelect dbltap.cmPortraitSelect", (e) => {
      selectFromStageEvent(e, true);
    });
  };

  const timer = setInterval(() => {
    if (bind()) clearInterval(timer);
  }, 250);
})();

// ============================================================
// PORTRAIT HOVER HELPER (uses existing delayed tooltip system)
// - No photo:  "Drag frame to move. Double-click to add photo."
// - With photo:"Blue handles to adjust. Double-click to replace."
// Notes:
// - Uses stage.container() as the tooltip target (no new DOM overlays)
// - Only sets data-pamela-tip while hovering a portrait wrapper
// ============================================================
(function CM_PortraitHoverHelper_Tooltip() {
  if (window.__CM_PortraitHoverHelper_TooltipOn) return;
  window.__CM_PortraitHoverHelper_TooltipOn = true;

  const WRAPPER_NAME = "pamela-portrait-group";
  const PHOTO_NAME = "pamela-portrait-photo";

  const TIP_NO_PHOTO = "Drag frame to move. Double-click to add photo.";
  const TIP_WITH_PHOTO = "Use Fit Photo to adjust. Double-click to replace.";

  // --- Must match your existing editor locator ---
  function findEditor() {
    const cands = [
      window.pamelaEditor,
      window.pamelaMonumentEditor,
      window.editor,
      window.PAMELA_EDITOR,
      window.__PAMELA_EDITOR__
    ].filter(Boolean);

    for (const ed of cands) {
      if (ed && ed.stage && ed.layers) return ed;
    }

    for (const k in window) {
      try {
        const v = window[k];
        if (v && v.stage && v.layers && typeof v.stage.find === "function") return v;
      } catch (_) {}
    }
    return null;
  }

  function isPortraitWrapper(node) {
    try {
      if (!node) return false;
      if (typeof node.getAttr === "function" && node.getAttr("__isPortraitGroup") === true) return true;
      if (typeof node.name === "function") return (node.name() || "").includes(WRAPPER_NAME);
    } catch (_) {}
    return false;
  }

  function findPortraitWrapperFromNode(node) {
    let cur = node;
    let hops = 0;
    while (cur && hops < 12) {
      if (isPortraitWrapper(cur)) return cur;
      try { cur = cur.getParent && cur.getParent(); } catch (_) { cur = null; }
      hops++;
    }
    return null;
  }

  function portraitHasPhoto(wrapper) {
    try {
      // strongest truth source: your attr
      if (wrapper?.getAttr && wrapper.getAttr("__portraitHasPhoto") === true) return true;

      // fallback: actual node exists
      const inner = wrapper?.findOne?.(".pamela-portrait-inner") || null;
      const photo =
        inner?.findOne?.("." + PHOTO_NAME) ||
        inner?.findOne?.(PHOTO_NAME) ||
        wrapper?.findOne?.("." + PHOTO_NAME) ||
        wrapper?.findOne?.(PHOTO_NAME) ||
        null;

      return !!photo;
    } catch (_) {
      return false;
    }
  }

  function setContainerTip(containerEl, txt) {
    if (!containerEl) return;
    if (txt) {
      containerEl.setAttribute("data-pamela-tip", txt);
      // optional aria help
      if (!containerEl.getAttribute("aria-label")) containerEl.setAttribute("aria-label", txt);
    } else {
      containerEl.removeAttribute("data-pamela-tip");
      // don't remove aria-label globally; only remove if it matches our tips
      const al = containerEl.getAttribute("aria-label") || "";
      if (al === TIP_NO_PHOTO || al === TIP_WITH_PHOTO) containerEl.removeAttribute("aria-label");
    }
  }

  function wire() {
    const ed = findEditor();
    const stage = ed?.stage;
    const containerEl = stage?.container?.();
    if (!ed || !stage || !containerEl) return false;

    // Ensure your delayed tooltip system is on
    try { window.pamelaWireDelayedTooltips && window.pamelaWireDelayedTooltips(); } catch (_) {}

    if (containerEl.__cmPortraitHoverTipBound) return true;
    containerEl.__cmPortraitHoverTipBound = true;

    let lastWrapper = null;
    let lastMsg = "";

    const clear = () => {
      lastWrapper = null;
      lastMsg = "";
      setContainerTip(containerEl, "");
    };

    const update = () => {
      try {
        const pos = stage.getPointerPosition && stage.getPointerPosition();
        if (!pos) return clear();

        // Find the top-most listening node under pointer (your hit rect is listening=true)
        const hit = stage.getIntersection && stage.getIntersection(pos);
        const wrapper = findPortraitWrapperFromNode(hit);

        if (!wrapper) return clear();

        const msg = portraitHasPhoto(wrapper) ? TIP_WITH_PHOTO : TIP_NO_PHOTO;

        // Avoid thrashing attributes every mousemove
        if (wrapper === lastWrapper && msg === lastMsg) return;

        lastWrapper = wrapper;
        lastMsg = msg;
        setContainerTip(containerEl, msg);
      } catch (_) {
        clear();
      }
    };

    // Use pointermove so it works across mouse/stylus/touchpad
    containerEl.addEventListener("pointermove", update, true);
    containerEl.addEventListener("pointerleave", clear, true);
    window.addEventListener("blur", clear, true);

    // Also clear if they scroll/resize (keeps things sane)
    window.addEventListener("scroll", clear, true);
    window.addEventListener("resize", clear, true);

    // Initial clear
    clear();

    return true;
  }

  // Try now; if editor boots later, retry a few times
  if (wire()) return;

  let tries = 0;
  const t = setInterval(() => {
    tries++;
    if (wire() || tries > 200) clearInterval(t); // ~10s at 50ms
  }, 50);
})();


// ============================================================
// BACK LETTERING v1 (SAFE): Creates back only when needed
// - No global layer.add() patching
// - Marks the back minimum active once (single:150 / companion:200)
// - Adds FRONT/BACK toolbar buttons only after back is started
// - Adds Last Name on Back (copies front last name text/font if found; else MDRoman "Last Name")
// - Adds Verse on Back (multi-line)
// - Greys out back buttons; re-enables if that back node is deleted
// ============================================================
(function CM_BackLettering_SafeV1() {
  if (window.__CM_BackLettering_SafeV1_On) return;
  window.__CM_BackLettering_SafeV1_On = true;

  // ---------- CONFIG (change here if you later need to) ----------
  const BTN_LAST_ID = "pamela-add-back-lastname";
  const BTN_VERSE_ID = "pamela-add-back-verse";

  // Toolbar insertion anchor (already exists in your toolbar row)
  const TOOLBAR_FS_ID = "pamela-fullscreen";

  // IDs we will create for the view toggle buttons
  const BTN_FRONT_ID = "pamela-side-front";
  const BTN_BACK_ID  = "pamela-side-back";

  // Konva node names we add (used for lookup + re-enable)
  const BACK_LAST_NAME = "__cm_back_lastname";
  const BACK_VERSE_NAME = "__cm_back_verse";

  const DEFAULT_LASTNAME_TEXT = "Last Name";
  const DEFAULT_VERSE_TEXT = "Verse line 1\nVerse line 2";

  const DEFAULT_FONT_FAMILY = "MDRoman";
  // --------------------------------------------------------------

  function getEditor() {
    return (
      window.pamelaMonumentEditor ||
      window.pamelaEditor ||
      window.editor ||
      window.PAMELA_EDITOR ||
      window.__PAMELA_EDITOR__ ||
      null
    );
  }

  function getLayoutKind(ed) {
    try {
      if (typeof pamelaGetEditorLayoutKind === "function") return pamelaGetEditorLayoutKind(ed);
    } catch (_) {}

    // Tries editor first, then URL param (?layout=single), then falls back to companion.
    try {
      const v = (ed && (ed.layout || ed.kind || ed.productLayout)) ? String(ed.layout || ed.kind || ed.productLayout) : "";
      if (v.toLowerCase().includes("single")) return "single";
      if (v.toLowerCase().includes("comp")) return "companion";
    } catch (_) {}

    try {
      const p = new URLSearchParams(location.search);
      const layout = String(p.get("layout") || "");
      if (layout.toLowerCase() === "single") return "single";
      if (layout.toLowerCase() === "companion") return "companion";
    } catch (_) {}

    return "companion";
  }

  function isMarkerMonument(ed) {
    try {
      const style = String(ed?.monumentStyle || "").trim().toLowerCase();
      if (style.includes("slant")) return false;
      if (style.includes("marker")) return true;
    } catch (_) {}

    try {
      const p = new URLSearchParams(location.search);
      const style = String(p.get("style") || "").trim().toLowerCase();
      if (style.includes("slant")) return false;
      if (style.includes("marker")) return true;
    } catch (_) {}

    try {
      const title = String(document.querySelector(".product__title, .product-title, h1")?.textContent || "").toLowerCase();
      if (title.includes("slant")) return false;
      if (title.includes("marker")) return true;
    } catch (_) {}

    return false;
  }

  // ---------- Back minimum state ----------
  // The live-total system reads the real Shopify variant price. This state only
  // prevents repeated initialization while back lettering is active.
  function addBackMinimumOnce(_ed) {
    window.__CM_BACK_STATE__ = window.__CM_BACK_STATE__ || {};
    if (window.__CM_BACK_STATE__.minAdded) return;

    window.__CM_BACK_STATE__.minAdded = true;
    try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
  }

  // ---------- Pick the best content layer (we do NOT touch other layers) ----------
  function isUiLikeLayer(layer) {
    try {
      const nm = (layer && layer.name) ? String(layer.name() || "") : "";
      const id = (layer && layer.id) ? String(layer.id() || "") : "";
      const tag = (nm + " " + id).toLowerCase();
      return (
        tag.includes("grid") ||
        tag.includes("border") ||
        tag.includes("overlay") ||
        tag.includes("ui") ||
        tag.includes("guid") ||
        tag.includes("handle") ||
        tag.includes("transform")
      );
    } catch (_) {
      return false;
    }
  }

  function pickContentLayer(stage) {
    try {
      const layers = stage?.getLayers?.() || [];
      if (!layers.length) return null;

      let best = null;
      let bestScore = -1;

      layers.forEach((lyr) => {
        if (!lyr || !lyr.find) return;
        if (isUiLikeLayer(lyr)) return;

        // score based on how many Text/Image nodes it contains
        let score = 0;
        try { score += (lyr.find("Text") || []).length * 3; } catch (_) {}
        try { score += (lyr.find("Image") || []).length * 2; } catch (_) {}
        try { score += (lyr.find("Group") || []).length * 0.2; } catch (_) {}

        if (score > bestScore) {
          bestScore = score;
          best = lyr;
        }
      });

      // If none scored, fall back to topmost non-UI layer
      if (!best) {
        for (let i = layers.length - 1; i >= 0; i--) {
          const lyr = layers[i];
          if (lyr && !isUiLikeLayer(lyr)) { best = lyr; break; }
        }
      }

      return best;
    } catch (_) {
      return null;
    }
  }
function ensureSideGroupsSafe(layer) {
  // Ã¢Å“â€¦ REMOVED: group-based side system (front/back Konva.Groups)
  // We are using SIDE-SPLIT LAYERS (ed.layers.sides.front/back) instead.
  // Returning null ensures any old group-based callers safely do nothing.
  return null;
}

function updateSideToolbarUI(isBack) {
  // 1) Highlight FRONT/BACK buttons
  const frontBtn = document.getElementById(BTN_FRONT_ID);
  const backBtn  = document.getElementById(BTN_BACK_ID);

  const setActive = (btn, on) => {
    if (!btn) return;
    btn.disabled = false;
    btn.style.border = on ? "2px solid #111" : "1px solid #111";
    btn.style.boxShadow = on ? "0 0 0 2px rgba(0,0,0,0.15) inset" : "none";
    btn.style.background = on ? "rgba(255,255,255,0.42)" : "#fff";
    btn.style.color = "#111";
    btn.style.opacity = "1";
    btn.style.cursor = "pointer";
    btn.setAttribute("aria-pressed", on ? "true" : "false");
  };

  setActive(frontBtn, !isBack);
  setActive(backBtn,  isBack);

  // 2) Disable FRONT text-add buttons when viewing BACK
  // (These are your top toolbar buttons, NOT the back flyout buttons.)
  const FRONT_TEXT_ADD_IDS = [
    "pamela-add-lastname",
    "pamela-add-name",
    "pamela-add-date",
    "pamela-add-wedding",
    "pamela-add-verse",
  ];

  const setDisabled = (id, disabled) => {
    const b = document.getElementById(id);
    if (!b) return;
    b.disabled = !!disabled;
    b.style.opacity = disabled ? "0.35" : "1";
    b.style.cursor  = disabled ? "not-allowed" : "pointer";
  };

  FRONT_TEXT_ADD_IDS.forEach((id) => setDisabled(id, isBack));

  // 3) Overlay a pill on JUST the disabled "add text" buttons (not the font
  // select, size, or curve-text controls that share the same toolbar row).
  const textGroup = document.getElementById("pamela-toolbar-text-add-buttons");
  if (textGroup) {
    if (!textGroup.style.position) textGroup.style.position = "relative";
    let pill = document.getElementById("pamela-back-text-overlay-pill");
    if (!pill) {
      pill = document.createElement("div");
      pill.id = "pamela-back-text-overlay-pill";
      pill.textContent = "Use Optional Add-ons for back text";
      pill.setAttribute("role", "button");
      pill.setAttribute("tabindex", "0");
      pill.style.cssText =
        "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;" +
        "pointer-events:auto;cursor:pointer;border-radius:999px;background:#111;color:#fff;" +
        "font:900 12px/1.1 system-ui,-apple-system,Segoe UI,Roboto,Arial;" +
        "padding:6px 14px;text-align:center;z-index:2;";
      // The pill names "Optional Add-ons", but on mobile that button lives
      // under the Design tab the customer isn't currently looking at (they're
      // on Text, since selecting back lettering switches them there). Make
      // the pill itself the entry point instead of a dead-end label: tapping
      // it opens the addons flyout directly over the current tab via the
      // existing desktop button's own handler, with no tab switch needed
      // since the flyout's contents are all text-related already.
      pill.addEventListener("click", () => {
        pamelaToolbarProxyClick("pamela-open-addons");
        requestAnimationFrame(() => {
          try { window.PAMELA_openAddonFlyoutSection?.("back-lettering"); } catch (_) {}
        });
      });
      textGroup.appendChild(pill);
    }
    pill.style.display = isBack ? "flex" : "none";
  }
}

function setSide(side) {
  const ed = getEditor();
  if (!ed) return;

  if (side === "back" && isMarkerMonument(ed)) {
    side = "front";
  }

  const stage = ed.stage || ed.konvaStage;
  if (!stage) return;

  const directSwitch = !window.__PAMELA_SIDE_SWITCHING__;
  const previousSide =
    (window.PAMELA_SIDES?.current === "back")
      ? "back"
      : "front";

  if (directSwitch && previousSide !== side) {
    try {
      window.PAMELA_SIDES.current = previousSide;
      saveCurrentSideState();
    } catch (_) {}
  }

  if (directSwitch) {
    window.__PAMELA_SIDE_SWITCHING__ = true;
    window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = true;
  }

  const releaseDirectSwitch = () => {
    if (!directSwitch) return;
    try {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.__PAMELA_SIDE_SWITCHING__ = false;
          window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
          try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
        });
      });
    } catch (_) {
      window.__PAMELA_SIDE_SWITCHING__ = false;
      window.__PAMELA_SUPPRESS_PRICE_TOASTS__ = false;
      try { window.PAMELA_scheduleLiveTotalRefresh?.(); } catch (_) {}
    }
  };

  // Track state
  window.PAMELA_SIDES = window.PAMELA_SIDES || {};
  window.PAMELA_SIDES.current = (side === "back") ? "back" : "front";

  const sides = ed.layers?.sides;
  if (!sides || !sides.front || !sides.back) {
    console.warn("[setSide] ed.layers.sides missing Ã¢â‚¬â€ cannot switch.");
    releaseDirectSwitch();
    return;
  }

  const isBack = (side === "back");
  if (directSwitch && isBack && window.__PAMELA_ADDONS_OPENED_FOR_FIRST_BACK__ !== true) {
    window.__PAMELA_ADDONS_OPENED_FOR_FIRST_BACK__ = true;
    try {
      const fly = typeof ensureAddOnFlyout === "function" ? ensureAddOnFlyout() : document.getElementById("pamela-addon-flyout");
      if (fly) fly.style.display = "block";
      requestAnimationFrame(() => {
        try { window.PAMELA_openAddonFlyoutSection?.("back-lettering"); } catch (_) {}
      });
    } catch (_) {}
  }

  // 1) Hide/show the correct physical layers
  try { sides.front.panels.visible(!isBack); } catch(_) {}
  try { sides.front.content.visible(!isBack); } catch(_) {}
  try { sides.front.portraits.visible(!isBack); } catch(_) {}

  try { sides.back.panels.visible(isBack); } catch(_) {}
  try { sides.back.content.visible(isBack); } catch(_) {}
  try { sides.back.portraits.visible(isBack); } catch(_) {}

  // 2) IMPORTANT: switch ACTIVE aliases so ALL add/move/select logic targets the visible side
  if (isBack) {
    ed.layers.panels    = sides.back.panels;
    ed.layers.content   = sides.back.content;
    ed.layers.portraits = sides.back.portraits;
  } else {
    ed.layers.panels    = sides.front.panels;
    ed.layers.content   = sides.front.content;
    ed.layers.portraits = sides.front.portraits;
  }

    updateSideToolbarUI(isBack);

        try {
      const fn = window.__CM_BACK_LETTERING__?.syncFrontTextButtonsForSide;
      if (typeof fn === "function") {
        // Run now, then again AFTER the toolbar DOM settles
        fn(isBack);
        requestAnimationFrame(() => {
          try { fn(isBack); } catch (_) {}
          requestAnimationFrame(() => { try { fn(isBack); } catch (_) {} });
        });
      }
    } catch (_) {}
  // 3) Clear selection visuals safely
  try { ed.nodes?.tr?.nodes([]); } catch (_) {}
  try { ed.nodes?.trPanel?.nodes([]); } catch (_) {}

  // Clear portrait edit transformer too
  try {
    const ptr = ed.stage?.findOne?.("#cmPortraitTransformer");
    if (ptr) {
      ptr.nodes([]);
      try { ptr.forceUpdate?.(); } catch (_) {}
      try { ptr.visible(false); } catch (_) {}
      try { ptr.rotateEnabled?.(true); } catch (_) {}
      try { ptr.keepRatio?.(true); } catch (_) {}
      try { ptr.enabledAnchors?.(["top-left", "top-right", "bottom-left", "bottom-right"]); } catch (_) {}
      const ptrLayer = ptr.getLayer?.();
      if (ptrLayer?.batchDraw) ptrLayer.batchDraw();
      else if (ptrLayer?.draw) ptrLayer.draw();
    }
  } catch (_) {}

  // Hide all portrait center handles when switching sides
  try {
    const handles = ed.stage?.find?.(".cm-portrait-move-handle") || [];
    handles.forEach((h) => {
      try { h.visible(false); } catch (_) {}
    });

    const touchedLayers = new Set();
    handles.forEach((h) => {
      const lyr = h?.getLayer?.();
      if (lyr) touchedLayers.add(lyr);
    });

    touchedLayers.forEach((lyr) => {
      try {
        if (lyr?.batchDraw) lyr.batchDraw();
        else if (lyr?.draw) lyr.draw();
      } catch (_) {}
    });
  } catch (_) {}

  try { ed.selected = null; } catch (_) {}
  try { ed.__cmSelectedPortraitWrapper = null; } catch (_) {}
  try { window.__CM_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
  try { window.__PAMELA_IMAGES_SELECTED_PORTRAIT_WRAPPER__ = null; } catch (_) {}
  try { window.__CM_LAST_PORTRAIT_WRAPPER__ = null; } catch (_) {}
  try { pamelaRefreshImagesToolbar(ed); } catch (_) {}

  // Ã¢Å“â€¦ FIX: When returning to FRONT, force the real button-state refresh immediately
  // (removes the Ã¢â‚¬Å“must click onceÃ¢â‚¬Â behavior)
  try {
    requestAnimationFrame(() => {
      try { ed.refreshButtons && ed.refreshButtons(); } catch (_) {}
      try { ed.refreshSizeButtons && ed.refreshSizeButtons(); } catch (_) {}
      try { ed._refreshClipButtons && ed._refreshClipButtons(); } catch (_) {}
      try { ed._refreshFontUI && ed._refreshFontUI(); } catch (_) {}
    });
  } catch (_) {}

  // 4) Draw
  try { sides.front.panels.batchDraw?.(); } catch (_) {}
  try { sides.front.content.batchDraw?.(); } catch (_) {}
  try { sides.front.portraits.batchDraw?.(); } catch (_) {}
  try { sides.back.panels.batchDraw?.(); } catch (_) {}
  try { sides.back.content.batchDraw?.(); } catch (_) {}
  try { sides.back.portraits.batchDraw?.(); } catch (_) {}
  try { ed.layers?.ui?.batchDraw?.(); } catch (_) {}
    // Ã¢Å“â€¦ Force apply the current view to the newly-activated side (so it shows immediately)
  try {
    if (typeof ed._applyView === "function") ed._applyView();
  } catch (_) {}

  // Note: only one knockout rebuild pass runs per switch (below, after the
  // asset swap settles). An earlier "schedule now" pass used to run here too,
  // but pamelaRestorePanelKnockouts() at the top of every rebuild immediately
  // undoes whatever that early pass drew, so it was pure duplicated work.

  try {
    const applySideAssets = window.__PAMELA_APPLY_SIDE_ASSETS__;
    if (typeof applySideAssets === "function") {
      applySideAssets(isBack ? "back" : "front", { resetView: false })
        .then(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try { window.pamelaRebuildPanelKnockouts(ed); } catch (e) { console.error("[setSide] LATE pamelaRebuildPanelKnockouts call threw:", e); }
              try { ed.layers?.panels?.draw?.(); } catch (_) {}
              try { ed.layers?.content?.draw?.(); } catch (_) {}
            });
          });
        })
        .catch((e) => {
          console.warn("[setSide] side asset swap failed:", e);
        });
    }
  } catch (e) {
    console.warn("[setSide] side asset swap failed:", e);
  }
  try { saveCurrentSideState(); } catch (_) {}
  try {
    requestAnimationFrame(() => {
      try { if (typeof syncButtonStates === "function") syncButtonStates(ed); } catch (_) {}
    });
  } catch (_) {}
  releaseDirectSwitch();
}
  // ---------- Find front last name text node (best-effort heuristics) ----------
  function looksLikeLastNameTextNode(t) {
    try {
      if (!t || t.getClassName?.() !== "Text") return false;
      const a = t.attrs || {};
      const nm = (t.name && typeof t.name === "function") ? String(t.name() || "") : "";
      const id = (t.id && typeof t.id === "function") ? String(t.id() || "") : "";
      if (nm === BACK_LAST_NAME || id === BACK_LAST_NAME) return false;
      const s = (nm + " " + id + " " + JSON.stringify(a)).toLowerCase();

      // Try several Ã¢â‚¬Å“likelyÃ¢â‚¬Â markers
      if (s.includes("lastname")) return true;
      if (s.includes("last_name")) return true;
      if (s.includes("pamela-add-lastname")) return true;
      if (a.__pamelaField && String(a.__pamelaField).toLowerCase().includes("last")) return true;
      if (a.pamelaType && String(a.pamelaType).toLowerCase().includes("last")) return true;

      return false;
    } catch (_) {
      return false;
    }
  }

  function findFrontLastNameText(stage) {
    try {
      const ed = getEditor();
      const frontContent = ed?.layers?.sides?.front?.content;
      const texts = frontContent?.find ? frontContent.find("Text") : (stage?.find?.("Text") || []);
      // Prefer an exact marker if it exists
      for (const t of texts) if (looksLikeLastNameTextNode(t)) return t;
      return null;
    } catch (_) {
      return null;
    }
  }

  function ensureToolbarFrontBackButtons() {
    const ed = getEditor();
    if (isMarkerMonument(ed)) return false;

    const fs = document.getElementById(TOOLBAR_FS_ID);
    if (!fs || !fs.parentNode) return false;

    if (document.getElementById(BTN_FRONT_ID) && document.getElementById(BTN_BACK_ID)) return true;

    // Copy styling from an existing view-row button so it matches
    const sample = document.getElementById("pamela-zoom-out") || fs;
    const baseCss = (sample && sample.style && sample.style.cssText) ? sample.style.cssText : "";

    const mk = (id, label, tip) => {
      const b = document.createElement("button");
      b.type = "button";
      b.id = id;
      b.textContent = label;
      b.style.cssText = baseCss;
      b.style.minWidth = "78px";
      b.disabled = false;
      b.style.display = "inline-flex";
      b.style.alignItems = "center";
      b.style.justifyContent = "center";
      b.style.opacity = "1";
      b.style.cursor = "pointer";
      // TOOLTIP PLACE: change `title` strings below if you want different wording.
      b.title = tip;
      return b;
    };

    const frontBtn = mk(BTN_FRONT_ID, "FRONT", "View the front of the monument");
    const backBtn  = mk(BTN_BACK_ID,  "BACK",  "View the back of the monument");

    frontBtn.addEventListener("click", () => setSide("front"));
    backBtn.addEventListener("click",  () => setSide("back"));

    fs.parentNode.insertBefore(frontBtn, fs);
    fs.parentNode.insertBefore(backBtn, fs);

    return true;
  }

  function getBackGroup(ed) {
    // Ã¢Å“â€¦ REMOVED: group-based back group. We now add directly to ACTIVE alias layers.
    return null;
  }

  function nodeExistsInBack(ed, nodeName) {
    try {
      const sides = ed?.layers?.sides;
      const backContent = sides?.back?.content;
      const backPanels  = sides?.back?.panels;
      const backPorts   = sides?.back?.portraits;

      const inLayer = (lyr) => {
        if (!lyr || !lyr.findOne) return false;
        return !!(lyr.findOne("." + nodeName) || lyr.findOne("#" + nodeName));
      };

      const liveExists = inLayer(backContent) || inLayer(backPanels) || inLayer(backPorts);
      if (liveExists) return true;

      if (window.PAMELA_SIDES?.current === "back" && window.__PAMELA_DRAFT_RESTORING__ !== true) {
        return false;
      }

      const storedTexts = window.PAMELA_SIDES?.back?.draft_state?.texts || [];
      if (!Array.isArray(storedTexts)) return false;
      return storedTexts.some((entry) => {
        try {
          const attrs = (entry && entry.attrs && typeof entry.attrs === "object") ? entry.attrs : {};
          const name = String(entry?.name || attrs.name || "").trim();
          const id = String(entry?.id || attrs.id || "").trim();
          const text = String(entry?.text || "").trim();
          return !!text && (name === nodeName || id === nodeName);
        } catch (_) {
          return false;
        }
      });
    } catch (_) {
      return false;
    }
  }

  function setBtnDisabled(id, disabled) {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.disabled = !!disabled;
    btn.style.opacity = disabled ? "0.45" : "1";
    btn.style.cursor = disabled ? "not-allowed" : "pointer";
  }

function wireReenableOnDelete(ed, node, btnId) {
  if (!node) return;

  const reenable = () => {
    setBtnDisabled(btnId, false);
    try { checkIfBackShouldDeactivate(ed); } catch (_) {}
  };

  // If node is truly destroyed, this will fire.
  try { node.on && node.on("destroy", reenable); } catch (_) {}

  // Ã¢Å“â€¦ The real fix: watch the parent container for child changes.
  // This catches remove(), destroyChildren(), history rebuilds, and node replacement.
  try {
    const parent = node.getParent && node.getParent();
    if (parent && parent.on) {
      const onChildrenChange = () => {
        // If the node is no longer attached, it was removed/replaced.
        if (!node.getParent || !node.getParent()) {
          try { parent.off && parent.off("childrenChange", onChildrenChange); } catch (_) {}
          reenable();
        }
      };
      parent.on("childrenChange", onChildrenChange);
    } else {
      // If we can't observe a parent, fall back to immediate sanity
      // (rare edge cases where node was already detached)
      if (!node.getParent || !node.getParent()) reenable();
    }
  } catch (_) {}
}

function addBackFieldViaEditor(ed, fieldType) {
  const layer = ed?.layers?.content;
  if (!ed || !layer || !layer.getChildren || typeof ed.addField !== "function") return null;

  const before = layer.getChildren((n) => n && n.className === "Text").slice();
  ed.addField(fieldType);

  const after = layer.getChildren((n) => n && n.className === "Text").slice();
  return after.find((n) => !before.includes(n)) || null;
}

function getBackLetteringTargetRect(ed, stage) {
  function detectBackTabletRectFromBackground() {
    try {
      const bg = ed?.nodes?.bg;
      const img = bg?.image?.();
      if (!bg || !img) return null;

      const naturalW = Number(img.naturalWidth || img.width || 0);
      const naturalH = Number(img.naturalHeight || img.height || 0);
      if (!(naturalW > 0) || !(naturalH > 0)) return null;

      const maxSample = 260;
      const scale = Math.min(1, maxSample / Math.max(naturalW, naturalH));
      const sw = Math.max(1, Math.round(naturalW * scale));
      const sh = Math.max(1, Math.round(naturalH * scale));
      // Stop above the base. Scanning lower can connect the upright and vase
      // through the base, making the detector center the whole monument instead
      // of the upright tablet.
      const scanBottom = Math.max(1, Math.round(sh * 0.70));

      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, sw, sh);

      const data = ctx.getImageData(0, 0, sw, scanBottom).data;
      const total = sw * scanBottom;
      const solid = new Uint8Array(total);

      for (let i = 0, p = 0; i < total; i++, p += 4) {
        const alpha = data[p + 3];
        const lum = (data[p] * 0.299) + (data[p + 1] * 0.587) + (data[p + 2] * 0.114);
        if (alpha > 24 && lum < 90) solid[i] = 1;
      }

      const seen = new Uint8Array(total);
      const queue = new Int32Array(total);
      let best = null;

      for (let i = 0; i < total; i++) {
        if (!solid[i] || seen[i]) continue;

        let head = 0;
        let tail = 0;
        let count = 0;
        let minX = sw;
        let maxX = 0;
        let minY = scanBottom;
        let maxY = 0;

        // Per-row left/right edges. On single uprights with an attached vase,
        // the vase touches the upright in a MINORITY of rows and bulges the
        // silhouette to one side -- using the overall minX/maxX would shift
        // the detected center toward whichever side the vase happens to be
        // on. Tracking edges per row and taking the median below ignores
        // that bulge (it's an outlier in a few rows) and locks onto the
        // upright's own consistent width instead.
        const rowMinX = new Int32Array(scanBottom).fill(sw);
        const rowMaxX = new Int32Array(scanBottom).fill(-1);

        seen[i] = 1;
        queue[tail++] = i;

        while (head < tail) {
          const idx = queue[head++];
          const x = idx % sw;
          const y = (idx - x) / sw;

          count++;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          if (x < rowMinX[y]) rowMinX[y] = x;
          if (x > rowMaxX[y]) rowMaxX[y] = x;

          const neighbors = [
            x > 0 ? idx - 1 : -1,
            x < sw - 1 ? idx + 1 : -1,
            y > 0 ? idx - sw : -1,
            y < scanBottom - 1 ? idx + sw : -1
          ];

          for (const next of neighbors) {
            if (next < 0 || seen[next] || !solid[next]) continue;
            seen[next] = 1;
            queue[tail++] = next;
          }
        }

        const componentW = maxX - minX + 1;
        const componentH = maxY - minY + 1;
        if (componentW < sw * 0.18 || componentH < scanBottom * 0.24) continue;

        const touchedRowLefts = [];
        const touchedRowRights = [];
        for (let y = minY; y <= maxY; y++) {
          if (rowMaxX[y] < 0) continue;
          touchedRowLefts.push(rowMinX[y]);
          touchedRowRights.push(rowMaxX[y]);
        }
        touchedRowLefts.sort((a, b) => a - b);
        touchedRowRights.sort((a, b) => a - b);
        const coreMinX = touchedRowLefts.length
          ? touchedRowLefts[Math.floor(touchedRowLefts.length / 2)]
          : minX;
        const coreMaxX = touchedRowRights.length
          ? touchedRowRights[Math.floor(touchedRowRights.length / 2)]
          : maxX;

        const score = componentW * 3 + componentH + Math.min(count / 80, componentW * componentH);
        if (!best || score > best.score) {
          best = { score, minX: coreMinX, maxX: coreMaxX, minY, maxY };
        }
      }

      if (!best) return null;

      const inv = 1 / scale;
      const local = {
        left: Math.max(0, best.minX * inv),
        top: Math.max(0, best.minY * inv),
        width: Math.max(1, (best.maxX - best.minX + 1) * inv),
        height: Math.max(1, (best.maxY - best.minY + 1) * inv)
      };

      const sx = Number(bg.scaleX?.() || 1) || 1;
      const sy = Number(bg.scaleY?.() || 1) || 1;
      const x1 = Number(bg.x?.() || 0) + local.left * sx;
      const x2 = Number(bg.x?.() || 0) + (local.left + local.width) * sx;
      const y1 = Number(bg.y?.() || 0) + local.top * sy;
      const y2 = Number(bg.y?.() || 0) + (local.top + local.height) * sy;
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      if (!(right > left) || !(bottom > top)) return null;
      return { left, right, top, bottom, width: right - left, height: bottom - top };
    } catch (_) {
      return null;
    }
  }

  const detectedTablet = detectBackTabletRectFromBackground();
  if (detectedTablet && Number(detectedTablet.width) > 0 && Number(detectedTablet.height) > 0) {
    return detectedTablet;
  }

  try {
    if (typeof pamelaGetTabletLandingRect === "function") {
      const tablet = pamelaGetTabletLandingRect(ed);
      if (tablet && Number(tablet.width) > 0 && Number(tablet.height) > 0) {
        return {
          left: Number(tablet.left) || 0,
          right: Number(tablet.right) || (Number(tablet.left) || 0) + Number(tablet.width || 0),
          top: Number(tablet.top) || 0,
          bottom: Number(tablet.bottom) || (Number(tablet.top) || 0) + Number(tablet.height || 0),
          width: Number(tablet.width) || 0,
          height: Number(tablet.height) || 0
        };
      }
    }
  } catch (_) {}

  const w = ed?.state?.design?.width || stage?.width?.() || 1000;
  const h = ed?.state?.design?.height || stage?.height?.() || 600;
  const safe = (typeof pamelaGetSafeRect === "function")
    ? pamelaGetSafeRect(w, h)
    : { left: 0, right: w, top: 0, bottom: h };

  return {
    left: Number(safe.left) || 0,
    right: Number(safe.right) || w,
    top: Number(safe.top) || 0,
    bottom: Number(safe.bottom) || h,
    width: Math.max(1, (Number(safe.right) || w) - (Number(safe.left) || 0)),
    height: Math.max(1, (Number(safe.bottom) || h) - (Number(safe.top) || 0))
  };
}

function getBackLetteringCenterX(ed, stage) {
  const target = getBackLetteringTargetRect(ed, stage);
  return Math.round(target.left + target.width / 2);
}

function centerBackTextVisualBoxOnTablet(node, ed, stage) {
  try {
    if (!node) return;
    const layer = node.getLayer?.() || ed?.layers?.content || ed?.layers?.sides?.back?.content || null;
    const targetX = getBackLetteringCenterX(ed, stage);
    const rect = node.getClientRect?.({
      relativeTo: layer || undefined,
      skipStroke: true,
      skipShadow: true,
      skipTransform: false
    });
    const visualCenter = Number(rect?.x || 0) + Number(rect?.width || 0) / 2;
    if (!Number.isFinite(visualCenter)) return;
    node.x(Number(node.x?.() || 0) + (targetX - visualCenter));
  } catch (_) {}
}

function addLastNameOnBack() {
  const ed = getEditor();
  const stage = ed?.stage || ed?.konvaStage;
  if (!ed || !stage) return;
  if (isMarkerMonument(ed)) return;

  ensureToolbarFrontBackButtons();
  setSide("back");

  // DonÃ¢â‚¬â„¢t double-add
  if (nodeExistsInBack(ed, BACK_LAST_NAME)) {
    try {
      const existing = ed.layers?.sides?.back?.content?.findOne?.(`#${BACK_LAST_NAME}, .${BACK_LAST_NAME}`);
      if (existing && getLayoutKind(ed) === "single") {
        existing.setAttr?.("__cmBackLastNameLayout", "single");
        if (typeof window.pamelaNormalizeSingleBackLastNameDefault === "function") {
          window.pamelaNormalizeSingleBackLastNameDefault(existing, ed);
        }
        try { pamelaFixTextMetrics(existing); } catch (_) {}
        try { pamelaClampNodeIntoSafeRect(ed, existing); } catch (_) {}
        try { centerBackTextVisualBoxOnTablet(existing, ed, stage); } catch (_) {}
        try { ed.layers?.sides?.back?.content?.batchDraw?.(); } catch (_) {}
      }
    } catch (_) {}
    window.__CM_BACK_UI__ = window.__CM_BACK_UI__ || { hasLast: false, hasVerse: false };
    window.__CM_BACK_UI__.hasLast = true;
    setBtnDisabled(BTN_LAST_ID, true);
    return;
  }

  // Singles intentionally have no front-side "lastname" field, so use the normal
  // name pipeline for the back placeholder and then mark it as the back last name.
  const isSingleStone = getLayoutKind(ed) === "single";
  const backFieldType = isSingleStone ? "name" : "lastname";
  const layer = ed.layers?.content;
  const created = addBackFieldViaEditor(ed, backFieldType);
  if (!created) return;

  // Mark it as the BACK last name (reliable finder)
  try { created.name(BACK_LAST_NAME); } catch (_) {}
  try { created.id(BACK_LAST_NAME); } catch (_) {}
  try { created.setAttr("pamelaFieldType", "lastname"); } catch (_) {}
  try { created.setAttr("__cmBackLastNameLayout", isSingleStone ? "single" : "companion"); } catch (_) {}

  addBackMinimumOnce(ed);

  // One-time copy FROM front last name (text + font + size + fill), but keep independent node
  let copiedFrontLastName = false;
  try {
    const frontLast = findFrontLastNameText(stage);
    if (frontLast) {
      const frontText = String(frontLast.text?.() || "").trim();
      if (frontText) {
        created.text(frontText);
        copiedFrontLastName = true;
      }

      const ff = frontLast.fontFamily?.();
      if (ff) created.fontFamily(String(ff));

      const fs = frontLast.fontSize?.();
      if (!isSingleStone && fs) created.fontSize(Number(fs));

      const fill = frontLast.fill?.();
      if (fill) created.fill(String(fill));
    }
  } catch (_) {}
  if (!copiedFrontLastName) {
    try { created.text(DEFAULT_LASTNAME_TEXT); } catch (_) {}
    try { created.fontFamily(DEFAULT_FONT_FAMILY); } catch (_) {}
  }

  if (isSingleStone) {
    try { window.pamelaApplyCapHeightInToTextNode(created, PAMELA_SINGLE_BACK_LASTNAME_MAX_IN); } catch (_) {
      try { created.fontSize(pxFromIn(PAMELA_SINGLE_BACK_LASTNAME_MAX_IN)); } catch (__) {}
    }
    try { created.setAttr("__cmBackLastNameInitialized", true); } catch (_) {}
  }

  // Back placement: centered, ~30% down (and visibly lower than front)
  try {
    const target = getBackLetteringTargetRect(ed, stage);

    // Center on the upright/tablet area, not the full base.
    created.x(Math.round(target.left + target.width / 2));
    // ~42% down from TOP of target area
    created.y(Math.round(target.top + target.height * 0.42));

    created.align("center");
  } catch (_) {}

  // Ensure metrics + clamping match the front system
  try { pamelaFixTextMetrics(created); } catch (_) {}
  try { pamelaClampNodeIntoSafeRect(ed, created); } catch (_) {}
  try { centerBackTextVisualBoxOnTablet(created, ed, stage); } catch (_) {}
  try {
    requestAnimationFrame(() => {
      try { pamelaFixTextMetrics(created); } catch (_) {}
      try { centerBackTextVisualBoxOnTablet(created, ed, stage); } catch (_) {}
      try { layer.batchDraw?.(); } catch (_) {}
      try { ed.nodes?.tr?.forceUpdate?.(); } catch (_) {}
      try { ed.layers?.ui?.batchDraw?.(); } catch (_) {}
    });
  } catch (_) {}

  // Disable button (single-use until deleted)
  setBtnDisabled(BTN_LAST_ID, true);
  wireReenableOnDelete(ed, created, BTN_LAST_ID);

  // Draw + select
  try { layer.batchDraw?.(); } catch (_) {}
  try { ed.selectNode?.(created); } catch (_) {}
  try {
    requestAnimationFrame(() => {
      try { centerBackTextVisualBoxOnTablet(created, ed, stage); } catch (_) {}
      try { layer.batchDraw?.(); } catch (_) {}
      try { ed.selectNode?.(created); } catch (_) {}
    });
  } catch (_) {}
}

function addVerseOnBack() {
  const ed = getEditor();
  const stage = ed?.stage || ed?.konvaStage;
  if (!ed || !stage) return;
  if (isMarkerMonument(ed)) return;

  ensureToolbarFrontBackButtons();
  setSide("back");

  if (nodeExistsInBack(ed, BACK_VERSE_NAME)) {
    window.__CM_BACK_UI__ = window.__CM_BACK_UI__ || { hasLast: false, hasVerse: false };
    window.__CM_BACK_UI__.hasVerse = true;
    setBtnDisabled(BTN_VERSE_ID, true);
    return;
  }

  const layer = ed.layers?.content;
  const created = addBackFieldViaEditor(ed, "verse");
  if (!created) return;

  // Mark as BACK verse
  try { created.name(BACK_VERSE_NAME); } catch (_) {}
  try { created.id(BACK_VERSE_NAME); } catch (_) {}

  addBackMinimumOnce(ed);

  // Ã¢Å“â€¦ BACK VERSE: multi-line by Enter; do NOT force any wrap width
  try {
    created.wrap("none"); // only break on \n (Enter)
  } catch (_) {}

  // Back verse formatting requirements
  try {
    created.text(DEFAULT_VERSE_TEXT || "VERSE TEXT\nLINE 2\nLINE 3");
    created.align("center");
  } catch (_) {}

  // Ã¢Å“â€¦ Enforce sandblast line spacing rule (if your helper exists)
  try {
    if (typeof ed.CM_applyBackVerseLineSpacing === "function") ed.CM_applyBackVerseLineSpacing(created);
    else if (typeof CM_applyBackVerseLineSpacing === "function") CM_applyBackVerseLineSpacing(created);
  } catch (_) {}

  // Place centered, lower than last name
  try {
    const target = getBackLetteringTargetRect(ed, stage);

    created.x(Math.round(target.left + target.width / 2));
    const lastNode = ed.layers?.sides?.back?.content?.findOne(`#${BACK_LAST_NAME}, .${BACK_LAST_NAME}`);
const gapPx = 1.5 * 72; // 1.5 inches at 72 DPI

if (lastNode) {
  created.y(Math.round(lastNode.y() + lastNode.height() + gapPx));
} else {
  created.y(Math.round(target.top + target.height * 0.30));
}
  } catch (_) {}

  try { pamelaFixTextMetrics(created); } catch (_) {}
  try { centerBackTextVisualBoxOnTablet(created, ed, stage); } catch (_) {}
  try { pamelaClampNodeIntoSafeRect(ed, created); } catch (_) {}
  try { pamelaSnapTextToCenterX(ed, created); } catch (_) {}

  setBtnDisabled(BTN_VERSE_ID, true);
  wireReenableOnDelete(ed, created, BTN_VERSE_ID);

  try { layer.batchDraw?.(); } catch (_) {}
  try { ed.selectNode?.(created); } catch (_) {}
}
function syncButtonStates(ed) {
  try {
    if (!ed) return;

    if (isMarkerMonument(ed)) {
      setBtnDisabled(BTN_LAST_ID, true);
      setBtnDisabled(BTN_VERSE_ID, true);
      const frontBtn = document.getElementById(BTN_FRONT_ID);
      const backBtn = document.getElementById(BTN_BACK_ID);
      if (frontBtn) frontBtn.remove();
      if (backBtn) backBtn.remove();
      return;
    }

    const stage = ed.stage || ed.konvaStage;
    if (!stage || !stage.find) return;

    const hasLast  = nodeExistsInBack(ed, BACK_LAST_NAME);
    const hasVerse = nodeExistsInBack(ed, BACK_VERSE_NAME);

    setBtnDisabled(BTN_LAST_ID,  hasLast);
    setBtnDisabled(BTN_VERSE_ID, hasVerse);
  } catch (_) {}
}

function syncFrontTextButtonsForSide(isBack) {
  var ids = [
    "pamela-add-lastname",
    "pamela-add-name",
    "pamela-add-date",
    "pamela-add-wedding",
    "pamela-add-verse"
  ];

  for (var i = 0; i < ids.length; i++) {
    var id = ids[i];
    var btn = document.getElementById(id);
    if (!btn) continue;

    if (isBack) {
      // Save original state ONCE per back-entry
      if (btn.dataset.cmForcedDisabled !== "1") {
        btn.dataset.cmPrevDisabled = btn.disabled ? "1" : "0";
      }

      // Force disabled while in BACK
      btn.dataset.cmForcedDisabled = "1";
      btn.disabled = true;
      btn.style.opacity = "0.45";
      btn.style.cursor = "not-allowed";
    } else {
      // Restore only if WE forced it
      if (btn.dataset.cmForcedDisabled === "1") {
        var prev = btn.dataset.cmPrevDisabled === "1";

        delete btn.dataset.cmForcedDisabled;
        delete btn.dataset.cmPrevDisabled;

        // Restore exact previous disabled state
        btn.disabled = prev;

        if (prev) {
          btn.style.opacity = "0.45";
          btn.style.cursor = "not-allowed";
        } else {
          btn.style.opacity = "1";
          btn.style.cursor = "pointer";
        }
      }
    }
  }
}
function applyToolbarSideLocks(isBack) {
  try {
    // Apply once immediately
    syncFrontTextButtonsForSide(isBack);

    var toolbar = document.getElementById("pamela-editor-toolbar");
    if (!toolbar || typeof MutationObserver === "undefined") return;

    // Kill any previous observer
    var obj = window.__CM_BACK_LETTERING__;
    if (obj && obj._toolbarMO) {
      try { obj._toolbarMO.disconnect(); } catch (_) {}
      obj._toolbarMO = null;
    }

    // Re-apply whenever toolbar DOM changes (rebuilds/reset)
    var mo = new MutationObserver(function () {
      try { syncFrontTextButtonsForSide(isBack); } catch (_) {}
    });

    try {
      mo.observe(toolbar, { childList: true, subtree: true, attributes: true });
    } catch (_) {}

    // Store so we can disconnect later
    window.__CM_BACK_LETTERING__ = window.__CM_BACK_LETTERING__ || {};
    window.__CM_BACK_LETTERING__._toolbarMO = mo;

    // Auto-disconnect after a short window (prevents Ã¢â‚¬Å“watch foreverÃ¢â‚¬Â)
    setTimeout(function () {
      try { mo.disconnect(); } catch (_) {}
      try {
        if (window.__CM_BACK_LETTERING__ && window.__CM_BACK_LETTERING__._toolbarMO === mo) {
          window.__CM_BACK_LETTERING__._toolbarMO = null;
        }
      } catch (_) {}
    }, 1500);
  } catch (_) {}
}
  function checkIfBackShouldDeactivate(ed) {
    try {
      const hasLast = nodeExistsInBack(ed, BACK_LAST_NAME);
      const hasVerse = nodeExistsInBack(ed, BACK_VERSE_NAME);

      if (!hasLast && !hasVerse) {
        // Back is now empty

        // Reset minimum charge flag
        if (window.__CM_BACK_STATE__) {
          window.__CM_BACK_STATE__.minAdded = false;
        }

        // Switch to front
        setSide("front");

        // Uprights/slants keep FRONT/BACK controls available even when the
        // back is empty.
        ensureToolbarFrontBackButtons();
        updateSideToolbarUI(false);
      }
    } catch (_) {}

    // Ã¢Å“â€¦ Always re-sync button disabled state AFTER any back change (prevents "stuck gray")
    try {
      if (typeof syncButtonStates === "function") syncButtonStates(ed);
    } catch (_) {}
  }

  function wireButtonsWhenPresent() {
    const lastBtn = document.getElementById(BTN_LAST_ID);
    const verseBtn = document.getElementById(BTN_VERSE_ID);
    if (!lastBtn || !verseBtn) return false;

    if (!lastBtn.__cmWired) {
      lastBtn.__cmWired = true;
      lastBtn.addEventListener("click", (e) => {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
        addLastNameOnBack();
        try { window.PAMELA_closeAddonFlyoutOnMobile?.(); } catch (_) {}
      });
    }
    if (!verseBtn.__cmWired) {
      verseBtn.__cmWired = true;
      verseBtn.addEventListener("click", (e) => {
        try { e.preventDefault(); e.stopPropagation(); } catch (_) {}
        addVerseOnBack();
        try { window.PAMELA_closeAddonFlyoutOnMobile?.(); } catch (_) {}
      });
    }

    const ed = getEditor();
    if (ed) syncButtonStates(ed);

    return true;
  }

  // Ã¢Å“â€¦ REMOVED: __CM_BackLettering_SafeV1_MultiLayerPatch()
  // That patch was ONLY for group-based side toggling across layers.
  // We now use physical side-split layers + alias switching in setSide() above.

  // Boot: keep trying until the main toolbar exists, then keep FRONT/BACK visible.
  let sideBtnTries = 0;
  const sideBtnTimer = setInterval(() => {
    sideBtnTries++;
    try {
      const ok = ensureToolbarFrontBackButtons();
      if (ok) {
        updateSideToolbarUI(window.PAMELA_SIDES?.current === "back");
        clearInterval(sideBtnTimer);
      }
    } catch (_) {}
    if (sideBtnTries > 400) clearInterval(sideBtnTimer);
  }, 50);

  // Boot: keep trying until the flyout creates the buttons and the editor exists
  let tries = 0;
  const t = setInterval(() => {
    tries++;
    const ok = wireButtonsWhenPresent();
    if (ok || tries > 400) clearInterval(t);
  }, 50);

  // Ã¢Å“â€¦ expose helpers so flyout can call the real back-add functions (buttons have no IDs)
window.__CM_BACK_LETTERING__ = window.__CM_BACK_LETTERING__ || {};
window.__CM_BACK_LETTERING__.setSide = setSide;
window.__CM_BACK_LETTERING__.addLastNameOnBack = addLastNameOnBack;
window.__CM_BACK_LETTERING__.addVerseOnBack = addVerseOnBack;
window.__CM_BACK_LETTERING__.syncButtonStates = syncButtonStates;
window.__CM_BACK_LETTERING__.syncFrontTextButtonsForSide = syncFrontTextButtonsForSide;

})();

// Ã¢Å“â€¦ Wire the actual flyout buttons (they have NO IDs) using delegation from #pamela-addon-body
// This is LOCAL to BackLettering (no global layer patching).
(function CM_BackVerse_EnterAddsNewLine() {
  if (window.__CM_BackVerse_EnterAddsNewLine_On) return;
  window.__CM_BackVerse_EnterAddsNewLine_On = true;

  function getEditorSafe() {
    return window.pamelaEditor || window.pamelaMonumentEditor || null;
  }

  function isBackVerseSelected(ed) {
    const n = ed?.selected;
    if (!n || n.className !== "Text") return false;
    const nm = (typeof n.name === "function") ? String(n.name() || "") : String(n.attrs?.name || "");
    const id = (typeof n.id === "function") ? String(n.id() || "") : String(n.attrs?.id || "");
    return (nm === "__cm_back_verse" || id === "__cm_back_verse");
  }

  // Capture BEFORE other handlers
  document.addEventListener("keydown", (e) => {
    const el = e.target;
    if (!el || el.tagName !== "TEXTAREA") return;

    const ed = getEditorSafe();
    if (!isBackVerseSelected(ed)) return;

    // ENTER should be a newline, not "confirm"
    if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
      e.stopPropagation(); // allow newline, prevent commit logic upstream
      return;
    }

    // Ctrl+Enter (or Cmd+Enter) = confirm
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      try { el.blur(); } catch (_) {}
    }
  }, true);
})();
